import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  NgZone,
  ChangeDetectorRef,
  ApplicationRef,
  inject,
  Inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import {
  Subject,
  Subscription,
  takeUntil,
  take,
  firstValueFrom,
  finalize,
  throwError,
  of,
  Observable,
  forkJoin,
  shareReplay,
} from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { ModalStateService, ModalState } from '../../services/modal-state.service';
import {
  NotificationData,
  NotificationService,
  LprAxisImagesReadyEvent,
  LprAxisEntryUpdatedEvent,
  LprEntryUpdatedNotification,
} from '../../services/notification.service';
import { RealtimeEventsService } from '../../services/realtime-events.service';
import {
  OrderCreationService,
  CreateOrderPayload,
  CreateOrderResponse,
  CreateOrderAddOnVariantPayload,
  CheckoutOrderResponse,
} from '../../services/order-creation.service';
import {
  applyCheckoutSelectionToDraft,
  buildCheckoutDraftResponse,
  checkoutClaimErrorMessage,
  CheckoutSelectionScope,
  isFinalCheckoutRefusal,
  newCheckoutRequestId,
  rememberCheckoutRequestId,
  rememberedCheckoutRequestId,
} from './utils/notification-checkout-claim.util';
import { readCommonSettings, updateCommonSettings } from '../../shared/utils/common-settings.util';
import { PackageService } from '../../services/package.service';
import {
  ApiResponse,
  Package,
  VendorSubscriptionLookupData,
  VendorSubscriptionRedeemRequest,
  VendorMembershipLookupData,
} from '../../shared/types';
import { MatSelectModule } from '@angular/material/select';
import { PaymentService, CreatePaymentLinkRequest } from '../../services/payment.service';
import { ApiService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { FullStoryService } from '../../services/fullstory.service';
import { CustomerService } from '../../services/customer.service';
import {
  VehicleService,
  CreateVehiclePayload,
  UpdateVehiclePayload,
} from '../../services/vehicle.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { loadStripe } from '@stripe/stripe-js';
import { environment } from '../../../environments/environment';
import { bindPaymentSseEvents } from '../../orders/utils/payment-realtime-sse.util';
import { OrdersService } from '../../orders/services/orders.service';
import { OrderUtils } from '../../orders/utils/order.utils';
import { LocationService } from '../../services/location.service';
import { CustomInputComponent, ValidationRule } from '../custom-input/custom-input.component';
import { extractErrorMessage, sanitizeErrorMessage } from '../../shared/utils/error.util';
import { FamilyTags, familyTagsOf } from '../../shared/utils/family-tags.util';
import { FamilyTagsComponent } from '../../shared/components/family-tags/family-tags.component';
import {
  CUSTOMER_INACTIVE_MESSAGE,
  CUSTOMER_INACTIVE_REFUSAL_REASON,
  isCustomerInactive,
} from '../../shared/utils/customer-status.util';
import {
  buildVendorLookupRequest,
  isVendorLookupRetryable,
  mapVendorLookupOutcomeToText,
  resolveVendorMembershipLookupOffer,
  toPutShapedLprEntryResponse,
  vendorLookupButtonLabel,
  vendorLookupNotFoundText,
  vendorLookupOfferMessage,
  vendorPaidRecentlyHint,
  VENDOR_EXPIRED_MESSAGE_MARKER,
  VendorLookupResultText,
  VendorMembershipLookupOffer,
} from '../../shared/utils/vendor-membership-lookup.util';
import { isPrepaidRedeemLprNotification } from '../../shared/utils/get-lp-image-prepaid-redeem.util';
import { isManualLprCaptureNotification } from '../../shared/utils/notification-display.util';
import {
  formatOrderSuccessCustomerName,
  isWayOrderSuccessPayload,
  orderSuccessDisplayValue,
} from '../../shared/utils/order-success-display.util';
import { NO_VEHICLE_DETAILS_MESSAGE } from '../../shared/utils/vehicle-fetch.util';
import { GateService } from '../../services/gate.service';
import {
  canOpenGateForActiveSubscription,
  isMultiEligibleMembershipRedeemCompleted,
  isPostPurchaseLprMembershipActivation,
  resolveNotificationLaneId,
  resolveNotificationLocationId,
  findManualOperableLane,
  LaneGateRecord,
} from '../../shared/utils/gate-lane.util';
import {
  getActiveUserAuthToken,
  getAuthHeadersFromUserData,
  getUserDataLaneId,
  getUserDataLocationId,
} from '../../shared/utils/auth-headers.util';
import {
  buildCreateOrderLaneIdPayload,
  buildLprEntryLocationLanePutFields,
  isUiLprImageUploadNotificationData,
} from './utils/notification-create-order-lane.util';
import {
  MembershipStatusStripView,
  buildMembershipStatusStrip,
  isRedeemableRefusalDetail,
  refusalDetailOf,
} from './utils/membership-status-strip.util';
import { formatMembershipAllowance } from './utils/notification-order-request-allowance.util';
import { shouldRouteCustomerPickThroughPhoneChooser } from './utils/notification-customer-pick.util';
import {
  buildLocationPickerStamp,
  collectNotificationIdentityKeys,
  computeLocationHeaderVisible,
  pickOrderRequestContextForCheckout,
  resolveCarriedPickedLaneId,
  resolveFormLocationOnPopulate,
  resolveLocationPickerOrigin,
} from './utils/notification-order-request-location.util';
import {
  splitLicensePlateForApi,
  DEFAULT_LICENSE_PLATE_STATE,
  coerceUsStateCode,
} from '../../shared/utils/split-license-plate-for-api';
import { isLicensePlateCharactersValid } from '../../shared/utils/license-plate-characters.util';
import { US_STATES } from '../../shared/constants/us-states.constant';
import {
  MEMBERSHIP_ACTIVATION_DELAYED_MESSAGE,
  MEMBERSHIP_ACTIVATION_PENDING_MESSAGE,
  MEMBERSHIP_ACTIVATION_POLL_ATTEMPTS,
  MEMBERSHIP_ACTIVATION_POLL_INTERVAL_MS,
  MEMBERSHIP_ACTIVATION_WAIT_MS,
  isDeferredMembershipCardSetup,
  isMembershipActivationPending,
  isPaymentRequiredError,
  resolveMembershipFailureMessage,
} from '../../shared/utils/membership-payment-status.util';
import {
  isNotificationModalLprEntryFlow,
  notificationDataHasLprCaptureForOrderSource,
  notificationDataIndicatesLprAxisImageAwaitingPusher,
  notificationDataShowsLprPlatePreviewColumn,
  resolveLprAxisImageUrlFromNotificationData,
  resolveTapToPayOrderSource,
} from './notification-order-source.util';
import {
  formatDateTimeInTimeZone,
  formatDateTimeWithTimezone,
  formatTimeInTimeZone,
  getUserTimezone,
} from '../../shared/utils/date.util';
import { LoggerService } from '../../services/logger.service';
import {
  SuccessModalComponent,
  SuccessModalData,
} from '../../shared/components/success-modal/success-modal.component';
import { ModalFooterActionsComponent } from '../../shared/components/modal-footer-actions';
import { PrepaidRedeemedSuccessModalComponent } from './components/prepaid-redeemed-success-modal/prepaid-redeemed-success-modal.component';
import { VendorActiveSubscriptionModalComponent } from './components/vendor-active-subscription-modal/vendor-active-subscription-modal.component';
import {
  buildVendorActiveSubscriptionDisplayVm,
  resolveActiveSubscriptionCustomerName,
  VendorActiveSubscriptionDisplay,
} from './components/vendor-active-subscription-modal/vendor-active-subscription-display.util';
import {
  VendorActiveConfirmPaymentPayload,
  VendorActivePaymentMethod,
  VendorUpgradeDiscountOption,
  VendorUpgradePlanOption,
  VendorUpgradePlanSelection,
} from './components/vendor-active-subscription-modal/vendor-active-subscription-modal.component';
import { buildVendorActiveSubscriptionDisplayFromLookup } from './components/vendor-active-subscription-modal/vendor-active-subscription-lookup-display.util';
import {
  shouldAutoContinueVendorMembershipCheckout,
  shouldProcessVendorMembershipCardInline,
} from './components/vendor-active-subscription-modal/vendor-active-add-payment.util';
import { VendorMembershipBarcodeScannerModalComponent } from './components/vendor-membership-barcode-scanner-modal/vendor-membership-barcode-scanner-modal.component';
import { PrepaidCodeDialogComponent } from '../../shared/components/prepaid-code-dialog/prepaid-code-dialog.component';
import { shouldOfferCodeAfterQrFailure } from '../../shared/utils/prepaid-redemption-code.util';
import { VendorMembershipDeviceScanModalComponent } from './components/vendor-membership-device-scan-modal/vendor-membership-device-scan-modal.component';
import { extractVendorSourceIdFromBarcode } from '../../shared/utils/normalize-vendor-source-id.util';
import { getIsMobileOrTablet } from '../../shared/utils/device.util';
import {
  isCameraApiAvailable,
  requestBarcodeCameraStream,
  stopMediaStream,
} from '../../shared/utils/camera-stream.util';
import {
  buildPrepaidOrderRedemptionItemsFromSlotKeys,
  buildPrepaidRedeemedSuccessDisplay,
  getPrepaidPurchaseStatusBadge,
  isPrepaidPurchaseFullyRedeemed,
  PrepaidPurchaseStatusBadge,
  mapPrepaidPurchaseRedemptionsToSlots,
  PrepaidRedeemedSuccessDisplay,
  PrepaidRedemptionSlot,
} from '../../models/prepaid-purchase-status.util';
import {
  describePrepaidRedemptionResult,
  PrepaidRedemptionResultDisplay,
  PrepaidRedemptionVisitLike,
  resolvePrepaidRedemptionOrderId,
  shouldUpdateLprEntryAfterPrepaidRedemption,
} from '../../shared/utils/prepaid-redemption-result.util';
import {
  buildPrepaidRedemptionRecipientFromPurchaser,
  buildPurchaserSnapshotFromFormData,
  emptyPrepaidRedemptionRecipient,
  getPrepaidRedemptionPurchaserBaseline,
  normalizePhoneForRecipientCompare,
  normalizePlateForRecipientCompare,
  prepaidRedemptionContactFieldOverwritten,
  PrepaidRedemptionRecipientForm,
  PrepaidRedemptionPurchaserSnapshot,
  prepaidRedemptionVehiclePlateChanged,
  resolvePrepaidRedemptionAction,
  buildBlankOnlyCustomerPatch,
  buildPrepaidRedeemerPayloadFields,
  dropStalePurchaserIdsFromRecipient,
  isPlaceholderCustomerRecord,
} from '../../models/prepaid-redemption-recipient.util';
import {
  buildPaymentSuccessModalData,
  PAYMENT_SUCCESS_MODAL_DEFAULTS,
  resolveRawCustomerName,
} from '../../shared/utils/payment-success-modal.util';
import {
  buildCreateOrderPackageIdField,
  buildCreateOrderVehicleIdField,
  clearPrepaidWashBookContext,
  getOrderRequestCheckoutValidationError,
  ORDER_REQUEST_LANE_REQUIRED_MESSAGE,
  ORDER_REQUEST_LANES_LOAD_FAILED_MESSAGE,
  ORDER_REQUEST_LANES_LOADING_MESSAGE,
  ORDER_REQUEST_LINE_ITEMS_REQUIRED_MESSAGE,
  ORDER_REQUEST_MONTHLY_PHONE_REQUIRED_MESSAGE,
  ORDER_REQUEST_PREPAID_PHONE_REQUIRED_MESSAGE,
  PREPAID_REDEMPTION_PLATE_TAKEN_MESSAGE,
  ORDER_REQUEST_PLATE_INVALID_MESSAGE,
  ORDER_REQUEST_PLATE_REQUIRED_MESSAGE,
  ORDER_REQUEST_VALID_EMAIL_MESSAGE,
  ORDER_REQUEST_VALID_PHONE_MESSAGE,
  OrderRequestCheckoutValidationInput,
} from './utils/notification-order-checkout.util';
import {
  PhoneValidator,
  USA_PHONE_CUSTOM_INPUT_RULE,
} from '../../shared/validators/phone.validator';
import { EmailValidator } from '../../shared/validators/email.validator';
import { isMeaningfulDisplayName } from '../../shared/validators/display-name.validator';
import {
  CUSTOMER_PHONE_MAX_DIGITS,
  CUSTOMER_PHONE_MAX_LENGTH,
  sanitizeCustomerPhoneInput,
  validateCustomerEmail,
  validateCustomerName,
  validateCustomerPhone,
} from '../../shared/validators/customer-contact-field.rules';
import { RoleService } from '../../services/role.service';
import { UserRole } from '../../shared/types/role.interface';
import {
  BuiltInPaymentMethodCode,
  CheckoutPaymentMethodCode,
  isBuiltInPaymentMethodCode,
  normalizePaymentMethodValue,
  PaymentMethodCode,
  resolveBuiltInPaymentMethodCode,
} from '../../shared/types/payment-method.enum';
import { EmployeeService } from '../../services/employee.service';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  NotificationUtils,
  applyResolvedLprEntryTypeToNotification,
  lprEntryManualPlateEditFlagPayload,
  notificationDataFromLprEntryPutResponse,
  resolveLprEntryDismissEvent,
  resolveLprEntryIdFromNotificationData,
  resolveNotificationTypeFromLprEntry,
  hasReusableLprEntryRowFromNotificationsList,
  isInvalidLicensePlateLprEntryRow,
  mergeLprEntryUpdateIntoOpenNotification,
  sanitizeLprEntryPutEvent,
} from '../../notifications/notification.utils';
import { FleetService } from '../../fleet/services/fleet.service';
import { PrepaidWashBookService } from '../../services/prepaid-wash-book.service';
import { MultiEligibleSelectionModalStateService } from '../multi-eligible-selection-modal/services/multi-eligible-selection-modal-state.service';
import {
  buildMultiEligibleConfirmRequest,
  buildVehicleCardsFromPhoneResponse,
  countRedeemableWaySingleWashPurchases,
  shouldOpenPhoneActiveSubscriptionLookup,
} from '../multi-eligible-selection-modal/utils/multi-eligible-confirm.util';
import { MultiEligibleConfirmApiService } from '../multi-eligible-selection-modal/services/multi-eligible-confirm-api.service';
import { isReactivatedPausedRead } from '../../shared/utils/paused-subscription-notification.util';
import { WayPurchaseApiService } from '../way-purchase-modal/services/way-purchase-api.service';
import type { WayNormalizedPurchase } from '../way-purchase-modal/models/way-purchase.models';
import {
  isFormPlateInCustomerVehicleRows,
  isFormPlateInMembershipsPhonePayload,
} from './utils/order-request-phone-plate.util';
import {
  buildPrepaidCheckoutSummaryViewModel,
  formatOrderRequestPrepaidExpiryLine,
  formatOrderRequestPrepaidWashLine,
  PrepaidCheckoutSummaryViewModel,
  PrepaidPurchaseRedemption,
  PrepaidPurchaseStatus,
  PrepaidWashBook,
  PrepaidWashBookListLine,
} from '../../models/prepaid-wash-book.models';
import { parsePrepaidRedemptionUrl } from '../../shared/utils/prepaid-redemption-url.util';
import { TapToPayAndroidNativeService } from '../../services/tap-to-pay-android-native.service';
import { TerminalSettingsService } from '../../services/terminal-settings.service';
import {
  DEFAULT_TERMINAL_SETTINGS,
  TerminalReader,
  TerminalSettings,
} from '../terminal-settings/terminal-settings.data';
import { computeTerminalConvenienceFee } from '../../shared/utils/terminal-convenience-fee.util';
import {
  rememberTerminalReaderId,
  selectCheckoutTerminalReaderId,
} from '../../shared/utils/terminal-reader-preference.util';
import {
  buildTapToPayNativeParams,
  cleanTapToPayAmount,
  resolveInternalOrderId,
  tapToPayOrderIdsMatch,
  buildTerminalReaderCheckoutMetadata,
  resolvePublicOrderId,
} from '../../shared/utils/tap-to-pay-order-params.util';
import { unwrapOrderFromApiResponse } from '../../shared/utils/order-api.util';
import { PdfService } from '../../services/pdf.service';
import { ReceiptPrinterService } from '../../services/receipt-printer.service';
import { OrderReceiptService } from '../../services/order-receipt.service';
import { VendorModeService } from '../../services/vendor-mode.service';
import { PlanChangeSettingsService } from '../../services/plan-change-settings.service';
import { CancellationSettingsService } from '../../services/cancellation-settings.service';
import { CustomerSubscriptionService } from '../../services/customer-subscription.service';
import type { FamilyStartResult } from '../../shared/types/family-start.types';
import { CancelRefundDone } from '../../shared/components/cancel-refund-panel/cancel-refund-panel.component';
import type { SubscriptionPause } from '../../shared/types/subscription-pause.types';
import { PlanChangeResult, buildPlanChangeNotice } from '../../services/plan-change.service';
import {
  ORDER_REQUEST_CUSTOM_DISCOUNT,
  OrderRequestAddonItem,
  OrderRequestAddonPackageKind,
  OrderRequestAddonVariantLine,
  OrderRequestCustomerHistory,
  OrderRequestDiscountItem,
  OrderRequestRewashTarget,
  OrderRequestTodayBannerState,
  OrderRequestVendingItem,
  NotificationPaymentModeApiItem,
  MembershipRewashEligibilityState,
  PrepaidRewashEligibilityState,
  RewashEligibilityState,
} from './models/notification-order-request.models';
import { CUSTOM_DISCOUNT_REASON_OPTIONS } from '../../discount/constants/discount.constants';
import { mapAddOnPackagesToOrderItems } from './utils/notification-order-request-addon.mapper';
import { filterOrderRequestDiscountsForOrder } from './utils/notification-order-request-discount.util';
import { isValidAddOnPackagesCache } from '../../shared/utils/add-on-packages-response.util';
import { formatCheckoutCurrency as formatCheckoutCurrencyUtil } from './utils/notification-currency.util';
import {
  FamilyCheckoutBreakdown,
  FamilyCheckoutRow,
  buildFamilyPrimary,
  familyLprEntryId,
  buildFamilyQuotePayload,
  eligibleFamilyPackages,
  familyCheckoutBreakdown,
  familyCheckoutRows,
  familyErrorWithPlates,
  familyPackageListPrice,
  isPrimaryPackageFamilyEligible,
  isRetryableFamilyCompleteCode,
  newFamilyRequestId,
} from './utils/notification-family-sale.util';
import type {
  FamilyMemberDraft,
  FamilySaleCompleteData,
  FamilySaleQuote,
  FamilySaleQuotePayload,
} from '../../shared/types/family-sale.types';
import { FamilySaleService } from '../../services/family-sale.service';
import {
  expandOrderRequestPackages,
  normalizePackageThemeColor,
} from './utils/notification-order-request-package.util';
import { isLightThemeBackground } from '../../shared/utils/theme-color.util';
import { normalizeOrderRequestWashType } from './utils/notification-order-request-wash-type.util';
import { NotificationOrderRequestLayoutComponent } from './components/order-request-layout/order-request-layout.component';
import { RewashReasonModalComponent } from './components/rewash-reason-modal/rewash-reason-modal.component';
import {
  ConfirmationModalComponent,
  ConfirmationModalData,
} from '../../shared/components/confirmation-modal/confirmation-modal.component';
import { NotificationPaymentCheckoutLayoutComponent } from './components/payment-checkout-layout/payment-checkout-layout.component';
import { NotificationFleetVehicleFoundLayoutComponent } from './components/fleet-vehicle-found-layout/fleet-vehicle-found-layout.component';
import {
  buildOrderRequestHistoryInsight,
  EMPTY_HISTORY_WELCOME,
  formatWashTypeLabel as formatWashTypeLabelUtil,
  isTodayVisitSuggestion,
  omitSubscriptionWashFromHistoryResponse,
  normalizeRewashTarget,
  parseMembershipRewashEligibilityFromHistoryResponse,
  parsePrepaidRewashEligibilityFromHistoryResponse,
  parseRewashEligibilityFromHistoryResponse,
  patchOrderRequestHistoryBannerPage,
  resolveSubscriptionWashIdToOmit,
} from './utils/notification-order-request-history.util';
import {
  NotificationOrderRequestHistoryService,
  OrderRequestHistoryFetchFilters,
} from './services/notification-order-request-history.service';
import { NotificationOrderRequestCatalogService } from './services/notification-order-request-catalog.service';
import { NotificationOrderRequestWayLookupService } from './services/notification-order-request-way-lookup.service';
import { isWayPurchaseLookupEnabled } from '../way-purchase-modal/utils/way-purchase-lookup-enabled.util';

type BuiltInPaymentMethod = BuiltInPaymentMethodCode;
type PaymentMethod = CheckoutPaymentMethodCode | string;
type TerminalCardType = 'credit' | 'debit';
/** Which surface a vendor membership barcode came from. */
type VendorMembershipScanOrigin = 'camera' | 'device';

/** Focus targets for pre-API order-request validation (existing field ids / sections). */
type OrderRequestValidationFocusTarget =
  | 'services'
  | 'plate'
  | 'phone'
  | 'email'
  | 'lane'
  | 'singleWashPrice'
  | 'redemptionPlate'
  | 'redemptionPhone'
  | 'redemptionEmail'
  | 'redemptionFirstName'
  | 'redemptionLastName';

interface PaymentForm {
  method: PaymentMethod;
  phoneNumber: string;
  cardholderName: string;
  billingAddress: string;
  cardNumber: string;
  cardExpiry: string;
  cardCvc: string;
  /** Cash tender entered for create-payment-request checkout (exact / over-tender). */
  cashReceived: string;
  terminalReaderId: string;
  terminalCardType: TerminalCardType | '';
}

interface DynamicPaymentMethodOption {
  id: string;
  name: string;
  washTypes: ('single_wash' | 'monthly_membership' | 'add_on')[];
}

const FAMILY_MEMBER_REQUIRED_MESSAGE =
  'Add at least one family member, or turn off Sell as Family Plan.';
const FAMILY_COMPLETE_ATTEMPTS = 3;
const FAMILY_COMPLETE_RETRY_MS = 2000;

/** @deprecated Use {@link OrderRequestExpandedPackage} from models; alias kept for minimal churn. */
type ExpandedPackage =
  import('./models/notification-order-request.models').OrderRequestExpandedPackage;

/** Shown as an extra tile under a parent add-on (e.g. Wonder × 2). */
interface OrderRequestAddonCommittedTileLine {
  id: string;
  title: string;
  priceLabel: string;
  parentLabel: string;
}

/** Item in the "Most used services" shortcut strip (from the default-by-location API). */
interface MostUsedServiceItem {
  id: string;
  label: string;
  /** Price or type badge text shown beneath the label. */
  priceLabel: string;
  kind: 'package' | 'prepaid' | 'addon';
  packageRef?: ExpandedPackage;
  prepaidBookId?: string;
  addonRef?: OrderRequestAddonItem;
}

/** One line in the committed vending cart (after Continue). */
interface OrderRequestVendingSelectionLine {
  id: string;
  name: string;
  quantity: number;
  unitPrice: number;
  lineTotalFormatted: string;
}

interface LocationDropdownOption {
  id: string;
  name: string;
  wayPurchaseLookupEnabled?: boolean;
}

@Component({
  selector: 'app-notification-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    CustomInputComponent,
    SuccessModalComponent,
    NotificationOrderRequestLayoutComponent,
    NotificationPaymentCheckoutLayoutComponent,
    NotificationFleetVehicleFoundLayoutComponent,
    PrepaidRedeemedSuccessModalComponent,
    PrepaidCodeDialogComponent,
    VendorActiveSubscriptionModalComponent,
    FamilyTagsComponent,
    VendorMembershipBarcodeScannerModalComponent,
    VendorMembershipDeviceScanModalComponent,
    RewashReasonModalComponent,
    ConfirmationModalComponent,
    ModalFooterActionsComponent,
  ],
  templateUrl: './notification-modal.component.html',
  styleUrls: ['./notification-modal.component.scss'],
  providers: [NotificationOrderRequestHistoryService, NotificationOrderRequestCatalogService],
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-in', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('200ms ease-out', style({ opacity: 0 }))]),
    ]),
    trigger('slideInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px) scale(0.95)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0) scale(1)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(-10px) scale(0.95)' })),
      ]),
    ]),
  ],
})
export class NotificationModalComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly PaymentMethodCode = PaymentMethodCode;
  private destroy$ = new Subject<void>();

  isOpen = false;
  notification: NotificationData | null = null;
  // Form properties for two-way data binding
  formData = {
    customerFirstName: '',
    customerLastName: '',
    customerEmail: '',
    customerContact: '',
    vehicleLicensePlate: '',
    vehicleLicensePlateState: DEFAULT_LICENSE_PLATE_STATE,
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    vehicleVin: '',
    location: '',
    lane: '',
    package: '',
    vehicleId: '',
    customerId: '',
    locationId: '',
    laneId: '',
    packageId: '',
    price: '',
    orderId: '',
    appointmentTime: '',
    stripeCustomerId: '',
    notificationId: '', // Preserve notification ID for updates
    detailingAppointmentTime: '',
  };

  // Track which fields user has interacted with
  userInteractedFields = new Set<string>();

  // Edit mode for create-payment-request
  isEditMode = false; // Track if we're in edit mode for payment request
  isFetchingOrderData = false; // Track if we're fetching order data for edit mode
  originalValues = {
    // Store original values to detect changes
    packageId: '',
    customerFirstName: '',
    customerLastName: '',
    customerEmail: '',
    customerContact: '',
    vehicleLicensePlate: '',
    vehicleLicensePlateState: DEFAULT_LICENSE_PLATE_STATE,
    vehicleVin: '',
    vehicleId: '',
  };

  /** Set to true when getVehicleData (plate lookup) succeeds so we call updateVehicle instead of createVehicle. */
  vehicleDataFetched = false;
  /** The vehicle id returned by getVehicleData, used when linking an existing vehicle to a new customer. */
  fetchedVehicleId = '';
  /** Plate string (trimmed, uppercased) for which {@link fetchedVehicleId} is valid — avoids duplicate getVehicleData on checkout. */
  private lastVehicleLookupPlate = '';
  /** Plate already sent to GET vehicle/plate this session (incl. 4xx) — do not call again on Save/checkout. */
  private lastVehiclePlateLookupAttempted = '';
  /** In-flight plate key to collapse duplicate concurrent lookups (search + save). */
  private plateLookupInFlightPlate: string | null = null;
  /** Collapses duplicate customer-by-plate searches for the same plate on modal open / hydrate. */
  private licensePlateCustomerSearchInFlightKey: string | null = null;
  private licensePlateCustomerSearchCompletedKey = '';
  /**
   * Vehicle id + plate when staff linked an existing customer/vehicle in the modal.
   * If the plate is edited away from this key, we create/match a new vehicle on proceed — never
   * PUT a new plate onto the old vehicle row (keeps past orders on the original vehicle).
   */
  private linkedVehicleAtSelection: { vehicleId: string; plateKey: string } | null = null;
  /**
   * Plate key the scan's images actually depict, captured once when the scan prefills the form.
   *
   * Never refreshed afterwards: editing the plate PUTs the correction back to the LPR entry and
   * the response replaces `notification` wholesale, so `notification.data.plate` becomes the
   * typed value. Comparing against that live would always match and never hide the image.
   */
  private scannedPlateKeyForImages: string | null = null;

  // Loading state for update order
  isUpdatingOrder = false;
  /**
   * CWS-1945: Continue no longer creates the order. This is the order it would have created; it
   * is sent to POST /order/checkout when the attendant pays, and cleared once that call returns
   * the real order.
   */
  private checkoutDraftPayload: CreateOrderPayload | null = null;
  /** CWS-1945: summary toggles changed on a draft (they used to PUT the order immediately). */
  private readonly checkoutDraftTouchedScopes = new Set<CheckoutSelectionScope>();
  /** CWS-1945: one id per checkout on this tablet; retries reuse it so they pay the same order. */
  private checkoutRequestId: string | null = null;
  /** CWS-1945: POST /order/checkout in flight for the current pay press. */
  isClaimingCheckout = false;
  /** CWS-1945: the claim for this pay press succeeded; the payment handler consumes it once. */
  private checkoutClaimPassed = false;
  isOpeningGate = false;
  /** CWS-1469: redeeming a paused-read membership after Activate now. */
  isRedeemingReactivated = false;
  updateOrderError = '';

  /** Order lane + location resolved for the payment success modal's "Open Gate" button. */
  private openGateLocationId = '';
  private openGateLaneId = '';

  // Validation errors for phone and email
  phoneValidationError = '';
  /** Max length for Order Request US phone inputs (digits + formatting). */
  readonly customerPhoneMaxLength = CUSTOMER_PHONE_MAX_LENGTH;
  emailValidationError = '';
  /** Existing checkout copy: "Plate number is required." — shown inline under plate. */
  licensePlateValidationError = '';
  /**
   * CWS-1881: a plate-level refusal on the redemption panel. The only one today is the
   * backend's VEHICLE_PLATE_TAKEN (409) — the walked-in plate is registered to a third
   * customer, so it is neither the redeemer's to reuse nor ours to move.
   */
  prepaidRedemptionPlateError = '';
  prepaidRedemptionPhoneError = '';
  prepaidRedemptionEmailError = '';
  prepaidRedemptionFirstNameError = '';
  prepaidRedemptionLastNameError = '';

  // Package selection properties
  packages: Package[] = [];
  expandedPackages: ExpandedPackage[] = []; // Expanded packages for display
  /** Single-wash options for display (shown first). */
  get singleWashPackages(): ExpandedPackage[] {
    return this.expandedPackages.filter(
      ep => this.normalizeWashType(ep.washType) === 'single_wash'
    );
  }
  /** Monthly membership options for display (shown below single wash). */
  get monthlyPackages(): ExpandedPackage[] {
    return this.expandedPackages.filter(
      ep => this.normalizeWashType(ep.washType) === 'monthly_membership'
    );
  }

  /** Hide Fleet block when no accounts exist for the location (still show while loading). */
  get showOrderRequestFleetSection(): boolean {
    return this.isLoadingOrderRequestFleets || this.orderRequestFleets.length > 0;
  }

  /**
   * "Most used" strip: rows from `GET package/default-by-location/:id`,
   * resolved against loaded packages, add-ons, and prepaid books (same order as the default API).
   */
  mostUsedServiceItems: MostUsedServiceItem[] = [];
  /** Raw default packages for the current location (API order). */
  private defaultPackagesForMostUsed: Package[] = [];

  /** Load configured defaults and rebuild the shortcut strip (order-request layout only). */
  private loadMostUsedDefaultsForOrderRequest(locationId: string): void {
    if (!this.showOrderRequestMostUsedServicesSection) {
      this.defaultPackagesForMostUsed = [];
      this.mostUsedServiceItems = [];
      return;
    }
    const loc = String(locationId ?? '').trim();
    if (!loc) {
      this.defaultPackagesForMostUsed = [];
      this.rebuildMostUsedServiceItems();
      return;
    }
    if (!this.showOrderRequestCheckoutLayout()) {
      this.defaultPackagesForMostUsed = [];
      this.rebuildMostUsedServiceItems();
      return;
    }

    this.packageService
      .getDefaultPackagesByLocation(loc)
      .pipe(
        catchError(() => of({ status: false, data: [] } as ApiResponse<Package[]>)),
        takeUntil(this.destroy$)
      )
      .subscribe(pkgs => {
        this.defaultPackagesForMostUsed =
          pkgs?.status && Array.isArray(pkgs.data) ? pkgs.data.map(p => ({ ...p })) : [];
        this.rebuildMostUsedServiceItems();
      });
  }

  /** Recompute {@link mostUsedServiceItems} from default API ids + current catalogs. */
  private rebuildMostUsedServiceItems(): void {
    if (!this.showOrderRequestMostUsedServicesSection) {
      this.mostUsedServiceItems = [];
      return;
    }
    if (!this.showOrderRequestCheckoutLayout()) {
      this.mostUsedServiceItems = [];
      return;
    }
    const items: MostUsedServiceItem[] = [];

    for (const apiPkg of this.defaultPackagesForMostUsed) {
      const pkgId = String(apiPkg.id || '').trim();
      if (!pkgId) {
        continue;
      }
      const typeStr = String(apiPkg.type || '').toLowerCase();
      const washTypesLower = (apiPkg.wash_types || []).map(w => String(w).toLowerCase());
      const looksLikeAddOn =
        typeStr === 'add_on' ||
        typeStr === 'addon' ||
        washTypesLower.some(w => w.includes('add_on') || w.includes('addon'));
      const looksLikeDetailing =
        typeStr === 'detailing' || washTypesLower.some(w => w.includes('detailing'));

      if (looksLikeAddOn || looksLikeDetailing) {
        const addon = this.orderRequestAddons.find(
          a => a.apiPackageId != null && String(a.apiPackageId).trim() === pkgId
        );
        if (addon) {
          const isComplex =
            addon.mode === 'choose_products' || addon.mode === 'multi_variant_package';
          items.push({
            id: `mu_addon_${addon.id}`,
            label: addon.label,
            priceLabel: isComplex ? '' : this.getMostUsedAddonPriceLabel(addon),
            kind: 'addon',
            addonRef: addon,
          });
        }
        continue;
      }

      const expandedMatches = this.expandedPackages.filter(
        ep => String(ep.package.id || '').trim() === pkgId
      );
      if (expandedMatches.length) {
        for (const ep of expandedMatches) {
          const normalized = this.normalizeWashType(ep.washType);
          items.push({
            id: `mu_pkg_${ep.uniqueId}`,
            label: ep.displayName,
            priceLabel: this.formatExpandedPackageMostUsedPriceLabel(ep),
            kind: 'package',
            packageRef: ep,
          });
        }
        continue;
      }

      const prepaidBook =
        !this.isOrderRequestPrepaidRedeemFlow() &&
        this.orderRequestPrepaidBooks.find(
          b => String(b.name || '').trim() === String(apiPkg.package_name || '').trim()
        );
      if (prepaidBook) {
        const price = Number(prepaidBook.selling_price);
        items.push({
          id: `mu_pre_${prepaidBook.id}`,
          label: prepaidBook.name,
          priceLabel: Number.isFinite(price)
            ? this.formatUsdPriceForOrderRequestDisplay(String(price))
            : 'Bundle',
          kind: 'prepaid',
          prepaidBookId: prepaidBook.id,
        });
      }
    }

    this.mostUsedServiceItems = items;
  }

  private getMostUsedAddonPriceLabel(a: OrderRequestAddonItem): string {
    if (a.mode === 'fixed' && a.fixedPrice) {
      return this.formatUsdPriceForOrderRequestDisplay(a.fixedPrice) || String(a.fixedPrice);
    }
    if (a.mode === 'custom_price') {
      const saved = this.formatOrderRequestAddonSavedPrice(a.id);
      if (!saved) {
        return 'Custom price';
      }
      return this.formatUsdPriceForOrderRequestDisplay(saved) || saved;
    }
    return 'Add-on';
  }

  isMostUsedAddonSimpleMode(a: OrderRequestAddonItem): boolean {
    return a.mode === 'fixed' || a.mode === 'custom_price';
  }

  isMostUsedAddonComplexMode(a: OrderRequestAddonItem): boolean {
    return a.mode === 'choose_products' || a.mode === 'multi_variant_package';
  }

  /** Shown under the title when vending / multi-variant already has a selection. */
  getMostUsedAddonCommittedSummary(a: OrderRequestAddonItem): string | null {
    if (a.mode === 'choose_products') {
      if (this.getOrderRequestVendingCommittedQuantityFor(a.id) <= 0) {
        return null;
      }
      const t = this.orderRequestVendingSelections[a.id]?.totalFormatted?.trim();
      return t || 'Products selected';
    }
    if (a.mode === 'multi_variant_package' && a.packageVariants?.length) {
      let sum = 0;
      for (const v of a.packageVariants) {
        const q = this.getOrderRequestAddonVariantCommittedQty(v.id);
        if (q <= 0) {
          continue;
        }
        const unit =
          v.mode === 'custom_price'
            ? (this.parseUsdNumber(this.orderRequestAddonSavedCustomPrice[v.id]) ?? 0)
            : (v.unitPrice ?? 0);
        sum += unit * q;
      }
      return sum > 0 ? this.formatCheckoutCurrency(sum) : null;
    }
    return null;
  }

  onMostUsedServiceItemClick(item: MostUsedServiceItem, event?: Event): void {
    if (event?.type === 'click' && Date.now() - this.lastMostUsedPointerSelectAt < 300) {
      return;
    }
    this.closeTransientSearchDropdowns();
    if (item.kind === 'package' && item.packageRef) {
      this.onExpandedPackageSelect(item.packageRef);
    } else if (item.kind === 'prepaid' && item.prepaidBookId) {
      this.selectOrderRequestPrepaid(item.prepaidBookId, event);
    } else if (item.kind === 'addon' && item.addonRef) {
      if (this.isMostUsedServiceItemPairedDisabled(item)) {
        return;
      }
      this.toggleOrderRequestAddonSelection(item.addonRef, event);
    }
  }

  onMostUsedServiceItemPointerDown(item: MostUsedServiceItem, event: Event): void {
    if (this.isMostUsedInteractiveSubTarget(event.target)) {
      return;
    }
    if (this.isMostUsedServiceItemPairedDisabled(item)) {
      return;
    }
    this.lastMostUsedPointerSelectAt = Date.now();
    this.onMostUsedServiceItemClick(item, event);
  }

  private isMostUsedInteractiveSubTarget(target: EventTarget | null): boolean {
    const el = target instanceof Element ? target : null;
    return !!el?.closest('button, input, select, textarea, a, label');
  }

  private closeTransientSearchDropdowns(): void {
    this.showNameDropdown = false;
    this.showPhoneDropdown = false;
    this.showLicensePlateDropdown = false;
    this.licensePlateInputFocused = false;
  }

  isMostUsedServiceItemSelected(item: MostUsedServiceItem): boolean {
    if (item.kind === 'package' && item.packageRef) {
      return this.isExpandedPackageSelected(item.packageRef);
    }
    if (item.kind === 'prepaid' && item.prepaidBookId) {
      return this.isOrderRequestPrepaidSelected(item.prepaidBookId);
    }
    if (item.kind === 'addon' && item.addonRef) {
      const a = item.addonRef;
      if (a.mode === 'multi_variant_package' && a.packageVariants) {
        return a.packageVariants.some(v => this.isOrderRequestAddonVariantLineCommitted(v));
      }
      return this.isOrderRequestAddonSelected(a.id);
    }
    return false;
  }
  private lastMostUsedPointerSelectAt = 0;
  selectedPackage: Package | null = null;
  selectedWashType: string | null = null; // Store selected wash type from expanded package
  isLoadingPackages = false;
  /** A package load has finished (either way); until then an empty list is not an answer (CWS-1588). */
  packagesLoadSettled = false;
  /** The last package load errored, so an empty list means "could not load", not "none". */
  packagesLoadFailed = false;
  private packageCatalogLoadedLocationId: string | null = null;
  private orderRequestAddonCatalogLoadedLocationId: string | null = null;
  private orderRequestFleetCatalogLoadedLocationKey: string | null = null;
  /** Placeholder slots for order-request section card shimmer loaders. */
  readonly orderRequestSectionShimmerSlots = [0, 1, 2, 3] as const;

  // Wash type selection properties (deprecated - keeping for backward compatibility)
  availableWashTypes: string[] = [];
  showWashTypeDropdown = false;

  // Payment form
  paymentForm: PaymentForm = {
    method: this.getDefaultPaymentMethod(),
    phoneNumber: '',
    cardholderName: '',
    billingAddress: '',
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
    cashReceived: '',
    terminalReaderId: '',
    terminalCardType: 'credit',
  };

  terminalReaders: TerminalReader[] = [];
  terminalSettings: TerminalSettings | null = null;
  isLoadingTerminalCheckout = false;
  isTerminalReaderDropdownOpen = false;
  isTerminalCardTypeDropdownOpen = false;
  private terminalCheckoutLoadedLocationId: string | null = null;
  private terminalCheckoutLoadingLocationId: string | null = null;
  private terminalPaymentPollTimer: ReturnType<typeof setInterval> | null = null;
  private terminalPaymentPollStartedAt = 0;
  private static readonly TERMINAL_PAYMENT_POLL_MS = 2500;
  private static readonly TERMINAL_PAYMENT_POLL_TIMEOUT_MS = 120000;
  readonly terminalCardTypeOptions: { label: string; value: TerminalCardType }[] = [
    { label: 'Credit', value: 'credit' },
    { label: 'Debit', value: 'debit' },
  ];

  // Track processed payments to prevent duplicate toasts
  private processedPayments = new Set<string>();
  /** Increments on each Tap to Pay launch so failure toasts can show again after retry. */
  private tapToPayFailureDedupAttempt = 0;
  private tapToPayFailureToastKeys = new Set<string>();

  /**
   * Detect if device is mobile/tablet
   */
  get isMobileOrTablet(): boolean {
    // Check for user agent patterns
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

    // Check screen size (common mobile/tablet breakpoints)
    const isSmallScreen = window.matchMedia('(max-width: 1024px)').matches;

    // Check for touch with screen size consideration
    const isTouchWithSmallScreen =
      ('ontouchstart' in window || navigator.maxTouchPoints > 0) && isSmallScreen;

    // Check for specific mobile/tablet indicators
    const hasMobileUA = mobileRegex.test(userAgent);

    // Combine checks for better accuracy
    return hasMobileUA || isTouchWithSmallScreen;
  }

  /**
   * Get default payment method based on device type and subscription selection
   * Subscription selected + Desktop: card payment
   * Subscription selected + Mobile/Tablet: tap-to-pay (supports NFC)
   * No subscription + Mobile/Tablet: tap-to-pay (supports NFC)
   * No subscription + Desktop: cash
   */
  private getDefaultPaymentMethod(): BuiltInPaymentMethodCode {
    // Check if subscription is selected - use same logic as isCashPaymentDisabled()
    let isSubscription = false;

    // For create-payment-request notifications, check order wash_type from cached value or notification data
    if (this.notification?.type === 'create-payment-request') {
      // First check cached order wash_type
      if (this.cachedOrderWashType) {
        const normalized = this.normalizeWashType(this.cachedOrderWashType);
        if (normalized === 'monthly_membership') {
          isSubscription = true;
        }
      }

      // Check notification data for wash_type if not found in cache
      if (!isSubscription) {
        const orderWashType =
          this.notification?.data?.wash_type || this.notification?.data?.order?.wash_type || '';
        if (orderWashType) {
          const normalized = this.normalizeWashType(orderWashType);
          if (normalized === 'monthly_membership') {
            isSubscription = true;
          }
        }
      }
    }

    // Check selected wash type (for order creation flows)
    if (!isSubscription && this.selectedWashType) {
      const normalized = this.normalizeWashType(this.selectedWashType);
      if (normalized === 'monthly_membership') {
        isSubscription = true;
      }
    }

    // Check selected package wash types (for order creation flows)
    if (!isSubscription && this.selectedPackage?.wash_types) {
      const washTypes = this.selectedPackage.wash_types;
      // If only monthly_membership is available
      if (washTypes.length === 1 && this.normalizeWashType(washTypes[0]) === 'monthly_membership') {
        isSubscription = true;
      }
      // If both are available but monthly is selected
      if (washTypes.length === 2 && this.selectedWashType) {
        const normalized = this.normalizeWashType(this.selectedWashType);
        if (normalized === 'monthly_membership') {
          isSubscription = true;
        }
      }
    }

    // If subscription is selected and it's desktop, default to card payment
    if (isSubscription && !this.isMobileOrTablet) {
      return PaymentMethodCode.Card;
    }

    // Otherwise, use device-based default
    return this.isMobileOrTablet ? PaymentMethodCode.TapToPay : PaymentMethodCode.Cash;
  }

  private normalizePaymentMethodName(name: string | null | undefined): string {
    return String(name ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }

  private isBuiltInPaymentMethod(
    method: string | null | undefined
  ): method is BuiltInPaymentMethod {
    return isBuiltInPaymentMethodCode(method);
  }

  isDynamicPaymentMethod(method: string | null | undefined): boolean {
    return !!method && !this.isBuiltInPaymentMethod(method);
  }

  get filteredDynamicPaymentMethodOptions(): DynamicPaymentMethodOption[] {
    //for the force rebuild
    const currentWashType = this.getNormalizedWashType();
    const washTypeForFilter =
      currentWashType === 'prepaid_wash_book' ? 'single_wash' : currentWashType;
    return this.dynamicPaymentMethodOptions.filter(option =>
      option.washTypes.includes(washTypeForFilter)
    );
  }

  shouldShowMorePaymentMethodsToggle(): boolean {
    return this.filteredDynamicPaymentMethodOptions.length > 0;
  }

  isDynamicPaymentMethodSelected(methodName: string): boolean {
    return this.paymentForm.method === methodName;
  }

  private mapDynamicPaymentMethod(
    mode: NotificationPaymentModeApiItem
  ): DynamicPaymentMethodOption | null {
    const name = String(mode?.name ?? '').trim();
    if (!name) {
      return null;
    }

    const normalizedName = this.normalizePaymentMethodName(name);
    if (
      isBuiltInPaymentMethodCode(name) ||
      normalizePaymentMethodValue(name) === PaymentMethodCode.CardPresent ||
      normalizedName === 'cardpayment' ||
      normalizedName === 'paymentlink'
    ) {
      return null;
    }

    const washTypes = Array.isArray(mode?.wash_types)
      ? Array.from(
          new Set(
            mode.wash_types
              .map(washType => this.normalizeWashType(String(washType)))
              .filter(
                (washType): washType is 'single_wash' | 'monthly_membership' | 'add_on' =>
                  !!washType
              )
          )
        )
      : [];

    if (!washTypes.length) {
      return null;
    }

    return {
      id: String(mode.id || name),
      name,
      washTypes,
    };
  }

  private applyDynamicPaymentMethodsFromApiItems(modes: NotificationPaymentModeApiItem[]): void {
    const seen = new Set<string>();
    this.dynamicPaymentMethodOptions = modes
      .map(mode => this.mapDynamicPaymentMethod(mode))
      .filter((mode): mode is DynamicPaymentMethodOption => !!mode)
      .filter(mode => {
        const key = `${this.normalizePaymentMethodName(mode.name)}:${mode.washTypes.join('|')}`;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });

    if (
      this.paymentForm.method &&
      this.isDynamicPaymentMethod(this.paymentForm.method) &&
      !this.filteredDynamicPaymentMethodOptions.some(
        option => option.name === this.paymentForm.method
      )
    ) {
      this.paymentForm.method = this.getDefaultPaymentMethod();
    }
  }

  private loadDynamicPaymentMethods(): void {
    if (this.notification?.type !== 'create-payment-request') {
      this.dynamicPaymentMethodOptions = [];
      return;
    }

    if (this.orderRequestCatalogService.isPaymentModeLoadInFlight()) {
      return;
    }

    const cachedModes = this.orderRequestCatalogService.getCachedPaymentModes();
    if (cachedModes) {
      this.logger.info(
        '[NotificationModal] checkout payment modes from cache',
        {
          count: cachedModes.length,
          cacheKey: this.orderRequestCatalogService.paymentModeCacheKey(),
        },
        'NotificationModal'
      );
      this.applyDynamicPaymentMethodsFromApiItems(cachedModes);
      return;
    }

    this.orderRequestCatalogService.runPaymentModeLoad({
      onStart: () => {
        this.isLoadingDynamicPaymentMethods = true;
      },
      onSuccess: modes => {
        this.logger.info(
          '[NotificationModal] checkout payment modes from API',
          { count: modes.length, cacheKey: this.orderRequestCatalogService.paymentModeCacheKey() },
          'NotificationModal'
        );
        this.applyDynamicPaymentMethodsFromApiItems(modes);
      },
      onError: error => {
        this.dynamicPaymentMethodOptions = [];
        if (this.paymentForm.method && this.isDynamicPaymentMethod(this.paymentForm.method)) {
          this.paymentForm.method = this.getDefaultPaymentMethod();
        }
        this.logger.trackError('NotificationModal.loadDynamicPaymentMethods', error, {
          notificationType: this.notification?.type,
        });
      },
      onFinalize: () => {
        this.isLoadingDynamicPaymentMethods = false;
      },
    });
  }

  private reconcilePaymentMethodAfterContextChange(): void {
    if (this.paymentForm.method === PaymentMethodCode.Cash && this.isCashPaymentDisabled()) {
      this.paymentForm.method = this.getDefaultPaymentMethod();
      return;
    }

    // Without a connected Stripe account only Cash (or a custom method) can settle a single wash,
    // so move off a Stripe tile the defaults left selected — typically Tap to Pay on mobile.
    if (!this.isCashPaymentDisabled() && this.isSelectedStripeMethodUnavailable()) {
      this.paymentForm.method = PaymentMethodCode.Cash;
      return;
    }

    if (
      this.isDynamicPaymentMethod(this.paymentForm.method) &&
      !this.filteredDynamicPaymentMethodOptions.some(
        option => option.name === this.paymentForm.method
      )
    ) {
      this.paymentForm.method = this.getDefaultPaymentMethod();
    }
  }

  // Payment state
  isProcessingPayment = false;
  isCreatingPaymentLink = false;
  isConfirmingCashPayment = false;
  paymentLinkSent = false; // Track if payment link was sent successfully
  paymentExpired = false; // Track if payment link has expired
  showMorePaymentMethods = false; // Track if more payment methods section is expanded
  isLoadingDynamicPaymentMethods = false;
  dynamicPaymentMethodOptions: DynamicPaymentMethodOption[] = [];

  /** Collapsible checkout summary (create-payment-request; Figma 44-32053). */
  showCheckoutSummary = false;
  /** Expanded rows under “Additional Service” (Figma 124-29496). */
  checkoutAddonDetailsOpen = false;
  /** When false, discount is excluded from totals (Figma 124-29489). */
  checkoutSummaryDiscountSelected = true;
  /** When false, add-ons / additional services are excluded from totals (same pattern as discount). */
  checkoutSummaryAddonSelected = true;
  private isCheckoutSelectionSyncInFlight = false;
  private pendingCheckoutSelectionSyncScopes = new Set<'discount' | 'addon'>();
  private checkoutLastActiveDiscountId: string | null = null;
  private checkoutLastActiveDiscountAmount: number | null = null;
  private checkoutLastActiveAddOnVariants: CreateOrderAddOnVariantPayload[] = [];
  /** Figma cash panel preset denominations ($1 / $5 / $10 / $50 / $100). */
  readonly checkoutCashPresetAmounts: readonly number[] = [1, 5, 10, 50, 100];
  /** Per-denomination tap counts for cash preset chips. */
  checkoutCashPresetCounts: Record<number, number> = {};
  /**
   * Manual Dollars ($) draft — separate from denomination taps.
   * Cash received = preset total + this manual amount.
   */
  checkoutCashManualDollarsDraft = '';
  /** Manual Cents (¢) draft (0–99), separate from denomination taps. */
  checkoutCashManualCentsDraft = '';

  /**
   * Way+ checkout marketing: fixed count for banner, breakdown, and benefits modal (Figma 175-15228).
   * Package `features` length only controls whether the Way+ row is shown.
   */
  readonly checkoutWayPlusStaticBenefitsCount = 9;

  /** “Benefits Await You!” dialog (opened from checkout banner). */
  checkoutWayPlusBenefitsModalOpen = false;

  /** Static Way+ benefit list copy for the modal body. */
  readonly checkoutWayPlusBenefitModalItems: readonly { title: string; description: string }[] = [
    {
      title: 'Free Oil Change',
      description:
        'Earn credits and 2% off every oil change, and a free oil change after your 50th car wash purchase.',
    },
    {
      title: '$1000 AD&D Insurance',
      description: 'Accidental death & dismemberment coverage, valid for 30 days from the wash.',
    },
    {
      title: 'Gas Cashback up to 50¢ a Gallon',
      description:
        'Save up to 50¢ per gallon and get cashback on the Way app at over 60k gas stations across the country.',
    },
    {
      title: '10% off Parking',
      description: 'Enjoy an extra 10% off airport, event, or city parking.',
    },
    {
      title: 'Free 100-Point Inspection',
      description: 'Drop by any of our authorized car body shops for a free checkup anytime.',
    },
    {
      title: 'Free Mechanics Hotline',
      description:
        'Contact our expert mechanics for any car-related concerns as often as you need.',
    },
    {
      title: '24-Hour Roadside Assistance',
      description:
        'Access to real-time digital tracking as your call for help is sent to a network of 30k tow trucks. Charges may apply.',
    },
    {
      title: 'Free Mileage Expense Tracker',
      description: 'Track your mileage for tax deductions & reimbursements.',
    },
    {
      title: '10% off Auto Repairs',
      description: 'Save up to 10% on auto repairs.',
    },
  ];

  // Payment link timer properties
  paymentMinutes = 5;
  paymentSeconds = 0;
  private paymentTimerInterval: any;
  private stripeMountTimeoutId: any = null;

  paymentPhoneValidation: ValidationRule[] = [USA_PHONE_CUSTOM_INPUT_RULE];

  // Stripe Elements
  stripe: any;
  elements: any;
  cardNumberElement: any;
  cardExpiryElement: any;
  cardCvcElement: any;
  // Track Stripe Elements completion state
  stripeCardNumberComplete = false;
  stripeCardExpiryComplete = false;
  stripeCardCvcComplete = false;

  // Store last error messages for Stripe elements
  private stripeCardNumberError = '';
  private stripeCardExpiryError = '';
  private stripeCardCvcError = '';
  // Payment intent ID from order
  paymentIntentClientSecret = '';

  // Phone number search properties
  phoneSearchResults: any[] = [];
  showPhoneDropdown = false;
  isSearchingCustomers = false;
  private phoneSearchSubject = new Subject<string>();

  // Name search properties
  nameSearchResults: any[] = [];
  showNameDropdown = false;
  isSearchingCustomersByName = false;
  private nameSearchSubject = new Subject<string>();

  // Email search properties
  emailSearchResults: any[] = [];
  showEmailDropdown = false;
  isSearchingCustomersByEmail = false;
  private emailSearchSubject = new Subject<string>();

  // Location dropdown properties
  availableLocationsForDropdown: LocationDropdownOption[] = [];
  isLocationDropdownOpen = false;
  locationDropdownActiveIndex = -1;
  isLoadingLocationsForDropdown = false;

  // Lane dropdown (order-request UI image scan / header + when location has lanes)
  availableOrderRequestLanes: LocationDropdownOption[] = [];
  isLaneDropdownOpen = false;
  laneDropdownActiveIndex = -1;
  isLoadingOrderRequestLanes = false;
  private orderRequestLanesLoadToken = 0;
  /** The lanes request failed; a UI upload cannot be submitted without knowing whether a lane is needed. */
  private orderRequestLanesLoadFailed = false;

  // License plate search properties
  licensePlateSearchResult: any = null;
  showLicensePlateDropdown = false;
  isSearchingCustomerByLicensePlate = false;
  showVehicleFieldsForLicensePlate = false; // Show make/model/year when no customer found
  /** Pusher-refined plate image URL; takes precedence over notification payload getter. */
  private orderRequestLprAxisPlateImageUrlOverride: string | null = null;
  /** Preloaded URL shown in the plate preview (avoids progressive img paint). */
  private orderRequestLprAxisPlateImageLoadedUrl: string | null = null;
  private orderRequestLprAxisPlateImageLoadFailed = false;
  /** Vehicle preview URL that finally failed `<img>` load after all retries — slot is then hidden. */
  private orderRequestVehicleImageLoadFailedUrl: string | null = null;
  /** Vehicle preview URL confirmed loaded — resets the retry state when the URL changes. */
  private orderRequestVehicleImageLoadedUrl: string | null = null;
  /**
   * Remounts the vehicle `<img>` between retries so the browser re-fetches the same presigned S3
   * URL. We deliberately never append a cache-busting query param: lane images are presigned SigV4
   * URLs, so any extra query param invalidates the signature (→ 403). Remounting a fresh element
   * re-issues the request without a stale cached error and keeps the signature intact.
   */
  private orderRequestVehicleImageSrcMounted = true;
  private orderRequestVehicleImageLoadAttempt = 0;
  private orderRequestVehicleImageRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private orderRequestVehicleImageAttemptTimer: ReturnType<typeof setTimeout> | null = null;
  /** Once true, lane image URL/preload must not change until the modal closes. */
  private orderRequestLprAxisPlateImageLocked = false;
  private orderRequestLprAxisPlateImagePreloadTimer: ReturnType<typeof setTimeout> | null = null;
  /** UI image-scan: poll LPR entry when SSE images-ready may have been missed. */
  private orderRequestUiScanPlateReconcileTimer: ReturnType<typeof setTimeout> | null = null;
  private orderRequestUiScanPlateReconcileAttempt = 0;
  /** Remounts `<img>` between retries so the browser re-fetches the same presigned URL. */
  private orderRequestLprAxisPlateImageSrcMounted = true;
  private orderRequestLprAxisPlateImageLoadAttempt = 0;
  private orderRequestLprAxisPlateImageRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private orderRequestLprAxisPlateImageAttemptTimer: ReturnType<typeof setTimeout> | null = null;
  /** True while `loadNotificationFromApi` may still attach `plate_image_s3_url` to the payload. */
  private orderRequestLprAxisPlateImageApiHydrationPending = false;
  /**
   * After an in-place LPR correction (Pusher update), stop waiting on API/Pusher for lane images —
   * plate is already shown and background hydration should not keep spinners visible.
   */
  private orderRequestSuppressAxisImageAwaitingAfterLprUpdate = false;
  /** One LPR entry hydration per modal open — not on update events. */
  private lprEntriesLocationHydrationAttemptedForOpen = false;
  private lprEntriesLocationFetchInFlight = false;
  /** When true, by-id fetch failed — location list may run as fallback. */
  private lprEntryByIdFetchFailedForOpen = false;
  private static readonly ORDER_REQUEST_LPR_PLATE_IMAGE_LOAD_TIMEOUT_MS = 20_000;
  /** Poll LPR entry while awaiting webflow plate image if realtime notify was missed. */
  private static readonly ORDER_REQUEST_UI_SCAN_PLATE_RECONCILE_INTERVAL_MS = 2_000;
  private static readonly ORDER_REQUEST_UI_SCAN_PLATE_RECONCILE_MAX_ATTEMPTS = 6;
  /** Per-attempt timeout when a URL exists but `<img>` never fires load/error (slow CDN / S3). */
  private static readonly ORDER_REQUEST_LPR_PLATE_IMAGE_ATTEMPT_TIMEOUT_MS = 8_000;
  private static readonly ORDER_REQUEST_LPR_PLATE_IMAGE_MAX_LOAD_ATTEMPTS = 8;
  private static readonly ORDER_REQUEST_LPR_PLATE_IMAGE_RETRY_DELAYS_MS = [
    1_000, 1_500, 2_000, 2_500, 3_000, 4_000, 5_000, 5_000,
  ] as const;
  /** Per-attempt timeout when the vehicle URL exists but `<img>` never fires load/error. */
  private static readonly ORDER_REQUEST_VEHICLE_IMAGE_ATTEMPT_TIMEOUT_MS = 8_000;
  /** Total vehicle-image load attempts (initial + retries) before giving up and hiding the slot. */
  private static readonly ORDER_REQUEST_VEHICLE_IMAGE_MAX_LOAD_ATTEMPTS = 5;
  /** Backoff between vehicle-image retries; index clamps to the last entry. */
  private static readonly ORDER_REQUEST_VEHICLE_IMAGE_RETRY_DELAYS_MS = [
    1_000, 2_000, 4_000, 5_000,
  ] as const;
  /** While applying LPR plate correction (`updated_pusher` / axis entry updated). */
  isApplyingLprPlateCorrection = false;
  private licensePlateInputFocused = false;
  private licensePlateSearchSubject = new Subject<string>();
  /** Debounced PUT `order/lpr-entries/:id` with `plate_manually_edited` (once per vehicle). */
  private licensePlateManualLprPersistSubject = new Subject<void>();
  /** LPR entry id for which `plate_manually_edited` was already sent this modal session. */
  private lprManualPlateEditFlagSentForLprEntryId = '';
  /**
   * Customer fields as they were when an existing customer was linked to this order, from
   * either the plate lookup or the phone/name search. `persistLinkedCustomerDetailsIfChangedThen`
   * diffs the form against this on submit and PUTs the customer when anything changed.
   *
   * CWS-1880: this used to be set by the plate lookup only (hence its old name), so an
   * attendant who found the customer by phone and then typed in their email saw it silently
   * dropped — the order and the customer record both kept the old contact, and the prepaid
   * purchase email had no address to go to.
   */
  private linkedCustomerSnapshot: {
    customerId: string;
    firstName: string;
    lastName: string;
    email: string;
    contact: string;
  } | null = null;
  private isUpdatingLinkedCustomer = false;
  /**
   * CWS-1451: id of the looked-up customer when that customer is inactive. Keyed on the id so
   * picking another customer, or clearing the fields, lifts the block without extra resets.
   */
  private inactiveCustomerId = '';
  /** True while the inline "Reactivate customer" request is in flight. */
  isReactivatingCustomer = false;
  /**
   * CWS-1555: customer a membership barcode scan was refused for as inactive. Separate from
   * `inactiveCustomerId` because the scanned member need not be the customer on the form.
   */
  private vendorScanInactiveCustomerId = '';

  /** Payment realtime via shared SSE stream (`GET /api/v1/events/stream`). */
  private paymentSseSub: Subscription | null = null;
  private readonly realtimeEvents = inject(RealtimeEventsService);

  /** Cache order wash_type to avoid repeated API calls. */
  private cachedOrderWashType: string | null = null;

  /** Dedupes repeated `onTapToPayAndroidPaymentResult` / `taptopay_android` for the same order. */
  private readonly tapToPayNativeSuccessOrderIds = new Set<string>();
  /** When true, skip GET order/lpr-entries on open — notifications list/dropdown row already has the full payload. */
  private skipLprEntryApiHydrationFromNotificationsList = false;

  /** Dedupes repeated `PUT order/lpr-entries/:id` after payment for the same order. */
  private readonly lprEntryUpdatedAfterPaymentOrderIds = new Set<string>();
  /** Prevents duplicate concurrent post-payment LPR sync when success modal closes before GET order/:id returns. */
  private readonly lprEntryPaymentSyncInFlightOrderIds = new Set<string>();
  /** Dedupes `create-payment-request` LPR PUT per order (fires right after POST /order succeeds). */
  private readonly lprEntryCreatePaymentRequestSyncedOrderIds = new Set<string>();
  /** Wait for GET order/:id before PUT LPR entry so wash type (single vs monthly) is correct. */
  private deferLprEntrySyncUntilOrderLoaded = false;
  /** Order id for in-flight post-payment LPR sync (survives modal close until PUT completes). */
  private pendingPostPaymentLprSyncOrderId: string | null = null;
  private tapToPayAndroidJsCallback: ((detail: any) => void) | null = null;
  /** True only after this modal launches native Tap to Pay (ignore Android callbacks for order-details / add-order). */
  private tapToPayAwaitingNativeResult = false;
  private tapToPayAwaitingOrderId: string | null = null;
  paymentDoneOrderId: string | null = null;

  /**
   * A membership charge was accepted but the membership is not active yet: the backend answers
   * `membership_status: 'pending'` and only activates once Stripe's webhook lands. While this is
   * true a non-active order is EXPECTED and must not be reported as a failure.
   */
  private membershipActivationAwaitingWebhook = false;
  private membershipActivationWaitTimer: ReturnType<typeof setTimeout> | null = null;
  private membershipActivationPollAttempts = 0;

  showPaymentSuccess = false;
  successModalData: SuccessModalData = { ...PAYMENT_SUCCESS_MODAL_DEFAULTS };

  showPrepaidRedeemedSuccess = false;
  prepaidRedeemedSuccessData: PrepaidRedeemedSuccessDisplay = {
    packageLabel: '',
    washName: '',
    washType: '',
    purchaseDate: '',
    validTill: '',
  };
  /** CWS-1882: wording for the redeemed-success modal (fresh visit vs. reused visit). */
  prepaidRedeemedResult: PrepaidRedemptionResultDisplay = describePrepaidRedemptionResult(null);

  showVendorMembershipBarcodeScanner = false;
  /** True when staff chose "Or upload a barcode image" instead of live camera scan. */
  vendorMembershipScannerUploadOnly = false;
  /**
   * getUserMedia started from the "Scan by Camera" click so WebView can attach
   * camera permission to that user gesture.
   */
  vendorMembershipCameraStreamPromise: Promise<MediaStream> | null = null;
  /** CWS-1914: the prepaid redemption QR scanner opened from the redeem row. */
  showPrepaidRedemptionQrScanner = false;
  isPrepaidRedemptionQrProcessing = false;
  prepaidRedemptionQrCameraStreamPromise: Promise<MediaStream> | null = null;
  prepaidRedemptionQrError = '';

  /**
   * CWS-1947 follow-up: the redemption-code fallback, as the navbar scan has had since
   * CWS-1908.
   *
   * A QR that cannot be read, or that the lookup refuses, is not the end of the road --
   * the customer also has the code in their SMS or email. Before this, those cases wrote
   * `prepaidRedemptionQrError` and the template never rendered it, so the scanner simply
   * closed and nothing at all appeared on screen.
   */
  isPrepaidCodeDialogOpen = false;
  prepaidCodeIntro = '';
  prepaidCodeError = '';
  prepaidCodeBusy = false;
  isVendorMembershipLookupLoading = false;
  /** CWS-1038: stacked above barcode/device-scan overlays (z-index 12000 in shared component). */
  showVendorMembershipVehicleLinkConfirm = false;
  vendorMembershipVehicleLinkConfirmData: ConfirmationModalData = {
    title: '',
    description: '',
    cancelText: 'Cancel',
    confirmText: 'Confirm',
    showCancelButton: true,
    showConfirmButton: true,
  };
  private pendingVendorMembershipVehicleLinkRedeem: {
    sourceId: string;
    origin: VendorMembershipScanOrigin;
    confirmAction: 'link' | 'replace';
  } | null = null;
  showVendorActiveSubscriptionLookupModal = false;
  vendorActiveSubscriptionLookupDisplay: VendorActiveSubscriptionDisplay | null = null;
  /**
   * Imported vendor membership: staff is only saving a card on file ($0 today).
   * Do not open the "Payment Successful" wash-complete overlay.
   */
  private importedVendorCardOnFileCheckout = false;
  showVendorUpgradePlanPicker = false;
  isLoadingVendorUpgradePlans = false;
  /** CWS-1426: Update Membership state on the Active subscription modal. */
  showVendorPlanChange = false;
  vendorPlanChangeAvailable = false;
  /** CWS-1780: cancel & refund is on at the lane's location, for the membership shown. */
  vendorCancelAvailable = false;
  /** CWS-1581: the API says this membership can start a family. The API re-checks. */
  vendorStartFamilyAvailable = false;
  vendorPlanChangeNotice: { title: string; body: string } | null = null;
  vendorPlanChangeUpcoming: string | null = null;
  private vendorPlanChangeAvailabilityKey: string | null = null;

  // Order completed modal state
  orderCompletedModalTitle = 'Payment Successful!';
  orderCompletedCustomerName = '';
  orderCompletedVehicleNumber = '';
  orderCompletedVehicleMake = '';
  orderCompletedVehicleModel = '';
  orderCompletedVehicleYear = '';
  orderCompletedPackageName = '';

  // Subscription suggestion message
  subscriptionSuggestionMessage = '';

  /**
   * CWS-1266: attendant-triggered live vendor (DRB) lookup offered on a refused
   * card. The gate no longer asks the vendor itself; this button does.
   */
  vendorMembershipLookupOffer: VendorMembershipLookupOffer | null = null;
  isVendorMembershipLookupRunning = false;
  vendorMembershipLookupResult: VendorLookupResultText | null = null;
  /**
   * Only an expired DRB member's card drops the Redeem Existing Membership section:
   * barcode / scan-by-camera redeem add nothing there, the banner's Refresh button is
   * the one action. Every other card (no membership row, unknown plate) keeps the
   * section exactly as before, with the lookup button as an extra option.
   * Recomputed whenever a card's offer is resolved and cleared with the card's state.
   */
  vendorLookupReplacesVerifySection = false;

  private static readonly SUBSCRIPTION_EXPIRED_BACKEND_MESSAGE =
    'Subscription expired. Please create a new order.';
  private static readonly MEMBERSHIP_EXPIRED_GREETING =
    "Membership for this customer has expired. Please collect the customer's payment details to process the renewal now.";
  private static readonly MEMBERSHIP_EXPIRING_GREETING =
    "Membership for this customer is expiring. Please collect the customer's payment details to process the renewal now.";

  /** When set, shows the blue order insight block (create-order-request), backed by vehicle history API. */
  orderRequestTodayBanner: OrderRequestTodayBannerState | null = null;
  orderRequestRewashEligibility: RewashEligibilityState | null = null;
  orderRequestMembershipRewashEligibility: MembershipRewashEligibilityState | null = null;
  /** CWS-1882: rewash candidates for prepaid visits, which have no order of their own. */
  orderRequestPrepaidRewashEligibility: PrepaidRewashEligibilityState | null = null;
  /** Target selected from history header/row Rewash CTA for the reason modal. */
  orderRequestRewashTarget: OrderRequestRewashTarget | null = null;
  orderRequestHistoryExpanded = false;
  isLoadingOrderRequestHistoryPage = false;
  private orderRequestHistoryContextKey: string | null = null;
  private orderRequestHistoryBuildInFlightKey: string | null = null;
  /** Session omit for the redemption that opened the Active subscription history panel. */
  private vendorActiveSubscriptionOmitWashId: string | null = null;

  /** Inline editor for add-ons (custom price / vending). */
  orderRequestAddonExpandedId: string | null = null;
  private orderRequestAddonCustomPriceDraft: Record<string, string> = {};
  orderRequestAddonSavedCustomPrice: Record<string, string> = {};
  /** Single-wash package row with `allows_custom_price`: custom list price UI state. */
  orderRequestSingleWashCustomExpandedUniqueId: string | null = null;
  private orderRequestSingleWashCustomPriceDraft: Record<string, string> = {};
  orderRequestSingleWashSavedCustomPrice: Record<string, string> = {};
  orderRequestVendingDialogOpen = false;
  orderRequestRewashReasonModalOpen = false;
  /** Multi-variant add-on: dialog context (package row). */
  orderRequestAddonVariantsDialog: OrderRequestAddonItem | null = null;
  /** Draft qty per variant line while the multi-variant dialog is open (+ / −). */
  orderRequestAddonVariantsDialogQty: Record<string, number> = {};
  /** Checkbox state per variant line in the multi-variant dialog (only checked lines commit). */
  orderRequestAddonVariantsDialogChecked: Record<string, boolean> = {};
  /** Expanded “Enter amount” row for `custom_price` variant lines inside the multi-variant dialog. */
  orderRequestAddonVariantDialogCustomExpandedId: string | null = null;
  /** Checkbox state per vending row (only checked lines commit on Continue). */
  orderRequestVendingDialogChecked: Record<string, boolean> = {};
  /** Expanded custom price editor for a vending catalog row. */
  orderRequestVendingCustomExpandedItemId: string | null = null;
  /** Committed qty per variant id after Done (same pattern as vending). */
  orderRequestAddonVariantQuantities: Record<string, number> = {};
  orderRequestVendingItems: OrderRequestVendingItem[] = [];
  /** Which add-on row (`choose_products`) the vending dialog is editing. */
  orderRequestVendingDialogAddonId: string | null = null;
  /** Committed product-picker carts keyed by add-on row id (after Continue). */
  orderRequestVendingSelections: Record<
    string,
    { lines: OrderRequestVendingSelectionLine[]; totalFormatted: string }
  > = {};

  /** Custom discount editor (Figma: name, value, type, attendant reason). */
  orderRequestCustomDiscountExpanded = false;
  orderRequestCustomDiscountName = '';
  orderRequestCustomDiscountValueDraft = '';
  orderRequestCustomDiscountType: 'fixed_amount' | 'percentage' = 'fixed_amount';
  /** Attendant-only required reason; empty for Admin / other roles. */
  orderRequestCustomDiscountReason = '';
  orderRequestCustomDiscountReasonError: string | null = null;
  orderRequestCustomDiscountSaved: {
    name: string;
    valueLabel: string;
    typeKey: 'fixed_amount' | 'percentage';
    /** UUID from `discounts` once persisted for `orders.discount_id`. */
    id?: string;
    /** Attendant custom-discount reason when provided. */
    reason?: string;
  } | null = null;

  readonly orderRequestCustomDiscountTypeOptions: {
    value: 'fixed_amount' | 'percentage';
    label: string;
  }[] = [
    { value: 'fixed_amount', label: 'Flat' },
    { value: 'percentage', label: 'Percentage' },
  ];

  readonly orderRequestCustomDiscountReasonOptions = CUSTOM_DISCOUNT_REASON_OPTIONS;

  /** Active prepaid wash books for order-request Services (catalog). */
  orderRequestPrepaidBooks: PrepaidWashBook[] = [];
  isLoadingOrderRequestPrepaid = false;
  /** The last prepaid catalog fetch errored, so an empty list means "could not load", not "none". */
  orderRequestPrepaidLoadFailed = false;
  /**
   * "Services" tab, most-used strip, and `getDefaultPackagesByLocation` / `getDefaultFleets` calls.
   * Set to `true` to show that UI and run those APIs again (logic remains in this component).
   */
  readonly showOrderRequestMostUsedServicesSection = false;
  /** Fleets for create-order-request, from `GET /fleet` (same as Fleet module). */
  orderRequestFleets: { id: string; label: string }[] = [];
  isLoadingOrderRequestFleets = false;
  /** Add-on + detailing tiles for create-order-request, loaded per location. */
  orderRequestAddons: OrderRequestAddonItem[] = [];
  isLoadingOrderRequestAddons = false;
  /** Discount tiles: API rows from `package/discounts` + static Custom discount. */
  orderRequestDiscounts: OrderRequestDiscountItem[] = [ORDER_REQUEST_CUSTOM_DISCOUNT];
  isLoadingOrderRequestDiscounts = false;

  /**
   * Hover text for the Discounts label. Only two discount tiles are fetched, so without
   * this an attendant cannot tell whether the location has more.
   */
  get orderRequestDiscountCountTooltip(): string | null {
    const total = this.orderRequestCatalogService.getDiscountTotalForLocation(
      String(this.formData?.locationId ?? '').trim()
    );
    if (total === null) {
      return null;
    }
    const noun = total === 1 ? 'discount' : 'discounts';
    return `${total} ${noun} available for this location`;
  }
  /** Single-select prepaid wash book tile (book id). */
  orderRequestSelectedPrepaidBookId: string | null = null;
  /** Existing prepaid purchase id from customer/by-license-plate. */
  prepaidPurchaseId: string | null = null;
  /** Identity of the last prepaid QR/LPR notification that ran bootstrap enrichment (see getPrepaidRedeemBootstrapKey). */
  private prepaidRedeemBootstrapKey: string | null = null;
  /**
   * Bumps to remount order-request layout after first async settle (history + prepaid).
   * Live SSE first-open otherwise freezes the loading snapshot; close/reopen remounts cleanly.
   */
  orderRequestLayoutPaintKey = 0;
  private orderRequestLayoutAwaitingFirstAsyncSettle = false;
  private orderRequestUiSettleTimer: ReturnType<typeof setTimeout> | null = null;
  private orderRequestUiSettleAttempts = 0;
  prepaidPurchaseStatus: PrepaidPurchaseStatus | null = null;
  isLoadingPrepaidPurchaseStatus = false;
  prepaidPurchaseStatusError: string | null = null;
  prepaidRedemptions: PrepaidPurchaseRedemption[] = [];
  isLoadingPrepaidRedemptions = false;
  prepaidRedemptionsError: string | null = null;
  prepaidRedemptionsLoaded = false;
  /** Selected redemption slots (`purchaseItemId#slotIndex`), multi-select. */
  orderRequestRedemptionSlotKeys: string[] = [];
  /** Redeemer contact/vehicle (defaults from purchaser; may differ for family sharing). */
  prepaidRedemptionRecipient: PrepaidRedemptionRecipientForm = emptyPrepaidRedemptionRecipient();
  prepaidRedemptionRecipientTouched = false;
  /** Redeemer-pane phone lookup (does not change booking purchaser fields). */
  recipientPhoneSearchResults: Record<string, any>[] = [];
  showRecipientPhoneDropdown = false;
  isSearchingRecipientByPhone = false;
  private recipientPhoneSearchSubject = new Subject<string>();
  /** Immutable contact baseline captured at redeem bootstrap (billing contact overwrite detection). */
  prepaidRedemptionPrefilledSnapshot: PrepaidRedemptionPurchaserSnapshot | null = null;
  /** Single-select fleet tile (fleet id). */
  orderRequestSelectedFleetId: string | null = null;
  /** Multi-select in Add-on tiles. */
  orderRequestSelectedAddonIds = new Set<string>();
  /** Single-select in Discounts tiles. */
  orderRequestSelectedDiscountId: string | null = null;
  /** Memo for `orderRequestVisibleDiscounts`; see that getter. */
  private orderRequestVisibleDiscountCache: {
    source: OrderRequestDiscountItem[] | null;
    washType: string | null;
    packageId: string | null;
    customerHistory: OrderRequestCustomerHistory | null;
    orderAmount: number | null;
    timeZone: string;
    result: OrderRequestDiscountItem[];
  } = {
    source: null,
    washType: null,
    packageId: null,
    customerHistory: null,
    orderAmount: null,
    timeZone: '',
    result: [],
  };
  /**
   * Audience bucket for this plate, from svc-order. Null until the lookup answers, which
   * the tile filter reads as first time — the same default the server applies when plate
   * is unknown.
   */
  private orderRequestCustomerHistory: OrderRequestCustomerHistory | null = null;
  /**
   * Set when staff deselect the wash package, so the tiles stop falling back to the wash
   * type the order was loaded with.
   *
   * cachedOrderWashType describes the order on file, not the current selection, and it
   * also drives payment-method choices — so it has to survive a deselect. Without this
   * flag it would keep answering for the discount tiles and they would never hide.
   */
  private orderRequestWashSelectionCleared = false;
  /**
   * Plate the cached history belongs to, so the lookup runs once per plate.
   *
   * Starts `undefined` rather than null because null is a real value here — it is what a
   * missing plate resolves to — and a null sentinel would make the first lookup look like
   * a repeat and skip it.
   */
  private orderRequestCustomerHistoryFor: string | null | undefined = undefined;
  isLoadingCustomerOrders = false;

  /**
   * After GET /order when returning from payment → edit, re-apply add-ons/discount once
   * add-on + fleet + discount catalogs have finished loading.
   */
  private pendingCreatePaymentOrderRequestUiRestore = false;
  private lastOrderFetchPayloadForOrderRequestRestore: any | null = null;
  private isHydratingCreatePaymentCheckout = false;
  private createPaymentCheckoutHydratedOrderId: string | null = null;

  private modalOpenTime = 0;
  private paymentStartTime = 0;
  private orderCreateStartTime = 0;

  /** Debounced field logging to BetterStack (avoids flooding on every keystroke). */
  private static readonly FIELD_LOG_DEBOUNCE_MS = 400;
  private readonly modalLogSource = 'NotificationModal';
  private fieldInteractionLogTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly fullStoryService = inject(FullStoryService);
  private readonly vendorModeService = inject(VendorModeService);
  private readonly multiEligibleModalState = inject(MultiEligibleSelectionModalStateService);
  private readonly multiEligibleConfirmApi = inject(MultiEligibleConfirmApiService);
  private readonly wayPurchaseApi = inject(WayPurchaseApiService);
  private readonly planChangeSettings = inject(PlanChangeSettingsService);
  private readonly familySaleService = inject(FamilySaleService);
  private readonly cancellationSettings = inject(CancellationSettingsService);
  private readonly customerSubscriptions = inject(CustomerSubscriptionService);

  constructor(
    private modalStateService: ModalStateService,
    private orderCreationService: OrderCreationService,
    private packageService: PackageService,
    private paymentService: PaymentService,
    private messageService: NotificationService,
    private apiService: ApiService,
    private authService: AuthService,
    private customerService: CustomerService,
    private vehicleService: VehicleService,
    private http: HttpClient,
    private ordersService: OrdersService,
    private locationService: LocationService,
    private router: Router,
    private roleService: RoleService,
    private employeeService: EmployeeService,
    private notificationService: NotificationService,
    private logger: LoggerService,
    private fleetService: FleetService,
    private prepaidWashBookService: PrepaidWashBookService,
    private ngZone: NgZone,
    private appRef: ApplicationRef,
    private tapToPayAndroidNative: TapToPayAndroidNativeService,
    private cdr: ChangeDetectorRef,
    @Inject(NotificationOrderRequestHistoryService) private orderRequestHistoryService: any,
    @Inject(NotificationOrderRequestCatalogService) private orderRequestCatalogService: any,
    @Inject(NotificationOrderRequestWayLookupService) private orderRequestWayLookupService: any,
    private pdfService: PdfService,
    private orderReceiptService: OrderReceiptService,
    private terminalSettingsService: TerminalSettingsService,
    private receiptPrinterService: ReceiptPrinterService,
    private gateService: GateService
  ) {}

  ngOnInit(): void {
    console.log('opening model');
    this.vendorModeService.enabledVendorSources$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cdr.markForCheck());
    this.modalStateService.modalState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: ModalState) => {
        // Lane SSE opens the modal from outside NgZone; keep the open/reset path inside zone.
        this.ngZone.run(() => this.applyModalState(state));
      });

    // Setup phone number search with debounce
    this.phoneSearchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(phoneNumber => {
        this.logModalInteraction('search.phone', { query: phoneNumber });
        this.triggerOrderRequestWayLookup({ phone: phoneNumber });
        this.searchCustomersByPhone(phoneNumber);
      });

    this.recipientPhoneSearchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((phoneNumber: string) => {
        this.searchRecipientCustomerByPhone(phoneNumber);
      });

    // Setup name search with debounce
    this.nameSearchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(name => {
      this.logModalInteraction('search.name', { query: name });
      this.searchCustomersByName(name);
    });

    // Setup email search with debounce
    this.emailSearchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(email => {
      this.logModalInteraction('search.email', { query: email });
      this.triggerOrderRequestWayLookup({ email });
      this.searchCustomersByEmail(email);
    });

    // Setup license plate search with debounce
    this.licensePlateSearchSubject
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(licensePlate => {
        this.logModalInteraction('search.licensePlate', { licensePlate });
        this.triggerOrderRequestWayLookup({ plate: licensePlate });
        this.searchCustomerByLicensePlate(licensePlate);
      });

    this.licensePlateManualLprPersistSubject
      .pipe(debounceTime(600), takeUntil(this.destroy$))
      .subscribe(() => this.persistLprEntryManualPlateEdit());

    this.notificationService.lprAxisImagesReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload: LprAxisImagesReadyEvent) => {
        this.ngZone.run(() => this.applyLprAxisImagesReadyPayload(payload));
      });

    this.notificationService.lprAxisEntryUpdated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payload: LprAxisEntryUpdatedEvent) => {
        this.ngZone.run(() => this.applyLprAxisEntryUpdatedPayload(payload));
      });

    this.notificationService.lprEntryUpdatedNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: LprEntryUpdatedNotification) => {
        this.ngZone.run(() => this.applyLprEntryUpdatedNotification(event));
      });

    this.installTapToPayAndroidBridge();
  }

  ngOnDestroy(): void {
    this.removeTapToPayAndroidBridge();
    this.fieldInteractionLogTimers.forEach(t => clearTimeout(t));
    this.fieldInteractionLogTimers.clear();
    this.cancelStripeMountRetries();
    this.unmountStripeElements();
    this.unsubscribeFromOrderChannel();
    this.clearPaymentTimer();
    this.clearMembershipActivationWait();
    this.clearOrderRequestLprAxisPlateImagePreloadTimer();
    this.clearOrderRequestUiScanPlateReconcileTimer();
    this.clearOrderRequestLprAxisPlateImageRetryTimers();
    this.clearOrderRequestVehicleImageRetryTimers();
    this.stopTerminalPaymentStatusPoll();

    this.tapToPayNativeSuccessOrderIds.clear();
    this.tapToPayFailureToastKeys.clear();
    this.clearTapToPayAwaitingNativeResult();

    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Remote (BetterStack) log: one interaction + full serializable modal snapshot. */
  private logModalInteraction(action: string, detail?: Record<string, any>): void {
    this.logger.info(
      `[NotificationModal] ${action}`,
      { action, ...detail, snapshot: this.getModalSnapshot() },
      this.modalLogSource
    );
  }

  /**
   * Apply modal open/close/replace from ModalStateService.
   * Always invoked inside NgZone (SSE fetch loop is outside the zone).
   */
  private applyModalState(state: ModalState): void {
    const wasOpen = this.isOpen;
    const previousNotification = this.notification;
    this.isOpen = state.isOpen;
    this.notification = state.notification;

    if (state.isOpen && !wasOpen) {
      this.modalOpenTime = Date.now();
      this.skipLprEntryApiHydrationFromNotificationsList =
        this.shouldSkipLprEntryApiHydrationFromNotificationsList(
          state.notification,
          state.shouldStore
        );
      this.orderRequestLprAxisPlateImageUrlOverride = null;
      this.orderRequestLprAxisPlateImageApiHydrationPending = false;
      this.orderRequestSuppressAxisImageAwaitingAfterLprUpdate = false;
      this.clearOrderRequestUiScanPlateReconcileTimer();
      this.resetOrderRequestLprAxisPlateImageLoadState();
      this.isApplyingLprPlateCorrection = false;
      this.packageCatalogLoadedLocationId = null;
      this.orderRequestLayoutAwaitingFirstAsyncSettle = true;
      this.scheduleOrderRequestUiSettleLoop();
      // Keep add-on/fleet caches across modal opens to avoid repeated catalog API calls.
      if (
        state.notification?.type === 'create-order-request' &&
        !(state.notification.data as { isBuyPackageFlow?: boolean })?.isBuyPackageFlow
      ) {
        this.clearOrderRequestWashPackageSelection();
      }
      this.logModalInteraction('modal.open', {
        notificationType: state.notification?.type,
        shouldStore: state.shouldStore,
      });
    }
    if (!state.isOpen && wasOpen) {
      this.clearOrderRequestUiSettleLoop();
      this.orderRequestLayoutAwaitingFirstAsyncSettle = false;
      this.orderRequestLprAxisPlateImageUrlOverride = null;
      this.orderRequestLprAxisPlateImageApiHydrationPending = false;
      this.orderRequestSuppressAxisImageAwaitingAfterLprUpdate = false;
      this.deferLprEntrySyncUntilOrderLoaded = false;
      this.orderRequestRewashReasonModalOpen = false;
      this.orderRequestRewashTarget = null;
      if (!this.pendingPostPaymentLprSyncOrderId) {
        this.paymentDoneOrderId = null;
      }
      this.clearTapToPayAwaitingNativeResult();
      this.lprEntriesLocationHydrationAttemptedForOpen = false;
      this.lprEntriesLocationFetchInFlight = false;
      this.lprEntryByIdFetchFailedForOpen = false;
      this.resetOrderRequestLprAxisPlateImageLoadState();
      this.isApplyingLprPlateCorrection = false;
      this.lprManualPlateEditFlagSentForLprEntryId = '';
      this.linkedVehicleAtSelection = null;
      this.scannedPlateKeyForImages = null;
      this.prepaidRedeemBootstrapKey = null;
      this.clearOrderRequestHistoryState();
      this.clearOrderRequestCatalogState();
      this.orderRequestWayLookupService.clearStoredWayPurchase();
      this.logModalInteraction('modal.close', {
        notificationType: this.notification?.type,
        openDurationMs: this.modalOpenTime ? Date.now() - this.modalOpenTime : undefined,
      });
      this.showPaymentSuccess = false;
    }

    const shouldResetFormForModalState =
      !(state.isOpen && wasOpen) ||
      this.shouldResetFormOnInPlaceModalStateChange(state, previousNotification);

    if (!shouldResetFormForModalState) {
      // Live LPR can patch an already-open modal without form reset; still bootstrap
      // prepaid redeem so status/catalog are not stuck on loading forever.
      if (state.isOpen && state.notification) {
        this.maybeBootstrapPrepaidRedeemFromNotification(state.notification);
      }
      this.refreshOrderRequestAsyncUi();
      return;
    }

    // Clear search results when notification changes
    // Close the full-vehicle image preview so it never carries over to a replaced
    // notification (in-place swap keeps isOpen true and would otherwise leave it open).
    this.orderRequestVehicleImagePreviewOpen = false;
    this.orderRequestMembershipShopDeviceWaiting = false;
    this.licensePlateSearchResult = null;
    this.showLicensePlateDropdown = false;
    this.licensePlateInputFocused = false;
    this.showVehicleFieldsForLicensePlate = false;
    this.phoneSearchResults = [];
    this.showPhoneDropdown = false;
    this.nameSearchResults = [];
    this.showNameDropdown = false;
    this.emailSearchResults = [];
    this.showEmailDropdown = false;
    this.isLocationDropdownOpen = false;

    // Reset edit mode when notification changes
    this.isEditMode = false;
    this.originalValues = {
      packageId: '',
      customerFirstName: '',
      customerLastName: '',
      customerEmail: '',
      customerContact: '',
      vehicleLicensePlate: '',
      vehicleLicensePlateState: DEFAULT_LICENSE_PLATE_STATE,
      vehicleVin: '',
      vehicleId: '',
    };

    // Reset loading states only when modal is newly opened (not on every state emission).
    // This keeps the Checkout button disabled during card payment until the success modal is closed.
    if (state.isOpen && !wasOpen) {
      this.isUpdatingOrder = false;
      this.resetCheckoutClaimState();
      this.isConfirmingCashPayment = false;
      this.isProcessingPayment = false;
      this.isCreatingPaymentLink = false;
      this.createPaymentCheckoutHydratedOrderId = null;
      // Allow location list + default selection to run fresh for each modal open.
      this.availableLocationsForDropdown = [];
      this.licensePlateCustomerSearchInFlightKey = null;
      this.licensePlateCustomerSearchCompletedKey = '';
      this.lastVehiclePlateLookupAttempted = '';
      this.plateLookupInFlightPlate = null;
      this.clearVehicleLookupSuccessCache();
      this.resetCustomerSearchSubjects();
    }

    // IMMEDIATELY capture notificationId when notification is received (before any other operations).
    // It is sent as lpr_entry_id on order create, so it must belong to this notification: on a
    // fresh open, never carry the previous car's entry over (its entry is usually already linked
    // and svc-order rejects the order with "LPR entry could not be linked to order").
    if (state.isOpen && state.notification) {
      const notificationId = this.isDirectOrder()
        ? ''
        : resolveLprEntryIdFromNotificationData(
            state.notification.data as Record<string, any> | undefined
          );
      if (notificationId || !wasOpen) {
        this.formData.notificationId = notificationId;
      }
    }

    // Store notification in localStorage when modal is opened with notification (only if shouldStore is true)
    // The service method handles license plate validation internally
    if (state.isOpen && state.notification && state.shouldStore !== false) {
      this.modalStateService.storeNotificationInLocalStorage(state.notification);
    }

    // Populate from notification first so package/location APIs see the right formData.
    // Location options load async; populate must not run after sync only to wipe the default selection.
    this.populateFormData();

    if (state.isOpen && state.notification) {
      this.maybeBootstrapPrepaidRedeemFromNotification(state.notification);
    }

    if (state.isOpen) {
      if (
        !wasOpen &&
        state.notification &&
        this.shouldHydrateViewOnlyOrderSuccessOnOpen(state.notification)
      ) {
        this.hydrateViewOnlyOrderSuccessFromOrderApi(state.notification);
      }

      // Group D view-only (active-subscription, order-success, detailing-appointment-found) only display
      // info — they never need package catalog, location lists, add-ons or discounts.
      if (!this.isViewOnlyNotification()) {
        // Payment-only checkout does not need package / add-on / discount / LPR-list APIs.
        this.loadCarWashPackages();
        this.ensureOrderRequestLocationSource();
      }
      // Hydrate thin LPR rows once when the modal opens — not on update/Pusher events.
      if (
        !wasOpen &&
        state.notification &&
        this.shouldRefreshNotificationFromLprEntriesApi(state.notification) &&
        !this.skipLprEntryApiHydrationFromNotificationsList
      ) {
        this.loadNotificationFromApi(state.notification);
      }
      this.refreshOrderRequestAsyncUi();
    }
  }

  /**
   * While the modal stays open, ignore live lane SSE swaps that would rerun populateFormData
   * and wipe in-progress staff input. Explicit user/system transitions still reset the form.
   */
  private shouldResetFormOnInPlaceModalStateChange(
    state: ModalState,
    previousNotification: NotificationData | null
  ): boolean {
    const data = (state.notification?.data ?? {}) as Record<string, any>;
    // A CWS-1945 draft has no order id yet but still needs the checkout setup (payment form,
    // terminal readers) that a saved order got here before Continue replaced Save Changes.
    if (
      state.notification?.type === 'create-payment-request' &&
      data['open_checkout_summary'] === true &&
      (!!(data['order_id'] || data['orderId']) || this.checkoutDraftPayload !== null)
    ) {
      return true;
    }
    if (data['quick_plus_order'] === true || data['isBuyPackageFlow'] === true) {
      return true;
    }
    // CWS-1730: a new header scan replaces the open popup ('replace'). Another car's entry
    // must repopulate the form, or the popup keeps the previous car's customer, vehicle,
    // history and membership sentence with only the plate swapped in.
    if (
      isManualLprCaptureNotification(state.notification) &&
      this.didNotificationIdentityChange(previousNotification, state.notification)
    ) {
      return true;
    }
    // Live prepaid redeem uses shouldStore:true; without a reset, replace-onto-open
    // skips populateFormData and leaves history/prepaid UI stuck on loading.
    const nextIsPrepaidRedeem = isPrepaidRedeemLprNotification(state.notification?.type, data);
    if (nextIsPrepaidRedeem) {
      const prevData = (previousNotification?.data ?? {}) as Record<string, any>;
      const prevIsPrepaidRedeem = isPrepaidRedeemLprNotification(
        previousNotification?.type,
        prevData
      );
      if (
        !prevIsPrepaidRedeem ||
        this.didNotificationIdentityChange(previousNotification, state.notification)
      ) {
        return true;
      }
    }
    if (
      state.shouldStore === false &&
      this.didNotificationIdentityChange(previousNotification, state.notification)
    ) {
      return true;
    }
    return false;
  }

  private didNotificationIdentityChange(
    previousNotification: NotificationData | null,
    nextNotification: NotificationData | null
  ): boolean {
    if (!previousNotification || !nextNotification) {
      return true;
    }
    if (previousNotification.type !== nextNotification.type) {
      return true;
    }
    const prevId = resolveLprEntryIdFromNotificationData(
      previousNotification.data as Record<string, any> | undefined
    );
    const nextId = resolveLprEntryIdFromNotificationData(
      nextNotification.data as Record<string, any> | undefined
    );
    if (!prevId || !nextId) {
      return prevId !== nextId;
    }
    return prevId !== nextId;
  }

  private getModalSnapshot(): Record<string, any> {
    const nd = this.notification?.data as Record<string, any> | undefined;
    const notificationSummary = this.notification
      ? {
          type: this.notification.type,
          id: nd?.['id'] ?? nd?.['licence_plate_id'] ?? nd?.['license_plate_id'],
          plate: nd?.['plate'] ?? nd?.['vehicle_license_plate'],
          order_id: nd?.['order_id'] ?? nd?.['orderId'],
          location_id: nd?.['location_id'] ?? nd?.['locationId'],
        }
      : null;

    return {
      isOpen: this.isOpen,
      notificationSummary,
      formData: { ...this.formData },
      paymentForm: this.getPaymentFormForLog(),
      expandedPackageCount: this.expandedPackages?.length ?? 0,
      selectedPackageId: this.selectedPackage?.id ?? null,
      selectedPackageName: this.selectedPackage?.package_name ?? null,
      selectedWashType: this.selectedWashType,
      isEditMode: this.isEditMode,
      userInteractedFields: Array.from(this.userInteractedFields),
      flags: {
        isUpdatingOrder: this.isUpdatingOrder,
        isConfirmingCashPayment: this.isConfirmingCashPayment,
        isProcessingPayment: this.isProcessingPayment,
        isCreatingPaymentLink: this.isCreatingPaymentLink,
        isFetchingOrderData: this.isFetchingOrderData,
        isLoadingPackages: this.isLoadingPackages,
      },
      ui: {
        showMorePaymentMethods: this.showMorePaymentMethods,
        paymentLinkSent: this.paymentLinkSent,
        paymentExpired: this.paymentExpired,
        showPaymentSuccess: this.showPaymentSuccess,
        showLicensePlateDropdown: this.showLicensePlateDropdown,
        showPhoneDropdown: this.showPhoneDropdown,
        showNameDropdown: this.showNameDropdown,
        licensePlateInputFocused: this.licensePlateInputFocused,
      },
      paymentTracking: {
        paymentDoneOrderId: this.paymentDoneOrderId,
      },
      validation: {
        phoneValidationError: this.phoneValidationError,
        emailValidationError: this.emailValidationError,
        updateOrderError: this.updateOrderError,
      },
      timingMsSinceModalOpen: this.modalOpenTime ? Date.now() - this.modalOpenTime : undefined,
    };
  }

  /** Omit raw card / CVC strings; lengths are enough for debugging. */
  private getPaymentFormForLog(): Record<string, any> {
    const p = this.paymentForm;
    return {
      method: p.method,
      phoneNumber: p.phoneNumber,
      cardholderName: p.cardholderName,
      billingAddress: p.billingAddress,
      cardNumberLen: p.cardNumber?.length ?? 0,
      cardExpiry: p.cardExpiry,
      cardCvcLen: p.cardCvc?.length ?? 0,
      cashReceived: p.cashReceived,
    };
  }

  private scheduleFieldInputLog(fieldName: string, value: string): void {
    const prev = this.fieldInteractionLogTimers.get(fieldName);
    if (prev) clearTimeout(prev);
    const tid = setTimeout(() => {
      this.fieldInteractionLogTimers.delete(fieldName);
      const preview = value.length > 200 ? `${value.slice(0, 200)}…` : value;
      this.logModalInteraction('field.input', {
        field: fieldName,
        valueLength: value.length,
        valuePreview: preview,
      });
    }, NotificationModalComponent.FIELD_LOG_DEBOUNCE_MS);
    this.fieldInteractionLogTimers.set(fieldName, tid);
  }

  private unmountStripeElements(): void {
    try {
      if (this.cardNumberElement) {
        this.cardNumberElement.unmount();
      }
      if (this.cardExpiryElement) {
        this.cardExpiryElement.unmount();
      }
      if (this.cardCvcElement) {
        this.cardCvcElement.unmount();
      }
    } catch (error) {
      console.error('Error unmounting Stripe elements:', error);
    }
  }

  /** X icon — on New Order Request, persist LPR draft first; other screens close as before. */
  onOrderRequestCloseClick(): void {
    if (!this.isUpdatingOrder) {
      this.persistLprEntryOnOrderRequestDismiss('close-icon');
    }
    this.onClose();
  }

  onClose(): void {
    this.logModalInteraction('interaction.dismiss', {
      trigger: 'onClose',
      notificationType: this.notification?.type,
      orderId: this.formData.orderId || undefined,
      openDurationMs: this.modalOpenTime ? Date.now() - this.modalOpenTime : undefined,
    });
    // Cancel pending Stripe mount retries and tear down active resources so
    // they don't keep running after the modal is hidden.
    this.cancelStripeMountRetries();
    this.clearPaymentTimer();
    this.unmountStripeElements();
    this.unsubscribeFromOrderChannel();
    this.modalStateService.hideModal();
    this.locationPickerOriginMemory = null;
    this.checkoutWayPlusBenefitsModalOpen = false;
    this.orderRequestRewashReasonModalOpen = false;
    this.orderRequestRewashTarget = null;
    this.paymentIntentClientSecret = '';
    // Reset payment/loading state so next time modal opens buttons are not stuck
    this.isProcessingPayment = false;
    this.resetCheckoutClaimState();
    // Clear validation errors so they don't bleed into the next modal open
    this.phoneValidationError = '';
    this.emailValidationError = '';
    this.licensePlateValidationError = '';
    this.linkedCustomerSnapshot = null;
    this.orderRequestVehicleImagePreviewOpen = false;
    this.orderRequestMembershipShopDeviceWaiting = false;
    this.resetFamilySale();
  }

  onOverlayClick(event: Event): void {
    // Only close if clicking directly on the overlay, not on the modal content
    const close = event.target === event.currentTarget;
    this.logModalInteraction('interaction.overlay.click', { closesModal: close });
    if (close) {
      if (!this.isUpdatingOrder) {
        this.persistLprEntryOnOrderRequestDismiss('close-icon');
      }
      this.onClose();
    }
  }

  /**
   * `create-payment-request` without edit mode is checkout / payment only — skip package catalog
   * (`view-by-location`, `add-ons-by-location`, `discounts`, fleets, most-used).
   */
  private shouldLoadPackageCatalogApis(): boolean {
    // View-only notification types never use the package catalog, locations,
    // discounts, add-ons or fleets — skip all of those calls.
    if (this.isViewOnlyNotification()) return false;
    if (this.isOrderRequestPrepaidRedeemContext()) return false;
    return !(
      this.notification?.type === 'create-payment-request' &&
      !this.isEditMode &&
      this.hasCheckoutOrderId()
    );
  }

  private isDirectOrder(): boolean {
    return (
      this.notification?.type === 'create-order-request' &&
      this.notification?.data?.quick_plus_order === true
    );
  }

  /**
   * Returns true for notification types that only display information and
   * do not require any order-creation catalog (packages, add-ons, discounts,
   * locations, fleets, most-used defaults).
   */
  /** Group D — view-only dismiss (Got it): no order creation in modal. */
  private isViewOnlyNotification(): boolean {
    const viewOnlyTypes = [
      'active-subscription',
      'order-success',
      'detailing-appointment-found',
    ] as const;
    return viewOnlyTypes.includes(this.notification?.type as any);
  }

  /**
   * LPR hydration on modal open uses GET order/lpr-entries/:id when possible;
   * location list is only a fallback. Payment checkout uses GET order/:id instead.
   */
  private shouldRefreshNotificationFromLprEntriesApi(notification: NotificationData): boolean {
    // View-only notifications (order-success, etc.) have no LPR entry to refresh.
    if (this.isViewOnlyNotification()) return false;
    if (
      notification.type === 'create-order-request' &&
      notification.data?.quick_plus_order === true
    )
      return false;
    if (notification.type === 'create-payment-request') {
      const d = notification.data as Record<string, any> | undefined;
      return !String(d?.['order_id'] ?? d?.['orderId'] ?? '').trim();
    }
    return true;
  }

  loadCarWashPackages(): void {
    if (!this.shouldLoadPackageCatalogApis()) {
      this.logModalInteraction('packages.load.skip', { reason: 'create_payment_checkout_only' });
      return;
    }

    this.logModalInteraction('packages.load.start');
    this.isLoadingPackages = this.expandedPackages.length === 0;
    console.log('🔄 Loading car wash packages...');

    const currentUser = this.authService.user();
    // Prefer the order-request location dropdown (formData.locationId) over session/header
    // location so admin "+" creates catalog + submit against the shop they selected.
    const locationId =
      this.formData.locationId ||
      this.notification?.data?.location_id ||
      this.notification?.data?.locationId ||
      currentUser?.location;

    if (locationId) {
      this.loadPackagesByLocationId(locationId);
      return;
    }

    // Manual "new order" / staff without user.location: resolve from employee profile or locations API
    this.resolveLocationIdForPackageLoad()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: resolvedId => {
          if (resolvedId) {
            if (!this.formData.locationId) {
              this.formData.locationId = resolvedId;
            }
            this.loadPackagesByLocationId(resolvedId);
          } else {
            console.warn('⚠️ No location ID could be resolved for package load');
            this.packages = [];
            this.expandedPackages = [];
            this.isLoadingPackages = false;
          }
        },
        error: err => {
          console.error('❌ Error resolving location for packages:', err);
          this.packages = [];
          this.expandedPackages = [];
          this.isLoadingPackages = false;
          this.packagesLoadSettled = true;
          this.packagesLoadFailed = true;
        },
      });
  }

  /**
   * Staff often have location on employee profile but not on auth user; admins use first active location.
   */
  private resolveLocationIdForPackageLoad(): Observable<string | null> {
    const userRole = this.roleService.getCurrentUserRole();
    if (userRole === UserRole.ATTENDANT || userRole === UserRole.ATTENDANT_PLUS) {
      return this.employeeService.getCurrentEmployeeProfile().pipe(
        map((response: any) =>
          response?.status && response?.data?.location_id ? response.data.location_id : null
        ),
        catchError(() => of(null))
      );
    }
    return this.pickFirstLocationIdFromLocationsApi();
  }

  private pickFirstLocationIdFromLocationsApi(): Observable<string | null> {
    return this.locationService.getLocations().pipe(
      map((response: any) => {
        if (response?.status && Array.isArray(response.data) && response.data.length > 0) {
          const list = response.data as any[];
          const active = list.filter((loc: any) => loc.status === true);
          const pick = active.length > 0 ? active[0] : list[0];
          return pick?.id ?? null;
        }
        return null;
      }),
      catchError(() => of(null))
    );
  }

  private loadVendorUpgradePlanCatalog(): void {
    const currentUser = this.authService.user();
    const locationId = String(
      this.formData.locationId ||
        this.notification?.data?.location_id ||
        this.notification?.data?.locationId ||
        currentUser?.location ||
        ''
    ).trim();
    if (!locationId) {
      this.resolveLocationIdForPackageLoad()
        .pipe(takeUntil(this.destroy$))
        .subscribe(resolvedId => {
          if (resolvedId) {
            this.loadVendorUpgradePlanCatalogForLocation(resolvedId);
          }
        });
      return;
    }
    this.loadVendorUpgradePlanCatalogForLocation(locationId);
  }

  private loadVendorUpgradePlanCatalogForLocation(locationId: string): void {
    const loc = String(locationId || '').trim();
    if (!loc) {
      return;
    }
    this.isLoadingVendorUpgradePlans = this.monthlyPackages.length === 0;
    this.loadOrderRequestDiscounts(loc);
    this.orderRequestCatalogService.runPackageLoad(loc, {
      onStart: () => {
        if (this.monthlyPackages.length === 0) {
          this.isLoadingVendorUpgradePlans = true;
        }
      },
      onSuccess: packages => {
        this.packageCatalogLoadedLocationId = loc;
        this.packages = packages;
        this.expandedPackages = expandOrderRequestPackages(packages);
      },
      onError: error => {
        console.error('❌ Error loading upgrade plan packages:', error);
        this.packages = [];
        this.expandedPackages = [];
      },
      onFinalize: () => {
        this.isLoadingVendorUpgradePlans = false;
      },
    });
  }

  private loadPackagesByLocationId(locationId: string): void {
    if (!this.shouldLoadPackageCatalogApis()) {
      return;
    }

    const loc = String(locationId || '').trim();
    if (!loc) {
      return;
    }
    if (this.orderRequestCatalogService.isPackageLoadInFlight(loc)) {
      this.ensureOrderRequestAddonsAndFleetsLoaded(loc);
      return;
    }
    if (this.packageCatalogLoadedLocationId === loc && this.expandedPackages.length > 0) {
      this.ensureOrderRequestAddonsAndFleetsLoaded(loc);
      // Always refresh discounts (force-refetch inside loadOrderRequestDiscounts) so a warm
      // package catalog does not leave Attendant sessions on a stale discount list.
      if (!this.isLoadingOrderRequestDiscounts) {
        this.loadOrderRequestDiscounts(loc);
      }
      if (!this.orderRequestPrepaidBooks.length && !this.isLoadingOrderRequestPrepaid) {
        this.loadOrderRequestPrepaidBooks(loc);
      }
      this.loadMostUsedDefaultsForOrderRequest(loc);
      this.applyPackageSelectionAfterCatalogLoad();
      return;
    }

    const cachedPackages = this.orderRequestCatalogService.getCachedPackages(loc);
    if (cachedPackages?.length && this.expandedPackages.length === 0) {
      this.packageCatalogLoadedLocationId = loc;
      this.packages = cachedPackages;
      this.expandedPackages = this.expandPackages(this.packages);
      this.applyPackageSelectionAfterCatalogLoad();
    }

    this.isLoadingPackages = this.expandedPackages.length === 0;
    this.packagesLoadSettled = false;
    this.packagesLoadFailed = false;
    this.loadAddOnsByLocationId(loc);
    this.loadOrderRequestFleets(loc);
    this.loadOrderRequestDiscounts(loc);
    this.loadOrderRequestPrepaidBooks(loc);
    this.loadMostUsedDefaultsForOrderRequest(loc);

    this.orderRequestCatalogService.runPackageLoad(loc, {
      onStart: () => {
        if (this.expandedPackages.length === 0) {
          this.isLoadingPackages = true;
        }
      },
      onSuccess: packages => {
        this.packageCatalogLoadedLocationId = loc;
        this.packages = packages;
        this.expandedPackages = this.expandPackages(this.packages);
        this.applyPackageSelectionAfterCatalogLoad();
      },
      onError: error => {
        console.error('❌ Error loading car wash packages:', error);
        this.packageCatalogLoadedLocationId = null;
        this.packages = [];
        this.expandedPackages = [];
        this.packagesLoadFailed = true;
      },
      onFinalize: () => {
        this.isLoadingPackages = false;
        this.packagesLoadSettled = true;
        this.rebuildMostUsedServiceItems();
        this.tryApplyPendingCreatePaymentOrderRequestUiRestore();
      },
    });
  }

  /** Safe CSS background for a wash package card; omit accent when unset. */
  themeColorForPackage(pkg: Package | null | undefined): string | null {
    return normalizePackageThemeColor(pkg?.theme_color);
  }

  /** Safe CSS background for a prepaid wash book card; default theme-1 when unset (DB default). */
  themeColorForPrepaid(book: PrepaidWashBook | null | undefined): string | null {
    return normalizePackageThemeColor(book?.theme_color || 'theme-1');
  }

  themeColorForMostUsedServiceItem(item: MostUsedServiceItem): string | null {
    if (item.kind === 'package') {
      return this.themeColorForPackage(item.packageRef?.package);
    }
    if (item.kind === 'prepaid' && item.prepaidBookId) {
      const book = this.orderRequestPrepaidBooks.find(b => b.id === item.prepaidBookId);
      return this.themeColorForPrepaid(book);
    }
    return null;
  }

  /** Whether a resolved/safe theme background needs the light-contrast scrim. */
  isLightTheme(themeColor: string | null | undefined): boolean {
    return isLightThemeBackground(themeColor);
  }

  private getOrderRequestCatalogLocationId(): string | null {
    const loc = String(
      this.formData.locationId ||
        this.notification?.data?.location_id ||
        this.notification?.data?.locationId ||
        ''
    ).trim();
    return loc || null;
  }

  /** Same fallbacks as order submit (staff dropdown, branch name, notification). */
  private getOrderRequestWayLookupLocationId(): string | null {
    const resolved = this.resolveLocationIdForSubmission();
    if (resolved) {
      return resolved;
    }
    // Header "+": do not use session/header location — admin must use the form dropdown.
    if (this.isDirectOrder()) {
      return null;
    }
    const fromAuth = String(this.authService.user()?.location ?? '').trim();
    return fromAuth || null;
  }

  /**
   * Vendor barcode verify uses POST vendor-subscription-redeem — not Way lookup.
   * Suppress parallel way-purchase-lookup while scanning/redeeming or for plate auto-search
   * when the DRB "Verify membership source" block is active.
   */
  private shouldSkipOrderRequestWayLookup(params: {
    plate?: string;
    phone?: string;
    email?: string;
  }): boolean {
    if (this.showVendorMembershipBarcodeScanner || this.isVendorMembershipLookupLoading) {
      return true;
    }
    if (params.plate !== undefined && this.showOrderRequestMembershipVerifySection()) {
      return true;
    }
    return false;
  }

  private triggerOrderRequestWayLookup(params: {
    plate?: string;
    phone?: string;
    email?: string;
  }): void {
    if (!this.showOrderRequestCheckoutLayout()) {
      return;
    }

    if (this.shouldSkipOrderRequestWayLookup(params)) {
      return;
    }

    // LPR unredeemable → create-order handoff already showed Way; populate/location retries
    // must not look up the same plate again and re-open the unredeemable modal.
    if (this.shouldSkipWayLookupAfterUnredeemableHandoff(params)) {
      return;
    }

    const plate = params.plate?.trim();
    const phone = params.phone?.trim();
    const email = params.email?.trim();
    const locationId = this.getOrderRequestWayLookupLocationId();
    const wayPurchaseLookupEnabled = this.isWayPurchaseLookupEnabledForLocation(locationId);
    const parentContext = this.buildOrderRequestWayLookupParentContext();
    const plateHintState =
      typeof parentContext['state'] === 'string' ? parentContext['state'] : null;
    const normalizePlateForWayLookup = (raw: string): string => {
      // Same as Way lookup body: strip only the selected/parent state prefix.
      const { license_plate } = splitLicensePlateForApi(raw, plateHintState);
      return (license_plate || raw).trim().toUpperCase();
    };

    this.orderRequestWayLookupService.lookupFromOrderRequest({
      locationId,
      wayPurchaseLookupEnabled,
      plate,
      phone,
      email,
      parentContext,
      isStale: () => {
        if (String(this.getOrderRequestWayLookupLocationId() ?? '') !== String(locationId ?? '')) {
          return true;
        }
        if (plate !== undefined) {
          // Must match Way lookup dedupe key (stripped plate_number), not raw form text.
          return (
            normalizePlateForWayLookup(this.formData.vehicleLicensePlate?.trim() ?? '') !==
            normalizePlateForWayLookup(plate)
          );
        }
        if (phone !== undefined) {
          return (this.formData.customerContact?.trim() ?? '') !== phone;
        }
        if (email !== undefined) {
          return (this.formData.customerEmail?.trim() ?? '') !== email;
        }
        return true;
      },
    });
  }

  /** Backend-advertised Way lookup capability for the current create-order location. */
  private isWayPurchaseLookupEnabledForLocation(locationId: string | null): boolean {
    const id = String(locationId ?? '').trim();
    if (!id) {
      return false;
    }
    const fromDropdown = this.availableLocationsForDropdown.find(o => o.id === id);
    if (fromDropdown && typeof fromDropdown.wayPurchaseLookupEnabled === 'boolean') {
      return fromDropdown.wayPurchaseLookupEnabled;
    }
    const fromCache = this.locationService.getLocationById(id);
    if (fromCache && typeof fromCache.way_purchase_lookup_enabled === 'boolean') {
      return isWayPurchaseLookupEnabled(fromCache);
    }
    return false;
  }

  /**
   * After Way unredeemable dismiss → create-order, skip auto Way lookup until the attendant
   * edits plate/phone/email (then clear the handoff flag so a new identity can look up).
   */
  private shouldSkipWayLookupAfterUnredeemableHandoff(params: {
    plate?: string;
    phone?: string;
    email?: string;
  }): boolean {
    const data = this.notification?.data as Record<string, any> | undefined;
    if (!data || data['way_unredeemable_handoff'] !== true) {
      return false;
    }

    const userTriggered =
      (params.plate !== undefined && this.userInteractedFields.has('vehicleLicensePlate')) ||
      (params.phone !== undefined && this.userInteractedFields.has('customerContact')) ||
      (params.email !== undefined && this.userInteractedFields.has('customerEmail'));

    if (!userTriggered) {
      return true;
    }

    delete data['way_unredeemable_handoff'];
    return false;
  }

  /** Re-run Way lookup after async location resolution when staff already typed plate/phone/email. */
  private retryOrderRequestWayLookupFromFormFields(): void {
    if (!this.showOrderRequestCheckoutLayout()) {
      return;
    }
    const plate = (this.formData.vehicleLicensePlate ?? '').trim();
    if (plate.length >= 3) {
      this.triggerOrderRequestWayLookup({ plate });
      return;
    }
    const phone = (this.formData.customerContact ?? '').trim();
    if (phone.replace(/\D/g, '').length === CUSTOMER_PHONE_MAX_DIGITS) {
      this.triggerOrderRequestWayLookup({ phone });
      return;
    }
    const email = (this.formData.customerEmail ?? '').trim();
    if (email.length >= 3 && email.includes('@')) {
      this.triggerOrderRequestWayLookup({ email });
    }
  }

  get orderRequestWayPurchaseBanner$() {
    return this.orderRequestWayLookupService.showWayPurchaseReopenBanner$;
  }

  openOrderRequestWayPurchaseDetails(): void {
    this.logModalInteraction('wayPurchase.reopenBanner');
    this.orderRequestWayLookupService.reopenStoredWayPurchase();
  }

  private getOrderRequestFleetCatalogLocationKey(locationId?: string): string {
    return this.orderRequestCatalogService.fleetCatalogLocationKey(
      locationId ?? this.formData.locationId
    );
  }

  /**
   * Keep addon/fleet tiles populated across repeated populateFormData calls for the same location.
   */
  private syncOrderRequestCatalogForCurrentLocation(): void {
    const loc = this.getOrderRequestCatalogLocationId();
    if (!loc) {
      return;
    }
    this.ensureOrderRequestAddonsAndFleetsLoaded(loc);
  }

  private ensureOrderRequestAddonsAndFleetsLoaded(
    locationId: string,
    options?: { force?: boolean }
  ): void {
    const loc = String(locationId || '').trim();
    if (!loc) {
      return;
    }
    if (!options?.force) {
      this.tryRestoreOrderRequestAddonsFromCache(loc);
      this.tryRestoreOrderRequestFleetsFromCache(loc);
    }
    const fleetKey = this.getOrderRequestFleetCatalogLocationKey(loc);
    const needsAddons =
      !!options?.force ||
      this.orderRequestAddons.length === 0 ||
      this.orderRequestAddonCatalogLoadedLocationId !== loc;
    const needsFleets =
      !!options?.force ||
      this.orderRequestFleets.length === 0 ||
      this.orderRequestFleetCatalogLoadedLocationKey !== fleetKey;
    if (needsAddons) {
      this.loadAddOnsByLocationId(loc);
    }
    if (needsFleets) {
      this.loadOrderRequestFleets(loc);
    }
  }

  private tryRestoreOrderRequestAddonsFromCache(loc: string): boolean {
    const cachedRaw = this.orderRequestCatalogService.getCachedAddons(loc);
    if (!isValidAddOnPackagesCache(cachedRaw) || this.orderRequestAddons.length > 0) {
      return false;
    }
    const mapped = mapAddOnPackagesToOrderItems(cachedRaw);
    if (!mapped.length) {
      return false;
    }
    this.orderRequestAddons = mapped;
    this.orderRequestAddonCatalogLoadedLocationId = loc;
    this.rebuildMostUsedServiceItems();
    this.tryApplyPendingCreatePaymentOrderRequestUiRestore();
    this.restoreCheckoutAddonSelectionFromOrderSnapshot();
    this.syncOrderRequestAddonSelectionToPairedWith();
    return true;
  }

  private tryRestoreOrderRequestFleetsFromCache(loc: string): boolean {
    const fleetKey = this.getOrderRequestFleetCatalogLocationKey(loc);
    const cachedFleets = this.orderRequestCatalogService.getCachedFleets(fleetKey);
    if (!cachedFleets || this.orderRequestFleets.length > 0) {
      return false;
    }
    this.orderRequestFleets = cachedFleets;
    this.orderRequestFleetCatalogLoadedLocationKey = fleetKey;
    this.applyFleetVehicleSelectionFromNotification();
    this.rebuildMostUsedServiceItems();
    this.tryApplyPendingCreatePaymentOrderRequestUiRestore();
    this.syncOrderRequestAddonSelectionToPairedWith();
    return true;
  }

  private flushOrderRequestCatalogRetry(): void {
    const loc = this.orderRequestCatalogService.takeCatalogRetryLocationId();
    if (!loc) {
      return;
    }
    this.ensureOrderRequestAddonsAndFleetsLoaded(loc);
  }

  private clearOrderRequestCatalogState(): void {
    this.orderRequestAddons = [];
    this.orderRequestFleets = [];
    this.orderRequestDiscounts = [ORDER_REQUEST_CUSTOM_DISCOUNT];
    this.packages = [];
    this.expandedPackages = [];
    this.packageCatalogLoadedLocationId = null;
    this.orderRequestAddonCatalogLoadedLocationId = null;
    this.orderRequestFleetCatalogLoadedLocationKey = null;
    this.isLoadingOrderRequestAddons = false;
    this.isLoadingOrderRequestFleets = false;
    this.isLoadingOrderRequestDiscounts = false;
    this.isLoadingPackages = false;
    this.packagesLoadSettled = false;
    this.packagesLoadFailed = false;
    // Retain package/addon/fleet service caches across modal opens (admin + staff).
    // Discounts are force-refetched on each loadOrderRequestDiscounts so other sessions
    // (e.g. Attendant) are not stuck on a same-SPA cache that only clears on local CRUD.
  }

  /** Load add-on packages for create-order-request checkout (location-scoped). */
  private loadAddOnsByLocationId(locationId: string): void {
    const loc = String(locationId || '').trim();
    if (!loc) {
      return;
    }
    if (this.orderRequestCatalogService.isAddonLoadInFlight(loc)) {
      this.orderRequestCatalogService.scheduleCatalogRetry(loc);
      return;
    }
    if (
      this.orderRequestAddonCatalogLoadedLocationId === loc &&
      this.orderRequestAddons.length > 0
    ) {
      return;
    }
    if (this.tryRestoreOrderRequestAddonsFromCache(loc)) {
      return;
    }

    this.orderRequestCatalogService.runAddonLoad(loc, {
      onStart: () => {
        this.isLoadingOrderRequestAddons = true;
      },
      onSuccess: raw => {
        this.orderRequestAddonCatalogLoadedLocationId = loc;
        this.orderRequestAddons = mapAddOnPackagesToOrderItems(raw);
        this.rebuildMostUsedServiceItems();
      },
      onError: err => {
        console.error('Error loading add-ons by location:', err);
        this.orderRequestAddons = [];
        this.rebuildMostUsedServiceItems();
      },
      onFinalize: () => {
        this.isLoadingOrderRequestAddons = false;
        if (this.orderRequestAddons.length === 0) {
          this.tryRestoreOrderRequestAddonsFromCache(loc);
        }
        this.tryApplyPendingCreatePaymentOrderRequestUiRestore();
        this.restoreCheckoutAddonSelectionFromOrderSnapshot();
        this.syncOrderRequestAddonSelectionToPairedWith();
        this.flushOrderRequestCatalogRetry();
      },
    });
  }

  /** Load active prepaid wash books for order-request Services section. */
  private loadOrderRequestPrepaidBooks(locationId?: string): void {
    if (
      !this.showOrderRequestCheckoutLayout() &&
      this.notification?.type !== 'fleet-vehicle-found'
    ) {
      return;
    }
    const loc = String(locationId ?? this.formData.locationId ?? '').trim();
    this.isLoadingOrderRequestPrepaid = true;
    this.orderRequestPrepaidLoadFailed = false;
    this.prepaidWashBookService
      .getPrepaidWashBooks({
        status: true,
        page: 1,
        limit: 50,
        ...(loc ? { location_id: loc } : {}),
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingOrderRequestPrepaid = false;
          this.rebuildMostUsedServiceItems();
          this.tryApplyPendingCreatePaymentOrderRequestUiRestore();
          this.refreshOrderRequestAsyncUi();
        })
      )
      .subscribe({
        next: ({ books }) => {
          const allBooks = books ?? [];
          if (
            this.isOrderRequestPrepaidRedeemContext() &&
            !this.isPrepaidPurchaseConsumedForUi() &&
            this.prepaidPurchaseStatus?.prepaid_wash_book_id
          ) {
            const matched = allBooks.filter(
              book => book.id === this.prepaidPurchaseStatus?.prepaid_wash_book_id
            );
            // A scan came here to redeem one named book, so the row narrows to it; the
            // fallback keeps the catalog rather than a blank row when the location filter
            // does not carry that book. A looked-up plate keeps the whole catalog: owning one
            // book is no reason to stop selling the others.
            this.orderRequestPrepaidBooks =
              matched.length > 0 ? matched : this.buildRedeemContextPrepaidBooksFallback(allBooks);
          } else {
            this.orderRequestPrepaidBooks = allBooks;
          }
        },
        error: err => {
          console.error('Error loading prepaid wash books for order request:', err);
          this.orderRequestPrepaidLoadFailed = true;
          this.orderRequestPrepaidBooks = this.buildRedeemContextPrepaidBooksFallback([]);
        },
      });
  }

  /**
   * When location catalog omits the purchase's wash book, still show a tile from status
   * so Redeem Prepaid Purchase is not stuck on "No active prepaid wash books".
   */
  private buildRedeemContextPrepaidBooksFallback(
    catalogBooks: PrepaidWashBook[]
  ): PrepaidWashBook[] {
    const status = this.prepaidPurchaseStatus;
    const bookId = String(status?.prepaid_wash_book_id || '').trim();
    if (!bookId || this.isPrepaidPurchaseConsumedForUi()) {
      return catalogBooks;
    }
    const fromCatalog = catalogBooks.find(book => book.id === bookId);
    if (fromCatalog) {
      return [fromCatalog];
    }
    return [
      {
        id: bookId,
        user_id: '',
        slug: String(status?.book_slug || ''),
        name: String(status?.book_name || 'Prepaid Washbook').trim() || 'Prepaid Washbook',
        status: true,
        selling_price: '0',
        theme_color: null,
        number_of_purchased: 0,
        total_redeemed: '0',
        created_at: '',
        updated_at: '',
        items: [],
      },
    ];
  }

  /** Refresh async order-request UI when live SSE/HTTP finishes outside a click CD cycle. */
  private refreshOrderRequestAsyncUi(): void {
    const paint = () => {
      if (!this.isOpen) {
        return;
      }
      this.tryRemountOrderRequestLayoutAfterAsyncSettle();
      this.cdr.markForCheck();
      try {
        this.cdr.detectChanges();
      } catch {
        // Spec harness / partial view.
      }
      try {
        this.appRef.tick();
      } catch {
        // Avoid throwing during tests / teardown.
      }
    };
    this.ngZone.run(() => {
      paint();
      // Second pass after microtasks (status + books + history often settle together).
      setTimeout(paint, 0);
    });
  }

  /**
   * First live open mounts the layout while history/prepaid flags are still loading.
   * Close/reopen works because the view is created with data already present — remount once settled.
   */
  private tryRemountOrderRequestLayoutAfterAsyncSettle(): void {
    if (!this.isOpen || !this.orderRequestLayoutAwaitingFirstAsyncSettle) {
      return;
    }
    if (!this.showOrderRequestCheckoutLayout()) {
      return;
    }

    const expectHistory = !!(this.formData.customerId && this.formData.vehicleId);
    const historyPending =
      expectHistory && this.isLoadingOrderRequestHistoryPage && !this.orderRequestTodayBanner;
    const prepaidPending =
      this.isOrderRequestPrepaidRedeemContext() &&
      (this.isLoadingPrepaidPurchaseStatus ||
        this.isLoadingOrderRequestPrepaid ||
        (!this.prepaidPurchaseStatus && !this.prepaidPurchaseStatusError));

    if (historyPending || prepaidPending) {
      return;
    }

    this.orderRequestLayoutAwaitingFirstAsyncSettle = false;
    this.clearOrderRequestUiSettleLoop();
    this.orderRequestLayoutPaintKey += 1;
  }

  private scheduleOrderRequestUiSettleLoop(): void {
    this.clearOrderRequestUiSettleLoop();
    this.orderRequestUiSettleAttempts = 0;
    const tick = () => {
      if (!this.isOpen) {
        this.clearOrderRequestUiSettleLoop();
        return;
      }
      this.refreshOrderRequestAsyncUi();
      this.orderRequestUiSettleAttempts += 1;
      if (
        !this.orderRequestLayoutAwaitingFirstAsyncSettle ||
        this.orderRequestUiSettleAttempts >= 25
      ) {
        // Force remount even if something stayed "loading" so staff are not stuck forever.
        if (this.orderRequestLayoutAwaitingFirstAsyncSettle) {
          this.orderRequestLayoutAwaitingFirstAsyncSettle = false;
          this.orderRequestLayoutPaintKey += 1;
          this.refreshOrderRequestAsyncUi();
        }
        this.clearOrderRequestUiSettleLoop();
        return;
      }
      this.orderRequestUiSettleTimer = setTimeout(tick, 100);
    };
    this.orderRequestUiSettleTimer = setTimeout(tick, 0);
  }

  private clearOrderRequestUiSettleLoop(): void {
    if (this.orderRequestUiSettleTimer != null) {
      clearTimeout(this.orderRequestUiSettleTimer);
      this.orderRequestUiSettleTimer = null;
    }
    this.orderRequestUiSettleAttempts = 0;
  }

  /**
   * Start prepaid purchase-status + washbook bootstrap for LPR/QR redeem notifications.
   * Safe to call on in-place modal updates (does not require a full form reset).
   */
  private maybeBootstrapPrepaidRedeemFromNotification(
    notification: NotificationData | null | undefined
  ): void {
    if (!notification) {
      return;
    }
    const prepaidRedeemBootstrapKey = this.getPrepaidRedeemBootstrapKey(notification);
    if (!prepaidRedeemBootstrapKey) {
      return;
    }

    const sameKey = prepaidRedeemBootstrapKey === this.prepaidRedeemBootstrapKey;
    const hasStatus = !!this.prepaidPurchaseStatus;

    if (sameKey && hasStatus) {
      this.ensureOrderRequestPrepaidBooksLoadedForRedemption();
      this.refreshOrderRequestAsyncUi();
      return;
    }

    // In-flight for this plate/purchase — only force a view refresh (zoneless CD).
    if (sameKey && this.isLoadingPrepaidPurchaseStatus) {
      this.refreshOrderRequestAsyncUi();
      return;
    }

    // Same key finished without status (failed / skipped) while still in redeem UI — retry.
    const shouldRetryEmpty =
      sameKey &&
      !hasStatus &&
      !this.isLoadingPrepaidPurchaseStatus &&
      this.isOrderRequestPrepaidRedeemContext();

    if (sameKey && !shouldRetryEmpty) {
      this.refreshOrderRequestAsyncUi();
      return;
    }

    this.prepaidRedeemBootstrapKey = prepaidRedeemBootstrapKey;
    if (this.isPrepaidQrScanBootstrapNotification(notification)) {
      this.bootstrapPrepaidRedeemFromQrScan(notification);
    } else if (this.isPrepaidLprRedeemBootstrapNotification(notification)) {
      this.bootstrapPrepaidRedeemFromLprScan(notification);
    }
  }

  /** Redeem UI still shows catalog tiles; full package catalog load is skipped in redeem context. */
  private ensureOrderRequestPrepaidBooksLoadedForRedemption(): void {
    if (!this.isOrderRequestPrepaidRedeemContext()) {
      return;
    }
    if (this.isLoadingOrderRequestPrepaid) {
      return;
    }
    // Always refresh when status is known so a prior empty/stale catalog from live open is replaced.
    if (
      this.orderRequestPrepaidBooks.length > 0 &&
      !this.prepaidPurchaseStatus?.prepaid_wash_book_id
    ) {
      return;
    }
    if (
      this.orderRequestPrepaidBooks.length > 0 &&
      this.prepaidPurchaseStatus?.prepaid_wash_book_id &&
      this.orderRequestPrepaidBooks.some(
        book => book.id === this.prepaidPurchaseStatus?.prepaid_wash_book_id
      )
    ) {
      return;
    }
    this.loadOrderRequestPrepaidBooks();
  }

  /** Load fleet accounts for order-request UI (`GET /fleet` with `location_id` when known). */
  private loadOrderRequestFleets(locationId?: string): void {
    if (
      !this.showOrderRequestCheckoutLayout() &&
      this.notification?.type !== 'fleet-vehicle-found'
    ) {
      return;
    }
    const loc = String(locationId ?? this.formData.locationId ?? '').trim();
    const locationKey = this.getOrderRequestFleetCatalogLocationKey(loc);
    if (this.orderRequestCatalogService.isFleetLoadInFlight(locationKey)) {
      this.orderRequestCatalogService.scheduleCatalogRetry(loc);
      return;
    }
    if (
      this.orderRequestFleetCatalogLoadedLocationKey === locationKey &&
      this.orderRequestFleets.length > 0
    ) {
      return;
    }
    if (this.tryRestoreOrderRequestFleetsFromCache(loc)) {
      return;
    }

    this.orderRequestCatalogService.runFleetLoad(loc, {
      onStart: () => {
        this.isLoadingOrderRequestFleets = true;
        this.orderRequestFleets = [];
      },
      onSuccess: (mapped, fleetLocationKey) => {
        this.orderRequestFleetCatalogLoadedLocationKey = fleetLocationKey;
        this.orderRequestFleets = mapped;
        this.applyFleetVehicleSelectionFromNotification();
        this.rebuildMostUsedServiceItems();
      },
      onError: err => {
        console.error('Error loading fleets for order request:', err);
        this.orderRequestFleets = [];
        this.rebuildMostUsedServiceItems();
      },
      onFinalize: () => {
        this.isLoadingOrderRequestFleets = false;
        if (this.orderRequestFleets.length === 0) {
          this.tryRestoreOrderRequestFleetsFromCache(loc);
        }
        this.tryApplyPendingCreatePaymentOrderRequestUiRestore();
        this.syncOrderRequestAddonSelectionToPairedWith();
        this.flushOrderRequestCatalogRetry();
      },
    });
  }

  /**
   * Load shop discounts for create-order-request discount tiles.
   * Always force-refetch: DiscountService.discountsCacheCleared$ only fires in the SPA
   * that mutated discounts, so a long-lived Attendant session would otherwise keep a
   * stale NotificationOrderRequestCatalogService cache until a full page reload.
   * Existing eligibility/schedule/restriction/min-amount/location/wash/package filters
   * still run against the refreshed list via orderRequestVisibleDiscounts.
   */
  private loadOrderRequestDiscounts(locationId?: string): void {
    const loc = String(locationId ?? this.formData.locationId ?? '').trim();
    if (this.orderRequestCatalogService.isDiscountLoadInFlight(loc)) {
      return;
    }

    this.orderRequestCatalogService.runDiscountLoad(
      loc || undefined,
      {
        onStart: () => {
          this.isLoadingOrderRequestDiscounts = true;
        },
        onSuccess: discounts => {
          this.orderRequestDiscounts = discounts;
          this.refreshOrderRequestCustomerHistory();
          this.clearOrderRequestDiscountSelectionIfHidden();
        },
        onError: err => {
          console.error('Error loading package discounts:', err);
          this.orderRequestDiscounts = [ORDER_REQUEST_CUSTOM_DISCOUNT];
        },
        onFinalize: () => {
          this.isLoadingOrderRequestDiscounts = false;
          this.tryApplyPendingCreatePaymentOrderRequestUiRestore();
          this.syncOrderRequestAddonSelectionToPairedWith();
        },
      },
      { force: true }
    );
  }

  /** Add-on tiles: show all loaded add-ons for any plan (single / monthly / prepaid). */
  getOrderRequestAddonsOffered(): OrderRequestAddonItem[] {
    return this.orderRequestAddons;
  }

  /** API `paired_with` keys (lowercase). */
  private normalizePairedWithKey(raw: string): string {
    return String(raw ?? '')
      .trim()
      .toLowerCase();
  }

  /**
   * Active purchase context for add-on `paired_with` matching.
   * Uses fleet id, prepaid label, and selected package wash (when applicable).
   */
  private getOrderRequestPairedWithContextKeys(): Set<string> {
    const keys = new Set<string>();
    if (this.orderRequestSelectedFleetId) {
      keys.add('individual');
    }
    if (this.orderRequestSelectedPrepaidBookId || this.isOrderRequestPrepaidRedeemFlow()) {
      keys.add('prepaid');
    }
    if (this.selectedPackage && !this.orderRequestSelectedPrepaidBookId) {
      const wts = this.selectedPackage.wash_types || [];
      let resolved: string | null = this.selectedWashType;
      if (!resolved && wts.length === 1) {
        resolved = wts[0];
      }
      if (resolved) {
        keys.add(this.normalizeWashType(resolved));
      }
    }
    return keys;
  }

  /** When `paired_with` is absent/empty, any context is allowed (legacy add-ons). */
  isOrderRequestAddonPairedWithEligible(addon: OrderRequestAddonItem): boolean {
    const paired = addon.pairedWith;
    if (!paired?.length) {
      return true;
    }
    const normalized = paired.map(p => this.normalizePairedWithKey(p)).filter(p => p.length > 0);
    if (!normalized.length) {
      return true;
    }
    const ctx = this.getOrderRequestPairedWithContextKeys();
    if (ctx.size === 0) {
      // No wash package / prepaid selected: allow add-on-only checkout.
      return true;
    }
    return normalized.some(p => ctx.has(p));
  }

  isOrderRequestAddonPairedWithDisabled(addon: OrderRequestAddonItem): boolean {
    return !this.isOrderRequestAddonPairedWithEligible(addon);
  }

  isMostUsedServiceItemPairedDisabled(item: MostUsedServiceItem): boolean {
    if (item.kind !== 'addon' || !item.addonRef) {
      return false;
    }
    return this.isOrderRequestAddonPairedWithDisabled(item.addonRef);
  }

  private stripOrderRequestAddonSelectionForIneligible(addon: OrderRequestAddonItem): void {
    if (addon.mode === 'choose_products') {
      if (this.getOrderRequestVendingCommittedQuantityFor(addon.id) > 0) {
        delete this.orderRequestVendingSelections[addon.id];
        const next = new Set(this.orderRequestSelectedAddonIds);
        next.delete(addon.id);
        this.orderRequestSelectedAddonIds = next;
      }
      if (this.orderRequestVendingDialogAddonId === addon.id) {
        this.closeOrderRequestVendingDialog();
      }
      return;
    }
    if (addon.mode === 'multi_variant_package' && addon.packageVariants) {
      const anySelected = addon.packageVariants.some(v =>
        this.isOrderRequestAddonVariantLineCommitted(v)
      );
      if (!anySelected) {
        return;
      }
      if (this.orderRequestAddonVariantsDialog?.id === addon.id) {
        this.closeOrderRequestAddonVariantsDialog();
      }
      const next = new Set<string>(this.orderRequestSelectedAddonIds);
      for (const v of addon.packageVariants) {
        next.delete(v.id);
        delete this.orderRequestAddonVariantQuantities[v.id];
        delete this.orderRequestAddonSavedCustomPrice[v.id];
        delete this.orderRequestAddonCustomPriceDraft[v.id];
        if (this.orderRequestAddonExpandedId === v.id) {
          this.orderRequestAddonExpandedId = null;
        }
      }
      this.orderRequestSelectedAddonIds = next;
      return;
    }
    if (addon.mode === 'custom_price') {
      delete this.orderRequestAddonSavedCustomPrice[addon.id];
      delete this.orderRequestAddonCustomPriceDraft[addon.id];
      if (this.orderRequestAddonExpandedId === addon.id) {
        this.orderRequestAddonExpandedId = null;
      }
    }
    const next = new Set(this.orderRequestSelectedAddonIds);
    next.delete(addon.id);
    this.orderRequestSelectedAddonIds = next;
  }

  getOrderRequestAddonPackageKind(addon: OrderRequestAddonItem): OrderRequestAddonPackageKind {
    return addon.packageKind === 'detailing' ? 'detailing' : 'add_on';
  }

  /** Distinct package kinds among committed Add on section tiles. */
  private getOrderRequestCommittedAddonPackageKinds(): Set<OrderRequestAddonPackageKind> {
    const kinds = new Set<OrderRequestAddonPackageKind>();
    for (const addon of this.orderRequestAddons) {
      if (!this.isOrderRequestAddonCommitted(addon)) {
        continue;
      }
      kinds.add(this.getOrderRequestAddonPackageKind(addon));
    }
    return kinds;
  }

  /** Committed tile in the shared Add on section (add-on vs detailing). */
  getOrderRequestCommittedAddonPackageKind(): OrderRequestAddonPackageKind | null {
    const kinds = this.getOrderRequestCommittedAddonPackageKinds();
    if (kinds.size !== 1) {
      return null;
    }
    return kinds.values().next().value ?? null;
  }

  /** Checkout summary section label: Add-on or Detailing (legacy fallback: Additional Service). */
  getCheckoutAdditionalServiceBlockLabel(): string {
    const kinds = this.getOrderRequestCommittedAddonPackageKinds();
    if (kinds.size > 1) {
      return 'Add on';
    }
    if (kinds.has('detailing')) {
      return 'Detailing';
    }
    if (kinds.has('add_on')) {
      return 'Add-on';
    }
    return 'Additional Service';
  }

  private getCheckoutAddonServiceNameDetailLabelForPackageId(pkgId: string): string {
    const addon = this.orderRequestAddons.find(a => (a.apiPackageId || '').trim() === pkgId.trim());
    if (addon && this.getOrderRequestAddonPackageKind(addon) === 'detailing') {
      return 'Detailing name';
    }
    return 'Add-on name';
  }

  private getCheckoutAddonServiceNameDetailLabel(): string {
    const kinds = this.getOrderRequestCommittedAddonPackageKinds();
    if (kinds.size > 1) {
      return 'Service name';
    }
    return kinds.has('detailing') ? 'Detailing name' : 'Add-on name';
  }

  /** Shared Add on section: keep add-on + detailing in one multi-select collection. */
  private syncOrderRequestAddonSelectionAcrossKinds(_committedAddon: OrderRequestAddonItem): void {
    this.clearAutoSelectedWashPackageWhenAddonsCommitted();
  }

  /** Drop committed add-ons that no longer match `paired_with` for the current service selection. */
  private syncOrderRequestAddonSelectionToPairedWith(): void {
    if (!this.orderRequestAddons.length) {
      return;
    }
    for (const addon of this.orderRequestAddons) {
      if (this.isOrderRequestAddonPairedWithEligible(addon)) {
        continue;
      }
      if (!this.isOrderRequestAddonSelected(addon.id)) {
        continue;
      }
      this.stripOrderRequestAddonSelectionForIneligible(addon);
    }
  }

  trackOrderRequestAddonById(_index: number, a: OrderRequestAddonItem): string {
    return a.id;
  }

  trackOrderRequestPrepaidBookById(_index: number, book: PrepaidWashBook): string {
    return book.id;
  }

  /**
   * Expand packages: if a package has 2 wash types, split it into 2 separate options.
   * Order matches the API: all single-wash rows follow `packages` order (and each package's
   * `wash_types` order), then all monthly rows the same way — so Single Wash / Monthly sections
   * match the backend sequence instead of interleaving both types per package.
   */
  private expandPackages(packages: Package[]): ExpandedPackage[] {
    return expandOrderRequestPackages(packages, {
      filterPackageId:
        this.notification?.data?.isBuyPackageFlow && this.formData.packageId
          ? this.formData.packageId
          : undefined,
    });
  }

  /**
   * Same Figma order UI as the header + New order flow: manual orders, LPR order requests,
   * and image/LPR new-customer or invalid-plate flows. `notification.type` is unchanged for APIs.
   */
  isOrderRequestStyleNotification(): boolean {
    const t = this.getEffectiveNotificationType();
    return (
      t === 'create-order-request' ||
      t === 'create-customer-request' ||
      t === 'invalid-license-plate'
    );
  }

  /**
   * True when the modal is on payment checkout with a real order to pay (not a pre-order LPR lane row).
   */
  hasCheckoutOrderId(): boolean {
    // CWS-1945: a draft shows checkout too; its order is created when the attendant pays.
    if (this.checkoutDraftPayload) {
      return true;
    }
    return !!String(
      this.formData.orderId ||
        this.notification?.data?.order_id ||
        this.notification?.data?.orderId ||
        ''
    ).trim();
  }

  /** create-payment-request before POST /order — still the new-order UI, not Stripe checkout. */
  isPreOrderPaymentNotification(): boolean {
    return this.notification?.type === 'create-payment-request' && !this.hasCheckoutOrderId();
  }

  /**
   * Body uses the Figma order-request layout (packages grid, add-ons, etc.): create flows
   * and editing an order from checkout.
   */
  showOrderRequestCheckoutLayout(): boolean {
    return (
      this.isOrderRequestStyleNotification() ||
      (this.notification?.type === 'create-payment-request' &&
        (this.isEditMode || this.isPreOrderPaymentNotification()))
    );
  }

  /**
   * Figma "Redeem existing membership" — shown when the shop's super admin has it on
   * (Customer Migration → vendor → Settings), via `company/me` → `membershipRedeemEnabled`.
   *
   * CWS-1369 moved this off `enabledVendorSources`. That flag says a vendor *sync* is live,
   * which is not the same question: The Wash Express and Champs both run DRB, but only
   * Champs hands members a barcode card — at Wash Express the section was two buttons that
   * could never find anyone. Where nobody has answered, the server still falls back to the
   * vendor's own capability, so a shop that is never visited behaves exactly as before.
   */
  showOrderRequestMembershipVerifySection(): boolean {
    if (!this.showOrderRequestCheckoutLayout()) {
      return false;
    }
    // Reading the signal in-component is what makes the section appear once company/me lands.
    return this.vendorModeService.membershipRedeemEnabled();
  }

  /**
   * Figma Active subscription modal — shown instead of the legacy inline layout for
   * LPR/image sole membership and vendor lookup/redeem flows.
   * Suppressed while payment success is up, and for post-purchase first-wash activation
   * SSE (`subscription_activated`) so it does not stack on the purchase success modal.
   */
  shouldShowVendorActiveSubscriptionModal(): boolean {
    if (
      this.showVendorActiveSubscriptionLookupModal &&
      this.vendorActiveSubscriptionLookupDisplay
    ) {
      return true;
    }
    if (!this.isOpen || this.notification?.type !== 'active-subscription') {
      return false;
    }
    if (this.showPaymentSuccess) {
      return false;
    }
    if (isPostPurchaseLprMembershipActivation(this.notification)) {
      return false;
    }
    if (isMultiEligibleMembershipRedeemCompleted(this.notification)) {
      return false;
    }
    return true;
  }

  getVendorActiveSubscriptionDisplay(): VendorActiveSubscriptionDisplay {
    const vm =
      this.vendorActiveSubscriptionLookupDisplay ??
      buildVendorActiveSubscriptionDisplayVm(
        (this.notification?.data ?? null) as Record<string, any> | null
      );
    this.ensureVendorMembershipAvailability(vm);
    return vm;
  }

  /** Where the attendant is, for the plan-change endpoints. */
  resolveVendorPlanChangeLocationId(): string | null {
    return this.resolveVendorRedeemLocationId() || null;
  }

  /**
   * Update Membership is offered for a Way membership at a location where the
   * tenant switched it on. No role restriction (Product, 30 Sep 2026): every
   * attendant and admin can change a plan. Client-side gating only; the API
   * re-checks the flag and the membership.
   */
  canChangeVendorPlan(): boolean {
    return this.vendorPlanChangeAvailable && !this.showVendorActiveSubscriptionLookupModal;
  }

  /**
   * CWS-1780: Cancel membership is offered when cancel & refund is on at this location AND the
   * subscriptions list says the membership can be cancelled (`can_cancel`: a live Way membership,
   * not a family plan, not already cancelling). The lane payload's source field is only a
   * pre-filter — a DRB membership synced into Way arrives without one — so the server decides.
   */
  canCancelVendorMembership(): boolean {
    return this.vendorCancelAvailable && !this.showVendorActiveSubscriptionLookupModal;
  }

  /**
   * The lane scan behind the pop-up. A pop-up opened from a membership lookup has none, so adding
   * family members from it always asks for the primary's code.
   */
  vendorLprEntryId(): string | null {
    if (this.showVendorActiveSubscriptionLookupModal) return null;
    const data = (this.notification?.data ?? null) as Record<string, unknown> | null;
    return String(data?.['lpr_entry_id'] ?? '').trim() || null;
  }

  /** CWS-1581: Start Family is offered when Family Plan is on here and the membership qualifies. */
  canStartVendorFamily(): boolean {
    return this.vendorStartFamilyAvailable && !this.showVendorActiveSubscriptionLookupModal;
  }

  /**
   * One plan-change and one cancel availability lookup per membership + location; the results
   * land on a later CD pass.
   */
  private ensureVendorMembershipAvailability(vm: VendorActiveSubscriptionDisplay): void {
    const locationId = this.resolveVendorPlanChangeLocationId();
    const eligibleSource = !vm.sourceRaw || vm.sourceRaw === 'way';
    const key =
      vm.subscriptionId && locationId && eligibleSource
        ? `${vm.subscriptionId}|${locationId}`
        : null;
    if (key === this.vendorPlanChangeAvailabilityKey) {
      return;
    }
    this.vendorPlanChangeAvailabilityKey = key;
    this.vendorPlanChangeAvailable = false;
    this.vendorCancelAvailable = false;
    this.vendorStartFamilyAvailable = false;
    if (!key || !locationId) {
      return;
    }
    this.planChangeSettings
      .getAvailability(locationId)
      .pipe(catchError(() => of(null)))
      .subscribe(res => {
        if (this.vendorPlanChangeAvailabilityKey !== key) return;
        this.vendorPlanChangeAvailable = res?.data?.enabled === true;
        this.cdr.markForCheck();
      });
    const customerId = (vm.customerId ?? '').trim();
    // One read of the membership's list row answers both Cancel (`can_cancel`) and Start Family
    // (`can_start_family`): the server decides each, so the pop-up only reads the flags.
    const subscription$ = customerId
      ? this.customerSubscriptions
          .getSubscription(customerId, vm.subscriptionId as string)
          .pipe(shareReplay(1))
      : of(null);
    this.cancellationSettings
      .getAvailability(locationId)
      .pipe(
        switchMap(res =>
          res?.data?.enabled === true
            ? subscription$.pipe(map(sub => sub?.can_cancel === true))
            : of(false)
        ),
        catchError(() => of(false))
      )
      .subscribe(canCancel => {
        if (this.vendorPlanChangeAvailabilityKey !== key) return;
        this.vendorCancelAvailable = canCancel;
        this.cdr.markForCheck();
      });
    // CWS-1581: the lane payload only carries the family tags.
    subscription$
      .pipe(
        map(sub => sub?.can_start_family === true),
        catchError(() => of(false))
      )
      .subscribe(canStart => {
        if (this.vendorPlanChangeAvailabilityKey !== key) return;
        this.vendorStartFamilyAvailable = canStart;
        this.cdr.markForCheck();
      });
  }

  /** CWS-1581: a family was started from the lane pop-up; the lists behind it must follow. */
  onVendorFamilyStarted(result: FamilyStartResult): void {
    this.logModalInteraction('modal.vendorActiveSubscription.familyStarted', {
      familyGroupId: result.group_id,
    });
    // The membership is in a family now; drop the cached answers so the next read starts fresh.
    this.vendorPlanChangeAvailabilityKey = null;
    this.vendorStartFamilyAvailable = false;
    this.ordersService.refreshOrders();
  }

  /** CWS-1780: a membership was cancelled from the lane pop-up; the lists behind it must follow. */
  onVendorMembershipCancelled(done: CancelRefundDone): void {
    this.logModalInteraction('modal.vendorActiveSubscription.membershipCancelled', {
      cancellationId: done.result.cancellation?.id,
      status: done.result.status,
    });
    // The membership can no longer be cancelled or re-planned; drop the cached answers.
    this.vendorPlanChangeAvailabilityKey = null;
    this.ordersService.refreshOrders();
  }

  onVendorActiveSubscriptionPlanChange(): void {
    this.logModalInteraction('modal.vendorActiveSubscription.planChange');
    this.vendorPlanChangeNotice = null;
    this.showVendorPlanChange = true;
  }

  onVendorPlanChangeBack(): void {
    this.showVendorPlanChange = false;
  }

  /** The change was applied or scheduled: reflect it on the detail body and downstream lists. */
  onVendorPlanChangeApplied(result: PlanChangeResult): void {
    this.logModalInteraction('modal.vendorActiveSubscription.planChange.applied', {
      changeId: result.change?.id,
      timing: result.change?.timing,
    });
    const membership = result.membership;
    if (membership && result.change?.timing === 'immediate') {
      const data = (this.notification?.data ?? null) as Record<string, any> | null;
      if (data) {
        data['package_id'] = membership.new_plan.package_id;
        data['package_name'] = membership.new_plan.name;
        data['list_price'] = membership.new_plan.monthly_price;
        data['recurring_monthly_price'] = membership.new_plan.monthly_price;
        data['total_washes_available'] = membership.unlimited
          ? null
          : membership.washes_this_cycle === null
            ? membership.washes_per_renewal
            : membership.washes_this_cycle;
        data['wash_count_period'] = membership.unlimited ? 'unlimited' : 'cycle_override';
      }
    }
    const notice = buildPlanChangeNotice(result);
    this.vendorPlanChangeNotice = notice ? { title: notice.title, body: notice.body } : null;
    this.vendorPlanChangeUpcoming = notice?.upcoming ?? null;
    this.vendorPlanChangeAvailabilityKey = null;
    this.ordersService.refreshOrders();
  }

  /** CWS-1469: pause and Activate now are for Attendant+ and Admin, as the API enforces. */
  canManageVendorPause(): boolean {
    return this.roleService.canManageCustomers();
  }

  /** CWS-1547: a pause, activation or cancelled pause went through in the Active Subscription popup. */
  onVendorPauseChanged(pause: SubscriptionPause): void {
    // The popup rebuilds from this payload; keep it current so a reopen or re-render stays correct.
    // A paused read reactivated here turns its primary into Redeem (isReactivatedPausedRead).
    if (this.notification?.data && !this.showVendorActiveSubscriptionLookupModal) {
      this.notification.data['pause'] = pause;
    }
    this.logModalInteraction('modal.vendorActiveSubscription.pauseChanged');
    this.ordersService.refreshOrders();
  }

  getVendorActiveSubscriptionPrimaryButtonLabel(): string {
    if (this.showVendorActiveSubscriptionLookupModal) {
      return 'Got it';
    }
    if (this.isReactivatedPausedRead()) {
      return 'Redeem';
    }
    return this.getViewOnlyDismissButtonLabel();
  }

  private isReactivatedPausedRead(): boolean {
    return (
      !this.showVendorActiveSubscriptionLookupModal &&
      isReactivatedPausedRead(this.notification?.data as Record<string, any> | undefined)
    );
  }

  /**
   * Redeems today's wash against the paused read's LPR entry, now that the membership is active
   * again. The API records the wash, sends it to the tunnel and opens the lane gate.
   */
  private redeemReactivatedMembership(): void {
    const data = this.notification?.data as Record<string, any> | undefined;
    const request = buildMultiEligibleConfirmRequest(data, {
      redeem_event: 'active-subscription',
      order_id: data?.['order_id'] ?? null,
      subscription_id: data?.['subscription_id'] ?? null,
    });
    if (!request) {
      this.updateOrderError = 'This scan can no longer be redeemed. Scan the plate again.';
      return;
    }
    this.updateOrderError = '';
    this.isRedeemingReactivated = true;
    this.multiEligibleConfirmApi
      .confirm(request)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isRedeemingReactivated = false;
        })
      )
      .subscribe({
        next: () => {
          const packageName = String(data?.['package_name'] ?? '').trim();
          this.messageService.add({
            severity: 'success',
            summary: 'Membership redeemed',
            detail: packageName
              ? `${packageName} redeemed. Please proceed to the wash bay.`
              : 'Subscription redeemed. Please proceed to the wash bay.',
          });
          this.onClose();
        },
        error: (error: any) => {
          this.updateOrderError = extractErrorMessage(
            error,
            'Could not redeem this membership. Please try again.'
          );
        },
      });
  }

  onVendorActiveSubscriptionModalClose(): void {
    this.showVendorUpgradePlanPicker = false;
    this.showVendorPlanChange = false;
    this.vendorPlanChangeNotice = null;
    this.vendorPlanChangeUpcoming = null;
    this.importedVendorCardOnFileCheckout = false;
    this.orderRequestHistoryExpanded = false;
    this.clearVendorActiveSubscriptionOmitWashId();
    if (this.showVendorActiveSubscriptionLookupModal) {
      this.closeVendorActiveSubscriptionLookupModal();
      return;
    }
    this.logModalInteraction('modal.vendorActiveSubscription.close');
    this.onClose();
    this.ordersService.refreshOrders();
  }

  onVendorActiveSubscriptionModalPrimary(): void {
    if (this.showVendorActiveSubscriptionLookupModal) {
      this.logModalInteraction('modal.vendorActiveSubscription.lookup.primary');
      this.closeVendorActiveSubscriptionLookupModal();
      this.onClose();
      this.ordersService.refreshOrders();
      return;
    }
    this.logModalInteraction('modal.vendorActiveSubscription.primary', {
      notificationType: this.notification?.type,
    });
    if (this.isReactivatedPausedRead()) {
      this.redeemReactivatedMembership();
      return;
    }
    this.onOkayClick();
  }

  onVendorActiveSubscriptionUpgradePlan(): void {
    this.logModalInteraction('modal.vendorActiveSubscription.upgradePlan');
    this.showVendorUpgradePlanPicker = true;
    this.loadVendorUpgradePlanCatalog();
  }

  onVendorUpgradePlanBack(): void {
    this.showVendorUpgradePlanPicker = false;
  }

  onVendorUpgradePlanConfirm(selection: VendorUpgradePlanSelection): void {
    this.logModalInteraction('modal.vendorActiveSubscription.upgradePlan.confirm', {
      packageId: selection.packageId,
    });
    this.formData.packageId = selection.packageId;
    this.formData.price = String(selection.listPrice);
    if (this.notification?.data) {
      const data = this.notification.data as Record<string, any>;
      data['package_id'] = selection.packageId;
      data['package_name'] = selection.packageName;
      data['list_price'] = selection.listPrice;
      data['recurring_monthly_price'] = selection.listPrice;
      if (selection.discountId) {
        data['discount_id'] = selection.discountId;
      } else {
        delete data['discount_id'];
      }
    }
    this.showVendorUpgradePlanPicker = false;
  }

  get vendorUpgradePlans(): VendorUpgradePlanOption[] {
    return this.monthlyPackages.map(plan => {
      const listPrice = this.parseUsdNumber(plan.price) ?? 0;
      return {
        uniqueId: plan.uniqueId,
        packageId: String(plan.package?.id || '').trim(),
        displayName: plan.displayName,
        priceLabel: this.formatUsdPriceForOrderRequestDisplay(plan.price) || '$0.00',
        listPrice,
        themeColor: this.themeColorForPackage(plan.package),
      };
    });
  }

  get vendorUpgradeDiscounts(): VendorUpgradeDiscountOption[] {
    return this.orderRequestDiscounts.map(discount => ({
      id: discount.id,
      label: discount.label,
      priceLabel: discount.fixedPrice || '',
      mode: discount.mode,
    }));
  }

  /** Stripe card fields complete (same rules as checkout card validation). */
  isVendorActiveCardFormComplete(): boolean {
    const hasCardholder = !!this.paymentForm.cardholderName?.trim();
    const usingStripeElements =
      this.cardNumberElement && this.cardExpiryElement && this.cardCvcElement;
    if (usingStripeElements) {
      return (
        hasCardholder &&
        this.stripeCardNumberComplete &&
        this.stripeCardExpiryComplete &&
        this.stripeCardCvcComplete
      );
    }
    return false;
  }

  onVendorActivePaymentMethodChange(method: VendorActivePaymentMethod): void {
    this.paymentForm.method = method as PaymentMethod;
    if (method === PaymentMethodCode.Card) {
      if (!this.paymentForm.cardholderName?.trim()) {
        this.paymentForm.cardholderName = this.formData.customerFirstName || '';
      }
      this.scheduleStripeMountForNotificationModal(8, 250);
      return;
    }
    this.cancelStripeMountRetries();
    this.unmountStripeElements();
  }

  onVendorActiveSubscriptionConfirmPayment(payload: VendorActiveConfirmPaymentPayload): void {
    const display = this.getVendorActiveSubscriptionDisplay();
    const orderId =
      payload.orderId ||
      display.orderId ||
      this.notification?.data?.order_id ||
      this.notification?.data?.orderId ||
      this.formData.orderId;

    if (!orderId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Required Field',
        detail: 'Order ID is required for payment confirmation',
      });
      return;
    }

    if (this.isProcessingPayment || this.isConfirmingCashPayment) {
      return;
    }

    this.logModalInteraction('modal.vendorActiveSubscription.confirmPayment', {
      orderId,
      paymentMethod: payload.paymentMethod,
    });

    const phone = payload.phoneNumber || display.phoneNumber || '';
    const membershipStartDate = payload.membershipStartDate || display.membershipStartDate || '';

    if (payload.cardholderName != null) {
      this.paymentForm.cardholderName = payload.cardholderName;
    }
    if (payload.billingAddress != null) {
      this.paymentForm.billingAddress = payload.billingAddress;
    }
    this.paymentForm.method = payload.paymentMethod as PaymentMethod;
    this.paymentForm.phoneNumber = phone;

    // Prepare the existing order for payment (do not create a new order).
    this.isProcessingPayment = true;
    const body: Record<string, any> = {
      order_id: orderId,
      membership_start_date: membershipStartDate || undefined,
      phone_number: phone || undefined,
    };
    const upgradePackageId = String(
      (this.notification?.data as Record<string, any> | undefined)?.['package_id'] ||
        this.formData.packageId ||
        ''
    ).trim();
    if (upgradePackageId) {
      body['package_id'] = upgradePackageId;
    }
    void firstValueFrom(this.apiService.post('order/vendor-membership-prepare-payment', body))
      .then((response: any) => {
        const prepared = (response?.data ?? response ?? {}) as Record<string, any>;
        const preparedOrderId = String(prepared['order_id'] || orderId).trim();
        const recurringAmount =
          Number(
            prepared['recurring_monthly_price'] ??
              prepared['list_price'] ??
              display.listPrice ??
              display.finalPrice
          ) || null;
        const amountDueToday =
          prepared['amount_due_today'] != null
            ? Number(prepared['amount_due_today'])
            : prepared['final_price'] != null
              ? Number(prepared['final_price'])
              : 0;
        const packageName = String(prepared['package_name'] || display.packageName || '').trim();
        const priorData = (this.notification?.data ?? {}) as Record<string, any>;

        this.applyVendorMembershipPreparedOrderState({
          prepared,
          preparedOrderId,
          amount: recurringAmount,
          amountDueToday: Number.isFinite(amountDueToday) ? amountDueToday : 0,
          packageName,
          phone,
          membershipStartDate,
          priorData,
          display,
          keepVendorActiveModal: shouldProcessVendorMembershipCardInline(payload.paymentMethod),
        });

        if (shouldProcessVendorMembershipCardInline(payload.paymentMethod)) {
          // Charge on this modal — keep Stripe fields mounted; do not open checkout.
          this.importedVendorCardOnFileCheckout = true;
          this.paymentForm.method = PaymentMethodCode.Card;
          this.paymentForm.phoneNumber = phone;
          this.isProcessingPayment = false;
          void this.processStripePayment();
          this.cdr.markForCheck();
          return;
        }

        this.selectPaymentMethod(payload.paymentMethod as PaymentMethod);
        this.paymentForm.phoneNumber = phone;
        this.isProcessingPayment = false;

        if (shouldAutoContinueVendorMembershipCheckout(payload.paymentMethod)) {
          // Link / Tap to Pay can proceed with the existing continue handlers.
          this.onContinue();
        }
        this.cdr.markForCheck();
      })
      .catch((error: any) => {
        this.isProcessingPayment = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Payment Error',
          detail: sanitizeErrorMessage(
            error?.error?.message ||
              error?.message ||
              'Failed to prepare membership order for payment'
          ),
        });
        this.cdr.markForCheck();
      });
  }

  /**
   * Apply prepare-payment response onto notification/form state.
   * Card stays on the Active Membership modal; other methods open checkout.
   */
  private applyVendorMembershipPreparedOrderState(args: {
    prepared: Record<string, any>;
    preparedOrderId: string;
    amount: number | null;
    amountDueToday: number;
    packageName: string;
    phone: string;
    membershipStartDate: string;
    priorData: Record<string, any>;
    display: VendorActiveSubscriptionDisplay;
    keepVendorActiveModal: boolean;
  }): void {
    const {
      prepared,
      preparedOrderId,
      amount,
      amountDueToday,
      packageName,
      phone,
      membershipStartDate,
      priorData,
      display,
      keepVendorActiveModal,
    } = args;

    const preparedData = {
      ...priorData,
      ...prepared,
      order_id: preparedOrderId,
      orderId: preparedOrderId,
      open_checkout_summary: true,
      wash_type: 'monthly_membership',
      package_id: prepared['package_id'] || priorData['package_id'],
      package_name: packageName,
      packageName,
      list_price: prepared['list_price'] ?? amount,
      final_price: prepared['final_price'] ?? amountDueToday,
      finalPrice: prepared['final_price'] ?? amountDueToday,
      recurring_monthly_price: prepared['recurring_monthly_price'] ?? amount,
      amount_due_today: prepared['amount_due_today'] ?? amountDueToday,
      billing_starts_at:
        prepared['billing_starts_at'] || prepared['membership_start_date'] || membershipStartDate,
      customer_contact: phone,
      customerContact: phone,
      payment_required: true,
      payment_status: 'pending',
      has_payment: false,
      customer_source: String(prepared['customer_source'] || display.sourceLabel || '')
        .trim()
        .toLowerCase(),
      membership_start_date: prepared['membership_start_date'] || membershipStartDate,
      order: {
        ...((priorData['order'] as Record<string, any>) || {}),
        id: preparedOrderId,
        order_id: prepared['public_order_id'] || priorData['order_id'],
        wash_type: 'monthly_membership',
        package_id: prepared['package_id'] || priorData['package_id'],
        list_price: prepared['list_price'] ?? amount,
        final_price: prepared['final_price'] ?? amountDueToday,
        payment_status: 'pending',
        location_id: prepared['location_id'] || priorData['location_id'],
        customer_id: prepared['customer_id'] || priorData['customer_id'],
        vehicle_id: prepared['vehicle_id'] || priorData['vehicle_id'],
      },
    };

    if (keepVendorActiveModal) {
      // Stay on Active Membership so Stripe card fields remain mounted.
      if (
        this.showVendorActiveSubscriptionLookupModal &&
        this.vendorActiveSubscriptionLookupDisplay
      ) {
        this.vendorActiveSubscriptionLookupDisplay = {
          ...this.vendorActiveSubscriptionLookupDisplay,
          orderId: preparedOrderId,
          finalPrice: amountDueToday,
          listPrice: amount,
          paymentRequired: true,
          cardDetailsCollected: false,
          paymentStatus: 'pending',
          phoneNumber: phone || this.vendorActiveSubscriptionLookupDisplay.phoneNumber,
        };
      }
      if (this.notification) {
        this.notification = {
          ...this.notification,
          data: {
            ...(this.notification.data || {}),
            ...preparedData,
          },
        };
      }
    } else {
      this.showVendorActiveSubscriptionLookupModal = false;
      this.vendorActiveSubscriptionLookupDisplay = null;
      this.notification = {
        type: 'create-payment-request',
        message: 'Collect payment to add membership in the system',
        timestamp: this.notification?.timestamp ?? new Date(),
        data: preparedData,
      };
      this.isOpen = true;
    }

    this.formData.orderId = preparedOrderId;
    this.formData.customerContact = phone;
    this.formData.package = packageName;
    if (prepared['package_id']) {
      this.formData.packageId = String(prepared['package_id']);
    }
    if (amount != null) {
      this.formData.price = String(amount);
    }
    this.selectedWashType = 'monthly_membership';
    this.cachedOrderWashType = 'monthly_membership';
    if (!keepVendorActiveModal) {
      this.populateFormData();
      this.showCheckoutSummary = true;
      this.userInteractedFields.add('package');
      this.userInteractedFields.add('packageId');
      if (prepared['package_id']) {
        const pkg = this.packages.find(
          p => String(p?.id || '').trim() === String(prepared['package_id']).trim()
        );
        if (pkg) {
          this.selectedPackage = pkg;
        }
      }
    }
  }

  private closeVendorActiveSubscriptionLookupModal(): void {
    this.showVendorActiveSubscriptionLookupModal = false;
    this.vendorActiveSubscriptionLookupDisplay = null;
    this.importedVendorCardOnFileCheckout = false;
    this.clearVendorActiveSubscriptionOmitWashId();
    this.cdr.markForCheck();
  }

  /** Opens the in-modal barcode scanner for vendor membership verification. */
  onOrderRequestMembershipScanNow(): void {
    this.logModalInteraction('orderRequest.membershipVerify.scanNow');
    this.vendorMembershipScannerUploadOnly = false;
    this.beginVendorMembershipCameraStream();
    this.showVendorMembershipBarcodeScanner = true;
    this.cdr.markForCheck();
  }

  onVendorMembershipBarcodeScannerClose(): void {
    if (this.isVendorMembershipLookupLoading) {
      return;
    }
    this.showVendorMembershipBarcodeScanner = false;
    this.vendorMembershipScannerUploadOnly = false;
    this.vendorMembershipCameraStreamPromise = null;
    this.cdr.markForCheck();
  }

  onVendorMembershipBarcodeScanned(rawValue: string): void {
    this.lookupVendorMembershipFromBarcode(rawValue, 'camera');
  }

  /**
   * CWS-1914: Redeem Prepaid, beside Scan by Camera. The attendant already has the car in
   * front of them — its plate is in the booking details — and the customer's QR names the
   * book. Scanning it here loads that book into this order request, so the redemption is
   * attached to the car at the lane rather than to whoever bought the book.
   */
  onOrderRequestScanPrepaidRedemptionQr(): void {
    this.logModalInteraction('orderRequest.prepaidRedeem.scanQr');
    this.prepaidRedemptionQrError = '';
    this.beginPrepaidRedemptionQrCameraStream();
    this.showPrepaidRedemptionQrScanner = true;
    this.cdr.markForCheck();
  }

  onPrepaidRedemptionQrScannerClose(): void {
    if (this.isPrepaidRedemptionQrProcessing) {
      return;
    }
    this.showPrepaidRedemptionQrScanner = false;
    this.prepaidRedemptionQrCameraStreamPromise = null;
    this.cdr.markForCheck();
  }

  onPrepaidRedemptionQrScanned(rawValue: string): void {
    const scan = parsePrepaidRedemptionUrl(String(rawValue ?? ''));
    if (!scan) {
      this.closePrepaidRedemptionQrScanner();
      this.openPrepaidCodeDialog(
        "That isn't a prepaid wash QR code. Ask the customer for the redemption code from their SMS or email and type it below."
      );
      return;
    }

    this.isPrepaidRedemptionQrProcessing = true;
    this.prepaidRedemptionQrError = '';
    this.cdr.markForCheck();

    this.prepaidWashBookService
      .resolvePrepaidPurchaseStatusFromScan(scan)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: status => this.applyScannedPrepaidRedemption(status),
        error: err => {
          this.isPrepaidRedemptionQrProcessing = false;
          this.closePrepaidRedemptionQrScanner();
          const detail =
            err?.error?.message || err?.message || 'Could not load the prepaid wash book.';
          // Only when the QR itself was refused. An outage fails the code lookup the same
          // way, so there it stays an ordinary error rather than a dead-end dialog.
          if (shouldOfferCodeAfterQrFailure(err)) {
            this.openPrepaidCodeDialog(
              `${detail} Ask the customer for the redemption code from their SMS or email and type it below.`
            );
            return;
          }
          this.prepaidRedemptionQrError = detail;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * The scanned book, opened against the car already on this order request.
   *
   * The redeemer pane is reset before it is prefilled: its snapshot is captured once and the
   * booking details here describe whoever drove in, so starting clean is what makes the pane
   * show the scanned plate instead of the purchaser's.
   */
  /**
   * The photo held a QR the reader could not read, rather than no code at all.
   *
   * Same answer the app shell has given since CWS-1908: a damaged, blurred or decorative
   * QR is still a customer holding a prepaid wash, and the code in their SMS reaches the
   * same book. Telling them to find another photo sends them away from the one thing that
   * would work.
   */
  onPrepaidRedemptionQrUnreadable(): void {
    this.isPrepaidRedemptionQrProcessing = false;
    this.closePrepaidRedemptionQrScanner();
    this.openPrepaidCodeDialog(
      "The QR code couldn't be read. Ask the customer for the redemption code from their SMS or email and type it below."
    );
  }

  /** Shut the camera down whichever way the scan ended. */
  private closePrepaidRedemptionQrScanner(): void {
    this.showPrepaidRedemptionQrScanner = false;
    this.prepaidRedemptionQrCameraStreamPromise = null;
    this.cdr.markForCheck();
  }

  openPrepaidCodeDialog(intro: string): void {
    this.prepaidCodeIntro = intro;
    this.prepaidCodeError = '';
    this.isPrepaidCodeDialogOpen = true;
    this.cdr.markForCheck();
  }

  closePrepaidCodeDialog(): void {
    if (this.prepaidCodeBusy) return;
    this.isPrepaidCodeDialogOpen = false;
    this.prepaidCodeError = '';
    this.cdr.markForCheck();
  }

  /**
   * Find the book by its typed code and redeem it here.
   *
   * Deliberately lands on applyScannedPrepaidRedemption rather than the app-level handler
   * the navbar uses: that one opens a FRESH order request from the purchaser's details,
   * which would throw away the car already on this one. A code typed here means the same
   * thing a QR scanned here means -- redeem against the vehicle in front of the attendant.
   */
  onPrepaidCodeFind(code: string): void {
    if (this.prepaidCodeBusy) return;
    this.prepaidCodeBusy = true;
    this.prepaidCodeError = '';
    this.cdr.markForCheck();

    this.prepaidWashBookService
      .resolvePrepaidPurchaseStatusFromCode(code)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: status => {
          this.prepaidCodeBusy = false;
          this.isPrepaidCodeDialogOpen = false;
          this.applyScannedPrepaidRedemption(status);
        },
        error: err => {
          this.prepaidCodeBusy = false;
          this.prepaidCodeError =
            err?.error?.message || err?.message || 'No prepaid book found for this code.';
          this.cdr.markForCheck();
        },
      });
  }

  private applyScannedPrepaidRedemption(status: PrepaidPurchaseStatus): void {
    this.isPrepaidRedemptionQrProcessing = false;
    this.showPrepaidRedemptionQrScanner = false;
    this.prepaidRedemptionQrCameraStreamPromise = null;

    this.resetPrepaidRedemptionRecipient();
    this.prepaidPurchaseId = status.prepaid_purchase_id;
    // CWS-1914: mark the request as opened to redeem, exactly as the orders-page scan does.
    // The header, the locked booking details and the catalog all ask the notification, not the
    // loaded status, so without this stamp a scan left the modal titled "New Order Request".
    // The purchaser's customer and vehicle are deliberately NOT copied across: the car at the
    // lane is already in the booking details and is who this redemption belongs to.
    if (this.notification) {
      this.notification = {
        ...this.notification,
        data: {
          ...((this.notification.data ?? {}) as Record<string, any>),
          prepaid_purchase_id: status.prepaid_purchase_id,
          qr_scan: true,
          source_context: 'prepaid_qr',
        },
      } as typeof this.notification;
    }
    // Prefilled before the status round-trip, not after: the pane is built from the booking
    // details that are already here, so a slow or failed status load must not leave the
    // attendant staring at an empty redeemer pane.
    this.applyDefaultPrepaidRedemptionRecipientFromPurchaser();
    this.loadPrepaidPurchaseStatus(status.prepaid_purchase_id);
    this.cdr.markForCheck();
  }

  private beginPrepaidRedemptionQrCameraStream(): void {
    if (!getIsMobileOrTablet() || !isCameraApiAvailable()) {
      this.prepaidRedemptionQrCameraStreamPromise = null;
      return;
    }
    const request = requestBarcodeCameraStream();
    this.prepaidRedemptionQrCameraStreamPromise = request.then(stream => {
      if (!this.showPrepaidRedemptionQrScanner) {
        stopMediaStream(stream);
        throw Object.assign(new Error('CAMERA_PREVIEW_CLOSED'), { name: 'AbortError' });
      }
      return stream;
    });
  }

  /**
   * CWS-1914: Redeem Prepaid is offered on any order request, not only where the vendor
   * membership row applies — a shop can sell prepaid books without vendor membership redeem
   * ever being switched on, and the button lived in a row that only that flag rendered.
   */
  showOrderRequestPrepaidRedeemScanOption(): boolean {
    if (!this.showOrderRequestCheckoutLayout()) {
      return false;
    }
    return !this.isOrderRequestPrepaidRedeemContext();
  }

  /**
   * CWS-1914: the whole "Redeem Existing Membership" row.
   *
   * Hidden outright once the modal is a prepaid redemption: the attendant is here to draw a
   * wash off a book that is already open, so neither a membership barcode nor a second QR scan
   * is anything they are about to do. Kept separate from
   * `showOrderRequestMembershipVerifySection()`, which also gates a Way lookup and the vendor
   * lookup's placement and must keep answering for those.
   */
  showOrderRequestRedeemRow(): boolean {
    if (this.isOrderRequestPrepaidRedeemEntryPoint()) {
      return false;
    }
    return (
      this.showOrderRequestMembershipVerifyGroup() || this.showOrderRequestPrepaidRedeemScanOption()
    );
  }

  /**
   * The membership half of the redeem row, as actually rendered on this card.
   *
   * CWS-1266 hides the membership options for an expired DRB member, whose banner carries a
   * Refresh button instead. That used to hide the whole row, which was fine while the row held
   * nothing else; CWS-1914 put Redeem Prepaid in the same row, and the prepaid scan has nothing
   * to do with the vendor's membership — so an expired DRB plate made the prepaid button come
   * and go from one car to the next. The replacement now takes only the group it replaces.
   * `showOrderRequestMembershipVerifySection()` stays the capability check the Way and vendor
   * lookups key on.
   */
  showOrderRequestMembershipVerifyGroup(): boolean {
    return (
      this.showOrderRequestMembershipVerifySection() && !this.vendorLookupReplacesVerifySection
    );
  }

  /** CWS-1914: the rule between the two groups only earns its place when both are shown. */
  get showOrderRequestRedeemRowDivider(): boolean {
    return (
      this.showOrderRequestMembershipVerifyGroup() && this.showOrderRequestPrepaidRedeemScanOption()
    );
  }

  /**
   * Normalize a scanned/uploaded barcode and redeem via vendor-subscription-redeem.
   * `origin` decides which surface a failed scan returns to — the camera
   * scanner modal, or the shop-device wait overlay.
   */
  private lookupVendorMembershipFromBarcode(
    rawValue: string,
    origin: VendorMembershipScanOrigin
  ): void {
    this.vendorMembershipLookupOrigin = origin;

    const sourceId = extractVendorSourceIdFromBarcode(rawValue, {
      // DRB barcodes are customerCode values — keep hyphens/plus intact.
      preserveSeparators: true,
    });
    if (!sourceId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid barcode',
        detail: 'Could not read a membership ID from that barcode.',
        life: 5000,
      });
      this.resetVendorMembershipScanSurface();
      return;
    }

    this.submitVendorMembershipRedeem(sourceId, origin);
  }

  /**
   * POST vendor-subscription-redeem. When the lane plate is not yet on the barcode
   * customer, the API returns `needs_confirmation` and staff must confirm link/replace.
   */
  private submitVendorMembershipRedeem(
    sourceId: string,
    origin: VendorMembershipScanOrigin,
    confirmVehicleAction?: 'link' | 'replace'
  ): void {
    const redeemPayload = this.buildVendorSubscriptionRedeemPayload(sourceId, confirmVehicleAction);
    if (!redeemPayload) {
      this.messageService.add({
        severity: 'error',
        summary: 'Missing shop location',
        detail: 'Select the shop location before scanning the membership barcode.',
        life: 6000,
      });
      this.resetVendorMembershipBarcodeScanner();
      return;
    }

    this.isVendorMembershipLookupLoading = true;
    this.logModalInteraction('orderRequest.membershipVerify.scanned', {
      origin,
      sourceIdLength: sourceId.length,
      confirmVehicleAction: confirmVehicleAction ?? null,
    });

    this.apiService.redeemVendorSubscription(redeemPayload).subscribe({
      next: response => {
        this.isVendorMembershipLookupLoading = false;

        const data = response.data;
        if (data?.needs_confirmation && data.confirmation_type) {
          this.promptVendorMembershipVehicleLinkConfirmation(sourceId, origin, data);
          this.cdr.markForCheck();
          return;
        }

        this.handleVendorMembershipRedeemSuccess(response, origin);
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        this.isVendorMembershipLookupLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Redeem failed',
          detail: extractErrorMessage(error, 'Could not verify membership. Please try again.'),
          life: 6000,
        });
        this.resetVendorMembershipScanSurface();
        this.cdr.markForCheck();
      },
    });
  }

  /** CWS-1038: confirm linking or replacing the lane plate before vendor redeem proceeds. */
  private promptVendorMembershipVehicleLinkConfirmation(
    sourceId: string,
    origin: VendorMembershipScanOrigin,
    data: VendorSubscriptionLookupData
  ): void {
    const lanePlate = String(data.lane_plate ?? '')
      .trim()
      .toUpperCase();
    const existingPlate = String(data.existing_plate ?? '')
      .trim()
      .toUpperCase();
    const vendorLabel = this.formatVendorMembershipSourceLabel(data.customer?.source);
    const isReplace = data.confirmation_type === 'replace';
    const confirmAction = isReplace ? 'replace' : 'link';

    this.pendingVendorMembershipVehicleLinkRedeem = {
      sourceId,
      origin,
      confirmAction,
    };
    this.vendorMembershipVehicleLinkConfirmData = {
      title: isReplace ? 'Replace license plate?' : 'Link license plate?',
      description: isReplace
        ? `This customer already has license plate ${existingPlate || 'on file'} linked to ${vendorLabel} with an active membership. Replace it with ${lanePlate}?`
        : `Link license plate ${lanePlate} to this ${vendorLabel} membership customer and redeem this visit?`,
      cancelText: 'Cancel',
      confirmText: isReplace ? 'Replace and redeem' : 'Link and redeem',
      showCancelButton: true,
      showConfirmButton: true,
    };
    this.showVendorMembershipVehicleLinkConfirm = true;
    this.cdr.markForCheck();
  }

  onVendorMembershipVehicleLinkConfirmCancel(): void {
    this.showVendorMembershipVehicleLinkConfirm = false;
    this.pendingVendorMembershipVehicleLinkRedeem = null;
    this.messageService.add({
      severity: 'info',
      summary: 'Redeem cancelled',
      detail: 'Membership was not redeemed. Handle this visit manually if needed.',
      life: 5000,
    });
    this.resetVendorMembershipScanSurface();
    this.cdr.markForCheck();
  }

  onVendorMembershipVehicleLinkConfirmAccept(): void {
    const pending = this.pendingVendorMembershipVehicleLinkRedeem;
    this.showVendorMembershipVehicleLinkConfirm = false;
    this.pendingVendorMembershipVehicleLinkRedeem = null;
    if (!pending) {
      return;
    }
    this.submitVendorMembershipRedeem(pending.sourceId, pending.origin, pending.confirmAction);
  }

  private formatVendorMembershipSourceLabel(source?: string | null): string {
    const normalized = String(source ?? '')
      .trim()
      .toLowerCase();
    if (normalized === 'drb') {
      return 'DRB';
    }
    if (normalized === 'everwash') {
      return 'EverWash';
    }
    if (!normalized) {
      return 'vendor';
    }
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }

  private handleVendorMembershipRedeemSuccess(
    response: ApiResponse<VendorSubscriptionLookupData>,
    origin: VendorMembershipScanOrigin
  ): void {
    if (response.status && response.data?.found && response.data.redeemed) {
      this.showVendorMembershipBarcodeScanner = false;
      this.orderRequestMembershipShopDeviceWaiting = false;
      this.applyVendorLookupCustomerVehicleIds(response.data);
      this.rememberVendorActiveSubscriptionOmitWashId(
        response.data.subscription_wash_id ??
          response.data.redemption?.data?.['subscription_wash_id']
      );
      this.vendorActiveSubscriptionLookupDisplay = buildVendorActiveSubscriptionDisplayFromLookup(
        response.data,
        this.getVendorMembershipOrderRequestLicensePlate()
      );
      this.showVendorActiveSubscriptionLookupModal = true;
      this.ensureVendorActiveSubscriptionWashHistory();
      this.ordersService.refreshOrders();
      this.messageService.add({
        severity: 'success',
        summary: 'Membership redeemed',
        detail: 'Subscription verified and linked to this lane visit.',
        life: 5000,
      });
      return;
    }

    if (response.status && response.data?.found && response.data.subscription) {
      this.showVendorMembershipBarcodeScanner = false;
      this.orderRequestMembershipShopDeviceWaiting = false;
      this.applyVendorLookupCustomerVehicleIds(response.data);
      this.vendorActiveSubscriptionLookupDisplay = buildVendorActiveSubscriptionDisplayFromLookup(
        response.data,
        this.getVendorMembershipOrderRequestLicensePlate()
      );
      this.showVendorActiveSubscriptionLookupModal = true;
      this.ensureVendorActiveSubscriptionWashHistory();
      return;
    }

    const customerInactive = response.data?.reason === CUSTOMER_INACTIVE_REFUSAL_REASON;
    const inactiveScannedCustomerId = String(response.data?.customer?.id ?? '').trim();
    if (customerInactive && inactiveScannedCustomerId) {
      this.showInactiveScannedCustomer(inactiveScannedCustomerId);
      return;
    }
    this.messageService.add({
      severity: 'warn',
      summary: customerInactive ? 'Customer inactive' : 'Membership not found',
      detail:
        response.message || 'No active vendor subscription found for this membership barcode.',
      life: 6000,
    });
    this.resetVendorMembershipScanSurface();
  }

  /**
   * CWS-1555: a scan refused for an inactive member closes the scanner and puts the inactive
   * message, with its "Reactivate customer" action, in the dialog footer. Re-opening the
   * scanner (as for other refusals) would hide that action behind the scan overlay.
   */
  private showInactiveScannedCustomer(customerId: string): void {
    this.vendorScanInactiveCustomerId = customerId;
    this.onVendorMembershipBarcodeScannerClose();
    this.closeOrderRequestMembershipShopDeviceWaiting();
    this.updateOrderError = CUSTOMER_INACTIVE_MESSAGE;
    this.messageService.add({
      severity: 'warn',
      summary: 'Customer inactive',
      detail: CUSTOMER_INACTIVE_MESSAGE,
      life: 6000,
    });
  }

  /** Prefer lookup/redeem customer + vehicle ids when present (form may already have lane ids). */
  private applyVendorLookupCustomerVehicleIds(data: VendorSubscriptionLookupData): void {
    const customerId = String(data.customer?.id ?? '').trim();
    const vehicleId = String(data.vehicle?.id ?? '').trim();
    if (customerId) {
      this.formData.customerId = customerId;
    }
    if (vehicleId) {
      this.formData.vehicleId = vehicleId;
    }
  }

  /**
   * Sync customer/vehicle ids then load shared wash-history for vendor Active subscription.
   * Bypasses sales-panel upsell greeting short-circuit.
   */
  private ensureVendorActiveSubscriptionWashHistory(): void {
    this.syncCustomerVehicleIdsForVendorActiveSubscription();
    const customerId = String(this.formData.customerId || '').trim();
    const vehicleId = String(this.formData.vehicleId || '').trim();
    if (!customerId || !vehicleId) {
      return;
    }
    this.seedVendorActiveSubscriptionOmitWashIdFromNotification();
    this.loadWashHistoryInsightForCurrentContext();
  }

  private rememberVendorActiveSubscriptionOmitWashId(washId: unknown): void {
    const id = String(washId ?? '').trim();
    if (id) {
      this.vendorActiveSubscriptionOmitWashId = id;
    }
  }

  private clearVendorActiveSubscriptionOmitWashId(): void {
    this.vendorActiveSubscriptionOmitWashId = null;
  }

  private seedVendorActiveSubscriptionOmitWashIdFromNotification(): void {
    if (this.vendorActiveSubscriptionOmitWashId) {
      return;
    }
    const nd = (this.notification?.data ?? {}) as Record<string, any>;
    this.rememberVendorActiveSubscriptionOmitWashId(nd['subscription_wash_id']);
  }

  private resolveVendorActiveSubscriptionMembershipOrderId(): string | null {
    const nd = (this.notification?.data ?? {}) as Record<string, any>;
    const fromNotification = String(nd['order_id'] ?? '').trim();
    if (fromNotification) {
      return fromNotification;
    }
    const fromDisplay = String(this.getVendorActiveSubscriptionDisplay()?.orderId ?? '').trim();
    return fromDisplay || null;
  }

  /**
   * History filters for the informational Active subscription modal (LPR / image
   * auto-redeem). Excludes the wash that opened this modal when known so the
   * attendant sees prior visits only. Vendor membership orders are omitted
   * server-side (non–Way order sources).
   */
  private resolveVendorActiveSubscriptionHistoryFilters(): OrderRequestHistoryFetchFilters {
    if (!this.shouldShowVendorActiveSubscriptionModal()) {
      return {};
    }

    this.seedVendorActiveSubscriptionOmitWashIdFromNotification();
    const washId = String(this.vendorActiveSubscriptionOmitWashId ?? '').trim() || null;

    return {
      ...(washId ? { excludeSubscriptionWashId: washId } : {}),
    };
  }

  /**
   * First-paint fallback when exclude_subscription_wash_id was unknown at request time
   * (e.g. older LPR payload without wash id). Applies to all Active subscription opens.
   */
  private applyVendorActiveSubscriptionClientOmitFallback(response: any): any {
    if (!this.shouldShowVendorActiveSubscriptionModal()) {
      return response;
    }
    if (this.vendorActiveSubscriptionOmitWashId) {
      // Backend should already have excluded; no-op if absent from rows.
      return omitSubscriptionWashFromHistoryResponse(
        response,
        this.vendorActiveSubscriptionOmitWashId
      );
    }
    // LPR active-subscription always creates a wash; resolve newest for this membership order.
    if (this.notification?.type !== 'active-subscription') {
      return response;
    }
    const rows = Array.isArray(response?.data) ? response.data : [];
    const washId = resolveSubscriptionWashIdToOmit({
      membershipOrderId: this.resolveVendorActiveSubscriptionMembershipOrderId(),
      historyRows: rows,
    });
    if (!washId) {
      return response;
    }
    this.vendorActiveSubscriptionOmitWashId = washId;
    return omitSubscriptionWashFromHistoryResponse(response, washId);
  }

  private syncCustomerVehicleIdsForVendorActiveSubscription(): void {
    const nd = (this.notification?.data ?? {}) as Record<string, any>;
    const customerId = String(
      this.formData.customerId || nd['customer_id'] || nd['customerId'] || ''
    ).trim();
    const vehicleId = String(
      this.formData.vehicleId || nd['vehicle_id'] || nd['vehicleId'] || ''
    ).trim();
    if (customerId) {
      this.formData.customerId = customerId;
    }
    if (vehicleId) {
      this.formData.vehicleId = vehicleId;
    }
  }

  /** Location for vendor redeem — same fallbacks as Way lookup (form, notification, staff shop). */
  private resolveVendorRedeemLocationId(): string {
    return String(this.getOrderRequestWayLookupLocationId() ?? '').trim();
  }

  private buildVendorSubscriptionRedeemPayload(
    sourceId: string,
    confirmVehicleAction?: 'link' | 'replace'
  ): VendorSubscriptionRedeemRequest | null {
    const nd = (this.notification?.data ?? {}) as Record<string, any>;
    const lprEntryId = this.resolveOpenModalLprEntryId();
    const locationId = this.resolveVendorRedeemLocationId();
    if (!locationId) {
      return null;
    }

    const plateFromForm = this.buildLprEntryPlateFromForm();
    const plateNumber =
      plateFromForm || String(nd['plate'] ?? nd['vehicle_license_plate'] ?? '').trim();
    const state =
      this.resolveLprEntryStateFromForm() ||
      String(nd['state'] ?? nd['vehicle_state'] ?? '').trim() ||
      undefined;
    const roiRaw = nd['roi_id'] ?? nd['roiID'] ?? nd['roiId'];
    const roiId = roiRaw !== null && roiRaw !== undefined ? String(roiRaw).trim() : '';

    const vehicleId = String(
      this.formData.vehicleId || nd['vehicle_id'] || nd['vehicleId'] || ''
    ).trim();

    return {
      source_id: sourceId,
      location_id: locationId,
      ...(lprEntryId ? { lpr_entry_id: lprEntryId } : {}),
      ...(plateNumber ? { plate_number: plateNumber } : {}),
      ...(state ? { state } : {}),
      ...(vehicleId ? { vehicle_id: vehicleId } : {}),
      ...(roiId ? { roi_id: roiId } : {}),
      ...(confirmVehicleAction ? { confirm_vehicle_action: confirmVehicleAction } : {}),
      wash_date: this.getTodayDate(),
      timezone: this.getTimeZone(),
    };
  }

  /**
   * Plate of the vehicle in the current order request, for the membership result
   * screen. Prefers the form value (staff may have corrected the LPR read) and
   * falls back to the notification payload. Plate only, no state code.
   */
  private getVendorMembershipOrderRequestLicensePlate(): string {
    const formPlate = String(this.formData?.vehicleLicensePlate ?? '').trim();
    if (formPlate) {
      const data = (this.notification?.data ?? {}) as Record<string, any>;
      const hintState = coerceUsStateCode(
        String(data['state'] ?? data['vehicle_state'] ?? '').trim() || null
      );
      const { license_plate } = splitLicensePlateForApi(formPlate, hintState);
      return (license_plate || formPlate).trim().toUpperCase();
    }
    return this.getOrderSuccessLicensePlateDisplay();
  }

  /** Return the surface the failed scan came from to a ready-to-scan-again state. */
  private resetVendorMembershipScanSurface(): void {
    if (this.vendorMembershipLookupOrigin === 'device') {
      // The wait overlay stays open and its wedge listener stays live, so the
      // next scan is picked up without staff reopening anything. Any manually
      // typed value is left in place so a typo can be corrected.
      this.orderRequestMembershipShopDeviceWaiting = true;
      return;
    }
    this.resetVendorMembershipBarcodeScanner();
  }

  private resetVendorMembershipBarcodeScanner(): void {
    this.showVendorMembershipBarcodeScanner = false;
    this.cdr.detectChanges();
    if (!this.vendorMembershipScannerUploadOnly) {
      this.beginVendorMembershipCameraStream();
    }
    this.showVendorMembershipBarcodeScanner = true;
    this.cdr.markForCheck();
  }

  /**
   * Start getUserMedia in the current call stack (user tap). Android WebView
   * often ignores camera permission unless the request is tied to that gesture.
   */
  private beginVendorMembershipCameraStream(): void {
    if (
      this.vendorMembershipScannerUploadOnly ||
      !getIsMobileOrTablet() ||
      !isCameraApiAvailable()
    ) {
      this.vendorMembershipCameraStreamPromise = null;
      return;
    }

    const request = requestBarcodeCameraStream();
    this.vendorMembershipCameraStreamPromise = request.then(stream => {
      if (!this.showVendorMembershipBarcodeScanner) {
        stopMediaStream(stream);
        throw Object.assign(new Error('CAMERA_PREVIEW_CLOSED'), { name: 'AbortError' });
      }
      return stream;
    });
  }

  /** Wait overlay for "Using the shop's device" (USB / Bluetooth barcode scanner). */
  orderRequestMembershipShopDeviceWaiting = false;

  /** Which surface produced the in-flight scan, so failures reset the right one. */
  private vendorMembershipLookupOrigin: VendorMembershipScanOrigin = 'camera';

  /** True only while a device-originated lookup is in flight. */
  get isVendorMembershipDeviceLookupLoading(): boolean {
    return this.isVendorMembershipLookupLoading && this.vendorMembershipLookupOrigin === 'device';
  }

  /** Opens the wait overlay above the order request and starts listening for a scan. */
  onOrderRequestMembershipShopDevice(): void {
    this.logModalInteraction('orderRequest.membershipVerify.shopDevice');
    this.vendorMembershipLookupOrigin = 'device';
    this.orderRequestMembershipShopDeviceWaiting = true;
    this.cdr.markForCheck();
  }

  onVendorMembershipDeviceScanned(rawValue: string): void {
    this.lookupVendorMembershipFromBarcode(rawValue, 'device');
  }

  onVendorMembershipDeviceScanClose(): void {
    if (this.isVendorMembershipDeviceLookupLoading) {
      return;
    }
    this.closeOrderRequestMembershipShopDeviceWaiting();
  }

  closeOrderRequestMembershipShopDeviceWaiting(): void {
    this.orderRequestMembershipShopDeviceWaiting = false;
    this.cdr.markForCheck();
  }

  /** Payment methods / summary for create-payment-request once an order exists. */
  showPaymentCheckoutLayout(): boolean {
    return (
      this.notification?.type === 'create-payment-request' &&
      !this.isEditMode &&
      this.hasCheckoutOrderId()
    );
  }

  /**
   * Plate crop URL (`plate_image_s3_url`) for the order-request preview column.
   * Only the top-level `notification.data.plate_image_s3_url` is shown; nested keys and pinned overrides are ignored when null.
   */
  get orderRequestPlateImageS3Url(): string | null {
    if (!this.showOrderRequestCheckoutLayout()) {
      return null;
    }
    return this.resolveTopLevelNotificationImageS3Url(
      this.notification?.data as Record<string, any> | undefined,
      'plate_image_s3_url'
    );
  }

  /** Full lane / vehicle capture URL (`image_s3_url`) shown beside the plate crop. */
  get orderRequestVehicleImageS3Url(): string | null {
    if (!this.showOrderRequestCheckoutLayout()) {
      return null;
    }
    return this.resolveTopLevelNotificationImageS3Url(
      this.notification?.data as Record<string, any> | undefined,
      'image_s3_url'
    );
  }

  /** Read a lane image URL from top-level `notification.data` only (null / empty → hidden). */
  private resolveTopLevelNotificationImageS3Url(
    data: Record<string, any> | undefined | null,
    key: 'plate_image_s3_url' | 'image_s3_url'
  ): string | null {
    if (!data) {
      return null;
    }
    const raw = data[key];
    if (raw === null || raw === '') {
      return null;
    }
    return typeof raw === 'string' && raw.trim().length > 0 ? raw.trim() : null;
  }

  /** True when the API/list payload explicitly cleared a top-level lane image field. */
  private isTopLevelImageFieldExplicitlyEmpty(
    data: Record<string, any> | undefined | null,
    key: 'plate_image_s3_url' | 'image_s3_url'
  ): boolean {
    if (!data) {
      return false;
    }
    return (
      Object.prototype.hasOwnProperty.call(data, key) && (data[key] === null || data[key] === '')
    );
  }

  /** @deprecated Use {@link orderRequestPlateImageS3Url} — kept for existing preload helpers. */
  get orderRequestLprAxisPlateImageUrl(): string | null {
    return this.orderRequestPlateImageS3Url;
  }

  /** Whether the enlarged full-vehicle image preview overlay is open. */
  orderRequestVehicleImagePreviewOpen = false;

  /**
   * Image URL shown in the enlarged preview when the plate thumbnail is clicked.
   * Prefers the full vehicle frame (`image_s3_url`), falling back to the plate crop, but skips any
   * source already known to have failed to load so a click never opens a broken full-image modal.
   * Returns null when only failed URLs remain, which also disables opening the preview.
   */
  get orderRequestLprVehicleImageUrl(): string | null {
    const vehicleUrl = this.orderRequestVehicleImageS3Url;
    if (vehicleUrl && this.orderRequestVehicleImageLoadFailedUrl !== vehicleUrl) {
      return vehicleUrl;
    }
    const plateUrl = this.orderRequestPlateImageS3Url;
    if (plateUrl && !this.orderRequestLprAxisPlateImageLoadFailed) {
      return plateUrl;
    }
    return null;
  }

  /** True when an image is available to enlarge from the plate thumbnail. */
  canOpenOrderRequestVehicleImagePreview(): boolean {
    return !!this.orderRequestLprVehicleImageUrl;
  }

  openOrderRequestVehicleImagePreview(): void {
    if (!this.canOpenOrderRequestVehicleImagePreview()) {
      return;
    }
    this.orderRequestVehicleImagePreviewOpen = true;
  }

  closeOrderRequestVehicleImagePreview(): void {
    this.orderRequestVehicleImagePreviewOpen = false;
  }

  /** Plate shown as the image preview overlay title (mirrors the notification table overlay). */
  get orderRequestVehicleImagePreviewTitle(): string {
    const data = this.notification?.data as Record<string, any> | undefined;
    const rawPlate =
      (typeof data?.['plate'] === 'string' ? (data['plate'] as string) : '') ||
      (this.formData?.vehicleLicensePlate ?? '');
    const plate = rawPlate.trim();
    return plate ? plate.toUpperCase() : 'Vehicle view';
  }

  /** Reserve half-width plate row when at least one LPR preview slot is shown or expected. */
  showOrderRequestLprAxisPlatePreview(): boolean {
    if (!this.showOrderRequestCheckoutLayout()) {
      return false;
    }
    if (!this.doScanImagesMatchCurrentPlate()) {
      return false;
    }
    if (
      this.isOrderRequestPlateImagePreviewVisible() ||
      this.isOrderRequestVehicleImagePreviewVisible()
    ) {
      return true;
    }
    if (this.orderRequestLprAxisPlateImageLoadFailed) {
      return true;
    }
    const data = this.notification?.data as Record<string, any> | undefined;
    return notificationDataShowsLprPlatePreviewColumn(data, this.notification?.type);
  }

  /** Plate crop slot is shown while loading or ready; hidden after load failure without a URL. */
  isOrderRequestPlateImagePreviewVisible(): boolean {
    if (this.orderRequestLprAxisPlateImageLoadFailed && !this.orderRequestPlateImageS3Url) {
      return false;
    }
    if (this.orderRequestPlateImageS3Url) {
      return (
        this.isOrderRequestLprAxisPlateImageLoading() || this.isOrderRequestLprAxisPlateImageReady()
      );
    }
    return this.isOrderRequestLprAxisPlateImageSlotPending();
  }

  /** Plate slot (loader, crop, or compact "not available") — distinct from the vehicle capture slot. */
  isOrderRequestPlateImageSlotShown(): boolean {
    return (
      this.isOrderRequestPlateImagePreviewVisible() ||
      this.isOrderRequestPlateImageSlotUnavailable()
    );
  }

  /**
   * Plate crop is settled but missing or failed while the preview column is shown.
   * Vehicle capture may still render in the adjacent slot (e.g. app photo upload).
   */
  isOrderRequestPlateImageSlotUnavailable(): boolean {
    if (!this.showOrderRequestCheckoutLayout() || !this.showOrderRequestLprAxisPlatePreview()) {
      return false;
    }
    if (this.isOrderRequestLprAxisPlateImageSlotPending()) {
      return false;
    }
    if (this.isOrderRequestLprAxisPlateImageLoading()) {
      return false;
    }
    if (this.isOrderRequestLprAxisPlateImageReady()) {
      return false;
    }
    return true;
  }

  /** Vehicle capture slot is shown until its `<img>` fails for the current URL.
   * UI license-plate image scan is plate-only (no vehicle frame). */
  isOrderRequestVehicleImagePreviewVisible(): boolean {
    if (this.isUiLicensePlateImageScanOrderRequest()) {
      return false;
    }
    const url = this.orderRequestVehicleImageS3Url;
    return !!url && this.orderRequestVehicleImageLoadFailedUrl !== url;
  }

  /** True when exactly one preview image slot is visible (plate-only or vehicle-only). */
  orderRequestLprPreviewShowsSingleImage(): boolean {
    const visibleCount =
      (this.isOrderRequestPlateImageSlotShown() ? 1 : 0) +
      (this.isOrderRequestVehicleImagePreviewVisible() ? 1 : 0);
    return visibleCount === 1;
  }

  /** Spinner while waiting for a URL or until the `<img>` fires `load`. */
  isOrderRequestLprAxisPlateImageLoading(): boolean {
    if (this.orderRequestLprAxisPlateImageLocked) {
      return false;
    }
    if (this.orderRequestLprAxisPlateImageLoadFailed) {
      return false;
    }
    const url = this.orderRequestLprAxisPlateImageUrl?.trim();
    if (url) {
      const loaded = this.orderRequestLprAxisPlateImageLoadedUrl?.trim();
      return !loaded || !this.lprAxisPlateImageUrlsMatch(loaded, url);
    }
    return this.isOrderRequestLprAxisPlateImageSlotPending();
  }

  isOrderRequestLprAxisPlateImageReady(): boolean {
    if (this.orderRequestLprAxisPlateImageLoadFailed) {
      return false;
    }
    if (this.orderRequestLprAxisPlateImageLocked) {
      return true;
    }
    // Render `<img>` as soon as a URL is known; load/error handlers confirm availability.
    return (
      !!this.orderRequestLprAxisPlateImageUrl?.trim() &&
      this.orderRequestLprAxisPlateImageSrcMounted
    );
  }

  /** @deprecated Use {@link isOrderRequestPlateImageSlotUnavailable}. */
  isOrderRequestLprAxisPlateImageUnavailable(): boolean {
    return this.isOrderRequestPlateImageSlotUnavailable();
  }

  onOrderRequestLprAxisPlateImageLoad(): void {
    if (this.orderRequestLprAxisPlateImageLoadFailed) {
      return;
    }
    const url = this.orderRequestLprAxisPlateImageUrl?.trim();
    if (!url) {
      return;
    }
    this.orderRequestLprAxisPlateImageLoadedUrl = url;
    this.orderRequestLprAxisPlateImageLoadFailed = false;
    this.orderRequestLprAxisPlateImageLoadAttempt = 0;
    this.clearOrderRequestLprAxisPlateImagePreloadTimer();
    this.clearOrderRequestLprAxisPlateImageRetryTimers();
    this.lockOrderRequestLprAxisPlateImage();
    this.cdr.detectChanges();
  }

  /** Spinner while the vehicle capture URL is present but its `<img>` has not yet fired `load`. */
  isOrderRequestVehicleImageLoading(): boolean {
    const url = this.orderRequestVehicleImageS3Url;
    if (!url || this.orderRequestVehicleImageLoadFailedUrl === url) {
      return false;
    }
    return this.orderRequestVehicleImageLoadedUrl !== url;
  }

  /** Render the vehicle `<img>` while a (non-failed) URL is present and the element is mounted. */
  isOrderRequestVehicleImageReady(): boolean {
    const url = this.orderRequestVehicleImageS3Url;
    return (
      !!url &&
      this.orderRequestVehicleImageLoadFailedUrl !== url &&
      this.orderRequestVehicleImageSrcMounted
    );
  }

  onOrderRequestVehicleImageLoad(): void {
    const url = this.orderRequestVehicleImageS3Url;
    if (!url) {
      return;
    }
    this.orderRequestVehicleImageLoadedUrl = url;
    this.orderRequestVehicleImageLoadAttempt = 0;
    this.clearOrderRequestVehicleImageRetryTimers();
    // No manual detectChanges(): this fires from a DOM `(load)` event, so Angular runs change
    // detection automatically afterwards. This component is mounted in the app shell (sibling of
    // <router-outlet>), so a re-entrant detectChanges() here could throw during app-shell CD and
    // blank the entire SPA — see CWS-461.
  }

  onOrderRequestVehicleImageError(): void {
    this.handleOrderRequestVehicleImageLoadFailure('img_error');
  }

  /**
   * Vehicle capture is optional, but S3/CDN may hand back the presigned URL before the object is
   * readable (upload still in flight), or the first fetch may fail on flaky kiosk Wi‑Fi. Retry with
   * backoff until load succeeds or attempts are exhausted, then hide the slot gracefully (no
   * broken-image icon). Never mutate the presigned URL — remount the element instead.
   */
  private handleOrderRequestVehicleImageLoadFailure(reason: string): void {
    if (this.orderRequestVehicleImageRetryTimer != null) {
      return;
    }
    const url = this.orderRequestVehicleImageS3Url;
    if (!url || this.orderRequestVehicleImageLoadFailedUrl === url) {
      return;
    }

    this.clearOrderRequestVehicleImageAttemptTimer();
    this.orderRequestVehicleImageLoadAttempt += 1;

    if (
      this.orderRequestVehicleImageLoadAttempt >=
      NotificationModalComponent.ORDER_REQUEST_VEHICLE_IMAGE_MAX_LOAD_ATTEMPTS
    ) {
      this.logModalInteraction('vehicleImage.load.giveUp', {
        reason,
        attempts: this.orderRequestVehicleImageLoadAttempt,
      });
      this.markOrderRequestVehicleImageFailed(url);
      return;
    }

    const delayIndex = Math.min(
      this.orderRequestVehicleImageLoadAttempt - 1,
      NotificationModalComponent.ORDER_REQUEST_VEHICLE_IMAGE_RETRY_DELAYS_MS.length - 1
    );
    const delayMs =
      NotificationModalComponent.ORDER_REQUEST_VEHICLE_IMAGE_RETRY_DELAYS_MS[delayIndex];

    this.logModalInteraction('vehicleImage.load.retryScheduled', {
      reason,
      attempt: this.orderRequestVehicleImageLoadAttempt,
      delayMs,
    });

    this.orderRequestVehicleImageRetryTimer = setTimeout(() => {
      this.orderRequestVehicleImageRetryTimer = null;
      this.ngZone.run(() =>
        this.runGuardedVehicleImageStep(() => this.retryOrderRequestVehicleImageLoad())
      );
    }, delayMs);
  }

  private retryOrderRequestVehicleImageLoad(): void {
    const url = this.orderRequestVehicleImageS3Url;
    if (!url || this.orderRequestVehicleImageLoadFailedUrl === url) {
      return;
    }

    // Remount the `<img>` so the browser re-fetches the same presigned URL (no query-param
    // cache-busting: that would break the SigV4 signature). The unmount and the remount each run
    // as their own zone task, so Angular runs change detection after each — no manual (and
    // potentially re-entrant) detectChanges() is needed.
    this.orderRequestVehicleImageLoadedUrl = null;
    this.orderRequestVehicleImageSrcMounted = false;

    setTimeout(() => {
      this.ngZone.run(() =>
        this.runGuardedVehicleImageStep(() => {
          const currentUrl = this.orderRequestVehicleImageS3Url;
          if (!currentUrl || this.orderRequestVehicleImageLoadFailedUrl === currentUrl) {
            return;
          }
          this.orderRequestVehicleImageSrcMounted = true;
          this.scheduleOrderRequestVehicleImageAttemptTimeout();
        })
      );
    }, 0);
  }

  /** Treat a hung vehicle `<img>` request (no load/error) like a failure so retries continue. */
  private scheduleOrderRequestVehicleImageAttemptTimeout(): void {
    this.clearOrderRequestVehicleImageAttemptTimer();
    if (!this.orderRequestVehicleImageSrcMounted) {
      return;
    }
    const url = this.orderRequestVehicleImageS3Url;
    if (!url || this.orderRequestVehicleImageLoadFailedUrl === url) {
      return;
    }

    this.orderRequestVehicleImageAttemptTimer = setTimeout(() => {
      this.orderRequestVehicleImageAttemptTimer = null;
      this.ngZone.run(() =>
        this.runGuardedVehicleImageStep(() => {
          if (this.orderRequestVehicleImageLoadedUrl === this.orderRequestVehicleImageS3Url) {
            return;
          }
          this.handleOrderRequestVehicleImageLoadFailure('attempt_timeout');
        })
      );
    }, NotificationModalComponent.ORDER_REQUEST_VEHICLE_IMAGE_ATTEMPT_TIMEOUT_MS);
  }

  private markOrderRequestVehicleImageFailed(url: string): void {
    this.clearOrderRequestVehicleImageRetryTimers();
    this.orderRequestVehicleImageLoadFailedUrl = url;
    // Runs inside a zone context (DOM event or ngZone.run timer) → automatic change detection.
  }

  /**
   * Run a vehicle-image retry step behind a hard guard. This component is mounted in the app shell
   * (sibling of <router-outlet>), so an uncaught throw during its change detection would blank the
   * whole SPA (CWS-461). Contain any error, log it, and gracefully hide the optional vehicle slot.
   */
  private runGuardedVehicleImageStep(step: () => void): void {
    try {
      step();
    } catch (error) {
      this.logModalInteraction('vehicleImage.retry.error', {
        message: (error as Error)?.message,
      });
      const url = this.orderRequestVehicleImageS3Url;
      this.clearOrderRequestVehicleImageRetryTimers();
      if (url) {
        this.orderRequestVehicleImageLoadFailedUrl = url;
      }
    }
  }

  private clearOrderRequestVehicleImageAttemptTimer(): void {
    if (this.orderRequestVehicleImageAttemptTimer != null) {
      clearTimeout(this.orderRequestVehicleImageAttemptTimer);
      this.orderRequestVehicleImageAttemptTimer = null;
    }
  }

  private clearOrderRequestVehicleImageRetryTimers(): void {
    if (this.orderRequestVehicleImageRetryTimer != null) {
      clearTimeout(this.orderRequestVehicleImageRetryTimer);
      this.orderRequestVehicleImageRetryTimer = null;
    }
    this.clearOrderRequestVehicleImageAttemptTimer();
  }

  private resetOrderRequestVehicleImageLoadState(): void {
    this.clearOrderRequestVehicleImageRetryTimers();
    this.orderRequestVehicleImageLoadFailedUrl = null;
    this.orderRequestVehicleImageLoadedUrl = null;
    this.orderRequestVehicleImageSrcMounted = true;
    this.orderRequestVehicleImageLoadAttempt = 0;
  }

  onOrderRequestLprAxisPlateImageError(): void {
    this.handleOrderRequestLprAxisPlateImageLoadFailure('img_error');
  }

  private isOrderRequestLprAxisPlateImageSlotPending(): boolean {
    if (!this.showOrderRequestCheckoutLayout()) {
      return false;
    }
    if (this.orderRequestPlateImageS3Url || this.orderRequestVehicleImageS3Url) {
      return false;
    }
    if (this.orderRequestSuppressAxisImageAwaitingAfterLprUpdate) {
      return false;
    }
    if (this.orderRequestLprAxisPlateImageApiHydrationPending) {
      return true;
    }
    const data = this.notification?.data as Record<string, any> | undefined;
    return notificationDataIndicatesLprAxisImageAwaitingPusher(data);
  }

  private resetOrderRequestLprAxisPlateImageLoadState(): void {
    this.clearOrderRequestLprAxisPlateImagePreloadTimer();
    this.clearOrderRequestLprAxisPlateImageRetryTimers();
    this.orderRequestLprAxisPlateImageLoadedUrl = null;
    this.orderRequestLprAxisPlateImageLoadFailed = false;
    this.orderRequestLprAxisPlateImageLocked = false;
    this.orderRequestLprAxisPlateImageSrcMounted = true;
    this.orderRequestLprAxisPlateImageLoadAttempt = 0;
    this.resetOrderRequestVehicleImageLoadState();
  }

  /** After first successful preload, ignore later URL changes (Pusher/API/update events). */
  private lockOrderRequestLprAxisPlateImage(): void {
    const loaded = this.orderRequestLprAxisPlateImageLoadedUrl?.trim();
    if (!loaded || this.orderRequestLprAxisPlateImageLoadFailed) {
      return;
    }
    this.orderRequestLprAxisPlateImageLocked = true;
    this.orderRequestLprAxisPlateImageUrlOverride = loaded;
    this.clearOrderRequestLprAxisPlateImagePreloadTimer();
  }

  private clearOrderRequestLprAxisPlateImagePreloadTimer(): void {
    if (this.orderRequestLprAxisPlateImagePreloadTimer != null) {
      clearTimeout(this.orderRequestLprAxisPlateImagePreloadTimer);
      this.orderRequestLprAxisPlateImagePreloadTimer = null;
    }
  }

  private clearOrderRequestLprAxisPlateImageRetryTimers(): void {
    if (this.orderRequestLprAxisPlateImageRetryTimer != null) {
      clearTimeout(this.orderRequestLprAxisPlateImageRetryTimer);
      this.orderRequestLprAxisPlateImageRetryTimer = null;
    }
    if (this.orderRequestLprAxisPlateImageAttemptTimer != null) {
      clearTimeout(this.orderRequestLprAxisPlateImageAttemptTimer);
      this.orderRequestLprAxisPlateImageAttemptTimer = null;
    }
  }

  /**
   * S3 / CDN may return the URL before the object is readable, or the first fetch may fail on flaky kiosk Wi‑Fi.
   * Retry with backoff until load succeeds or attempts are exhausted (then show "Image not available").
   */
  private handleOrderRequestLprAxisPlateImageLoadFailure(reason: string): void {
    if (
      this.orderRequestLprAxisPlateImageLocked ||
      this.orderRequestLprAxisPlateImageLoadFailed ||
      this.orderRequestLprAxisPlateImageRetryTimer != null
    ) {
      return;
    }
    const url = this.orderRequestLprAxisPlateImageUrl?.trim();
    if (!url) {
      this.markOrderRequestLprAxisPlateImageFailed();
      return;
    }

    this.clearOrderRequestLprAxisPlateImageAttemptTimer();
    this.orderRequestLprAxisPlateImageLoadAttempt += 1;

    if (
      this.orderRequestLprAxisPlateImageLoadAttempt >=
      NotificationModalComponent.ORDER_REQUEST_LPR_PLATE_IMAGE_MAX_LOAD_ATTEMPTS
    ) {
      this.logModalInteraction('lprPlateImage.load.giveUp', {
        reason,
        attempts: this.orderRequestLprAxisPlateImageLoadAttempt,
      });
      this.markOrderRequestLprAxisPlateImageFailed();
      return;
    }

    const delayIndex = Math.min(
      this.orderRequestLprAxisPlateImageLoadAttempt - 1,
      NotificationModalComponent.ORDER_REQUEST_LPR_PLATE_IMAGE_RETRY_DELAYS_MS.length - 1
    );
    const delayMs =
      NotificationModalComponent.ORDER_REQUEST_LPR_PLATE_IMAGE_RETRY_DELAYS_MS[delayIndex];

    this.logModalInteraction('lprPlateImage.load.retryScheduled', {
      reason,
      attempt: this.orderRequestLprAxisPlateImageLoadAttempt,
      delayMs,
    });

    this.orderRequestLprAxisPlateImageRetryTimer = setTimeout(() => {
      this.orderRequestLprAxisPlateImageRetryTimer = null;
      this.ngZone.run(() => this.retryOrderRequestLprAxisPlateImageLoad());
    }, delayMs);
  }

  private retryOrderRequestLprAxisPlateImageLoad(): void {
    if (
      this.orderRequestLprAxisPlateImageLocked ||
      this.orderRequestLprAxisPlateImageLoadFailed ||
      !this.orderRequestLprAxisPlateImageUrl?.trim()
    ) {
      return;
    }

    this.orderRequestLprAxisPlateImageLoadedUrl = null;
    this.orderRequestLprAxisPlateImageLoadFailed = false;
    this.orderRequestLprAxisPlateImageSrcMounted = false;
    this.cdr.detectChanges();

    setTimeout(() => {
      if (
        this.orderRequestLprAxisPlateImageLocked ||
        this.orderRequestLprAxisPlateImageLoadFailed ||
        !this.orderRequestLprAxisPlateImageUrl?.trim()
      ) {
        return;
      }
      this.orderRequestLprAxisPlateImageSrcMounted = true;
      this.cdr.detectChanges();
      this.scheduleOrderRequestLprAxisPlateImageAttemptTimeout();
    }, 0);
  }

  /** Treat hung `<img>` requests like errors so we keep retrying until success or max attempts. */
  private scheduleOrderRequestLprAxisPlateImageAttemptTimeout(): void {
    this.clearOrderRequestLprAxisPlateImageAttemptTimer();
    if (
      this.orderRequestLprAxisPlateImageLocked ||
      this.orderRequestLprAxisPlateImageLoadFailed ||
      !this.orderRequestLprAxisPlateImageUrl?.trim() ||
      !this.orderRequestLprAxisPlateImageSrcMounted
    ) {
      return;
    }

    this.orderRequestLprAxisPlateImageAttemptTimer = setTimeout(() => {
      this.orderRequestLprAxisPlateImageAttemptTimer = null;
      this.ngZone.run(() => {
        if (this.orderRequestLprAxisPlateImageLoadedUrl) {
          return;
        }
        this.handleOrderRequestLprAxisPlateImageLoadFailure('attempt_timeout');
      });
    }, NotificationModalComponent.ORDER_REQUEST_LPR_PLATE_IMAGE_ATTEMPT_TIMEOUT_MS);
  }

  private clearOrderRequestLprAxisPlateImageAttemptTimer(): void {
    if (this.orderRequestLprAxisPlateImageAttemptTimer != null) {
      clearTimeout(this.orderRequestLprAxisPlateImageAttemptTimer);
      this.orderRequestLprAxisPlateImageAttemptTimer = null;
    }
  }

  /**
   * After open/API/images-ready had a chance to attach a URL, mark settled LPR payloads without images.
   * Avoids flashing "Image not available" before `lpr-axis-images-ready` or LPR entries API respond.
   */
  private scheduleOrderRequestLprAxisPlateImageSettledWithoutUrlCheck(): void {
    if (this.orderRequestLprAxisPlateImagePreloadTimer != null) {
      return;
    }
    this.orderRequestLprAxisPlateImagePreloadTimer = setTimeout(() => {
      this.orderRequestLprAxisPlateImagePreloadTimer = null;
      this.ngZone.run(() => {
        if (this.orderRequestPlateImageS3Url) {
          this.refreshOrderRequestLprAxisPlateImagePreload();
          return;
        }
        if (this.orderRequestVehicleImageS3Url) {
          return;
        }
        if (this.isOrderRequestLprAxisPlateImageSlotPending()) {
          return;
        }
        const data = this.notification?.data as Record<string, any> | undefined;
        if (
          this.showOrderRequestCheckoutLayout() &&
          notificationDataShowsLprPlatePreviewColumn(data, this.notification?.type) &&
          !notificationDataIndicatesLprAxisImageAwaitingPusher(data)
        ) {
          this.markOrderRequestLprAxisPlateImageFailed();
        }
      });
    }, 0);
  }

  /** `lpr-axis-images-ready` may arrive after the main Pusher payload (Axis `image_source` only). */
  private scheduleOrderRequestLprAxisPlateImageAwaitingTimeout(): void {
    if (this.orderRequestLprAxisPlateImagePreloadTimer != null) {
      return;
    }
    this.orderRequestLprAxisPlateImagePreloadTimer = setTimeout(() => {
      this.orderRequestLprAxisPlateImagePreloadTimer = null;
      this.ngZone.run(() => {
        if (
          !this.orderRequestPlateImageS3Url &&
          this.isOrderRequestLprAxisPlateImageSlotPending()
        ) {
          this.markOrderRequestLprAxisPlateImageFailed();
        }
      });
    }, NotificationModalComponent.ORDER_REQUEST_LPR_PLATE_IMAGE_LOAD_TIMEOUT_MS);
  }

  private markOrderRequestLprAxisPlateImageFailed(): void {
    if (this.orderRequestLprAxisPlateImageLoadFailed) {
      return;
    }
    this.clearOrderRequestLprAxisPlateImagePreloadTimer();
    this.clearOrderRequestLprAxisPlateImageRetryTimers();
    this.clearOrderRequestUiScanPlateReconcileTimer();
    this.orderRequestLprAxisPlateImageLoadFailed = true;
    this.cdr.detectChanges();
  }

  /**
   * UI image-scan fallback when `lpr-axis-images-ready` is missed (SSE down after DB persist).
   * Polls GET order/lpr-entries/:id for plate_image_s3_url while the preview is still pending.
   */
  private clearOrderRequestUiScanPlateReconcileTimer(resetAttempt = true): void {
    if (this.orderRequestUiScanPlateReconcileTimer != null) {
      clearTimeout(this.orderRequestUiScanPlateReconcileTimer);
      this.orderRequestUiScanPlateReconcileTimer = null;
    }
    if (resetAttempt) {
      this.orderRequestUiScanPlateReconcileAttempt = 0;
    }
  }

  private scheduleOrderRequestUiScanPlateReconcile(): void {
    if (!this.isOpen || !this.isUiLicensePlateImageScanOrderRequest()) {
      return;
    }
    if (this.orderRequestPlateImageS3Url) {
      this.clearOrderRequestUiScanPlateReconcileTimer();
      return;
    }
    if (this.orderRequestUiScanPlateReconcileTimer != null) {
      return;
    }
    this.orderRequestUiScanPlateReconcileAttempt = 0;
    this.armOrderRequestUiScanPlateReconcileTick();
  }

  private armOrderRequestUiScanPlateReconcileTick(): void {
    this.clearOrderRequestUiScanPlateReconcileTimer(false);
    this.orderRequestUiScanPlateReconcileTimer = setTimeout(() => {
      this.orderRequestUiScanPlateReconcileTimer = null;
      this.ngZone.run(() => this.runOrderRequestUiScanPlateReconcileTick());
    }, NotificationModalComponent.ORDER_REQUEST_UI_SCAN_PLATE_RECONCILE_INTERVAL_MS);
  }

  private runOrderRequestUiScanPlateReconcileTick(): void {
    if (!this.isOpen || !this.isUiLicensePlateImageScanOrderRequest()) {
      this.clearOrderRequestUiScanPlateReconcileTimer();
      return;
    }
    if (this.orderRequestPlateImageS3Url) {
      this.clearOrderRequestUiScanPlateReconcileTimer();
      return;
    }

    this.orderRequestUiScanPlateReconcileAttempt += 1;
    if (
      this.orderRequestUiScanPlateReconcileAttempt >
      NotificationModalComponent.ORDER_REQUEST_UI_SCAN_PLATE_RECONCILE_MAX_ATTEMPTS
    ) {
      this.clearOrderRequestUiScanPlateReconcileTimer();
      return;
    }

    const lprEntryId = this.resolveOpenModalLprEntryId();
    if (!lprEntryId) {
      this.armOrderRequestUiScanPlateReconcileTick();
      return;
    }

    this.apiService
      .get(`order/lpr-entries/${lprEntryId}`)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        catchError(() => of(null))
      )
      .subscribe(response => {
        if (!this.isOpen || this.orderRequestPlateImageS3Url) {
          this.clearOrderRequestUiScanPlateReconcileTimer();
          return;
        }
        const row = this.extractLprEntryRowFromApiResponse(response);
        const plateUrl =
          this.resolveTopLevelNotificationImageS3Url(
            row as Record<string, any> | null,
            'plate_image_s3_url'
          ) ||
          this.resolveTopLevelNotificationImageS3Url(
            row as Record<string, any> | null,
            'image_s3_url'
          );
        if (plateUrl) {
          this.applyOrderRequestLprAxisPlateImageFromUpdate(plateUrl);
          this.clearOrderRequestUiScanPlateReconcileTimer();
          this.cdr.detectChanges();
          return;
        }
        if (
          this.orderRequestUiScanPlateReconcileAttempt <
          NotificationModalComponent.ORDER_REQUEST_UI_SCAN_PLATE_RECONCILE_MAX_ATTEMPTS
        ) {
          this.armOrderRequestUiScanPlateReconcileTick();
        } else {
          this.clearOrderRequestUiScanPlateReconcileTimer();
        }
      });
  }

  private refreshOrderRequestLprAxisPlateImagePreload(): void {
    if (this.orderRequestLprAxisPlateImageLocked) {
      return;
    }
    const url = this.orderRequestLprAxisPlateImageUrl;
    if (!url) {
      if (
        this.orderRequestLprAxisPlateImageLoadedUrl &&
        this.orderRequestLprAxisPlateImageUrlOverride ===
          this.orderRequestLprAxisPlateImageLoadedUrl
      ) {
        return;
      }
      this.clearOrderRequestLprAxisPlateImagePreloadTimer();
      if (this.isOrderRequestLprAxisPlateImageSlotPending()) {
        this.scheduleOrderRequestLprAxisPlateImageAwaitingTimeout();
        this.scheduleOrderRequestUiScanPlateReconcile();
        return;
      }
      this.clearOrderRequestUiScanPlateReconcileTimer();
      this.scheduleOrderRequestLprAxisPlateImageSettledWithoutUrlCheck();
      return;
    }
    this.clearOrderRequestUiScanPlateReconcileTimer();
    const loaded = this.orderRequestLprAxisPlateImageLoadedUrl?.trim();
    if (
      loaded &&
      this.lprAxisPlateImageUrlsMatch(loaded, url) &&
      !this.orderRequestLprAxisPlateImageLoadFailed
    ) {
      return;
    }
    this.orderRequestLprAxisPlateImageLoadFailed = false;
    this.orderRequestLprAxisPlateImageApiHydrationPending = false;
    if (loaded && !this.lprAxisPlateImageUrlsMatch(loaded, url)) {
      this.orderRequestLprAxisPlateImageLoadedUrl = null;
      this.orderRequestLprAxisPlateImageLoadAttempt = 0;
    }
    this.clearOrderRequestLprAxisPlateImagePreloadTimer();
    this.clearOrderRequestLprAxisPlateImageRetryTimers();
    this.orderRequestLprAxisPlateImageSrcMounted = true;
    this.cdr.detectChanges();
    this.scheduleOrderRequestLprAxisPlateImageAttemptTimeout();
  }

  /** LPR plate/metadata updates without a new image — keep the already-loaded lane image pinned. */
  private freezeOrderRequestLprAxisPlateImageForLprUpdate(): void {
    this.lockOrderRequestLprAxisPlateImage();
  }

  /** Drop pinned/preloaded lane images when the LPR row no longer has image URLs. */
  private clearOrderRequestLprImagePreviewState(): void {
    this.orderRequestLprAxisPlateImageUrlOverride = null;
    this.resetOrderRequestLprAxisPlateImageLoadState();
    this.cdr.detectChanges();
  }

  /**
   * Align modal image preview with `notification.data` after GET/PUT/Pusher merges.
   * Clears stale pinned URLs when the API explicitly returns null image fields.
   */
  private syncOrderRequestLprImagePreviewFromNotificationData(): void {
    if (!this.showOrderRequestCheckoutLayout()) {
      return;
    }
    const d = this.notification?.data as Record<string, any> | undefined;
    if (!d) {
      return;
    }

    const plateUrl = this.resolveTopLevelNotificationImageS3Url(d, 'plate_image_s3_url');
    const vehicleUrl = this.resolveTopLevelNotificationImageS3Url(d, 'image_s3_url');
    const plateCleared =
      Object.prototype.hasOwnProperty.call(d, 'plate_image_s3_url') &&
      (d['plate_image_s3_url'] === null || d['plate_image_s3_url'] === '');
    const vehicleCleared =
      Object.prototype.hasOwnProperty.call(d, 'image_s3_url') &&
      (d['image_s3_url'] === null || d['image_s3_url'] === '');

    if (!plateUrl && !vehicleUrl && (plateCleared || vehicleCleared)) {
      this.clearOrderRequestLprImagePreviewState();
      return;
    }

    if (plateCleared && !plateUrl) {
      this.orderRequestLprAxisPlateImageUrlOverride = null;
      this.orderRequestLprAxisPlateImageLoadedUrl = null;
      this.orderRequestLprAxisPlateImageLocked = false;
      this.orderRequestLprAxisPlateImageLoadFailed = false;
    } else if (plateUrl) {
      this.applyOrderRequestLprAxisPlateImageFromUpdate(plateUrl);
    }

    this.cdr.detectChanges();
  }

  /**
   * List/dropdown rows are reusable for form fields, but Axis lane rows must still
   * GET order/lpr-entries/:id so image URLs reflect the latest backend state.
   */
  private shouldSkipLprEntryApiHydrationFromNotificationsList(
    notification: NotificationData | null | undefined,
    shouldStore: boolean | undefined
  ): boolean {
    if (shouldStore !== false || !hasReusableLprEntryRowFromNotificationsList(notification)) {
      return false;
    }
    const d = notification?.data as Record<string, any> | undefined;
    const imageSource = String(d?.['image_source'] ?? d?.['imageSource'] ?? '')
      .trim()
      .toLowerCase();
    if (imageSource === 'axis' || imageSource === 'rhombus') {
      return false;
    }
    return true;
  }

  /** Compare S3 object paths so presigned query strings do not block refined image swaps. */
  private lprAxisPlateImageUrlKey(url: string): string {
    try {
      return new URL(url.trim()).pathname;
    } catch {
      return url.trim().split('?')[0];
    }
  }

  private lprAxisPlateImageUrlsMatch(a: string, b: string): boolean {
    const left = a.trim();
    const right = b.trim();
    if (!left || !right) {
      return false;
    }
    if (left === right) {
      return true;
    }
    return this.lprAxisPlateImageUrlKey(left) === this.lprAxisPlateImageUrlKey(right);
  }

  private resolveOpenModalLprEntryId(): string {
    const nd = this.notification?.data as Record<string, any> | undefined;
    return (
      resolveLprEntryIdFromNotificationData(nd) || String(this.formData.notificationId || '').trim()
    );
  }

  private buildOrderRequestWayLookupParentContext(): Record<string, any> {
    const nd = (this.notification?.data ?? {}) as Record<string, any>;
    const lprEntryId = this.resolveOpenModalLprEntryId();
    const context: Record<string, any> = {
      location_id: this.getOrderRequestWayLookupLocationId(),
      customer_id: this.formData.customerId || nd['customer_id'] || nd['customerId'] || null,
      vehicle_id: this.formData.vehicleId || nd['vehicle_id'] || nd['vehicleId'] || null,
      state: this.formData.vehicleLicensePlateState || nd['state'] || null,
      roi_id: nd['roi_id'] ?? nd['roiID'] ?? nd['roiId'] ?? null,
    };
    if (lprEntryId && this.isNotificationModalLprFlow()) {
      context['lpr_entry_id'] = lprEntryId;
    }
    const plate = (this.formData.vehicleLicensePlate ?? nd['plate'] ?? nd['plateNumber'] ?? '')
      .toString()
      .trim();
    if (plate) {
      context['plate'] = plate;
      context['plateNumber'] = plate;
    }
    return context;
  }

  /** All ids that may refer to the open modal row (`id`, `lpr_entry_id`, list aliases). */
  private collectOpenModalLprEntryIdCandidates(): Set<string> {
    const ids = new Set<string>();
    const add = (v: any) => {
      const s = v != null ? String(v).trim() : '';
      if (s) {
        ids.add(s);
      }
    };
    add(this.formData.notificationId);
    const nd = this.notification?.data as Record<string, any> | undefined;
    if (nd) {
      add(nd['lpr_entry_id']);
      add(nd['lprEntryId']);
      add(nd['id']);
      add(nd['licence_plate_id']);
      add(nd['license_plate_id']);
      const nested = nd['data'] as Record<string, any> | undefined;
      if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
        add(nested['lpr_entry_id']);
        add(nested['lprEntryId']);
        add(nested['id']);
        add(nested['licence_plate_id']);
        add(nested['license_plate_id']);
      }
    }
    add(resolveLprEntryIdFromNotificationData(nd));
    return ids;
  }

  /** Match Pusher `lpr_entry_id` against every known alias on the open modal row (same car/entry only). */
  private doesIncomingLprEntryMatchOpenModal(incomingLpr: string): boolean {
    const incoming = incomingLpr.trim();
    if (!incoming) {
      return false;
    }
    // Axis update/lost events only apply to an open LPR lane notification — not direct "+" orders or other manual flows.
    if (!this.isNotificationModalLprFlow()) {
      return false;
    }
    const candidates = this.collectOpenModalLprEntryIdCandidates();
    if (candidates.size === 0) {
      return false;
    }
    return candidates.has(incoming);
  }

  private doesIncomingLocationMatchOpenModal(incomingLoc: string): boolean {
    const incoming = incomingLoc.trim();
    if (!incoming) {
      return true;
    }
    const nd = this.notification?.data as Record<string, any> | undefined;
    const expected = String(
      this.formData.locationId || nd?.['location_id'] || nd?.['locationId'] || ''
    ).trim();
    if (!expected) {
      return true;
    }
    return incoming === expected;
  }

  /**
   * Apply a lane image URL from `lpr-axis-images-ready` (or other in-place LPR updates).
   * Unlocks and preloads when the incoming file differs from what is on screen.
   */
  private applyOrderRequestLprAxisPlateImageFromUpdate(
    incomingUrl: string | null | undefined
  ): void {
    const next = incomingUrl?.trim() || null;
    if (!next) {
      return;
    }

    const current =
      this.orderRequestLprAxisPlateImageLoadedUrl?.trim() ||
      this.orderRequestLprAxisPlateImageUrlOverride?.trim() ||
      this.resolveTopLevelNotificationImageS3Url(
        this.notification?.data as Record<string, any> | undefined,
        'plate_image_s3_url'
      ) ||
      null;

    if (current && this.lprAxisPlateImageUrlKey(current) === this.lprAxisPlateImageUrlKey(next)) {
      return;
    }

    this.orderRequestLprAxisPlateImageLocked = false;
    this.orderRequestLprAxisPlateImageUrlOverride = next;
    this.orderRequestLprAxisPlateImageLoadedUrl = null;
    this.orderRequestLprAxisPlateImageLoadFailed = false;
    this.orderRequestLprAxisPlateImageLoadAttempt = 0;
    this.orderRequestSuppressAxisImageAwaitingAfterLprUpdate = true;
    this.orderRequestLprAxisPlateImageApiHydrationPending = false;
    this.clearOrderRequestUiScanPlateReconcileTimer();
    if (this.notification?.data) {
      (this.notification.data as Record<string, any>)['plate_image_s3_url'] = next;
    }
    this.refreshOrderRequestLprAxisPlateImagePreload();
  }

  private notificationTypeUsesLprPlateCorrectionLoader(): boolean {
    const type = this.notification?.type;
    return type === 'invalid-license-plate';
  }

  /**
   * Lane image + plate field loaders should settle once the corrected plate is on screen.
   * Background GET customer/vehicle or LPR hydration must not leave spinners running.
   */
  private settleOrderRequestUiAfterLprInPlaceUpdate(): void {
    this.orderRequestLprAxisPlateImageApiHydrationPending = false;
    this.orderRequestSuppressAxisImageAwaitingAfterLprUpdate = true;
    this.isApplyingLprPlateCorrection = false;
    const override = this.orderRequestLprAxisPlateImageUrlOverride?.trim();
    const loaded = this.orderRequestLprAxisPlateImageLoadedUrl?.trim();
    const imagePreloadPending =
      !!override && (!loaded || !this.lprAxisPlateImageUrlsMatch(loaded, override));
    if (!imagePreloadPending) {
      this.clearOrderRequestLprAxisPlateImagePreloadTimer();
      if (loaded) {
        this.lockOrderRequestLprAxisPlateImage();
      }
    }
    this.cdr.detectChanges();
  }

  /**
   * Automated LPR corrections already carry row data — skip debounced plate search unless we still
   * need to resolve customer/vehicle for manual entry flows.
   */
  private shouldRunLicensePlateSearchAfterLprInPlaceUpdate(
    plateChanged: boolean,
    payload?: Record<string, any> | null
  ): boolean {
    if (!plateChanged) {
      return false;
    }
    if (this.formData.customerId && this.formData.vehicleId) {
      return false;
    }
    const d = payload ?? {};
    const customerId = String(d['customer_id'] ?? d['customerId'] ?? '').trim();
    const vehicleId = String(d['vehicle_id'] ?? d['vehicleId'] ?? '').trim();
    if (customerId && vehicleId) {
      return false;
    }
    return true;
  }

  /** Show Axis plate / vehicle images in order-request layout when Pusher event matches this LPR entry / location. */
  private applyLprAxisImagesReadyPayload(payload: LprAxisImagesReadyEvent): void {
    if (!this.isOpen || !payload || !this.showOrderRequestCheckoutLayout()) {
      return;
    }
    const incomingLpr = payload.lpr_entry_id != null ? String(payload.lpr_entry_id).trim() : '';
    if (!this.doesIncomingLprEntryMatchOpenModal(incomingLpr)) {
      return;
    }
    const incomingLoc = payload.location_id != null ? String(payload.location_id).trim() : '';
    if (!this.doesIncomingLocationMatchOpenModal(incomingLoc)) {
      return;
    }
    const plateUrl =
      typeof payload.plate_image_s3_url === 'string' ? payload.plate_image_s3_url.trim() : '';
    const vehicleUrl = typeof payload.image_s3_url === 'string' ? payload.image_s3_url.trim() : '';
    const plateKeyPresent = Object.prototype.hasOwnProperty.call(payload, 'plate_image_s3_url');
    const vehicleKeyPresent = Object.prototype.hasOwnProperty.call(payload, 'image_s3_url');
    const plateCleared = plateKeyPresent && !plateUrl;
    const vehicleCleared = vehicleKeyPresent && !vehicleUrl;

    if (!plateUrl && !vehicleUrl && !plateCleared && !vehicleCleared) {
      return;
    }

    if (plateUrl) {
      this.applyOrderRequestLprAxisPlateImageFromUpdate(plateUrl);
    } else if (plateCleared) {
      this.orderRequestLprAxisPlateImageUrlOverride = null;
      this.orderRequestLprAxisPlateImageLoadedUrl = null;
      this.orderRequestLprAxisPlateImageLocked = false;
      this.orderRequestLprAxisPlateImageLoadFailed = false;
    }

    if (this.notification?.data) {
      const d = this.notification.data as Record<string, any>;
      if (plateUrl) {
        d['plate_image_s3_url'] = plateUrl;
      } else if (plateCleared) {
        d['plate_image_s3_url'] = null;
      }
      if (vehicleUrl) {
        d['image_s3_url'] = vehicleUrl;
      } else if (vehicleCleared) {
        d['image_s3_url'] = null;
      }
      if (typeof payload.plate === 'string' && payload.plate.trim()) {
        d['plate'] = payload.plate.trim();
        d['vehicle_license_plate'] = payload.plate.trim();
      }
    }

    if (plateCleared && vehicleCleared) {
      this.clearOrderRequestLprImagePreviewState();
    }

    this.logModalInteraction('lpr-axis-images-ready.applied', {
      lpr_entry_id: incomingLpr || this.resolveOpenModalLprEntryId(),
      plate_image_url: plateUrl || undefined,
      vehicle_image_url: vehicleUrl || undefined,
    });
    this.cdr.detectChanges();
  }

  /**
   * Axis `lpr-axis-entry-updated` with `Updated_event`: refined plate/metadata for the same lane row.
   */
  private applyLprAxisEntryUpdatedPayload(payload: LprAxisEntryUpdatedEvent): void {
    if (!this.isOpen || !payload) {
      return;
    }
    const orderLayout = this.showOrderRequestCheckoutLayout();
    const fleetFound = this.showFleetVehicleFoundServicesLayout();
    if (!orderLayout && !fleetFound) {
      return;
    }

    const nd = this.notification?.data as Record<string, any> | undefined;
    const incomingLpr = payload.lpr_entry_id != null ? String(payload.lpr_entry_id).trim() : '';
    if (!this.doesIncomingLprEntryMatchOpenModal(incomingLpr)) {
      return;
    }

    const incomingLoc = payload.location_id != null ? String(payload.location_id).trim() : '';
    if (!this.doesIncomingLocationMatchOpenModal(incomingLoc)) {
      return;
    }

    this.freezeOrderRequestLprAxisPlateImageForLprUpdate();

    const newPlateRaw = typeof payload.plate === 'string' ? payload.plate.trim() : '';
    let plateChanged = false;
    if (newPlateRaw) {
      const normalized = newPlateRaw.toUpperCase();
      const prev = (this.formData.vehicleLicensePlate || '').trim().toUpperCase();
      plateChanged = prev !== normalized;
      if (plateChanged && this.canApplyExternalLicensePlateToForm()) {
        if (this.notificationTypeUsesLprPlateCorrectionLoader()) {
          this.isApplyingLprPlateCorrection = true;
          this.cdr.detectChanges();
        }
        this.setCombinedLicensePlateOnForm(
          normalized,
          typeof payload.state === 'string' ? payload.state : null
        );
        this.formData.vehicleId = '';
      }
    }

    this.mergeLprAxisEntryUpdateIntoNotificationData(payload);

    if (this.isOrderRequestLocationLockedToNotificationLane()) {
      this.pinFormLocationToNotificationIfLaneLocked();
    } else if (!String(this.formData.locationId || '').trim() && incomingLoc) {
      this.formData.locationId = incomingLoc;
    }
    if (
      !String(this.formData.location || '').trim() &&
      payload.branch != null &&
      String(payload.branch).trim()
    ) {
      this.formData.location = String(payload.branch).trim();
    }

    this.settleOrderRequestUiAfterLprInPlaceUpdate();

    if (
      orderLayout &&
      this.shouldRunLicensePlateSearchAfterLprInPlaceUpdate(
        plateChanged,
        payload as Record<string, any>
      )
    ) {
      this.triggerLicensePlateSearchFromValue(this.formData.vehicleLicensePlate);
      this.trackFullStoryLicensePlate('lpr', { immediate: true });
    }

    if (orderLayout && this.formData.customerId) {
      setTimeout(() => this.checkCustomerOrdersForSubscription(), 0);
    }

    this.syncOrderRequestAddonSelectionToPairedWith();

    this.logModalInteraction('lpr-axis-entry-updated.applied', {
      lpr_entry_id: incomingLpr,
      plate: newPlateRaw || (this.formData.vehicleLicensePlate || '').trim(),
    });
  }

  private mergeLprAxisEntryUpdateIntoNotificationData(payload: LprAxisEntryUpdatedEvent): void {
    if (!this.notification?.data) {
      return;
    }
    const d = this.notification.data as Record<string, any>;
    if (typeof payload.plate === 'string' && payload.plate.trim()) {
      const p = payload.plate.trim();
      d['plate'] = p;
      d['vehicle_license_plate'] = p;
    }
    if (payload.state != null && String(payload.state).trim() !== '') {
      d['state'] = String(payload.state).trim();
    }
    if (payload.branch != null && String(payload.branch).trim() !== '') {
      d['branch'] = String(payload.branch).trim();
    }
    if (payload.location_id != null && String(payload.location_id).trim() !== '') {
      d['location_id'] = String(payload.location_id).trim();
    }
    if (payload.vehicle_info && typeof payload.vehicle_info === 'object') {
      d['vehicle_info'] = payload.vehicle_info;
    }
    if (typeof payload.plate_image_s3_url === 'string' && payload.plate_image_s3_url.trim()) {
      d['plate_image_s3_url'] = payload.plate_image_s3_url.trim();
    }
    if (typeof payload.image_s3_url === 'string' && payload.image_s3_url.trim()) {
      d['image_s3_url'] = payload.image_s3_url.trim();
    }
  }

  /**
   * Handle `updated_pusher: true` notification events — update open modal in-place when `lpr_entry_id` matches.
   */
  private applyLprEntryUpdatedNotification(event: LprEntryUpdatedNotification): void {
    if (!this.isOpen || !event?.notification) {
      return;
    }

    const incoming = event.notification;
    const d = incoming.data as Record<string, any> | undefined;
    if (!d) {
      return;
    }

    const incomingLpr = resolveLprEntryIdFromNotificationData(d);
    if (!incomingLpr || !this.doesIncomingLprEntryMatchOpenModal(incomingLpr)) {
      return;
    }

    const incomingLoc = String(d['location_id'] ?? '').trim();
    if (!this.doesIncomingLocationMatchOpenModal(incomingLoc)) {
      return;
    }

    this.logModalInteraction('lpr-entry-updated-notification.matched', {
      lpr_entry_id: incomingLpr,
      incoming_type: incoming.type,
      current_type: this.notification?.type,
    });

    const plateKeyPresent = Object.prototype.hasOwnProperty.call(d, 'plate_image_s3_url');
    const vehicleKeyPresent = Object.prototype.hasOwnProperty.call(d, 'image_s3_url');
    const plateExplicitlyEmpty =
      plateKeyPresent && (d['plate_image_s3_url'] === null || d['plate_image_s3_url'] === '');
    const vehicleExplicitlyEmpty =
      vehicleKeyPresent && (d['image_s3_url'] === null || d['image_s3_url'] === '');
    const incomingPlate =
      typeof d['plate_image_s3_url'] === 'string' ? d['plate_image_s3_url'].trim() : '';

    const previousData = this.notification?.data as Record<string, any> | undefined;
    const previousPlateImageUrl = previousData?.['plate_image_s3_url'];
    const previousVehicleImageUrl = previousData?.['image_s3_url'];

    if (this.notification) {
      this.notification = mergeLprEntryUpdateIntoOpenNotification(this.notification, incoming);
    }

    const mergedData = this.notification?.data as Record<string, any> | undefined;
    if (mergedData) {
      if (plateExplicitlyEmpty) {
        mergedData['plate_image_s3_url'] = null;
        this.orderRequestLprAxisPlateImageUrlOverride = null;
        this.orderRequestLprAxisPlateImageLoadedUrl = null;
        this.orderRequestLprAxisPlateImageLocked = false;
        this.orderRequestLprAxisPlateImageLoadFailed = false;
      } else if (
        !plateKeyPresent &&
        previousPlateImageUrl != null &&
        previousPlateImageUrl !== ''
      ) {
        mergedData['plate_image_s3_url'] = previousPlateImageUrl;
      }
      if (vehicleExplicitlyEmpty) {
        mergedData['image_s3_url'] = null;
      } else if (
        !vehicleKeyPresent &&
        previousVehicleImageUrl != null &&
        previousVehicleImageUrl !== ''
      ) {
        mergedData['image_s3_url'] = previousVehicleImageUrl;
      }
    }

    if (plateExplicitlyEmpty && vehicleExplicitlyEmpty) {
      this.clearOrderRequestLprImagePreviewState();
    } else if (!plateExplicitlyEmpty && incomingPlate) {
      this.freezeOrderRequestLprAxisPlateImageForLprUpdate();
    }

    this.syncOrderRequestLprImagePreviewFromNotificationData();

    const str = (v: any) => (v != null && v !== '' ? String(v).trim() : '');
    const newPlate = str(d['plate']).toUpperCase();
    const prevPlate = (this.formData.vehicleLicensePlate || '').toUpperCase();
    const plateChanged = !!newPlate && newPlate !== prevPlate;
    if (plateChanged && this.canApplyExternalLicensePlateToForm()) {
      this.setCombinedLicensePlateOnForm(newPlate, str(d['state']) || null);
      this.formData.vehicleId = '';
    }

    const newOrderId = str(d['order_id'] || d['orderId']);
    if (newOrderId && !this.formData.orderId) {
      this.formData.orderId = newOrderId;
    }

    const vd = d['vehicle_data'] as Record<string, any> | undefined;
    const newPkgId = str(d['package_id'] || vd?.['package_id']);
    const newPkgName = str(d['package_name'] || vd?.['package_name']);
    if (newPkgId && !this.formData.packageId) {
      this.formData.packageId = newPkgId;
    }
    if (newPkgName && !this.formData.package) {
      this.formData.package = newPkgName;
    }

    const newLocId = str(d['location_id']);
    if (newLocId && !this.formData.locationId) {
      this.formData.locationId = newLocId;
    }
    const newBranch = str(d['branch']);
    if (newBranch && !this.formData.location) {
      this.formData.location = newBranch;
    }

    const hadCustomerBefore = !!this.formData.customerId;
    const newCustomerId = str(d['customerId'] || d['customer_id']);
    if (newCustomerId && !this.formData.customerId) {
      this.formData.customerId = newCustomerId;
    }

    const newStripeCustomerId = str(d['stripe_customer_id'] || d['stripeCustomerId']);
    if (newStripeCustomerId && !this.formData.stripeCustomerId) {
      this.formData.stripeCustomerId = newStripeCustomerId;
    }

    const newFirstName = str(d['customer_first_name'] || d['customerFirstName']);
    if (newFirstName && !this.formData.customerFirstName) {
      this.formData.customerFirstName = newFirstName;
    }
    const newLastName = str(d['customer_last_name'] || d['customerLastName']);
    if (newLastName && !this.formData.customerLastName) {
      this.formData.customerLastName = newLastName;
    }
    const newEmail = str(d['customer_email'] || d['customerEmail']);
    if (newEmail && !this.formData.customerEmail) {
      this.formData.customerEmail = newEmail;
    }
    const newContact = str(d['customer_contact'] || d['customerContact']);
    if (newContact && !this.formData.customerContact) {
      this.formData.customerContact = newContact;
    }

    const hadVehicleBefore = !!this.formData.vehicleId;
    const newVehicleId = str(d['vehicleId'] || d['vehicle_id']);
    if (newVehicleId && !this.formData.vehicleId) {
      this.formData.vehicleId = newVehicleId;
    }

    const newMake = str(d['vehicle_make'] || d['vehicleMake']);
    if (newMake && !this.formData.vehicleMake) {
      this.formData.vehicleMake = newMake;
    }
    const newModel = str(d['vehicle_model'] || d['vehicleModel']);
    if (newModel && !this.formData.vehicleModel) {
      this.formData.vehicleModel = newModel;
    }
    const newYear = d['vehicle_year'] != null ? String(d['vehicle_year']).trim() : '';
    if (newYear && !this.formData.vehicleYear) {
      this.formData.vehicleYear = newYear;
    }
    const newVin = str(d['vehicle_vin'] || d['vehicleVin']);
    if (newVin && !this.formData.vehicleVin) {
      this.formData.vehicleVin = newVin;
    }

    const orderLayout = this.showOrderRequestCheckoutLayout();
    const customerOrVehicleNowAvailable =
      (!hadCustomerBefore && !!this.formData.customerId) ||
      (!hadVehicleBefore && !!this.formData.vehicleId);

    if (plateChanged && this.notificationTypeUsesLprPlateCorrectionLoader()) {
      this.isApplyingLprPlateCorrection = true;
      this.cdr.detectChanges();
    }

    this.settleOrderRequestUiAfterLprInPlaceUpdate();

    if (orderLayout && this.shouldRunLicensePlateSearchAfterLprInPlaceUpdate(plateChanged, d)) {
      this.triggerLicensePlateSearchFromValue(this.formData.vehicleLicensePlate);
      this.trackFullStoryLicensePlate('lpr', { immediate: true });
    }

    if (orderLayout && this.formData.customerId && this.formData.vehicleId) {
      if (customerOrVehicleNowAvailable) {
        setTimeout(() => this.checkCustomerOrdersForSubscription(), 0);
      }
    }

    if (orderLayout && !hadCustomerBefore && this.formData.customerId) {
      this.tryAutoSelectPackageWhenExistingCustomerLinked();
    }

    this.syncOrderRequestAddonSelectionToPairedWith();

    this.logModalInteraction('lpr-entry-updated-notification.applied', {
      lpr_entry_id: incomingLpr,
      incoming_type: incoming.type,
      new_type: this.notification?.type,
      plate_changed: plateChanged,
      customer_id: this.formData.customerId,
      vehicle_id: this.formData.vehicleId,
    });
  }

  /** Plate / lane image from merged notification payload (Pusher / websocket). */
  private syncOrderRequestPlateImageFromNotificationData(): void {
    if (!this.showOrderRequestCheckoutLayout()) {
      return;
    }
    const d = this.notification?.data as Record<string, any> | undefined;
    if (!d) {
      return;
    }
    const plate = resolveLprAxisImageUrlFromNotificationData(d);
    if (plate) {
      this.applyOrderRequestLprAxisPlateImageFromUpdate(plate);
    }
  }

  /** Fleet LPR hit: same package + fleet card grid as order request, without the full checkout form. */
  showFleetVehicleFoundServicesLayout(): boolean {
    return this.notification?.type === 'fleet-vehicle-found';
  }

  /**
   * Location picker in the Booking Details row: only a multi-location operator creating the order
   * by hand (header "+" or UI plate upload) needs it. Shop-floor staff have a fixed shop from their
   * profile, a lane pins camera popups, and a single location is set on the form without a picker.
   */
  shouldShowOrderRequestLocationPicker(): boolean {
    const origin = this.resolveCurrentLocationPickerOrigin();
    return computeLocationHeaderVisible({
      isDirectOrder: origin === 'direct',
      isUiUpload: origin === 'ui-upload',
      isLaneTriggered: this.isOrderRequestLocationLockedToNotificationLane(),
      locationCount: this.availableLocationsForDropdown.length,
      isShopFloor: this.roleService.isShopFloorEmployee(),
    });
  }

  /**
   * Origin of the location picker, remembered per popup. Something other than this component can swap
   * the open notification for one that has the same ids but no `is_lpr_upload` / stamped origin (live
   * SSE, notification-list refresh, rehydrate) — without the memory, Back from payment would then read
   * its `lane_id` as a camera lane and hide both Location and Lane.
   */
  private locationPickerOriginMemory: {
    keys: string[];
    origin: NonNullable<ReturnType<typeof resolveLocationPickerOrigin>>;
  } | null = null;

  private resolveCurrentLocationPickerOrigin(): ReturnType<typeof resolveLocationPickerOrigin> {
    const data = this.notification?.data as Record<string, any> | undefined;
    const origin = resolveLocationPickerOrigin({
      isDirectOrder: this.isDirectOrder(),
      isUiUpload: this.isUiLicensePlateImageScanOrderRequest(),
      notificationData: data,
    });
    const keys = collectNotificationIdentityKeys(data);
    if (origin) {
      if (keys.length) {
        this.locationPickerOriginMemory = { keys, origin };
      }
      return origin;
    }
    const remembered = this.locationPickerOriginMemory;
    if (remembered && keys.some(key => remembered.keys.includes(key))) {
      return remembered.origin;
    }
    return null;
  }

  /**
   * Camera / Pusher LPR pins the shop: changing location would send a foreign lane_id.
   * UI image scan (`is_lpr_upload`) stamps header lane context — location stays editable.
   * Header "+" (no scan lane) keeps location selectable (unchanged).
   */
  isOrderRequestLocationLockedToNotificationLane(): boolean {
    if (this.isDirectOrder()) {
      return false;
    }
    // Back from payment: the checkout data carries the origin instead of `is_lpr_upload`.
    if (this.resolveCurrentLocationPickerOrigin() === 'ui-upload') {
      return false;
    }
    return !!resolveNotificationLaneId(this.notification?.data as Record<string, any> | undefined);
  }

  /**
   * CWS-744: lane picker + mandatory lane apply only to UI license-plate image scan
   * (`is_lpr_upload`). Header "+" and camera/Pusher LPR keep prior lane behavior.
   */
  isUiLicensePlateImageScanOrderRequest(): boolean {
    if (this.isDirectOrder()) {
      return false;
    }
    return isUiLprImageUploadNotificationData(
      this.notification?.data as Record<string, any> | undefined
    );
  }

  /**
   * Show lane picker only for UI image-scan order requests when the selected
   * location has more than one active lane (a single lane is pre-selected silently).
   */
  shouldShowOrderRequestLanePicker(): boolean {
    if (this.resolveCurrentLocationPickerOrigin() !== 'ui-upload') {
      return false;
    }
    if (!this.showOrderRequestCheckoutLayout()) {
      return false;
    }
    if (!String(this.formData.locationId || '').trim()) {
      return false;
    }
    return !this.isLoadingOrderRequestLanes && this.availableOrderRequestLanes.length > 1;
  }

  /**
   * Lane is required whenever the UI-scan lane picker is visible. A location with a single lane hides
   * the picker; that lane is still pre-selected into the form and sent on create.
   */
  isOrderRequestLaneSelectionRequired(): boolean {
    return (
      this.shouldShowOrderRequestLanePicker() &&
      !this.isLoadingOrderRequestLanes &&
      this.availableOrderRequestLanes.length > 1
    );
  }

  /**
   * Admin location list — not payment checkout or staff single-shop flows. Loaded whether or not the
   * picker ends up visible: the count decides that, and a lone location is still set on the form.
   */
  private shouldLoadLocationOptionsForModal(): boolean {
    if (this.isViewOnlyNotification() || this.showPaymentCheckoutLayout()) {
      return false;
    }
    return !this.roleService.isShopFloorEmployee();
  }

  /** Admins load the location list; shop-floor staff resolve their assigned shop from the profile. */
  private ensureOrderRequestLocationSource(): void {
    if (this.shouldLoadLocationOptionsForModal()) {
      this.ensureLocationOptionsLoaded();
    } else if (this.roleService.isShopFloorEmployee()) {
      this.ensureStaffLocationFromProfile();
    }
  }

  /** Staff: resolve assigned shop from employee profile (no GET /locations). */
  private ensureStaffLocationFromProfile(): void {
    if (!this.roleService.isShopFloorEmployee()) {
      return;
    }
    if ((this.formData.locationId || '').trim()) {
      return;
    }

    this.employeeService
      .getCurrentEmployeeProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const id = response?.status ? String(response.data?.location_id || '').trim() : '';
          if (!id) {
            return;
          }
          const name =
            String(
              response.data?.location_name || response.data?.location?.name || 'Staff Location'
            ).trim() || 'Staff Location';
          const wayPurchaseLookupEnabled = isWayPurchaseLookupEnabled(response.data);
          this.formData.locationId = id;
          if (!this.formData.location) {
            this.formData.location = name;
          }
          this.availableLocationsForDropdown = [{ id, name, wayPurchaseLookupEnabled }];
          if (this.showOrderRequestCheckoutLayout()) {
            this.loadCarWashPackages();
            this.retryOrderRequestWayLookupFromFormFields();
            this.loadOrderRequestLanesForSelectedLocation();
          }
        },
        error: () => {},
      });
  }

  /**
   * Manual `create-order-request` / `create-customer-request` without LPR payload → `saas`.
   * Same types after header scan or Pusher/LPR (image URL, `lpr_entry_id`, etc.) → `lpr`.
   */
  private getOrderSourceForCreatePayload(): 'lpr' | 'saas' {
    const type = this.notification?.type;
    if (type === 'create-order-request' || type === 'create-customer-request') {
      if (
        notificationDataHasLprCaptureForOrderSource(
          this.notification?.data as Record<string, any> | undefined
        )
      ) {
        return 'lpr';
      }
      return 'saas';
    }

    return 'lpr';
  }

  /** Resolves stale LPR `event` values using row completion flags and order_id. */
  private getEffectiveNotificationType(): NotificationData['type'] {
    if (!this.notification) {
      return 'invalid-license-plate';
    }
    const data = (this.notification.data ?? {}) as Record<string, any>;
    if (
      this.notification.type === 'create-order-request' ||
      data['way_unredeemable_handoff'] === true
    ) {
      const dataEvent = String(data['event'] ?? '').trim();
      if (!dataEvent || dataEvent === 'create-order-request' || dataEvent.startsWith('way-')) {
        return 'create-order-request';
      }
    }
    return resolveNotificationTypeFromLprEntry({
      event: this.notification.type,
      ...data,
    });
  }

  getNotificationTitle(): string {
    if (!this.notification) return 'Notification';
    if (this.isFamilyCheckout) {
      return 'Checkout Summary';
    }

    // Same chrome as new order; editing from checkout uses the unified order-request UI.
    if (this.notification.type === 'create-payment-request' && this.isEditMode) {
      return 'Order Request';
    }
    if (this.isPreOrderPaymentNotification()) {
      return this.notification?.data?.isBuyPackageFlow ? 'Buy Package' : 'New Order Request';
    }

    switch (this.getEffectiveNotificationType()) {
      case 'fleet-vehicle-found':
        return 'Fleet Vehicle Found!';
      case 'create-customer-request':
        return 'New customer request';
      case 'invalid-license-plate':
      case 'create-order-request':
        // CWS-1914: a scanned QR opens to redeem exactly as an LPR redeem event does, but it
        // carries `qr_scan` rather than `lpr_prepaid_redeem`, so keying the header on the raw
        // flag left a scan titled "New Order Request". Both entry points answer this.
        if (this.isOrderRequestPrepaidRedeemEntryPoint()) {
          return 'Prepaid redemption';
        }
        if (this.notification?.data?.isBuyPackageFlow) {
          return 'Buy Package';
        }
        return this.notification?.data?.quick_plus_order === true
          ? 'Order Request'
          : 'New Order Request';
      case 'order-success':
        return 'Order Success!';
      case 'create-payment-request':
        return 'Checkout Summary';
      case 'order-not-found':
        return 'Order Not Found';
      case 'detailing-appointment-found':
        return 'Detailing Appointment Found';
      case 'fleet-invoice-added':
        return 'Fleet Invoice Added';
      case 'order-completed':
        return 'Order Completed';
      case 'vehicle-not-found':
        return 'Vehicle Not Found';
      case 'active-subscription':
        return 'Active Subscription';
      case 'exit-active-subscription':
        return 'Active Subscription Found at Exit';
      case 'exit-detailing-appointment-found':
        return 'Detailing Appointment Found at Exit';
      default:
        return 'Notification';
    }
  }

  shouldShowUpdateOrderButton(): boolean {
    if (!this.notification) return false;
    return this.isOrderRequestStyleNotification() || this.isPreOrderPaymentNotification();
  }

  /** create-payment-request edit mode uses Cancel + Save Changes in the shared footer. */
  get isPaymentRequestEditFooter(): boolean {
    return this.notification?.type === 'create-payment-request' && this.isEditMode;
  }

  get footerCancelText(): string {
    if (this.isFamilyCheckout) {
      return 'Back';
    }
    if (this.isPaymentRequestEditFooter) {
      return this.checkoutDraftPayload ? 'Back' : 'Cancel';
    }
    if (this.showPaymentCheckoutLayout()) {
      return 'Back';
    }
    if (this.shouldShowUpdateOrderButton()) {
      return 'Cancel';
    }
    return this.getViewOnlyDismissButtonLabel();
  }

  get footerCancelDisabled(): boolean {
    if (this.isFamilyCheckout) {
      return this.isFamilyPaying;
    }
    if (this.isPaymentRequestEditFooter) {
      return this.isUpdatingOrder;
    }
    return (
      this.isUpdatingOrder ||
      this.isProcessingPayment ||
      this.isCreatingPaymentLink ||
      this.isOpeningGate
    );
  }

  get footerShowPrimary(): boolean {
    if (this.isFamilyCheckout) {
      return true;
    }
    if (this.isPaymentRequestEditFooter || this.shouldShowUpdateOrderButton()) {
      return true;
    }
    if (!this.showPaymentCheckoutLayout()) {
      return false;
    }
    if (this.paymentExpired) {
      return true;
    }
    if (!this.isCheckoutPaymentRequired()) {
      return true;
    }
    if (this.isFooterCashOrDynamicPaymentPrimary()) {
      return true;
    }
    return this.isFooterCardLikePaymentPrimary();
  }

  get footerPrimaryText(): string {
    if (this.isFamilyCheckout) {
      return 'Checkout';
    }
    // CWS-1914: on Prepaid redemption the button draws a wash off the book. Keyed on the
    // same predicate that routes the click to submitPrepaidRedemptionOrder, so the label can
    // never say one thing while the click does another.
    if (this.isOrderRequestRedeemingPrepaid()) {
      return 'Redeem';
    }
    if (this.isPaymentRequestEditFooter) {
      // CWS-1945: a draft is not saved; Continue rebuilds it.
      return this.checkoutDraftPayload ? 'Continue' : 'Save Changes';
    }
    if (this.shouldShowUpdateOrderButton()) {
      // CWS-1945: Continue no longer creates the order; paying does.
      return 'Continue';
    }
    if (this.paymentExpired) {
      return 'Create New Payment Link';
    }
    if (this.showPaymentCheckoutLayout() && !this.isCheckoutPaymentRequired()) {
      return 'Complete order';
    }
    if (this.isFooterCashOrDynamicPaymentPrimary()) {
      return 'Confirm Payment';
    }
    if (this.paymentForm.method === PaymentMethodCode.TapToPay) {
      return 'Proceed';
    }
    if (this.paymentForm.method === PaymentMethodCode.Card) {
      return 'Checkout';
    }
    if (this.paymentForm.method === PaymentMethodCode.Terminal) {
      return 'Collect Payment';
    }
    return 'Send Link';
  }

  get footerPrimaryDisabled(): boolean {
    if (this.isClaimingCheckout) {
      return true;
    }
    if (this.isFamilyCheckout) {
      return (
        this.isFamilyPaying ||
        this.isFamilyPaymentMethodUnsupported ||
        this.arePaymentMethodsDisabled() ||
        !this.isPaymentFormValid()
      );
    }
    if (this.isPaymentRequestEditFooter || this.shouldShowUpdateOrderButton()) {
      return this.isUpdatingOrder;
    }
    if (this.paymentExpired) {
      return this.isCreatingPaymentLink;
    }
    if (this.showPaymentCheckoutLayout() && !this.isCheckoutPaymentRequired()) {
      return this.isConfirmingCashPayment;
    }
    if (this.isFooterCashOrDynamicPaymentPrimary()) {
      return (
        this.isConfirmingCashPayment ||
        (this.paymentForm.method === PaymentMethodCode.Cash && !this.isCheckoutCashReceivedValid())
      );
    }
    // Without a connected Stripe account a membership cannot complete by any method, and a Stripe
    // method cannot complete for any order.
    if (this.arePaymentMethodsDisabled() || this.isSelectedStripeMethodUnavailable()) {
      return true;
    }
    return !this.isPaymentFormValid() || this.isProcessingPayment || this.isCreatingPaymentLink;
  }

  get footerPrimaryLoading(): boolean {
    if (this.isClaimingCheckout) {
      return true;
    }
    if (this.isFamilyCheckout) {
      return this.isFamilyPaying;
    }
    if (this.isPaymentRequestEditFooter || this.shouldShowUpdateOrderButton()) {
      return this.isUpdatingOrder;
    }
    if (this.paymentExpired) {
      return this.isCreatingPaymentLink;
    }
    if (this.showPaymentCheckoutLayout() && !this.isCheckoutPaymentRequired()) {
      return this.isConfirmingCashPayment;
    }
    if (this.isFooterCashOrDynamicPaymentPrimary()) {
      return this.isConfirmingCashPayment;
    }
    return this.isProcessingPayment || this.isCreatingPaymentLink;
  }

  get footerPrimaryLoadingText(): string {
    if (this.isClaimingCheckout) {
      return 'Starting payment...';
    }
    if (this.isFamilyCheckout) {
      return 'Processing...';
    }
    if (this.isPaymentRequestEditFooter) {
      return this.checkoutDraftPayload ? 'Preparing checkout...' : 'Saving...';
    }
    if (this.shouldShowUpdateOrderButton()) {
      return 'Preparing checkout...';
    }
    if (this.paymentExpired) {
      return 'Creating...';
    }
    if (this.showPaymentCheckoutLayout() && !this.isCheckoutPaymentRequired()) {
      return 'Completing...';
    }
    if (this.isFooterCashOrDynamicPaymentPrimary()) {
      return 'Confirming...';
    }
    if (this.isProcessingPayment) {
      return 'Processing...';
    }
    if (this.isCreatingPaymentLink) {
      return 'Creating Link...';
    }
    return 'Loading...';
  }

  get footerPrimaryExtraClass(): Record<string, boolean> {
    return { disabled: this.footerPrimaryDisabled };
  }

  onFooterCancelClick(): void {
    if (this.isFamilyCheckout) {
      this.leaveFamilyCheckout();
      return;
    }
    if (this.isPaymentRequestEditFooter) {
      this.cancelEditMode();
      return;
    }
    if (this.shouldShowUpdateOrderButton() || this.showPaymentCheckoutLayout()) {
      this.onCancelClick();
      return;
    }
    this.onOkayClick();
  }

  onFooterPrimaryClick(): void {
    if (this.isFamilyCheckout) {
      void this.confirmFamilyPayment();
      return;
    }
    if (this.isPaymentRequestEditFooter) {
      if (this.checkoutDraftPayload) {
        this.onUpdateOrder();
      } else {
        this.onSaveEditChanges();
      }
      return;
    }
    if (this.shouldShowUpdateOrderButton()) {
      this.onUpdateOrder();
      return;
    }
    if (this.paymentExpired) {
      this.onCreateNewPaymentLink();
      return;
    }
    if (this.showPaymentCheckoutLayout() && !this.isCheckoutPaymentRequired()) {
      this.confirmZeroBalanceCheckout();
      return;
    }
    if (this.isFooterCashOrDynamicPaymentPrimary()) {
      this.confirmPaymentMode(this.paymentForm.method);
      return;
    }
    this.onContinue();
  }

  private isFooterCashOrDynamicPaymentPrimary(): boolean {
    return (
      this.showPaymentCheckoutLayout() &&
      this.isCheckoutPaymentRequired() &&
      (this.paymentForm.method === PaymentMethodCode.Cash ||
        this.isDynamicPaymentMethod(this.paymentForm.method))
    );
  }

  private isFooterCardLikePaymentPrimary(): boolean {
    return (
      this.showPaymentCheckoutLayout() &&
      this.isCheckoutPaymentRequired() &&
      (!this.paymentLinkSent ||
        this.paymentForm.method === PaymentMethodCode.Card ||
        this.paymentForm.method === PaymentMethodCode.TapToPay ||
        this.paymentForm.method === PaymentMethodCode.Terminal) &&
      this.paymentForm.method !== PaymentMethodCode.Cash &&
      !this.isDynamicPaymentMethod(this.paymentForm.method)
    );
  }

  getNotificationMessage(): string {
    return this.notification?.message || 'You have a new notification!';
  }

  getFormattedTime(): string {
    if (!this.notification) return '';
    return formatTimeInTimeZone(this.notification.timestamp, getUserTimezone());
  }

  getFormattedAppointmentTime(): string {
    const data = this.notification?.data as
      { order?: { appointment_time?: string }; detailing_appointment_time?: string } | undefined;
    const appointmentTime =
      data?.order?.appointment_time ||
      this.notification?.data?.detailing_appointment_time ||
      this.formData.detailingAppointmentTime ||
      this.formData.appointmentTime;

    if (!appointmentTime) {
      return '';
    }

    const shopTimezone = getUserTimezone();
    return shopTimezone
      ? formatDateTimeInTimeZone(appointmentTime, shopTimezone)
      : formatDateTimeWithTimezone(appointmentTime);
  }

  getLastWashedDate(): string {
    if (!this.notification?.data) return '';
    const lastWashedDate =
      this.notification?.data?.vehicle_data?.fleet_last_washed_date ||
      this.notification?.data?.fleet_last_washed_date ||
      '';
    return lastWashedDate;
  }

  getFormattedLastWashedDate(): string {
    const lastWashedDate = this.getLastWashedDate();
    if (!lastWashedDate) return 'N/A';

    try {
      const date = new Date(lastWashedDate);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (e) {
      return lastWashedDate;
    }
  }

  isLastWashedToday(): boolean {
    const lastWashedDate = this.getLastWashedDate();
    if (!lastWashedDate) return false;

    try {
      const lastWashed = new Date(lastWashedDate);
      const today = new Date();

      // Compare dates (ignoring time)
      return (
        lastWashed.getFullYear() === today.getFullYear() &&
        lastWashed.getMonth() === today.getMonth() &&
        lastWashed.getDate() === today.getDate()
      );
    } catch (e) {
      return false;
    }
  }

  /** Way redemptions hide the package price on order-success; SaaS shows it. */
  isWayOrderSuccessRedemption(): boolean {
    return isWayOrderSuccessPayload(this.notification?.data);
  }

  /**
   * License plate for view-only success / subscription modals — plate only, no state code.
   */
  getOrderSuccessLicensePlateDisplay(): string {
    const data = (this.notification?.data ?? {}) as Record<string, any>;
    const raw = String(
      data['vehicle_license_plate'] ?? data['license_plate'] ?? data['plate'] ?? ''
    ).trim();
    if (!raw) {
      return '';
    }
    const hintState = coerceUsStateCode(
      String(data['state'] ?? data['vehicle_state'] ?? '').trim() || null
    );
    const { license_plate } = splitLicensePlateForApi(raw, hintState);
    return (license_plate || raw).trim().toUpperCase();
  }

  /** Customer full name on order-success, or a dash when the customer is unknown. */
  getOrderSuccessCustomerNameDisplay(): string {
    const data = (this.notification?.data ?? {}) as Record<string, any>;
    return formatOrderSuccessCustomerName(data['customer_first_name'], data['customer_last_name']);
  }

  /** Customer email on order-success, or a dash when the customer is unknown. */
  getOrderSuccessCustomerEmailDisplay(): string {
    return orderSuccessDisplayValue(this.notification?.data?.customer_email);
  }

  /** License plate on order-success, or a dash when no plate was captured. */
  getOrderSuccessLicensePlateOrEmpty(): string {
    return orderSuccessDisplayValue(this.getOrderSuccessLicensePlateDisplay());
  }

  /**
   * Get last washed date for active-subscription notification
   */
  getActiveSubscriptionLastWashedDate(): string {
    if (!this.notification?.data || this.notification?.type !== 'active-subscription') return '';
    return this.notification.data.last_washed_date || '';
  }

  /**
   * Get formatted last washed date for active-subscription notification
   */
  getFormattedActiveSubscriptionLastWashedDate(): string {
    const lastWashedDate = this.getActiveSubscriptionLastWashedDate();
    if (!lastWashedDate) return 'N/A';

    try {
      const date = new Date(lastWashedDate);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (e) {
      return lastWashedDate;
    }
  }

  /**
   * "Total Washes Available" for the legacy inline Active subscription block.
   *
   * The template hard-coded "Unlimited" here, so a finite package — three washes,
   * twelve washes — read as unlimited to the attendant whatever the member actually
   * bought (CWS-949). The payload already carries the package's allowance in
   * `total_washes_available` (svc-order sends `eligible.allowedWashes`) alongside
   * `wash_count_period`.
   *
   * Unlimited is period `unlimited` or a 0 / absent frequency — the rule OrderUtils
   * and svc-order's membershipWashLimit both encode, and the reason this cannot just
   * print the raw number: "0" reads as no washes left for a member entitled to every
   * wash. Nested `data.data` is read as a fallback for the same reason
   * {@link getActiveSubscriptionVehicleAttr} does — list reopen and live SSE.
   */
  getActiveSubscriptionWashesAvailable(): string {
    const data = (this.notification?.data ?? {}) as Record<string, any>;
    const nested =
      data['data'] && typeof data['data'] === 'object' && !Array.isArray(data['data'])
        ? (data['data'] as Record<string, any>)
        : {};
    const rawAllowance = data['total_washes_available'] ?? nested['total_washes_available'];
    const period = data['wash_count_period'] ?? nested['wash_count_period'];
    const parsed =
      rawAllowance === null || rawAllowance === undefined || rawAllowance === ''
        ? null
        : Number.parseInt(String(rawAllowance), 10);
    const allowance = parsed === null || Number.isNaN(parsed) ? null : parsed;
    if (OrderUtils.isUnlimitedMembershipWashLimit(period, allowance)) {
      return 'Unlimited';
    }
    return String(allowance);
  }

  /**
   * Family plan tags for the legacy inline Active subscription block (CWS-1582 §8). The fields sit
   * on the payload, or on a nested `data` when the notification wraps its row.
   */
  get activeSubscriptionFamilyTags(): FamilyTags {
    const data = (this.notification?.data ?? {}) as Record<string, any>;
    const nested =
      data['data'] && typeof data['data'] === 'object' && !Array.isArray(data['data'])
        ? (data['data'] as Record<string, any>)
        : {};
    return familyTagsOf(data['is_family'] === undefined ? nested : data);
  }

  /**
   * "Status" row on the legacy inline Active subscription block.
   *
   * This row printed the literal "Subscription Active" for every membership, so a
   * cancelled, past-due or paused subscription still read as active to the attendant
   * (CWS-949, alongside the hard-coded wash count above).
   *
   * `subscription_status` is the field that carries this — `status` is overloaded
   * across the notification payloads (HTTP codes, 'order-success', order status), so
   * it is only trusted when it holds a recognised subscription status. Anything
   * unknown, or nothing at all, keeps today's wording: the block only renders for an
   * active-subscription notification, so "active" remains the right default and this
   * change can only speak up when the payload actually contradicts it.
   */
  getActiveSubscriptionStatusLabel(): string {
    const data = (this.notification?.data ?? {}) as Record<string, any>;
    const nested =
      data['data'] && typeof data['data'] === 'object' && !Array.isArray(data['data'])
        ? (data['data'] as Record<string, any>)
        : {};
    const labels: Record<string, string> = {
      active: 'Subscription Active',
      trialing: 'Subscription Active (Trial)',
      canceled: 'Subscription Cancelled',
      cancelled: 'Subscription Cancelled',
      past_due: 'Subscription Past Due',
      unpaid: 'Subscription Unpaid',
      paused: 'Subscription Paused',
      incomplete: 'Subscription Incomplete',
      incomplete_expired: 'Subscription Incomplete',
    };
    const explicit = String(data['subscription_status'] ?? nested['subscription_status'] ?? '')
      .trim()
      .toLowerCase();
    if (labels[explicit]) {
      return labels[explicit];
    }
    const ambiguous = String(data['status'] ?? nested['status'] ?? '')
      .trim()
      .toLowerCase();
    return labels[ambiguous] ?? 'Subscription Active';
  }

  /** Customer name for Active Subscription Got-it modal (first/last, customer_name, nested). */
  getActiveSubscriptionCustomerName(): string {
    return resolveActiveSubscriptionCustomerName(
      (this.notification?.data ?? null) as Record<string, any> | null
    );
  }

  /** Vehicle year / make / model with nested-data fallbacks (list reopen + live SSE). */
  getActiveSubscriptionVehicleAttr(attr: 'year' | 'make' | 'model'): string {
    const data = (this.notification?.data ?? {}) as Record<string, any>;
    const nested =
      data['data'] && typeof data['data'] === 'object' && !Array.isArray(data['data'])
        ? (data['data'] as Record<string, any>)
        : {};
    const keys =
      attr === 'year'
        ? ['vehicle_year', 'vehicleYear', 'year']
        : attr === 'make'
          ? ['vehicle_make', 'vehicleMake', 'make']
          : ['vehicle_model', 'vehicleModel', 'model'];
    for (const key of keys) {
      const value = data[key] ?? nested[key];
      if (value != null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return 'N/A';
  }

  /**
   * Check if last washed date is today for active-subscription notification
   */
  getPlateNumber(): string {
    if (!this.notification?.data) return 'N/A';
    return this.notification.data.plate || this.notification.data.data?.plate || 'N/A';
  }

  getState(): string {
    if (!this.notification?.data) return 'N/A';
    return this.notification.data.state || this.notification.data.data?.state || 'N/A';
  }

  getStatusMessage(): string {
    if (!this.notification?.data) return '';
    const message = this.notification.data.message || this.notification.data.data?.message || '';
    return message.replace(/-/g, ' ');
  }

  private getDefaultAppointmentTime(): string {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 10);
    return now.toISOString();
  }

  /**
   * Get appointment time as current time + 5 minutes
   * This is used when creating orders to ensure the appointment time is always in the future
   */
  private getCurrentAppointmentTime(): string {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 0);
    return now.toISOString();
  }

  populateFormData(): void {
    // ALWAYS preserve order completed modal data - never clear it in populateFormData
    // These values should only be cleared when the success modal is explicitly closed
    const preserveOrderCompletedName = this.orderCompletedCustomerName || null;
    const preserveOrderCompletedVehicle =
      this.orderCompletedVehicleNumber || this.formData.vehicleLicensePlate || null;
    const preserveOrderCompletedMake = this.orderCompletedVehicleMake || null;
    const preserveOrderCompletedModel = this.orderCompletedVehicleModel || null;
    const preserveOrderCompletedYear = this.orderCompletedVehicleYear || null;
    const preserveOrderCompletedPackage = this.orderCompletedPackageName || null;

    // Preserve user-entered vehicleLicensePlate if user has interacted with it (BEFORE resetting formData)
    const preserveVehicleLicensePlate = this.userInteractedFields.has('vehicleLicensePlate')
      ? this.formData.vehicleLicensePlate
      : null;
    const preserveVehicleLicensePlateState = this.userInteractedFields.has(
      'vehicleLicensePlateState'
    )
      ? this.formData.vehicleLicensePlateState
      : null;

    // The location on the form is read before the reset below wipes it; whether it survives the
    // re-population is decided by resolveFormLocationOnPopulate once the notification data is known.
    const existingLocation = {
      locationId: this.formData?.locationId,
      location: this.formData?.location,
    };
    const userSelectedLocation =
      !!this.userChosenLocationId && this.userChosenLocationId === existingLocation.locationId;

    // Preserve appointmentTime BEFORE resetting formData (especially for detailing appointments)
    const preserveAppointmentTime =
      this.formData.appointmentTime ||
      this.notification?.data?.appointment_time ||
      this.notification?.data?.appointmentTime ||
      null;

    // Preserve notificationId BEFORE resetting formData (so a real id survives modal state
    // changes and is still available for updates after the modal closes).
    // It is an LPR entry id, so only a real one is worth keeping: direct orders
    // (quick_plus_order) have no LPR entry, and when nothing real is available we leave it
    // empty so the downstream lpr-entries calls skip themselves on the falsy guard.
    const preserveNotificationId = this.isDirectOrder()
      ? ''
      : this.formData.notificationId ||
        (this.notification?.data as any)?.lpr_entry_id ||
        this.notification?.data?.id ||
        this.notification?.data?.licence_plate_id ||
        this.notification?.data?.license_plate_id ||
        '';

    console.log('🔍 Debug - populateFormData preserveNotificationId:', {
      formDataNotificationId: this.formData.notificationId,
      notificationDataId: this.notification?.data?.id,
      preserveNotificationId: preserveNotificationId,
    });

    // Reset formData to default values first to clear any old data
    // BUT preserve notificationId - never clear it
    this.formData = {
      customerFirstName: '',
      customerLastName: '',
      customerEmail: '',
      customerContact: '',
      vehicleLicensePlate: '',
      vehicleLicensePlateState: DEFAULT_LICENSE_PLATE_STATE,
      vehicleMake: '',
      vehicleModel: '',
      vehicleYear: '',
      vehicleVin: '',
      location: '',
      lane: '',
      package: '',
      vehicleId: '',
      customerId: '',
      locationId: '',
      laneId: '',
      packageId: '',
      price: '',
      orderId: '',
      appointmentTime: '',
      stripeCustomerId: '',
      notificationId: preserveNotificationId || '', // Preserve notificationId - never clear it
      detailingAppointmentTime: '',
    };
    this.vehicleDataFetched = false;
    this.fetchedVehicleId = '';
    this.lastVehicleLookupPlate = '';
    this.lastVehiclePlateLookupAttempted = '';
    this.plateLookupInFlightPlate = null;

    // Clear all errors when new notification arrives
    this.updateOrderError = '';

    this.resetOrderRequestAddonUi();
    this.resetOrderRequestDiscountUi();
    this.orderRequestSelectedFleetId = null;
    this.orderRequestSelectedPrepaidBookId = null;
    this.defaultPackagesForMostUsed = [];
    this.mostUsedServiceItems = [];

    this.checkoutSummaryDiscountSelected = true;
    this.checkoutSummaryAddonSelected = true;
    this.checkoutAddonDetailsOpen = false;
    this.checkoutWayPlusBenefitsModalOpen = false;

    // Reset cached order wash_type
    this.cachedOrderWashType = null;
    // A fresh notification is not a deselect, so the tiles start from the new order.
    this.orderRequestWashSelectionCleared = false;

    // Clear selected package and wash type fields when new notification arrives
    this.selectedPackage = null;
    this.selectedWashType = null;
    this.availableWashTypes = [];
    this.showWashTypeDropdown = false;
    // Note: expandedPackages will be regenerated when packages are loaded

    // If no notification data, return early (modal closed)
    if (!this.notification?.data) {
      this.clearPrepaidRedemptionState();
      // Reset user interaction tracking when modal closes
      this.userInteractedFields.clear();
      this.userChosenLocationId = null;

      // ALWAYS restore order completed modal data if it exists (before returning)
      // This ensures data persists even if populateFormData is called multiple times
      if (preserveOrderCompletedName) {
        this.orderCompletedCustomerName = preserveOrderCompletedName;
      }
      if (preserveOrderCompletedVehicle) {
        this.orderCompletedVehicleNumber = preserveOrderCompletedVehicle;
      }
      if (preserveOrderCompletedMake) {
        this.orderCompletedVehicleMake = preserveOrderCompletedMake;
      }
      if (preserveOrderCompletedModel) {
        this.orderCompletedVehicleModel = preserveOrderCompletedModel;
      }
      if (preserveOrderCompletedYear) {
        this.orderCompletedVehicleYear = preserveOrderCompletedYear;
      }
      if (preserveOrderCompletedPackage) {
        this.orderCompletedPackageName = preserveOrderCompletedPackage;
      }

      return;
    }

    // Clear Stripe element errors
    const cardNumberError = document.getElementById('card-number-error');
    const cardExpiryError = document.getElementById('card-expiry-error');
    const cardCvcError = document.getElementById('card-cvc-error');
    if (cardNumberError) {
      cardNumberError.textContent = '';
      cardNumberError.style.display = 'none';
    }
    if (cardExpiryError) {
      cardExpiryError.textContent = '';
      cardExpiryError.style.display = 'none';
    }
    if (cardCvcError) {
      cardCvcError.textContent = '';
      cardCvcError.style.display = 'none';
    }
    // Clear stored Stripe error messages
    this.stripeCardNumberError = '';
    this.stripeCardExpiryError = '';
    this.stripeCardCvcError = '';

    const data = this.notification.data;
    console.log('notfication dataee', data);

    // Determine vehicleLicensePlate value: if ACTIONTEST, set to empty string, otherwise use data.plate
    let vehicleLicensePlateValue = '';
    if (preserveVehicleLicensePlate !== null) {
      // Use preserved user-entered value
      vehicleLicensePlateValue = preserveVehicleLicensePlate;
    } else {
      // Use data.plate, but if it's ACTIONTEST, set to empty string
      const plateValue = data.plate || data.vehicle_license_plate || '';
      vehicleLicensePlateValue = plateValue === 'ACTIONTEST' ? '' : plateValue;
    }

    // Now populate formData with new notification data (formData was already reset above)
    // Preserve notificationId - use preserved value first (never clear it), then try notification data, then fallback
    const notificationId = this.isDirectOrder()
      ? ''
      : preserveNotificationId ||
        data.lpr_entry_id ||
        data.id ||
        data.licence_plate_id ||
        data.license_plate_id ||
        this.notification?.data?.id ||
        this.notification?.data?.licence_plate_id ||
        this.notification?.data?.license_plate_id ||
        this.formData.notificationId || // Fallback to existing formData value
        '';

    console.log('🔍 Debug - populateFormData setting notificationId:', {
      preserveNotificationId: preserveNotificationId,
      dataId: data.id,
      dataLicencePlateId: data.licence_plate_id,
      finalNotificationId: notificationId,
    });

    const resolvedLocation = resolveFormLocationOnPopulate({
      isLaneLocked: this.isOrderRequestLocationLockedToNotificationLane(),
      userSelectedLocation,
      existing: existingLocation,
      notification: { locationId: data.location_id || data.locationId, branch: data.branch },
    });

    this.formData = {
      customerFirstName: data.customer_first_name || data.customerFirstName || '',
      customerLastName: data.customer_last_name || data.customerLastName || '',
      customerEmail: data.customer_email || data.customerEmail || '',
      customerContact: data.customer_contact || data.customerContact || '',
      location: resolvedLocation.location,
      lane: '',
      vehicleLicensePlate: '',
      vehicleLicensePlateState: DEFAULT_LICENSE_PLATE_STATE,
      vehicleMake: data.vehicle_make || data.vehicleMake || '',
      vehicleModel: data.vehicle_model || data.vehicleModel || '',
      vehicleYear: data.vehicle_year || data.vehicleYear || '',
      vehicleVin: data.vehicle_vin || data.vehicleVin || '',
      package:
        data.package_name ||
        data.packageName ||
        data.vehicle_data?.package_name ||
        data.vehicle_data?.packageName ||
        '',
      vehicleId: data.vehicle_id || data.vehicleId || '',
      customerId: data.customer_id || data.customerId || '',
      locationId: resolvedLocation.locationId,
      laneId: resolveCarriedPickedLaneId(this.resolveCurrentLocationPickerOrigin(), data),
      packageId:
        data.package_id ||
        data.packageId ||
        data.vehicle_data?.package_id ||
        data.vehicle_data?.packageId ||
        '',
      price: data.final_price || data.finalPrice || '',
      orderId: data.order_id || data.orderId || '',
      appointmentTime:
        preserveAppointmentTime ||
        data.appointment_time ||
        data.appointmentTime ||
        this.getDefaultAppointmentTime(),
      stripeCustomerId: data.stripe_customer_id || data.stripeCustomerId || '',
      notificationId: notificationId, // Preserve notification ID early - NEVER clear this
      detailingAppointmentTime:
        data.detailing_appointment_time || data.detailingAppointmentTime || '',
    };
    // An LPR read refused for an inactive customer blocks the order before any plate lookup lands.
    // Set-only: a later re-populate from the lpr-entries API carries no refusal_reason and must
    // not lift a block the plate lookup already applied.
    if (data.refusal_reason === CUSTOMER_INACTIVE_REFUSAL_REASON && this.formData.customerId) {
      this.inactiveCustomerId = this.formData.customerId;
    }
    this.setCombinedLicensePlateOnForm(
      vehicleLicensePlateValue,
      preserveVehicleLicensePlateState || data.state || data.state_code || data.vehicle_state
    );
    if (preserveVehicleLicensePlateState) {
      const plateOnly = splitLicensePlateForApi(
        this.formData.vehicleLicensePlate,
        preserveVehicleLicensePlateState
      ).license_plate;
      this.formData.vehicleLicensePlateState = preserveVehicleLicensePlateState;
      this.formData.vehicleLicensePlate = plateOnly
        ? `${preserveVehicleLicensePlateState}${plateOnly}`
        : '';
    }
    this.syncVehiclePlateLookupCacheForFormPlate(this.formData.vehicleLicensePlate);

    // Only while the plate on the form is still the scanned one. populateFormData re-runs on
    // later notification updates, and by then the plate may be a value staff typed — capturing
    // that would make both checks below compare a value against itself and never fire.
    if (this.canApplyExternalLicensePlateToForm()) {
      const scannedPlate = (this.formData.vehicleLicensePlate || '').trim();
      this.scannedPlateKeyForImages = scannedPlate
        ? this.plateKeyFromFullPlate(scannedPlate)
        : null;

      // A scan prefills vehicleId and the plate together without going through the plate-lookup
      // path that records the pairing, so detachVehicleIdIfPlateChangedFromSelection() bailed on
      // a null link and staff retyping the plate kept the scanned vehicle on the order — order
      // details then showed the scanned plate rather than the typed one (CWS-818).
      this.syncLinkedVehicleSelection(this.formData.vehicleId, scannedPlate);
    }

    // Merge persisted order from POST /order or thin LPR notification fields into data.order
    if (this.notification?.type === 'create-payment-request') {
      const d = this.notification.data as Record<string, any>;
      const str = (v: any) => (v != null && v !== '' ? String(v).trim() : '');
      const existing =
        data.order && typeof data.order === 'object' && !Array.isArray(data.order)
          ? ({ ...(data.order as Record<string, any>) } as Record<string, any>)
          : ({} as Record<string, any>);
      const pick = (orderKey: string, ...dataKeys: string[]): any => {
        if (existing[orderKey] != null && String(existing[orderKey]).trim() !== '') {
          return existing[orderKey];
        }
        for (const k of dataKeys) {
          const v = data[k];
          if (v != null && String(v).trim() !== '') {
            return v;
          }
        }
        return undefined;
      };
      const mergedOrder: Record<string, any> = {
        ...existing,
        list_price: pick('list_price', 'list_price', 'listPrice'),
        discount: pick('discount', 'discount') ?? existing['discount'] ?? 0,
        final_price: pick('final_price', 'final_price', 'finalPrice'),
        package_id: pick('package_id', 'package_id', 'packageId'),
        package_name: pick('package_name', 'package_name', 'packageName'),
        prepaid_wash_book_id: pick(
          'prepaid_wash_book_id',
          'prepaid_wash_book_id',
          'prepaidWashBookId'
        ),
        prepaid_wash_book_name: pick(
          'prepaid_wash_book_name',
          'prepaid_wash_book_name',
          'prepaidWashBookName'
        ),
        wash_type: pick('wash_type', 'wash_type', 'washType'),
        appointment_time: pick('appointment_time', 'appointment_time', 'appointmentTime'),
        make: pick('make', 'vehicle_make', 'vehicleMake'),
        model: pick('model', 'vehicle_model', 'vehicleModel'),
        year: pick('year', 'vehicle_year', 'vehicleYear'),
        license_plate: pick('license_plate', 'vehicle_license_plate', 'license_plate', 'plate'),
      };
      d['order'] = mergedOrder;
      this.restoreCheckoutPrepaidSelectionFromOrderSnapshot();
      if (str(mergedOrder['prepaid_wash_book_name'])) {
        this.formData.package = str(mergedOrder['prepaid_wash_book_name']);
      }

      const o = mergedOrder;
      if (str(o['make'])) {
        this.formData.vehicleMake = str(o['make']);
      }
      if (str(o['model'])) {
        this.formData.vehicleModel = str(o['model']);
      }
      if (o['year'] != null && o['year'] !== '') {
        this.formData.vehicleYear = String(o['year']);
      }
      if (str(o['license_plate']) && this.canApplyExternalLicensePlateToForm()) {
        this.setCombinedLicensePlateOnForm(str(o['license_plate']), str(o['state']) || null);
      }
      const isPrepaidOrder = !!str(o['prepaid_wash_book_id']);
      if (!isPrepaidOrder) {
        if (str(o['package_name'])) {
          this.formData.package = str(o['package_name']);
        }
        if (str(o['package_id'])) {
          this.formData.packageId = str(o['package_id']);
        }
      } else {
        delete d['packageId'];
        delete d['package_id'];
        delete d['packageName'];
        delete d['package_name'];
        delete mergedOrder['package_id'];
        delete mergedOrder['package_name'];
      }
      const listOrFinal = isPrepaidOrder
        ? str(o['list_price']) || str(o['final_price'])
        : str(o['final_price']) || str(o['list_price']);
      if (listOrFinal) {
        this.formData.price = listOrFinal.replace(/^\$/, '');
      }
      if (isPrepaidOrder) {
        const prepaidList = this.parseUsdNumber(o['list_price']);
        if (prepaidList != null && prepaidList > 0) {
          this.formData.price = prepaidList.toFixed(2);
        }
      }
      if (str(o['appointment_time'])) {
        this.formData.appointmentTime = str(o['appointment_time']);
      }
      if (str(o['wash_type'])) {
        this.cachedOrderWashType = str(o['wash_type']);
      }
    }

    // Vehicle make/model for invalid-license-plate is loaded via license-plate search / fetchVehicleDataByLicensePlate dedupe.

    // For create-payment-request notifications, cache wash_type from notification data or fetch from order
    if (this.notification?.type === 'create-payment-request') {
      const orderWashType = data.wash_type || data.order?.wash_type || '';
      if (orderWashType) {
        this.cachedOrderWashType = orderWashType;
        // Update payment method if monthly subscription is detected (only for desktop)
        const normalized = this.normalizeWashType(orderWashType);
        if (normalized === 'monthly_membership' && !this.isMobileOrTablet) {
          // Payment form will be initialized below, but ensure method is set correctly
          // This handles the case where paymentForm might already exist
          if (this.paymentForm) {
            this.paymentForm.method = this.getDefaultPaymentMethod();
            // Initialize Stripe elements if card is set as default
            this.initializeStripeForCardDefault();
          }
        }
        this.reconcilePaymentMethodAfterContextChange();
      }
      if (this.formData.orderId) {
        this.hydrateCreatePaymentRequestCheckoutFromOrderIfNeeded();
      }
    }

    // Set selected package if packageId is available (use formData — snake_case package_id is merged above)
    const populatedPackageId = (this.formData.packageId || '').trim();
    if (this.isAddonOnlyWashContext()) {
      this.clearOrderRequestWashPackageSelection();
    } else if (this.isCheckoutPrepaidPurchase()) {
      this.clearOrderRequestWashPackageSelection();
    } else if (populatedPackageId && this.expandedPackages.length > 0) {
      // Try to find wash type from notification data
      const washType = data.wash_type || data.washType || data.order?.wash_type || null;

      if (washType) {
        const washNorm = this.normalizeWashType(String(washType));
        const expandedPkg = this.expandedPackages.find(
          ep =>
            ep.package.id === populatedPackageId && this.normalizeWashType(ep.washType) === washNorm
        );
        if (expandedPkg) {
          this.selectedPackage = expandedPkg.package;
          this.selectedWashType = expandedPkg.washType;
          this.formData.package = expandedPkg.package.package_name || '';
          this.formData.packageId = expandedPkg.package.id || '';
        } else {
          // Fallback: find by packageId only
          const pkg = this.packages.find(p => p.id === populatedPackageId) || null;
          this.selectedPackage = pkg;
          if (pkg) {
            this.formData.package = pkg.package_name || '';
            this.formData.packageId = pkg.id || '';
            // If package has only one wash type, auto-select it
            if (pkg.wash_types && pkg.wash_types.length === 1) {
              this.selectedWashType = pkg.wash_types[0];
            }
          }
        }
      } else {
        // No wash type info, find package and auto-select wash type if only one
        const pkg = this.packages.find(p => p.id === populatedPackageId) || null;
        this.selectedPackage = pkg;
        if (pkg) {
          this.formData.package = pkg.package_name || '';
          this.formData.packageId = pkg.id || '';
          if (pkg.wash_types && pkg.wash_types.length === 1) {
            this.selectedWashType = pkg.wash_types[0];
          }
        }
      }
    } else if (
      populatedPackageId &&
      this.expandedPackages.length === 0 &&
      !this.isCheckoutPrepaidPurchase()
    ) {
      // If packages haven't loaded yet, wait for them to load
      this.packageService.packages$.pipe(takeUntil(this.destroy$)).subscribe(packages => {
        if (packages.length > 0) {
          // Re-expand packages
          this.expandedPackages = this.expandPackages(packages);
          if (this.isCheckoutPrepaidPurchase()) {
            this.clearOrderRequestWashPackageSelection();
            return;
          }

          // Try to find wash type from notification data
          const washType = data.wash_type || data.washType || data.order?.wash_type || null;

          if (washType) {
            const washNorm = this.normalizeWashType(String(washType));
            const expandedPkg = this.expandedPackages.find(
              ep =>
                ep.package.id === populatedPackageId &&
                this.normalizeWashType(ep.washType) === washNorm
            );
            if (expandedPkg) {
              this.selectedPackage = expandedPkg.package;
              this.selectedWashType = expandedPkg.washType;
              this.formData.package = expandedPkg.package.package_name || '';
              this.formData.packageId = expandedPkg.package.id || '';
            } else {
              const pkg = packages.find(p => p.id === populatedPackageId) || null;
              this.selectedPackage = pkg;
              if (pkg) {
                this.formData.package = pkg.package_name || '';
                this.formData.packageId = pkg.id || '';
                if (pkg.wash_types && pkg.wash_types.length === 1) {
                  this.selectedWashType = pkg.wash_types[0];
                }
              }
            }
          } else {
            const pkg = packages.find(p => p.id === populatedPackageId) || null;
            this.selectedPackage = pkg;
            if (pkg) {
              this.formData.package = pkg.package_name || '';
              this.formData.packageId = pkg.id || '';
              if (pkg.wash_types && pkg.wash_types.length === 1) {
                this.selectedWashType = pkg.wash_types[0];
              }
            }
          }

          this.rebuildMostUsedServiceItems();
          if (this.notification?.type === 'create-payment-request') {
            this.restoreOrderRequestSingleWashCustomPriceFromPersistedSources();
          }
        }
      });
    }

    // Reset user interaction tracking when new data is loaded
    // This ensures all fields come back to default enabled state
    this.userInteractedFields.clear();

    // Initialize payment form only when checkout has a real order (not pre-order LPR lane UI).
    if (this.notification.type === 'create-payment-request' && this.hasCheckoutOrderId()) {
      this.initializePaymentForm();
      // Reset payment link sent state when modal opens
      this.paymentLinkSent = false;
      this.paymentExpired = false;
      this.clearPaymentTimer();
      this.resetPaymentTimer();

      // First, check if payment intent is in notification data
      if (this.notification.data?.payment_intent_client_secret) {
        this.paymentIntentClientSecret = this.notification.data.payment_intent_client_secret;
        console.log(
          '✅ Payment intent from notification data (populateFormData):',
          this.paymentIntentClientSecret.substring(0, 20) + '...'
        );
      } else if (this.notification.data?.client_secret) {
        this.paymentIntentClientSecret = this.notification.data.client_secret;
        console.log(
          '✅ Payment intent (client_secret) from notification data (populateFormData):',
          this.paymentIntentClientSecret.substring(0, 20) + '...'
        );
      } else if (this.formData.orderId) {
        // Payment intent is now created when user selects card (create-payment-intent API), not fetched from order
      }

      this.restoreOrderRequestSingleWashCustomPriceFromPersistedSources();
      this.restoreCheckoutPrepaidSelectionFromOrderSnapshot();
      this.restoreCheckoutAddonSelectionFromOrderSnapshot();
      if (this.hasPersistedWashPackageOnCheckoutOrder()) {
        this.userInteractedFields.add('package');
        this.userInteractedFields.add('packageId');
      }
      this.ensureCheckoutPrepaidBookLoadedForSummary();
    }

    // Check customer orders for subscription suggestion for all order-request-layout types
    if (this.showOrderRequestCheckoutLayout()) {
      this.vendorModeService.loadIfNeeded();
      this.syncOrderRequestHistoryUiForCurrentContext();
      this.checkCustomerOrdersForSubscription();
      this.scheduleInitialLicensePlateSearchIfNeeded();
    }
    if (this.notification?.type === 'active-subscription') {
      this.vendorModeService.loadIfNeeded();
    }
    if (this.shouldShowVendorActiveSubscriptionModal()) {
      this.ensureVendorActiveSubscriptionWashHistory();
    }
    if (
      this.showOrderRequestCheckoutLayout() ||
      this.notification?.type === 'fleet-vehicle-found'
    ) {
      this.syncOrderRequestCatalogForCurrentLocation();
    }

    // ALWAYS restore order completed modal data at the end of populateFormData
    // This ensures data persists even when populateFormData is called multiple times or when new notifications arrive
    if (preserveOrderCompletedName) {
      this.orderCompletedCustomerName = preserveOrderCompletedName;
    }
    if (preserveOrderCompletedVehicle) {
      this.orderCompletedVehicleNumber = preserveOrderCompletedVehicle;
    }
    if (preserveOrderCompletedMake) {
      this.orderCompletedVehicleMake = preserveOrderCompletedMake;
    }
    if (preserveOrderCompletedModel) {
      this.orderCompletedVehicleModel = preserveOrderCompletedModel;
    }
    if (preserveOrderCompletedYear) {
      this.orderCompletedVehicleYear = preserveOrderCompletedYear;
    }
    if (preserveOrderCompletedPackage) {
      this.orderCompletedPackageName = preserveOrderCompletedPackage;
    }

    this.reapplyCachedLocationToFormAfterPopulate();
    this.syncOrderRequestPlateImageFromNotificationData();
    this.refreshOrderRequestLprAxisPlateImagePreload();

    if (this.notification?.type === 'create-payment-request') {
      this.loadTerminalCheckoutData();
    }
  }

  /**
   * One customer + vehicle lookup per prefilled plate when the modal opens (avoids duplicate GET vehicle/plate).
   */
  private scheduleInitialLicensePlateSearchIfNeeded(): void {
    const plate = (this.formData.vehicleLicensePlate || '').trim();
    if (plate.length < 3) {
      return;
    }
    const plateKey = this.normalizePlateForVehicleLookupCache(plate);
    if (
      this.licensePlateCustomerSearchCompletedKey === plateKey ||
      this.licensePlateCustomerSearchInFlightKey === plateKey
    ) {
      return;
    }
    setTimeout(() => {
      const current = (this.formData.vehicleLicensePlate || '').trim();
      if (current.length < 3) {
        return;
      }
      if (this.normalizePlateForVehicleLookupCache(current) !== plateKey) {
        return;
      }
      this.triggerLicensePlateSearchFromValue(current);
    }, 0);
  }

  /**
   * Uses GET order/lpr-entries/:id when the Pusher payload already has an entry id;
   * only falls back to the location list when id is missing.
   */
  private loadNotificationFromApi(notification: NotificationData): void {
    if (!this.shouldRefreshNotificationFromLprEntriesApi(notification)) {
      this.logModalInteraction('notification.api.load.skip', {
        reason: 'create_payment_request',
        type: notification.type,
      });
      return;
    }
    if (this.lprEntriesLocationHydrationAttemptedForOpen || this.lprEntriesLocationFetchInFlight) {
      this.logModalInteraction('notification.api.load.skip', {
        reason: 'already_hydrated_or_in_flight',
      });
      return;
    }
    if (this.skipLprEntryApiHydrationFromNotificationsList) {
      this.lprEntriesLocationHydrationAttemptedForOpen = true;
      this.orderRequestLprAxisPlateImageApiHydrationPending = false;
      this.refreshOrderRequestLprAxisPlateImagePreload();
      this.logModalInteraction('notification.api.load.skip', { reason: 'notifications_list_row' });
      return;
    }
    if (this.shouldSkipLprHydrationForPrepaidRedeem(notification)) {
      this.lprEntriesLocationHydrationAttemptedForOpen = true;
      this.orderRequestLprAxisPlateImageApiHydrationPending = false;
      this.refreshOrderRequestLprAxisPlateImagePreload();
      this.logModalInteraction('notification.api.load.skip', {
        reason: 'prepaid_redeem_payload_complete',
      });
      return;
    }
    if (this.hasInlineLprEntryHydrationFromPusher(notification)) {
      this.lprEntriesLocationHydrationAttemptedForOpen = true;
      this.orderRequestLprAxisPlateImageApiHydrationPending = false;
      this.refreshOrderRequestLprAxisPlateImagePreload();
      this.logModalInteraction('notification.api.load.skip', { reason: 'inline_sse_payload' });
      return;
    }
    this.lprEntriesLocationHydrationAttemptedForOpen = true;
    this.lprEntriesLocationFetchInFlight = true;

    this.orderRequestLprAxisPlateImageApiHydrationPending = true;
    this.refreshOrderRequestLprAxisPlateImagePreload();

    const licensePlate =
      notification.data?.plate ||
      notification.data?.vehicle_license_plate ||
      notification.data?.data?.plate ||
      notification.data?.data?.vehicle_license_plate ||
      '';

    if (!licensePlate || licensePlate === 'Unknown' || licensePlate.trim() === '') {
      console.log('⏭️ Skipping API load: No valid license plate in notification');
      this.logModalInteraction('notification.api.load.skip', { reason: 'no_valid_license_plate' });
      this.lprEntriesLocationFetchInFlight = false;
      this.orderRequestLprAxisPlateImageApiHydrationPending = false;
      this.refreshOrderRequestLprAxisPlateImagePreload();
      return;
    }

    const lprEntryId = this.resolveLprEntryIdForModalHydration(notification);
    if (lprEntryId) {
      this.logModalInteraction('notification.api.load.start', {
        licensePlate,
        lprEntryId,
        strategy: 'by_id',
      });
      this.apiService
        .get(`order/lpr-entries/${lprEntryId}`)
        .pipe(
          takeUntil(this.destroy$),
          catchError(error => {
            console.error(`Error fetching LPR entry ${lprEntryId}:`, error);
            this.lprEntryByIdFetchFailedForOpen = true;
            return of({ status: false, data: null });
          })
        )
        .subscribe({
          next: (response: any) => {
            const matchingEntry = this.extractLprEntryRowFromApiResponse(response);
            if (matchingEntry) {
              this.applyHydratedLprEntryFromApi(notification, matchingEntry, licensePlate);
              return;
            }
            if (
              this.lprEntryByIdFetchFailedForOpen &&
              !this.skipLprEntryApiHydrationFromNotificationsList
            ) {
              this.loadNotificationFromApiByLocationList(notification, licensePlate);
              return;
            }
            this.logModalInteraction('notification.api.load.partial', {
              reason: 'by_id_unparsed',
              lprEntryId,
            });
            this.finishLprEntryHydration();
          },
        });
      return;
    }

    if (this.skipLprEntryApiHydrationFromNotificationsList) {
      this.logModalInteraction('notification.api.load.skip', {
        reason: 'notifications_list_row_no_id',
      });
      this.finishLprEntryHydration();
      return;
    }

    this.logModalInteraction('notification.api.load.start', {
      licensePlate,
      strategy: 'location_list',
    });
    this.loadNotificationFromApiByLocationList(notification, licensePlate);
  }

  /** Pusher already sent a full LPR row — skip redundant list/by-id hydration on open. */
  private hasInlineLprEntryHydrationFromPusher(notification: NotificationData): boolean {
    const d = notification.data as Record<string, any> | undefined;
    if (!d) {
      return false;
    }
    if (!resolveLprEntryIdFromNotificationData(d)) {
      return false;
    }
    const plate = String(d['plate'] ?? d['vehicle_license_plate'] ?? '').trim();
    if (!plate || plate === 'Unknown') {
      return false;
    }
    const hasImage = !!resolveLprAxisImageUrlFromNotificationData(d);
    const hasOrderContext =
      String(d['customer_id'] ?? d['customerId'] ?? '').trim().length > 0 ||
      String(d['package_id'] ?? d['packageId'] ?? '').trim().length > 0 ||
      String(d['vehicle_id'] ?? d['vehicleId'] ?? '').trim().length > 0;
    return hasImage && hasOrderContext;
  }

  /**
   * Live prepaid redeem SSE already carries purchase/customer/vehicle ids after normalize.
   * Skipping by-id hydration avoids a second populateFormData that leaves history/prepaid
   * stuck on loading when the fetch loop ran outside NgZone.
   */
  private shouldSkipLprHydrationForPrepaidRedeem(notification: NotificationData): boolean {
    const d = notification.data as Record<string, any> | undefined;
    if (!d || !isPrepaidRedeemLprNotification(notification.type, d)) {
      return false;
    }
    const purchaseId = String(d['prepaid_purchase_id'] || d['prepaidPurchaseId'] || '').trim();
    const customerId = String(d['customer_id'] || d['customerId'] || '').trim();
    const vehicleId = String(d['vehicle_id'] || d['vehicleId'] || '').trim();
    return !!(purchaseId && customerId && vehicleId);
  }

  private resolveLprEntryIdForModalHydration(notification: NotificationData): string {
    const fromForm = String(this.formData.notificationId || '').trim();
    if (fromForm) {
      return fromForm;
    }
    return resolveLprEntryIdFromNotificationData(notification.data as Record<string, any>);
  }

  private extractLprEntryRowFromApiResponse(response: any): Record<string, any> | null {
    if (!response) {
      return null;
    }
    const ok =
      response.status === true ||
      response.status_code === 200 ||
      (response.status !== false && response.data != null);
    if (!ok || response.data == null) {
      return null;
    }
    const row = response.data?.data ?? response.data;
    return row && typeof row === 'object' && !Array.isArray(row)
      ? (row as Record<string, any>)
      : null;
  }

  private loadNotificationFromApiByLocationList(
    notification: NotificationData,
    licensePlate: string
  ): void {
    const lprEntryId = this.resolveLprEntryIdForModalHydration(notification);
    if (lprEntryId && !this.lprEntryByIdFetchFailedForOpen) {
      this.finishLprEntryHydration();
      return;
    }
    this.resolveLprEntriesLocationIds(notification)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (locationIds: string[]) => {
          if (locationIds.length === 0) {
            console.warn('No location IDs available for loading notification');
            this.lprEntriesLocationFetchInFlight = false;
            this.orderRequestLprAxisPlateImageApiHydrationPending = false;
            this.refreshOrderRequestLprAxisPlateImagePreload();
            return;
          }

          const requests = locationIds.map(locationId =>
            this.apiService.get(`order/lpr-entries/location/${locationId}`).pipe(
              catchError(error => {
                console.error(`Error fetching notifications for location ${locationId}:`, error);
                return of({ status: false, data: [] });
              })
            )
          );

          forkJoin(requests)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (responses: any[]) => {
                let matchingEntry: Record<string, any> | null = null;

                for (const response of responses) {
                  if (response.status && response.data && Array.isArray(response.data)) {
                    matchingEntry =
                      response.data.find((entry: any) => {
                        const entryPlate =
                          entry.plate || entry.vehicle_license_plate || entry.license_plate || '';
                        return (
                          entryPlate.trim().toUpperCase() === licensePlate.trim().toUpperCase()
                        );
                      }) ?? null;

                    if (matchingEntry) {
                      break;
                    }
                  }
                }

                if (matchingEntry) {
                  this.applyHydratedLprEntryFromApi(notification, matchingEntry, licensePlate);
                } else {
                  console.log('⚠️ Matching notification entry not found in API');
                  this.logModalInteraction('notification.api.load.noMatch', { licensePlate });
                  this.finishLprEntryHydration();
                }
              },
              error: (error: any) => {
                console.error('Error loading notification from API:', error);
                this.logModalInteraction('notification.api.load.error', {
                  message: error?.message ?? String(error),
                });
                this.finishLprEntryHydration();
              },
            });
        },
        error: (error: any) => {
          console.error('Error getting location IDs:', error);
          this.finishLprEntryHydration();
        },
      });
  }

  private applyHydratedLprEntryFromApi(
    notification: NotificationData,
    matchingEntry: Record<string, any>,
    licensePlate: string
  ): void {
    const preservedAppointmentTime =
      notification.data?.appointment_time ||
      notification.data?.appointmentTime ||
      matchingEntry['appointment_time'] ||
      matchingEntry['appointmentTime'];

    const originalData = notification.data as Record<string, any> | undefined;
    const preservePlateNull = this.isTopLevelImageFieldExplicitlyEmpty(
      originalData,
      'plate_image_s3_url'
    );
    const preserveVehicleNull = this.isTopLevelImageFieldExplicitlyEmpty(
      originalData,
      'image_s3_url'
    );

    const mergedData: Record<string, any> = {
      ...notification.data,
      ...matchingEntry,
      plate:
        matchingEntry['plate'] ||
        matchingEntry['vehicle_license_plate'] ||
        matchingEntry['license_plate'] ||
        licensePlate,
      vehicle_license_plate:
        matchingEntry['vehicle_license_plate'] ||
        matchingEntry['plate'] ||
        matchingEntry['license_plate'] ||
        licensePlate,
      appointment_time:
        preservedAppointmentTime ||
        matchingEntry['appointment_time'] ||
        matchingEntry['appointmentTime'],
      appointmentTime:
        preservedAppointmentTime ||
        matchingEntry['appointment_time'] ||
        matchingEntry['appointmentTime'],
    };

    // Way unredeemable → create-order handoff: keep staff order UI; DB row may still be Way terminal.
    if (
      notification.type === 'create-order-request' &&
      (originalData?.['way_unredeemable_handoff'] === true ||
        String(matchingEntry['event'] ?? '').startsWith('way-'))
    ) {
      mergedData['event'] = 'create-order-request';
      mergedData['way_unredeemable_handoff'] = true;
      if (originalData?.['way_unredeemable_reason'] != null) {
        mergedData['way_unredeemable_reason'] = originalData['way_unredeemable_reason'];
      }
    }

    if (preservePlateNull) {
      mergedData['plate_image_s3_url'] = null;
    }
    if (preserveVehicleNull) {
      mergedData['image_s3_url'] = null;
    }
    this.keepPushedLaneImagesOverStaleApiRow(notification, matchingEntry, mergedData);

    this.notification = applyResolvedLprEntryTypeToNotification(
      {
        ...notification,
        type:
          mergedData['event'] === 'create-order-request' &&
          notification.type === 'create-order-request'
            ? 'create-order-request'
            : notification.type,
        data: mergedData,
      },
      mergedData
    );
    console.log('🔍 Notification data from API:', this.notification.data);

    const notificationId = String(
      matchingEntry['lpr_entry_id'] ||
        matchingEntry['lprEntryId'] ||
        matchingEntry['id'] ||
        matchingEntry['licence_plate_id'] ||
        matchingEntry['license_plate_id'] ||
        this.formData.notificationId ||
        ''
    );
    if (notificationId) {
      this.formData.notificationId = notificationId;
      console.log('✅ Notification ID captured from API entry:', notificationId);
    }

    this.populateFormData();
    if (this.notification) {
      this.maybeBootstrapPrepaidRedeemFromNotification(this.notification);
    }
    if (
      this.showOrderRequestCheckoutLayout() ||
      this.notification?.type === 'fleet-vehicle-found'
    ) {
      this.loadCarWashPackages();
      this.ensureOrderRequestLocationSource();
    }
    console.log('✅ Notification data updated from API');
    this.logModalInteraction('notification.api.load.matched', {
      notificationId: this.formData.notificationId,
    });

    setTimeout(() => {
      if (this.showOrderRequestCheckoutLayout() && this.formData.customerId) {
        this.checkCustomerOrdersForSubscription();
      }
      this.refreshOrderRequestAsyncUi();
    }, 100);

    this.syncOrderRequestLprImagePreviewFromNotificationData();
    this.finishLprEntryHydration();
    this.refreshOrderRequestAsyncUi();
  }

  /**
   * The by-id GET can leave the server just before deferred lane images (Vaxtor) are stored,
   * and `lpr-axis-images-ready` can then land before its response. That stale row carries null
   * image fields; spreading it over the data would wipe the URLs the push already set, so a
   * URL already on screen for the same entry wins over an empty field from the API.
   */
  private keepPushedLaneImagesOverStaleApiRow(
    openedNotification: NotificationData,
    matchingEntry: Record<string, any>,
    mergedData: Record<string, any>
  ): void {
    const openedData = openedNotification.data as Record<string, any> | undefined;
    const liveData = this.notification?.data as Record<string, any> | undefined;
    const openedEntryId = resolveLprEntryIdFromNotificationData(openedData);
    const liveIsSameEntry =
      !!liveData &&
      (liveData === openedData ||
        (!!openedEntryId && resolveLprEntryIdFromNotificationData(liveData) === openedEntryId));

    for (const key of ['plate_image_s3_url', 'image_s3_url'] as const) {
      if (this.resolveTopLevelNotificationImageS3Url(matchingEntry, key)) {
        continue;
      }
      // The live data is the newest view of this entry, including a deliberate clear, so it
      // never falls back to the URL the modal opened with.
      const known = this.resolveTopLevelNotificationImageS3Url(
        liveIsSameEntry ? liveData : openedData,
        key
      );
      if (known) {
        mergedData[key] = known;
      }
    }
  }

  private finishLprEntryHydration(): void {
    this.orderRequestLprAxisPlateImageApiHydrationPending = false;
    this.lprEntriesLocationFetchInFlight = false;
    this.syncOrderRequestLprImagePreviewFromNotificationData();
    this.refreshOrderRequestLprAxisPlateImagePreload();
  }

  /**
   * Prefer the notification's own location for LPR hydration (one request).
   * Fall back to all user locations only when the payload has no location id.
   */
  private resolveLprEntriesLocationIds(notification: NotificationData): Observable<string[]> {
    const d = notification.data as Record<string, any> | undefined;
    const fromNotification = String(d?.['location_id'] ?? d?.['locationId'] ?? '').trim();
    if (fromNotification) {
      return of([fromNotification]);
    }
    return this.getLocationIds();
  }

  /**
   * Get location IDs based on user role
   */
  private getLocationIds(): any {
    const userRole = this.roleService.getCurrentUserRole();

    if (userRole === UserRole.ATTENDANT || userRole === UserRole.ATTENDANT_PLUS) {
      // For staff: get location from employee profile
      return this.employeeService.getCurrentEmployeeProfile().pipe(
        map((response: any) => {
          if (response.status && response.data?.location_id) {
            return [response.data.location_id];
          }
          return [];
        }),
        catchError(error => {
          console.error('Error fetching employee profile:', error);
          return of([]);
        })
      );
    } else {
      // For admin: get all locations
      return this.locationService.getLocations().pipe(
        map((response: any) => {
          if (response.status && response.data && Array.isArray(response.data)) {
            return response.data.map((location: any) => location.id).filter((id: string) => id);
          }
          return [];
        }),
        catchError(error => {
          console.error('Error fetching locations:', error);
          return of([]);
        })
      );
    }
  }

  /**
   * Pusher payloads and stored notification list items may carry renewal copy on
   * `notification.message` or nested `data.message`.
   */
  private resolveMembershipRenewalGreeting(): string | null {
    const candidates = [
      this.notification?.message,
      this.notification?.data?.message,
      this.notification?.data?.data?.message,
    ].filter((m): m is string => typeof m === 'string' && m.trim().length > 0);

    for (const raw of candidates) {
      const normalized = raw.trim();
      // CWS-1266: a vendor member's copy already tells the attendant what to do
      // ("…Check with DRB…"), so it is shown as-is rather than swapped for the
      // Way renewal greeting, which would send them to collect payment details.
      if (VENDOR_EXPIRED_MESSAGE_MARKER.test(normalized)) {
        return normalized;
      }
      if (
        normalized === NotificationModalComponent.SUBSCRIPTION_EXPIRED_BACKEND_MESSAGE ||
        /subscription\s+expired/i.test(normalized)
      ) {
        return NotificationModalComponent.MEMBERSHIP_EXPIRED_GREETING;
      }
      if (/subscription\s+expiring/i.test(normalized)) {
        return NotificationModalComponent.MEMBERSHIP_EXPIRING_GREETING;
      }
    }
    return null;
  }

  /** CWS-1371: wash-cap copy uses the same header slot as expired memberships. */
  private membershipAwareSuggestion(fallback: string): string {
    return this.isMembershipLimitReached() ? this.getMembershipLimitReachedMessage() : fallback;
  }

  private applyMembershipLimitSuggestionIfNeeded(): boolean {
    if (!this.isMembershipLimitReached()) {
      return false;
    }
    this.subscriptionSuggestionMessage = this.getMembershipLimitReachedMessage();
    return true;
  }

  /**
   * CWS-1729: a DRB member the gate lets wash; the lane only declined to auto-redeem.
   * A used-up allowance is not one: its wash-limit sentence stays in the banner.
   */
  get isRedeemableVendorMember(): boolean {
    return (
      isRedeemableRefusalDetail(refusalDetailOf(this.notification)) &&
      !this.isMembershipLimitReached()
    );
  }

  /**
   * CWS-1729: a redeemable member's "Check with DRB" is how the attendant redeems at a shop
   * with auto-redeem off, so it sits in the Redeem Existing Membership row; the banner
   * keeps it only when the shop has no such row.
   */
  get vendorLookupInVerifyRow(): boolean {
    return this.isRedeemableVendorMember && this.showOrderRequestMembershipVerifySection();
  }

  /**
   * The banner's sentence. CWS-1650: the status strip replaces it. CWS-1729: a redeemable
   * member keeps the same-day warning and never gets an upsell. CWS-1792: that warning
   * stays even behind the member's active strip, which says nothing about today's visit.
   * CWS-1828: an inactive strip keeps the welcome tip, so the banner beside "Check with DRB"
   * is never empty; its same-day warning still wins, and every other strip keeps the banner
   * sentence-free.
   */
  get subscriptionSuggestionSentence(): string {
    const message = this.subscriptionSuggestionMessage;
    const strip = this.membershipStatusStrip;
    if (this.isRedeemableVendorMember) {
      return isTodayVisitSuggestion(message) ? message : '';
    }
    if (!strip) {
      return message;
    }
    if (strip.tone !== 'inactive') {
      return '';
    }
    return isTodayVisitSuggestion(message) ? message : this.inactiveMembershipSuggestion();
  }

  /**
   * CWS-1828: the welcome tip, plus the ask to check with the vendor while its lookup button
   * is on the card and has not answered yet; once it has, the answer speaks for itself.
   */
  private inactiveMembershipSuggestion(): string {
    const offer = this.vendorMembershipLookupOffer;
    if (!offer || !this.showVendorMembershipLookup || this.vendorMembershipLookupResult) {
      return EMPTY_HISTORY_WELCOME;
    }
    return `${EMPTY_HISTORY_WELCOME} ${vendorPaidRecentlyHint(offer.label)}`;
  }

  /** The yellow banner above the order form (order-request flows only, not payment checkout). */
  get showSubscriptionSuggestionBanner(): boolean {
    if (!this.showOrderRequestCheckoutLayout()) {
      return false;
    }
    const lookupInBanner =
      !this.vendorLookupInVerifyRow &&
      (!!this.vendorMembershipLookupOffer || !!this.vendorMembershipLookupResult);
    return (
      (!!this.subscriptionSuggestionSentence || lookupInBanner) &&
      (!this.isLoadingOrderRequestHistoryPage || this.isMembershipLimitReached())
    );
  }

  /** CWS-1266: decide whether this card carries the live vendor lookup button. */
  private syncVendorMembershipLookupOffer(): void {
    const offer = resolveVendorMembershipLookupOffer(this.notification, source =>
      this.vendorModeService.isVendorSourceEnabled(source)
    );
    const redeemable = this.isRedeemableVendorMember;
    const changed =
      offer?.vendor !== this.vendorMembershipLookupOffer?.vendor ||
      offer?.kind !== this.vendorMembershipLookupOffer?.kind;
    this.vendorMembershipLookupOffer = offer;
    // Recomputed on every card, so an expired member's card never leaves the
    // section hidden on the next card this modal shows. CWS-1729: a redeemable
    // member's button lives in that section, so it is never replaced.
    this.vendorLookupReplacesVerifySection = offer?.kind === 'refresh' && !redeemable;
    if (changed) {
      this.vendorMembershipLookupResult = null;
    }
    // A known vendor member with no membership row has no greeting of its own; give the
    // banner a sentence so the button sits with the text that asks for it. CWS-1729: not
    // for a member DRB lists as redeemable, who does have a membership.
    const offerMessage = redeemable ? null : vendorLookupOfferMessage(offer);
    if (offerMessage && !this.subscriptionSuggestionMessage) {
      this.subscriptionSuggestionMessage = offerMessage;
    }
  }

  /**
   * CWS-1266: the button is moot once this card carries an order. The order
   * service writes `order_id` onto the very notification the modal shows, so
   * this follows the sale without waiting for another offer sync.
   */
  get showVendorMembershipLookup(): boolean {
    return !!this.vendorMembershipLookupOffer && !this.hasCheckoutOrderId();
  }

  get vendorMembershipLookupButtonLabel(): string {
    return this.vendorMembershipLookupOffer
      ? vendorLookupButtonLabel(
          this.vendorMembershipLookupOffer,
          this.isVendorMembershipLookupRunning
        )
      : '';
  }

  private clearVendorMembershipLookupState(): void {
    this.vendorMembershipLookupOffer = null;
    this.isVendorMembershipLookupRunning = false;
    this.vendorMembershipLookupResult = null;
    this.vendorLookupReplacesVerifySection = false;
  }

  /**
   * CWS-1266: ask the vendor about this refused entry. One request at a time; the
   * backend answers within its 10 s vendor timeout and the button shows progress.
   */
  onLookupVendorMembership(): void {
    const offer = this.vendorMembershipLookupOffer;
    const lprEntryId = resolveLprEntryIdFromNotificationData(this.notification?.data);
    const request = buildVendorLookupRequest(this.notification?.data, this.formData?.locationId);
    if (
      !offer ||
      !this.showVendorMembershipLookup ||
      !lprEntryId ||
      !request ||
      this.isVendorMembershipLookupRunning
    ) {
      return;
    }

    this.isVendorMembershipLookupRunning = true;
    this.vendorMembershipLookupResult = null;
    this.logModalInteraction('orderRequest.vendorLookup.pressed', {
      vendor: offer.vendor,
      kind: offer.kind,
      lpr_entry_id: lprEntryId,
    });

    this.apiService
      .lookupVendorMembershipForLprEntry(lprEntryId, request)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isVendorMembershipLookupRunning = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => this.applyVendorMembershipLookupResponse(response?.data, offer),
        error: error => {
          // A failed request is a miss like any other: the lookup only helps, the
          // order form beside it is unaffected either way.
          this.vendorMembershipLookupResult = {
            severity: 'warn',
            text: vendorLookupNotFoundText(offer.label),
          };
          this.logModalInteraction('orderRequest.vendorLookup.failed', { vendor: offer.vendor });
        },
      });
  }

  private applyVendorMembershipLookupResponse(
    data: VendorMembershipLookupData | undefined,
    offer: VendorMembershipLookupOffer
  ): void {
    this.vendorMembershipLookupResult = mapVendorLookupOutcomeToText(data, offer.label);
    this.logModalInteraction('orderRequest.vendorLookup.completed', {
      vendor: offer.vendor,
      outcome: data?.outcome ?? null,
      condition: data?.condition ?? null,
      reason: data?.reason ?? null,
      gate_opened: data?.gate_opened ?? null,
    });

    // The row was updated server-side (redeemed, or the vendor's answer written
    // on it): re-label it everywhere through the same in-place sync a PUT uses.
    const putShaped = toPutShapedLprEntryResponse(data?.lpr_entry ?? null);
    if (putShaped) {
      this.notificationService.syncLprEntryFromPutResponse(putShaped);
    }

    if (data?.outcome === 'redeemed') {
      // Not closing here: the backend emitted the same `active-subscription`
      // event the camera path emits, which opens the vendor active-subscription
      // card, and it may already have arrived.
      this.messageService.showSuccess(
        'Membership confirmed',
        this.vendorMembershipLookupResult.text,
        5000
      );
      this.ordersService.refreshOrders();
    }

    if (!isVendorLookupRetryable(data)) {
      this.vendorMembershipLookupOffer = null;
    }
  }

  private checkCustomerOrdersForSubscription(options?: { force?: boolean }): void {
    // CWS-1266: runs for every refusal card, linked customer or not.
    this.syncVendorMembershipLookupOffer();

    if (this.applyMembershipLimitSuggestionIfNeeded()) {
      this.loadWashHistoryInsightForCurrentContext(options);
      return;
    }

    const membershipRenewalGreeting = this.resolveMembershipRenewalGreeting();
    if (membershipRenewalGreeting) {
      this.subscriptionSuggestionMessage = membershipRenewalGreeting;
      return;
    }

    // Unified order UI: welcome until a customer is linked (same copy for + / LPR / image flows).
    if (this.isOrderRequestStyleNotification() && !this.formData.customerId) {
      this.subscriptionSuggestionMessage =
        'Welcome! Quick tip — if you plan to wash twice a month or more, the unlimited plan usually costs less. Want to start with that today?';
      return;
    }

    // With customer linked, require vehicleId for per-vehicle history / today banner
    if (!this.formData.customerId) {
      return;
    }

    // Also check if vehicleId is available, otherwise skip filtering by vehicle
    if (!this.formData.vehicleId) {
      return;
    }

    this.loadWashHistoryInsightForCurrentContext(options);
  }

  /**
   * Load / restore vehicle wash-history insight for the current customer+vehicle.
   * Used by sales panel and vendor Active subscription (no upsell greeting side effects).
   */
  private loadWashHistoryInsightForCurrentContext(options?: { force?: boolean }): void {
    const contextKey = this.getCurrentOrderRequestHistoryContextKey();
    if (!contextKey) {
      return;
    }

    if (options?.force) {
      const vehicleId = this.formData.vehicleId.trim();
      this.orderRequestHistoryService.invalidateForVehicle(vehicleId);
      this.orderRequestTodayBanner = null;
      this.orderRequestRewashEligibility = null;
      this.orderRequestMembershipRewashEligibility = null;
      this.orderRequestPrepaidRewashEligibility = null;
      this.orderRequestRewashTarget = null;
      this.orderRequestHistoryContextKey = null;
      this.orderRequestHistoryBuildInFlightKey = null;
    }

    if (this.orderRequestHistoryBuildInFlightKey === contextKey && !options?.force) {
      return;
    }
    if (
      !options?.force &&
      this.orderRequestHistoryContextKey === contextKey &&
      !!this.orderRequestTodayBanner &&
      !this.isLoadingOrderRequestHistoryPage
    ) {
      return;
    }

    if (!options?.force && this.restoreOrderRequestHistoryInsightFromCache(contextKey)) {
      this.orderRequestHistoryContextKey = contextKey;
      return;
    }

    if (this.orderRequestHistoryContextKey !== contextKey) {
      this.orderRequestTodayBanner = null;
      this.orderRequestRewashEligibility = null;
      this.orderRequestMembershipRewashEligibility = null;
      this.orderRequestPrepaidRewashEligibility = null;
      this.orderRequestRewashTarget = null;
      this.subscriptionSuggestionMessage = this.membershipAwareSuggestion('');
      this.orderRequestHistoryExpanded = false;
    }

    this.orderRequestHistoryBuildInFlightKey = contextKey;

    this.fetchVehicleOrderHistoryPage(1, { buildInsight: true, contextKey });
  }

  goToOrderRequestHistoryPage(page: number): void {
    if (!this.formData.vehicleId || !this.orderRequestTodayBanner) {
      return;
    }
    const hp = this.orderRequestTodayBanner.historyPagination;
    const target = Math.max(1, Math.min(page, hp?.totalPages ?? page));
    if (hp && target === hp.page) {
      return;
    }
    this.fetchVehicleOrderHistoryPage(target, { buildInsight: false });
  }

  orderRequestHistoryPreviousPage(): void {
    const hp = this.orderRequestTodayBanner?.historyPagination;
    if (!hp || hp.page <= 1) {
      return;
    }
    this.goToOrderRequestHistoryPage(hp.page - 1);
  }

  orderRequestHistoryNextPage(): void {
    const hp = this.orderRequestTodayBanner?.historyPagination;
    if (!hp || hp.page >= hp.totalPages) {
      return;
    }
    this.goToOrderRequestHistoryPage(hp.page + 1);
  }

  orderRequestHistoryFirstPage(): void {
    this.goToOrderRequestHistoryPage(1);
  }

  orderRequestHistoryLastPage(): void {
    const hp = this.orderRequestTodayBanner?.historyPagination;
    if (!hp) {
      return;
    }
    this.goToOrderRequestHistoryPage(hp.totalPages);
  }

  orderRequestHistoryPageSizeChange(limit: number): void {
    const next = Math.floor(Number(limit));
    if (!Number.isFinite(next) || next < 1) {
      return;
    }
    if (next === this.orderRequestHistoryService.pageLimit) {
      return;
    }
    this.orderRequestHistoryService.setPageLimit(next);
    if (this.formData.vehicleId) {
      this.orderRequestHistoryService.invalidateForVehicle(this.formData.vehicleId.trim());
    }
    this.fetchVehicleOrderHistoryPage(1, { buildInsight: false });
  }

  private fetchVehicleOrderHistoryPage(
    page: number,
    options: { buildInsight: boolean; contextKey?: string }
  ): void {
    const vehicleId = this.formData.vehicleId?.trim();
    if (!vehicleId) {
      return;
    }

    const historyFilters = this.resolveVendorActiveSubscriptionHistoryFilters();

    const skipReason = this.orderRequestHistoryService.peekFetchSkip(
      vehicleId,
      page,
      options.buildInsight,
      historyFilters
    );
    if (skipReason) {
      if (skipReason === 'cooldown' && options.buildInsight) {
        this.isLoadingCustomerOrders = false;
        this.isLoadingOrderRequestHistoryPage = false;
        const cacheKey = options.contextKey ?? this.getCurrentOrderRequestHistoryContextKey();
        if (cacheKey) {
          this.restoreOrderRequestHistoryInsightFromCache(cacheKey);
        }
        this.refreshOrderRequestAsyncUi();
      } else if (skipReason === 'in-flight') {
        // Keep existing loading flags from the in-flight request; do not leave a
        // second caller stuck if somehow loading was never set.
        this.refreshOrderRequestAsyncUi();
      }
      return;
    }

    this.isLoadingOrderRequestHistoryPage = true;
    if (options.buildInsight) {
      this.isLoadingCustomerOrders = true;
    }

    this.orderRequestHistoryService
      .fetchVehicleOrderHistoryPage(vehicleId, page, options.buildInsight, historyFilters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          if (result.skipped) {
            this.isLoadingCustomerOrders = false;
            this.isLoadingOrderRequestHistoryPage = false;
            if (
              options.buildInsight &&
              options.contextKey &&
              this.orderRequestHistoryBuildInFlightKey === options.contextKey
            ) {
              this.orderRequestHistoryBuildInFlightKey = null;
            }
            if (options.buildInsight && result.skipReason === 'cooldown') {
              const cacheKey = options.contextKey ?? this.getCurrentOrderRequestHistoryContextKey();
              if (cacheKey) {
                this.restoreOrderRequestHistoryInsightFromCache(cacheKey);
              }
            }
            this.refreshOrderRequestAsyncUi();
            return;
          }

          this.isLoadingCustomerOrders = false;
          this.isLoadingOrderRequestHistoryPage = false;
          if (
            options.buildInsight &&
            options.contextKey &&
            this.orderRequestHistoryBuildInFlightKey === options.contextKey
          ) {
            this.orderRequestHistoryBuildInFlightKey = null;
          }

          const response = result.response;
          if (!response?.status) {
            this.refreshOrderRequestAsyncUi();
            return;
          }

          if (options.buildInsight && options.contextKey) {
            const currentContextKey = this.getCurrentOrderRequestHistoryContextKey();
            if (currentContextKey && currentContextKey !== options.contextKey) {
              this.refreshOrderRequestAsyncUi();
              return;
            }
          }

          // Backend already filters redemptions + exclude when known; FE omit covers
          // first-paint races before subscription_wash_id was available for the query.
          const filteredResponse = this.applyVendorActiveSubscriptionClientOmitFallback(response);
          const rows = Array.isArray(filteredResponse.data) ? filteredResponse.data : [];
          if (!options.buildInsight) {
            this.patchOrderRequestHistoryPage(rows, filteredResponse, page);
            this.refreshOrderRequestAsyncUi();
            return;
          }
          if (options.contextKey) {
            this.orderRequestHistoryContextKey = options.contextKey;
          }

          const insight = buildOrderRequestHistoryInsight(
            filteredResponse,
            page,
            this.orderRequestHistoryService.pageLimit
          );
          this.subscriptionSuggestionMessage = this.membershipAwareSuggestion(insight.suggestion);
          this.orderRequestTodayBanner = insight.banner;
          this.orderRequestRewashEligibility =
            parseRewashEligibilityFromHistoryResponse(filteredResponse);
          this.orderRequestMembershipRewashEligibility =
            parseMembershipRewashEligibilityFromHistoryResponse(filteredResponse);
          this.orderRequestPrepaidRewashEligibility =
            parsePrepaidRewashEligibilityFromHistoryResponse(filteredResponse);
          if (options.contextKey) {
            this.saveOrderRequestHistoryInsightCache(
              options.contextKey,
              insight.banner,
              insight.suggestion,
              this.orderRequestRewashEligibility,
              this.orderRequestMembershipRewashEligibility,
              this.orderRequestPrepaidRewashEligibility
            );
          }
          this.refreshOrderRequestAsyncUi();
        },
        error: (error: any) => {
          this.isLoadingCustomerOrders = false;
          this.isLoadingOrderRequestHistoryPage = false;
          if (
            options.buildInsight &&
            options.contextKey &&
            this.orderRequestHistoryBuildInFlightKey === options.contextKey
          ) {
            this.orderRequestHistoryBuildInFlightKey = null;
          }
          console.error('Error fetching vehicle order history:', error);
          this.refreshOrderRequestAsyncUi();
        },
      });
  }

  private getCurrentOrderRequestHistoryContextKey(): string | null {
    return this.orderRequestHistoryService.buildContextKey(
      this.formData.customerId,
      this.formData.vehicleId,
      this.resolveVendorActiveSubscriptionHistoryFilters()
    );
  }

  /**
   * Re-apply cached insight after populateFormData without wiping banner/greeting for the same customer+vehicle.
   */
  private syncOrderRequestHistoryUiForCurrentContext(): void {
    const contextKey = this.getCurrentOrderRequestHistoryContextKey();
    if (!contextKey) {
      return;
    }
    if (this.orderRequestHistoryContextKey === contextKey) {
      this.restoreOrderRequestHistoryInsightFromCache(contextKey);
      return;
    }
    if (this.orderRequestHistoryContextKey && this.orderRequestHistoryContextKey !== contextKey) {
      this.orderRequestTodayBanner = null;
      this.orderRequestRewashEligibility = null;
      this.orderRequestMembershipRewashEligibility = null;
      this.orderRequestPrepaidRewashEligibility = null;
      this.orderRequestRewashTarget = null;
      this.subscriptionSuggestionMessage = this.membershipAwareSuggestion('');
      this.orderRequestHistoryExpanded = false;
    }
  }

  private clearOrderRequestHistoryState(): void {
    this.orderRequestTodayBanner = null;
    this.orderRequestRewashEligibility = null;
    this.orderRequestMembershipRewashEligibility = null;
    this.orderRequestPrepaidRewashEligibility = null;
    this.orderRequestRewashTarget = null;
    this.subscriptionSuggestionMessage = '';
    this.clearVendorMembershipLookupState();
    this.orderRequestHistoryExpanded = false;
    this.orderRequestHistoryContextKey = null;
    this.orderRequestHistoryBuildInFlightKey = null;
    this.clearVendorActiveSubscriptionOmitWashId();
    this.orderRequestHistoryService.clearAll();
    this.isLoadingOrderRequestHistoryPage = false;
    this.isLoadingCustomerOrders = false;
  }

  /** Clears stale order-history UI when the plate lookup context is changing. */
  private clearOrderRequestHistoryForPlateChange(): void {
    if (!this.isOrderRequestStyleNotification()) {
      return;
    }
    this.orderRequestTodayBanner = null;
    this.orderRequestRewashEligibility = null;
    this.orderRequestMembershipRewashEligibility = null;
    this.orderRequestPrepaidRewashEligibility = null;
    this.orderRequestRewashTarget = null;
    this.orderRequestHistoryContextKey = null;
    this.orderRequestHistoryBuildInFlightKey = null;
    this.isLoadingOrderRequestHistoryPage = false;
    this.isLoadingCustomerOrders = false;
  }

  /** Refetch vehicle order history (insight panel) once customer + vehicle are known. */
  private refreshOrderRequestHistoryAfterVehicleResolved(force = false): void {
    if (!this.isOrderRequestStyleNotification()) {
      return;
    }
    if (!(this.formData.customerId || '').trim() || !(this.formData.vehicleId || '').trim()) {
      return;
    }
    this.checkCustomerOrdersForSubscription(force ? { force: true } : undefined);
  }

  private saveOrderRequestHistoryInsightCache(
    contextKey: string,
    banner: OrderRequestTodayBannerState | null,
    suggestion: string,
    rewashEligibility: RewashEligibilityState | null = null,
    membershipRewashEligibility: MembershipRewashEligibilityState | null = null,
    prepaidRewashEligibility: PrepaidRewashEligibilityState | null = null
  ): void {
    this.orderRequestHistoryService.saveInsightCache(
      contextKey,
      banner,
      suggestion,
      rewashEligibility,
      membershipRewashEligibility,
      prepaidRewashEligibility
    );
  }

  private restoreOrderRequestHistoryInsightFromCache(contextKey: string): boolean {
    const cached = this.orderRequestHistoryService.restoreInsightCache(contextKey);
    if (!cached) {
      return false;
    }
    this.orderRequestTodayBanner = cached.banner;
    this.orderRequestRewashEligibility = cached.rewashEligibility;
    this.orderRequestMembershipRewashEligibility = cached.membershipRewashEligibility;
    this.orderRequestPrepaidRewashEligibility = cached.prepaidRewashEligibility;
    this.subscriptionSuggestionMessage = this.membershipAwareSuggestion(cached.suggestion);
    this.isLoadingOrderRequestHistoryPage = false;
    this.isLoadingCustomerOrders = false;
    return true;
  }

  private patchOrderRequestHistoryPage(rows: any[], response: any, page: number): void {
    if (!this.orderRequestTodayBanner) {
      return;
    }
    this.orderRequestTodayBanner = patchOrderRequestHistoryBannerPage(
      this.orderRequestTodayBanner,
      rows,
      response,
      page,
      this.orderRequestHistoryService.pageLimit
    );
  }

  toggleOrderRequestHistory(): void {
    this.orderRequestHistoryExpanded = !this.orderRequestHistoryExpanded;
  }

  openOrderRequestRewashReasonModal(target?: OrderRequestRewashTarget | null): void {
    const normalized = normalizeRewashTarget(target);
    if (!normalized) {
      return;
    }
    this.orderRequestRewashTarget = normalized;
    this.orderRequestRewashReasonModalOpen = true;
  }

  closeOrderRequestRewashReasonModal(): void {
    this.orderRequestRewashReasonModalOpen = false;
    this.orderRequestRewashTarget = null;
  }

  onOrderRequestRewashConfirmed(result: { created: boolean }): void {
    this.closeOrderRequestRewashReasonModal();
    this.orderRequestRewashEligibility = null;
    this.orderRequestMembershipRewashEligibility = null;
    this.orderRequestPrepaidRewashEligibility = null;
    this.checkCustomerOrdersForSubscription({ force: true });
    this.ordersService.refreshOrders();
    this.messageService.add({
      severity: 'success',
      summary: result.created ? 'Rewash created' : 'Rewash already exists',
      detail: result.created
        ? 'The rewash order was created successfully.'
        : 'An active rewash order already exists for this wash.',
    });
    this.onClose();
  }

  private resetOrderRequestAddonUi(): void {
    this.orderRequestAddonExpandedId = null;
    this.orderRequestAddonCustomPriceDraft = {};
    this.orderRequestAddonSavedCustomPrice = {};
    this.orderRequestSingleWashCustomExpandedUniqueId = null;
    this.orderRequestSingleWashCustomPriceDraft = {};
    this.orderRequestSingleWashSavedCustomPrice = {};
    this.orderRequestSelectedAddonIds = new Set<string>();
    this.orderRequestVendingDialogOpen = false;
    this.orderRequestVendingDialogAddonId = null;
    this.orderRequestAddonVariantsDialog = null;
    this.orderRequestAddonVariantsDialogQty = {};
    this.orderRequestAddonVariantsDialogChecked = {};
    this.orderRequestAddonVariantDialogCustomExpandedId = null;
    this.orderRequestAddonVariantQuantities = {};
    this.orderRequestVendingItems = [];
    this.orderRequestVendingDialogChecked = {};
    this.orderRequestVendingCustomExpandedItemId = null;
    this.orderRequestVendingSelections = {};
    /** Addon/fleet catalog arrays are restored via `syncOrderRequestCatalogForCurrentLocation` after populate. */
  }

  getSelectedSingleWashCustomPriceExpandedPackage(): ExpandedPackage | null {
    if (!this.selectedPackage || !this.selectedWashType) {
      return null;
    }
    if (this.normalizeWashType(this.selectedWashType) !== 'single_wash') {
      return null;
    }
    return (
      this.expandedPackages.find(
        e =>
          e.package.id === this.selectedPackage!.id &&
          e.washType === this.selectedWashType &&
          e.allowsCustomPrice === true
      ) ?? null
    );
  }

  isOrderRequestSingleWashCustomExpanded(uniqueId: string): boolean {
    return this.orderRequestSingleWashCustomExpandedUniqueId === uniqueId;
  }

  formatOrderRequestSingleWashSavedPrice(uniqueId: string): string | null {
    const v = this.orderRequestSingleWashSavedCustomPrice[uniqueId];
    return v && v.trim() ? v : null;
  }

  getOrderRequestSingleWashCustomPriceDraft(uniqueId: string): string {
    return this.orderRequestSingleWashCustomPriceDraft[uniqueId] ?? '';
  }

  formatExpandedSingleWashListPriceDisplay(ep: ExpandedPackage): string {
    if (!ep.allowsCustomPrice) {
      return this.formatUsdPriceForOrderRequestDisplay(ep.price) || '';
    }
    const saved = this.formatOrderRequestSingleWashSavedPrice(ep.uniqueId);
    return saved ? this.formatUsdPriceForOrderRequestDisplay(saved) || saved : 'Custom price';
  }

  private formatExpandedPackageMostUsedPriceLabel(ep: ExpandedPackage): string {
    const normalized = this.normalizeWashType(ep.washType);
    if (ep.allowsCustomPrice && normalized === 'single_wash') {
      const saved = this.formatOrderRequestSingleWashSavedPrice(ep.uniqueId);
      if (saved) {
        return this.formatUsdPriceForOrderRequestDisplay(saved) || saved;
      }
      return 'Custom price';
    }
    const formatted = this.formatUsdPriceForOrderRequestDisplay(ep.price);
    return normalized === 'monthly_membership' ? formatted || 'Monthly' : formatted || 'Single';
  }

  onOrderRequestSingleWashCustomPriceInput(uniqueId: string, value: string): void {
    const cleaned = value.replace(/[^\d.]/g, '');
    const firstDot = cleaned.indexOf('.');
    let normalized = cleaned;
    if (firstDot !== -1) {
      const rest = cleaned.slice(firstDot + 1).replace(/\./g, '');
      normalized = cleaned.slice(0, firstDot + 1) + rest;
    }
    this.orderRequestSingleWashCustomPriceDraft[uniqueId] = normalized;
  }

  private openOrderRequestSingleWashCustomPriceEditor(ep: ExpandedPackage): void {
    this.orderRequestSingleWashCustomExpandedUniqueId = ep.uniqueId;
    const saved = this.orderRequestSingleWashSavedCustomPrice[ep.uniqueId];
    this.orderRequestSingleWashCustomPriceDraft[ep.uniqueId] = saved
      ? this.stripUsdForAddonInput(saved)
      : '';
  }

  onSingleWashPackageCustomPricePillClick(ep: ExpandedPackage, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.shouldDisableField('package')) {
      return;
    }
    if (!this.isExpandedPackageSelected(ep)) {
      // Selecting an unpriced custom tile opens its editor; the pill must not then toggle it shut.
      this.onExpandedPackageSelect(ep);
      return;
    }
    if (this.orderRequestSingleWashCustomExpandedUniqueId === ep.uniqueId) {
      this.orderRequestSingleWashCustomExpandedUniqueId = null;
      return;
    }
    this.openOrderRequestSingleWashCustomPriceEditor(ep);
  }

  onOrderRequestSingleWashEditCustomPrice(ep: ExpandedPackage, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.shouldDisableField('package')) {
      return;
    }
    if (!this.isExpandedPackageSelected(ep)) {
      this.onExpandedPackageSelect(ep);
    }
    this.openOrderRequestSingleWashCustomPriceEditor(ep);
  }

  saveOrderRequestSingleWashCustomPrice(ep: ExpandedPackage): void {
    if (!ep.allowsCustomPrice) {
      return;
    }
    if (this.shouldDisableField('package')) {
      return;
    }
    const raw = (this.orderRequestSingleWashCustomPriceDraft[ep.uniqueId] || '').trim();
    if (!raw) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Amount required',
        detail: 'Enter a valid amount.',
      });
      return;
    }
    const num = parseFloat(raw);
    if (!Number.isFinite(num) || num <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid amount',
        detail: 'Enter an amount greater than zero.',
      });
      return;
    }
    this.orderRequestSingleWashSavedCustomPrice[ep.uniqueId] = `$${num.toFixed(2)}`;
    this.formData.price = num.toFixed(2);
    this.orderRequestSingleWashCustomExpandedUniqueId = null;
    this.rebuildMostUsedServiceItems();
    this.clearOrderRequestDiscountSelectionIfHidden();
    this.clearStaleOrderRequestValidationDisplay();
  }

  removeOrderRequestSingleWashCustomPrice(ep: ExpandedPackage, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    delete this.orderRequestSingleWashSavedCustomPrice[ep.uniqueId];
    delete this.orderRequestSingleWashCustomPriceDraft[ep.uniqueId];
    if (this.orderRequestSingleWashCustomExpandedUniqueId === ep.uniqueId) {
      this.orderRequestSingleWashCustomExpandedUniqueId = null;
    }
    if (this.isExpandedPackageSelected(ep)) {
      this.formData.price = '';
    }
    this.rebuildMostUsedServiceItems();
    this.clearOrderRequestDiscountSelectionIfHidden();
  }

  private getSingleWashCustomPriceSubmissionError(): string | null {
    if (!this.showOrderRequestCheckoutLayout()) {
      return null;
    }
    if (this.isFleetVehicleBillingOnlyAddons()) {
      return null;
    }
    if (this.orderRequestSelectedPrepaidBookId) {
      return null;
    }
    const ep = this.getSelectedSingleWashCustomPriceExpandedPackage();
    if (!ep) {
      return null;
    }
    const saved = this.orderRequestSingleWashSavedCustomPrice[ep.uniqueId];
    const n = this.parseUsdNumber(saved);
    if (n == null || !Number.isFinite(n) || n <= 0) {
      return 'Enter a valid price for the selected single wash package.';
    }
    return null;
  }

  getOrderRequestVendingCommittedQuantityFor(addonId: string): number {
    return (
      this.orderRequestVendingSelections[addonId]?.lines?.reduce(
        (sum, line) => sum + line.quantity,
        0
      ) ?? 0
    );
  }

  private getCheckoutAllVendingCommittedQuantity(): number {
    let n = 0;
    for (const addon of this.orderRequestAddons) {
      if (addon.mode === 'choose_products') {
        n += this.getOrderRequestVendingCommittedQuantityFor(addon.id);
      }
    }
    return n;
  }

  private getCheckoutVendingCombinedAmount(): number {
    let sum = 0;
    for (const addon of this.orderRequestAddons) {
      if (addon.mode !== 'choose_products') {
        continue;
      }
      const sel = this.orderRequestVendingSelections[addon.id];
      sum += this.parseUsdNumber(sel?.totalFormatted) ?? 0;
    }
    return sum;
  }

  getOrderRequestVendingSummaryLabelFor(addonId: string): string {
    const lines = this.orderRequestVendingSelections[addonId]?.lines ?? [];
    if (!lines.length) {
      const addon = this.orderRequestAddons.find(a => a.id === addonId);
      return addon?.label?.trim() || 'Products';
    }
    return lines.map(l => `${l.name} × ${l.quantity}`).join(' · ');
  }

  getCheckoutCombinedVendingSummaryLabel(): string {
    const parts: string[] = [];
    for (const a of this.orderRequestAddons) {
      if (a.mode !== 'choose_products') {
        continue;
      }
      if (this.getOrderRequestVendingCommittedQuantityFor(a.id) === 0) {
        continue;
      }
      parts.push(this.getOrderRequestVendingSummaryLabelFor(a.id));
    }
    return parts.length ? parts.join(' · ') : 'Products';
  }

  getOrderRequestVendingSelectionLinesFor(addonId: string): OrderRequestVendingSelectionLine[] {
    return this.orderRequestVendingSelections[addonId]?.lines ?? [];
  }

  getOrderRequestVendingDialogTitle(): string {
    const id = this.orderRequestVendingDialogAddonId;
    if (!id) {
      return '';
    }
    const a = this.orderRequestAddons.find(x => x.id === id);
    return a?.label?.trim() || '';
  }

  /** Modal title line, e.g. "Select Vending" (Figma: Select vending products). */
  getOrderRequestVendingModalHeading(): string {
    const label = this.getOrderRequestVendingDialogTitle().trim();
    return label ? `Select ${label}` : 'Select products';
  }

  trackOrderRequestVendingItemById(_index: number, item: OrderRequestVendingItem): string {
    return item.id;
  }

  trackOrderRequestVendingLineById(_index: number, line: OrderRequestVendingSelectionLine): string {
    return line.id;
  }

  private resetOrderRequestDiscountUi(): void {
    this.orderRequestSelectedDiscountId = null;
    this.orderRequestCustomDiscountExpanded = false;
    this.orderRequestCustomDiscountName = '';
    this.orderRequestCustomDiscountValueDraft = '';
    this.orderRequestCustomDiscountType = 'fixed_amount';
    this.orderRequestCustomDiscountReason = '';
    this.orderRequestCustomDiscountReasonError = null;
    this.orderRequestCustomDiscountSaved = null;
  }

  showOrderRequestPrepaidSection(): boolean {
    const isInLayout =
      this.showOrderRequestCheckoutLayout() || this.notification?.type === 'fleet-vehicle-found';
    // An empty catalog (or one still loading) has nothing to sell, so no heading either.
    if (!isInLayout || this.orderRequestPrepaidBooks.length === 0) {
      return false;
    }
    // Fully redeemed / expired: allow buying a new wash book from the catalog.
    if (this.isPrepaidPurchaseConsumedForUi()) {
      return true;
    }
    // A purchase in play means the redemption panel above is showing that book. Selling a
    // second wash book on top of it is not a thing we offer, and listing the catalog here put
    // the same book on screen twice. The other wash types stay available (CWS-1144).
    if (this.isOrderRequestPrepaidRedeemContext()) {
      return false;
    }
    return true;
  }

  showOrderRequestPrepaidRedemptionSection(): boolean {
    // Stays on screen whatever else is picked. Picking a wash package deselects the book
    // rather than hiding it -- a section that vanishes leaves the attendant wondering where
    // the customer's wash book went and no way back to it.
    return (
      (this.showOrderRequestCheckoutLayout() ||
        this.notification?.type === 'fleet-vehicle-found') &&
      this.isOrderRequestPrepaidRedeemContext()
    );
  }

  /**
   * True when an existing prepaid purchase is in play (LPR, plate lookup, QR) -- it decides
   * whether the redemption panel appears and which wash books the catalog fetch keeps.
   *
   * It is deliberately *not* what hides the rest of the catalog; see
   * `isOrderRequestPrepaidRedeemEntryPoint()`.
   */
  isOrderRequestPrepaidRedeemContext(): boolean {
    if (this.isOrderRequestPrepaidRedeemFlow()) {
      return true;
    }
    const data = this.notification?.data as Record<string, any> | undefined;
    if (data?.['lpr_prepaid_redeem'] === true) {
      return !!String(data?.['prepaid_purchase_id'] || this.prepaidPurchaseId || '').trim();
    }
    return !!(this.prepaidPurchaseId || '').trim();
  }

  /**
   * True only when the modal was *opened to redeem* — a QR scan, an LPR redeem event, or the
   * attendant picking Redeem. Collapsing single wash, monthly, fleet, add-ons, discounts and
   * the wash-book catalog is right there: the attendant is mid-redemption.
   *
   * A plate typed into Add Order is not that. Owning a prepaid purchase says nothing about
   * what the attendant came to sell, and hiding everything left them unable to sell the same
   * plate anything at all (CWS-1144). Monthly membership has always shown its redemption
   * beside the full catalog; prepaid was the outlier.
   */
  isOrderRequestPrepaidRedeemEntryPoint(): boolean {
    const data = this.notification?.data as Record<string, any> | undefined;
    if (!data || !String(data['prepaid_purchase_id'] || '').trim()) {
      return false;
    }
    // The scan itself names the purchase to redeem, so the modal opens knowing it. A plate
    // typed into Add Order never does: there the purchase id arrives from the customer
    // lookup, long after the modal opened, and says nothing about the attendant's intent.
    return data['qr_scan'] === true || data['lpr_prepaid_redeem'] === true;
  }

  /**
   * A scan opened this modal to redeem a book that still has washes to draw.
   *
   * Once the book is used up or expired the catalog reopens and the screen is an ordinary
   * create-order again, so everything that only holds "while redeeming" has to stop holding
   * too — the collapsed catalog and, since CWS-1914, the customer pick that skips the
   * Active subscription chooser.
   */
  private isOrderRequestPrepaidRedeemEntryLive(): boolean {
    return this.isOrderRequestPrepaidRedeemEntryPoint() && !this.isPrepaidPurchaseConsumedForUi();
  }

  /** Redeem-driven entry collapses the rest of the catalog; a looked-up plate does not. */
  private suppressesOrderRequestCatalog(): boolean {
    return this.isOrderRequestPrepaidRedeemEntryLive();
  }

  showOrderRequestPackagesSection(): boolean {
    if (this.suppressesOrderRequestCatalog()) {
      return false;
    }
    return (
      this.isLoadingPackages ||
      this.singleWashPackages.length > 0 ||
      this.monthlyPackages.length > 0
    );
  }

  /**
   * The Packages block (heading + wash-type tabs) stays on screen even when the location has
   * no packages, so each tab can say so (CWS-1588). Discounts still follow the packages section.
   * The fleet layout keeps the old rule: its tiles are locked, so an empty block adds nothing.
   */
  showOrderRequestPackagesBlock(): boolean {
    if (this.notification?.type === 'fleet-vehicle-found') {
      return this.showOrderRequestPackagesSection();
    }
    return !this.suppressesOrderRequestCatalog();
  }

  showOrderRequestAddonsSection(): boolean {
    return !this.suppressesOrderRequestCatalog() && this.orderRequestAddons.length > 0;
  }

  showOrderRequestDiscountsSection(): boolean {
    if (this.familySaleActive) {
      return false;
    }
    return this.showOrderRequestPackagesSection() && this.orderRequestVisibleDiscounts.length > 0;
  }

  isOrderRequestPrepaidSelected(bookId: string): boolean {
    // A chosen wash package is the selection; the book tile must not keep its tick.
    if (this.selectedPackage) {
      return false;
    }
    if (this.isOrderRequestPrepaidRedeemFlow() && !this.isPrepaidPurchaseConsumedForUi()) {
      return (this.prepaidPurchaseStatus?.prepaid_wash_book_id || '').trim() === bookId;
    }
    return this.orderRequestSelectedPrepaidBookId === bookId;
  }

  getOrderRequestPrepaidRedemptionStatusBadge(): PrepaidPurchaseStatusBadge | null {
    return getPrepaidPurchaseStatusBadge(this.prepaidPurchaseStatus);
  }

  isOrderRequestPrepaidDisabled(): boolean {
    return (
      // The fleet's plan decides the wash on a fleet-vehicle-found order, which is why
      // shouldDisableField('package') locks every package tile there. Prepaid books are the
      // same kind of choice and were left selectable, so the one screen where nothing is the
      // attendant's to pick still offered them a wash book to buy.
      this.notification?.type === 'fleet-vehicle-found' ||
      this.isFleetVehicleBillingOnlyAddons() ||
      (this.isOrderRequestPrepaidRedeemFlow() && !this.isPrepaidPurchaseConsumedForUi())
    );
  }

  getOrderRequestSelectedPrepaidBook(): PrepaidWashBook | null {
    const id = this.orderRequestSelectedPrepaidBookId;
    if (!id) {
      return null;
    }
    return this.orderRequestPrepaidBooks.find(b => b.id === id) ?? null;
  }

  /** Parse prepaid book catalog selling price (API may return plain or formatted strings). */
  private parsePrepaidBookSellingPrice(book: PrepaidWashBook | null | undefined): number | null {
    if (!book) {
      return null;
    }
    const n = this.parseUsdNumber(book.selling_price);
    return n != null && n > 0 ? n : null;
  }

  /**
   * Detail rows for the selected prepaid book. `id` is the book line's id: two lines can share
   * a wash label (the same package sold twice with different validity), so the template must
   * not track rows by their text or Angular raises NG0955 on every change-detection pass.
   */
  getOrderRequestPrepaidDetailRows(): { id: string; washLine: string; expiryLine: string }[] {
    const book = this.getOrderRequestSelectedPrepaidBook();
    if (!book?.items?.length) {
      return [];
    }
    const sorted = [...book.items].sort((a, b) => a.sort_order - b.sort_order);
    return sorted.map((item: PrepaidWashBookListLine) => ({
      id: item.id,
      washLine: formatOrderRequestPrepaidWashLine(item),
      expiryLine: formatOrderRequestPrepaidExpiryLine(item),
    }));
  }

  formatOrderRequestPrepaidSellingPrice(book: PrepaidWashBook): string {
    const n = this.parsePrepaidBookSellingPrice(book);
    return n != null ? this.formatCheckoutCurrency(n) : '';
  }

  private getOrderRequestSelectedPrepaidBookIdForApi(): string | null {
    const id = (this.orderRequestSelectedPrepaidBookId || '').trim();
    return id || null;
  }

  private getOrderRequestPrepaidListPriceForApi(): number | null {
    return this.parsePrepaidBookSellingPrice(this.resolveCheckoutPrepaidBookForSummary());
  }

  /** Prepaid selling price for checkout totals (UI selection, catalog, or order snapshot). */
  private resolvePrepaidListPriceForCheckout(): number | null {
    const fromBook = this.parsePrepaidBookSellingPrice(this.resolveCheckoutPrepaidBookForSummary());
    if (fromBook != null) {
      return fromBook;
    }
    const ord = this.getCheckoutOrderSnapshot();
    const fromOrderList =
      this.parseUsdNumber(ord?.['list_price']) ?? this.parseUsdNumber(ord?.['listPrice']);
    if (fromOrderList != null && fromOrderList > 0) {
      return fromOrderList;
    }
    const d = this.notification?.data as Record<string, any> | undefined;
    const fromTopList =
      this.parseUsdNumber(d?.['list_price']) ?? this.parseUsdNumber(d?.['listPrice']);
    if (fromTopList != null && fromTopList > 0) {
      return fromTopList;
    }
    const fromForm = this.parseUsdNumber(this.formData.price);
    return fromForm != null && fromForm > 0 ? fromForm : null;
  }

  isOrderRequestDiscountDisabled(): boolean {
    return (
      this.isFleetVehicleBillingOnlyAddons() ||
      !!this.orderRequestSelectedPrepaidBookId ||
      !!this.orderRequestSelectedFleetId ||
      this.suppressesOrderRequestCatalog()
    );
  }

  private getFleetVehicleContextFromNotification(): {
    fleetId: string | null;
    fleetName: string | null;
  } {
    const d = this.notification?.data as Record<string, any> | undefined;
    const v = (d?.['vehicle_data'] as Record<string, any> | undefined) || undefined;

    const fleetIdRaw = v?.['fleet_id'] ?? d?.['fleet_id'] ?? d?.['fleetId'] ?? null;
    const fleetNameRaw = v?.['fleet_name'] ?? d?.['fleet_name'] ?? d?.['fleetName'] ?? null;

    const fleetId = String(fleetIdRaw ?? '').trim() || null;
    const fleetName = String(fleetNameRaw ?? '').trim() || null;
    return { fleetId, fleetName };
  }

  /** Fleet in order-request is auto-driven by arriving fleet vehicle context, not manually chosen. */
  isOrderRequestFleetSelectionLocked(): boolean {
    const { fleetId, fleetName } = this.getFleetVehicleContextFromNotification();
    return !!fleetId || !!fleetName;
  }

  /**
   * Fleet named in the "Billed to fleet" notice of the order-request layout, '' when the order is not
   * fleet-billed. Fleet context locks the package catalog, and the notice is what tells staff why.
   */
  getFleetBillingNoticeLabel(): string {
    if (!this.isFleetVehicleBillingOnlyAddons()) {
      return '';
    }
    const { fleetId, fleetName } = this.getFleetVehicleContextFromNotification();
    const selectedId = this.orderRequestSelectedFleetId || fleetId;
    const loaded = this.orderRequestFleets.find(f => String(f.id).trim() === selectedId);
    return loaded?.label || fleetName || 'a fleet account';
  }

  /** Fleet vehicle billing mode: disable package/prepaid/discount and allow add-ons only. */
  isFleetVehicleBillingOnlyAddons(): boolean {
    if (this.notification?.type === 'fleet-vehicle-found') {
      return false;
    }
    return this.isOrderRequestFleetSelectionLocked();
  }

  private applyFleetVehicleSelectionFromNotification(): void {
    if (!this.isOrderRequestFleetSelectionLocked()) {
      return;
    }

    const { fleetId, fleetName } = this.getFleetVehicleContextFromNotification();
    const normalizedName = (fleetName || '').toLowerCase();
    const matchedById = fleetId
      ? this.orderRequestFleets.find(f => String(f.id).trim() === fleetId)
      : undefined;
    const matchedByName =
      !matchedById && normalizedName
        ? this.orderRequestFleets.find(f => f.label.trim().toLowerCase() === normalizedName)
        : undefined;
    const matched = matchedById || matchedByName;

    this.orderRequestSelectedFleetId = matched ? matched.id : fleetId || null;
    this.orderRequestSelectedPrepaidBookId = null;

    if (this.notification?.type === 'fleet-vehicle-found') {
      this.syncOrderRequestDiscountStateForFleetAndPrepaid();
      this.syncOrderRequestAddonSelectionToPairedWith();
      this.selectExpandedPackageForFleetVehicleFoundNotification();
      return;
    }

    this.selectedPackage = null;
    this.selectedWashType = null;
    this.formData.package = '';
    this.formData.packageId = '';
    this.formData.price = '';
    this.syncOrderRequestDiscountStateForFleetAndPrepaid();
    this.syncOrderRequestAddonSelectionToPairedWith();
  }

  /**
   * After packages are expanded, align radio selection with Pusher payload (package_id / name / wash_type).
   */
  private selectExpandedPackageForFleetVehicleFoundNotification(): void {
    if (this.notification?.type !== 'fleet-vehicle-found') {
      return;
    }
    const d = this.notification.data as Record<string, any> | undefined;
    if (!d) {
      return;
    }
    const v = (d['vehicle_data'] as Record<string, any> | undefined) || {};
    const pid = String(
      v['package_id'] ?? d['package_id'] ?? d['packageId'] ?? this.formData.packageId ?? ''
    ).trim();
    const pname = String(v['package_name'] ?? d['package_name'] ?? d['packageName'] ?? '')
      .trim()
      .toLowerCase();
    // A payload that names the package without carrying its id still identifies the wash the
    // fleet vehicle is on. Bailing on the missing id skipped the name match below and left the
    // tile unhighlighted, so the attendant could not see which wash the order was for -- the
    // card is read-only here, but it still has to say what was chosen.
    if (this.expandedPackages.length === 0 || (!pid && !pname)) {
      return;
    }

    const washRaw = v['wash_type'] ?? d['wash_type'] ?? d['washType'] ?? null;
    if (pid && washRaw != null && String(washRaw).trim() !== '') {
      const washNorm = this.normalizeWashType(String(washRaw));
      const byWash = this.expandedPackages.find(
        ep =>
          String(ep.package.id).trim() === pid && this.normalizeWashType(ep.washType) === washNorm
      );
      if (byWash) {
        this.applyExpandedPackageSelectionForFleetFound(byWash);
        return;
      }
    }

    if (pname) {
      const byName = this.expandedPackages.find(ep => {
        const label = String(ep.displayName || ep.package.package_name || '')
          .trim()
          .toLowerCase();
        return label === pname;
      });
      if (byName) {
        this.applyExpandedPackageSelectionForFleetFound(byName);
        return;
      }
    }

    const matches = this.expandedPackages.filter(ep => String(ep.package.id).trim() === pid);
    const preferred =
      matches.find(ep => this.normalizeWashType(ep.washType) === 'single_wash') || matches[0];
    if (preferred) {
      this.applyExpandedPackageSelectionForFleetFound(preferred);
    }
  }

  private applyExpandedPackageSelectionForFleetFound(ep: ExpandedPackage): void {
    this.selectedPackage = ep.package;
    this.selectedWashType = ep.washType;
    this.orderRequestWashSelectionCleared = false;
    this.formData.package = ep.package.package_name || '';
    this.formData.packageId = ep.package.id || '';
  }

  private syncOrderRequestDiscountStateForFleetAndPrepaid(): void {
    if (!this.isOrderRequestDiscountDisabled()) {
      return;
    }
    this.resetOrderRequestDiscountUi();
  }

  selectOrderRequestPrepaid(bookId: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.isFleetVehicleBillingOnlyAddons()) {
      return;
    }
    if (this.orderRequestSelectedPrepaidBookId === bookId) {
      this.orderRequestSelectedPrepaidBookId = null;
    } else {
      this.orderRequestSelectedPrepaidBookId = bookId;
    }
    // Deselect any single/monthly package so selection is mutually exclusive
    this.selectedPackage = null;
    this.selectedWashType = null;
    this.formData.package = '';
    this.formData.packageId = '';
    const book = this.getOrderRequestSelectedPrepaidBook();
    if (book) {
      const price = this.getOrderRequestPrepaidListPriceForApi();
      this.formData.price = price != null ? price.toFixed(2) : '';
    } else {
      this.formData.price = '';
    }
    this.syncOrderRequestDiscountStateForFleetAndPrepaid();
    this.syncOrderRequestAddonSelectionToPairedWith();
    this.rebuildMostUsedServiceItems();
    this.clearStaleOrderRequestValidationDisplay();
  }

  isOrderRequestFleetSelected(fleetId: string): boolean {
    return this.orderRequestSelectedFleetId === fleetId;
  }

  selectOrderRequestFleet(fleetId: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    // Fleet selection is auto-driven by arriving fleet vehicle context only (not manually clickable).
    return;
  }

  private findOrderRequestAddonVariantLine(addonId: string): OrderRequestAddonVariantLine | null {
    for (const a of this.orderRequestAddons) {
      if (a.mode !== 'multi_variant_package' || !a.packageVariants) {
        continue;
      }
      const hit = a.packageVariants.find(v => v.id === addonId);
      if (hit) {
        return hit;
      }
    }
    return null;
  }

  /** Committed quantity for a multi-variant line (0 = not in order). */
  getOrderRequestAddonVariantCommittedQty(variantId: string): number {
    const q = this.orderRequestAddonVariantQuantities[variantId];
    if (q != null && q > 0) {
      return q;
    }
    if (this.orderRequestSelectedAddonIds.has(variantId)) {
      return 1;
    }
    return 0;
  }

  private isOrderRequestAddonVariantLineCommitted(v: OrderRequestAddonVariantLine): boolean {
    return this.getOrderRequestAddonVariantCommittedQty(v.id) > 0;
  }

  getOrderRequestAddonVariantsDialogQty(variantId: string): number {
    return this.orderRequestAddonVariantsDialogQty[variantId] ?? 0;
  }

  /** Price column in the variant picker (catalog preview; totals only count checked lines on Done). */
  getOrderRequestAddonVariantDialogPriceDisplay(v: OrderRequestAddonVariantLine): string {
    const checked = this.isOrderRequestAddonVariantDialogChecked(v.id);
    const qRaw = this.getOrderRequestAddonVariantsDialogQty(v.id) ?? 0;
    // Preview: never show $0 for a priced line when unchecked / qty 0 — show unit × 1 (or × qty when set).
    const qPreview = !checked && qRaw <= 0 ? 1 : Math.max(1, qRaw);
    if (v.mode === 'custom_price') {
      const saved = this.orderRequestAddonSavedCustomPrice[v.id];
      const unit = this.parseUsdNumber(saved);
      if (unit == null || !Number.isFinite(unit) || unit <= 0) {
        return '—';
      }
      return this.formatCheckoutCurrency(unit * qPreview);
    }
    const unit = v.unitPrice ?? 0;
    if (!Number.isFinite(unit) || unit <= 0) {
      return '—';
    }
    return this.formatCheckoutCurrency(unit * qPreview);
  }

  /** @deprecated Use {@link getOrderRequestAddonVariantDialogPriceDisplay} in templates. */
  getOrderRequestAddonVariantDialogLineTotal(v: OrderRequestAddonVariantLine): string {
    return this.getOrderRequestAddonVariantDialogPriceDisplay(v);
  }

  /** `multiple` rows show +/-; `single` uses implicit qty 1 when checked. */
  isOrderRequestAddonVariantMultiple(v: OrderRequestAddonVariantLine): boolean {
    return v.quantitySelection === 'multiple';
  }

  /**
   * Variants dialog: show +/- only for multiple-qty lines. Custom-price lines keep the pill/editor
   * until an amount is saved — then only the stepper shows (no overlap with Custom price UI).
   */
  showAddonVariantStepperInDialog(v: OrderRequestAddonVariantLine): boolean {
    if (!this.isOrderRequestAddonVariantMultiple(v)) {
      return false;
    }
    if (v.mode === 'custom_price') {
      return !!this.formatOrderRequestAddonSavedPrice(v.id);
    }
    return true;
  }

  isOrderRequestAddonVariantDialogChecked(variantId: string): boolean {
    return this.orderRequestAddonVariantsDialogChecked[variantId] === true;
  }

  onOrderRequestAddonVariantDialogCheckboxChange(v: OrderRequestAddonVariantLine, ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const checked = !!input?.checked;
    this.orderRequestAddonVariantsDialogChecked = {
      ...this.orderRequestAddonVariantsDialogChecked,
      [v.id]: checked,
    };
    if (!checked) {
      if (this.orderRequestAddonVariantDialogCustomExpandedId === v.id) {
        this.orderRequestAddonVariantDialogCustomExpandedId = null;
      }
    } else if (!this.isOrderRequestAddonVariantMultiple(v)) {
      this.orderRequestAddonVariantsDialogQty = {
        ...this.orderRequestAddonVariantsDialogQty,
        [v.id]: 1,
      };
    } else {
      const cur = this.orderRequestAddonVariantsDialogQty[v.id] ?? 0;
      this.orderRequestAddonVariantsDialogQty = {
        ...this.orderRequestAddonVariantsDialogQty,
        [v.id]: Math.max(1, cur),
      };
    }
  }

  isOrderRequestAddonVariantDialogCustomExpanded(variantId: string): boolean {
    return this.orderRequestAddonVariantDialogCustomExpandedId === variantId;
  }

  openOrderRequestAddonVariantDialogCustomEditor(
    v: OrderRequestAddonVariantLine,
    ev?: Event
  ): void {
    ev?.preventDefault();
    ev?.stopPropagation();
    if (v.mode !== 'custom_price') {
      return;
    }
    // After a price is saved, row shows amount + optional +/- only — no re-edit from this pill.
    if (this.formatOrderRequestAddonSavedPrice(v.id)) {
      return;
    }
    this.orderRequestAddonVariantDialogCustomExpandedId = v.id;
    const saved = this.orderRequestAddonSavedCustomPrice[v.id];
    this.orderRequestAddonCustomPriceDraft[v.id] = saved ? this.stripUsdForAddonInput(saved) : '';
  }

  saveOrderRequestAddonVariantLineCustomPrice(v: OrderRequestAddonVariantLine): void {
    if (v.mode !== 'custom_price') {
      return;
    }
    const raw = (this.orderRequestAddonCustomPriceDraft[v.id] || '').trim();
    if (!raw) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Amount required',
        detail: 'Enter a valid amount.',
      });
      return;
    }
    const num = parseFloat(raw);
    if (!Number.isFinite(num) || num <= 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid amount',
        detail: 'Enter an amount greater than zero.',
      });
      return;
    }
    this.orderRequestAddonSavedCustomPrice[v.id] = `$${num.toFixed(2)}`;
    this.orderRequestAddonVariantDialogCustomExpandedId = null;
  }

  /** Custom-price variant lines: checkbox only after a valid saved amount (Figma flow). */
  isOrderRequestAddonVariantDialogCheckboxVisible(v: OrderRequestAddonVariantLine): boolean {
    if (v.mode !== 'custom_price') {
      return true;
    }
    const unit = this.parseUsdNumber(this.orderRequestAddonSavedCustomPrice[v.id]);
    return unit != null && Number.isFinite(unit) && unit > 0;
  }

  adjustOrderRequestAddonVariantsQty(variantId: string, delta: number): void {
    if (!this.orderRequestAddonVariantsDialog?.packageVariants) {
      return;
    }
    const v = this.orderRequestAddonVariantsDialog.packageVariants.find(x => x.id === variantId);
    if (!v || !this.isOrderRequestAddonVariantMultiple(v)) {
      return;
    }
    const cur = this.orderRequestAddonVariantsDialogQty[variantId] ?? 0;
    const nextQty = Math.max(0, cur + delta);
    this.orderRequestAddonVariantsDialogQty = {
      ...this.orderRequestAddonVariantsDialogQty,
      [variantId]: nextQty,
    };
  }

  isOrderRequestAddonSelected(addonId: string): boolean {
    const row = this.orderRequestAddons.find(a => a.id === addonId);
    if (row?.mode === 'choose_products') {
      return this.getOrderRequestVendingCommittedQuantityFor(addonId) > 0;
    }
    if (row?.mode === 'multi_variant_package' && row.packageVariants) {
      return row.packageVariants.some(v => this.isOrderRequestAddonVariantLineCommitted(v));
    }
    if (row?.mode === 'custom_price' && this.formatOrderRequestAddonSavedPrice(addonId)) {
      return true;
    }
    if (row) {
      return this.orderRequestSelectedAddonIds.has(addonId);
    }
    const line = this.findOrderRequestAddonVariantLine(addonId);
    if (line) {
      return this.isOrderRequestAddonVariantLineCommitted(line);
    }
    return this.orderRequestSelectedAddonIds.has(addonId);
  }

  getOrderRequestMultiVariantCommittedTiles(
    addon: OrderRequestAddonItem
  ): OrderRequestAddonCommittedTileLine[] {
    if (addon.mode !== 'multi_variant_package' || !addon.packageVariants?.length) {
      return [];
    }
    return addon.packageVariants
      .map(v => {
        const q = this.getOrderRequestAddonVariantCommittedQty(v.id);
        if (q <= 0) {
          return null;
        }
        const unit =
          v.mode === 'custom_price'
            ? (this.parseUsdNumber(this.orderRequestAddonSavedCustomPrice[v.id]) ?? 0)
            : (v.unitPrice ?? 0);
        const lineTotal = this.formatCheckoutCurrency(unit * q);
        return {
          id: v.id,
          title: q > 1 ? `${v.label} × ${q}` : v.label,
          priceLabel: lineTotal,
          parentLabel: addon.label,
        };
      })
      .filter((x): x is OrderRequestAddonCommittedTileLine => x != null);
  }

  trackOrderRequestCommittedAddonTileById(
    _index: number,
    line: OrderRequestAddonCommittedTileLine
  ): string {
    return line.id;
  }

  openOrderRequestAddonVariantsDialog(addon: OrderRequestAddonItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.isOrderRequestAddonPairedWithDisabled(addon)) {
      return;
    }
    if (addon.mode !== 'multi_variant_package' || !addon.packageVariants?.length) {
      return;
    }
    this.orderRequestAddonVariantsDialog = addon;
    const qty: Record<string, number> = {};
    const checked: Record<string, boolean> = {};
    for (const v of addon.packageVariants) {
      const committed = this.getOrderRequestAddonVariantCommittedQty(v.id);
      qty[v.id] = committed;
      checked[v.id] = committed > 0;
    }
    this.orderRequestAddonVariantsDialogQty = { ...qty };
    this.orderRequestAddonVariantsDialogChecked = checked;
    this.orderRequestAddonVariantDialogCustomExpandedId = null;
  }

  closeOrderRequestAddonVariantsDialog(): void {
    this.orderRequestAddonVariantsDialog = null;
    this.orderRequestAddonVariantsDialogQty = {};
    this.orderRequestAddonVariantsDialogChecked = {};
    this.orderRequestAddonVariantDialogCustomExpandedId = null;
  }

  applyOrderRequestAddonVariantsDialog(): void {
    const pkg = this.orderRequestAddonVariantsDialog;
    if (!pkg?.packageVariants) {
      return;
    }
    if (this.isOrderRequestAddonPairedWithDisabled(pkg)) {
      this.closeOrderRequestAddonVariantsDialog();
      return;
    }

    const next = new Set(this.orderRequestSelectedAddonIds);
    const qtyGlobal = { ...this.orderRequestAddonVariantQuantities };
    for (const v of pkg.packageVariants) {
      next.delete(v.id);
      delete qtyGlobal[v.id];
      if (this.orderRequestAddonExpandedId === v.id) {
        this.orderRequestAddonExpandedId = null;
      }
    }

    for (const v of pkg.packageVariants) {
      if (!this.orderRequestAddonVariantsDialogChecked[v.id]) {
        continue;
      }
      let q = this.orderRequestAddonVariantsDialogQty[v.id] ?? 0;
      if (!this.isOrderRequestAddonVariantMultiple(v)) {
        q = 1;
      } else {
        q = Math.floor(q);
      }
      if (q <= 0) {
        continue;
      }
      if (v.mode === 'custom_price' && !this.formatOrderRequestAddonSavedPrice(v.id)) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Custom price',
          detail: `Enter and save a price for ${v.label}.`,
        });
        continue;
      }
      qtyGlobal[v.id] = q;
      next.add(v.id);
    }

    // Clear custom price only for lines not committed (do not wipe before validation — that broke Done after Save).
    for (const v of pkg.packageVariants) {
      const qFinal = qtyGlobal[v.id] ?? 0;
      if (qFinal <= 0 || !next.has(v.id)) {
        delete this.orderRequestAddonSavedCustomPrice[v.id];
        delete this.orderRequestAddonCustomPriceDraft[v.id];
        if (this.orderRequestAddonExpandedId === v.id) {
          this.orderRequestAddonExpandedId = null;
        }
      }
    }

    this.orderRequestAddonVariantQuantities = qtyGlobal;
    this.orderRequestSelectedAddonIds = next;
    const anyCommitted =
      pkg.packageVariants?.some(v => (qtyGlobal[v.id] ?? 0) > 0 && next.has(v.id)) ?? false;
    if (anyCommitted) {
      this.syncOrderRequestAddonSelectionAcrossKinds(pkg);
    }
    this.closeOrderRequestAddonVariantsDialog();
    this.clearOrderRequestDiscountSelectionIfHidden();
  }

  trackOrderRequestAddonVariantById(_index: number, v: OrderRequestAddonVariantLine): string {
    return v.id;
  }

  toggleOrderRequestAddonSelection(addon: OrderRequestAddonItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    if (this.isOrderRequestAddonPairedWithDisabled(addon)) {
      return;
    }

    if (addon.mode === 'choose_products') {
      if (this.getOrderRequestVendingCommittedQuantityFor(addon.id) > 0) {
        delete this.orderRequestVendingSelections[addon.id];
        const next = new Set(this.orderRequestSelectedAddonIds);
        next.delete(addon.id);
        this.orderRequestSelectedAddonIds = next;
        this.clearOrderRequestDiscountSelectionIfHidden();
      } else {
        this.openOrderRequestVendingDialog(addon);
      }
      return;
    }

    if (addon.mode === 'multi_variant_package' && addon.packageVariants) {
      const anySelected = addon.packageVariants.some(v =>
        this.isOrderRequestAddonVariantLineCommitted(v)
      );
      const next = new Set<string>(this.orderRequestSelectedAddonIds);
      if (anySelected) {
        if (this.orderRequestAddonVariantsDialog?.id === addon.id) {
          this.closeOrderRequestAddonVariantsDialog();
        }
        for (const v of addon.packageVariants) {
          next.delete(v.id);
          delete this.orderRequestAddonVariantQuantities[v.id];
          delete this.orderRequestAddonSavedCustomPrice[v.id];
          delete this.orderRequestAddonCustomPriceDraft[v.id];
          if (this.orderRequestAddonExpandedId === v.id) {
            this.orderRequestAddonExpandedId = null;
          }
        }
        this.orderRequestSelectedAddonIds = next;
        this.clearOrderRequestDiscountSelectionIfHidden();
      } else {
        this.openOrderRequestAddonVariantsDialog(addon, event);
      }
      return;
    }

    const next = new Set<string>(this.orderRequestSelectedAddonIds);
    if (next.has(addon.id)) {
      if (addon.mode === 'custom_price' && this.formatOrderRequestAddonSavedPrice(addon.id)) {
        return;
      }
      next.delete(addon.id);
    } else {
      next.add(addon.id);
    }
    this.orderRequestSelectedAddonIds = next;
    if (next.has(addon.id)) {
      this.syncOrderRequestAddonSelectionAcrossKinds(addon);
    }
    this.clearOrderRequestDiscountSelectionIfHidden();
    this.clearStaleOrderRequestValidationDisplay();
  }

  isOrderRequestDiscountSelected(discountId: string): boolean {
    return this.orderRequestSelectedDiscountId === discountId;
  }

  /**
   * Discount tiles this order may actually use.
   *
   * Applies every gate svc-order enforces on submit — wash type, package scope, audience,
   * schedule window, day/time restriction and minimum purchase — so staff cannot pick a
   * tile the API will reject.
   *
   * Cached on (list, wash type, package id, customer history, order amount) so a template
   * read returns the same array instance until one of them actually changes — a fresh
   * array every change-detection pass would re-render the tiles continuously. The clock
   * is deliberately not part of the key: re-filtering on every read would defeat the memo,
   * and the server still has the final say at submit time.
   */
  get orderRequestVisibleDiscounts(): OrderRequestDiscountItem[] {
    const source = this.orderRequestDiscounts;
    const washType = this.getOrderRequestDiscountWashType();
    const packageId =
      String(this.selectedPackage?.id ?? this.formData.packageId ?? '').trim() || null;
    const customerHistory = this.orderRequestCustomerHistory;
    // Same package + add-on basis svc-order uses for minimum_purchase_amount.
    const orderAmount = this.getOrderRequestPreDiscountTotal();
    // Order Request Location timezone — same zone svc-order uses for schedule/restriction.
    const timeZone = this.getOrderRequestDiscountTimeZone();
    const cache = this.orderRequestVisibleDiscountCache;
    if (
      cache.source === source &&
      cache.washType === washType &&
      cache.packageId === packageId &&
      cache.customerHistory === customerHistory &&
      cache.orderAmount === orderAmount &&
      cache.timeZone === timeZone
    ) {
      return cache.result;
    }
    const result = filterOrderRequestDiscountsForOrder(source, {
      washType,
      packageId,
      customerHistory,
      at: this.getOrderRequestDiscountEvaluationDate(),
      timeZone,
      orderAmount,
    });
    this.orderRequestVisibleDiscountCache = {
      source,
      washType,
      packageId,
      customerHistory,
      orderAmount,
      timeZone,
      result,
    };
    return result;
  }

  /**
   * IANA timezone of the Location selected on the Order Request.
   * Falls back to the effective shop timezone when the location has none cached.
   */
  private getOrderRequestDiscountTimeZone(): string {
    const locationId =
      String(this.formData.locationId ?? '').trim() ||
      String(this.notification?.data?.location_id ?? '').trim() ||
      String(this.notification?.data?.locationId ?? '').trim() ||
      null;
    return this.locationService.getTimezoneForLocationId(locationId) || getUserTimezone() || '';
  }

  /**
   * Instant the day/time and schedule rules are judged against.
   *
   * svc-order evaluates a discount at `appointment_time` when the order carries one, so
   * a booking made now for Sunday is judged on Sunday. Matching that here keeps the
   * tiles and the API in agreement; with no appointment set yet, both fall back to now.
   */
  /**
   * Wash type the discount tiles are matched against.
   *
   * Same resolution as getOrderRequestEffectiveWashType, except a deselect wins over the
   * loaded order's wash type. Staff clearing the package expect the tiles to go with it,
   * while the cached value still answers on a freshly reopened order, so an
   * already-applied discount is not dropped before the package selection has hydrated.
   */
  private getOrderRequestDiscountWashType(): string | null {
    const explicit = String(this.selectedWashType ?? '').trim();
    if (explicit) {
      return this.normalizeWashType(explicit);
    }
    const packageWashTypes = this.selectedPackage?.wash_types || [];
    if (packageWashTypes.length === 1) {
      return this.normalizeWashType(packageWashTypes[0]);
    }
    if (this.orderRequestWashSelectionCleared) {
      return null;
    }
    const cached = String(this.cachedOrderWashType ?? '').trim();
    return cached ? this.normalizeWashType(cached) : null;
  }

  private getOrderRequestDiscountEvaluationDate(): Date {
    const raw = String(this.formData.appointmentTime ?? '').trim();
    if (!raw) {
      return new Date();
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  /**
   * Refresh the audience bucket for the selected plate.
   *
   * Skipped when the plate has not changed, so switching package/wash type or reopening
   * the discount list does not re-hit the API. On failure the bucket is left null, which
   * the filter treats as first time — the server re-checks on submit either way.
   */
  private refreshOrderRequestCustomerHistory(): void {
    const hintState =
      this.formData.vehicleLicensePlateState || this.notification?.data?.state || null;
    const plateOnly = splitLicensePlateForApi(
      this.formData.vehicleLicensePlate || '',
      hintState
    ).license_plate;
    const licensePlate =
      String(plateOnly ?? '')
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '') || null;

    if (this.orderRequestCustomerHistoryFor === licensePlate) {
      return;
    }
    this.orderRequestCustomerHistoryFor = licensePlate;

    this.packageService
      .getDiscountEligibility({
        licensePlate,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          // A late reply for a plate that has since been swapped out must not win.
          if (this.orderRequestCustomerHistoryFor !== licensePlate) {
            return;
          }
          const history = String(response?.data?.customer_history ?? '').trim();
          this.orderRequestCustomerHistory = history === 'returning' ? 'returning' : 'first_time';
          this.clearOrderRequestDiscountSelectionIfHidden();
        },
        error: err => {
          console.error('Error loading discount eligibility:', err);
          if (this.orderRequestCustomerHistoryFor === licensePlate) {
            this.orderRequestCustomerHistory = null;
          }
        },
      });
  }

  /**
   * Wash type the order is for: what the user picked, else the package's own when it
   * offers only one, else the wash type the order was loaded with.
   */
  private getOrderRequestEffectiveWashType(): string | null {
    const explicit = String(this.selectedWashType ?? '').trim();
    if (explicit) {
      return this.normalizeWashType(explicit);
    }
    const packageWashTypes = this.selectedPackage?.wash_types || [];
    if (packageWashTypes.length === 1) {
      return this.normalizeWashType(packageWashTypes[0]);
    }
    const cached = String(this.cachedOrderWashType ?? '').trim();
    return cached ? this.normalizeWashType(cached) : null;
  }

  /**
   * A discount that no longer applies must not stay applied: switching from single wash
   * to monthly can hide the selected tile, and a hidden discount would keep changing the
   * total with nothing on screen to explain it.
   */
  private clearOrderRequestDiscountSelectionIfHidden(): void {
    const selected = this.orderRequestSelectedDiscountId;
    if (!selected || selected === ORDER_REQUEST_CUSTOM_DISCOUNT.id) {
      return;
    }
    if (!this.orderRequestVisibleDiscounts.some(d => d.id === selected)) {
      this.clearOrderRequestDiscountSelection();
    }
  }

  selectOrderRequestDiscount(discountId: string, event?: Event): void {
    const t = event?.target as HTMLElement | undefined;
    if (
      t?.closest(
        'input, textarea, select, [contenteditable="true"], .order-request-discount-renewal-info'
      )
    ) {
      return;
    }
    event?.preventDefault();
    event?.stopPropagation();
    if (this.isOrderRequestDiscountDisabled()) {
      this.resetOrderRequestDiscountUi();
      return;
    }
    // Clicking the selected tile again takes the discount off the order.
    if (this.orderRequestSelectedDiscountId === discountId) {
      this.clearOrderRequestDiscountSelection();
      return;
    }
    this.orderRequestSelectedDiscountId = discountId;
    if (discountId !== 'disc_custom') {
      this.orderRequestCustomDiscountExpanded = false;
    }
    // No-op while creating an order; on the checkout of an existing one this pushes the
    // applied discount straight to the order.
    this.requestCheckoutSelectionSync('discount');
  }

  /**
   * Drop the selection so the order carries no shop discount: the summary stops
   * subtracting it and both the create payload and the checkout PUT send
   * `discount_id: null` with a zero amount.
   *
   * A saved custom discount draft is kept, so re-selecting that tile restores it rather
   * than making the user retype it.
   */
  private clearOrderRequestDiscountSelection(): void {
    this.orderRequestSelectedDiscountId = null;
    this.orderRequestCustomDiscountExpanded = false;
    this.requestCheckoutSelectionSync('discount');
    this.clearStaleOrderRequestValidationDisplay();
  }

  isOrderRequestAddonExpanded(addonId: string): boolean {
    return this.orderRequestAddonExpandedId === addonId;
  }

  formatOrderRequestAddonSavedPrice(addonId: string): string | null {
    const v = this.orderRequestAddonSavedCustomPrice[addonId];
    return v && v.trim() ? v : null;
  }

  getOrderRequestAddonCustomPriceDraft(addonId: string): string {
    return this.orderRequestAddonCustomPriceDraft[addonId] ?? '';
  }

  onOrderRequestAddonPillClick(addon: OrderRequestAddonItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isOrderRequestAddonPairedWithDisabled(addon)) {
      return;
    }
    if (addon.mode === 'multi_variant_package') {
      this.openOrderRequestAddonVariantsDialog(addon, event);
      return;
    }
    if (addon.mode === 'choose_products') {
      this.openOrderRequestVendingDialog(addon);
      return;
    }
    if (!this.orderRequestSelectedAddonIds.has(addon.id)) {
      const next = new Set<string>(this.orderRequestSelectedAddonIds);
      next.add(addon.id);
      this.orderRequestSelectedAddonIds = next;
    }
    if (addon.mode === 'fixed') {
      if (this.orderRequestSelectedAddonIds.has(addon.id)) {
        this.syncOrderRequestAddonSelectionAcrossKinds(addon);
      }
      return;
    }
    if (addon.mode === 'custom_price') {
      if (this.orderRequestAddonExpandedId === addon.id) {
        this.orderRequestAddonExpandedId = null;
        return;
      }
      this.openOrderRequestAddonCustomPriceEditor(addon);
      return;
    }
  }

  /** Re-open editor from saved price + pencil (Figma after custom price). */
  onOrderRequestAddonEditCustomPrice(addon: OrderRequestAddonItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isOrderRequestAddonPairedWithDisabled(addon)) {
      return;
    }
    if (!this.orderRequestSelectedAddonIds.has(addon.id)) {
      const next = new Set<string>(this.orderRequestSelectedAddonIds);
      next.add(addon.id);
      this.orderRequestSelectedAddonIds = next;
    }
    if (addon.mode !== 'custom_price') {
      return;
    }
    this.openOrderRequestAddonCustomPriceEditor(addon);
  }

  private openOrderRequestAddonCustomPriceEditor(addon: OrderRequestAddonItem): void {
    this.orderRequestAddonExpandedId = addon.id;
    const saved = this.orderRequestAddonSavedCustomPrice[addon.id];
    this.orderRequestAddonCustomPriceDraft[addon.id] = saved
      ? this.stripUsdForAddonInput(saved)
      : '';
  }

  /** Block scientific notation and sign keys that `type="number"` still allows. */
  onOrderRequestAddonCustomPriceKeydown(event: KeyboardEvent): void {
    if (event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '-') {
      event.preventDefault();
    }
  }

  onOrderRequestAddonCustomPriceInput(
    addonId: string,
    value: string | number | null,
    event?: Event
  ): void {
    let s = String(value ?? '').replace(/[^\d.]/g, '');
    const firstDot = s.indexOf('.');
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
    }
    let normalized = '';
    if (s) {
      const parts = s.split('.');
      const intPart = (parts[0] ?? '').slice(0, 6);
      const frac = parts.length > 1 ? parts[1].slice(0, 2) : '';
      normalized = parts.length > 1 ? (frac ? `${intPart}.${frac}` : `${intPart}.`) : intPart;
    }
    this.orderRequestAddonCustomPriceDraft[addonId] = normalized;
    const el = event?.target as HTMLInputElement | null;
    if (el && el.value !== normalized) {
      el.value = normalized;
    }
  }

  saveOrderRequestAddonCustomPrice(addon: OrderRequestAddonItem): void {
    if (addon.mode !== 'custom_price') {
      return;
    }
    if (this.isOrderRequestAddonPairedWithDisabled(addon)) {
      return;
    }
    const raw = (this.orderRequestAddonCustomPriceDraft[addon.id] || '').trim();
    if (!raw) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Amount required',
        detail: 'Enter a valid amount.',
      });
      return;
    }
    const num = parseFloat(raw);
    if (!Number.isFinite(num) || num < 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid amount',
        detail: 'Enter a valid number.',
      });
      return;
    }
    if (num === 0) {
      this.removeOrderRequestAddonCustomPrice(addon);
      return;
    }
    this.orderRequestAddonSavedCustomPrice[addon.id] = `$${num.toFixed(2)}`;
    const sel = new Set(this.orderRequestSelectedAddonIds);
    sel.add(addon.id);
    this.orderRequestSelectedAddonIds = sel;
    this.orderRequestAddonExpandedId = null;
    this.syncOrderRequestAddonSelectionAcrossKinds(addon);
    this.clearOrderRequestDiscountSelectionIfHidden();
  }

  /** Clears saved custom price and deselects the add-on (only way to turn off a priced custom add-on). */
  removeOrderRequestAddonCustomPrice(addon: OrderRequestAddonItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (addon.mode !== 'custom_price') {
      return;
    }
    delete this.orderRequestAddonSavedCustomPrice[addon.id];
    delete this.orderRequestAddonCustomPriceDraft[addon.id];
    if (this.orderRequestAddonExpandedId === addon.id) {
      this.orderRequestAddonExpandedId = null;
    }
    const next = new Set(this.orderRequestSelectedAddonIds);
    next.delete(addon.id);
    this.orderRequestSelectedAddonIds = next;
    this.clearOrderRequestDiscountSelectionIfHidden();
  }

  openOrderRequestVendingDialog(addon: OrderRequestAddonItem): void {
    if (addon.mode !== 'choose_products') {
      return;
    }
    if (this.isOrderRequestAddonPairedWithDisabled(addon)) {
      return;
    }
    this.orderRequestVendingDialogAddonId = addon.id;
    const catalog = addon.vendingCatalog?.length ? addon.vendingCatalog.map(c => ({ ...c })) : [];
    const qtyById = new Map<string, number>(
      (this.orderRequestVendingSelections[addon.id]?.lines ?? []).map(l => [l.id, l.quantity])
    );
    this.orderRequestVendingItems = catalog.map(item => ({
      ...item,
      quantity: qtyById.get(item.id) ?? 0,
    }));
    const chk: Record<string, boolean> = {};
    for (const it of this.orderRequestVendingItems) {
      chk[it.id] = it.quantity > 0;
    }
    this.orderRequestVendingDialogChecked = chk;
    this.orderRequestVendingCustomExpandedItemId = null;
    this.orderRequestVendingDialogOpen = true;
  }

  closeOrderRequestVendingDialog(): void {
    this.orderRequestVendingDialogOpen = false;
    this.orderRequestVendingDialogAddonId = null;
    this.orderRequestVendingDialogChecked = {};
    this.orderRequestVendingCustomExpandedItemId = null;
  }

  adjustOrderRequestVendingQuantity(itemId: string, delta: number): void {
    this.orderRequestVendingItems = this.orderRequestVendingItems.map(item => {
      if (item.id !== itemId) {
        return item;
      }
      if (item.quantitySelection !== 'multiple') {
        return item;
      }
      const nextQuantity = Math.max(0, item.quantity + delta);
      return { ...item, quantity: nextQuantity };
    });
  }

  getOrderRequestVendingItemTotal(item: OrderRequestVendingItem): string {
    let unit = item.unitPrice;
    if (item.allowsCustomPrice) {
      unit = this.parseUsdNumber(this.orderRequestAddonSavedCustomPrice[item.id]) ?? 0;
    }
    let q =
      item.quantitySelection === 'multiple'
        ? Math.max(0, Math.floor(item.quantity))
        : item.quantity > 0
          ? 1
          : 0;
    // Price column preview: list × 1 when qty is 0 but unit is set (checked or not).
    if (q === 0 && unit > 0) {
      q = 1;
    }
    if (item.allowsCustomPrice && (unit <= 0 || !Number.isFinite(unit))) {
      return '—';
    }
    if (!item.allowsCustomPrice && (!Number.isFinite(unit) || unit <= 0)) {
      return '—';
    }
    return `$${(q * unit).toFixed(2)}`;
  }

  isOrderRequestVendingDialogCustomExpanded(itemId: string): boolean {
    return this.orderRequestVendingCustomExpandedItemId === itemId;
  }

  openOrderRequestVendingCustomEditor(item: OrderRequestVendingItem, ev?: Event): void {
    ev?.preventDefault();
    ev?.stopPropagation();
    if (!item.allowsCustomPrice) {
      return;
    }
    this.orderRequestVendingCustomExpandedItemId = item.id;
    const saved = this.orderRequestAddonSavedCustomPrice[item.id];
    this.orderRequestAddonCustomPriceDraft[item.id] = saved
      ? this.stripUsdForAddonInput(saved)
      : '';
  }

  saveOrderRequestVendingItemCustomPrice(item: OrderRequestVendingItem): void {
    if (!item.allowsCustomPrice) {
      return;
    }
    const raw = (this.orderRequestAddonCustomPriceDraft[item.id] || '').trim();
    if (!raw) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Amount required',
        detail: 'Enter a valid amount.',
      });
      return;
    }
    const num = parseFloat(raw);
    if (!Number.isFinite(num) || num < 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid amount',
        detail: 'Enter a valid number.',
      });
      return;
    }
    this.orderRequestAddonSavedCustomPrice[item.id] = `$${num.toFixed(2)}`;
    this.orderRequestVendingCustomExpandedItemId = null;
  }

  onOrderRequestVendingDialogCheckboxChange(item: OrderRequestVendingItem, ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const checked = !!input?.checked;
    this.orderRequestVendingDialogChecked = {
      ...this.orderRequestVendingDialogChecked,
      [item.id]: checked,
    };
    if (!checked) {
      if (this.orderRequestVendingCustomExpandedItemId === item.id) {
        this.orderRequestVendingCustomExpandedItemId = null;
      }
    } else if (item.quantitySelection !== 'multiple') {
      this.orderRequestVendingItems = this.orderRequestVendingItems.map(i =>
        i.id === item.id ? { ...i, quantity: 1 } : i
      );
    } else {
      this.orderRequestVendingItems = this.orderRequestVendingItems.map(i => {
        if (i.id !== item.id) {
          return i;
        }
        const nextQty = Math.max(1, Math.floor(i.quantity) || 1);
        return { ...i, quantity: nextQty };
      });
    }
  }

  continueOrderRequestVendingSelection(): void {
    const addonId = this.orderRequestVendingDialogAddonId;
    const addonRow = addonId ? this.orderRequestAddons.find(a => a.id === addonId) : undefined;
    if (addonRow && this.isOrderRequestAddonPairedWithDisabled(addonRow)) {
      this.closeOrderRequestVendingDialog();
      return;
    }
    const lines: OrderRequestVendingSelectionLine[] = [];
    let sum = 0;
    for (const item of this.orderRequestVendingItems) {
      if (!this.orderRequestVendingDialogChecked[item.id]) {
        continue;
      }
      let quantity = item.quantity;
      if (item.quantitySelection !== 'multiple') {
        quantity = 1;
      } else {
        quantity = Math.floor(quantity);
      }
      let unitPrice = item.unitPrice;
      if (item.allowsCustomPrice) {
        const p = this.parseUsdNumber(this.orderRequestAddonSavedCustomPrice[item.id]);
        if (p == null || !Number.isFinite(p) || p < 0) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Custom price',
            detail: `Enter and save a price for ${item.name}.`,
          });
          continue;
        }
        unitPrice = p;
        quantity = item.quantitySelection === 'multiple' ? Math.max(1, quantity || 1) : 1;
      } else if (quantity <= 0) {
        continue;
      } else {
        quantity = Math.max(1, quantity);
      }
      const lineAmount = quantity * unitPrice;
      sum += lineAmount;
      lines.push({
        id: item.id,
        name: item.name,
        quantity,
        unitPrice,
        lineTotalFormatted: `$${lineAmount.toFixed(2)}`,
      });
    }

    const next = new Set<string>(this.orderRequestSelectedAddonIds);
    if (!addonId) {
      this.orderRequestVendingDialogOpen = false;
      this.orderRequestVendingDialogAddonId = null;
      this.orderRequestVendingDialogChecked = {};
      this.orderRequestVendingCustomExpandedItemId = null;
      return;
    }
    if (lines.length === 0) {
      delete this.orderRequestVendingSelections[addonId];
      next.delete(addonId);
      this.orderRequestSelectedAddonIds = next;
    } else {
      this.orderRequestVendingSelections[addonId] = {
        lines,
        totalFormatted: `$${sum.toFixed(2)}`,
      };
      next.add(addonId);
      this.orderRequestSelectedAddonIds = next;
      const addon = this.orderRequestAddons.find(a => a.id === addonId);
      if (addon) {
        this.syncOrderRequestAddonSelectionAcrossKinds(addon);
      }
    }
    this.orderRequestVendingDialogOpen = false;
    this.orderRequestVendingDialogAddonId = null;
    this.orderRequestVendingDialogChecked = {};
    this.orderRequestVendingCustomExpandedItemId = null;
    this.clearOrderRequestDiscountSelectionIfHidden();
  }

  getOrderRequestCustomDiscountTypeLabel(key: 'fixed_amount' | 'percentage'): string {
    return this.orderRequestCustomDiscountTypeOptions.find(o => o.value === key)?.label ?? key;
  }

  toggleOrderRequestCustomDiscount(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isOrderRequestDiscountDisabled()) {
      this.resetOrderRequestDiscountUi();
      return;
    }
    this.orderRequestSelectedDiscountId = 'disc_custom';
    if (this.orderRequestCustomDiscountExpanded) {
      this.orderRequestCustomDiscountExpanded = false;
      return;
    }
    this.openOrderRequestCustomDiscountEditor();
  }

  onOrderRequestCustomDiscountEditClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isOrderRequestDiscountDisabled()) {
      this.resetOrderRequestDiscountUi();
      return;
    }
    this.orderRequestSelectedDiscountId = 'disc_custom';
    this.openOrderRequestCustomDiscountEditor();
  }

  private openOrderRequestCustomDiscountEditor(): void {
    this.orderRequestCustomDiscountExpanded = true;
    this.orderRequestCustomDiscountReasonError = null;
    if (this.orderRequestCustomDiscountSaved) {
      this.orderRequestCustomDiscountName = this.orderRequestCustomDiscountSaved.name;
      this.orderRequestCustomDiscountType = this.orderRequestCustomDiscountSaved.typeKey;
      this.orderRequestCustomDiscountReason =
        this.orderRequestCustomDiscountSaved.reason?.trim() || '';
      const v = this.orderRequestCustomDiscountSaved.valueLabel;
      if (this.orderRequestCustomDiscountSaved.typeKey === 'percentage') {
        this.orderRequestCustomDiscountValueDraft = v.replace(/%/g, '').trim();
      } else {
        this.orderRequestCustomDiscountValueDraft = this.stripUsdForAddonInput(v);
      }
    }
    this.orderRequestCustomDiscountValueDraft = this.sanitizeOrderRequestCustomDiscountValueDraft(
      this.orderRequestCustomDiscountValueDraft ?? '',
      this.orderRequestCustomDiscountType
    );
  }

  /** Attendant Order Request requires a custom-discount reason; Admin does not. */
  isOrderRequestCustomDiscountReasonRequired(): boolean {
    return this.roleService.isAttendant();
  }

  getOrderRequestCustomDiscountReasonLabel(reason: string | null | undefined): string {
    const normalized = String(reason ?? '').trim();
    if (!normalized) {
      return '';
    }
    const match = this.orderRequestCustomDiscountReasonOptions.find(
      option => option.value === normalized
    );
    return match ? match.label : normalized;
  }

  onOrderRequestCustomDiscountReasonChange(reason: string): void {
    this.orderRequestCustomDiscountReason = reason;
    this.orderRequestCustomDiscountReasonError = null;
  }

  onOrderRequestCustomDiscountValueInput(value: string): void {
    this.orderRequestCustomDiscountValueDraft = this.sanitizeOrderRequestCustomDiscountValueDraft(
      String(value ?? ''),
      this.orderRequestCustomDiscountType
    );
  }

  /** Flat / % radios: keep draft numeric-only under the active type rules. */
  onOrderRequestCustomDiscountTypeChange(type: 'fixed_amount' | 'percentage'): void {
    this.orderRequestCustomDiscountType = type;
    this.onOrderRequestCustomDiscountValueInput(this.orderRequestCustomDiscountValueDraft ?? '');
  }

  /**
   * Discount value field: digits + at most one decimal point, max 2 fractional digits.
   * Percentage is clamped to 100. Letters and other characters are stripped (not left in the field).
   */
  private sanitizeOrderRequestCustomDiscountValueDraft(
    raw: string,
    mode: 'fixed_amount' | 'percentage'
  ): string {
    let s = raw.replace(/[^\d.]/g, '');
    const firstDot = s.indexOf('.');
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
    }
    if (!s) {
      return '';
    }
    if (s === '.') {
      return '0.';
    }
    const parts = s.split('.');
    const intPart = parts[0] ?? '';
    const frac = parts.length > 1 ? parts[1].slice(0, 2) : '';
    s = parts.length > 1 ? (frac ? `${intPart}.${frac}` : `${intPart}.`) : intPart;
    if (mode === 'percentage') {
      const n = parseFloat(s);
      if (Number.isFinite(n) && n > 100) {
        return '100';
      }
    }
    return s;
  }

  /** Package list price + committed add-ons (same basis as checkout discount / breakdown). */
  private getOrderRequestPreDiscountTotal(): number {
    const data = this.notification?.data as
      { order?: { list_price?: string | number } } | undefined;
    const order = data?.order;
    const addon = this.getCheckoutAddonAmount();
    const fromPkg = this.getCheckoutListPriceFromPackage();
    const subtotal =
      fromPkg ??
      this.parseUsdNumber(order?.list_price) ??
      this.parseUsdNumber(this.formData.price) ??
      0;
    return Math.max(0, subtotal + addon);
  }

  saveOrderRequestCustomDiscount(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isOrderRequestDiscountDisabled()) {
      this.resetOrderRequestDiscountUi();
      this.messageService.add({
        severity: 'warn',
        summary: 'Discount unavailable',
        detail: 'Discount cannot be used when both fleet and prepaid are selected.',
      });
      return;
    }
    const name = (this.orderRequestCustomDiscountName || '').trim();
    if (!name) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Discount name required',
        detail: 'Enter a name for this discount.',
      });
      return;
    }
    if (!isMeaningfulDisplayName(name)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid discount name',
        detail: 'Enter a meaningful name with at least one letter.',
      });
      return;
    }
    const raw = (this.orderRequestCustomDiscountValueDraft || '').trim();
    if (!raw) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Discount value required',
        detail: 'Enter a discount amount or percentage.',
      });
      return;
    }
    const num = parseFloat(raw);
    if (!Number.isFinite(num) || num < 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid value',
        detail: 'Enter a valid number.',
      });
      return;
    }
    if (this.orderRequestCustomDiscountType === 'percentage' && num > 100) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid percentage',
        detail: 'Percentage cannot exceed 100.',
      });
      return;
    }
    const preDiscountTotal = this.getOrderRequestPreDiscountTotal();
    if (this.orderRequestCustomDiscountType === 'fixed_amount') {
      if (preDiscountTotal <= 0 && num > 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'No order total yet',
          detail:
            'Select a package (and add-ons if any) so the order total is known before entering a flat discount.',
        });
        return;
      }
      if (num > preDiscountTotal + 1e-9) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Discount too large',
          detail: `Flat discount cannot exceed the order total (${this.formatCheckoutCurrency(preDiscountTotal)}).`,
        });
        return;
      }
    }
    const reasonRequired = this.isOrderRequestCustomDiscountReasonRequired();
    const reason = (this.orderRequestCustomDiscountReason || '').trim();
    if (reasonRequired && !reason) {
      this.orderRequestCustomDiscountReasonError = 'Reason is required';
      this.messageService.add({
        severity: 'warn',
        summary: 'Reason required',
        detail: 'Select a reason for this custom discount.',
      });
      return;
    }
    if (reason && !CUSTOM_DISCOUNT_REASON_OPTIONS.some(option => option.value === reason)) {
      this.orderRequestCustomDiscountReasonError = 'Select a valid reason';
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid reason',
        detail: 'Select a reason from the list.',
      });
      return;
    }
    this.orderRequestCustomDiscountReasonError = null;
    const valueLabel =
      this.orderRequestCustomDiscountType === 'percentage'
        ? `${Number.isInteger(num) ? num : parseFloat(num.toFixed(2))}%`
        : `$${num.toFixed(2)}`;
    this.orderRequestCustomDiscountSaved = {
      name,
      valueLabel,
      typeKey: this.orderRequestCustomDiscountType,
      ...(reason ? { reason } : {}),
      // New/edited draft — persist a fresh discounts row on order create/update.
    };
    this.orderRequestSelectedDiscountId = 'disc_custom';
    this.orderRequestCustomDiscountExpanded = false;
  }

  private stripUsdForAddonInput(saved: string): string {
    return saved.replace(/^\s*\$\s*/, '').trim();
  }

  /** Cancel any pending Stripe mount retry. */
  private cancelStripeMountRetries(): void {
    if (this.stripeMountTimeoutId !== null) {
      clearTimeout(this.stripeMountTimeoutId);
      this.stripeMountTimeoutId = null;
    }
  }

  /** Checkout vs Active-subscription card hosts. Duplicate global IDs make Stripe mount the first match. */
  private getStripeCardElementIds(): {
    number: string;
    expiry: string;
    cvc: string;
    numberError: string;
    expiryError: string;
    cvcError: string;
  } {
    const vendorCard =
      this.shouldShowVendorActiveSubscriptionModal() &&
      this.paymentForm.method === PaymentMethodCode.Card;
    const prefix = vendorCard ? 'vendor-active-' : '';
    return {
      number: `${prefix}card-number`,
      expiry: `${prefix}card-expiry`,
      cvc: `${prefix}card-cvc`,
      numberError: `${prefix}card-number-error`,
      expiryError: `${prefix}card-expiry-error`,
      cvcError: `${prefix}card-cvc-error`,
    };
  }

  /** Schedule Stripe init and mount with retries so DOM is ready. */
  private scheduleStripeMountForNotificationModal(retries = 6, delayMs = 300): void {
    this.cancelStripeMountRetries();
    this.stripeMountTimeoutId = setTimeout(() => {
      this.stripeMountTimeoutId = null;
      const vendorCardCapture =
        this.shouldShowVendorActiveSubscriptionModal() &&
        this.paymentForm.method === PaymentMethodCode.Card;
      // Stop retrying once the modal has closed or payment method changed
      if (
        (!this.isOpen && !vendorCardCapture) ||
        this.paymentForm.method !== PaymentMethodCode.Card
      ) {
        return;
      }
      const ids = this.getStripeCardElementIds();
      const cardNumberContainer = document.getElementById(ids.number);
      const cardExpiryContainer = document.getElementById(ids.expiry);
      const cardCvcContainer = document.getElementById(ids.cvc);
      if (!cardNumberContainer || !cardExpiryContainer || !cardCvcContainer) {
        if (retries > 0) {
          this.scheduleStripeMountForNotificationModal(retries - 1, delayMs);
        }
        return;
      }
      if (this.stripe && this.cardNumberElement) {
        this.mountStripeElements();
      } else if (!this.stripe) {
        this.initializeStripeIfNeeded()
          .then(() => this.mountStripeElements())
          .catch(err => console.error('[Stripe] Failed to initialize/mount Stripe elements:', err));
      }
    }, delayMs);
  }

  private initializePaymentForm(): void {
    const data = this.notification?.data as Record<string, any> | undefined;
    this.showCheckoutSummary = data?.['open_checkout_summary'] === true;
    // Reset payment form with default method based on device
    this.paymentForm = {
      method: this.getDefaultPaymentMethod(),
      phoneNumber: this.formData.customerContact || '',
      cardholderName: this.formData.customerFirstName || '',
      billingAddress: '',
      cardNumber: '',
      cardExpiry: '',
      cardCvc: '',
      cashReceived: '',
      terminalReaderId: '',
      terminalCardType: 'credit',
    };
    this.resetCheckoutCashPanelEntryState();

    this.terminalCheckoutLoadedLocationId = null;
    this.loadTerminalCheckoutData();

    if (
      this.paymentForm.method === PaymentMethodCode.Cash &&
      this.notification?.type === 'create-payment-request'
    ) {
      this.resetCashReceivedForManualEntry();
      // Desktop defaults to Cash — open drawer when checkout lands on Cash (not only on re-click).
      void this.receiptPrinterService.openCashDrawer();
    }

    // Initialize Stripe when card is the default payment method
    if (
      this.paymentForm.method === PaymentMethodCode.Card &&
      this.notification?.type === 'create-payment-request'
    ) {
      this.initializeStripeForCardDefault();
    }

    this.loadDynamicPaymentMethods();
  }

  async ngAfterViewInit(): Promise<void> {
    this.logModalInteraction('lifecycle.afterViewInit');
    // Don't initialize Stripe here - only initialize when modal is opened and payment is needed
    // This prevents unnecessary Stripe network calls when the component is initialized but not visible
  }

  /**
   * Initialize Stripe elements when card payment is selected as default (Payment Request / license plate reader modal).
   * Only mounts elements; intent is created at Checkout to avoid unused intents.
   */
  private initializeStripeForCardDefault(): void {
    if (!this.isOpen || this.paymentForm.method !== PaymentMethodCode.Card) return;
    if (this.notification?.type !== 'create-payment-request') return;
    this.scheduleStripeMountForNotificationModal(6, 400);
  }

  private async initializeStripeIfNeeded(): Promise<void> {
    if (this.stripe && this.cardNumberElement) {
      return;
    }
    try {
      this.stripe = await loadStripe(environment.stripePublicKey);
      if (!this.stripe) {
        throw new Error('Failed to load Stripe');
      }

      this.elements = this.stripe.elements({
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#15172E',
            colorBackground: '#FFFFFF',
            colorText: '#555963',
            colorDanger: '#dc2626',
            fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
            spacingUnit: '4px',
            borderRadius: '6px',
          },
        },
      });

      this.cardNumberElement = this.elements.create('cardNumber', {
        placeholder: '8539 5932 9402 4739',
        style: {
          base: {
            fontSize: '14px',
            color: '#555963',
            fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
            '::placeholder': {
              color: '#9CA3AF',
            },
          },
          invalid: {
            color: '#dc2626',
          },
        },
      });

      this.cardExpiryElement = this.elements.create('cardExpiry', {
        placeholder: '05/30',
        style: {
          base: {
            fontSize: '14px',
            color: '#555963',
            fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
            '::placeholder': {
              color: '#9CA3AF',
            },
          },
          invalid: {
            color: '#dc2626',
          },
        },
      });

      this.cardCvcElement = this.elements.create('cardCvc', {
        placeholder: '472',
        style: {
          base: {
            fontSize: '14px',
            color: '#555963',
            fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
            '::placeholder': {
              color: '#9CA3AF',
            },
          },
          invalid: {
            color: '#dc2626',
          },
        },
      });
    } catch (error) {
      console.error('Error initializing Stripe:', error);
    }
  }

  mountStripeElements(): void {
    this.logModalInteraction('stripe.elements.mount.attempt');
    if (!this.cardNumberElement || !this.cardExpiryElement || !this.cardCvcElement) {
      console.warn('Stripe elements not initialized yet');
      return;
    }

    try {
      const ids = this.getStripeCardElementIds();
      const cardNumberContainer = document.getElementById(ids.number);
      const cardExpiryContainer = document.getElementById(ids.expiry);
      const cardCvcContainer = document.getElementById(ids.cvc);

      if (!cardNumberContainer || !cardExpiryContainer || !cardCvcContainer) {
        console.warn('Stripe element containers not found in DOM');
        return;
      }

      if (cardNumberContainer.querySelector('.StripeElement')) {
        try {
          this.cardNumberElement.unmount();
        } catch (e) {
          // Element may already be unmounted
        }
      }
      if (cardExpiryContainer.querySelector('.StripeElement')) {
        try {
          this.cardExpiryElement.unmount();
        } catch (e) {
          // Element may already be unmounted
        }
      }
      if (cardCvcContainer.querySelector('.StripeElement')) {
        try {
          this.cardCvcElement.unmount();
        } catch (e) {
          // Element may already be unmounted
        }
      }

      this.cardNumberElement.mount(`#${ids.number}`);
      this.setupStripeElementListeners(this.cardNumberElement, ids.numberError);

      this.cardExpiryElement.mount(`#${ids.expiry}`);
      this.setupStripeElementListeners(this.cardExpiryElement, ids.expiryError);

      this.cardCvcElement.mount(`#${ids.cvc}`);
      this.setupStripeElementListeners(this.cardCvcElement, ids.cvcError);

      console.log('✅ Stripe Elements mounted successfully');
    } catch (error) {
      console.error('Error mounting Stripe elements:', error);
    }
  }

  private setupStripeElementListeners(element: any, errorElementId: string): void {
    const errorElement = document.getElementById(errorElementId);

    if (!errorElement) {
      return;
    }

    element.on('change', (event: any) => {
      if (event.error) {
        const errorMessage = event.error.message;
        errorElement.textContent = errorMessage;
        errorElement.style.display = 'block';
        this.updateStripeElementCompletionState(errorElementId, false);
        // Store the error message
        this.storeStripeError(errorElementId, errorMessage);
      } else {
        errorElement.textContent = '';
        errorElement.style.display = 'none';
        const isComplete = event.complete || false;
        this.updateStripeElementCompletionState(errorElementId, isComplete);
        // Clear stored error when field becomes valid
        this.storeStripeError(errorElementId, '');
      }
    });

    element.on('blur', (event: any) => {
      // On blur, check if field is still invalid and restore error message
      if (event.error) {
        const errorMessage = event.error.message;
        errorElement.textContent = errorMessage;
        errorElement.style.display = 'block';
        this.storeStripeError(errorElementId, errorMessage);
      } else {
        // Check if we have a stored error and the field is incomplete
        const storedError = this.getStoredStripeError(errorElementId);
        const isComplete = this.getStripeElementCompletionState(errorElementId);

        // If field is incomplete and we have a stored error, restore it
        if (!isComplete && storedError) {
          errorElement.textContent = storedError;
          errorElement.style.display = 'block';
        } else if (isComplete) {
          // Field is complete, clear error
          errorElement.textContent = '';
          errorElement.style.display = 'none';
          this.storeStripeError(errorElementId, '');
        }
      }
    });

    element.on('ready', () => {
      // Element is ready
    });
  }

  private stripeCardFieldFromErrorId(errorElementId: string): 'number' | 'expiry' | 'cvc' | null {
    if (errorElementId.endsWith('card-number-error')) {
      return 'number';
    }
    if (errorElementId.endsWith('card-expiry-error')) {
      return 'expiry';
    }
    if (errorElementId.endsWith('card-cvc-error')) {
      return 'cvc';
    }
    return null;
  }

  private storeStripeError(errorElementId: string, errorMessage: string): void {
    const field = this.stripeCardFieldFromErrorId(errorElementId);
    if (field === 'number') {
      this.stripeCardNumberError = errorMessage;
    } else if (field === 'expiry') {
      this.stripeCardExpiryError = errorMessage;
    } else if (field === 'cvc') {
      this.stripeCardCvcError = errorMessage;
    }
  }

  private getStoredStripeError(errorElementId: string): string {
    const field = this.stripeCardFieldFromErrorId(errorElementId);
    if (field === 'number') {
      return this.stripeCardNumberError;
    }
    if (field === 'expiry') {
      return this.stripeCardExpiryError;
    }
    if (field === 'cvc') {
      return this.stripeCardCvcError;
    }
    return '';
  }

  private getStripeElementCompletionState(errorElementId: string): boolean {
    const field = this.stripeCardFieldFromErrorId(errorElementId);
    if (field === 'number') {
      return this.stripeCardNumberComplete;
    }
    if (field === 'expiry') {
      return this.stripeCardExpiryComplete;
    }
    if (field === 'cvc') {
      return this.stripeCardCvcComplete;
    }
    return false;
  }

  private updateStripeElementCompletionState(errorElementId: string, isComplete: boolean): void {
    const field = this.stripeCardFieldFromErrorId(errorElementId);
    if (field === 'number') {
      this.stripeCardNumberComplete = isComplete;
    } else if (field === 'expiry') {
      this.stripeCardExpiryComplete = isComplete;
    } else if (field === 'cvc') {
      this.stripeCardCvcComplete = isComplete;
    }
    if (this.shouldShowVendorActiveSubscriptionModal()) {
      this.cdr.markForCheck();
    }
  }

  /**
   * Check if cash payment should be disabled (only for monthly subscription)
   */
  isCashPaymentDisabled(): boolean {
    // If notification already has wash_type, normalize it and decide from that
    if (this.notification?.data?.wash_type) {
      const normalizedFromNotification = this.normalizeWashType(this.notification.data.wash_type);
      return normalizedFromNotification === 'monthly_membership';
    }

    // For create-payment-request notifications, check order wash_type from cached value or notification data
    if (this.notification?.type === 'create-payment-request') {
      // First check cached order wash_type
      if (this.cachedOrderWashType) {
        const normalized = this.normalizeWashType(this.cachedOrderWashType);
        if (normalized === 'monthly_membership') {
          return true;
        }
      }

      // Check notification data for wash_type
      const orderWashType =
        this.notification?.data?.wash_type || this.notification?.data?.order?.wash_type || '';
      if (orderWashType) {
        const normalized = this.normalizeWashType(orderWashType);
        if (normalized === 'monthly_membership') {
          return true;
        }
      }
    }

    // Check selected wash type
    if (this.selectedWashType) {
      const normalized = this.normalizeWashType(this.selectedWashType);
      if (normalized === 'monthly_membership') {
        return true;
      }
    }

    // Check selected package wash types
    if (this.selectedPackage?.wash_types) {
      const washTypes = this.selectedPackage.wash_types;
      // If only monthly_membership is available
      if (washTypes.length === 1 && this.normalizeWashType(washTypes[0]) === 'monthly_membership') {
        return true;
      }
      // If both are available but monthly is selected
      if (washTypes.length === 2 && this.selectedWashType) {
        const normalized = this.normalizeWashType(this.selectedWashType);
        if (normalized === 'monthly_membership') {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Payment-request checkout: load GET order/:id once per open when the payload is thin
   * (e.g. Pusher with order_id only). Skip when POST /order data is already on notification.data.
   */
  private hydrateCreatePaymentRequestCheckoutFromOrderIfNeeded(): void {
    if (this.notification?.type !== 'create-payment-request' || this.isEditMode) {
      return;
    }

    const orderId = String(
      this.formData.orderId ||
        this.notification?.data?.order_id ||
        this.notification?.data?.orderId ||
        ''
    ).trim();
    if (!orderId) {
      return;
    }

    if (this.createPaymentCheckoutHydratedOrderId === orderId) {
      return;
    }

    if (this.hasInlineCreatePaymentCheckoutOrderData()) {
      this.createPaymentCheckoutHydratedOrderId = orderId;
      this.cdr.detectChanges();
      return;
    }

    if (this.isHydratingCreatePaymentCheckout) {
      return;
    }

    this.isHydratingCreatePaymentCheckout = true;
    this.apiService
      .get(`order/${orderId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (orderResponse: any) => {
          this.isHydratingCreatePaymentCheckout = false;
          this.createPaymentCheckoutHydratedOrderId = orderId;
          const root =
            (orderResponse as { data?: any })?.data != null
              ? (orderResponse as { data: any }).data
              : orderResponse;
          this.applyCreatePaymentRequestCheckoutFromOrderApi(root);
          this.cdr.detectChanges();
        },
        error: error => {
          this.isHydratingCreatePaymentCheckout = false;
          console.error('Error loading order for checkout summary:', error);
        },
      });
  }

  /** True when checkout already has order totals from POST /order (order-request → payment flow). */
  private hasInlineCreatePaymentCheckoutOrderData(): boolean {
    const order = this.getCheckoutOrderSnapshot();
    if (!order) {
      return false;
    }
    const finalPrice = this.parseUsdNumber(order['final_price'] ?? order['finalPrice']);
    const listPrice = this.parseUsdNumber(order['list_price'] ?? order['listPrice']);
    const hasTotals =
      (finalPrice != null && finalPrice > 0) || (listPrice != null && listPrice > 0);
    if (!hasTotals) {
      return false;
    }
    const addonLines = this.getCheckoutAddOnVariantsSnapshot();
    const discount = this.parseUsdNumber(order['discount']) ?? 0;
    const impliedAddon =
      finalPrice != null && listPrice != null
        ? Math.max(0, Math.round((finalPrice + discount - listPrice) * 100) / 100)
        : 0;
    if (impliedAddon > 0.001 && addonLines.length === 0) {
      return false;
    }
    return true;
  }

  /** Merge GET order/:id into notification.data + formData for payment checkout summary. */
  private applyCreatePaymentRequestCheckoutFromOrderApi(root: any): void {
    if (!root || typeof root !== 'object' || !this.notification?.data) {
      return;
    }

    const orderLike = this.mergeOrderLikeForRestore(root);
    const data = this.notification.data as Record<string, any>;
    const prevOrder = this.getCheckoutOrderSnapshot() ?? {};

    const listPrice = orderLike['list_price'] ?? orderLike['listPrice'] ?? prevOrder['list_price'];
    const discountAmt = orderLike['discount'] ?? prevOrder['discount'] ?? 0;
    const finalPrice =
      orderLike['final_price'] ?? orderLike['finalPrice'] ?? prevOrder['final_price'];

    data['order'] = {
      ...prevOrder,
      ...orderLike,
      list_price: listPrice,
      discount: discountAmt,
      final_price: finalPrice,
      package_id: orderLike['package_id'] ?? orderLike['packageId'] ?? prevOrder['package_id'],
      package_name:
        orderLike['package_name'] ?? orderLike['packageName'] ?? prevOrder['package_name'],
      prepaid_wash_book_id:
        orderLike['prepaid_wash_book_id'] ??
        orderLike['prepaidWashBookId'] ??
        prevOrder['prepaid_wash_book_id'],
      prepaid_wash_book_name:
        orderLike['prepaid_wash_book_name'] ??
        orderLike['prepaidWashBookName'] ??
        prevOrder['prepaid_wash_book_name'],
      wash_type: orderLike['wash_type'] ?? orderLike['washType'] ?? prevOrder['wash_type'],
    };

    let addonLines = this.extractAddOnLinesFromOrderApiRoot(root);
    if (!addonLines.length) {
      const lp = this.parseUsdNumber(listPrice);
      const disc = this.parseUsdNumber(discountAmt) ?? 0;
      const fp = this.parseUsdNumber(finalPrice);
      const implied = lp != null && fp != null ? Math.max(0, fp + disc - lp) : 0;
      if (implied > 0.001) {
        addonLines = [
          {
            add_on_name: String(orderLike['addon_label'] ?? 'addon').trim() || 'addon',
            line_total: implied,
            price: implied,
          },
        ];
      }
    }
    data['add_ons'] = addonLines;
    data['add_on_variants'] = addonLines;

    const discRow = this.extractDiscountRowFromFetchedOrder(root);
    if (discRow && Object.keys(discRow).length > 0) {
      data['discount'] = discRow;
    }

    const washType = orderLike['wash_type'] ?? orderLike['washType'];
    if (washType != null && String(washType).trim()) {
      const rawWashType = String(washType).trim();
      this.cachedOrderWashType = rawWashType;
      data['wash_type'] = this.isPrepaidWashBookWashType(rawWashType)
        ? 'prepaid_wash_book'
        : this.normalizeWashType(rawWashType);
      const normalized = this.normalizeWashType(rawWashType);
      if (normalized === 'monthly_membership' && !this.isMobileOrTablet && this.paymentForm) {
        this.paymentForm.method = this.getDefaultPaymentMethod();
        this.initializeStripeForCardDefault();
      }
      this.reconcilePaymentMethodAfterContextChange();
    }

    const fp = this.parseUsdNumber(finalPrice);
    if (fp != null && Number.isFinite(fp)) {
      this.formData.price = fp.toFixed(2);
    }

    this.populateFormDataFromOrder(orderLike);
    if (this.isAddonOnlyWashContext(orderLike)) {
      this.clearOrderRequestWashPackageSelection();
    } else {
      this.restoreOrderRequestSingleWashCustomPriceFromListPriceSource(orderLike);
    }
    this.restoreCheckoutPrepaidSelectionFromOrderSnapshot();
    this.ensureCheckoutPrepaidBookLoadedForSummary();
  }

  /**
   * Why the Stripe-backed tiles are unavailable. Rendered as a notice above the tiles, so it must
   * read as a complete sentence on its own rather than as a tooltip fragment.
   */
  readonly cardPaymentUnavailableMessage =
    'Card payments aren’t available yet. Connect this location to Stripe to accept card payments.';

  /**
   * Is this checkout selling a monthly membership?
   *
   * Delegates to isCashPaymentDisabled, which already answers exactly this — cash is barred
   * precisely because the order is a subscription — and resolves wash_type from the four places
   * it can arrive (notification data, cached order, selected wash type, package wash types).
   * Duplicating that resolution would be two things to keep in step.
   */
  private isMonthlyMembershipCheckout(): boolean {
    return this.isCashPaymentDisabled();
  }

  /**
   * Card, Payment Link, Tap to Pay and Terminal all settle on the location's connected Stripe
   * account, and the backend refuses every one of them without it — for a single wash as much as
   * for a membership (the payment-intent, payment-link and terminal account paths all reject an
   * order with no stripe_account_id). Cash and custom methods need no Stripe, so a single wash
   * keeps them.
   */
  areStripePaymentMethodsDisabled(): boolean {
    return !this.hasConnectedStripeAccount();
  }

  /**
   * A membership at a location with no connected Stripe account cannot be taken by ANY route: the
   * Stripe methods are off, and confirming cash or a custom method would still book a membership
   * that could never renew, so those are turned off with the rest.
   */
  arePaymentMethodsDisabled(): boolean {
    return this.isMonthlyMembershipCheckout() && this.areStripePaymentMethodsDisabled();
  }

  /** Payment links are Stripe Checkout sessions on the connected account — same condition. */
  isPaymentLinkDisabled(): boolean {
    return this.areStripePaymentMethodsDisabled();
  }

  /** Card charges settle on the connected account — same condition. */
  isCardPaymentDisabled(): boolean {
    return this.areStripePaymentMethodsDisabled();
  }

  private isStripePaymentMethod(method: string | null | undefined): boolean {
    return (
      method === PaymentMethodCode.Card ||
      method === PaymentMethodCode.Link ||
      method === PaymentMethodCode.TapToPay ||
      method === PaymentMethodCode.Terminal
    );
  }

  /** The selected method settles on Stripe, and this location has no connected account. */
  private isSelectedStripeMethodUnavailable(): boolean {
    return (
      this.isStripePaymentMethod(this.paymentForm.method) && this.areStripePaymentMethodsDisabled()
    );
  }

  /**
   * Can this location take a card at all?
   *
   * Unknown is treated as connected: the locations list is cached and an attendant whose cache has
   * not populated must not be blocked from taking money. The backend still rejects a genuinely
   * unconfigured location, so the cost of guessing wrong here is the error we have today, while
   * guessing the other way would block valid payments.
   */
  hasConnectedStripeAccount(): boolean {
    const locationId =
      this.getCheckoutLocationId() || String(this.authService.user()?.location ?? '').trim();
    if (!locationId) {
      return true;
    }
    const location = this.locationService.getLocationById(locationId);
    if (!location) {
      return true;
    }
    return String(location.stripe_account_id ?? '').trim().length > 0;
  }

  selectPaymentMethod(method: PaymentMethod): void {
    this.logModalInteraction('payment.method.select', { method });
    if (!this.isCheckoutPaymentRequired()) {
      return;
    }
    // Prevent selecting cash payment for monthly subscription
    if (method === PaymentMethodCode.Cash && this.isCashPaymentDisabled()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Cash Payment Not Available',
        detail:
          'Cash payment is only available for single wash orders. Monthly subscriptions require card payment.',
        life: 3000,
      });
      return;
    }

    // The tiles are disabled when Stripe is not connected, but a caller that is not a guarded tile
    // (the Tap to Pay tile's own handler, a deep link, a restored draft) must not slip past: nothing
    // is selectable for such a membership, and no Stripe method for any order.
    if (
      this.arePaymentMethodsDisabled() ||
      (this.isStripePaymentMethod(method) && this.areStripePaymentMethodsDisabled())
    ) {
      return;
    }

    // Terminal requires the reader list to finish loading and at least one online reader.
    if (method === PaymentMethodCode.Terminal && !this.isTerminalPaymentMethodEnabled()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Online Reader',
        detail: this.isTerminalCheckoutReady()
          ? this.getTerminalReaderUnavailableMessage()
          : 'Still checking for available readers. Please wait a moment.',
        life: 3000,
      });
      return;
    }

    // Clear stored Stripe errors when switching payment methods
    if (this.paymentForm.method === PaymentMethodCode.Card && method !== PaymentMethodCode.Card) {
      this.stripeCardNumberError = '';
      this.stripeCardExpiryError = '';
      this.stripeCardCvcError = '';
      // Also clear error displays
      const cardNumberError = document.getElementById('card-number-error');
      const cardExpiryError = document.getElementById('card-expiry-error');
      const cardCvcError = document.getElementById('card-cvc-error');
      if (cardNumberError) {
        cardNumberError.textContent = '';
        cardNumberError.style.display = 'none';
      }
      if (cardExpiryError) {
        cardExpiryError.textContent = '';
        cardExpiryError.style.display = 'none';
      }
      if (cardCvcError) {
        cardCvcError.textContent = '';
        cardCvcError.style.display = 'none';
      }
    }

    this.paymentForm.method = method;
    if (method === PaymentMethodCode.Cash) {
      // Open drawer when staff selects Cash so they can take tender (not on Print Receipt).
      void this.receiptPrinterService.openCashDrawer();
      if (this.notification?.type === 'create-payment-request') {
        this.resetCashReceivedForManualEntry();
      } else {
        this.paymentForm.cashReceived = '';
        this.resetCheckoutCashPanelEntryState();
      }
    } else {
      this.resetCheckoutCashPanelEntryState();
    }

    // Built-in cash and API-driven custom methods are confirmed via confirmPaymentMode.
    if (method === PaymentMethodCode.Cash || this.isDynamicPaymentMethod(method)) {
      // These methods will be confirmed via confirmPaymentMode function
      return;
    }

    if (method === PaymentMethodCode.TapToPay) {
      // Tap to Pay doesn't require any initialization
      return;
    } else if (method === PaymentMethodCode.Terminal) {
      this.loadTerminalCheckoutData();
      return;
    } else if (method === PaymentMethodCode.Card) {
      // Only mount Stripe elements; intent is created at Checkout to avoid unused intents
      this.scheduleStripeMountForNotificationModal();
    }
  }

  /**
   * Toggle the more payment methods section
   */
  toggleMorePaymentMethods(): void {
    this.showMorePaymentMethods = !this.showMorePaymentMethods;
    this.logModalInteraction('payment.moreMethods.toggle', {
      expanded: this.showMorePaymentMethods,
    });
  }

  toggleCheckoutSummary(): void {
    this.showCheckoutSummary = !this.showCheckoutSummary;
  }

  /** Grand total shown as “Amount due” when paying with cash on checkout. */
  getCheckoutAmountDueTotal(): number {
    const base = this.getPaymentCheckoutBreakdown().total;
    if (this.paymentForm.method === PaymentMethodCode.Terminal) {
      return base + this.getTerminalConvenienceFeeAmount();
    }
    return base;
  }

  getCheckoutLocationId(): string {
    return String(
      this.formData.locationId ||
        this.notification?.data?.location_id ||
        this.notification?.data?.locationId ||
        ''
    ).trim();
  }

  /** Show Terminal only when the checkout location has at least one linked reader. */
  shouldShowTerminalPaymentMethod(): boolean {
    if (this.notification?.type !== 'create-payment-request') {
      return false;
    }
    if (!this.isTerminalCheckoutReady()) {
      return false;
    }
    return this.hasLinkedTerminalReaders();
  }

  /** True once the readers/settings API has resolved for the current checkout location. */
  isTerminalCheckoutReady(): boolean {
    const locationId = this.getCheckoutLocationId();
    return !!locationId && this.terminalCheckoutLoadedLocationId === locationId;
  }

  hasLinkedTerminalReaders(): boolean {
    return this.terminalReaders.length > 0;
  }

  /** Terminal payment is selectable only after load completes and a reader is online. */
  hasOnlineTerminalReader(): boolean {
    return this.terminalReaders.some(reader => reader.status === 'online');
  }

  isTerminalPaymentMethodEnabled(): boolean {
    return this.isTerminalCheckoutReady() && this.hasOnlineTerminalReader();
  }

  getTerminalMethodDescription(): string {
    return this.hasOnlineTerminalReader() ? 'Pay using POS Reader' : 'No online readers available';
  }

  /** Tooltip when Terminal is shown but every linked reader is offline. */
  shouldShowTerminalReaderNotice(): boolean {
    return (
      this.isTerminalCheckoutReady() &&
      this.hasLinkedTerminalReaders() &&
      !this.hasOnlineTerminalReader()
    );
  }

  getTerminalReaderUnavailableMessage(): string {
    return 'No terminal readers are currently online.';
  }

  private loadTerminalCheckoutData(): void {
    const locationId = this.getCheckoutLocationId();
    if (
      !locationId ||
      this.terminalCheckoutLoadedLocationId === locationId ||
      (this.isLoadingTerminalCheckout && this.terminalCheckoutLoadingLocationId === locationId)
    ) {
      return;
    }

    this.isLoadingTerminalCheckout = true;
    this.terminalCheckoutLoadingLocationId = locationId;
    forkJoin({
      readers: this.terminalSettingsService.listReaders(locationId),
      settings: this.terminalSettingsService.getTerminalSettings(locationId),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ readers, settings }) => {
          if (this.getCheckoutLocationId() !== locationId) {
            return;
          }
          this.terminalReaders = readers;
          this.terminalSettings = settings ?? { ...DEFAULT_TERMINAL_SETTINGS };
          this.terminalCheckoutLoadedLocationId = locationId;
          this.isLoadingTerminalCheckout = false;

          if (!readers.length && this.paymentForm.method === 'terminal') {
            this.paymentForm.method = this.getDefaultPaymentMethod();
            this.paymentForm.terminalReaderId = '';
          }

          if (!this.paymentForm.terminalReaderId && readers.length) {
            this.paymentForm.terminalReaderId = selectCheckoutTerminalReaderId(readers);
          }
          if (!this.paymentForm.terminalCardType) {
            this.paymentForm.terminalCardType = 'credit';
          }
          this.cdr.markForCheck();
        },
        error: () => {
          if (this.getCheckoutLocationId() !== locationId) {
            return;
          }
          this.terminalReaders = [];
          this.terminalCheckoutLoadedLocationId = locationId;
          this.isLoadingTerminalCheckout = false;
          if (this.paymentForm.method === 'terminal') {
            this.paymentForm.method = this.getDefaultPaymentMethod();
            this.paymentForm.terminalReaderId = '';
          }
          this.cdr.markForCheck();
        },
      });
  }

  getTerminalReaderLabel(reader: TerminalReader): string {
    return String(reader.label || reader.serial_number || reader.id || 'Reader').trim();
  }

  isTerminalReaderOnline(readerId: string | null | undefined): boolean {
    const id = String(readerId || '').trim();
    if (!id) {
      return false;
    }
    const reader = this.terminalReaders.find(item => item.id === id);
    return reader?.status === 'online';
  }

  // ── Terminal reader dropdown (custom, with status dots) ──────────────────
  toggleTerminalReaderDropdown(event: Event): void {
    event.stopPropagation();
    this.isTerminalCardTypeDropdownOpen = false;
    this.isTerminalReaderDropdownOpen = !this.isTerminalReaderDropdownOpen;
  }

  closeTerminalReaderDropdown(): void {
    setTimeout(() => {
      this.isTerminalReaderDropdownOpen = false;
    }, 120);
  }

  selectTerminalReaderOption(reader: TerminalReader, event?: Event): void {
    event?.stopPropagation();
    if (reader.status !== 'online') {
      return;
    }
    this.paymentForm.terminalReaderId = reader.id;
    rememberTerminalReaderId(reader.id);
    this.isTerminalReaderDropdownOpen = false;
  }

  getSelectedTerminalReaderLabel(): string {
    const id = (this.paymentForm.terminalReaderId || '').trim();
    const reader = this.terminalReaders.find(item => item.id === id);
    return reader ? this.getTerminalReaderLabel(reader) : 'Select reader';
  }

  hasSelectedTerminalReader(): boolean {
    return !!(this.paymentForm.terminalReaderId || '').trim();
  }

  // ── Terminal card-type dropdown (custom) ─────────────────────────────────
  toggleTerminalCardTypeDropdown(event: Event): void {
    event.stopPropagation();
    this.isTerminalReaderDropdownOpen = false;
    this.isTerminalCardTypeDropdownOpen = !this.isTerminalCardTypeDropdownOpen;
  }

  closeTerminalCardTypeDropdown(): void {
    setTimeout(() => {
      this.isTerminalCardTypeDropdownOpen = false;
    }, 120);
  }

  selectTerminalCardTypeOption(value: TerminalCardType, event?: Event): void {
    event?.stopPropagation();
    this.paymentForm.terminalCardType = value;
    this.isTerminalCardTypeDropdownOpen = false;
  }

  getSelectedTerminalCardTypeLabel(): string {
    const match = this.terminalCardTypeOptions.find(
      option => option.value === this.paymentForm.terminalCardType
    );
    return match?.label ?? 'Select card type';
  }

  getTerminalConvenienceFeeAmount(): number {
    if (this.paymentForm.method !== PaymentMethodCode.Terminal) {
      return 0;
    }
    if (this.paymentForm.terminalCardType !== 'credit') {
      return 0;
    }
    if (!this.terminalSettings?.convenienceFee) {
      return 0;
    }
    const base = this.getPaymentCheckoutBreakdown().total;
    const platformPct = this.terminalSettings?.platformFeePercentage ?? 0;
    return computeTerminalConvenienceFee(base, platformPct);
  }

  hasCheckoutTerminalConvenienceFee(): boolean {
    return this.getTerminalConvenienceFeeAmount() > 0.001;
  }

  getCheckoutSummaryGrandTotal(): number {
    if (this.paymentForm.method === PaymentMethodCode.Terminal) {
      return this.getCheckoutAmountDueTotal();
    }
    return this.getPaymentCheckoutBreakdown().total;
  }

  /** Payment methods / card / link / cash only when the checkout grand total is above $0. */
  /**
   * Whether the checkout must collect a payment method — which is not the same as "money is due
   * today".
   *
   * A 100% signup discount takes a monthly membership to $0 now, but the membership still
   * renews: an advance_custom ladder can bill 50% next month, 30% the month after, then full
   * catalog price forever. Stripe therefore needs a card on file at signup. Treating $0 as "no
   * payment needed" routed these orders to the cash path, which the backend correctly refuses
   * for memberships (orderService.confirmCashPayment), leaving the order impossible to complete.
   *
   * `isCashPaymentDisabled()` is the existing membership predicate, reused here so the two can
   * never disagree about what counts as a membership checkout.
   */
  /**
   * The order behind this checkout has already been settled. `paid` and `completed` are the
   * backend's own pair for that, the same one isLprEntryCompleted reads.
   */
  isCheckoutOrderAlreadyPaid(): boolean {
    const snapshot = this.getCheckoutOrderSnapshot();
    const status = String(snapshot?.['payment_status'] ?? '')
      .trim()
      .toLowerCase();
    return status === 'paid' || status === 'completed';
  }

  isCheckoutPaymentRequired(): boolean {
    if (this.isFamilyCheckout) {
      return true;
    }
    // A settled order owes nothing, whatever its line items add up to. Without this the
    // checkout priced the order from scratch once GET order/:id landed and asked the attendant
    // to collect it a second time -- the request showed "No payment is required" for the
    // moment before the fetch returned, then flipped to a payment screen (CWS-1389).
    if (this.isCheckoutOrderAlreadyPaid()) {
      return false;
    }
    if (this.getCheckoutAmountDueTotal() > 0.009) {
      return true;
    }
    return this.isCashPaymentDisabled();
  }

  /**
   * A membership whose first invoice is fully discounted: nothing is collected today, but a card
   * is still required for the renewals. Drives the checkout copy so the attendant is not
   * surprised by a card prompt on a $0 order.
   */
  isZeroDueCardOnFileCheckout(): boolean {
    return this.getCheckoutAmountDueTotal() <= 0.009 && this.isCashPaymentDisabled();
  }

  /** True when cash tender is at least the checkout total (create-payment-request only). */
  isCheckoutCashReceivedValid(): boolean {
    if (
      this.paymentForm.method !== PaymentMethodCode.Cash ||
      this.notification?.type !== 'create-payment-request'
    ) {
      return true;
    }
    if (!this.isCheckoutPaymentRequired()) {
      return true;
    }
    return this.isCheckoutCashTenderSufficient();
  }

  private isCheckoutCashTenderSufficient(): boolean {
    const due = this.getCheckoutAmountDueTotal();
    if (due <= 0.009) {
      return true;
    }
    const received = this.parseUsdNumber(String(this.paymentForm.cashReceived ?? '').trim());
    if (received == null || !Number.isFinite(received)) {
      return false;
    }
    return received + 1e-6 >= due;
  }

  /** Change to return when tender exceeds amount due; 0 if equal or under. */
  getCheckoutCashChangeDue(): number {
    const due = this.getCheckoutAmountDueTotal();
    const received = this.parseUsdNumber(String(this.paymentForm.cashReceived ?? '').trim()) ?? 0;
    const change = Math.round((received - due) * 100) / 100;
    return change > 0 ? change : 0;
  }

  /** Show green Change due only when cash received is strictly above grand total. */
  isCheckoutCashChangeDueVisible(): boolean {
    if (
      this.paymentForm.method !== PaymentMethodCode.Cash ||
      this.notification?.type !== 'create-payment-request'
    ) {
      return false;
    }
    return this.getCheckoutCashChangeDue() > 0;
  }

  /** Change due amount label (always two decimal places). */
  getCheckoutCashChangeDueLabel(): string {
    return `$${this.getCheckoutCashChangeDue().toFixed(2)}`;
  }

  /** Parsed cash received when the field is non-empty and numeric; otherwise null. */
  getCheckoutParsedCashReceived(): number | null {
    const received = this.parseUsdNumber(String(this.paymentForm.cashReceived ?? '').trim());
    if (received == null || !Number.isFinite(received)) {
      return null;
    }
    return received;
  }

  /** True when the user entered a dollar amount that parses but is strictly below amount due. */
  isCheckoutCashUnderTender(): boolean {
    if (
      this.paymentForm.method !== PaymentMethodCode.Cash ||
      this.notification?.type !== 'create-payment-request'
    ) {
      return false;
    }
    if (!String(this.paymentForm.cashReceived ?? '').trim()) {
      return false;
    }
    const received = this.getCheckoutParsedCashReceived();
    if (received == null) {
      return false;
    }
    return received + 1e-6 < this.getCheckoutAmountDueTotal();
  }

  /** Inline + toast copy when tender is below the minimum (amount due). */
  getCheckoutCashMinimumRequiredMessage(): string {
    return `Amount received must be at least ${this.formatCheckoutCurrency(this.getCheckoutAmountDueTotal())}`;
  }

  /**
   * Manual dollars/cents field error styling — only for unparseable amounts.
   * Under-tender uses the summary message, not red input borders.
   */
  isCheckoutCashReceivedFieldInError(): boolean {
    if (
      this.paymentForm.method !== PaymentMethodCode.Cash ||
      this.notification?.type !== 'create-payment-request'
    ) {
      return false;
    }
    const raw = String(this.paymentForm.cashReceived ?? '').trim();
    if (!raw.length) {
      return false;
    }
    return this.getCheckoutParsedCashReceived() == null;
  }

  /** Current tap count for a cash preset denomination (0 when unset). */
  getCheckoutCashPresetCount(amount: number): number {
    if (!Number.isFinite(amount) || amount <= 0) {
      return 0;
    }
    const count = this.checkoutCashPresetCounts[amount];
    if (!Number.isFinite(count) || count <= 0) {
      return 0;
    }
    return Math.floor(count);
  }

  /** Add a preset denomination to current tender. */
  addCheckoutCashPreset(amount: number): void {
    if (!Number.isFinite(amount) || amount <= 0) {
      return;
    }
    this.checkoutCashPresetCounts[amount] = this.getCheckoutCashPresetCount(amount) + 1;
    this.recomputeCheckoutCashReceived();
  }

  /** Adjust tender by a preset denomination up or down; floor at $0. */
  adjustCheckoutCashPresetByAmount(amount: number, sign: 1 | -1): void {
    if (!Number.isFinite(amount) || amount <= 0) {
      return;
    }
    if (sign === -1) {
      const count = this.getCheckoutCashPresetCount(amount);
      if (count <= 0) {
        return;
      }
      this.checkoutCashPresetCounts[amount] = count - 1;
      this.recomputeCheckoutCashReceived();
      return;
    }
    this.checkoutCashPresetCounts[amount] = this.getCheckoutCashPresetCount(amount) + 1;
    this.recomputeCheckoutCashReceived();
  }

  private resetCheckoutCashPresetCounts(): void {
    this.checkoutCashPresetCounts = {};
  }

  private resetCheckoutCashManualDraft(): void {
    this.checkoutCashManualDollarsDraft = '';
    this.checkoutCashManualCentsDraft = '';
  }

  /** Clears denomination counts and manual dollars/cents drafts (cashReceived left to caller). */
  private resetCheckoutCashPanelEntryState(): void {
    this.resetCheckoutCashPresetCounts();
    this.resetCheckoutCashManualDraft();
  }

  /** Sum of tapped denomination bills. */
  getCheckoutCashPresetTotal(): number {
    let sum = 0;
    for (const amount of this.checkoutCashPresetAmounts) {
      sum += amount * this.getCheckoutCashPresetCount(amount);
    }
    return Math.round(sum * 100) / 100;
  }

  /** Parsed manual dollars + cents (independent of denomination taps). */
  getCheckoutCashManualAmount(): number {
    const dollars = this.parseCheckoutCashDollarsInput(this.checkoutCashManualDollarsDraft);
    const centsRaw = this.checkoutCashManualCentsDraft;
    const cents = this.parseCheckoutCashCentsInput(centsRaw === '' ? '0' : centsRaw);
    return Math.round((dollars + cents / 100) * 100) / 100;
  }

  /**
   * Cash received = denomination total + manual dollars/cents entry (additive, not overwrite).
   */
  private recomputeCheckoutCashReceived(): void {
    const next = Math.max(
      0,
      Math.round((this.getCheckoutCashPresetTotal() + this.getCheckoutCashManualAmount()) * 100) /
        100
    );
    this.paymentForm.cashReceived = next > 0.009 ? this.formatCheckoutCurrency(next) : '';
  }

  /** Cash received summary — always show cents to match Figma. */
  getCheckoutCashReceivedSummaryLabel(): string {
    const received = this.getCheckoutParsedCashReceived() ?? 0;
    return `$${received.toFixed(2)}`;
  }

  /** Manual dollars field display (not the full cash-received total). */
  getCheckoutCashReceivedDollarsDisplay(): string {
    return this.checkoutCashManualDollarsDraft;
  }

  /** Manual cents field display (not the full cash-received total). */
  getCheckoutCashReceivedCentsDisplay(): string {
    return this.checkoutCashManualCentsDraft;
  }

  onCheckoutCashDollarsInput(raw: string, inputEl?: HTMLInputElement): void {
    const digits = String(raw ?? '')
      .replace(/\D/g, '')
      .slice(0, 7);
    this.checkoutCashManualDollarsDraft = digits;
    if (inputEl && inputEl.value !== digits) {
      inputEl.value = digits;
    }
    this.recomputeCheckoutCashReceived();
  }

  onCheckoutCashCentsInput(raw: string, inputEl?: HTMLInputElement): void {
    const digits = String(raw ?? '')
      .replace(/\D/g, '')
      .slice(0, 2);
    this.checkoutCashManualCentsDraft = digits;
    if (inputEl && inputEl.value !== digits) {
      inputEl.value = digits;
    }
    this.recomputeCheckoutCashReceived();
  }

  /** Allow digits and editing keys only in cash amount fields. */
  onCheckoutCashAmountKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const key = event.key;
    if (
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'Tab' ||
      key === 'Escape' ||
      key === 'Enter' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'ArrowUp' ||
      key === 'ArrowDown' ||
      key === 'Home' ||
      key === 'End'
    ) {
      return;
    }
    if (/^\d$/.test(key)) {
      return;
    }
    event.preventDefault();
  }

  onCheckoutCashDollarsPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') ?? '';
    const target = event.target as HTMLInputElement | null;
    this.onCheckoutCashDollarsInput(text, target ?? undefined);
  }

  onCheckoutCashCentsPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') ?? '';
    const target = event.target as HTMLInputElement | null;
    this.onCheckoutCashCentsInput(text, target ?? undefined);
  }

  private parseCheckoutCashDollarsInput(raw: string): number {
    const digits = String(raw ?? '').replace(/\D/g, '');
    if (!digits.length) {
      return 0;
    }
    const n = parseInt(digits, 10);
    return Number.isFinite(n) ? n : 0;
  }

  private parseCheckoutCashCentsInput(raw: string): number {
    const digits = String(raw ?? '')
      .replace(/\D/g, '')
      .slice(0, 2);
    if (!digits.length) {
      return 0;
    }
    const n = parseInt(digits, 10);
    if (!Number.isFinite(n)) {
      return 0;
    }
    return Math.max(0, Math.min(99, n));
  }

  /**
   * Start Cash entry empty (Figma: Dollars/Cents placeholders 0 / 00).
   * Staff tap denominations or type the amount — do not auto-fill grand total.
   */
  private resetCashReceivedForManualEntry(): void {
    if (
      this.notification?.type !== 'create-payment-request' ||
      this.paymentForm.method !== PaymentMethodCode.Cash
    ) {
      return;
    }
    this.paymentForm.cashReceived = '';
    this.resetCheckoutCashPanelEntryState();
  }

  /**
   * After Back → edit → save, checkout totals change — clear cash entry so staff re-enter tender.
   * Also re-open the cash drawer when Cash is still selected (returning to checkout does not
   * re-run initializePaymentForm / selectPaymentMethod).
   */
  private syncCashReceivedAfterOrderCheckoutUpdated(): void {
    if (
      this.notification?.type !== 'create-payment-request' ||
      this.paymentForm.method !== PaymentMethodCode.Cash
    ) {
      return;
    }
    this.resetCashReceivedForManualEntry();
    void this.receiptPrinterService.openCashDrawer();
  }

  openCheckoutWayPlusBenefitsModal(): void {
    this.checkoutWayPlusBenefitsModalOpen = true;
    this.logModalInteraction('checkout.wayplusBenefits.open', {
      count: this.checkoutWayPlusStaticBenefitsCount,
    });
  }

  closeCheckoutWayPlusBenefitsModal(): void {
    this.checkoutWayPlusBenefitsModalOpen = false;
    this.logModalInteraction('checkout.wayplusBenefits.close', {});
  }

  trackCheckoutWayPlusBenefitByTitle(_index: number, item: { title: string }): string {
    return item.title;
  }

  toggleCheckoutAddonDetails(event?: Event): void {
    event?.stopPropagation();
    this.checkoutAddonDetailsOpen = !this.checkoutAddonDetailsOpen;
  }

  /** `data.order` on create-payment-request after POST /order. */
  private getCheckoutOrderSnapshot(): Record<string, any> | null {
    const d = this.notification?.data as Record<string, any> | undefined;
    const o = d?.['order'];
    return o && typeof o === 'object' && !Array.isArray(o) ? (o as Record<string, any>) : null;
  }

  /** `data.discount` from POST /order. */
  private getCheckoutDiscountSnapshot(): Record<string, any> | null {
    const d = this.notification?.data as Record<string, any> | undefined;
    const x = d?.['discount'];
    return x && typeof x === 'object' && !Array.isArray(x) ? (x as Record<string, any>) : null;
  }

  private getCheckoutAddOnVariantsSnapshot(): Record<string, any>[] {
    const d = this.notification?.data as Record<string, any> | undefined;
    const raw = d?.['add_ons'] ?? d?.['add_on_variants'] ?? d?.['addOnVariants'];
    return Array.isArray(raw) ? (raw as Record<string, any>[]) : [];
  }

  /**
   * When the order-request add-on catalog or local selection state is active, checkout must
   * follow the UI — not stale `notification.data.add_on_variants` from an earlier save.
   */
  private preferOrderRequestAddonUiForCheckout(): boolean {
    if (this.notification?.type !== 'create-payment-request') {
      return false;
    }
    const uiLines = this.buildCreateOrderAddOnVariantsPayload();
    if (uiLines.length > 0) {
      return true;
    }
    if (this.isEditMode) {
      return this.orderRequestAddons.length > 0;
    }
    // Checkout view: empty UI selections must not override a synced empty snapshot.
    if (this.getCheckoutAddOnVariantsSnapshot().length === 0) {
      return false;
    }
    return (
      this.orderRequestSelectedAddonIds.size > 0 ||
      Object.keys(this.orderRequestAddonSavedCustomPrice).length > 0 ||
      Object.keys(this.orderRequestVendingSelections).length > 0
    );
  }

  private sumCreateOrderAddOnVariantsPayload(lines: CreateOrderAddOnVariantPayload[]): number {
    let sum = 0;
    for (const line of lines) {
      const unit = Number(line.add_on_custom_price);
      const cnt = Math.max(1, Math.floor(line.add_on_count) || 1);
      if (Number.isFinite(unit)) {
        sum += unit * cnt;
      }
    }
    return Math.round(Math.max(0, sum) * 100) / 100;
  }

  /** When line prices are omitted, infer add-on total from order totals (single-variant case). */
  private getCheckoutImpliedAddonTotalFromOrder(): number | null {
    if (this.areCheckoutAddonsExplicitlyCleared()) {
      return null;
    }
    const ord = this.getCheckoutOrderSnapshot();
    const lp = this.parseUsdNumber(ord?.['list_price']);
    const disc = this.parseUsdNumber(ord?.['discount']) ?? 0;
    const fp = this.parseUsdNumber(ord?.['final_price']);
    if (lp != null && fp != null) {
      const implied = fp + disc - lp;
      if (implied > 0.001) {
        return Math.round(implied * 100) / 100;
      }
    }
    return null;
  }

  /**
   * Add-ons were intentionally cleared (empty snapshot + empty UI) — do not infer addon $
   * from a stale `order.final_price` that still includes the removed lines.
   */
  private areCheckoutAddonsExplicitlyCleared(): boolean {
    if (this.notification?.type !== 'create-payment-request') {
      return false;
    }
    const d = this.notification?.data as Record<string, any> | undefined;
    if (!d) {
      return false;
    }
    const emptySnapshot =
      (Array.isArray(d['add_ons']) && d['add_ons'].length === 0) ||
      (Array.isArray(d['add_on_variants']) && d['add_on_variants'].length === 0);
    if (!emptySnapshot) {
      return false;
    }
    return this.buildCreateOrderAddOnVariantsPayload().length === 0;
  }

  /** Resolved add-on $ for checkout display (API lines, selection, or implied from order totals). */
  private getCheckoutResolvedAddonAmount(): number {
    const raw = Math.max(0, this.getCheckoutAddonAmount());
    if (this.preferOrderRequestAddonUiForCheckout()) {
      return raw;
    }
    if (raw > 0.001) {
      return raw;
    }
    if (this.isCheckoutAddonOnlyOrder()) {
      const listPrice = this.parseUsdNumber(this.getCheckoutOrderSnapshot()?.['list_price']);
      if (listPrice != null && listPrice > 0.001) {
        return Math.round(listPrice * 100) / 100;
      }
    }
    return Math.max(0, this.getCheckoutImpliedAddonTotalFromOrder() ?? 0);
  }

  private formatCheckoutApiVariantUnitPrice(v: Record<string, any>): string | null {
    const amt =
      this.parseUsdNumber(v['line_total']) ??
      this.parseUsdNumber(v['price']) ??
      this.parseUsdNumber(v['amount']);
    if (amt != null && amt > 0) {
      return this.formatCheckoutCurrency(amt);
    }
    const cp = Number(v['add_on_custom_price'] ?? v['custom_price']);
    const cnt = Math.max(1, Number(v['add_on_count']) || 1);
    if (Number.isFinite(cp) && cp > 0) {
      return this.formatCheckoutCurrency(cp * cnt);
    }
    return null;
  }

  /** Line items for expanded “Additional Service” (API first, else current selections). */
  getCheckoutAdditionalServiceLines(): { title: string; priceFormatted: string }[] {
    const uiPayload = this.buildCreateOrderAddOnVariantsPayload();
    if (this.preferOrderRequestAddonUiForCheckout() || uiPayload.length > 0) {
      const fromUi = this.getCheckoutAdditionalServiceLinesFromLocalSelection();
      if (fromUi.length > 0) {
        return fromUi;
      }
    }
    const variants = this.getCheckoutAddOnVariantsSnapshot();
    const out: { title: string; priceFormatted: string }[] = [];
    for (const v of variants) {
      const addOnName = String(v['add_on_name'] ?? 'Add-on').trim() || 'Add-on';
      const varName = String(v['add_on_variant_name'] ?? '').trim();
      const title = varName ? `${addOnName}: ${varName}` : addOnName;
      const formatted = this.formatCheckoutApiVariantUnitPrice(v);
      out.push({
        title,
        priceFormatted: formatted ?? '—',
      });
    }
    if (out.length === 1 && out[0].priceFormatted === '—') {
      const implied = this.getCheckoutImpliedAddonTotalFromOrder();
      if (implied != null) {
        out[0] = { ...out[0], priceFormatted: this.formatCheckoutCurrency(implied) };
      }
    }
    if (out.length) {
      return out;
    }
    return this.getCheckoutAdditionalServiceLinesFromLocalSelection();
  }

  /**
   * Expanded “Additional Service” detail blocks (Figma 124-29496): API rows get name / variant / qty / price.
   */
  getCheckoutAddonExpandedSections(): { cells: { label: string; value: string }[] }[] {
    const uiPayload = this.buildCreateOrderAddOnVariantsPayload();
    if (this.preferOrderRequestAddonUiForCheckout() || uiPayload.length > 0) {
      if (uiPayload.length > 0) {
        return uiPayload.map(row => {
          const names = this.resolveAddOnNamesForPersistedPayloadRow(row);
          const qty = Math.max(1, Math.floor(row.add_on_count) || 1);
          const lineTotal = Math.round(row.add_on_custom_price * qty * 100) / 100;
          return {
            cells: [
              {
                label: this.getCheckoutAddonServiceNameDetailLabelForPackageId(row.add_on_id),
                value: names.add_on_name,
              },
              { label: 'Variant', value: (names.add_on_variant_name || '').trim() || '—' },
              { label: 'Quantity', value: String(qty) },
              { label: 'Price', value: this.formatCheckoutCurrency(lineTotal) },
            ],
          };
        });
      }
    }
    const variants = this.getCheckoutAddOnVariantsSnapshot();
    if (variants.length) {
      const impliedOne =
        variants.length === 1 ? this.getCheckoutImpliedAddonTotalFromOrder() : null;
      return variants.map(v => {
        const addOnName = String(v['add_on_name'] ?? '').trim() || '—';
        const varName = String(v['add_on_variant_name'] ?? '').trim() || '—';
        const qty = Math.max(1, Number(v['add_on_count']) || 1);
        const pkgId = String(v['add_on_id'] ?? v['package_id'] ?? '').trim();
        let priceStr = this.formatCheckoutApiVariantUnitPrice(v);
        if (priceStr == null && impliedOne != null) {
          priceStr = this.formatCheckoutCurrency(impliedOne);
        }
        return {
          cells: [
            {
              label: pkgId
                ? this.getCheckoutAddonServiceNameDetailLabelForPackageId(pkgId)
                : this.getCheckoutAddonServiceNameDetailLabel(),
              value: addOnName,
            },
            { label: 'Variant', value: varName },
            { label: 'Quantity', value: String(qty) },
            { label: 'Price', value: priceStr ?? '—' },
          ],
        };
      });
    }
    return this.getCheckoutAdditionalServiceLinesFromLocalSelection().map(line => ({
      cells: [
        { label: 'Service', value: line.title },
        { label: 'Price', value: line.priceFormatted },
      ],
    }));
  }

  private getCheckoutAdditionalServiceLinesFromLocalSelection(): {
    title: string;
    priceFormatted: string;
  }[] {
    const lines: { title: string; priceFormatted: string }[] = [];
    for (const addon of this.orderRequestAddons) {
      if (addon.mode === 'choose_products') {
        if (this.getOrderRequestVendingCommittedQuantityFor(addon.id) <= 0) {
          continue;
        }
        for (const line of this.getOrderRequestVendingSelectionLinesFor(addon.id)) {
          const n = this.parseUsdNumber(line.lineTotalFormatted) ?? line.unitPrice * line.quantity;
          lines.push({
            title: `${line.name} × ${line.quantity}`,
            priceFormatted: this.formatCheckoutCurrency(n),
          });
        }
        continue;
      }
      if (addon.mode === 'multi_variant_package' && addon.packageVariants) {
        for (const v of addon.packageVariants) {
          const q = this.getOrderRequestAddonVariantCommittedQty(v.id);
          if (q <= 0) {
            continue;
          }
          const unit =
            v.mode === 'custom_price'
              ? (this.parseUsdNumber(this.orderRequestAddonSavedCustomPrice[v.id]) ?? 0)
              : (v.unitPrice ?? 0);
          const n = unit * q;
          lines.push({
            title: q > 1 ? `${addon.label}: ${v.label} × ${q}` : `${addon.label}: ${v.label}`,
            priceFormatted: this.formatCheckoutCurrency(n),
          });
        }
        continue;
      }
      if (!this.isOrderRequestAddonSelected(addon.id)) {
        continue;
      }
      if (addon.mode === 'fixed' && addon.fixedPrice) {
        lines.push({
          title: addon.label,
          priceFormatted: addon.fixedPrice.startsWith('$')
            ? addon.fixedPrice
            : this.formatCheckoutCurrency(this.parseUsdNumber(addon.fixedPrice) ?? 0),
        });
      } else if (addon.mode === 'custom_price') {
        const saved = this.orderRequestAddonSavedCustomPrice[addon.id];
        const amt = this.parseUsdNumber(saved) ?? 0;
        if (amt <= 0) {
          continue;
        }
        lines.push({
          title: addon.label,
          priceFormatted: this.formatCheckoutCurrency(amt),
        });
      }
    }
    return lines;
  }

  /** Addon $ total: API-implied or local selection. */
  private getCheckoutAddonAmountFromApiVariants(): number | null {
    const rows = this.getCheckoutAddOnVariantsSnapshot();
    if (!rows.length) {
      return null;
    }
    let sum = 0;
    let any = false;
    for (const v of rows) {
      const amt =
        this.parseUsdNumber(v['line_total']) ??
        this.parseUsdNumber(v['price']) ??
        this.parseUsdNumber(v['amount']);
      if (amt != null && amt > 0) {
        sum += amt;
        any = true;
      } else {
        const cp = Number(v['add_on_custom_price'] ?? v['custom_price']);
        const cnt = Math.max(1, Number(v['add_on_count']) || 1);
        if (Number.isFinite(cp) && cp > 0) {
          sum += cp * cnt;
          any = true;
        }
      }
    }
    if (any && sum > 0) {
      return Math.round(sum * 100) / 100;
    }
    return this.getCheckoutImpliedAddonTotalFromOrder();
  }

  onCheckoutDiscountCardClick(event: MouseEvent): void {
    const t = event.target as HTMLElement;
    if (
      t.closest('button.checkout-selectable-card__more') ||
      t.closest('.checkout-discount-details') ||
      t.closest('.checkout-addon-details')
    ) {
      return;
    }
    if (this.getCheckoutRawDiscountAmount() <= 0) {
      return;
    }
    const prevAmount = this.getCheckoutRawDiscountAmount();
    const prevId =
      this.getSelectedDiscountApiIdForCheckoutSync() ?? this.getCheckoutDiscountApiIdFromSnapshot();
    this.checkoutSummaryDiscountSelected = !this.checkoutSummaryDiscountSelected;
    if (!this.checkoutSummaryDiscountSelected && prevAmount > 0) {
      this.checkoutLastActiveDiscountAmount = prevAmount;
      this.checkoutLastActiveDiscountId = prevId;
    }
    this.requestCheckoutSelectionSync('discount');
  }

  onCheckoutDiscountCardKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    if (this.getCheckoutRawDiscountAmount() <= 0) {
      return;
    }
    const t = event.target as HTMLElement;
    if (
      t.closest('button.checkout-selectable-card__more') ||
      t.closest('.checkout-discount-details') ||
      t.closest('.checkout-addon-details')
    ) {
      return;
    }
    const prevAmount = this.getCheckoutRawDiscountAmount();
    const prevId =
      this.getSelectedDiscountApiIdForCheckoutSync() ?? this.getCheckoutDiscountApiIdFromSnapshot();
    this.checkoutSummaryDiscountSelected = !this.checkoutSummaryDiscountSelected;
    if (!this.checkoutSummaryDiscountSelected && prevAmount > 0) {
      this.checkoutLastActiveDiscountAmount = prevAmount;
      this.checkoutLastActiveDiscountId = prevId;
    }
    this.requestCheckoutSelectionSync('discount');
  }

  /** Raw add-on total for checkout (before include-in-bill toggle). */
  getCheckoutRawAddonAmount(): number {
    return Math.max(0, this.getCheckoutAddonAmount());
  }

  onCheckoutAddonCardClick(event: MouseEvent): void {
    const t = event.target as HTMLElement;
    if (
      t.closest('button.checkout-selectable-card__more') ||
      t.closest('.checkout-addon-details') ||
      t.closest('.checkout-discount-details')
    ) {
      return;
    }
    if (this.getCheckoutRawAddonAmount() <= 0) {
      return;
    }
    const prevAddons = this.getCheckoutSelectedAddOnVariantsForSync();
    this.checkoutSummaryAddonSelected = !this.checkoutSummaryAddonSelected;
    if (!this.checkoutSummaryAddonSelected && prevAddons.length > 0) {
      this.checkoutLastActiveAddOnVariants = this.cloneCreateOrderAddOnVariantLines(prevAddons);
    }
    if (!this.checkoutSummaryAddonSelected) {
      this.checkoutAddonDetailsOpen = false;
    }
    this.requestCheckoutSelectionSync('addon');
  }

  onCheckoutAddonCardKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    if (this.getCheckoutRawAddonAmount() <= 0) {
      return;
    }
    const t = event.target as HTMLElement;
    if (
      t.closest('button.checkout-selectable-card__more') ||
      t.closest('.checkout-addon-details') ||
      t.closest('.checkout-discount-details')
    ) {
      return;
    }
    const prevAddons = this.getCheckoutSelectedAddOnVariantsForSync();
    this.checkoutSummaryAddonSelected = !this.checkoutSummaryAddonSelected;
    if (!this.checkoutSummaryAddonSelected && prevAddons.length > 0) {
      this.checkoutLastActiveAddOnVariants = this.cloneCreateOrderAddOnVariantLines(prevAddons);
    }
    if (!this.checkoutSummaryAddonSelected) {
      this.checkoutAddonDetailsOpen = false;
    }
    this.requestCheckoutSelectionSync('addon');
  }

  /**
   * Persist checkout include/exclude toggles (discount/add-ons) to backend immediately.
   */
  private requestCheckoutSelectionSync(scope: 'discount' | 'addon'): void {
    // CWS-1945: a draft has no order to PUT; the toggle is applied when the attendant pays.
    if (this.notification?.type === 'create-payment-request' && this.checkoutDraftPayload) {
      this.checkoutDraftTouchedScopes.add(scope);
      return;
    }
    if (this.notification?.type !== 'create-payment-request' || !this.formData.orderId) {
      return;
    }

    if (this.isCheckoutSelectionSyncInFlight) {
      this.pendingCheckoutSelectionSyncScopes.add(scope);
      return;
    }

    const scopes = new Set<'discount' | 'addon'>([scope]);
    this.isCheckoutSelectionSyncInFlight = true;
    this.putOrderCheckoutSelectionFields(scopes)
      .catch(error => {
        console.error('Failed to sync checkout selection state:', error);
      })
      .finally(() => {
        this.isCheckoutSelectionSyncInFlight = false;
        if (this.pendingCheckoutSelectionSyncScopes.size > 0) {
          const nextScopes = new Set(this.pendingCheckoutSelectionSyncScopes);
          this.pendingCheckoutSelectionSyncScopes.clear();
          this.isCheckoutSelectionSyncInFlight = true;
          this.putOrderCheckoutSelectionFields(nextScopes)
            .catch(error => {
              console.error('Failed to sync queued checkout selection state:', error);
            })
            .finally(() => {
              this.isCheckoutSelectionSyncInFlight = false;
            });
        }
      });
  }

  /**
   * Checkbox-only sync for checkout page:
   * update only fields controlled by discount/add-on checkboxes (+ final_price).
   */
  private putOrderCheckoutSelectionFields(scopes: Set<'discount' | 'addon'>): Promise<any> {
    return firstValueFrom(this.ensureOrderRequestCustomDiscountId$()).then(
      () =>
        new Promise((resolve, reject) => {
          if (!this.formData.orderId) {
            reject(new Error('Order ID is required'));
            return;
          }

          const listPriceSel = this.computeOrderRequestListPriceForApi();
          const payload: Record<string, any> = {
            final_price: this.computeOrderRequestFinalPrice(),
            ...(listPriceSel != null ? { list_price: listPriceSel } : {}),
          };

          if (scopes.has('discount')) {
            const selection = this.getCheckoutDiscountSelectionForSync();
            payload['discount'] = selection.amount;
            payload['discount_id'] = selection.discountId;
          }
          if (scopes.has('addon')) {
            payload['add_on_variants'] = this.getCheckoutAddOnSelectionForSync();
          }

          this.apiService
            .put(`order/${this.formData.orderId}`, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: response => {
                this.applyOrderCheckoutTotalsFromPutResponse(response);
                resolve(response);
              },
              error: error => reject(error),
            });
        })
    );
  }

  /** Discount the checkout summary toggle selects (amount 0 / no id when it is turned off). */
  private getCheckoutDiscountSelectionForSync(): { amount: number; discountId: string | null } {
    if (!this.checkoutSummaryDiscountSelected) {
      return { amount: 0, discountId: null };
    }
    const computedAmount = Math.max(0, this.getCheckoutSelectedDiscountAmountForSync());
    return {
      amount:
        computedAmount > 0
          ? computedAmount
          : Math.max(0, this.checkoutLastActiveDiscountAmount ?? 0),
      discountId:
        this.getSelectedDiscountApiIdForCheckoutSync() ??
        this.getCheckoutDiscountApiIdFromSnapshot() ??
        this.checkoutLastActiveDiscountId,
    };
  }

  /** Add-on lines the checkout summary toggle selects (none when it is turned off). */
  private getCheckoutAddOnSelectionForSync(): CreateOrderAddOnVariantPayload[] {
    if (!this.checkoutSummaryAddonSelected) {
      return [];
    }
    const selectedAddons = this.getCheckoutSelectedAddOnVariantsForSync();
    if (selectedAddons.length > 0) {
      this.checkoutLastActiveAddOnVariants = this.cloneCreateOrderAddOnVariantLines(selectedAddons);
      return selectedAddons;
    }
    return this.cloneCreateOrderAddOnVariantLines(this.checkoutLastActiveAddOnVariants);
  }

  private getSelectedDiscountApiIdForCheckoutSync(): string | null {
    const sel = String(this.orderRequestSelectedDiscountId || '').trim();
    if (!sel) {
      return null;
    }
    if (sel === 'disc_custom') {
      const customId = String(this.orderRequestCustomDiscountSaved?.id || '').trim();
      return customId || null;
    }
    if (sel.startsWith('disc_')) {
      const id = sel.slice('disc_'.length).trim();
      return id || null;
    }
    return sel;
  }

  private getCheckoutDiscountApiIdFromSnapshot(): string | null {
    const snap = this.getCheckoutDiscountSnapshot();
    const ord = this.getCheckoutOrderSnapshot();
    const candidates = [
      snap?.['discount_id'],
      snap?.['discountId'],
      snap?.['id'],
      ord?.['discount_id'],
      ord?.['discountId'],
    ];
    for (const c of candidates) {
      if (c == null) {
        continue;
      }
      const raw = String(c).trim();
      if (!raw) {
        continue;
      }
      return raw.startsWith('disc_') ? raw.slice('disc_'.length).trim() || null : raw;
    }
    return null;
  }

  private getCheckoutSelectedDiscountAmountForSync(): number {
    const data = this.notification?.data as
      | {
          order?: {
            list_price?: string | number;
            discount?: string | number;
          };
        }
      | undefined;
    const order = data?.order;
    const addon = this.getCheckoutAddonAmount();
    const fromPkg = this.getCheckoutListPriceFromPackage();
    const subtotal =
      fromPkg ??
      this.parseUsdNumber(order?.list_price) ??
      this.parseUsdNumber(this.formData.price) ??
      0;
    const preDisc = subtotal + addon;

    if (
      this.orderRequestSelectedDiscountId === 'disc_custom' &&
      this.orderRequestCustomDiscountSaved
    ) {
      return Math.max(0, this.computeOrderRequestCustomDiscountAmount(preDisc));
    }

    const selected = String(this.orderRequestSelectedDiscountId || '').trim();
    if (selected && selected !== 'disc_custom') {
      const apiDisc =
        this.orderRequestDiscounts.find(d => d.id === selected) ??
        this.orderRequestDiscounts.find(d => d.id === `disc_${selected}`);
      if (apiDisc?.mode === 'fixed') {
        return Math.max(0, this.computeOrderRequestApiDiscountAmount(preDisc, apiDisc));
      }
    }

    const snap = this.getCheckoutDiscountSnapshot();
    const fromSnap =
      this.parseUsdNumber(snap?.['discount']) ??
      this.parseUsdNumber(snap?.['discount_value']) ??
      this.parseUsdNumber(order?.discount) ??
      0;
    return Math.max(0, fromSnap);
  }

  private cloneCreateOrderAddOnVariantLines(
    lines: CreateOrderAddOnVariantPayload[] | undefined | null
  ): CreateOrderAddOnVariantPayload[] {
    if (!Array.isArray(lines) || !lines.length) {
      return [];
    }
    return lines.map(line => ({
      add_on_id: line.add_on_id,
      add_on_variant_id: line.add_on_variant_id,
      add_on_custom_price: line.add_on_custom_price,
      add_on_count: line.add_on_count,
    }));
  }

  private getCheckoutSelectedAddOnVariantsForSync(): CreateOrderAddOnVariantPayload[] {
    const fromUi = this.buildCreateOrderAddOnVariantsPayload();
    if (this.preferOrderRequestAddonUiForCheckout() || fromUi.length > 0) {
      return fromUi;
    }

    const fromSnapshot = this.getCheckoutAddOnVariantsSnapshot();
    const lines: CreateOrderAddOnVariantPayload[] = [];
    for (const row of fromSnapshot) {
      const addOnId = String(
        row['add_on_id'] ?? row['addon_id'] ?? row['addonId'] ?? row['package_id'] ?? ''
      ).trim();
      if (!addOnId) {
        continue;
      }
      const addOnVariantId = String(
        row['add_on_variant_id'] ?? row['variant_id'] ?? row['addOnVariantId'] ?? ''
      ).trim();
      const count = Math.max(
        1,
        Math.floor(Number(row['add_on_count'] ?? row['count'] ?? row['quantity'])) || 1
      );
      const explicitUnit =
        this.parseUsdNumber(row['add_on_custom_price']) ??
        this.parseUsdNumber(row['custom_price']) ??
        this.parseUsdNumber(row['price']) ??
        this.parseUsdNumber(row['amount']);
      const fallbackLineTotal = this.parseUsdNumber(row['line_total']);
      let unit = explicitUnit;
      if (
        (unit == null || !Number.isFinite(unit)) &&
        fallbackLineTotal != null &&
        Number.isFinite(fallbackLineTotal)
      ) {
        unit = fallbackLineTotal / Math.max(1, count);
      }
      lines.push({
        add_on_id: addOnId,
        add_on_variant_id: addOnVariantId,
        add_on_custom_price: Math.round(Math.max(0, unit ?? 0) * 100) / 100,
        add_on_count: count,
      });
    }
    return lines;
  }

  /** Discount amount from order / custom editor before checkout apply/remove toggle. */
  getCheckoutRawDiscountAmount(): number {
    const data = this.notification?.data as
      | {
          order?: {
            list_price?: string | number;
            discount?: string | number;
          };
        }
      | undefined;
    const order = data?.order;
    const ordSnap = this.getCheckoutOrderSnapshot();
    if (
      this.notification?.type === 'create-payment-request' &&
      ordSnap &&
      ordSnap['discount'] != null &&
      String(ordSnap['discount']).trim() !== ''
    ) {
      const d = this.parseUsdNumber(ordSnap['discount']);
      if (d != null) {
        return Math.max(0, d);
      }
    }
    const addon = this.getCheckoutAddonAmount();
    const fromPkg = this.getCheckoutListPriceFromPackage();
    const subtotal =
      fromPkg ??
      this.parseUsdNumber(order?.list_price) ??
      this.parseUsdNumber(this.formData.price) ??
      0;
    const preDisc = subtotal + addon;
    let discount = this.parseUsdNumber(order?.discount) ?? 0;
    if (
      this.orderRequestSelectedDiscountId === 'disc_custom' &&
      this.orderRequestCustomDiscountSaved
    ) {
      discount = this.computeOrderRequestCustomDiscountAmount(preDisc);
    } else if (
      this.orderRequestSelectedDiscountId &&
      this.orderRequestSelectedDiscountId !== 'disc_custom'
    ) {
      const apiDisc = this.orderRequestDiscounts.find(
        d => d.id === this.orderRequestSelectedDiscountId
      );
      if (apiDisc?.mode === 'fixed') {
        discount = this.computeOrderRequestApiDiscountAmount(preDisc, apiDisc);
      }
    }
    return Math.max(0, discount);
  }

  private parseUsdNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const s = String(value)
      .replace(/[$,%\s]/g, '')
      .replace(/,/g, '');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }

  getCheckoutPackageTitle(): string {
    if (this.isCheckoutPrepaidPurchase()) {
      const book = this.getOrderRequestSelectedPrepaidBook();
      if (book?.name?.trim()) {
        return book.name.trim();
      }
      const ord = this.getCheckoutOrderSnapshot();
      const prepaidName = ord?.['prepaid_wash_book_name'];
      if (typeof prepaidName === 'string' && prepaidName.trim()) {
        return prepaidName.trim();
      }
    }
    const ord = this.getCheckoutOrderSnapshot();
    const pn = ord?.['package_name'];
    if (typeof pn === 'string' && pn.trim()) {
      return pn.trim();
    }
    const name = (this.formData.package || '').trim();
    if (name) {
      return name;
    }
    return this.selectedPackage?.package_name || 'Car wash package';
  }

  getCheckoutWashPlanLine(): string {
    if (this.isCheckoutPrepaidPurchase()) {
      return 'Prepaid';
    }
    if (!this.isCheckoutMonthlyMembership()) {
      return 'Single wash';
    }
    const pkg = this.selectedPackage;
    const ord = this.getCheckoutOrderSnapshot();
    return OrderUtils.formatMonthlyPlanWashTypeLine(
      pkg?.wash_count_period ?? ord?.['wash_count_period'],
      pkg?.wash_count_frequency ?? ord?.['wash_count_frequency']
    );
  }

  getMonthlyWashCardLabel(expandedPackage: ExpandedPackage | null | undefined): string {
    const pkg = expandedPackage?.package;
    return formatMembershipAllowance(pkg?.wash_count_period, pkg?.wash_count_frequency);
  }

  isMembershipLimitReached(): boolean {
    return OrderUtils.isMembershipLimitReachedPayload(this.notification);
  }

  private membershipStatusStripSource: unknown = undefined;
  private membershipStatusStripCache: MembershipStatusStripView | null = null;

  /**
   * CWS-1650: status strip for a refused member or a used-up allowance, from the backend
   * `refusal_detail` (live payload, or the LPR row's snapshot on reload). Null keeps the
   * card's sentence. Memoised on the detail object: read on every change detection.
   */
  get membershipStatusStrip(): MembershipStatusStripView | null {
    const detail = refusalDetailOf(this.notification);
    if (detail !== this.membershipStatusStripSource) {
      this.membershipStatusStripSource = detail;
      this.membershipStatusStripCache = buildMembershipStatusStrip(detail);
    }
    return this.membershipStatusStripCache;
  }

  getMembershipLimitReachedMessage(): string {
    return OrderUtils.membershipLimitReachedMessage(this.notification);
  }

  isCheckoutAddonOnlyOrder(): boolean {
    return this.isAddonOnlyWashContext();
  }

  shouldShowCheckoutServiceCard(): boolean {
    return (
      this.hasOrderRequestWashPackageForCheckout() &&
      this.getPaymentCheckoutBreakdown().subtotal > 0.001 &&
      !this.isCheckoutAddonOnlyOrder() &&
      !this.isCheckoutPrepaidPurchase()
    );
  }

  private isPrepaidWashBookWashType(washType: string | null | undefined): boolean {
    const normalized = String(washType ?? '')
      .toLowerCase()
      .trim()
      .replace(/[\s-]+/g, '_');
    return normalized === 'prepaid_wash_book';
  }

  isCheckoutPrepaidPurchase(): boolean {
    if (this.orderRequestSelectedPrepaidBookId) {
      return true;
    }
    const ord = this.getCheckoutOrderSnapshot();
    if (String(ord?.['prepaid_wash_book_id'] ?? '').trim()) {
      return true;
    }
    const d = this.notification?.data as Record<string, any> | undefined;
    if (String(d?.['prepaid_wash_book_id'] ?? d?.['prepaidWashBookId'] ?? '').trim()) {
      return true;
    }
    const raw = this.resolveRawCheckoutWashType();
    return this.isPrepaidWashBookWashType(raw);
  }

  getCheckoutPrepaidSummary(): PrepaidCheckoutSummaryViewModel | null {
    if (!this.isCheckoutPrepaidPurchase()) {
      return null;
    }
    const ord = this.getCheckoutOrderSnapshot();
    const d = this.notification?.data as Record<string, any> | undefined;
    const titleFallback =
      (typeof ord?.['prepaid_wash_book_name'] === 'string' ? ord['prepaid_wash_book_name'] : '') ||
      (typeof d?.['prepaid_wash_book_name'] === 'string' ? d['prepaid_wash_book_name'] : '') ||
      (this.formData.package || '').trim() ||
      null;
    const prepaidList = this.resolvePrepaidListPriceForCheckout();
    return buildPrepaidCheckoutSummaryViewModel({
      book: this.resolveCheckoutPrepaidBookForSummary(),
      titleFallback,
      purchaseDate: this.getCheckoutPrepaidPurchaseDate(),
      vehicleSummary: this.getCheckoutVehicleSummary(),
      priceFormatted:
        prepaidList != null
          ? this.formatCheckoutCurrency(prepaidList)
          : this.formatCheckoutServicePrice(),
    });
  }

  shouldShowCheckoutWayPlusBanner(): boolean {
    return (
      !this.isCheckoutPrepaidPurchase() &&
      this.getCheckoutFreeBenefitsCount() > 0 &&
      this.isCheckoutMonthlyMembership()
    );
  }

  ensureCheckoutPrepaidBookLoadedForSummary(): void {
    if (!this.isCheckoutPrepaidPurchase()) {
      return;
    }
    const bookId = this.getCheckoutPrepaidBookIdForCheckout();
    if (!bookId) {
      return;
    }
    if (this.resolveCheckoutPrepaidBookForSummary()) {
      this.refreshCheckoutPrepaidPriceFromCatalogOrSnapshot();
      return;
    }
    if (this.isLoadingOrderRequestPrepaid) {
      return;
    }
    this.isLoadingOrderRequestPrepaid = true;
    const loc = String(this.formData.locationId ?? '').trim();
    this.prepaidWashBookService
      .getPrepaidWashBooks({
        status: true,
        page: 1,
        limit: 50,
        ...(loc ? { location_id: loc } : {}),
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingOrderRequestPrepaid = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: ({ books }) => {
          this.orderRequestPrepaidBooks = books ?? [];
          this.refreshCheckoutPrepaidPriceFromCatalogOrSnapshot();
        },
        error: err => {
          console.error('Error loading prepaid wash books for checkout summary:', err);
        },
      });
  }

  /** After prepaid catalog/snapshot is available, align prepaid list_price for checkout display. */
  private refreshCheckoutPrepaidPriceFromCatalogOrSnapshot(): void {
    if (!this.isCheckoutPrepaidPurchase()) {
      return;
    }
    const price = this.resolvePrepaidListPriceForCheckout();
    if (price == null || price <= 0) {
      return;
    }
    this.formData.price = price.toFixed(2);
    if (this.notification?.type !== 'create-payment-request' || !this.notification.data) {
      return;
    }
    const data = this.notification.data as Record<string, any>;
    const prevOrder =
      data['order'] && typeof data['order'] === 'object' && !Array.isArray(data['order'])
        ? ({ ...(data['order'] as Record<string, any>) } as Record<string, any>)
        : {};
    prevOrder['list_price'] = this.formatCheckoutCurrency(price);
    data['order'] = prevOrder;
    this.cdr.detectChanges();
  }

  private resolvePersistedPrepaidWashBookId(orderLike?: Record<string, any> | null): string | null {
    const d = this.notification?.data as Record<string, any> | undefined;
    const nestedOrder =
      d?.['order'] && typeof d['order'] === 'object' && !Array.isArray(d['order'])
        ? (d['order'] as Record<string, any>)
        : null;
    const candidates = [
      orderLike?.['prepaid_wash_book_id'],
      orderLike?.['prepaidWashBookId'],
      this.getCheckoutOrderSnapshot()?.['prepaid_wash_book_id'],
      d?.['prepaid_wash_book_id'],
      d?.['prepaidWashBookId'],
      nestedOrder?.['prepaid_wash_book_id'],
    ];
    for (const candidate of candidates) {
      const id = String(candidate ?? '').trim();
      if (id) {
        return id;
      }
    }
    return null;
  }

  private resolvePersistedPrepaidWashBookName(
    orderLike?: Record<string, any> | null
  ): string | null {
    const d = this.notification?.data as Record<string, any> | undefined;
    const nestedOrder =
      d?.['order'] && typeof d['order'] === 'object' && !Array.isArray(d['order'])
        ? (d['order'] as Record<string, any>)
        : null;
    const candidates = [
      orderLike?.['prepaid_wash_book_name'],
      orderLike?.['prepaidWashBookName'],
      this.getCheckoutOrderSnapshot()?.['prepaid_wash_book_name'],
      d?.['prepaid_wash_book_name'],
      d?.['prepaidWashBookName'],
      nestedOrder?.['prepaid_wash_book_name'],
    ];
    for (const candidate of candidates) {
      const name = String(candidate ?? '').trim();
      if (name) {
        return name;
      }
    }
    return null;
  }

  /** Restore prepaid tile selection from GET order / notification snapshot (Back → edit). */
  private restoreOrderRequestPrepaidSelectionFromPersistedSources(
    orderLike?: Record<string, any> | null
  ): boolean {
    if (this.orderRequestSelectedPrepaidBookId) {
      return true;
    }

    const id = this.resolvePersistedPrepaidWashBookId(orderLike);
    if (!id) {
      return false;
    }

    this.orderRequestSelectedPrepaidBookId = id;
    this.selectedPackage = null;
    this.selectedWashType = null;
    this.formData.packageId = '';

    const name = this.resolvePersistedPrepaidWashBookName(orderLike);
    if (name) {
      this.formData.package = name;
    }

    const book = this.resolveCheckoutPrepaidBookForSummary();
    if (book) {
      const price = this.parsePrepaidBookSellingPrice(book);
      this.formData.price = price != null ? price.toFixed(2) : '';
    } else {
      const priceFromOrder =
        this.parseUsdNumber(orderLike?.['list_price']) ??
        this.parseUsdNumber(this.getCheckoutOrderSnapshot()?.['list_price']);
      if (priceFromOrder != null && Number.isFinite(priceFromOrder) && priceFromOrder > 0) {
        this.formData.price = priceFromOrder.toFixed(2);
      }
    }

    this.syncOrderRequestDiscountStateForFleetAndPrepaid();
    this.syncOrderRequestAddonSelectionToPairedWith();
    this.rebuildMostUsedServiceItems();
    return true;
  }

  private restoreCheckoutPrepaidSelectionFromOrderSnapshot(): void {
    this.restoreOrderRequestPrepaidSelectionFromPersistedSources(this.getCheckoutOrderSnapshot());
  }

  /** Repopulate add-on UI from notification.data after populateFormData cleared in-memory selection. */
  private restoreCheckoutAddonSelectionFromOrderSnapshot(): void {
    if (this.notification?.type !== 'create-payment-request' || this.isEditMode) {
      return;
    }
    const lines = this.getCheckoutAddOnVariantsSnapshot();
    if (!lines.length || !this.orderRequestAddons.length) {
      return;
    }
    if (this.orderRequestSelectedAddonIds.size > 0) {
      return;
    }
    this.applyPersistedAddonLines(lines);
  }

  private resolveRawCheckoutWashType(): string {
    if (this.orderRequestSelectedPrepaidBookId) {
      return 'prepaid_wash_book';
    }
    const ord = this.getCheckoutOrderSnapshot();
    const fromOrd = ord?.['wash_type'];
    if (fromOrd != null && String(fromOrd).trim()) {
      return String(fromOrd).trim();
    }
    if (this.cachedOrderWashType) {
      return this.cachedOrderWashType;
    }
    const d = this.notification?.data as
      { wash_type?: string; order?: { wash_type?: string } } | undefined;
    return String(d?.wash_type ?? d?.order?.wash_type ?? '').trim();
  }

  private getCheckoutPrepaidBookIdForCheckout(): string | null {
    const fromSelection = (this.orderRequestSelectedPrepaidBookId || '').trim();
    if (fromSelection) {
      return fromSelection;
    }
    const ord = this.getCheckoutOrderSnapshot();
    const fromOrder = String(ord?.['prepaid_wash_book_id'] ?? '').trim();
    if (fromOrder) {
      return fromOrder;
    }
    const d = this.notification?.data as Record<string, any> | undefined;
    const fromTop = String(d?.['prepaid_wash_book_id'] ?? d?.['prepaidWashBookId'] ?? '').trim();
    return fromTop || null;
  }

  private resolveCheckoutPrepaidBookForSummary(): PrepaidWashBook | null {
    const selected = this.getOrderRequestSelectedPrepaidBook();
    if (selected) {
      return selected;
    }
    const bookId = this.getCheckoutPrepaidBookIdForCheckout();
    if (!bookId) {
      return null;
    }
    return this.orderRequestPrepaidBooks.find(b => b.id === bookId) ?? null;
  }

  private getCheckoutPrepaidPurchaseDate(): Date {
    const ord = this.getCheckoutOrderSnapshot();
    const candidates = [
      this.formData.appointmentTime,
      ord?.['appointment_time'],
      ord?.['created_at'],
      ord?.['order_created_at'],
    ];
    for (const candidate of candidates) {
      if (candidate == null || String(candidate).trim() === '') {
        continue;
      }
      const d = new Date(String(candidate));
      if (!Number.isNaN(d.getTime())) {
        return d;
      }
    }
    return new Date();
  }

  getCheckoutDateRangeLine(): string {
    const ord = this.getCheckoutOrderSnapshot();
    const timeValue = ord?.['appointment_time'] || this.formData.appointmentTime;
    if (!timeValue) {
      return '';
    }
    // Prefer order.timezone from POST|PUT /order so checkout matches the location shop TZ.
    const orderTimezone =
      typeof ord?.['timezone'] === 'string' ? String(ord['timezone']).trim() : '';
    return formatDateTimeInTimeZone(String(timeValue), orderTimezone || getUserTimezone()).trim();
  }

  getCheckoutVehicleSummary(): string | null {
    const ord = this.getCheckoutOrderSnapshot();
    let y = (this.formData.vehicleYear || '').trim();
    let mk = (this.formData.vehicleMake || '').trim();
    let md = (this.formData.vehicleModel || '').trim();
    let plate = (this.formData.vehicleLicensePlate || '').trim();
    if (ord) {
      if (!y && ord['year'] != null) {
        y = String(ord['year']).trim();
      }
      if (!mk && typeof ord['make'] === 'string') {
        mk = ord['make'].trim();
      }
      if (!md && typeof ord['model'] === 'string') {
        md = ord['model'].trim();
      }
      if (!plate && typeof ord['license_plate'] === 'string') {
        plate = ord['license_plate'].trim();
      }
    }
    if (!y && !mk && !md && !plate) {
      return null;
    }
    const vehiclePart = [y, mk, md].filter(Boolean).join(' ');
    if (vehiclePart && plate) {
      return `${vehiclePart} · ${plate}`;
    }
    return vehiclePart || plate || null;
  }

  isCheckoutMonthlyMembership(): boolean {
    const wtRaw =
      this.cachedOrderWashType ||
      (this.notification?.data as { wash_type?: string; order?: { wash_type?: string } })
        ?.wash_type ||
      (this.notification?.data as { order?: { wash_type?: string } })?.order?.wash_type ||
      this.selectedWashType ||
      '';
    return this.normalizeWashType(wtRaw) === 'monthly_membership';
  }

  getCheckoutFreeBenefitsCount(): number {
    return this.selectedPackage?.features?.length ?? 0;
  }

  formatCheckoutCurrency(amount: number): string {
    return formatCheckoutCurrencyUtil(amount);
  }

  /** List / card price display: adds `$` and normalizes `10`, `10.00`, `$10` → `$10` / `$10.00`. */
  formatUsdPriceForOrderRequestDisplay(raw: string | null | undefined): string {
    const n = this.parseUsdNumber(raw);
    return n != null ? this.formatCheckoutCurrency(n) : '';
  }

  formatCheckoutServicePrice(): string {
    return this.formatCheckoutCurrency(this.getPaymentCheckoutBreakdown().subtotal);
  }

  getCheckoutSubtotalLineLabel(): string {
    if (this.isCheckoutAddonOnlyOrder()) {
      return 'Subtotal (Add on products)';
    }
    if (this.isCheckoutPrepaidPurchase()) {
      const summary = this.getCheckoutPrepaidSummary();
      if (summary) {
        return summary.subtotalLabel;
      }
    }
    const plan = this.isCheckoutMonthlyMembership()
      ? this.getCheckoutWashPlanLine()
      : 'Single wash';
    return `Subtotal (${plan} ${this.getCheckoutPackageTitle()})`;
  }

  getCheckoutAddonAmount(): number {
    const uiLines = this.buildCreateOrderAddOnVariantsPayload();
    if (this.preferOrderRequestAddonUiForCheckout() || uiLines.length > 0) {
      const fromUi = this.sumCreateOrderAddOnVariantsPayload(uiLines);
      if (fromUi > 0.001) {
        return fromUi;
      }
    }
    const fromApi = this.getCheckoutAddonAmountFromApiVariants();
    if (fromApi != null && fromApi > 0) {
      return fromApi;
    }
    const implied = this.getCheckoutImpliedAddonTotalFromOrder();
    if (implied != null && implied > 0) {
      return implied;
    }
    return this.getCheckoutVendingCombinedAmount() + this.getCheckoutNonVendingAddonAmount();
  }

  /** Selected API add-ons (custom saved price or fixed list price), excluding product-picker rows. */
  private getCheckoutNonVendingAddonAmount(): number {
    let total = 0;
    for (const addon of this.orderRequestAddons) {
      if (addon.mode === 'choose_products') {
        continue;
      }
      if (addon.mode === 'multi_variant_package' && addon.packageVariants) {
        for (const v of addon.packageVariants) {
          const q = this.getOrderRequestAddonVariantCommittedQty(v.id);
          if (q <= 0) {
            continue;
          }
          const unit =
            v.mode === 'custom_price'
              ? (this.parseUsdNumber(this.orderRequestAddonSavedCustomPrice[v.id]) ?? 0)
              : (v.unitPrice ?? 0);
          total += unit * q;
        }
        continue;
      }
      if (!this.isOrderRequestAddonSelected(addon.id)) {
        continue;
      }
      if (addon.mode === 'fixed' && addon.fixedPrice) {
        total += this.parseUsdNumber(addon.fixedPrice) ?? 0;
      } else if (addon.mode === 'custom_price') {
        const saved = this.orderRequestAddonSavedCustomPrice[addon.id];
        total += this.parseUsdNumber(saved) ?? 0;
      }
    }
    return total;
  }

  getCheckoutDiscountCardTitle(): string {
    const snap = this.getCheckoutDiscountSnapshot();
    const n = typeof snap?.['name'] === 'string' ? snap['name'].trim() : '';
    if (n) {
      return n;
    }
    if (this.orderRequestSelectedDiscountId === 'disc_custom') {
      const cn = this.orderRequestCustomDiscountSaved?.name?.trim();
      if (cn) {
        return cn;
      }
      return 'Custom discount';
    }
    const api = this.orderRequestDiscounts.find(d => d.id === this.orderRequestSelectedDiscountId);
    if (api?.label) {
      return api.label;
    }
    return 'Discount';
  }

  /**
   * The configured rate of a percentage discount, or null when it is not one.
   *
   * Reads `discount_value` (the configured 80), never the resolved amount, and trims a trailing
   * `.00` so the badge reads `80% OFF` rather than `80.00% OFF`.
   */
  private percentOffFromDiscountSnapshot(snap: Record<string, any> | null): number | null {
    if (!snap) {
      return null;
    }
    const type = String(snap['discount_type'] ?? '').toLowerCase();
    if (type !== 'percentage' && type !== 'percent') {
      return null;
    }
    const value =
      this.parseUsdNumber(snap['discount_value']) ??
      parseFloat(String(snap['discount_value'] ?? ''));
    if (!Number.isFinite(value) || value <= 0) {
      return null;
    }
    return parseFloat(value.toFixed(2));
  }

  getCheckoutDiscountCardValueLabel(): string {
    const snap = this.getCheckoutDiscountSnapshot();
    const raw = this.getCheckoutRawDiscountAmount();
    if (snap && raw > 0) {
      // A percentage discount must show its rate, not the resolved money labelled "Flat" —
      // an 80% discount on $200 read as "Flat $160 OFF".
      const pct = this.percentOffFromDiscountSnapshot(snap);
      if (pct !== null) {
        return `${pct}% OFF`;
      }
      const n = raw % 1 === 0 ? String(Math.round(raw)) : raw.toFixed(2);
      return `Flat $${n} OFF`;
    }
    if (
      this.orderRequestSelectedDiscountId === 'disc_custom' &&
      this.orderRequestCustomDiscountSaved
    ) {
      return this.orderRequestCustomDiscountSaved.valueLabel;
    }
    const api = this.orderRequestDiscounts.find(d => d.id === this.orderRequestSelectedDiscountId);
    if (api?.mode === 'fixed' && api.fixedPrice) {
      return api.fixedPrice;
    }
    if (raw > 0) {
      const n = raw % 1 === 0 ? String(Math.round(raw)) : raw.toFixed(2);
      return `Flat $${n} OFF`;
    }
    return '—';
  }

  getCheckoutAddonSummary(): { label: string; priceFormatted: string } | null {
    const lines = this.getCheckoutAdditionalServiceLines();
    let total = this.getCheckoutAddonAmount();
    if (total <= 0 && lines.length) {
      let s = 0;
      for (const L of lines) {
        const n = this.parseUsdNumber(L.priceFormatted);
        if (n != null) {
          s += n;
        }
      }
      if (s > 0) {
        total = s;
      }
    }
    if (total <= 0 && !lines.length) {
      return null;
    }
    if (lines.length === 1) {
      return { label: lines[0].title, priceFormatted: this.formatCheckoutCurrency(total) };
    }
    if (lines.length > 1) {
      return { label: 'Additional services', priceFormatted: this.formatCheckoutCurrency(total) };
    }
    const nonVending = this.getCheckoutNonVendingAddonAmount();
    const vendingTotal = this.getCheckoutVendingCombinedAmount();
    const vendingQty = this.getCheckoutAllVendingCommittedQuantity();
    if (vendingTotal > 0 && vendingQty > 0 && nonVending > 0) {
      return {
        label: `${this.getCheckoutCombinedVendingSummaryLabel()} + add-ons`,
        priceFormatted: this.formatCheckoutCurrency(total),
      };
    }
    if (vendingTotal > 0 && vendingQty > 0) {
      return {
        label: this.getCheckoutCombinedVendingSummaryLabel(),
        priceFormatted: this.formatCheckoutCurrency(vendingTotal),
      };
    }
    const labels = this.orderRequestAddons
      .filter(a => a.mode !== 'choose_products' && this.isOrderRequestAddonSelected(a.id))
      .map(a => {
        if (a.mode === 'multi_variant_package' && a.packageVariants) {
          const selected = a.packageVariants.filter(v =>
            this.isOrderRequestAddonVariantLineCommitted(v)
          );
          if (!selected.length) {
            return '';
          }
          if (selected.length === 1) {
            const q = this.getOrderRequestAddonVariantCommittedQty(selected[0].id);
            const sub = q > 1 ? `${selected[0].label} × ${q}` : selected[0].label;
            return `${a.label} (${sub})`;
          }
          return `${a.label} (${selected.length} variants)`;
        }
        return a.label;
      })
      .filter(s => !!s);
    const kinds = this.getOrderRequestCommittedAddonPackageKinds();
    const pluralFallback =
      kinds.size > 1
        ? 'Additional services'
        : kinds.has('detailing')
          ? 'Detailing services'
          : kinds.has('add_on')
            ? 'Add-on services'
            : 'Additional services';
    const label = labels.length === 1 ? labels[0] : pluralFallback;
    return { label, priceFormatted: this.formatCheckoutCurrency(total) };
  }

  private getCheckoutListPriceFromPackage(): number | null {
    if (this.isCheckoutPrepaidPurchase()) {
      return this.resolvePrepaidListPriceForCheckout();
    }
    if (!this.hasOrderRequestWashPackageForCheckout()) {
      return null;
    }
    const pkgId = (this.formData.packageId || this.selectedPackage?.id || '').trim();
    if (!pkgId) {
      return null;
    }
    const candidates = this.expandedPackages.filter(e => e.package.id === pkgId);
    const hints: string[] = [];
    const pushHint = (v: string | null | undefined) => {
      const s = (v != null ? String(v).trim() : '') || '';
      if (s) {
        hints.push(s);
      }
    };
    pushHint(this.selectedWashType);
    pushHint(this.cachedOrderWashType);
    const d = this.notification?.data as
      { wash_type?: string; order?: { wash_type?: string } } | undefined;
    pushHint(d?.wash_type);
    pushHint(d?.order?.wash_type);
    let expanded: (typeof this.expandedPackages)[0] | undefined;
    for (const h of hints) {
      const norm = this.normalizeWashType(h);
      expanded = candidates.find(e => this.normalizeWashType(e.washType) === norm);
      if (expanded) {
        break;
      }
    }
    if (!expanded && candidates.length === 1) {
      expanded = candidates[0];
    }
    if (!expanded && candidates.length > 1) {
      expanded =
        candidates.find(e => this.normalizeWashType(e.washType) === 'single_wash') ?? candidates[0];
    }
    if (expanded) {
      if (
        expanded.allowsCustomPrice === true &&
        this.normalizeWashType(expanded.washType) === 'single_wash'
      ) {
        const saved = this.orderRequestSingleWashSavedCustomPrice[expanded.uniqueId];
        const customN = this.parseUsdNumber(saved);
        if (customN != null && Number.isFinite(customN) && customN > 0) {
          return customN;
        }
        return null;
      }
      return this.parseUsdNumber(expanded.price);
    }
    const wtRaw =
      this.cachedOrderWashType ||
      d?.wash_type ||
      d?.order?.wash_type ||
      this.selectedWashType ||
      '';
    const normalized = this.normalizeWashType(wtRaw);
    const pkg = this.selectedPackage || this.packages.find(p => p.id === pkgId);
    if (!pkg) {
      return null;
    }
    if (normalized === 'monthly_membership') {
      return this.parseUsdNumber(pkg.monthly_list_price);
    }
    return this.parseUsdNumber(pkg.single_list_price);
  }

  private computeOrderRequestCustomDiscountAmount(preDiscountTotal: number): number {
    const saved = this.orderRequestCustomDiscountSaved;
    if (!saved) {
      return 0;
    }
    const cap = Math.max(0, preDiscountTotal);
    if (saved.typeKey === 'percentage') {
      const pctRaw = this.parseUsdNumber(String(saved.valueLabel).replace(/%/g, '')) ?? 0;
      const pct = Math.min(Math.max(0, pctRaw), 100);
      const amt = Math.round(cap * (pct / 100) * 100) / 100;
      return Math.min(amt, cap);
    }
    const fixed = this.parseUsdNumber(saved.valueLabel) ?? 0;
    return Math.min(Math.max(0, fixed), cap);
  }

  private computeOrderRequestApiDiscountAmount(
    preDiscountTotal: number,
    d: OrderRequestDiscountItem
  ): number {
    const rawStr = String(d.discountValue ?? '')
      .replace(/,/g, '')
      .trim();
    const raw = this.parseUsdNumber(rawStr) ?? parseFloat(rawStr);
    const n = Number.isFinite(raw) ? raw : 0;
    const dt = (d.discountType || '').toLowerCase();
    if (dt === 'percentage' || dt === 'percent') {
      return Math.round(preDiscountTotal * (n / 100) * 100) / 100;
    }
    return Math.min(Math.max(0, n), preDiscountTotal);
  }

  getPaymentCheckoutBreakdown(): {
    subtotal: number;
    addon: number;
    discount: number;
    wayPlus: number;
    tax: number;
    total: number;
    wayBenefitsCount: number;
  } {
    const data = this.notification?.data as
      | {
          order?: {
            list_price?: string | number;
            discount?: string | number;
            final_price?: string | number;
          };
        }
      | undefined;
    const order = data?.order;
    const addonOnly = this.isCheckoutAddonOnlyOrder();
    const rawAddon = this.getCheckoutResolvedAddonAmount();
    const appliedAddon = this.checkoutSummaryAddonSelected ? rawAddon : 0;

    const fromPackageList = this.getCheckoutListPriceFromPackage();
    const fromOrderList = this.parseUsdNumber(order?.list_price);
    const fromFormPrice = this.parseUsdNumber(this.formData.price);
    const selectedCustomSingleWash = this.getSelectedSingleWashCustomPriceExpandedPackage();
    let subtotal: number;
    if (this.isCheckoutPrepaidPurchase()) {
      subtotal = this.resolvePrepaidListPriceForCheckout() ?? 0;
    } else if (addonOnly) {
      subtotal = 0;
    } else if (selectedCustomSingleWash) {
      subtotal = fromPackageList ?? 0;
    } else {
      subtotal = fromPackageList ?? fromOrderList ?? fromFormPrice ?? 0;
    }

    const discountFromOrder = this.parseUsdNumber(order?.discount) ?? 0;
    let discount = discountFromOrder;
    const preDisc = subtotal + appliedAddon;
    if (
      this.orderRequestSelectedDiscountId === 'disc_custom' &&
      this.orderRequestCustomDiscountSaved
    ) {
      discount = this.computeOrderRequestCustomDiscountAmount(preDisc);
    } else if (
      this.orderRequestSelectedDiscountId &&
      this.orderRequestSelectedDiscountId !== 'disc_custom'
    ) {
      const apiDisc = this.orderRequestDiscounts.find(
        d => d.id === this.orderRequestSelectedDiscountId
      );
      if (apiDisc?.mode === 'fixed') {
        discount = this.computeOrderRequestApiDiscountAmount(preDisc, apiDisc);
      }
    }
    const rawDiscount = discount;

    const wayPlus = 0;
    const wayBenefitsCount =
      this.getCheckoutFreeBenefitsCount() > 0 ? this.checkoutWayPlusStaticBenefitsCount : 0;

    // Payment checkout: always derive display + PUT totals from live selections.
    // Stale `order.final_price` must never surface as "Taxes and Fees" after add-ons are removed.
    if (this.notification?.type === 'create-payment-request' && !this.isEditMode) {
      const effectiveAddon = this.checkoutSummaryAddonSelected ? rawAddon : 0;
      const effectiveDiscount = this.checkoutSummaryDiscountSelected ? rawDiscount : 0;
      const liveTotal = Math.max(0, subtotal + effectiveAddon - effectiveDiscount + wayPlus);
      return {
        subtotal,
        addon: effectiveAddon,
        discount: effectiveDiscount,
        wayPlus,
        tax: 0,
        total: liveTotal,
        wayBenefitsCount,
      };
    }

    const total =
      this.parseUsdNumber(order?.final_price) ?? this.parseUsdNumber(this.formData.price) ?? null;

    // In edit mode, when user changes wash/package/add-ons/discounts, ignore stale order snapshot totals
    // and recalculate from the currently selected UI state.
    const impliedAddonFromOrder = this.getCheckoutImpliedAddonTotalFromOrder() ?? 0;
    const packageChanged =
      fromPackageList != null &&
      fromOrderList != null &&
      Math.abs(fromPackageList - fromOrderList) > 0.001;
    const addonChanged = Math.abs(rawAddon - impliedAddonFromOrder) > 0.001;
    const discountChanged = Math.abs(rawDiscount - discountFromOrder) > 0.001;
    // After Back → edit add-on custom prices, checkout must not keep POST /order snapshot totals
    // (`isEditMode` is false on the payment view).
    const shouldUseLiveTotals =
      this.notification?.type === 'create-payment-request' &&
      (packageChanged || addonChanged || discountChanged);

    if (shouldUseLiveTotals) {
      const effectiveAddon = this.checkoutSummaryAddonSelected ? rawAddon : 0;
      const effectiveDiscount = this.checkoutSummaryDiscountSelected ? rawDiscount : 0;
      const liveTotal = Math.max(0, subtotal + effectiveAddon - effectiveDiscount + wayPlus);
      return {
        subtotal,
        addon: effectiveAddon,
        discount: effectiveDiscount,
        wayPlus,
        tax: 0,
        total: liveTotal,
        wayBenefitsCount,
      };
    }

    // ── New add-ons that weren't part of the original order snapshot ────────
    // When the user picks add-ons during order creation (or edits them after),
    // the `order.final_price` snapshot does NOT include those add-ons.
    // We must always compute the total from first principles in this case so
    // both the UI display and the API payload (`computeOrderRequestFinalPrice`)
    // stay in sync.
    const newAddonAdded = appliedAddon > 0 && impliedAddonFromOrder < appliedAddon - 0.001;
    const paymentRequestFromNotification =
      this.notification?.type === 'create-payment-request' && !this.isEditMode;
    const shouldRebuildTotal =
      shouldUseLiveTotals ||
      newAddonAdded ||
      total == null ||
      (paymentRequestFromNotification && impliedAddonFromOrder > 0.001);

    if (shouldRebuildTotal) {
      const effectiveAddon = this.checkoutSummaryAddonSelected ? rawAddon : 0;
      const effectiveDiscount = this.checkoutSummaryDiscountSelected ? rawDiscount : 0;
      const liveTotal = Math.max(0, subtotal + effectiveAddon - effectiveDiscount + wayPlus);
      return {
        subtotal,
        addon: effectiveAddon,
        discount: effectiveDiscount,
        wayPlus,
        tax: 0,
        total: liveTotal,
        wayBenefitsCount,
      };
    }

    // ── Existing order snapshot path (no new add-ons, totals from API) ───────
    const resolvedAddon = rawAddon > 0 ? rawAddon : impliedAddonFromOrder;

    // When the user adds a brand-new discount on top of an order that originally
    // had no discount, the order.final_price still reflects the undiscounted total.
    // We must rebase total = originalFinalPrice - newDiscount so the total is correct.
    const userAddedNewDiscount =
      rawDiscount > 0 && discountFromOrder === 0 && !!this.orderRequestSelectedDiscountId;
    const effectiveTotal = userAddedNewDiscount ? Math.max(0, total - rawDiscount) : total;

    let tax = effectiveTotal - subtotal - resolvedAddon - wayPlus;
    if (!Number.isFinite(tax) || tax < 0) {
      tax = 0;
    }

    let discountOut = discount;
    let totalOut = effectiveTotal;
    if (!this.checkoutSummaryDiscountSelected && rawDiscount > 0) {
      discountOut = 0;
      // Restore to original final_price when discount is deselected
      totalOut = userAddedNewDiscount ? total : totalOut + rawDiscount;
    }
    const addonOut = this.checkoutSummaryAddonSelected ? resolvedAddon : 0;
    if (!this.checkoutSummaryAddonSelected && resolvedAddon > 0) {
      totalOut = Math.max(0, totalOut - resolvedAddon);
    }

    return {
      subtotal,
      addon: addonOut,
      discount: discountOut,
      wayPlus,
      tax,
      total: totalOut,
      wayBenefitsCount,
    };
  }

  isFieldEmpty(fieldValue: string): boolean {
    return !fieldValue || fieldValue.trim() === '';
  }

  /**
   * CWS-1914: Prepaid redemption shows the booking details of whoever *bought* the book.
   * They are not the redeemer's to edit at the lane — typing over them is what used to rewrite
   * the purchaser's customer record — and whoever actually came in is captured in the redeemer
   * pane below instead. The location and lane pickers stay live: the attendant still has to say
   * where the wash is happening.
   */
  isOrderRequestBookingDetailsLocked(): boolean {
    return this.isOrderRequestPrepaidRedeemEntryPoint();
  }

  shouldDisableField(fieldName: string): boolean {
    if (
      (fieldName === 'package' || fieldName === 'packageId') &&
      this.notification?.type === 'fleet-vehicle-found'
    ) {
      return true;
    }
    if (
      (fieldName === 'package' || fieldName === 'packageId') &&
      this.isFleetVehicleBillingOnlyAddons()
    ) {
      return true;
    }
    // LPR lane pins location; package/plate stay editable. Defaults must not lock location otherwise.
    if (fieldName === 'location' || fieldName === 'locationId') {
      return this.isOrderRequestLocationLockedToNotificationLane();
    }
    if (fieldName === 'vehicleLicensePlate' && this.isOrderRequestBookingDetailsLocked()) {
      return true;
    }
    if (
      fieldName === 'package' ||
      fieldName === 'packageId' ||
      fieldName === 'vehicleLicensePlate'
    ) {
      return false;
    }

    // If user has interacted with this field, don't disable it
    if (this.userInteractedFields.has(fieldName)) {
      return false;
    }

    // If field has data and user hasn't interacted, disable it
    const fieldValue = this.formData[fieldName as keyof typeof this.formData];
    return !this.isFieldEmpty(fieldValue);
  }

  getFieldValue(fieldName: keyof typeof this.formData): string {
    return this.formData[fieldName] || '';
  }

  onFieldChange(fieldName: keyof typeof this.formData, event: Event): void {
    const target = event.target as HTMLInputElement;
    let value = target.value;

    // Validate vehicle year: only allow 4-digit numbers
    if (fieldName === 'vehicleYear') {
      // Remove any non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      // Limit to 4 digits
      value = value.slice(0, 4);
      // Update the input value
      target.value = value;
    }

    // Order Request phone: digits + formatting only (no letters/symbols)
    if (fieldName === 'customerContact') {
      value = sanitizeCustomerPhoneInput(value);
      if (target.value !== value) {
        target.value = value;
      }
    }

    this.formData[fieldName] = value;

    // Mark this field as user-interacted
    this.userInteractedFields.add(fieldName);

    if (fieldName === 'customerContact') {
      this.triggerCustomerPhoneSearchFromValue(value);
    } else if (fieldName === 'customerEmail') {
      this.triggerCustomerEmailSearchFromValue(target.value);
    }

    // If name field, trigger search
    if (fieldName === 'customerFirstName' && target.value.trim().length >= 2) {
      this.nameSearchSubject.next(target.value.trim());
    } else if (fieldName === 'customerFirstName' && target.value.trim().length < 2) {
      this.nameSearchResults = [];
      this.showNameDropdown = false;
    }

    if (fieldName === 'vehicleLicensePlate') {
      this.triggerLicensePlateSearchFromValue(target.value);
    }

    this.scheduleFieldInputLog(fieldName, value);
  }

  triggerCustomerEmailSearchFromValue(rawValue: string): void {
    const email = (rawValue ?? '').trim();
    this.userInteractedFields.add('customerEmail');
    this.onEmailInputChange(email);
    if (email.length >= 3) {
      this.emailSearchSubject.next(email);
    } else {
      this.emailSearchResults = [];
      this.showEmailDropdown = false;
    }
  }

  triggerCustomerPhoneSearchFromValue(rawValue: string): void {
    const phone = (rawValue ?? '').trim();
    this.userInteractedFields.add('customerContact');
    this.onPhoneInputChange(phone);
    if (phone.length >= 3) {
      this.phoneSearchSubject.next(phone);
    } else {
      this.phoneSearchResults = [];
      this.showPhoneDropdown = false;
      if (this.isOrderRequestPrepaidCheckoutContext()) {
        this.clearPrepaidRedemptionState();
      }
    }
  }

  readonly usStates = US_STATES;

  /**
   * Location the user picked in the dropdown. `userInteractedFields` is wiped by every populate, so
   * it cannot protect the pick from the second notification re-emission onward; this lives until the
   * modal closes.
   */
  private userChosenLocationId: string | null = null;

  getOrderRequestStateLabel(code: string | null | undefined): string {
    return String(code ?? '')
      .trim()
      .toUpperCase();
  }

  get orderRequestPlateNumber(): string {
    return splitLicensePlateForApi(
      this.formData.vehicleLicensePlate,
      this.formData.vehicleLicensePlateState
    ).license_plate;
  }

  set orderRequestPlateNumber(value: string) {
    this.setCombinedLicensePlateOnForm(value, this.formData.vehicleLicensePlateState);
  }

  /** Make/model/year — only when a plate number is entered and no vehicle is linked yet. */
  get showVehicleMakeModelYearFields(): boolean {
    if (!(this.orderRequestPlateNumber || '').trim()) {
      return false;
    }
    return !(this.formData.vehicleId || '').trim();
  }

  setCombinedLicensePlateOnForm(fullPlate: string, hintState?: string | null): void {
    const parsed = splitLicensePlateForApi(String(fullPlate ?? ''), hintState);
    this.formData.vehicleLicensePlateState = parsed.state;
    this.formData.vehicleLicensePlate = parsed.license_plate
      ? `${parsed.state}${parsed.license_plate}`
      : '';
    this.syncLicensePlateValidationError();
    this.clearStaleOrderRequestValidationDisplay();
    // Plate is part of first_time / returning eligibility — refresh when it changes.
    this.refreshOrderRequestCustomerHistory();
  }

  /** Inline plate field error: required on submit; invalid characters while typing. */
  private syncLicensePlateValidationError(): void {
    const plate = (this.orderRequestPlateNumber || '').trim();
    if (!plate) {
      if (this.licensePlateValidationError === ORDER_REQUEST_PLATE_INVALID_MESSAGE) {
        this.licensePlateValidationError = '';
      }
      return;
    }
    if (!isLicensePlateCharactersValid(plate)) {
      this.licensePlateValidationError = ORDER_REQUEST_PLATE_INVALID_MESSAGE;
      return;
    }
    if (
      this.licensePlateValidationError === ORDER_REQUEST_PLATE_INVALID_MESSAGE ||
      this.licensePlateValidationError === ORDER_REQUEST_PLATE_REQUIRED_MESSAGE
    ) {
      this.licensePlateValidationError = '';
    }
  }

  /**
   * Canonical value for `lpr_entries.plate`: the 2-char US state code followed by the
   * plate exactly as the attendant typed it. Built from the form's separate state +
   * number so that editing only the state still rewrites the stored 2-char prefix.
   * Returns '' when there is no plate number to store.
   */
  private buildLprEntryPlateFromForm(): string {
    const parsed = splitLicensePlateForApi(
      this.formData.vehicleLicensePlate,
      this.formData.vehicleLicensePlateState
    );
    if (!parsed.license_plate) {
      return '';
    }
    return `${this.resolveLprEntryStateFromForm(parsed.state)}${parsed.license_plate}`;
  }

  /**
   * 2-char US state code stored alongside the plate. Prefers the attendant's selected
   * state, then any state parsed off the plate, then the default — so the `state` column
   * always matches the prefix written into `plate`.
   */
  private resolveLprEntryStateFromForm(parsedState?: string): string {
    return (
      coerceUsStateCode(this.formData.vehicleLicensePlateState) ??
      coerceUsStateCode(parsedState) ??
      DEFAULT_LICENSE_PLATE_STATE
    );
  }

  onOrderRequestPlateStateChange(stateCode: string): void {
    const state = coerceUsStateCode(stateCode) ?? DEFAULT_LICENSE_PLATE_STATE;
    this.formData.vehicleLicensePlateState = state;
    const plate = splitLicensePlateForApi(this.formData.vehicleLicensePlate, state).license_plate;
    this.formData.vehicleLicensePlate = plate ? `${state}${plate}` : '';
    this.userInteractedFields.add('vehicleLicensePlate');
    this.userInteractedFields.add('vehicleLicensePlateState');
    this.syncLicensePlateValidationError();
    this.detachVehicleIdIfPlateChangedFromSelection();
    this.triggerLicensePlateSearchFromValue(this.formData.vehicleLicensePlate);
    this.scheduleLprEntryManualPlateEdit();
    this.clearStaleOrderRequestValidationDisplay();
  }

  onOrderRequestPlateNumberInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value ?? '';
    this.onOrderRequestPlateNumberChange(value);
  }

  /** Paste may not fire `input` when the value is unchanged; read DOM after insert. */
  onOrderRequestPlateNumberPaste(event: Event): void {
    this.scheduleDomInputSearch(event, value => this.onOrderRequestPlateNumberChange(value));
  }

  onCustomerEmailPaste(event: Event): void {
    this.scheduleDomInputSearch(event, value => this.triggerCustomerEmailSearchFromValue(value));
  }

  onCustomerPhonePaste(event: Event): void {
    this.scheduleDomInputSearch(event, raw => {
      const input = event.target as HTMLInputElement | null;
      const sanitized = sanitizeCustomerPhoneInput(raw);
      if (input && input.value !== sanitized) {
        input.value = sanitized;
      }
      this.formData.customerContact = sanitized;
      this.triggerCustomerPhoneSearchFromValue(sanitized);
    });
  }

  private scheduleDomInputSearch(event: Event, searchFromValue: (value: string) => void): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }
    setTimeout(() => searchFromValue(input.value ?? ''), 0);
  }

  /** Clears debounced search memory when the modal opens again in the same session. */
  private resetCustomerSearchSubjects(): void {
    this.phoneSearchSubject.next('');
    this.emailSearchSubject.next('');
    this.nameSearchSubject.next('');
    this.licensePlateSearchSubject.next('');
  }

  onOrderRequestPlateNumberChange(value: string): void {
    this.setCombinedLicensePlateOnForm(value, this.formData.vehicleLicensePlateState);
    this.userInteractedFields.add('vehicleLicensePlate');
    this.detachVehicleIdIfPlateChangedFromSelection();
    this.triggerLicensePlateSearchFromValue(this.formData.vehicleLicensePlate);
    this.scheduleLprEntryManualPlateEdit();
  }

  /** Stable key for matching vehicles by state + plate number (no state prefix in plate segment). */
  private plateKeyFromFullPlate(fullPlate: string): string {
    const parsed = splitLicensePlateForApi(
      fullPlate,
      this.formData.vehicleLicensePlateState || this.notification?.data?.state
    );
    return `${parsed.state}|${parsed.license_plate}`.trim().toUpperCase();
  }

  /**
   * Whether the scan's images still depict the plate on the form.
   *
   * The preview reads straight from `notification.data`, which typing never touches, so a scanned
   * crop stayed on screen beside a plate it no longer showed (CWS-818). Gating at display time
   * rather than clearing the data keeps this reversible — retyping the scanned plate brings the
   * image back — and avoids fighting the update merge, which deliberately re-instates
   * `plate_image_s3_url` across LPR entry updates.
   *
   * No captured plate (OCR returned nothing) means there is nothing to contradict, so the image
   * is shown.
   */
  private doScanImagesMatchCurrentPlate(): boolean {
    if (!this.scannedPlateKeyForImages) {
      return true;
    }
    const current = (this.formData.vehicleLicensePlate || '').trim();
    if (!current) {
      return false;
    }
    return this.plateKeyFromFullPlate(current) === this.scannedPlateKeyForImages;
  }

  private syncLinkedVehicleSelection(vehicleId: string, fullPlate: string): void {
    const vid = String(vehicleId || '').trim();
    const plate = String(fullPlate || '').trim();
    if (!vid || !plate) {
      return;
    }
    this.linkedVehicleAtSelection = { vehicleId: vid, plateKey: this.plateKeyFromFullPlate(plate) };
  }

  /** When staff has typed on plate fields, do not replace with stale API/Pusher/search values. */
  private canApplyExternalLicensePlateToForm(): boolean {
    return (
      !this.userInteractedFields.has('vehicleLicensePlate') &&
      !this.userInteractedFields.has('vehicleLicensePlateState')
    );
  }

  /** Drop stale vehicle id when staff edits plate away from the vehicle row they started with. */
  private detachVehicleIdIfPlateChangedFromSelection(): void {
    if (!this.linkedVehicleAtSelection) {
      return;
    }
    const current = (this.formData.vehicleLicensePlate || '').trim();
    // An emptied plate is staff editing away from that vehicle, not a reason to keep it.
    // Returning early here held the stale vehicle id through the clear, so the history
    // panel keyed on it stayed live (CWS-239).
    if (current && this.plateKeyFromFullPlate(current) === this.linkedVehicleAtSelection.plateKey) {
      return;
    }
    this.formData.vehicleId = '';
    this.linkedVehicleAtSelection = null;
    const cacheKey = this.normalizePlateForVehicleLookupCache(current);
    if (this.lastVehicleLookupPlate && this.lastVehicleLookupPlate !== cacheKey) {
      this.fetchedVehicleId = '';
      this.vehicleDataFetched = false;
      this.clearVehicleLookupSuccessCache();
    }
  }

  /**
   * Lane for POST order/rewash — same derivation POST /order uses, so a rewash
   * lands in the lane the camera event resolved. Yields nothing for a manually
   * opened rewash, and (via the shared helper) nothing for a UI image upload
   * unless a lane was explicitly picked on the form.
   */
  get rewashLaneId(): string | null {
    if (!this.isNotificationModalLprFlow()) {
      return null;
    }
    const data = this.notification?.data as Record<string, any> | undefined;
    const { lane_id: laneId } = buildCreateOrderLaneIdPayload({
      selectedLaneId: isUiLprImageUploadNotificationData(data)
        ? this.formData.laneId || null
        : null,
      notificationData: data,
      fallbackLaneId: getUserDataLaneId(),
    }) as { lane_id?: string };
    return laneId || null;
  }

  private isNotificationModalLprFlow(): boolean {
    return isNotificationModalLprEntryFlow(
      this.getEffectiveNotificationType(),
      this.notification?.data as Record<string, any> | undefined
    );
  }

  private scheduleLprEntryManualPlateEdit(): void {
    if (!this.shouldPersistManualPlateToLprEntry()) {
      return;
    }
    this.licensePlateManualLprPersistSubject.next();
  }

  private shouldPersistManualPlateToLprEntry(): boolean {
    if (this.isDirectOrder() || this.isApplyingLprPlateCorrection) {
      return false;
    }
    const notificationId = this.resolveLprEntryNotificationId();
    if (!notificationId || notificationId === this.lprManualPlateEditFlagSentForLprEntryId) {
      return false;
    }
    const manualPlateEdit =
      this.userInteractedFields.has('vehicleLicensePlate') ||
      this.userInteractedFields.has('vehicleLicensePlateState');
    if (!manualPlateEdit) {
      return false;
    }
    return this.isNotificationModalLprFlow() && this.showOrderRequestCheckoutLayout();
  }

  /** PUT `order/lpr-entries/:id` with `{ plate_manually_edited: true }` once per LPR row per modal open. */
  private persistLprEntryManualPlateEdit(): void {
    if (!this.shouldPersistManualPlateToLprEntry()) {
      return;
    }

    const notificationId = this.resolveLprEntryNotificationId();
    if (!notificationId) {
      return;
    }

    const updatePayload = lprEntryManualPlateEditFlagPayload();

    this.logModalInteraction('lpr.entry.persist.manualPlate', {
      notificationId,
      updatePayload,
    });

    this.apiService
      .put(`order/lpr-entries/${notificationId}`, updatePayload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.lprManualPlateEditFlagSentForLprEntryId = notificationId;
          this.notificationService.syncLprEntryFromPutResponse(response);
        },
        error: error => {
          console.error('❌ Error persisting manual plate edit to LPR entry:', error);
        },
      });
  }

  /**
   * Triggers customer-by-license-plate search when the license plate value changes.
   * Called from (input), (ngModelChange), and (paste) so typing, paste, and clear all trigger the API
   * for create-order-request, create-customer-request, and invalid-license-plate types.
   */
  triggerLicensePlateSearchFromValue(rawValue: string): void {
    const licensePlate = (rawValue ?? '').trim();
    const isRelevantType =
      this.notification?.type === 'create-customer-request' ||
      this.notification?.type === 'create-order-request' ||
      this.notification?.type === 'invalid-license-plate' ||
      (this.notification?.type === 'create-payment-request' && this.isEditMode);
    if (!isRelevantType) return;
    if (licensePlate.length >= 3) {
      this.licensePlateSearchSubject.next(licensePlate);
      this.trackFullStoryLicensePlate('order-form');
    } else {
      this.licensePlateSearchResult = null;
      this.showLicensePlateDropdown = false;
      this.showVehicleFieldsForLicensePlate = false;
      // A plate this short identifies no vehicle, so the customer and the history panel
      // the previous plate resolved go with it. This branch used to drop
      // linkedCustomerSnapshot on its own, which cleared nothing and also
      // disarmed the 404 cleanup below (it no-ops once the snapshot is null) — so
      // clearing the field and typing something random left the previous vehicle's
      // orders on screen under a plate matching nothing (CWS-239).
      this.clearCustomerFieldsFromPlateLookupIfLinked();
      this.clearOrderRequestHistoryForPlateChange();
      this.linkedCustomerSnapshot = null;
      this.clearPrepaidRedemptionState();
    }
  }

  /**
   * Emit a searchable FullStory timeline marker for the current plate (debounced in FullStoryService).
   */
  private trackFullStoryLicensePlate(source: string, options?: { immediate?: boolean }): void {
    const parsed = splitLicensePlateForApi(
      this.formData.vehicleLicensePlate,
      this.formData.vehicleLicensePlateState
    );
    const plate = (parsed.license_plate || '').trim();
    if (!plate) {
      return;
    }
    this.fullStoryService.trackLicensePlate(
      {
        plate,
        state: parsed.state || this.formData.vehicleLicensePlateState,
        make: this.formData.vehicleMake,
        model: this.formData.vehicleModel,
        source,
      },
      options
    );
  }

  onFieldFocus(fieldName: string): void {
    this.logModalInteraction('field.focus', { fieldName });
    this.userInteractedFields.add(fieldName);
    if (fieldName === 'vehicleLicensePlate') {
      this.licensePlateInputFocused = true;
    }
    if (
      fieldName !== 'customerFirstName' &&
      fieldName !== 'customerContact' &&
      fieldName !== 'customerEmail' &&
      fieldName !== 'vehicleLicensePlate'
    ) {
      this.showNameDropdown = false;
      this.showPhoneDropdown = false;
      this.showLicensePlateDropdown = false;
    }
    if (fieldName !== 'customerEmail') {
      this.showEmailDropdown = false;
    }
    if (fieldName !== 'location') {
      this.isLocationDropdownOpen = false;
    }
    if (fieldName !== 'lane' && fieldName !== 'laneId') {
      this.isLaneDropdownOpen = false;
    }
  }

  getSelectedLocationName(): string {
    const name = (this.formData.location || '').trim();
    if (name) {
      return name;
    }
    const id = (this.formData.locationId || '').trim();
    if (id && this.availableLocationsForDropdown.length > 0) {
      const found = this.availableLocationsForDropdown.find(o => o.id === id);
      if (found?.name) {
        return found.name;
      }
    }
    return 'Select location';
  }

  toggleLocationDropdown(event: Event): void {
    event.stopPropagation();
    this.onFieldFocus('location');
    if (this.shouldDisableField('location')) {
      return;
    }

    if (!this.isLocationDropdownOpen && this.availableLocationsForDropdown.length === 0) {
      this.ensureLocationOptionsLoaded();
    }
    this.isLocationDropdownOpen = !this.isLocationDropdownOpen;
    this.locationDropdownActiveIndex = this.isLocationDropdownOpen
      ? this.availableLocationsForDropdown.findIndex(o => o.id === this.formData.locationId)
      : -1;
  }

  closeLocationDropdown(): void {
    setTimeout(() => {
      this.isLocationDropdownOpen = false;
    }, 120);
  }

  /** Keyboard access for the location dropdown: focus stays on the trigger, arrows move the highlight. */
  onLocationDropdownKeydown(event: KeyboardEvent): void {
    const options = this.availableLocationsForDropdown;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        if (!this.isLocationDropdownOpen) {
          this.toggleLocationDropdown(event);
          return;
        }
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.locationDropdownActiveIndex = Math.min(
          options.length - 1,
          Math.max(0, this.locationDropdownActiveIndex + step)
        );
        return;
      }
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const active = options[this.locationDropdownActiveIndex];
        if (this.isLocationDropdownOpen && active) {
          this.selectLocationForNotification(active, event);
        } else {
          this.toggleLocationDropdown(event);
        }
        return;
      }
      case 'Escape':
        this.isLocationDropdownOpen = false;
        return;
    }
  }

  getSelectedLaneName(): string {
    const name = (this.formData.lane || '').trim();
    if (name) {
      return name;
    }
    const id = (this.formData.laneId || '').trim();
    if (id && this.availableOrderRequestLanes.length > 0) {
      const found = this.availableOrderRequestLanes.find(o => o.id === id);
      if (found?.name) {
        return found.name;
      }
    }
    return 'Select lane';
  }

  toggleLaneDropdown(event: Event): void {
    event.stopPropagation();
    this.onFieldFocus('lane');
    if (!this.shouldShowOrderRequestLanePicker() || this.isLoadingOrderRequestLanes) {
      return;
    }
    this.isLaneDropdownOpen = !this.isLaneDropdownOpen;
    this.laneDropdownActiveIndex = this.isLaneDropdownOpen
      ? this.availableOrderRequestLanes.findIndex(o => o.id === this.formData.laneId)
      : -1;
  }

  /** Keyboard access for the lane select, mirroring the location select. */
  onLaneDropdownKeydown(event: KeyboardEvent): void {
    const options = this.availableOrderRequestLanes;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        if (!this.isLaneDropdownOpen) {
          this.toggleLaneDropdown(event);
          return;
        }
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.laneDropdownActiveIndex = Math.min(
          options.length - 1,
          Math.max(0, this.laneDropdownActiveIndex + step)
        );
        return;
      }
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const active = options[this.laneDropdownActiveIndex];
        if (this.isLaneDropdownOpen && active) {
          this.selectLaneForNotification(active, event);
        } else {
          this.toggleLaneDropdown(event);
        }
        return;
      }
      case 'Escape':
        this.isLaneDropdownOpen = false;
        return;
    }
  }

  closeLaneDropdown(): void {
    setTimeout(() => {
      this.isLaneDropdownOpen = false;
    }, 120);
  }

  selectLaneForNotification(lane: LocationDropdownOption, event?: Event): void {
    event?.stopPropagation();
    this.formData.lane = lane.name;
    this.formData.laneId = lane.id;
    this.userInteractedFields.add('lane');
    this.userInteractedFields.add('laneId');
    this.isLaneDropdownOpen = false;
    this.clearStaleOrderRequestValidationDisplay();
  }

  /** Stops a submit on a checkout validation error; a failed lanes request is also retried. */
  private rejectOrderRequestCheckout(checkoutErr: string): void {
    if (checkoutErr === ORDER_REQUEST_LANES_LOAD_FAILED_MESSAGE) {
      this.loadOrderRequestLanesForSelectedLocation();
    }
    this.updateOrderError = sanitizeErrorMessage(checkoutErr);
    this.isUpdatingOrder = false;
  }

  private clearOrderRequestLaneSelection(): void {
    this.formData.laneId = '';
    this.formData.lane = '';
    this.availableOrderRequestLanes = [];
    this.isLaneDropdownOpen = false;
    this.isLoadingOrderRequestLanes = false;
    this.orderRequestLanesLoadFailed = false;
  }

  /**
   * Load active lanes for UI image-scan order requests only.
   * Pre-selects a lane when lanes are available (keeps current if still valid).
   */
  private loadOrderRequestLanesForSelectedLocation(): void {
    // Origin, not `is_lpr_upload`: checkout data (Back from payment) carries only the origin.
    if (this.resolveCurrentLocationPickerOrigin() !== 'ui-upload') {
      this.clearOrderRequestLaneSelection();
      return;
    }

    const locationId = String(this.formData.locationId || '').trim();
    if (!locationId) {
      this.clearOrderRequestLaneSelection();
      return;
    }

    const token = (this.orderRequestLanesLoadToken += 1);
    this.isLoadingOrderRequestLanes = true;
    this.orderRequestLanesLoadFailed = false;
    this.isLaneDropdownOpen = false;

    this.apiService
      .get<{ id?: string; name?: string; status?: string }[]>(
        `location/view-location/${encodeURIComponent(locationId)}/lanes`
      )
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (token !== this.orderRequestLanesLoadToken) {
            return;
          }
          const rows = response.status && Array.isArray(response.data) ? response.data : [];
          const lanes: LocationDropdownOption[] = rows
            .filter(lane => {
              const status = String(lane.status ?? 'active').toLowerCase();
              return (
                status === 'active' &&
                !!String(lane.id || '').trim() &&
                !!String(lane.name || '').trim()
              );
            })
            .map(lane => ({
              id: String(lane.id).trim(),
              name: String(lane.name).trim(),
            }));
          this.applyLoadedOrderRequestLanes(lanes);
          this.refreshOrderRequestAsyncUi();
        },
        error: () => {
          if (token !== this.orderRequestLanesLoadToken) {
            return;
          }
          this.availableOrderRequestLanes = [];
          this.formData.laneId = '';
          this.formData.lane = '';
          this.isLoadingOrderRequestLanes = false;
          this.orderRequestLanesLoadFailed = true;
          this.refreshOrderRequestAsyncUi();
        },
      });
  }

  private applyLoadedOrderRequestLanes(lanes: LocationDropdownOption[]): void {
    this.availableOrderRequestLanes = lanes;
    this.isLoadingOrderRequestLanes = false;

    if (lanes.length === 0) {
      this.formData.laneId = '';
      this.formData.lane = '';
      return;
    }

    const currentId = String(this.formData.laneId || '').trim();
    if (currentId) {
      const current = lanes.find(lane => lane.id === currentId);
      if (current) {
        this.formData.lane = current.name;
        return;
      }
    }

    // Pre-select the first active lane so the dropdown is never empty when shown.
    this.formData.laneId = lanes[0].id;
    this.formData.lane = lanes[0].name;
  }

  selectLocationForNotification(location: LocationDropdownOption, event?: Event): void {
    event?.stopPropagation();
    if (this.isOrderRequestLocationLockedToNotificationLane()) {
      return;
    }
    const previousLocationId = String(this.formData.locationId || '').trim();
    this.formData.location = location.name;
    this.formData.locationId = location.id;
    this.userInteractedFields.add('location');
    this.userInteractedFields.add('locationId');
    this.userChosenLocationId = String(location.id || '').trim() || null;
    this.isLocationDropdownOpen = false;

    if (previousLocationId !== String(location.id || '').trim()) {
      this.clearOrderRequestLaneSelection();
    }

    // Keep package / addon / fleet / discount catalogs aligned with the selected location.
    if (location.id) {
      this.loadPackagesByLocationId(location.id);
      this.ensureOrderRequestAddonsAndFleetsLoaded(location.id, { force: true });
      this.loadOrderRequestDiscounts(location.id);
      this.loadMostUsedDefaultsForOrderRequest(location.id);
      this.retryOrderRequestWayLookupFromFormFields();
      this.loadOrderRequestLanesForSelectedLocation();
    }

    if (this.notification?.type === 'create-payment-request') {
      this.terminalCheckoutLoadedLocationId = null;
      this.terminalReaders = [];
      this.loadTerminalCheckoutData();
    }
  }

  private resolveLocationIdForSubmission(): string {
    // LPR scan lane: always use the notification shop — never a mismatched dropdown value.
    if (this.isOrderRequestLocationLockedToNotificationLane()) {
      const lockedId = resolveNotificationLocationId(
        this.notification?.data as Record<string, any> | undefined
      );
      if (lockedId) {
        return lockedId;
      }
    }

    const directId = String(this.formData.locationId || '').trim();
    if (directId) {
      return directId;
    }

    // Staff: location picker is hidden; dropdown list is narrowed to their assigned shop when profile has location_id.
    if (this.roleService.isShopFloorEmployee() && this.availableLocationsForDropdown.length === 1) {
      const only = this.availableLocationsForDropdown[0];
      if (only?.id) {
        return String(only.id).trim();
      }
    }

    const selectedName = String(this.formData.location || '')
      .trim()
      .toLowerCase();
    if (selectedName && this.availableLocationsForDropdown.length > 0) {
      const match = this.availableLocationsForDropdown.find(
        loc =>
          String(loc.name || '')
            .trim()
            .toLowerCase() === selectedName
      );
      if (match?.id) {
        return String(match.id).trim();
      }
    }

    // Header "+": only the form location dropdown — never fall back to LPR/notification location.
    if (this.isDirectOrder()) {
      return '';
    }

    return String(
      this.notification?.data?.location_id || this.notification?.data?.locationId || ''
    ).trim();
  }

  /**
   * After locations are fetched: ensure `locationId` is set (match branch label, else first active row).
   * Fixes notifications that send `branch` without `location_id` ("Location ID is required" on submit).
   */
  private syncFormLocationFromLoadedOptions(options: LocationDropdownOption[]): void {
    if (!options.length) {
      return;
    }

    // LPR lane pins the shop — dropdown selection must match notification location_id.
    if (this.pinFormLocationToNotificationIfLaneLocked(options)) {
      this.clearOrderRequestLaneSelection();
      return;
    }

    const hasId = !!(this.formData.locationId || '').trim();
    if (hasId) {
      if (!this.formData.location) {
        const matched = options.find(o => o.id === this.formData.locationId);
        if (matched) {
          this.formData.location = matched.name;
        }
      }
      this.loadOrderRequestLanesForSelectedLocation();
      return;
    }

    const label = (this.formData.location || '').trim().toLowerCase();
    const byName = label
      ? options.find(
          o =>
            String(o.name || '')
              .trim()
              .toLowerCase() === label
        )
      : undefined;
    const pick = byName ?? options[0];
    this.formData.locationId = pick.id;
    this.formData.location = pick.name;
    if (this.showOrderRequestCheckoutLayout() && pick.id) {
      this.loadCarWashPackages();
      this.retryOrderRequestWayLookupFromFormFields();
    }
    this.loadOrderRequestLanesForSelectedLocation();
  }

  /**
   * When an LPR notification includes a lane, force form location to notification.location_id
   * so the dropdown display and submit payload stay aligned with the scan.
   * @returns true when the lock applied (caller should skip other location defaults)
   */
  private pinFormLocationToNotificationIfLaneLocked(
    options: LocationDropdownOption[] = this.availableLocationsForDropdown
  ): boolean {
    if (!this.isOrderRequestLocationLockedToNotificationLane()) {
      return false;
    }
    const lockedId = resolveNotificationLocationId(
      this.notification?.data as Record<string, any> | undefined
    );
    if (!lockedId) {
      return false;
    }

    const previousId = String(this.formData.locationId || '').trim();
    this.formData.locationId = lockedId;
    const matched = options.find(o => o.id === lockedId);
    if (matched?.name) {
      this.formData.location = matched.name;
    } else {
      const branch = String(
        (this.notification?.data as Record<string, any> | undefined)?.['branch'] ?? ''
      ).trim();
      if (branch) {
        this.formData.location = branch;
      }
    }

    if (previousId !== lockedId && this.showOrderRequestCheckoutLayout()) {
      this.loadPackagesByLocationId(lockedId);
      this.ensureOrderRequestAddonsAndFleetsLoaded(lockedId, { force: true });
      this.loadOrderRequestDiscounts(lockedId);
      this.loadMostUsedDefaultsForOrderRequest(lockedId);
    }
    return true;
  }

  /**
   * After populateFormData (including LPR API refresh), re-apply first/matched location when the list is already loaded.
   */
  private reapplyCachedLocationToFormAfterPopulate(): void {
    if (!this.showOrderRequestCheckoutLayout()) {
      return;
    }
    if (this.pinFormLocationToNotificationIfLaneLocked()) {
      this.clearOrderRequestLaneSelection();
      return;
    }
    if (this.availableLocationsForDropdown.length > 0) {
      this.syncFormLocationFromLoadedOptions(this.availableLocationsForDropdown);
      return;
    }
    this.loadOrderRequestLanesForSelectedLocation();
  }

  private ensureLocationOptionsLoaded(): void {
    if (!this.shouldLoadLocationOptionsForModal()) {
      return;
    }
    if (this.isLoadingLocationsForDropdown || this.availableLocationsForDropdown.length > 0) {
      return;
    }

    this.isLoadingLocationsForDropdown = true;
    this.getLocationOptionsForDropdown()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: options => {
          this.availableLocationsForDropdown = options;
          this.isLoadingLocationsForDropdown = false;
          this.syncFormLocationFromLoadedOptions(options);
        },
        error: () => {
          this.availableLocationsForDropdown = [];
          this.isLoadingLocationsForDropdown = false;
        },
      });
  }

  private getLocationOptionsForDropdown(): Observable<LocationDropdownOption[]> {
    const userRole = this.roleService.getCurrentUserRole();
    if (userRole === UserRole.ATTENDANT || userRole === UserRole.ATTENDANT_PLUS) {
      return this.employeeService.getCurrentEmployeeProfile().pipe(
        map((response: any) => {
          if (!response?.status) {
            return [];
          }
          const id = String(response.data?.location_id || '').trim();
          if (!id) {
            return [];
          }
          const name =
            String(
              response.data?.location_name || response.data?.location?.name || 'Staff Location'
            ).trim() || 'Staff Location';
          return [
            {
              id,
              name,
              wayPurchaseLookupEnabled: isWayPurchaseLookupEnabled(response.data),
            },
          ];
        }),
        catchError(() => of([]))
      );
    }

    return this.locationService.getLocations().pipe(
      map((response: any) => {
        const all = Array.isArray(response?.data) ? response.data : [];
        const active = all.filter((loc: any) => this.isActiveLocation(loc));
        const source = active.length > 0 ? active : all;
        const out = source
          .map((loc: any) => {
            const id = String(loc.id || '').trim();
            const label = this.getLocationLabelForDropdown(loc) || 'Unnamed location';
            return {
              id,
              name: label,
              wayPurchaseLookupEnabled: isWayPurchaseLookupEnabled(loc),
            } as LocationDropdownOption;
          })
          .filter((loc: LocationDropdownOption) => !!loc.id);
        return out;
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Matches LocationService.getLocationOptions(): API locations use `branch`, not `name`.
   * Mapping only `loc.name` was filtering out all rows (empty name) while raw API still had one location.
   */
  private getLocationLabelForDropdown(loc: any): string {
    const raw = loc?.name ?? loc?.branch ?? loc?.location_name ?? loc?.location?.name ?? '';
    const s = String(raw).trim();
    if (s) {
      return s;
    }
    const city = String(loc?.city ?? '').trim();
    const state = String(loc?.state ?? '').trim();
    if (city || state) {
      return [city, state].filter(Boolean).join(', ');
    }
    return '';
  }

  private isActiveLocation(location: any): boolean {
    const status = location?.status;
    if (status === undefined || status === null) {
      return true;
    }
    if (typeof status === 'boolean') {
      return status;
    }
    if (typeof status === 'number') {
      return status === 1;
    }
    if (typeof status === 'string') {
      const normalized = status.toLowerCase().trim();
      return normalized === 'true' || normalized === '1' || normalized === 'active';
    }
    return Boolean(status);
  }

  onPackageChange(event: any): void {
    this.logModalInteraction('package.change', {
      packageId: event?.value?.id,
      packageName: event?.value?.package_name,
    });
    const selectedPackage = event.value;
    this.selectedPackage = selectedPackage;
    this.formData.package = selectedPackage?.package_name || '';
    this.formData.packageId = selectedPackage?.id || '';

    // Reset wash type selection
    this.selectedWashType = null;
    this.availableWashTypes = [];
    this.showWashTypeDropdown = false;

    // Check wash_types in package data (only if package is selected)
    if (selectedPackage?.wash_types && Array.isArray(selectedPackage.wash_types)) {
      this.availableWashTypes = selectedPackage.wash_types;

      if (selectedPackage.wash_types.length === 1) {
        // If only 1 wash type, use it automatically
        const washType = selectedPackage.wash_types[0];
        this.selectedWashType = washType; // Store raw string value
        this.showWashTypeDropdown = false;

        this.syncWashTypeContextToNotification(this.selectedWashType);

        // Update payment method if subscription is selected (only for desktop)
        if (this.normalizeWashType(washType) === 'monthly_membership' && !this.isMobileOrTablet) {
          this.paymentForm.method = this.getDefaultPaymentMethod();
        }
        this.reconcilePaymentMethodAfterContextChange();
      } else if (selectedPackage.wash_types.length === 2) {
        // If 2 wash types, show dropdown
        this.showWashTypeDropdown = true;
        // Don't auto-select, let user choose
      }
    }

    // Mark package field as user-interacted
    this.userInteractedFields.add('package');
    this.userInteractedFields.add('packageId');
    this.syncOrderRequestAddonSelectionToPairedWith();
  }

  /**
   * Normalize wash type string to the expected format
   */
  /** Member plates added in the "Sell as Family Plan" block; read when the order is submitted. */
  familyMembers: FamilyMemberDraft[] = [];
  private familyEligibleSource: ExpandedPackage[] | null = null;
  private familyEligibleCache: ExpandedPackage[] = [];

  onFamilyMembersChange(members: FamilyMemberDraft[]): void {
    this.familyMembers = members;
  }

  /** "Sell as Family Plan" is on and shown (CWS-1580). Every family branch below keys off this or the stage. */
  familySaleActive = false;
  familyCheckoutStage: null | 'summary' | 'success' = null;
  familyQuote: FamilySaleQuote | null = null;
  familyCheckoutRowsData: FamilyCheckoutRow[] = [];
  familyBreakdown: FamilyCheckoutBreakdown | null = null;
  familySaleResult: FamilySaleCompleteData | null = null;
  isFamilyPaying = false;
  private familyPayload: FamilySaleQuotePayload | null = null;
  private familyRequestId = '';
  /** Set only by a family Continue: replaces the order step once the customer and vehicle exist. */
  private postVehicleResolvedHook: (() => void) | null = null;

  get isFamilyCheckout(): boolean {
    return this.familyCheckoutStage === 'summary';
  }

  /** Family checkout takes a card today; other methods wait for the backend (shown, not payable). */
  get isFamilyPaymentMethodUnsupported(): boolean {
    return this.isFamilyCheckout && this.paymentForm.method !== PaymentMethodCode.Card;
  }

  onFamilyActiveChange(active: boolean): void {
    this.familySaleActive = active;
    if (active) {
      // The primary pays the full package price: no shop discount on a family sale.
      if (this.orderRequestSelectedDiscountId) {
        this.clearOrderRequestDiscountSelection();
      }
    } else {
      this.postVehicleResolvedHook = null;
    }
  }

  private runPostVehicleResolvedHook(): void {
    const hook = this.postVehicleResolvedHook;
    this.postVehicleResolvedHook = null;
    hook?.();
  }

  private familyApiError(err: unknown, fallback: string): string {
    const body = (
      err as { error?: { message?: string; data?: { code?: string; details?: unknown } } }
    )?.error;
    return familyErrorWithPlates(
      body?.data?.code,
      body?.data?.details,
      sanitizeErrorMessage(body?.message || fallback)
    );
  }

  /** Customer and vehicle exist: price the family on the server, then show the checkout summary. */
  private requestFamilyQuote(): void {
    const payload = buildFamilyQuotePayload(
      this.getCheckoutLocationId(),
      buildFamilyPrimary({
        customerId: this.formData.customerId || '',
        plate: this.orderRequestPlateNumber,
        state: this.formData.vehicleLicensePlateState,
        packageId: this.selectedPackage?.id || '',
        addOns: this.buildCreateOrderAddOnVariantsPayload(),
      }),
      this.familyMembers
    );
    this.familySaleService.quote(payload).subscribe({
      next: res => {
        this.isUpdatingOrder = false;
        if (!res?.status || !res.data) {
          this.updateOrderError = sanitizeErrorMessage(
            res?.message || 'Could not price the Family Plan.'
          );
          return;
        }
        this.showFamilyCheckout(payload, res.data);
      },
      error: err => {
        this.isUpdatingOrder = false;
        this.updateOrderError = this.familyApiError(err, 'Could not price the Family Plan.');
      },
    });
  }

  private showFamilyCheckout(payload: FamilySaleQuotePayload, quote: FamilySaleQuote): void {
    this.familyPayload = payload;
    this.familyRequestId = newFamilyRequestId();
    this.familyQuote = quote;
    this.familyCheckoutRowsData = familyCheckoutRows(
      quote,
      {
        plate: this.orderRequestPlateNumber,
        packageName: this.selectedPackage?.package_name ?? '',
        customerName: this.familyPrimaryCustomerName,
      },
      this.familyMembers,
      this.familyEligiblePackages
    );
    this.familyBreakdown = familyCheckoutBreakdown(quote);
    this.familyCheckoutStage = 'summary';
    this.paymentForm.method = PaymentMethodCode.Card;
    this.scheduleStripeMountForNotificationModal();
  }

  /** Back from the family summary: the order request keeps everything that was entered. */
  leaveFamilyCheckout(): void {
    this.unmountStripeElements();
    this.familyCheckoutStage = null;
    this.familyQuote = null;
    this.familyPayload = null;
    this.familyRequestId = '';
  }

  /** Card: same PaymentMethod step as a membership sale, then the family checkout and completion. */
  async confirmFamilyPayment(): Promise<void> {
    if (this.isFamilyPaying || !this.familyPayload || this.isFamilyPaymentMethodUnsupported) {
      return;
    }
    if (!this.isPaymentFormValid()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Error',
        detail: 'Please fill in all card details correctly.',
      });
      return;
    }
    this.isFamilyPaying = true;
    try {
      const { paymentMethod, error: pmError } = await this.createStripeCardPaymentMethod();
      if (pmError || !paymentMethod) {
        this.isFamilyPaying = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Payment Error',
          detail:
            pmError?.message || 'Could not create payment method. Please check your card details.',
          life: 5000,
        });
        return;
      }
      const checkout = await firstValueFrom(
        this.familySaleService.checkout({
          ...this.familyPayload,
          request_id: this.familyRequestId,
          payment_method_id: paymentMethod.id,
          lpr_entry_id: familyLprEntryId(
            this.getOrderSourceForCreatePayload(),
            this.formData.notificationId
          ),
        })
      );
      if (checkout?.data?.status !== 'succeeded') {
        this.isFamilyPaying = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Payment Error',
          detail: 'The card payment did not go through. Please try again or use another card.',
          life: 5000,
        });
        return;
      }
      const result = await this.completeFamilySale();
      this.isFamilyPaying = false;
      if (result) {
        this.showFamilySaleSuccess(result);
      }
    } catch (err) {
      this.isFamilyPaying = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Error',
        detail: this.familyApiError(err, 'Payment failed. Please try again.'),
        life: 5000,
      });
    }
  }

  /** Completion retries while the payment settles; an expired attempt still finishes on the server. */
  private async completeFamilySale(attempt = 1): Promise<FamilySaleCompleteData | null> {
    try {
      const res = await firstValueFrom(this.familySaleService.complete(this.familyRequestId));
      return res?.data ?? null;
    } catch (err) {
      const code = (err as { error?: { data?: { code?: string } } })?.error?.data?.code;
      if (isRetryableFamilyCompleteCode(code) && attempt < FAMILY_COMPLETE_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, FAMILY_COMPLETE_RETRY_MS));
        return this.completeFamilySale(attempt + 1);
      }
      this.messageService.add({
        severity: code === 'FAMILY_CHECKOUT_NOT_FOUND' ? 'warn' : 'error',
        summary: 'Family Plan',
        detail: this.familyApiError(err, 'The payment went through but the sale could not finish.'),
        life: 8000,
      });
      return null;
    }
  }

  private showFamilySaleSuccess(result: FamilySaleCompleteData): void {
    this.familySaleResult = result;
    this.familyCheckoutStage = 'success';
    this.successModalData = {
      title: 'Payment Successful!',
      description: 'The Family Plan is active for these vehicles.',
      buttonText: 'Done',
      showOpenGate: false,
    };
    this.showPaymentSuccess = true;
  }

  private resetFamilySale(): void {
    this.familySaleActive = false;
    this.familyCheckoutStage = null;
    this.familyQuote = null;
    this.familyCheckoutRowsData = [];
    this.familyBreakdown = null;
    this.familySaleResult = null;
    this.familyPayload = null;
    this.familyRequestId = '';
    this.isFamilyPaying = false;
    this.postVehicleResolvedHook = null;
  }

  /** The selected main package is a family-eligible monthly membership. */
  get isFamilyPrimaryEligible(): boolean {
    return isPrimaryPackageFamilyEligible(
      this.monthlyPackages.find(ep => this.isExpandedPackageSelected(ep))
    );
  }

  /** Same array until the catalog changes, so the child input is stable between checks. */
  get familyEligiblePackages(): ExpandedPackage[] {
    if (this.familyEligibleSource !== this.expandedPackages) {
      this.familyEligibleSource = this.expandedPackages;
      this.familyEligibleCache = eligibleFamilyPackages(this.monthlyPackages);
    }
    return this.familyEligibleCache;
  }

  get familyPrimaryListPrice(): number {
    return familyPackageListPrice(
      this.monthlyPackages.find(ep => this.isExpandedPackageSelected(ep))
    );
  }

  get familyPrimaryCustomerName(): string {
    return `${this.formData.customerFirstName} ${this.formData.customerLastName}`.trim();
  }

  get familyPrimaryVehicleName(): string {
    const { vehicleYear, vehicleMake, vehicleModel } = this.formData;
    return [vehicleYear, vehicleMake, vehicleModel].filter(Boolean).join(' ');
  }

  isMonthlyPackageSelected(): boolean {
    if (!this.selectedWashType) return false;
    return this.normalizeWashType(this.selectedWashType) === 'monthly_membership';
  }

  /**
   * The "phone is required" message this checkout should show, or null when phone is optional.
   *
   * CWS-1880 adds the prepaid case: a book is redeemed later from the QR link and code sent by
   * SMS and email, so selling one without a phone leaves the customer nothing to present at the
   * lane. Email stays optional. Redemption checkouts are excluded — the redeemer's phone is
   * captured in the recipient panel, which validates itself.
   */
  /**
   * CWS-1880: whether the checkout demands a phone right now — drives the label asterisk,
   * the error state and the inline message, so the field looks required as well as behaving
   * that way. Public because the order-request layout template reads it.
   */
  isCustomerPhoneRequired(): boolean {
    return this.getCheckoutPhoneRequiredMessage() !== null;
  }

  /** The inline message for a missing-but-required phone; '' when phone is optional. */
  customerPhoneRequiredMessage(): string {
    return this.getCheckoutPhoneRequiredMessage() ?? '';
  }

  private getCheckoutPhoneRequiredMessage(): string | null {
    if (this.isMonthlyPackageSelected()) {
      return ORDER_REQUEST_MONTHLY_PHONE_REQUIRED_MESSAGE;
    }
    if (this.isCheckoutPrepaidBookPurchaseForContact()) {
      return ORDER_REQUEST_PREPAID_PHONE_REQUIRED_MESSAGE;
    }
    return null;
  }

  /**
   * Buying a prepaid book, with the top-level customer fields carrying the contact.
   *
   * Reads the same condition the checkout's phone/email validation switches on: once the
   * redemption recipient panel is in play it owns those fields and validates them itself,
   * so demanding a top-level phone there would block a redemption that is perfectly valid.
   */
  private isCheckoutPrepaidBookPurchaseForContact(): boolean {
    const recipientPanelOwnsContact =
      this.isInPrepaidRedemptionCheckoutMode() && this.hasOrderRequestRedemptionSelection();
    return !recipientPanelOwnsContact && this.isCheckoutPrepaidPurchase();
  }

  private normalizeWashType(washType: string): 'single_wash' | 'monthly_membership' | 'add_on' {
    return normalizeOrderRequestWashType(washType);
  }

  /**
   * CWS-1181: forget the prepaid wash book POST /order recorded, once the attendant has
   * moved the order off prepaid.
   *
   * `isCheckoutPrepaidPurchase()` checks the live selection first but then falls back to
   * `notification.data.order.prepaid_wash_book_id` / `notification.data.prepaid_wash_book_id`.
   * Clearing only `orderRequestSelectedPrepaidBookId` left that fallback answering "prepaid",
   * so `getNormalizedWashType()` kept returning `prepaid_wash_book`: the PUT carried the old
   * wash type (and, per the package_id rule, no package at all), the membership never reached
   * the API, and reopening the order showed prepaid again.
   *
   * Only call this where the attendant actively picked something that is not prepaid — never
   * from a path that is merely reading an order back from the API.
   */
  private forgetPrepaidCheckoutContext(): void {
    const cleared = clearPrepaidWashBookContext(
      this.notification?.data as Record<string, any> | undefined
    );
    if (cleared.length) {
      this.logModalInteraction('checkout.prepaidContext.cleared', { cleared });
    }
  }

  /** Keep cache + notification `wash_type` aligned (single vs monthly on same package id). */
  private syncWashTypeContextToNotification(rawWash: string | null | undefined): void {
    if (rawWash == null || String(rawWash).trim() === '') {
      return;
    }
    this.cachedOrderWashType = String(rawWash).trim();
    if (!this.notification?.data) {
      return;
    }
    const norm = this.normalizeWashType(this.cachedOrderWashType);
    this.notification.data.wash_type = norm;
    const ord = (this.notification.data as Record<string, any>)['order'];
    if (ord && typeof ord === 'object' && !Array.isArray(ord)) {
      (ord as Record<string, any>)['wash_type'] = norm;
    }
    this.clearOrderRequestDiscountSelectionIfHidden();
  }

  /**
   * Format wash type for display (e.g., "single_wash" -> "Single Wash")
   */
  formatWashTypeLabel(washType: string): string {
    return formatWashTypeLabelUtil(washType);
  }

  /**
   * Get the appropriate price for a package based on wash types
   * For single_wash only packages: use single_list_price
   * For monthly_membership only packages: use monthly_list_price
   * For packages with both: use price based on selected wash type (if this is the selected package), or default to single
   */
  getPackagePrice(packageOption: Package | null): string {
    if (!packageOption) return '';

    const washTypes = packageOption.wash_types || [];

    // If package has only single_wash
    if (washTypes.length === 1 && this.normalizeWashType(washTypes[0]) === 'single_wash') {
      return packageOption.single_list_price || '';
    }

    // If package has only monthly_membership
    if (washTypes.length === 1 && this.normalizeWashType(washTypes[0]) === 'monthly_membership') {
      return packageOption.monthly_list_price || '';
    }

    // If package has both wash types
    if (washTypes.length === 2) {
      // Only use selected wash type if this is the currently selected package
      if (
        this.selectedPackage &&
        this.selectedPackage.id === packageOption.id &&
        this.selectedWashType
      ) {
        const normalized = this.normalizeWashType(this.selectedWashType);
        if (normalized === 'single_wash') {
          return packageOption.single_list_price || '';
        } else if (normalized === 'monthly_membership') {
          return packageOption.monthly_list_price || '';
        }
      }
      // Default to single_list_price when browsing or no wash type selected yet
      return packageOption.single_list_price || '';
    }

    // Fallback to single_list_price
    return packageOption.single_list_price || '';
  }

  /**
   * Handle wash type selection from dropdown
   */
  onWashTypeChange(event: any): void {
    this.logModalInteraction('washType.change', { washType: event?.value });
    this.selectedWashType = event.value;
    this.orderRequestWashSelectionCleared = !String(event?.value ?? '').trim();
    this.syncWashTypeContextToNotification(this.selectedWashType);

    // Update payment method if monthly subscription is selected (only for desktop)
    const isSubscription = this.selectedWashType
      ? this.normalizeWashType(this.selectedWashType) === 'monthly_membership'
      : false;

    if (isSubscription && !this.isMobileOrTablet) {
      // Subscription on desktop requires card payment
      this.paymentForm.method = this.getDefaultPaymentMethod();
    } else if (this.paymentForm.method === PaymentMethodCode.Cash && this.isCashPaymentDisabled()) {
      // Reset cash payment if monthly subscription is selected
      this.paymentForm.method = this.getDefaultPaymentMethod();
    }
    this.reconcilePaymentMethodAfterContextChange();
    this.syncOrderRequestAddonSelectionToPairedWith();
  }

  /**
   * Handle expanded package selection from radio button
   */
  onExpandedPackageCardClick(expandedPackage: ExpandedPackage, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.onExpandedPackageSelect(expandedPackage);
  }

  onExpandedPackageSelect(expandedPackage: ExpandedPackage): void {
    if (this.shouldDisableField('package') || this.isFleetVehicleBillingOnlyAddons()) {
      this.logModalInteraction('package.expanded.select.blocked', {
        uniqueId: expandedPackage.uniqueId,
      });
      return;
    }

    if (this.isExpandedPackageSelected(expandedPackage)) {
      this.clearOrderRequestWashPackageSelection();
      this.clearStaleOrderRequestValidationDisplay();
      return;
    }

    this.logModalInteraction('package.expanded.select', {
      uniqueId: expandedPackage.uniqueId,
      packageId: expandedPackage.package.id,
      washType: expandedPackage.washType,
    });

    // Set the original package and wash type (deselect prepaid)
    this.selectedPackage = expandedPackage.package;
    this.selectedWashType = expandedPackage.washType;
    this.orderRequestWashSelectionCleared = false;
    this.orderRequestSelectedPrepaidBookId = null;
    this.forgetPrepaidCheckoutContext();
    // Deselect the redemption too: the order is for this package now, not for washes off the
    // book. The panel stays visible so the attendant can switch back.
    this.orderRequestRedemptionSlotKeys = [];

    this.syncWashTypeContextToNotification(this.selectedWashType);

    // Update form data
    this.formData.package = expandedPackage.package.package_name || '';
    this.formData.packageId = expandedPackage.package.id || '';

    // Update payment method if monthly subscription is selected (only for desktop)
    const normalized = this.normalizeWashType(expandedPackage.washType);
    if (normalized === 'monthly_membership' && !this.isMobileOrTablet) {
      this.paymentForm.method = this.getDefaultPaymentMethod();
    } else if (this.paymentForm.method === PaymentMethodCode.Cash && this.isCashPaymentDisabled()) {
      // Reset cash payment if monthly subscription is selected
      this.paymentForm.method = this.getDefaultPaymentMethod();
    }
    this.reconcilePaymentMethodAfterContextChange();

    // When leaving monthly, re-run phone validation so monthly-required clears
    // but format errors (e.g. country code) remain visible.
    if (normalized !== 'monthly_membership') {
      this.onPhoneInputChange(this.formData.customerContact);
    }

    // Mark package field as user-interacted
    this.userInteractedFields.add('package');
    this.userInteractedFields.add('packageId');
    this.syncOrderRequestAddonSelectionToPairedWith();

    if (
      this.orderRequestSingleWashCustomExpandedUniqueId &&
      this.orderRequestSingleWashCustomExpandedUniqueId !== expandedPackage.uniqueId
    ) {
      this.orderRequestSingleWashCustomExpandedUniqueId = null;
    }
    const swCustom = this.getSelectedSingleWashCustomPriceExpandedPackage();
    if (swCustom) {
      this.syncOrderRequestSingleWashCustomPriceForSelection(swCustom);
      // First click on a custom-price tile opens the amount editor — no second click on the pill.
      if (!this.formatOrderRequestSingleWashSavedPrice(swCustom.uniqueId)) {
        this.openOrderRequestSingleWashCustomPriceEditor(swCustom);
      }
    } else {
      this.formData.price = '';
    }
    this.rebuildMostUsedServiceItems();
    this.clearOrderRequestDiscountSelectionIfHidden();
    this.clearStaleOrderRequestValidationDisplay();
  }

  /** Package id on the persisted order / notification (before the user changes selection). */
  private resolvePersistedOrderPackageId(): string {
    const root = this.lastOrderFetchPayloadForOrderRequestRestore;
    const orderLike = root ? this.mergeOrderLikeForRestore(root) : this.getCheckoutOrderSnapshot();
    if (orderLike) {
      const id = String(orderLike['package_id'] ?? orderLike['packageId'] ?? '').trim();
      if (id) {
        return id;
      }
    }
    const d = this.notification?.data as Record<string, any> | undefined;
    if (!d) {
      return '';
    }
    const nested = d['order'];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const fromNested = String(
        (nested as Record<string, any>)['package_id'] ??
          (nested as Record<string, any>)['packageId'] ??
          ''
      ).trim();
      if (fromNested) {
        return fromNested;
      }
    }
    return String(d['package_id'] ?? d['packageId'] ?? '').trim();
  }

  /**
   * Align custom single-wash price state with the selected package.
   * Clears list_price restored from a different package when the user switches selection.
   */
  private syncOrderRequestSingleWashCustomPriceForSelection(ep: ExpandedPackage): void {
    const selectedPkgId = String(ep.package.id ?? '').trim();
    const orderPkgId = this.resolvePersistedOrderPackageId();
    if (orderPkgId && orderPkgId !== selectedPkgId) {
      delete this.orderRequestSingleWashSavedCustomPrice[ep.uniqueId];
      delete this.orderRequestSingleWashCustomPriceDraft[ep.uniqueId];
      this.formData.price = '';
      return;
    }
    const saved = this.orderRequestSingleWashSavedCustomPrice[ep.uniqueId];
    const n = this.parseUsdNumber(saved);
    if (n != null && Number.isFinite(n) && n > 0) {
      this.formData.price = n.toFixed(2);
    } else {
      this.formData.price = '';
    }
  }

  /**
   * Check if an expanded package is currently selected
   */
  isExpandedPackageSelected(expandedPackage: ExpandedPackage): boolean {
    if (!this.selectedPackage || !this.selectedWashType) {
      return false;
    }
    return (
      this.selectedPackage.id === expandedPackage.package.id &&
      this.selectedWashType === expandedPackage.washType
    );
  }

  /**
   * Handle package selection from radio button (deprecated - kept for backward compatibility)
   */
  onPackageRadioSelect(packageOption: Package): void {
    if (this.shouldDisableField('package')) {
      this.logModalInteraction('package.radio.select.blocked', { packageId: packageOption?.id });
      return;
    }

    this.logModalInteraction('package.radio.select', {
      packageId: packageOption?.id,
      packageName: packageOption?.package_name,
    });
    this.selectedPackage = packageOption;
    this.formData.package = packageOption?.package_name || '';
    this.formData.packageId = packageOption?.id || '';

    // Reset wash type selection
    this.selectedWashType = null;
    this.availableWashTypes = [];
    this.showWashTypeDropdown = false;

    // Check wash_types in package data (only if package is selected)
    if (packageOption?.wash_types && Array.isArray(packageOption.wash_types)) {
      this.availableWashTypes = packageOption.wash_types;

      if (packageOption.wash_types.length === 1) {
        // If only 1 wash type, use it automatically
        const washType = packageOption.wash_types[0];
        this.selectedWashType = washType; // Store raw string value
        this.showWashTypeDropdown = false;

        this.syncWashTypeContextToNotification(this.selectedWashType);

        // Update payment method if subscription is selected (only for desktop)
        if (this.normalizeWashType(washType) === 'monthly_membership' && !this.isMobileOrTablet) {
          this.paymentForm.method = this.getDefaultPaymentMethod();
        }
        this.reconcilePaymentMethodAfterContextChange();
      } else if (packageOption.wash_types.length === 2) {
        // If 2 wash types, show radio buttons
        this.showWashTypeDropdown = true;
        // Don't auto-select, let user choose
      }
    }

    // Mark package field as user-interacted
    this.userInteractedFields.add('package');
    this.userInteractedFields.add('packageId');
    this.syncOrderRequestAddonSelectionToPairedWith();
  }

  /**
   * Handle wash type selection from radio button
   */
  onWashTypeRadioSelect(washType: string): void {
    this.logModalInteraction('washType.radio.select', { washType });
    this.selectedWashType = washType;
    this.syncWashTypeContextToNotification(this.selectedWashType);

    // Update payment method if monthly subscription is selected (only for desktop)
    const isSubscription = this.selectedWashType
      ? this.normalizeWashType(this.selectedWashType) === 'monthly_membership'
      : false;

    if (isSubscription && !this.isMobileOrTablet) {
      // Subscription on desktop requires card payment
      this.paymentForm.method = this.getDefaultPaymentMethod();
    } else if (this.paymentForm.method === PaymentMethodCode.Cash && this.isCashPaymentDisabled()) {
      // Reset cash payment if monthly subscription is selected
      this.paymentForm.method = this.getDefaultPaymentMethod();
    }
    this.reconcilePaymentMethodAfterContextChange();
    this.syncOrderRequestAddonSelectionToPairedWith();
  }

  /**
   * Get package price for radio button display
   */
  getPackagePriceForRadio(packageOption: Package): string {
    if (!packageOption) return '';

    const washTypes = packageOption.wash_types || [];

    // If package has only single_wash
    if (washTypes.length === 1 && this.normalizeWashType(washTypes[0]) === 'single_wash') {
      return packageOption.single_list_price || '';
    }

    // If package has only monthly_membership
    if (washTypes.length === 1 && this.normalizeWashType(washTypes[0]) === 'monthly_membership') {
      return packageOption.monthly_list_price || '';
    }

    // If package has both wash types, show price range or default to single
    if (washTypes.length === 2) {
      return packageOption.single_list_price || '';
    }

    // Fallback to single_list_price
    return packageOption.single_list_price || '';
  }

  /**
   * Get wash type price for radio button display
   */
  getWashTypePrice(washType: string): string {
    if (!this.selectedPackage) return '';

    const normalized = this.normalizeWashType(washType);
    if (normalized === 'single_wash') {
      return this.selectedPackage.single_list_price || '';
    } else if (normalized === 'monthly_membership') {
      return this.selectedPackage.monthly_list_price || '';
    }

    return '';
  }

  /**
   * Get normalized wash type for API calls
   */
  private getNormalizedWashType():
    'single_wash' | 'monthly_membership' | 'add_on' | 'prepaid_wash_book' {
    if (this.isCheckoutPrepaidPurchase()) {
      return 'prepaid_wash_book';
    }
    if (!this.hasOrderRequestWashPackageForCheckout() && this.hasOrderRequestCommittedAddons()) {
      return 'add_on';
    }
    if (this.selectedWashType) {
      return this.normalizeWashType(this.selectedWashType);
    }
    if (this.cachedOrderWashType) {
      return this.normalizeWashType(this.cachedOrderWashType);
    }
    const d = this.notification?.data as
      { wash_type?: string; order?: { wash_type?: string } } | undefined;
    if (d?.wash_type) {
      return this.normalizeWashType(d.wash_type);
    }
    if (d?.order?.wash_type) {
      return this.normalizeWashType(d.order.wash_type);
    }
    if (!this.hasOrderRequestWashPackageForCheckout()) {
      return 'single_wash';
    }
    return this.orderCreationService.getWashType(
      this.formData.package || this.selectedPackage?.package_name || ''
    );
  }

  /** Wash type from order / notification cache (package + add-on flows may lack UI package selection). */
  private resolvePersistedWashTypeForOrderPayload(): 'single_wash' | 'monthly_membership' | null {
    const candidates: any[] = [
      this.cachedOrderWashType,
      (this.notification?.data as { wash_type?: string } | undefined)?.wash_type,
      (this.notification?.data as { order?: { wash_type?: string } } | undefined)?.order?.wash_type,
    ];
    for (const raw of candidates) {
      if (raw == null || String(raw).trim() === '') {
        continue;
      }
      const normalized = this.normalizeWashType(String(raw));
      if (normalized === 'monthly_membership' || normalized === 'single_wash') {
        return normalized;
      }
    }
    const packageId = (this.formData.packageId || this.selectedPackage?.id || '').trim();
    if (packageId && this.selectedPackage?.wash_types?.length === 1) {
      const normalized = this.normalizeWashType(this.selectedPackage.wash_types[0]);
      if (normalized === 'monthly_membership' || normalized === 'single_wash') {
        return normalized;
      }
    }
    return null;
  }

  onDropdownShow(): void {
    this.logModalInteraction('package.dropdown.show', { packageCount: this.packages.length });
    console.log('🔽 Dropdown opened, packages available:', this.packages.length);
    console.log('📋 Packages:', this.packages);
    // Close name, phone, and license plate dropdowns when package dropdown opens
    this.showNameDropdown = false;
    this.showPhoneDropdown = false;
    this.showLicensePlateDropdown = false;
  }

  onDropdownHide(): void {
    this.logModalInteraction('package.dropdown.hide');
    console.log('🔼 Dropdown closed');
  }

  /**
   * Clear all validation errors
   */
  clearValidationErrors(): void {
    this.phoneValidationError = '';
    this.emailValidationError = '';
    this.licensePlateValidationError = '';
  }

  /**
   * Validate phone number using PhoneValidator
   * Returns empty string if valid or no value, error message if invalid
   */
  validatePhoneNumber(phone: string): string {
    if (!phone || phone.trim() === '') {
      return ''; // Don't require field, just validate format when present
    }

    const control = { value: phone.trim() } as any;
    const result = PhoneValidator.usaPhone(control);

    if (result) {
      return this.normalizeFieldErrorMessage(
        result['usaPhone']?.message || ORDER_REQUEST_VALID_PHONE_MESSAGE
      );
    }

    return '';
  }

  /**
   * Validate email using EmailValidator
   * Returns empty string if valid or no value, error message if invalid
   */
  validateEmail(email: string): string {
    if (!email || email.trim() === '') {
      return ''; // Don't require field, just validate format when present
    }

    const control = { value: email.trim() } as any;
    const result = EmailValidator.strictEmail(control);

    if (result) {
      return this.normalizeFieldErrorMessage(
        result['strictEmail']?.message || ORDER_REQUEST_VALID_EMAIL_MESSAGE
      );
    }

    return '';
  }

  /** Trim and ensure field-level messages end with sentence punctuation. */
  private normalizeFieldErrorMessage(message: string): string {
    const trimmed = String(message || '').trim();
    if (!trimmed) {
      return '';
    }
    return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
  }

  /**
   * Check if phone validation should be enabled for current notification type
   */
  private shouldValidatePhone(): boolean {
    const type = this.notification?.type;
    return (
      type === 'create-order-request' ||
      type === 'create-customer-request' ||
      type === 'invalid-license-plate' ||
      (type === 'create-payment-request' && this.isEditMode)
    );
  }

  /**
   * Check if email validation should be enabled for current notification type
   */
  private shouldValidateEmail(): boolean {
    const type = this.notification?.type;
    return (
      type === 'create-order-request' ||
      type === 'create-customer-request' ||
      type === 'invalid-license-plate' ||
      (type === 'create-payment-request' && this.isEditMode)
    );
  }

  /**
   * Validate phone number on input change (real-time validation)
   */
  onPhoneInputChange(value: string): void {
    if (!this.shouldValidatePhone()) {
      this.phoneValidationError = '';
      this.clearStaleOrderRequestValidationDisplay();
      return;
    }

    const phoneRequiredMessage = this.getCheckoutPhoneRequiredMessage();
    if (phoneRequiredMessage && !value?.trim()) {
      this.phoneValidationError = phoneRequiredMessage;
      this.clearStaleOrderRequestValidationDisplay();
      return;
    }

    // Only validate if field has been interacted with or has a value
    if (this.userInteractedFields.has('customerContact') || value) {
      this.phoneValidationError = this.validatePhoneNumber(value);
    } else {
      this.phoneValidationError = '';
    }
    this.clearStaleOrderRequestValidationDisplay();
  }

  /**
   * Validate email on input change (real-time validation)
   */
  onEmailInputChange(value: string): void {
    if (!this.shouldValidateEmail()) {
      this.emailValidationError = '';
      this.clearStaleOrderRequestValidationDisplay();
      return;
    }

    // Only validate if field has been interacted with or has a value
    if (this.userInteractedFields.has('customerEmail') || value) {
      this.emailValidationError = this.validateEmail(value);
    } else {
      this.emailValidationError = '';
    }
    this.clearStaleOrderRequestValidationDisplay();
  }

  /**
   * Validate phone and email fields before submission
   * Returns true if valid, false otherwise
   */
  validatePhoneAndEmail(): boolean {
    if (this.isInPrepaidRedemptionCheckoutMode() && this.hasOrderRequestRedemptionSelection()) {
      return this.validatePrepaidRedemptionRecipientContactFields();
    }

    let isValid = true;

    if (this.shouldValidatePhone() && this.formData.customerContact) {
      this.phoneValidationError = this.validatePhoneNumber(this.formData.customerContact);
      if (this.phoneValidationError) {
        isValid = false;
      }
    }

    if (this.shouldValidateEmail() && this.formData.customerEmail) {
      this.emailValidationError = this.validateEmail(this.formData.customerEmail);
      if (this.emailValidationError) {
        isValid = false;
      }
    }

    return isValid;
  }

  private clearPrepaidRedemptionRecipientFieldErrors(): void {
    this.prepaidRedemptionPhoneError = '';
    this.prepaidRedemptionEmailError = '';
    this.prepaidRedemptionFirstNameError = '';
    this.prepaidRedemptionLastNameError = '';
  }

  private isPrepaidRedemptionRecipientContactFieldInvalid(
    field: 'phone' | 'email' | 'firstName' | 'lastName',
    validate: (value: string) => string
  ): boolean {
    const recipient = this.prepaidRedemptionRecipient;
    const baseline = this.getPrepaidRedemptionPrefilledBaseline();
    const value = String(recipient[field] ?? '').trim();
    if (!value || !prepaidRedemptionContactFieldOverwritten(recipient, baseline, field)) {
      return false;
    }
    return !!validate(value);
  }

  private validatePrepaidRedemptionRecipientContactFields(): boolean {
    let isValid = true;
    this.clearPrepaidRedemptionRecipientFieldErrors();
    const recipient = this.prepaidRedemptionRecipient;
    const baseline = this.getPrepaidRedemptionPrefilledBaseline();

    const fieldChecks: {
      field: 'phone' | 'email' | 'firstName' | 'lastName';
      value: string;
      validate: (value: string) => string;
      assignError: (message: string) => void;
    }[] = [
      {
        field: 'phone',
        value: recipient.phone?.trim() ?? '',
        validate: validateCustomerPhone,
        assignError: message => {
          this.prepaidRedemptionPhoneError = message;
        },
      },
      {
        field: 'email',
        value: recipient.email?.trim() ?? '',
        validate: validateCustomerEmail,
        assignError: message => {
          this.prepaidRedemptionEmailError = message;
        },
      },
      {
        field: 'firstName',
        value: recipient.firstName?.trim() ?? '',
        validate: validateCustomerName,
        assignError: message => {
          this.prepaidRedemptionFirstNameError = message;
        },
      },
      {
        field: 'lastName',
        value: recipient.lastName?.trim() ?? '',
        validate: validateCustomerName,
        assignError: message => {
          this.prepaidRedemptionLastNameError = message;
        },
      },
    ];

    for (const check of fieldChecks) {
      const overwritten = prepaidRedemptionContactFieldOverwritten(
        recipient,
        baseline,
        check.field
      );
      if (overwritten && check.value) {
        const error = check.validate(check.value);
        if (error) {
          check.assignError(error);
          isValid = false;
        }
      }
    }

    return isValid;
  }

  onUpdateOrder(): void {
    if (this.isUpdatingOrder) {
      this.logModalInteraction('order.update.skipped', { reason: 'already_updating' });
      return;
    }

    this.logModalInteraction('order.update.start');
    this.postVehicleResolvedHook = null;

    // Clear previous validation errors
    this.clearValidationErrors();
    this.updateOrderError = '';
    this.vendorScanInactiveCustomerId = '';
    this.clearAutoSelectedWashPackageWhenAddonsCommitted();

    if (this.isSelectedCustomerInactive()) {
      this.logModalInteraction('order.update.skipped', { reason: 'customer_inactive' });
      this.updateOrderError = CUSTOMER_INACTIVE_MESSAGE;
      return;
    }

    if (this.showOrderRequestCheckoutLayout() || this.shouldShowUpdateOrderButton()) {
      if (!this.hasOrderRequestCheckoutLineItems()) {
        // Existing create-order payload message — previously a silent early return.
        this.showOrderRequestRequiredValidation(
          ORDER_REQUEST_LINE_ITEMS_REQUIRED_MESSAGE,
          'services'
        );
        return;
      }
      const singleWashPriceErr = this.getSingleWashCustomPriceSubmissionError();
      if (singleWashPriceErr) {
        this.showOrderRequestRequiredValidation(singleWashPriceErr, 'singleWashPrice');
        return;
      }
      const checkoutErr = this.getOrderRequestCheckoutValidationError();
      if (checkoutErr) {
        this.applyOrderRequestCheckoutFieldErrors(checkoutErr);
        this.showOrderRequestRequiredValidation(
          checkoutErr,
          this.resolveOrderRequestFocusTargetFromCheckoutError(checkoutErr)
        );
        return;
      }
    }

    // Validate phone and email before submission
    if (!this.validatePhoneAndEmail()) {
      this.scrollAndFocusOrderRequestTarget(
        this.phoneValidationError
          ? 'phone'
          : this.emailValidationError
            ? 'email'
            : this.resolvePrepaidRedemptionFocusTarget()
      );
      return;
    }

    if (this.familySaleActive && !this.familyMembers.length) {
      this.showOrderRequestRequiredValidation(FAMILY_MEMBER_REQUIRED_MESSAGE, 'services');
      return;
    }

    this.isUpdatingOrder = true;

    // CWS-1914: a redemption resolves its own customer and vehicle, so it must not go through
    // the generic create-order path first. That path saw an empty booking-details customer id
    // and created a customer from those blank fields — the "Unknown Customer" — then parked
    // the plate under it. The redemption then created the redeemer's own record and asked for
    // the same plate, which the backend rightly refused: one click, colliding with itself.
    if (this.isOrderRequestRedeemingPrepaid()) {
      this.submitPrepaidRedemptionOrder();
      return;
    }

    if (this.familySaleActive) {
      this.postVehicleResolvedHook = () => this.requestFamilyQuote();
    }

    this.persistLinkedCustomerDetailsIfChangedThen(() => {
      const hasCustomerId = !!(this.formData.customerId || '').trim();
      const hasVehicleId = !!(this.formData.vehicleId || '').trim();
      const isOrderStyle = this.isOrderRequestStyleNotification();

      // New customer (no id): create customer first, then vehicle + order — skip when user picked an existing customer from search.
      const needsCustomerCreation =
        !hasCustomerId &&
        (this.notification?.type === 'create-customer-request' ||
          this.notification?.type === 'invalid-license-plate' ||
          this.notification?.type === 'create-order-request');

      if (needsCustomerCreation) {
        this.handleCreateCustomerRequest();
      } else if (isOrderStyle && hasCustomerId && !hasVehicleId) {
        // Existing customer, new or unresolved vehicle: reuse plate lookup when already fetched during search.
        this.updateCustomerFromFormIfNeededThen(() => this.createOrderAfterCustomerCreation());
      } else if (this.postVehicleResolvedHook) {
        this.runPostVehicleResolvedHook();
      } else {
        this.handleRegularOrderUpdate();
      }
    });
  }

  /**
   * Footer error for pre-API order-request validation (existing copy only).
   * Then scroll/focus the related control after change detection.
   * No toast — inline + footer are enough for click validation.
   */
  private showOrderRequestRequiredValidation(
    detail: string,
    focusTarget: OrderRequestValidationFocusTarget
  ): void {
    this.updateOrderError = sanitizeErrorMessage(detail);
    this.scrollAndFocusOrderRequestTarget(focusTarget);
  }

  /** CWS-1451: the order form still points at the customer a lookup found inactive. */
  isSelectedCustomerInactive(): boolean {
    return !!this.inactiveCustomerId && this.formData.customerId === this.inactiveCustomerId;
  }

  /** Inline "Reactivate customer" action next to the inactive-customer footer message. */
  shouldShowReactivateCustomerAction(): boolean {
    return !!this.getReactivatableCustomerId();
  }

  private isCustomerInactiveFooterMessage(): boolean {
    return String(this.updateOrderError || '').includes(CUSTOMER_INACTIVE_MESSAGE);
  }

  /**
   * Which customer the inactive footer message is about:
   * - a refused membership scan (CWS-1555),
   * - the looked-up customer flagged inactive (CWS-1451),
   * - otherwise the form's customer, when svc-order refused the order as `CUSTOMER_INACTIVE`
   *   without the modal having flagged them first (CWS-1555).
   */
  private getReactivatableCustomerId(): string {
    if (!this.isCustomerInactiveFooterMessage()) {
      return '';
    }
    if (this.vendorScanInactiveCustomerId) {
      return this.vendorScanInactiveCustomerId;
    }
    if (this.isSelectedCustomerInactive()) {
      return this.inactiveCustomerId;
    }
    return String(this.formData.customerId || '').trim();
  }

  /**
   * Sets the blocked customer back to active from the order modal (same API as the customer
   * details toggle), so staff can carry on with the order without leaving the dialog.
   */
  onReactivateCustomer(): void {
    const customerId = this.getReactivatableCustomerId();
    if (!customerId || this.isReactivatingCustomer) {
      return;
    }
    this.isReactivatingCustomer = true;
    this.logModalInteraction('customer.reactivate.start', { customerId });

    this.customerService.updateCustomerStatus(customerId, 'active').subscribe({
      next: response => {
        this.isReactivatingCustomer = false;
        if (!response?.status) {
          this.onReactivateCustomerFailed(customerId);
          return;
        }
        this.logModalInteraction('customer.reactivate.success', { customerId });
        const fromMembershipScan = this.vendorScanInactiveCustomerId === customerId;
        if (fromMembershipScan) {
          this.vendorScanInactiveCustomerId = '';
        }
        if (this.inactiveCustomerId === customerId) {
          this.inactiveCustomerId = '';
        }
        if (this.isCustomerInactiveFooterMessage()) {
          this.updateOrderError = '';
        }
        this.messageService.add({
          severity: 'success',
          summary: 'Customer reactivated',
          detail: fromMembershipScan
            ? 'The customer is active again. Scan the membership again to redeem it.'
            : 'The customer is active again. You can place the order.',
        });
      },
      error: () => {
        this.isReactivatingCustomer = false;
        this.onReactivateCustomerFailed(customerId);
      },
    });
  }

  private onReactivateCustomerFailed(customerId: string): void {
    this.logModalInteraction('customer.reactivate.failed', { customerId });
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Could not reactivate the customer. Please try again.',
    });
  }

  /**
   * Remembers whether a looked-up customer is inactive and tells staff straight away, so they
   * do not fill in the whole order only to have it refused on submit.
   */
  private trackLookedUpCustomerStatus(customerId: string, customerStatus: unknown): void {
    if (!isCustomerInactive(customerStatus)) {
      this.inactiveCustomerId = '';
      return;
    }
    this.inactiveCustomerId = customerId;
    this.updateOrderError = CUSTOMER_INACTIVE_MESSAGE;
    this.messageService.add({
      severity: 'warn',
      summary: 'Customer inactive',
      detail: CUSTOMER_INACTIVE_MESSAGE,
    });
  }

  /**
   * Drop footer / plate field errors that no longer match current form state.
   * Does not introduce new errors — only clears stale ones after user edits.
   */
  private clearStaleOrderRequestValidationDisplay(): void {
    this.syncLicensePlateValidationError();

    if (!this.updateOrderError) {
      return;
    }
    if (!this.showOrderRequestCheckoutLayout() && !this.shouldShowUpdateOrderButton()) {
      return;
    }

    const activeMessages: string[] = [];
    if (!this.hasOrderRequestCheckoutLineItems()) {
      activeMessages.push(ORDER_REQUEST_LINE_ITEMS_REQUIRED_MESSAGE);
    } else {
      const priceErr = this.getSingleWashCustomPriceSubmissionError();
      if (priceErr) {
        activeMessages.push(priceErr);
      }
      const checkoutErr = this.getOrderRequestCheckoutValidationError();
      if (checkoutErr) {
        activeMessages.push(checkoutErr);
      }
    }

    const footer = String(this.updateOrderError || '').trim();
    const stillRelevant = activeMessages.some(msg => {
      const sanitized = sanitizeErrorMessage(msg);
      return footer === sanitized || footer === msg || footer.includes(msg);
    });
    if (!stillRelevant) {
      this.updateOrderError = '';
    }
  }

  /**
   * Re-apply existing field-scoped errors for the checkout validation message that blocked submit.
   */
  private applyOrderRequestCheckoutFieldErrors(checkoutErr: string): void {
    if (checkoutErr === ORDER_REQUEST_PLATE_REQUIRED_MESSAGE) {
      this.licensePlateValidationError = ORDER_REQUEST_PLATE_REQUIRED_MESSAGE;
      return;
    }
    if (checkoutErr === ORDER_REQUEST_PLATE_INVALID_MESSAGE) {
      this.licensePlateValidationError = ORDER_REQUEST_PLATE_INVALID_MESSAGE;
      return;
    }
    if (
      checkoutErr === ORDER_REQUEST_MONTHLY_PHONE_REQUIRED_MESSAGE ||
      checkoutErr === ORDER_REQUEST_PREPAID_PHONE_REQUIRED_MESSAGE ||
      checkoutErr === ORDER_REQUEST_VALID_PHONE_MESSAGE
    ) {
      const phoneRequiredMessage = this.getCheckoutPhoneRequiredMessage();
      if (phoneRequiredMessage && !this.formData.customerContact?.trim()) {
        this.phoneValidationError = phoneRequiredMessage;
      } else if (this.formData.customerContact?.trim()) {
        this.phoneValidationError = this.validatePhoneNumber(this.formData.customerContact);
      }
      return;
    }
    if (checkoutErr === ORDER_REQUEST_VALID_EMAIL_MESSAGE && this.formData.customerEmail?.trim()) {
      this.emailValidationError = this.validateEmail(this.formData.customerEmail);
      return;
    }
    if (checkoutErr === 'License plate is required for redemption.') {
      // Recipient plate error is rendered by prepaid-redemption via recipient state; focus only.
      return;
    }
  }

  private resolveOrderRequestFocusTargetFromCheckoutError(
    checkoutErr: string
  ): OrderRequestValidationFocusTarget {
    if (checkoutErr === ORDER_REQUEST_PLATE_REQUIRED_MESSAGE) {
      return 'plate';
    }
    if (checkoutErr === ORDER_REQUEST_PLATE_INVALID_MESSAGE) {
      return 'plate';
    }
    if (
      checkoutErr === ORDER_REQUEST_MONTHLY_PHONE_REQUIRED_MESSAGE ||
      checkoutErr === ORDER_REQUEST_PREPAID_PHONE_REQUIRED_MESSAGE ||
      checkoutErr === ORDER_REQUEST_VALID_PHONE_MESSAGE
    ) {
      return 'phone';
    }
    if (checkoutErr === ORDER_REQUEST_VALID_EMAIL_MESSAGE) {
      return 'email';
    }
    if (
      checkoutErr === ORDER_REQUEST_LANE_REQUIRED_MESSAGE ||
      checkoutErr === ORDER_REQUEST_LANES_LOADING_MESSAGE ||
      checkoutErr === ORDER_REQUEST_LANES_LOAD_FAILED_MESSAGE
    ) {
      return 'lane';
    }
    if (
      checkoutErr === 'Select at least one wash to redeem.' ||
      checkoutErr === 'Customer is required. Select the customer from the license plate match.'
    ) {
      return 'services';
    }
    if (checkoutErr === 'License plate is required for redemption.') {
      return 'redemptionPlate';
    }
    if (checkoutErr.includes('first name') || checkoutErr === 'Please enter a valid first name.') {
      return 'redemptionFirstName';
    }
    if (checkoutErr.includes('last name') || checkoutErr === 'Please enter a valid last name.') {
      return 'redemptionLastName';
    }
    return 'services';
  }

  private resolvePrepaidRedemptionFocusTarget(): OrderRequestValidationFocusTarget {
    if (this.prepaidRedemptionPhoneError) {
      return 'redemptionPhone';
    }
    if (this.prepaidRedemptionEmailError) {
      return 'redemptionEmail';
    }
    if (this.prepaidRedemptionFirstNameError) {
      return 'redemptionFirstName';
    }
    if (this.prepaidRedemptionLastNameError) {
      return 'redemptionLastName';
    }
    return 'phone';
  }

  private scrollAndFocusOrderRequestTarget(target: OrderRequestValidationFocusTarget): void {
    this.cdr.detectChanges();
    const el = this.findOrderRequestFocusElement(target);
    if (!el) {
      return;
    }
    try {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } catch {
      // jsdom / test environments may not implement scrollIntoView fully
    }
    if (typeof (el as HTMLElement).focus === 'function') {
      try {
        (el as HTMLElement).focus({ preventScroll: true });
      } catch {
        (el as HTMLElement).focus();
      }
    }
  }

  private findOrderRequestFocusElement(
    target: OrderRequestValidationFocusTarget
  ): HTMLElement | null {
    const byId = (id: string) => document.getElementById(id);

    switch (target) {
      case 'services':
        return (
          (document.querySelector('.order-request-services') as HTMLElement | null) ||
          (document.querySelector(
            '.order-request-section.order-request-services'
          ) as HTMLElement | null)
        );
      case 'plate':
        return byId('license-plate');
      case 'phone':
        return byId('phone-number');
      case 'email':
        return byId('customer-email');
      case 'lane': {
        const lane = byId('lane');
        if (lane) {
          return lane;
        }
        return document.querySelector('#lane') as HTMLElement | null;
      }
      case 'singleWashPrice': {
        const ep = this.getSelectedSingleWashCustomPriceExpandedPackage();
        if (ep) {
          return byId(`single-wash-price-${ep.uniqueId}`);
        }
        return null;
      }
      case 'redemptionPlate':
        return document.querySelector('[id^="redeem-plate-"]') as HTMLElement | null;
      case 'redemptionPhone':
        return document.querySelector('[id^="redeem-phone-"]') as HTMLElement | null;
      case 'redemptionEmail':
        return document.querySelector('[id^="redeem-email-"]') as HTMLElement | null;
      case 'redemptionFirstName':
        return document.querySelector('[id^="redeem-first-"]') as HTMLElement | null;
      case 'redemptionLastName':
        return document.querySelector('[id^="redeem-last-"]') as HTMLElement | null;
      default:
        return null;
    }
  }

  private handleCreateCustomerRequest(): void {
    // First create customer with type 'regular'
    const customerData = {
      firstName: this.formData.customerFirstName,
      lastName: this.formData.customerLastName,
      email: this.formData.customerEmail,
      phone: this.formData.customerContact,
      licensePlate: this.formData.vehicleLicensePlate,
      customerType: 'regular',
    };

    console.log('🔄 Creating customer for create-customer-request:', customerData);

    this.createCustomerForRequest(customerData);
  }

  private createCustomerForRequest(customerData: any): void {
    // Create customer with type 'regular'
    const payload: any = {
      first_name: customerData.firstName,
      last_name: customerData.lastName,
      email: customerData.email,
      contact: customerData.phone,
      type: 'regular',
      license_plate: null,
      customer_status: 'active',
      force_create: false,
    };

    // Add vehicle make, model, and year when creating a customer for LPR flows (including create-order-request without id).
    if (
      this.notification?.type === 'invalid-license-plate' ||
      this.notification?.type === 'create-order-request' ||
      (this.notification?.type === 'create-customer-request' && this.showVehicleMakeModelYearFields)
    ) {
      if (this.formData.vehicleMake) {
        payload.make = this.formData.vehicleMake;
      }
      if (this.formData.vehicleModel) {
        payload.model = this.formData.vehicleModel;
      }
      if (this.formData.vehicleYear) {
        payload.year = this.formData.vehicleYear;
      }
      if (this.formData.vehicleVin) {
        payload.vin = this.formData.vehicleVin;
      }
    }

    this.apiService
      .post('customer', payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          console.log('✅ Customer API responded:', response);

          // Extract customer ID from response
          const customerId = response.data?.customer_id || response.data?.id;
          if (!customerId) {
            console.error('❌ No customer ID found in response:', response);
            this.updateOrderError = sanitizeErrorMessage(
              'Customer created but no customer ID returned'
            );
            this.isUpdatingOrder = false;
            return;
          }

          this.formData.customerId = customerId;
          console.log('📝 Updated formData with customer ID:', customerId);

          // Detect "already exists" success response (status 200 but message indicates duplicate)
          const message: string = response.message || response.data?.message || '';
          const isAlreadyExistingCustomer =
            typeof message === 'string' && message.toLowerCase().includes('already exists');

          if (isAlreadyExistingCustomer) {
            console.log(
              'ℹ️ Existing customer detected from create response, updating with form data.'
            );
            this.updateExistingCustomerFromForm(customerId);
            return;
          }

          // Newly created customer flow
          this.finalizeCustomerCreationOrUpdate(customerId);
        },
        error: error => {
          console.error('❌ Error creating customer:', error);

          // Handle duplicate customer case returned as error
          if (error.status === 409 || error.error?.message?.includes('already exists')) {
            console.log('🔄 Customer already exists (error response), extracting customer ID');

            // Try to extract customer ID from error response
            const customerId = error.error?.data?.customer_id || error.error?.data?.id;
            if (customerId) {
              this.formData.customerId = customerId;
              console.log('📝 Using existing customer ID from error:', customerId);

              // Proceed with existing customer flow
              this.finalizeCustomerCreationOrUpdate(customerId);
            } else {
              this.updateOrderError = sanitizeErrorMessage(
                'Customer already exists but no customer ID found'
              );
              this.isUpdatingOrder = false;
            }
          } else {
            this.updateOrderError = sanitizeErrorMessage(
              error.error?.message || 'Failed to create customer. Please try again.'
            );
            this.isUpdatingOrder = false;
          }
        },
      });
  }

  /**
   * When API says customer already exists (status 200 with "already exists" message),
   * update that existing customer with the values entered in the form.
   */
  private updateExistingCustomerFromForm(customerId: string): void {
    const payload: any = {
      first_name: this.formData.customerFirstName,
      last_name: this.formData.customerLastName || null,
      email: this.formData.customerEmail,
      contact: this.formData.customerContact,
      type: 'regular',
      license_plate: this.formData.vehicleLicensePlate || '',
    };

    console.log('🔄 Updating existing customer from create flow:', customerId, payload);

    this.customerService
      .updateCustomer(customerId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: updateResponse => {
          console.log('✅ Existing customer updated from create flow:', updateResponse);
          this.finalizeCustomerCreationOrUpdate(customerId);
        },
        error: error => {
          console.error('❌ Error updating existing customer from create flow:', error);
          this.updateOrderError = sanitizeErrorMessage(
            error.error?.message || 'Failed to update existing customer. Please try again.'
          );
          this.isUpdatingOrder = false;
        },
      });
  }

  /**
   * Common logic to run after a customer is either created or updated for this flow.
   */
  private finalizeCustomerCreationOrUpdate(customerId: string): void {
    // LPR entry sync runs on modal dismiss from the order-request screen (not on checkout proceed).

    // Check customer orders for subscription suggestion
    if (
      this.notification?.type === 'create-order-request' ||
      this.notification?.type === 'create-customer-request' ||
      this.notification?.type === 'invalid-license-plate'
    ) {
      this.checkCustomerOrdersForSubscription();
    }

    // Now create the order with the customer ID
    this.createOrderAfterCustomerCreation();
  }

  private createOrderAfterCustomerCreation(): void {
    if (!this.formData.customerId) {
      console.error('❌ Customer ID is missing');
      this.updateOrderError = sanitizeErrorMessage('Customer ID is required to fetch vehicles');
      this.isUpdatingOrder = false;
      return;
    }

    const licensePlate = this.formData.vehicleLicensePlate?.trim();
    if (!licensePlate) {
      console.warn('⚠️ License plate is empty, proceeding without vehicle ID');
      this.createOrderAfterVehicleCreation();
      return;
    }

    // Prefer PUT updateVehicle (link customer_id) after successful customer creation, then fall back to list/create.
    this.tryLinkVehicleToCustomerAfterCustomerCreation(licensePlate);
  }

  /**
   * After a new customer is created, link the vehicle row to that customer via updateVehicle when we already
   * have a vehicle id (e.g. from notification). Otherwise resolve id via get-vehicle-data then updateVehicle.
   * Falls back to vehicle/list + match or createVehicleForCustomer if lookup/update fails.
   */
  private tryLinkVehicleToCustomerAfterCustomerCreation(licensePlate: string): void {
    const customerId = this.formData.customerId;
    if (!customerId) {
      return;
    }

    const existingVehicleId = (this.formData.vehicleId || '').toString().trim();
    const plateKey = this.plateKeyFromFullPlate(licensePlate);
    const linked = this.linkedVehicleAtSelection;
    const sameVehicleAndPlate =
      !!existingVehicleId &&
      !!linked &&
      linked.vehicleId === existingVehicleId &&
      linked.plateKey === plateKey;

    if (sameVehicleAndPlate) {
      const parsed = splitLicensePlateForApi(licensePlate, this.notification?.data?.state);
      const state = parsed.state;
      const plateNoState = parsed.license_plate;
      let yearNum = parseInt(String(this.formData.vehicleYear || ''), 10);
      if (!Number.isFinite(yearNum)) {
        yearNum = 0;
      }
      const updatePayload = {
        license_plate: plateNoState,
        state,
        vin: this.formData.vehicleVin || '',
        make: this.formData.vehicleMake || '',
        model: this.formData.vehicleModel || '',
        year: yearNum,
        customer_id: customerId,
        is_family_plan: false,
      };
      console.log('🔗 Linking same vehicle row to customer (plate unchanged):', existingVehicleId);
      this.vehicleService
        .updateVehicle(existingVehicleId, updatePayload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            if (response.status) {
              this.createOrderAfterVehicleCreation();
            } else {
              this.matchOrCreateVehicleForCustomerPlate(licensePlate);
            }
          },
          error: () => this.matchOrCreateVehicleForCustomerPlate(licensePlate),
        });
      return;
    }

    if (existingVehicleId) {
      this.formData.vehicleId = '';
      this.linkedVehicleAtSelection = null;
    }

    this.tryLinkVehicleViaPlateLookupAfterCustomerCreation(licensePlate);
  }

  private tryLinkVehicleViaPlateLookupAfterCustomerCreation(licensePlate: string): void {
    const customerId = this.formData.customerId;
    if (!customerId) {
      return;
    }

    if (this.linkCachedVehicleLookupToCustomerThenCreateOrder(licensePlate, customerId)) {
      return;
    }

    if (this.wasVehiclePlateLookupAttempted(licensePlate)) {
      this.fetchVehiclesByCustomerIdForNewCustomerFlow(licensePlate);
      return;
    }

    this.tryLinkVehicleViaPlateLookupAfterCustomerCreationFetch(licensePlate);
  }

  /**
   * For an existing customer: use a vehicle row that already has this plate, or POST a new vehicle.
   * Never rewrites an unrelated vehicle id with a new plate number.
   */
  private matchOrCreateVehicleForCustomerPlate(
    licensePlate: string,
    then: () => void = () => this.createOrderAfterVehicleCreation()
  ): void {
    if (!this.formData.customerId) {
      then();
      return;
    }

    console.log(
      '🔄 Resolving vehicle for customer by plate:',
      this.formData.customerId,
      licensePlate
    );

    const parsed = splitLicensePlateForApi(licensePlate, this.notification?.data?.state);
    const licensePlateWithoutState = parsed.license_plate.trim().toUpperCase();
    const stateUpper = parsed.state.trim().toUpperCase();

    this.vehicleService
      .getVehiclesByCustomerId(this.formData.customerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.status && response.data && Array.isArray(response.data)) {
            const matchingVehicle = response.data.find((vehicle: any) => {
              const vehicleLicensePlate = (vehicle.license_plate || vehicle.licensePlate || '')
                .trim()
                .toUpperCase();
              const vehicleState = String(vehicle.state || parsed.state || '')
                .trim()
                .toUpperCase();
              return (
                vehicleLicensePlate === licensePlateWithoutState &&
                (!vehicle.state || vehicleState === stateUpper)
              );
            });

            if (matchingVehicle) {
              const vehicleId = String(
                matchingVehicle.vehicle_id || matchingVehicle.id || ''
              ).trim();
              if (vehicleId) {
                this.formData.vehicleId = vehicleId;
                this.syncLinkedVehicleSelection(vehicleId, licensePlate);
                if (matchingVehicle.make && !this.formData.vehicleMake) {
                  this.formData.vehicleMake = matchingVehicle.make;
                }
                if (matchingVehicle.model && !this.formData.vehicleModel) {
                  this.formData.vehicleModel = matchingVehicle.model;
                }
                if (matchingVehicle.year && !this.formData.vehicleYear) {
                  this.formData.vehicleYear = matchingVehicle.year.toString();
                }
                console.log('✅ Using existing customer vehicle for plate:', vehicleId);
                then();
                return;
              }
              this.updateOrderError = sanitizeErrorMessage(
                'Vehicle found but no vehicle ID available'
              );
              this.isUpdatingOrder = false;
              return;
            }
          }

          if (this.hasCachedVehicleLookupForPlate(licensePlate)) {
            this.persistVehicleForCustomer(
              {
                license_plate: parsed.license_plate,
                state: parsed.state,
                vin: this.formData.vehicleVin || '',
                make: this.formData.vehicleMake || '',
                model: this.formData.vehicleModel || '',
                year: this.formData.vehicleYear || new Date().getFullYear(),
                customer_id: this.formData.customerId,
                is_family_plan: false,
              },
              then
            );
            return;
          }

          this.createVehicleForCustomer(licensePlate, then);
        },
        error: error => {
          console.error('❌ Error fetching vehicles for customer:', error);
          this.createVehicleForCustomer(licensePlate, then);
        },
      });
  }

  /** @deprecated Use {@link matchOrCreateVehicleForCustomerPlate}. */
  private fetchVehiclesByCustomerIdForNewCustomerFlow(licensePlate: string): void {
    this.matchOrCreateVehicleForCustomerPlate(licensePlate);
  }

  private createVehicleForCustomer(
    fullLicensePlate: string,
    then: () => void = () => this.createOrderAfterVehicleCreation()
  ): void {
    const parsed = splitLicensePlateForApi(fullLicensePlate, this.notification?.data?.state);
    const state = parsed.state;
    const licensePlateWithoutState = parsed.license_plate;

    if (!licensePlateWithoutState) {
      console.warn('⚠️ License plate is empty after parsing, proceeding without vehicle ID');
      then();
      return;
    }

    // Check if vehicle data is available from notification
    const vin =
      this.formData.vehicleVin ||
      this.notification?.data?.vehicle_vin ||
      this.notification?.data?.vehicleVin;
    const make =
      this.formData.vehicleMake ||
      this.notification?.data?.vehicle_make ||
      this.notification?.data?.vehicleMake;
    const model =
      this.formData.vehicleModel ||
      this.notification?.data?.vehicle_model ||
      this.notification?.data?.vehicleModel;
    const year =
      this.formData.vehicleYear ||
      this.notification?.data?.vehicle_year ||
      this.notification?.data?.vehicleYear;

    if (this.hasCachedVehicleLookupForPlate(fullLicensePlate)) {
      this.persistVehicleForCustomer(
        {
          license_plate: licensePlateWithoutState,
          state: state,
          vin: this.formData.vehicleVin || vin || '',
          make: this.formData.vehicleMake || make || '',
          model: this.formData.vehicleModel || model || '',
          year: this.formData.vehicleYear || year || new Date().getFullYear(),
          customer_id: this.formData.customerId,
          is_family_plan: false,
        },
        then
      );
      return;
    }

    const needsVehicleDataFetch =
      (!make || !model) && !this.wasVehiclePlateLookupAttempted(fullLicensePlate);

    if (needsVehicleDataFetch && licensePlateWithoutState && state) {
      // Fetch vehicle data using the API
      console.log('🔄 Fetching vehicle data before persisting vehicle for customer...');
      const payload = {
        license_plate: licensePlateWithoutState,
        state: state,
      };

      this.markVehiclePlateLookupAttempted(fullLicensePlate);
      this.vehicleService
        .getVehicleData(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            if (response.status && response.data) {
              console.log('✅ Vehicle data fetched successfully:', response.data);
              this.vehicleDataFetched = true;
              this.fetchedVehicleId = (response.data.id || '').toString().trim();
              this.lastVehicleLookupPlate =
                this.normalizePlateForVehicleLookupCache(fullLicensePlate);
              this.persistVehicleForCustomer(
                {
                  license_plate: licensePlateWithoutState,
                  state: state,
                  vin: response.data.vin || vin || '',
                  make: response.data.make || make || '',
                  model: response.data.model || model || '',
                  year: response.data.year || year || new Date().getFullYear(),
                  customer_id: this.formData.customerId,
                  is_family_plan: false,
                },
                then
              );
            } else {
              console.log(
                '⚠️ Vehicle fetch failed, proceeding with available data from notification'
              );
              this.persistVehicleForCustomer(
                {
                  license_plate: licensePlateWithoutState,
                  state: state,
                  vin: vin || '',
                  make: make || '',
                  model: model || '',
                  year: year || new Date().getFullYear(),
                  customer_id: this.formData.customerId,
                  is_family_plan: false,
                },
                then
              );
            }
          },
          error: error => {
            console.error('❌ Error fetching vehicle data:', error);
            console.log('⚠️ Proceeding with available vehicle data from notification');
            this.persistVehicleForCustomer(
              {
                license_plate: licensePlateWithoutState,
                state: state,
                vin: vin || '',
                make: make || '',
                model: model || '',
                year: year || new Date().getFullYear(),
                customer_id: this.formData.customerId,
                is_family_plan: false,
              },
              then
            );
          },
        });
    } else {
      this.orderCompletedVehicleYear =
        this.formData.vehicleYear ||
        this.notification?.data?.vehicle_year?.toString() ||
        this.notification?.data?.vehicleYear?.toString() ||
        this.notification?.data?.vehicle_data?.year?.toString() ||
        this.notification?.data?.vehicle_data?.vehicle_year?.toString() ||
        '';
      this.persistVehicleForCustomer(
        {
          license_plate: licensePlateWithoutState,
          state: state,
          vin: vin || '',
          make: make || '',
          model: model || '',
          year: year || new Date().getFullYear(),
          customer_id: this.formData.customerId,
          is_family_plan: false,
        },
        then
      );
    }
  }

  /**
   * Persist vehicle for the customer:
   * - If getVehicleData succeeded for this plate → PUT updateVehicle (attach customer_id)
   * - Otherwise → POST createVehicle (new row — never change another vehicle's plate)
   */
  private persistVehicleForCustomer(
    vehicleData: any,
    then: () => void = () => this.createOrderAfterVehicleCreation()
  ): void {
    const fetchedId = (this.fetchedVehicleId || '').toString().trim();
    const alreadyHasId = (this.formData.vehicleId || '').toString().trim();
    const plate = (this.formData.vehicleLicensePlate || '').trim();
    const plateCacheKey = plate ? this.normalizePlateForVehicleLookupCache(plate) : '';
    const canLinkExistingFromPlateLookup =
      !!fetchedId &&
      (this.vehicleDataFetched || this.hasCachedVehicleLookupForPlate(plate)) &&
      !!plateCacheKey &&
      this.lastVehicleLookupPlate === plateCacheKey;

    const linked = this.linkedVehicleAtSelection;
    const vehicleStillMatchesPlate =
      !!alreadyHasId &&
      !!linked &&
      linked.vehicleId === alreadyHasId &&
      !!plate &&
      linked.plateKey === this.plateKeyFromFullPlate(plate);

    if (alreadyHasId && !canLinkExistingFromPlateLookup && vehicleStillMatchesPlate) {
      console.log('✅ Vehicle ID already set for current plate, proceeding:', alreadyHasId);
      then();
      return;
    }

    if (alreadyHasId && !vehicleStillMatchesPlate) {
      this.formData.vehicleId = '';
      this.linkedVehicleAtSelection = null;
    }

    if (canLinkExistingFromPlateLookup) {
      // Plate lookup returned an existing vehicle row → link customer via PUT
      console.log('🔗 Vehicle fetched with id, updating to link customer:', fetchedId);
      const updatePayload = {
        license_plate: vehicleData.license_plate,
        state: vehicleData.state,
        vin: vehicleData.vin || '',
        make: vehicleData.make || '',
        model: vehicleData.model || '',
        year:
          typeof vehicleData.year === 'string'
            ? parseInt(vehicleData.year, 10)
            : vehicleData.year || 0,
        customer_id: vehicleData.customer_id,
        is_family_plan: vehicleData.is_family_plan ?? false,
      };
      this.vehicleService
        .updateVehicle(fetchedId, updatePayload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            if (response.status) {
              this.formData.vehicleId = fetchedId;
              if (plate) {
                this.syncLinkedVehicleSelection(fetchedId, plate);
              }
              console.log('✅ Vehicle linked to customer via update:', fetchedId);
              then();
            } else {
              const errorMsg = sanitizeErrorMessage(
                response.message || 'Failed to link vehicle to customer'
              );
              this.updateOrderError = errorMsg;
              this.isUpdatingOrder = false;
              this.messageService.add({
                severity: 'error',
                summary: 'Vehicle Error',
                detail: errorMsg,
              });
            }
          },
          error: error => {
            const errorMsg = sanitizeErrorMessage(
              error.error?.message || error.message || 'Failed to update vehicle. Please try again.'
            );
            this.updateOrderError = errorMsg;
            this.isUpdatingOrder = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Vehicle Error',
              detail: errorMsg,
            });
          },
        });
      return;
    }

    // No fetched id — manually entered vehicle, call create
    console.log('🚗 Creating new vehicle for customer:', vehicleData);
    this.vehicleService
      .createVehicle(vehicleData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          console.log('✅ Vehicle created successfully:', response);
          const vehicleId = response.data?.vehicle_id || response.data?.id;
          if (vehicleId) {
            this.formData.vehicleId = vehicleId;
            if (plate) {
              this.syncLinkedVehicleSelection(String(vehicleId), plate);
            }
            console.log('📝 Updated formData with vehicle ID:', vehicleId);
            then();
          } else {
            console.error('❌ No vehicle ID found in response:', response);
            this.updateOrderError = sanitizeErrorMessage(
              'Vehicle created but no vehicle ID returned'
            );
            this.isUpdatingOrder = false;
          }
        },
        error: error => {
          console.error('❌ Error creating vehicle:', error);
          const errorMessage = sanitizeErrorMessage(
            error.error?.message || error.message || 'Failed to create vehicle. Please try again.'
          );
          const errorStatus = error.status || error.error?.status_code;
          const isLimitError =
            errorMessage.toLowerCase().includes('limit') ||
            errorMessage.toLowerCase().includes('5 vehicles') ||
            errorMessage.toLowerCase().includes('maximum') ||
            (errorStatus === 409 && errorMessage.toLowerCase().includes('add'));
          if (isLimitError) {
            this.updateOrderError = errorMessage;
            this.isUpdatingOrder = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Vehicle Limit Reached',
              detail: errorMessage,
            });
            return;
          }
          if (errorStatus === 409 || errorMessage.toLowerCase().includes('already exists')) {
            const vehicleId = error.error?.data?.vehicle_id || error.error?.data?.id;
            if (vehicleId) {
              this.formData.vehicleId = vehicleId;
              if (plate) {
                this.syncLinkedVehicleSelection(String(vehicleId), plate);
              }
              then();
            } else {
              this.vehicleService
                .getVehiclesByCustomerId(this.formData.customerId)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: fetchResponse => {
                    if (
                      fetchResponse.status &&
                      fetchResponse.data &&
                      Array.isArray(fetchResponse.data)
                    ) {
                      const searchPlate = vehicleData.license_plate.trim().toUpperCase();
                      const match = fetchResponse.data.find(
                        (v: any) =>
                          (v.license_plate || v.licensePlate || '').trim().toUpperCase() ===
                          searchPlate
                      );
                      if (match) {
                        const foundId = match.vehicle_id || match.id;
                        if (foundId) {
                          this.formData.vehicleId = foundId;
                          if (match.make && !this.formData.vehicleMake)
                            this.formData.vehicleMake = match.make;
                          if (match.model && !this.formData.vehicleModel)
                            this.formData.vehicleModel = match.model;
                          if (match.year && !this.formData.vehicleYear)
                            this.formData.vehicleYear = match.year.toString();
                          console.log('✅ Found vehicle ID after retry:', foundId);
                          if (plate) {
                            this.syncLinkedVehicleSelection(String(foundId), plate);
                          }
                          then();
                        } else {
                          const msg = sanitizeErrorMessage(
                            'Vehicle exists but no vehicle ID available'
                          );
                          this.updateOrderError = msg;
                          this.isUpdatingOrder = false;
                          this.messageService.add({
                            severity: 'error',
                            summary: 'Vehicle Error',
                            detail: msg,
                          });
                        }
                      } else {
                        const msg = sanitizeErrorMessage('Vehicle exists but could not be found');
                        this.updateOrderError = msg;
                        this.isUpdatingOrder = false;
                        this.messageService.add({
                          severity: 'error',
                          summary: 'Vehicle Error',
                          detail: msg,
                        });
                      }
                    } else {
                      const msg = sanitizeErrorMessage(
                        'Vehicle exists but could not fetch vehicle list'
                      );
                      this.updateOrderError = msg;
                      this.isUpdatingOrder = false;
                      this.messageService.add({
                        severity: 'error',
                        summary: 'Vehicle Error',
                        detail: msg,
                      });
                    }
                  },
                  error: () => {
                    this.updateOrderError = errorMessage;
                    this.isUpdatingOrder = false;
                    this.messageService.add({
                      severity: 'error',
                      summary: 'Vehicle Error',
                      detail: errorMessage,
                    });
                  },
                });
            }
          } else {
            this.updateOrderError = errorMessage;
            this.isUpdatingOrder = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Vehicle Creation Failed',
              detail: errorMessage,
            });
          }
        },
      });
  }

  /** @deprecated — replaced by persistVehicleForCustomer; kept for backward-compat references */
  private createVehicleWithDataForCustomer(vehicleData: any): void {
    this.persistVehicleForCustomer(vehicleData);
  }

  private createVehicleForRequest(): void {
    // Extract vehicle details from notification data
    const fullLicensePlate = this.formData.vehicleLicensePlate || this.notification?.data?.plate;
    const parsed = splitLicensePlateForApi(
      String(fullLicensePlate || ''),
      this.notification?.data?.state
    );
    const licensePlateWithoutState = parsed.license_plate;
    const state = parsed.state || this.notification?.data?.state;

    // Check if vin, make, or model are missing
    const vin = this.notification?.data?.vehicle_vin || this.notification?.data?.vehicleVin;
    const make = this.notification?.data?.vehicle_make || this.notification?.data?.vehicleMake;
    const model = this.notification?.data?.vehicle_model || this.notification?.data?.vehicleModel;

    const fullPlate = String(fullLicensePlate || '').trim();
    if (this.hasCachedVehicleLookupForPlate(fullPlate)) {
      this.persistVehicleForRequest({
        license_plate: licensePlateWithoutState,
        state: state,
        vin: this.formData.vehicleVin || vin || '',
        make: this.formData.vehicleMake || make || '',
        model: this.formData.vehicleModel || model || '',
        year:
          this.formData.vehicleYear ||
          this.notification?.data?.vehicle_year ||
          this.notification?.data?.vehicleYear ||
          new Date().getFullYear(),
        customer_id: this.formData.customerId,
        is_family_plan: false,
      });
      return;
    }

    const needsVehicleDataFetch =
      (!make || !model) && !this.wasVehiclePlateLookupAttempted(fullPlate);

    if (needsVehicleDataFetch && licensePlateWithoutState && state) {
      // Fetch vehicle data using the API
      console.log('🔄 Fetching vehicle data before creating vehicle...');
      const payload = {
        license_plate: licensePlateWithoutState,
        state: state,
      };

      this.markVehiclePlateLookupAttempted(fullPlate);
      this.vehicleService
        .getVehicleData(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            if (response.status && response.data) {
              console.log('✅ Vehicle data fetched successfully:', response.data);
              this.vehicleDataFetched = true;
              this.fetchedVehicleId = (response.data.id || '').toString().trim();
              this.lastVehicleLookupPlate = this.normalizePlateForVehicleLookupCache(fullPlate);
              this.persistVehicleForRequest({
                license_plate: licensePlateWithoutState,
                state: state,
                vin: response.data.vin || vin || '',
                make: response.data.make || make || '',
                model: response.data.model || model || '',
                year:
                  response.data.year ||
                  this.notification?.data?.vehicle_year ||
                  this.notification?.data?.vehicleYear ||
                  new Date().getFullYear(),
                customer_id: this.formData.customerId,
                is_family_plan: false,
              });
            } else {
              console.warn('⚠️ Failed to fetch vehicle data, proceeding with available data');
              this.persistVehicleForRequest({
                license_plate: licensePlateWithoutState,
                state: state,
                vin: vin || '',
                make: make || '',
                model: model || '',
                year:
                  this.notification?.data?.vehicle_year ||
                  this.notification?.data?.vehicleYear ||
                  new Date().getFullYear(),
                customer_id: this.formData.customerId,
                is_family_plan: false,
              });
            }
          },
          error: error => {
            console.error('❌ Error fetching vehicle data:', error);
            console.log('⚠️ Proceeding with available vehicle data');
            this.persistVehicleForRequest({
              license_plate: licensePlateWithoutState,
              state: state,
              vin: vin || '',
              make: make || '',
              model: model || '',
              year: this.notification?.data?.vehicleYear || new Date().getFullYear(),
              customer_id: this.formData.customerId,
              is_family_plan: false,
            });
          },
        });
    } else {
      this.orderCompletedVehicleYear =
        this.formData.vehicleYear ||
        this.notification?.data?.vehicle_year?.toString() ||
        this.notification?.data?.vehicleYear?.toString() ||
        this.notification?.data?.vehicle_data?.year?.toString() ||
        this.notification?.data?.vehicle_data?.vehicle_year?.toString() ||
        '';
      this.persistVehicleForRequest({
        license_plate: licensePlateWithoutState,
        state: state,
        vin: vin || '',
        make: make || '',
        model: model || '',
        year: this.notification?.data?.vehicleYear || new Date().getFullYear(),
        customer_id: this.formData.customerId,
        is_family_plan: false,
      });
    }
  }

  /**
   * Persist vehicle for the create-customer-request flow:
   * - If getVehicleData succeeded and returned an id → PUT updateVehicle (link customer_id, skip double-create)
   * - Otherwise → POST createVehicle via apiService
   */
  private persistVehicleForRequest(vehicleData: any): void {
    console.log('📝 License plate transformation:', {
      original: this.formData.vehicleLicensePlate || this.notification?.data?.plate,
      withoutState: vehicleData.license_plate,
      state: vehicleData.state,
    });

    const fetchedId = (this.fetchedVehicleId || '').toString().trim();
    const alreadyHasId = (this.formData.vehicleId || '').toString().trim();
    const plate = (this.formData.vehicleLicensePlate || '').trim();
    const canLinkExistingFromPlateLookup =
      !!fetchedId && (this.vehicleDataFetched || this.hasCachedVehicleLookupForPlate(plate));

    if (alreadyHasId && !canLinkExistingFromPlateLookup) {
      console.log('✅ Vehicle ID already set, skipping create/update:', alreadyHasId);
      this.createOrderAfterVehicleCreation();
      return;
    }

    if (canLinkExistingFromPlateLookup) {
      console.log(
        '🔗 Vehicle fetched with id, updating to link customer (request flow):',
        fetchedId
      );
      const updatePayload = {
        license_plate: vehicleData.license_plate,
        state: vehicleData.state,
        vin: vehicleData.vin || '',
        make: vehicleData.make || '',
        model: vehicleData.model || '',
        year:
          typeof vehicleData.year === 'string'
            ? parseInt(vehicleData.year, 10)
            : vehicleData.year || 0,
        customer_id: vehicleData.customer_id,
        is_family_plan: vehicleData.is_family_plan ?? false,
      };
      this.vehicleService
        .updateVehicle(fetchedId, updatePayload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            if (response.status) {
              this.formData.vehicleId = fetchedId;
              console.log('✅ Vehicle linked to customer via update (request flow):', fetchedId);
              this.createOrderAfterVehicleCreation();
            } else {
              const errorMsg = sanitizeErrorMessage(
                response.message || 'Failed to link vehicle to customer'
              );
              this.updateOrderError = errorMsg;
              this.isUpdatingOrder = false;
              this.messageService.add({
                severity: 'error',
                summary: 'Vehicle Error',
                detail: errorMsg,
              });
            }
          },
          error: error => {
            const errorMsg = sanitizeErrorMessage(
              error.error?.message || error.message || 'Failed to update vehicle. Please try again.'
            );
            this.updateOrderError = errorMsg;
            this.isUpdatingOrder = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Vehicle Error',
              detail: errorMsg,
            });
          },
        });
      return;
    }

    // No fetched id — create new vehicle
    console.log('🚗 Creating vehicle for create-customer-request:', vehicleData);
    this.apiService
      .post('vehicle', vehicleData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          console.log('✅ Vehicle created successfully:', response);
          const vehicleId = response.data?.vehicle_id || response.data?.id;
          if (vehicleId) {
            this.formData.vehicleId = vehicleId;
            console.log('📝 Updated formData with vehicle ID:', vehicleId);
            this.createOrderAfterVehicleCreation();
          } else {
            console.error('❌ No vehicle ID found in response:', response);
            this.updateOrderError = sanitizeErrorMessage(
              'Vehicle created but no vehicle ID returned'
            );
            this.isUpdatingOrder = false;
          }
        },
        error: error => {
          console.error('❌ Error creating vehicle:', error);
          if (error.status === 409 || error.error?.message?.includes('already exists')) {
            const vehicleId = error.error?.data?.vehicle_id || error.error?.data?.id;
            if (vehicleId) {
              this.formData.vehicleId = vehicleId;
              console.log('📝 Using existing vehicle ID from duplicate error:', vehicleId);
              this.createOrderAfterVehicleCreation();
            } else {
              this.updateOrderError = sanitizeErrorMessage(
                'Vehicle already exists but no vehicle ID found'
              );
              this.isUpdatingOrder = false;
            }
          } else {
            this.updateOrderError = sanitizeErrorMessage(
              error.error?.message || 'Failed to create vehicle. Please try again.'
            );
            this.isUpdatingOrder = false;
          }
        },
      });
  }

  /** @deprecated — replaced by persistVehicleForRequest */
  private createVehicleWithData(vehicleData: any): void {
    this.persistVehicleForRequest(vehicleData);
  }

  /**
   * Update notification with order_id and event create-payment-request.
   * Called immediately after POST /order succeeds so the LPR row gets order_id before checkout.
   */
  private updateNotificationAfterOrderCreation(orderId: string): void {
    if (this.isDirectOrder()) {
      return;
    }
    // Get notification ID from formData (preserved early) or fallback to notification data
    const notificationId =
      this.formData.notificationId ||
      this.notification?.data?.id ||
      this.notification?.data?.licence_plate_id ||
      this.notification?.data?.license_plate_id;

    if (!notificationId) {
      console.warn(
        '⚠️ Notification ID not found, skipping notification update after order creation'
      );
      return;
    }

    // Get all relevant data for the update
    const customerId =
      this.formData.customerId ||
      this.notification?.data?.customer_id ||
      this.notification?.data?.customerId ||
      null;
    const vehicleId =
      this.formData.vehicleId ||
      this.notification?.data?.vehicle_id ||
      this.notification?.data?.vehicleId ||
      null;
    const vehicleMake =
      this.formData.vehicleMake ||
      this.notification?.data?.vehicle_make ||
      this.notification?.data?.vehicleMake ||
      '';
    const vehicleModel =
      this.formData.vehicleModel ||
      this.notification?.data?.vehicle_model ||
      this.notification?.data?.vehicleModel ||
      '';
    const vehicleYear =
      this.formData.vehicleYear ||
      this.notification?.data?.vehicle_year ||
      this.notification?.data?.vehicleYear ||
      '';
    const vehicleVin =
      this.notification?.data?.vehicle_vin || this.notification?.data?.vehicleVin || '';
    const packageId =
      this.formData.packageId ||
      this.selectedPackage?.id ||
      this.notification?.data?.package_id ||
      this.notification?.data?.packageId ||
      null;
    // const licencePlateId = this.notification?.data?.licence_plate_id ||
    //                       this.notification?.data?.license_plate_id ||
    //                       notificationId;

    // Prepare payload for notification update after order creation
    // Event should be 'create-payment-request' and include order_id
    // Only include fields that have actual values (not null, not empty string)
    const updatePayload: any = {
      event: sanitizeLprEntryPutEvent('create-payment-request'),
      order_id: orderId,
    };

    // Only add fields that have actual values
    if (customerId) {
      updatePayload.customer_id = customerId;
    }
    if (vehicleId) {
      updatePayload.vehicle_id = vehicleId;
    }
    if (vehicleMake && vehicleMake.trim()) {
      updatePayload.vehicle_make = vehicleMake;
    }
    if (vehicleModel && vehicleModel.trim()) {
      updatePayload.vehicle_model = vehicleModel;
    }
    if (vehicleYear && String(vehicleYear).trim()) {
      updatePayload.vehicle_year = String(vehicleYear);
    }
    if (vehicleVin && vehicleVin.trim()) {
      updatePayload.vehicle_vin = vehicleVin;
    }
    if (packageId) {
      updatePayload.package_id = packageId;
    }
    Object.assign(
      updatePayload,
      buildLprEntryLocationLanePutFields({
        notificationData: this.notification?.data as Record<string, any> | undefined,
        locationId: this.formData.locationId,
        laneId: this.formData.laneId,
      })
    );
    // Persist the operator-confirmed plate (state prefix + typed number) so a manual
    // correction of a misread plate/state is saved on the entry itself. Identity stays
    // the entry id, not the plate.
    const correctedPlate = this.buildLprEntryPlateFromForm();
    if (correctedPlate) {
      updatePayload.plate = correctedPlate;
      updatePayload.state = this.resolveLprEntryStateFromForm();
    }

    console.log('🔄 Updating notification after order creation:', notificationId, updatePayload);

    // Call API to update notification
    this.apiService
      .put(`order/lpr-entries/${notificationId}`, updatePayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          console.log('✅ Notification updated successfully after order creation:', response);
          this.lprEntryCreatePaymentRequestSyncedOrderIds.add(orderId);
          setTimeout(
            () => this.lprEntryCreatePaymentRequestSyncedOrderIds.delete(orderId),
            5 * 60 * 1000
          );
          this.notificationService.syncLprEntryFromPutResponse(response);
        },
        error: error => {
          console.error('❌ Error updating notification after order creation:', error);
          // Don't show error to user or block flow - just log it
        },
      });
  }

  /** PUT create-payment-request + order_id on the LPR row once per order (after POST /order). */
  private syncLprEntryCreatePaymentRequestAfterOrderCreated(orderId: string): void {
    const normalizedOrderId = String(orderId || '').trim();
    if (!normalizedOrderId || this.isDirectOrder()) {
      return;
    }
    if (this.notification?.type !== 'create-payment-request' || this.isEditMode) {
      return;
    }
    if (this.lprEntryCreatePaymentRequestSyncedOrderIds.has(normalizedOrderId)) {
      return;
    }
    this.updateNotificationAfterOrderCreation(normalizedOrderId);
  }

  /**
   * Inline data for create-payment-request so checkout reads `order`, `discount`, `add_ons`
   * from POST /order instead of stale form defaults.
   */
  private buildCreatePaymentRequestDataFromResponse(
    orderId: string,
    preservedLocationId: string,
    response: CreateOrderResponse
  ): Record<string, any> {
    const resOrder = response.data?.order;
    const resBody = response.data;
    let addOnLines = resBody?.add_ons ?? resBody?.add_on_variants;
    if (!Array.isArray(addOnLines) || addOnLines.length === 0) {
      addOnLines = this.buildCreatePaymentRequestAddOnSnapshotRows();
    }
    const apiDisc = resBody?.discount;
    /** POST /order often omits a rich `discount` row for custom-only discounts — persist UI snapshot for Back → edit restore. */
    const uiDisc =
      this.orderRequestSelectedDiscountId === 'disc_custom' && this.orderRequestCustomDiscountSaved
        ? this.buildCreatePaymentRequestDiscountSnapshotRow()
        : null;
    const discount =
      uiDisc != null
        ? {
            ...(apiDisc && typeof apiDisc === 'object' && !Array.isArray(apiDisc)
              ? (apiDisc as Record<string, any>)
              : {}),
            ...uiDisc,
          }
        : apiDisc;
    const uiLines = this.buildCreateOrderAddOnVariantsPayload();
    const syncedFinal = this.computeOrderRequestFinalPrice();
    const apiFinal = this.parseUsdNumber(resOrder?.final_price);
    const syncedFinalN = this.parseUsdNumber(syncedFinal) ?? 0;
    const useComputedFinal =
      uiLines.length > 0 || (apiFinal != null && syncedFinalN > apiFinal + 0.001);
    const finalPrice = useComputedFinal ? syncedFinal : (resOrder?.final_price ?? syncedFinal);
    const prepaidId = this.getOrderRequestSelectedPrepaidBookIdForApi();
    const listPriceForPrepaid = prepaidId ? this.computeOrderRequestListPriceForApi() : undefined;
    const prepaidBookName =
      this.getOrderRequestSelectedPrepaidBook()?.name ||
      (resOrder as { prepaid_wash_book_name?: string } | undefined)?.prepaid_wash_book_name ||
      '';
    const buildOrderSnapshot = (): Record<string, any> | null | undefined => {
      if (prepaidId) {
        const snap: Record<string, any> = resOrder
          ? ({ ...(resOrder as Record<string, any>) } as Record<string, any>)
          : {};
        snap['wash_type'] = 'prepaid_wash_book';
        snap['prepaid_wash_book_id'] = prepaidId;
        snap['prepaid_wash_book_name'] = prepaidBookName;
        snap['final_price'] = finalPrice ?? snap['final_price'];
        if (listPriceForPrepaid != null) {
          snap['list_price'] = listPriceForPrepaid;
        }
        delete snap['package_id'];
        delete snap['package_name'];
        delete snap['packageId'];
        delete snap['packageName'];
        return snap;
      }
      return resOrder
        ? {
            ...resOrder,
            final_price: finalPrice ?? resOrder.final_price,
          }
        : resOrder;
    };
    return {
      orderId,
      order_id: orderId,
      public_order_id: resOrder?.order_id ?? '',
      customerId: this.formData.customerId,
      customerFirstName: this.formData.customerFirstName,
      customerLastName: this.formData.customerLastName,
      customerEmail: this.formData.customerEmail,
      customerContact: this.formData.customerContact,
      vehicleId: this.formData.vehicleId,
      vehicleLicensePlate: this.formData.vehicleLicensePlate,
      locationId: preservedLocationId,
      packageId: prepaidId
        ? ''
        : this.normalizeWashType(String(resOrder?.wash_type ?? this.getNormalizedWashType())) ===
            'add_on'
          ? ''
          : resOrder?.package_id || this.formData.packageId || this.selectedPackage?.id || '',
      packageName: prepaidId
        ? ''
        : this.normalizeWashType(String(resOrder?.wash_type ?? this.getNormalizedWashType())) ===
            'add_on'
          ? ''
          : resOrder?.package_name ||
            this.formData.package ||
            this.selectedPackage?.package_name ||
            '',
      finalPrice,
      appointmentTime: resOrder?.appointment_time || this.formData.appointmentTime,
      wash_type: prepaidId
        ? 'prepaid_wash_book'
        : resOrder?.wash_type || this.getNormalizedWashType(),
      ...(prepaidId
        ? { prepaid_wash_book_id: prepaidId, prepaid_wash_book_name: prepaidBookName }
        : {}),
      order: buildOrderSnapshot(),
      discount,
      add_ons: addOnLines,
      add_on_variants: addOnLines,
      ...(this.formData.notificationId
        ? {
            id: this.formData.notificationId,
            licence_plate_id: this.formData.notificationId,
            license_plate_id: this.formData.notificationId,
          }
        : {}),
    };
  }

  /** Swap to payment checkout in-place after POST /order — avoids dismiss suppression from hideModal. */
  private openPaymentCheckoutAfterOrderCreation(
    orderId: string,
    preservedLocationId: string,
    response: CreateOrderResponse
  ): void {
    const normalizedOrderId = String(orderId || '').trim();
    if (normalizedOrderId && !this.isDirectOrder()) {
      this.formData.orderId = normalizedOrderId;
      if (this.notification?.data) {
        this.notification.data.order_id = normalizedOrderId;
        this.notification.data.orderId = normalizedOrderId;
      }
    }
    const notificationId = String(
      this.formData.notificationId ||
        this.notification?.data?.id ||
        this.notification?.data?.licence_plate_id ||
        this.notification?.data?.license_plate_id ||
        ''
    ).trim();
    const locationPickerOrigin = this.resolveCurrentLocationPickerOrigin();
    const paymentRequestNotification: NotificationData = {
      type: 'create-payment-request',
      message: 'Payment request created',
      timestamp: new Date(),
      data: {
        ...this.buildCreatePaymentRequestDataFromResponse(orderId, preservedLocationId, response),
        open_checkout_summary: true,
        ...pickOrderRequestContextForCheckout(this.notification?.data),
        ...buildLocationPickerStamp(locationPickerOrigin, this.formData.laneId),
        ...(notificationId
          ? {
              id: notificationId,
              licence_plate_id: notificationId,
              license_plate_id: notificationId,
            }
          : {}),
      },
    };
    // POST /order often omits add-on rows/names — merge live order-request UI before checkout renders.
    this.notification = paymentRequestNotification;
    this.clearAutoSelectedWashPackageWhenAddonsCommitted();
    this.syncCreatePaymentRequestNotificationDataLayerFromOrderRequestUi();
    this.syncLprEntryCreatePaymentRequestAfterOrderCreated(normalizedOrderId);
    this.logger.trackNotification('Transitioning to create-payment-request', { orderId });
    this.modalStateService.forceShowModal(this.notification, false);
  }

  /** CWS-1945: forget the draft, the request id and any claim; a new checkout starts clean. */
  private resetCheckoutClaimState(): void {
    this.checkoutDraftPayload = null;
    this.checkoutDraftTouchedScopes.clear();
    this.checkoutRequestId = null;
    this.isClaimingCheckout = false;
    this.checkoutClaimPassed = false;
  }

  /** CWS-1945: checkout is showing an order that does not exist on the server yet. */
  isCheckoutDraft(): boolean {
    return this.checkoutDraftPayload !== null;
  }

  /**
   * CWS-1945: Continue. Persist the custom discount row if the form has one, then show checkout
   * for the order the form describes, without creating it.
   */
  private continueToCheckoutDraft(orderPayload: CreateOrderPayload): void {
    this.ensureOrderRequestCustomDiscountId$()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          Object.assign(orderPayload, this.buildCreateOrderDiscountAndAddonPayload());
          this.isUpdatingOrder = false;
          this.updateOrderError = '';
          this.isEditMode = false;
          this.checkoutDraftPayload = { ...orderPayload };
          this.checkoutDraftTouchedScopes.clear();
          this.checkoutRequestId = this.checkoutRequestId ?? newCheckoutRequestId();
          this.logger.trackOrder('Checkout draft opened (notification modal)', {
            notificationType: this.notification?.type,
            licensePlate: this.formData.vehicleLicensePlate,
          });
          const preservedLocationId =
            this.formData.locationId ||
            this.notification?.data?.location_id ||
            this.notification?.data?.locationId ||
            orderPayload.location_id ||
            '';
          this.openPaymentCheckoutAfterOrderCreation(
            '',
            preservedLocationId,
            buildCheckoutDraftResponse(orderPayload)
          );
        },
        error: error => {
          this.isUpdatingOrder = false;
          this.updateOrderError = sanitizeErrorMessage(
            error?.error?.message ||
              error?.message ||
              'Failed to prepare checkout. Please try again.'
          );
        },
      });
  }

  /** CWS-1945: the order id a pay press should claim, or '' while checkout shows a draft. */
  private resolveClaimableCheckoutOrderId(): string {
    if (this.checkoutDraftPayload) {
      return '';
    }
    return String(
      this.notification?.data?.order_id ||
        this.notification?.data?.orderId ||
        this.formData.orderId ||
        ''
    ).trim();
  }

  /** CWS-1945: the draft as it stands when the attendant pays, summary toggles included. */
  private buildCheckoutDraftForPayment(): CreateOrderPayload | null {
    if (!this.checkoutDraftPayload) {
      return null;
    }
    const touched = this.checkoutDraftTouchedScopes;
    return applyCheckoutSelectionToDraft(this.checkoutDraftPayload, touched, {
      finalPrice: this.computeOrderRequestFinalPrice(),
      listPrice: this.computeOrderRequestListPriceForApi(),
      discount: touched.has('discount') ? this.getCheckoutDiscountSelectionForSync() : undefined,
      addOnVariants: touched.has('addon') ? this.getCheckoutAddOnSelectionForSync() : undefined,
    });
  }

  /**
   * CWS-1945: every pay press first creates (or reuses) the order and claims it on the server, so
   * a second tablet showing the same car cannot pay it too. Returns true when it took over the
   * press: the handler stops, and `proceed` runs it again once the claim succeeded.
   */
  private claimCheckoutOrderThen(paymentMethod: string, proceed: () => void): boolean {
    if (this.checkoutClaimPassed) {
      this.checkoutClaimPassed = false;
      return false;
    }
    if (this.isClaimingCheckout) {
      return true;
    }
    const orderId = this.resolveClaimableCheckoutOrderId();
    const draft = orderId ? null : this.buildCheckoutDraftForPayment();
    if (!orderId && !draft) {
      return false;
    }
    // CWS-1987: an order this tablet already claimed keeps its id across reopen and app reload.
    const requestId =
      rememberedCheckoutRequestId(readCommonSettings().checkoutRequestIds, orderId) ??
      this.checkoutRequestId ??
      newCheckoutRequestId();
    this.checkoutRequestId = requestId;
    if (orderId) {
      // Saved before the call: if the server claims the order but the answer is lost, a reload
      // must still find the id the claim was taken with.
      this.rememberCheckoutClaim(orderId, requestId);
    }
    this.isClaimingCheckout = true;
    this.orderCreationService
      .checkoutOrder({
        request_id: requestId,
        payment_method: paymentMethod,
        ...(orderId ? { order_id: orderId } : { payload: draft as CreateOrderPayload }),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isClaimingCheckout = false;
          const claimedOrderId = String(response.data?.order?.id ?? '').trim();
          if (!claimedOrderId) {
            this.messageService.add({
              severity: 'error',
              summary: 'Payment Error',
              detail: 'Could not start the payment. Please try again.',
            });
            return;
          }
          this.rememberCheckoutClaim(claimedOrderId, requestId);
          this.adoptClaimedCheckoutOrder(claimedOrderId, response, !orderId);
          this.checkoutClaimPassed = true;
          try {
            proceed();
          } finally {
            this.checkoutClaimPassed = false;
          }
        },
        error: error => {
          this.isClaimingCheckout = false;
          this.logger.trackError('NotificationModal.checkoutOrder', error, {
            paymentMethod,
            orderId: orderId || undefined,
            notificationType: this.notification?.type,
          });
          this.messageService.add({
            severity: 'error',
            summary: isFinalCheckoutRefusal(error) ? 'Cannot take payment' : 'Payment Error',
            detail: sanitizeErrorMessage(checkoutClaimErrorMessage(error)),
          });
        },
      });
    return true;
  }

  /** CWS-1987: keep the id `orderId` is claimed with on this tablet, across reopen and reload. */
  private rememberCheckoutClaim(orderId: string, requestId: string): void {
    updateCommonSettings({
      checkoutRequestIds: rememberCheckoutRequestId(
        readCommonSettings().checkoutRequestIds,
        orderId,
        requestId
      ),
    });
  }

  /** CWS-1945: the claimed order replaces the draft; the payment handlers read its id from here. */
  private adoptClaimedCheckoutOrder(
    orderId: string,
    response: CheckoutOrderResponse,
    wasDraft: boolean
  ): void {
    this.formData.orderId = orderId;
    const data = this.notification?.data as Record<string, any> | undefined;
    if (data) {
      const serverOrder = response.data?.order as Record<string, any> | undefined;
      data['order_id'] = orderId;
      data['orderId'] = orderId;
      if (serverOrder?.['order_id']) {
        data['public_order_id'] = serverOrder['order_id'];
      }
      const prev =
        data['order'] && typeof data['order'] === 'object' && !Array.isArray(data['order'])
          ? (data['order'] as Record<string, any>)
          : {};
      data['order'] = {
        ...prev,
        id: orderId,
        ...(serverOrder?.['order_id'] ? { order_id: serverOrder['order_id'] } : {}),
        ...(serverOrder?.['stripe_account_id']
          ? { stripe_account_id: serverOrder['stripe_account_id'] }
          : {}),
      };
    }
    if (wasDraft) {
      this.checkoutDraftPayload = null;
      this.checkoutDraftTouchedScopes.clear();
      this.syncLprEntryCreatePaymentRequestAfterOrderCreated(orderId);
    }
  }

  /**
   * After payment, update the LPR entry so the notification list shows order-success (single wash)
   * or active-subscription (monthly membership) instead of create-payment-request.
   */
  private updateNotificationAfterPayment(orderId: string): void {
    if (this.isDirectOrder()) {
      return;
    }
    if (!orderId?.trim()) {
      return;
    }
    const normalizedOrderId = orderId.trim();
    if (this.lprEntryUpdatedAfterPaymentOrderIds.has(normalizedOrderId)) {
      return;
    }

    const notificationId = this.resolveLprEntryNotificationId();
    if (!notificationId) {
      console.warn('⚠️ Notification ID not found, skipping LPR entry update after payment');
      return;
    }

    const washTypeHint = this.resolveWashTypeHintForLprUpdate();
    // Only skip GET order/:id when wash type is confidently single/add-on.
    if (washTypeHint === 'single_wash' || washTypeHint === 'add_on') {
      this.putLprEntryAfterPayment(notificationId, normalizedOrderId, washTypeHint, null);
      return;
    }

    this.apiService
      .get(`order/${normalizedOrderId}`)
      .pipe(take(1))
      .subscribe({
        next: (orderResponse: any) => {
          const order = this.extractOrderRecordFromApiResponse(orderResponse);
          const washType = this.resolveWashTypeForLprUpdate(order);
          this.putLprEntryAfterPayment(notificationId, normalizedOrderId, washType, order);
        },
        error: error => {
          console.error('❌ Error fetching order for LPR update after payment:', error);
          const rawFallback = washTypeHint ?? this.getNormalizedWashType();
          const fallback = rawFallback === 'prepaid_wash_book' ? 'single_wash' : rawFallback;
          this.putLprEntryAfterPayment(notificationId, normalizedOrderId, fallback, null);
        },
      });
  }

  private resolveLprEntryNotificationId(): string {
    if (this.isDirectOrder()) {
      return '';
    }
    const fromForm = String(this.formData.notificationId || '').trim();
    if (fromForm) {
      return fromForm;
    }
    return resolveLprEntryIdFromNotificationData(
      this.notification?.data as Record<string, any> | undefined
    );
  }

  private resolveWashTypeHintForLprUpdate():
    'single_wash' | 'monthly_membership' | 'add_on' | null {
    if (!this.hasOrderRequestWashPackageForCheckout() && this.hasOrderRequestCommittedAddons()) {
      return 'add_on';
    }
    const persisted = this.resolvePersistedWashTypeForOrderPayload();
    if (persisted) {
      return persisted;
    }
    if (this.selectedWashType) {
      return this.normalizeWashType(this.selectedWashType);
    }
    return null;
  }

  private resolveWashTypeForLprUpdate(
    order?: Record<string, any> | null
  ): 'single_wash' | 'monthly_membership' | 'add_on' {
    const fromOrder = order?.['wash_type'] ?? order?.['washType'];
    if (fromOrder != null && String(fromOrder).trim()) {
      return this.normalizeWashType(String(fromOrder));
    }
    return this.resolveWashTypeHintForLprUpdate() ?? 'single_wash';
  }

  /**
   * Non-null Stripe subscription id from the loaded order (''/null/undefined → '').
   * The order GET is the source of truth — do not fall back to stale notification data.
   */
  private resolveOrderSubscriptionId(order?: Record<string, any> | null): string {
    const o = order ?? {};
    const id = String(o['subscription_id'] ?? o['subscriptionId'] ?? '').trim();
    if (!id || id.toLowerCase() === 'null' || id.toLowerCase() === 'undefined') {
      return '';
    }
    return id;
  }

  /**
   * True only when a monthly_membership order has a REAL subscription: a non-null
   * `subscription_id` AND (when the backend surfaces one) an active/trialing status.
   *
   * A succeeded one-time charge / `payment_done` is NOT sufficient: the backend can accept
   * the card charge, fail to create the Stripe subscription, and refund the charge — leaving
   * `subscription_id` null. Never treat that partial failure as an active membership.
   */
  private orderHasActiveMembershipSubscription(order?: Record<string, any> | null): boolean {
    if (!this.resolveOrderSubscriptionId(order)) {
      return false;
    }
    const o = order ?? {};
    const nested = o['subscription'];
    const status = String(
      o['subscription_status'] ??
        o['subscriptionStatus'] ??
        o['stripe_subscription_status'] ??
        (nested && typeof nested === 'object' ? (nested as Record<string, any>)['status'] : '') ??
        ''
    )
      .trim()
      .toLowerCase();
    if (!status) {
      // Some order payloads omit the status; a real subscription_id is a sufficient signal.
      return true;
    }
    return status === 'active' || status === 'trialing';
  }

  /** Backend-provided (or generic) reason a membership could not be activated. */
  private resolveMembershipActivationFailureMessage(order?: Record<string, any> | null): string {
    const o = order ?? {};
    const candidate = this.pickFirstNonEmpty(
      o['subscription_error'],
      o['membership_error'],
      o['failure_message'],
      o['error_message'],
      o['message'],
      this.notification?.data?.subscription_error,
      this.notification?.data?.message
    );
    return candidate || "Your card can't be used to purchase a membership.";
  }

  private buildLprEntryUpdatePayloadAfterPayment(
    orderId: string,
    washType: 'single_wash' | 'monthly_membership' | 'add_on',
    order?: Record<string, any> | null
  ): Record<string, any> {
    const o = order ?? {};
    const customerId =
      this.formData.customerId ||
      this.notification?.data?.customer_id ||
      this.notification?.data?.customerId ||
      o['customer_id'] ||
      o['customerId'] ||
      null;
    const vehicleId =
      this.formData.vehicleId ||
      this.notification?.data?.vehicle_id ||
      this.notification?.data?.vehicleId ||
      o['vehicle_id'] ||
      o['vehicleId'] ||
      null;
    const packageId =
      this.formData.packageId ||
      this.selectedPackage?.id ||
      this.notification?.data?.package_id ||
      this.notification?.data?.packageId ||
      o['package_id'] ||
      o['packageId'] ||
      null;
    const vehicleMake =
      this.formData.vehicleMake ||
      this.notification?.data?.vehicle_make ||
      this.notification?.data?.vehicleMake ||
      o['make'] ||
      o['vehicle_make'] ||
      '';
    const vehicleModel =
      this.formData.vehicleModel ||
      this.notification?.data?.vehicle_model ||
      this.notification?.data?.vehicleModel ||
      o['model'] ||
      o['vehicle_model'] ||
      '';
    const vehicleYear =
      this.formData.vehicleYear ||
      this.notification?.data?.vehicle_year ||
      this.notification?.data?.vehicleYear ||
      o['year'] ||
      o['vehicle_year'] ||
      '';
    const vehicleVin =
      this.notification?.data?.vehicle_vin ||
      this.notification?.data?.vehicleVin ||
      o['vehicle_vin'] ||
      o['vehicleVin'] ||
      '';
    // Operator-confirmed plate as state prefix + typed number (correction wins over
    // the original camera read); fall back to the existing plate when the form is empty.
    const plate =
      this.buildLprEntryPlateFromForm() ||
      String(
        this.notification?.data?.plate ||
          this.notification?.data?.vehicle_license_plate ||
          o['license_plate'] ||
          o['vehicle_license_plate'] ||
          ''
      ).trim();

    const payload: Record<string, any> = {
      order_id: orderId,
      is_completed: true,
      wash_date: this.getTodayDate(),
      timezone: this.getTimeZone(),
    };

    if (washType === 'monthly_membership') {
      if (this.orderHasActiveMembershipSubscription(order)) {
        payload['event'] = sanitizeLprEntryPutEvent('active-subscription');
        payload['subscription_id'] = this.resolveOrderSubscriptionId(order);
        if (vehicleId) {
          payload['vehicle_id'] = vehicleId;
        }
        if (packageId) {
          payload['package_id'] = packageId;
        }
      } else {
        // Card charge may have succeeded while the Stripe subscription was never created
        // (backend refunds the charge). Never persist an active membership with a null
        // subscription — keep the row as a pending payment request the operator can retry.
        payload['event'] = sanitizeLprEntryPutEvent('create-payment-request');
        payload['is_completed'] = false;
      }
    } else {
      payload['event'] = sanitizeLprEntryPutEvent('order-success');
    }

    if (customerId) {
      payload['customer_id'] = customerId;
    }
    if (vehicleId && washType !== 'monthly_membership') {
      payload['vehicle_id'] = vehicleId;
    }
    if (packageId && washType === 'single_wash') {
      payload['package_id'] = packageId;
    }
    if (String(vehicleMake).trim()) {
      payload['vehicle_make'] = String(vehicleMake).trim();
    }
    if (String(vehicleModel).trim()) {
      payload['vehicle_model'] = String(vehicleModel).trim();
    }
    if (String(vehicleYear).trim()) {
      payload['vehicle_year'] = String(vehicleYear).trim();
    }
    if (String(vehicleVin).trim()) {
      payload['vehicle_vin'] = String(vehicleVin).trim();
    }
    if (plate) {
      payload['plate'] = plate;
      // Only set the state column when the plate came from the form (keeps state and
      // prefix in sync); skip it for the camera-read fallback, which is already prefixed.
      if (this.buildLprEntryPlateFromForm()) {
        payload['state'] = this.resolveLprEntryStateFromForm();
      }
    }

    Object.assign(
      payload,
      buildLprEntryLocationLanePutFields({
        notificationData: this.notification?.data as Record<string, any> | undefined,
        locationId: String(
          this.formData.locationId ||
            o['location_id'] ||
            o['locationId'] ||
            this.notification?.data?.['location_id'] ||
            this.notification?.data?.['locationId'] ||
            ''
        ),
        laneId: String(
          this.formData.laneId ||
            o['lane_id'] ||
            o['laneId'] ||
            this.notification?.data?.['lane_id'] ||
            this.notification?.data?.['laneId'] ||
            ''
        ),
      })
    );

    this.assignLprEntryDisplayFieldsToPayload(payload, order);

    return payload;
  }

  /** Customer/package display fields for order-success list + modal (PUT + in-app cache). */
  private assignLprEntryDisplayFieldsToPayload(
    payload: Record<string, any>,
    order?: Record<string, any> | null
  ): void {
    const o = order ?? {};
    const assign = (key: string, value: any): void => {
      if (value != null && String(value).trim() !== '') {
        payload[key] = value;
      }
    };

    assign(
      'customer_first_name',
      this.formData.customerFirstName || o['customer_first_name'] || o['customerFirstName']
    );
    assign(
      'customer_last_name',
      this.formData.customerLastName || o['customer_last_name'] || o['customerLastName']
    );
    assign(
      'customer_email',
      this.formData.customerEmail || o['customer_email'] || o['customerEmail']
    );
    assign(
      'customer_contact',
      this.formData.customerContact || o['customer_contact'] || o['customerContact']
    );
    assign(
      'package_name',
      this.resolveSuccessModalPackageName(order) ||
        o['package_name'] ||
        o['packageName'] ||
        this.formData.package
    );
    assign(
      'final_price',
      o['final_price'] ??
        o['finalPrice'] ??
        this.formData.price ??
        this.notification?.data?.final_price
    );
  }

  /**
   * Merge checkout form + order into notification.data, then sync bell/list cache
   * so order-success rows show customer/package without a manual refresh.
   */
  private syncLprEntryToNotificationListAfterPayment(
    response: any,
    orderId: string,
    washType: 'single_wash' | 'monthly_membership' | 'add_on',
    order: Record<string, any> | null
  ): void {
    this.mergeOrderSuccessDisplayIntoNotification(orderId, washType, order);

    const fromPut = notificationDataFromLprEntryPutResponse(response);
    if (fromPut && this.notification?.data) {
      const data = this.notification.data as Record<string, any>;
      const putData = (fromPut.data ?? {}) as Record<string, any>;
      Object.assign(data, putData);
      this.notification.type = fromPut.type;
      if (fromPut.message) {
        this.notification.message = fromPut.message;
      }
      this.mergeOrderSuccessDisplayIntoNotification(orderId, washType, order);
    }

    if (this.notification) {
      this.notificationService.syncLprEntryFromNotificationData(this.notification);
    }
  }

  private mergeOrderSuccessDisplayIntoNotification(
    orderId: string,
    washType: 'single_wash' | 'monthly_membership' | 'add_on',
    order: Record<string, any> | null
  ): void {
    if (!this.notification?.data) {
      return;
    }
    this.mergeOrderRequestFormIntoNotificationData();
    const data = this.notification.data as Record<string, any>;
    const assign = (key: string, value: any): void => {
      if (value != null && String(value).trim() !== '') {
        data[key] = value;
      }
    };

    assign('order_id', orderId);
    assign('orderId', orderId);

    const membershipActivationFailed =
      washType === 'monthly_membership' && !this.orderHasActiveMembershipSubscription(order);

    if (!membershipActivationFailed) {
      assign('is_completed', true);
    }
    assign(
      'package_name',
      this.resolveSuccessModalPackageName(order) ||
        order?.['package_name'] ||
        order?.['packageName'] ||
        this.formData.package
    );
    assign(
      'final_price',
      order?.['final_price'] ?? order?.['finalPrice'] ?? this.formData.price ?? data['final_price']
    );

    if (membershipActivationFailed) {
      // No real subscription — do NOT flip the row to active-subscription off a bare charge.
      // Keep it as a pending payment request and surface the backend failure reason.
      data['is_completed'] = false;
      this.notification.type = 'create-payment-request';
      this.notification.message = this.resolveMembershipActivationFailureMessage(order);
      return;
    }

    const nextType = washType === 'monthly_membership' ? 'active-subscription' : 'order-success';
    this.notification.type = nextType;
    this.notification.message = NotificationUtils.getMessageForEventType(nextType);
  }

  private shouldHydrateViewOnlyOrderSuccessOnOpen(notification: NotificationData): boolean {
    if (notification.type !== 'order-success') {
      return false;
    }
    const d = notification.data as Record<string, any> | undefined;
    if (!d) {
      return true;
    }
    const orderId = String(d['order_id'] ?? d['orderId'] ?? '').trim();
    if (!orderId) {
      return false;
    }
    const hasName = !!String(d['customer_first_name'] ?? d['customerFirstName'] ?? '').trim();
    const hasPkg = !!String(d['package_name'] ?? d['packageName'] ?? '').trim();
    const hasPrice = !!String(d['final_price'] ?? d['finalPrice'] ?? '').trim();
    return !hasName || !hasPkg || !hasPrice;
  }

  /** Load order/:id when reopening a thin order-success row from the notifications list. */
  private hydrateViewOnlyOrderSuccessFromOrderApi(notification: NotificationData): void {
    const d = notification.data as Record<string, any> | undefined;
    const orderId = String(d?.['order_id'] ?? d?.['orderId'] ?? '').trim();
    if (!orderId) {
      return;
    }

    this.apiService
      .get(`order/${orderId}`)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (orderResponse: any) => {
          const order = this.extractOrderRecordFromApiResponse(orderResponse);
          if (!order || this.notification !== notification) {
            return;
          }
          this.applyOrderRecordToViewOnlyNotificationData(order, 'order-success');
          this.cdr.markForCheck();
        },
        error: error => {
          console.error('Error hydrating order-success notification from order API:', error);
        },
      });
  }

  private applyOrderRecordToViewOnlyNotificationData(
    order: Record<string, any>,
    eventType: NotificationData['type']
  ): void {
    if (!this.notification?.data) {
      return;
    }
    const data = this.notification.data as Record<string, any>;
    const assign = (key: string, value: any): void => {
      if (value != null && String(value).trim() !== '') {
        data[key] = value;
      }
    };

    const customer = (order['customer'] as Record<string, any> | undefined) ?? {};
    assign(
      'customer_first_name',
      order['customer_first_name'] ?? order['customerFirstName'] ?? customer['first_name']
    );
    assign(
      'customer_last_name',
      order['customer_last_name'] ?? order['customerLastName'] ?? customer['last_name']
    );
    assign(
      'customer_email',
      order['customer_email'] ?? order['customerEmail'] ?? customer['email']
    );
    assign(
      'customer_contact',
      order['customer_contact'] ?? order['customerContact'] ?? customer['contact']
    );
    assign('package_name', order['package_name'] ?? order['packageName']);
    assign('final_price', order['final_price'] ?? order['finalPrice']);
    assign('order_id', order['id'] ?? order['order_id'] ?? order['orderId']);
    assign('orderId', data['order_id']);
    assign('way_pass_id', order['way_pass_id'] ?? order['wayPassId']);
    assign('way_package_name', order['way_package_name'] ?? order['wayPackageName']);

    this.notification.type = eventType;
    this.notification.message = NotificationUtils.getMessageForEventType(eventType);
  }

  private putLprEntryAfterPayment(
    notificationId: string,
    orderId: string,
    washType: 'single_wash' | 'monthly_membership' | 'add_on',
    order: Record<string, any> | null
  ): void {
    if (this.lprEntryUpdatedAfterPaymentOrderIds.has(orderId)) {
      return;
    }
    if (this.lprEntryPaymentSyncInFlightOrderIds.has(orderId)) {
      return;
    }
    this.lprEntryPaymentSyncInFlightOrderIds.add(orderId);

    const updatePayload = this.buildLprEntryUpdatePayloadAfterPayment(orderId, washType, order);
    console.log('🔄 Updating LPR entry after payment:', notificationId, updatePayload);

    this.apiService
      .put(`order/lpr-entries/${notificationId}`, updatePayload)
      .pipe(take(1))
      .subscribe({
        next: response => {
          console.log('✅ LPR entry updated after payment:', response);
          this.syncLprEntryToNotificationListAfterPayment(response, orderId, washType, order);
          this.lprEntryUpdatedAfterPaymentOrderIds.add(orderId);
          setTimeout(() => this.lprEntryUpdatedAfterPaymentOrderIds.delete(orderId), 5 * 60 * 1000);
          if (this.pendingPostPaymentLprSyncOrderId === orderId) {
            this.pendingPostPaymentLprSyncOrderId = null;
            this.paymentDoneOrderId = null;
          }
          this.cdr.detectChanges();
        },
        error: error => {
          console.error('❌ Error updating LPR entry after payment:', error);
          this.syncLprEntryToNotificationListAfterPayment(null, orderId, washType, order);
          this.notificationService.refreshNotifications();
          if (this.pendingPostPaymentLprSyncOrderId === orderId) {
            this.pendingPostPaymentLprSyncOrderId = null;
          }
        },
        complete: () => {
          this.lprEntryPaymentSyncInFlightOrderIds.delete(orderId);
        },
      });
  }

  /** True on the order-request screen (New Order Request / LPR customer form — not payment checkout). */
  private shouldPersistLprEntryOnOrderRequestDismiss(): boolean {
    if (this.isDirectOrder()) {
      return false;
    }
    if (this.isUpdatingOrder || this.isUpdatingLinkedCustomer) {
      return false;
    }
    if (this.notification?.type === 'invalid-license-plate') {
      return false;
    }
    if (this.showPaymentCheckoutLayout()) {
      return false;
    }
    if (!this.isOrderRequestStyleNotification()) {
      return false;
    }
    const entryId =
      String(this.formData.notificationId || '').trim() ||
      resolveLprEntryIdFromNotificationData(this.notification?.data as Record<string, any>);
    return !!entryId;
  }

  /** Merge live form fields into notification.data so list reopen + PUT share the same draft snapshot. */
  private mergeOrderRequestFormIntoNotificationData(): void {
    if (!this.notification?.data) {
      return;
    }
    const data = this.notification.data as Record<string, any>;
    const assign = (key: string, value: any): void => {
      if (value != null && String(value).trim() !== '') {
        data[key] = value;
      }
    };

    assign('customer_id', this.formData.customerId);
    assign('customerId', this.formData.customerId);
    assign('vehicle_id', this.formData.vehicleId);
    assign('vehicleId', this.formData.vehicleId);
    assign('package_id', this.formData.packageId || this.selectedPackage?.id);
    assign('packageId', this.formData.packageId || this.selectedPackage?.id);
    assign('customer_first_name', this.formData.customerFirstName);
    assign('customerFirstName', this.formData.customerFirstName);
    assign('customer_last_name', this.formData.customerLastName);
    assign('customerLastName', this.formData.customerLastName);
    assign('customer_email', this.formData.customerEmail);
    assign('customerEmail', this.formData.customerEmail);
    assign('customer_contact', this.formData.customerContact);
    assign('customerContact', this.formData.customerContact);
    assign('vehicle_make', this.formData.vehicleMake);
    assign('vehicle_model', this.formData.vehicleModel);
    assign('vehicle_year', this.formData.vehicleYear);
    assign('vehicle_vin', this.formData.vehicleVin);

    if (this.isUiLicensePlateImageScanOrderRequest()) {
      assign('location_id', this.formData.locationId);
      assign('locationId', this.formData.locationId);
      assign('lane_id', this.formData.laneId);
      assign('laneId', this.formData.laneId);
    }

    const washType = this.getNormalizedWashType();
    if (washType) {
      data['wash_type'] = washType;
    }

    if (this.formData.vehicleLicensePlate?.trim()) {
      const parsed = splitLicensePlateForApi(
        this.formData.vehicleLicensePlate,
        this.formData.vehicleLicensePlateState
      );
      data['plate'] = parsed.license_plate
        ? `${parsed.state}${parsed.license_plate}`
        : this.formData.vehicleLicensePlate.trim();
      data['vehicle_license_plate'] = data['plate'];
      data['state'] = parsed.state;
    }
  }

  /**
   * PUT order/lpr-entries/:id when the user dismisses the order-request form without checkout.
   * Saves draft customer/plate/package fields so reopening the notification restores them.
   */
  private persistLprEntryOnOrderRequestDismiss(trigger: 'cancel' | 'close-icon'): void {
    if (!this.shouldPersistLprEntryOnOrderRequestDismiss()) {
      return;
    }

    const notificationId = this.resolveLprEntryNotificationId();

    if (!notificationId) {
      console.warn('⚠️ Notification ID not found, skipping LPR entry persist on dismiss');
      return;
    }

    this.mergeOrderRequestFormIntoNotificationData();

    const data = (this.notification?.data ?? {}) as Record<string, any>;
    const dismissEvent = sanitizeLprEntryPutEvent(
      resolveLprEntryDismissEvent(data, this.notification?.type)
    );
    if (!dismissEvent) {
      this.logModalInteraction('lpr.entry.persist.dismiss.skip', {
        trigger,
        notificationId,
        reason: isInvalidLicensePlateLprEntryRow(data, this.notification?.type)
          ? 'invalid_license_plate_row'
          : 'completed_or_no_overwrite',
      });
      return;
    }

    if (this.notification) {
      this.notification = applyResolvedLprEntryTypeToNotification(this.notification, data);
      this.notificationService.syncLprEntryFromNotificationData(this.notification);
    }

    const licencePlateId =
      this.notification?.data?.licence_plate_id ||
      this.notification?.data?.license_plate_id ||
      notificationId;
    const updatePayload: Record<string, any> = {
      event: dismissEvent,
      licence_plate_id: licencePlateId,
    };
    const assignPayload = (key: string, value: any): void => {
      if (value != null && String(value).trim() !== '') {
        updatePayload[key] = value;
      }
    };

    assignPayload('customer_id', data['customer_id'] ?? data['customerId']);
    assignPayload('vehicle_id', data['vehicle_id'] ?? data['vehicleId']);
    assignPayload('package_id', data['package_id'] ?? data['packageId']);
    assignPayload('customer_first_name', data['customer_first_name'] ?? data['customerFirstName']);
    assignPayload('customer_last_name', data['customer_last_name'] ?? data['customerLastName']);
    assignPayload('customer_email', data['customer_email'] ?? data['customerEmail']);
    assignPayload('customer_contact', data['customer_contact'] ?? data['customerContact']);
    assignPayload('vehicle_make', data['vehicle_make'] ?? data['vehicleMake']);
    assignPayload('vehicle_model', data['vehicle_model'] ?? data['vehicleModel']);
    assignPayload('vehicle_year', data['vehicle_year'] ?? data['vehicleYear']);
    assignPayload('vehicle_vin', data['vehicle_vin'] ?? data['vehicleVin']);
    assignPayload('wash_type', data['wash_type']);
    assignPayload('plate', data['plate'] ?? data['vehicle_license_plate']);
    assignPayload('state', data['state']);
    Object.assign(
      updatePayload,
      buildLprEntryLocationLanePutFields({
        notificationData: data,
        locationId: String(
          this.formData.locationId || data['location_id'] || data['locationId'] || ''
        ),
        laneId: String(this.formData.laneId || data['lane_id'] || data['laneId'] || ''),
      })
    );

    this.logModalInteraction('lpr.entry.persist.dismiss', { trigger, notificationId });

    this.apiService
      .put(`order/lpr-entries/${notificationId}`, updatePayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.notificationService.syncLprEntryFromPutResponse(response);
        },
        error: error => {
          console.error('❌ Error persisting LPR entry on order-request dismiss:', error);
        },
      });
  }

  private createOrderAfterVehicleCreation(): void {
    if (this.postVehicleResolvedHook) {
      this.runPostVehicleResolvedHook();
      return;
    }
    // Same test the checkout validation uses: a purchase found by plate lookup is not itself
    // an instruction to redeem, so an order carrying a wash package goes the ordinary way.
    if (this.isOrderRequestRedeemingPrepaid()) {
      this.submitPrepaidRedemptionOrder();
      return;
    }

    const singleWashPriceErr = this.getSingleWashCustomPriceSubmissionError();
    if (singleWashPriceErr) {
      this.updateOrderError = sanitizeErrorMessage(singleWashPriceErr);
      this.isUpdatingOrder = false;
      return;
    }

    if (!this.hasOrderRequestCheckoutLineItems()) {
      this.isUpdatingOrder = false;
      return;
    }
    const checkoutErr = this.getOrderRequestCheckoutValidationError();
    if (checkoutErr) {
      this.rejectOrderRequestCheckout(checkoutErr);
      return;
    }

    // Prepare the order payload with both customer ID and vehicle ID
    const resolvedLocationId = this.resolveLocationIdForSubmission();
    if (this.isDirectOrder() && !resolvedLocationId) {
      this.updateOrderError = sanitizeErrorMessage(
        'Location is required — please select a location'
      );
      this.isUpdatingOrder = false;
      return;
    }
    this.formData.locationId = resolvedLocationId;
    const listPrice = this.computeOrderRequestListPriceForApi();
    const washType = this.getNormalizedWashType();
    const orderPayload: CreateOrderPayload = {
      customer_id: this.formData.customerId,
      ...buildCreateOrderPackageIdField(
        washType,
        this.hasOrderRequestWashPackageSelected()
          ? this.formData.packageId || this.selectedPackage?.id
          : ''
      ),
      location_id: resolvedLocationId,
      ...buildCreateOrderVehicleIdField(this.resolveCreateOrderVehicleId()),
      wash_type: washType,
      appointment_time: this.getCurrentAppointmentTime(),
      source: 'web',
      voucher_code: '',
      order_source: this.getOrderSourceForCreatePayload(),
      final_price: this.computeOrderRequestFinalPrice(),
      ...(listPrice != null ? { list_price: listPrice } : {}),
      ...(this.formData.notificationId ? { lpr_entry_id: this.formData.notificationId } : {}),
      ...this.buildCreateOrderLaneIdField(),
      ...this.buildCreateOrderPrepaidPayloadFields(),
      ...this.buildCreateOrderDiscountAndAddonPayload(),
    };

    this.orderCreateStartTime = Date.now();
    this.logger.trackOrder('Order creation started (notification modal)', {
      customerId: orderPayload.customer_id,
      locationId: orderPayload.location_id,
      packageId: orderPayload.package_id,
      notificationType: this.notification?.type,
      licensePlate: this.notification?.data?.plate || this.formData.vehicleLicensePlate,
    });

    const validation = this.orderCreationService.validateOrderPayload(
      orderPayload,
      this.getCreateOrderValidationOptions()
    );
    if (!validation.isValid) {
      this.updateOrderError = sanitizeErrorMessage(validation.errors.join(', '));
      this.isUpdatingOrder = false;
      return;
    }

    // CWS-1945: Continue shows checkout from a draft; the order is created when the attendant
    // pays. The custom discount row is still persisted now so the draft can reference it.
    this.continueToCheckoutDraft(orderPayload);
  }

  /**
   * PATCH customer from the modal form, then run the next step (create order, or vehicle + order flow).
   * Skipped for order-request flows with an existing customer — backend links customer on vehicle/order save.
   */
  private updateCustomerFromFormIfNeededThen(next: () => void): void {
    if (!this.formData.customerId) {
      next();
      return;
    }

    if (this.isOrderRequestStyleNotification()) {
      next();
      return;
    }

    const customerPayload: any = {
      first_name: this.formData.customerFirstName,
      last_name: this.formData.customerLastName || null,
      email: this.formData.customerEmail,
      contact: this.formData.customerContact,
      type: 'regular',
      license_plate: this.formData.vehicleLicensePlate || '',
    };

    this.customerService
      .updateCustomer(this.formData.customerId, customerPayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => next(),
        error: error => {
          console.error('❌ Error updating customer before order flow:', error);
          this.isUpdatingOrder = false;
          this.updateOrderError = sanitizeErrorMessage(
            error.error?.message || 'Failed to update customer. Please try again.'
          );
        },
      });
  }

  /** Build PUT /vehicle payload linking the selected customer to the resolved vehicle row. */
  private buildVehicleLinkUpdatePayload(): {
    vehicleId: string;
    payload: UpdateVehiclePayload;
  } | null {
    const customerId = (this.formData.customerId || '').trim();
    const vehicleId = (this.formData.vehicleId || this.fetchedVehicleId || '').trim();
    const licensePlate = (this.formData.vehicleLicensePlate || '').trim();
    if (!customerId || !vehicleId || !licensePlate) {
      return null;
    }

    const parsed = splitLicensePlateForApi(licensePlate, this.notification?.data?.state);
    if (!parsed.license_plate || !parsed.state) {
      return null;
    }

    let yearNum = parseInt(String(this.formData.vehicleYear || ''), 10);
    if (!Number.isFinite(yearNum)) {
      yearNum = 0;
    }

    return {
      vehicleId,
      payload: {
        license_plate: parsed.license_plate,
        state: parsed.state,
        vin: this.formData.vehicleVin || '',
        make: this.formData.vehicleMake || '',
        model: this.formData.vehicleModel || '',
        year: yearNum,
        customer_id: customerId,
        is_family_plan: false,
      },
    };
  }

  /**
   * Existing-customer order save: attach selected customer to vehicle before POST /order
   * (e.g. plate lookup returned vehicle with customer_id null).
   */
  private linkVehicleToSelectedCustomerThen(next: () => void): void {
    if (!this.isOrderRequestStyleNotification()) {
      next();
      return;
    }

    const licensePlate = (this.formData.vehicleLicensePlate || '').trim();
    if (!licensePlate || !this.formData.customerId) {
      next();
      return;
    }

    this.matchOrCreateVehicleForCustomerPlate(licensePlate, () => next());
  }

  private handleRegularOrderUpdate(): void {
    // Same test the checkout validation uses: a purchase found by plate lookup is not itself
    // an instruction to redeem, so an order carrying a wash package goes the ordinary way.
    if (this.isOrderRequestRedeemingPrepaid()) {
      this.submitPrepaidRedemptionOrder();
      return;
    }

    const singleWashPriceErr = this.getSingleWashCustomPriceSubmissionError();
    if (singleWashPriceErr) {
      this.updateOrderError = sanitizeErrorMessage(singleWashPriceErr);
      this.isUpdatingOrder = false;
      return;
    }

    if (!this.hasOrderRequestCheckoutLineItems()) {
      this.isUpdatingOrder = false;
      return;
    }
    const checkoutErr = this.getOrderRequestCheckoutValidationError();
    if (checkoutErr) {
      this.rejectOrderRequestCheckout(checkoutErr);
      return;
    }

    // Prepare the order payload
    const resolvedLocationId = this.resolveLocationIdForSubmission();
    if (this.isDirectOrder() && !resolvedLocationId) {
      this.updateOrderError = sanitizeErrorMessage(
        'Location is required — please select a location'
      );
      this.isUpdatingOrder = false;
      return;
    }
    this.formData.locationId = resolvedLocationId;
    const listPriceRegular = this.computeOrderRequestListPriceForApi();
    const washTypeRegular = this.getNormalizedWashType();
    const orderPayload: CreateOrderPayload = {
      customer_id: this.formData.customerId || '',
      ...buildCreateOrderPackageIdField(
        washTypeRegular,
        this.hasOrderRequestWashPackageSelected() ? this.formData.packageId : ''
      ),
      location_id: resolvedLocationId,
      ...buildCreateOrderVehicleIdField(this.resolveCreateOrderVehicleId()),
      wash_type: washTypeRegular,
      appointment_time: this.getCurrentAppointmentTime(),
      source: 'web',
      voucher_code: '',
      order_source: this.getOrderSourceForCreatePayload(),
      final_price: this.computeOrderRequestFinalPrice(),
      ...(listPriceRegular != null ? { list_price: listPriceRegular } : {}),
      ...(this.formData.notificationId ? { lpr_entry_id: this.formData.notificationId } : {}),
      ...this.buildCreateOrderLaneIdField(),
      ...this.buildCreateOrderPrepaidPayloadFields(),
      ...this.buildCreateOrderDiscountAndAddonPayload(),
    };

    const validation = this.orderCreationService.validateOrderPayload(
      orderPayload,
      this.getCreateOrderValidationOptions()
    );
    if (!validation.isValid) {
      this.updateOrderError = sanitizeErrorMessage(validation.errors.join(', '));
      this.isUpdatingOrder = false;
      return;
    }

    // CWS-1945: Continue shows checkout from a draft; the order is created when the attendant pays.
    this.updateCustomerFromFormIfNeededThen(() =>
      this.linkVehicleToSelectedCustomerThen(() => this.continueToCheckoutDraft(orderPayload))
    );
  }

  isPaymentFormValid(): boolean {
    if (!this.isCheckoutPaymentRequired()) {
      return true;
    }
    if (this.paymentForm.method === PaymentMethodCode.TapToPay) {
      // Tap to Pay doesn't require form validation
      return true;
    } else if (this.paymentForm.method === PaymentMethodCode.Terminal) {
      const readerId = (this.paymentForm.terminalReaderId || '').trim();
      const cardType = this.paymentForm.terminalCardType;
      return (
        !!readerId &&
        (cardType === 'credit' || cardType === 'debit') &&
        this.isTerminalReaderOnline(readerId)
      );
    } else if (this.paymentForm.method === PaymentMethodCode.Cash) {
      return this.isCheckoutCashReceivedValid();
    } else if (this.isDynamicPaymentMethod(this.paymentForm.method)) {
      // API-driven custom payment methods don't require extra form validation
      return true;
    } else if (this.paymentForm.method === PaymentMethodCode.Card) {
      const hasCardholder = !!this.paymentForm.cardholderName?.trim();

      // Check if Stripe Elements are being used (mounted and complete)
      const usingStripeElements =
        this.cardNumberElement && this.cardExpiryElement && this.cardCvcElement;

      if (usingStripeElements) {
        // Validate using Stripe Elements completion state
        return (
          hasCardholder &&
          this.stripeCardNumberComplete &&
          this.stripeCardExpiryComplete &&
          this.stripeCardCvcComplete
        );
      } else {
        // Fallback to manual validation (for backward compatibility)
        const cardNumber = (this.paymentForm.cardNumber || '').replace(/\s/g, '');
        const cardNumberValid = /^\d{13,16}$/.test(cardNumber);
        const expiryParts = this.parseExpiry(this.paymentForm.cardExpiry);
        const hasValidExpiry = !!expiryParts;
        const cvcValid = /^\d{3,4}$/.test(this.paymentForm.cardCvc || '');

        return hasCardholder && cardNumberValid && hasValidExpiry && cvcValid;
      }
    } else {
      const phoneValue = (this.paymentForm.phoneNumber || '').trim();
      return phoneValue.length > 0 && !this.validatePhoneNumber(phoneValue);
    }
  }

  /** True when a wash package radio is highlighted in the order-request UI. */
  hasOrderRequestWashPackageSelected(): boolean {
    return !!(this.selectedPackage && this.selectedWashType);
  }

  private userExplicitlySelectedWashPackage(): boolean {
    return this.userInteractedFields.has('package') || this.userInteractedFields.has('packageId');
  }

  /**
   * POST /order snapshot already includes a wash package (single or monthly).
   * Checkout `populateFormData` clears `userInteractedFields`, so UI-only explicit-selection
   * checks must not hide a wash the user already committed on the order.
   */
  private hasPersistedWashPackageOnCheckoutOrder(): boolean {
    if (this.notification?.type !== 'create-payment-request' || !this.hasCheckoutOrderId()) {
      return false;
    }
    if (this.isCheckoutPrepaidPurchase()) {
      return false;
    }
    const d = this.notification.data as Record<string, any> | undefined;
    const ord = this.getCheckoutOrderSnapshot();
    const pkgId = String(
      ord?.['package_id'] ?? ord?.['packageId'] ?? d?.['packageId'] ?? d?.['package_id'] ?? ''
    ).trim();
    if (!pkgId) {
      return false;
    }
    const washRaw = this.resolveRawCheckoutWashType();
    if (!washRaw) {
      return true;
    }
    const norm = this.normalizeWashType(washRaw);
    return norm === 'single_wash' || norm === 'monthly_membership';
  }

  /**
   * Wash package counts toward POST/PUT /order and checkout totals.
   * Auto-selected default cards do not count when committed add-ons are present (add-on-only intent).
   */
  hasOrderRequestWashPackageForCheckout(): boolean {
    if (this.hasPersistedWashPackageOnCheckoutOrder()) {
      return true;
    }
    if (!this.hasOrderRequestWashPackageSelected()) {
      return false;
    }
    if (!this.userExplicitlySelectedWashPackage() && this.hasOrderRequestCommittedAddons()) {
      return false;
    }
    return true;
  }

  /** Drop auto-selected wash package once the user commits add-ons without picking a package. */
  private clearAutoSelectedWashPackageWhenAddonsCommitted(): void {
    if (
      this.hasOrderRequestCommittedAddons() &&
      this.hasOrderRequestWashPackageSelected() &&
      !this.userExplicitlySelectedWashPackage()
    ) {
      this.clearOrderRequestWashPackageSelection();
    }
  }

  /**
   * Add-on-only checkout: no wash package on the order/UI.
   * `list_price` on these orders is the add-on subtotal — must not map to a custom single-wash card.
   */
  private isAddonOnlyWashContext(orderLike?: Record<string, any> | null): boolean {
    const orderSnap =
      orderLike && typeof orderLike === 'object' ? orderLike : this.getCheckoutOrderSnapshot();

    const washSources: any[] = [
      orderSnap?.['wash_type'],
      orderSnap?.['washType'],
      orderLike?.['wash_type'],
      orderLike?.['washType'],
      this.cachedOrderWashType,
      (
        this.notification?.data as
          { wash_type?: string; order?: { wash_type?: string } } | undefined
      )?.wash_type,
      (this.notification?.data as { order?: { wash_type?: string } } | undefined)?.order?.wash_type,
    ];
    for (const raw of washSources) {
      if (raw != null && String(raw).trim() !== '') {
        if (this.normalizeWashType(String(raw)) === 'add_on') {
          return true;
        }
      }
    }

    const orderPkgId = String(
      orderSnap?.['package_id'] ??
        orderSnap?.['packageId'] ??
        orderLike?.['package_id'] ??
        orderLike?.['packageId'] ??
        ''
    ).trim();
    const editRestorePkgId =
      this.isEditMode || !!(this.formData.orderId || '').trim()
        ? (this.formData.packageId || '').trim()
        : '';
    if (orderPkgId || editRestorePkgId) {
      return false;
    }
    const hasPersistedAddons =
      this.getCheckoutAddOnVariantsSnapshot().length > 0 || this.hasOrderRequestCommittedAddons();
    if (hasPersistedAddons && !orderPkgId) {
      // New orders have no persisted package_id yet — still include wash package when selected in UI.
      return !this.hasOrderRequestWashPackageForCheckout();
    }

    return !this.hasOrderRequestWashPackageForCheckout() && this.hasOrderRequestCommittedAddons();
  }

  /** After an existing customer is linked, re-apply package selection from formData when catalog is ready. */
  private tryAutoSelectPackageWhenExistingCustomerLinked(): void {
    if (!(this.formData.customerId || '').trim()) {
      return;
    }
    this.applyPackageSelectionAfterCatalogLoad();
  }

  /** Select package from formData or fleet notification after catalog load. */
  private applyPackageSelectionAfterCatalogLoad(): void {
    if ((this.isEditMode || this.notification?.data?.isBuyPackageFlow) && this.formData.packageId) {
      this.selectPackageFromFormData();
      return;
    }
    if (this.notification?.type === 'fleet-vehicle-found') {
      this.selectExpandedPackageForFleetVehicleFoundNotification();
      return;
    }
    this.cdr.markForCheck();
  }

  /** Add-ons count only when they would be sent on POST /order (saved price / qty / fixed). */
  hasOrderRequestCommittedAddons(): boolean {
    return this.buildCreateOrderAddOnVariantsPayload().length > 0;
  }

  isOrderRequestAddonCommitted(addon: OrderRequestAddonItem): boolean {
    if (addon.mode === 'choose_products') {
      return this.getOrderRequestVendingCommittedQuantityFor(addon.id) > 0;
    }
    if (addon.mode === 'multi_variant_package' && addon.packageVariants?.length) {
      return addon.packageVariants.some(
        v => this.getOrderRequestAddonVariantCommittedQty(v.id) > 0
      );
    }
    return this.isOrderRequestAddonSelected(addon.id);
  }

  isOrderRequestLicensePlateRequired(): boolean {
    if (this.isFleetVehicleBillingOnlyAddons()) {
      return false;
    }
    return this.showOrderRequestCheckoutLayout();
  }

  hasOrderRequestCheckoutLineItems(): boolean {
    if (this.orderRequestSelectedPrepaidBookId) {
      return true;
    }
    if (this.hasOrderRequestRedemptionSelection()) {
      return true;
    }
    return this.hasOrderRequestWashPackageForCheckout() || this.hasOrderRequestCommittedAddons();
  }

  private buildOrderRequestCheckoutValidationInput(): OrderRequestCheckoutValidationInput {
    const useRecipientPlate =
      this.isInPrepaidRedemptionCheckoutMode() && this.hasOrderRequestRedemptionSelection();
    let phoneInvalid = false;
    let emailInvalid = false;
    let firstNameInvalid = false;
    let lastNameInvalid = false;
    if (useRecipientPlate) {
      phoneInvalid =
        !!this.prepaidRedemptionPhoneError ||
        this.isPrepaidRedemptionRecipientContactFieldInvalid('phone', validateCustomerPhone);
      emailInvalid =
        !!this.prepaidRedemptionEmailError ||
        this.isPrepaidRedemptionRecipientContactFieldInvalid('email', validateCustomerEmail);
      firstNameInvalid =
        !!this.prepaidRedemptionFirstNameError ||
        this.isPrepaidRedemptionRecipientContactFieldInvalid('firstName', validateCustomerName);
      lastNameInvalid =
        !!this.prepaidRedemptionLastNameError ||
        this.isPrepaidRedemptionRecipientContactFieldInvalid('lastName', validateCustomerName);
    } else {
      const phoneSource = this.formData.customerContact;
      const emailSource = this.formData.customerEmail;
      phoneInvalid = !!(
        this.shouldValidatePhone() &&
        phoneSource?.trim() &&
        this.validatePhoneNumber(phoneSource)
      );
      emailInvalid = !!(
        this.shouldValidateEmail() &&
        emailSource?.trim() &&
        this.validateEmail(emailSource)
      );
    }
    return {
      hasWashPackage: this.hasOrderRequestWashPackageForCheckout(),
      hasCommittedAddons: this.hasOrderRequestCommittedAddons(),
      licensePlateRequired: this.isOrderRequestLicensePlateRequired(),
      hasLicensePlate: useRecipientPlate
        ? !!this.prepaidRedemptionRecipient.licensePlate?.trim()
        : !!this.formData.vehicleLicensePlate?.trim(),
      licensePlateInvalid: useRecipientPlate
        ? false
        : !!(this.orderRequestPlateNumber || '').trim() &&
          !isLicensePlateCharactersValid(this.orderRequestPlateNumber),
      isMonthlyPackage: this.isMonthlyPackageSelected(),
      // CWS-1880: buying a book, not redeeming one.
      isPrepaidWashBookPurchase: this.isCheckoutPrepaidBookPurchaseForContact(),
      hasCustomerPhone: !!this.formData.customerContact?.trim(),
      phoneInvalid,
      emailInvalid,
      firstNameInvalid,
      lastNameInvalid,
    };
  }

  private getOrderRequestCheckoutValidationError(): string | null {
    // Only demand a wash to redeem when redeeming is what the order is. Picking a single wash,
    // a membership or a book to buy deselects the redemption while its panel stays on screen,
    // and without this the attendant was held up by a redemption they had moved away from.
    if (this.isOrderRequestRedeemingPrepaid()) {
      const purchaserId =
        (this.formData.customerId || '').trim() ||
        (this.prepaidPurchaseStatus?.customer_id || '').trim();
      if (!purchaserId) {
        return 'Customer is required. Select the customer from the license plate match.';
      }
      if (!this.hasOrderRequestRedemptionSelection()) {
        return 'Select at least one wash to redeem.';
      }
      if (!this.prepaidRedemptionRecipient.licensePlate?.trim()) {
        return 'License plate is required for redemption.';
      }
    }
    const checkoutInput = this.buildOrderRequestCheckoutValidationInput();
    if (checkoutInput.firstNameInvalid) {
      return this.prepaidRedemptionFirstNameError || 'Please enter a valid first name.';
    }
    if (checkoutInput.lastNameInvalid) {
      return this.prepaidRedemptionLastNameError || 'Please enter a valid last name.';
    }
    if (this.isOrderRequestLaneSelectionRequired() && !String(this.formData.laneId || '').trim()) {
      return ORDER_REQUEST_LANE_REQUIRED_MESSAGE;
    }
    // A restored lane (Back from payment) is unverified until the active-lane request returns.
    if (
      this.isLoadingOrderRequestLanes &&
      this.resolveCurrentLocationPickerOrigin() === 'ui-upload'
    ) {
      return ORDER_REQUEST_LANES_LOADING_MESSAGE;
    }
    if (
      this.orderRequestLanesLoadFailed &&
      this.resolveCurrentLocationPickerOrigin() === 'ui-upload'
    ) {
      return ORDER_REQUEST_LANES_LOAD_FAILED_MESSAGE;
    }
    return getOrderRequestCheckoutValidationError(checkoutInput);
  }

  private getCreateOrderValidationOptions(): { requireVehicle: boolean } {
    return {
      requireVehicle: this.isOrderRequestLicensePlateRequired(),
    };
  }

  private resolveCreateOrderVehicleId(): string {
    return String(
      this.formData.vehicleId ||
        this.notification?.data?.vehicle_id ||
        this.notification?.data?.vehicleId ||
        ''
    ).trim();
  }

  private clearOrderRequestWashPackageSelection(): void {
    this.selectedPackage = null;
    this.selectedWashType = null;
    // Deselecting takes the shop discount tiles with it; re-selecting brings them back.
    this.orderRequestWashSelectionCleared = true;
    this.formData.package = '';
    this.formData.packageId = '';
    if (!this.isCheckoutPrepaidPurchase() && !this.orderRequestSelectedPrepaidBookId) {
      this.formData.price = '';
    }
    this.orderRequestSingleWashCustomExpandedUniqueId = null;
    this.syncOrderRequestAddonSelectionToPairedWith();
    this.rebuildMostUsedServiceItems();
    // Drop a shop discount that is no longer offered once the package is gone.
    this.clearOrderRequestDiscountSelectionIfHidden();
  }

  /**
   * Validate form for each notification type
   * Returns true if all required fields are filled and valid
   */
  isFormValid(): boolean {
    if (!this.notification) return false;

    const type = this.notification.type;

    // Payment request validation
    if (type === 'create-payment-request') {
      // In edit mode, validate form fields like create-order-request
      if (this.isEditMode) {
        return (
          this.getOrderRequestCheckoutValidationError() == null &&
          this.getSingleWashCustomPriceSubmissionError() == null
        );
      }
      // Not in edit mode: $0 orders complete without a payment method
      if (!this.isCheckoutPaymentRequired()) {
        return true;
      }
      return this.isPaymentFormValid();
    }

    // Unified order UI (+ / LPR / image vehicle and checkout edit): same field rules
    if (this.showOrderRequestCheckoutLayout()) {
      return (
        this.hasOrderRequestCheckoutLineItems() &&
        this.getOrderRequestCheckoutValidationError() == null &&
        this.getSingleWashCustomPriceSubmissionError() == null
      );
    }

    // Vehicle not found / Order not found validation
    if (type === 'vehicle-not-found' || type === 'order-not-found') {
      return !!this.formData.vehicleLicensePlate?.trim();
    }

    // For other notification types that don't require form validation, return true
    return true;
  }

  /** Card PaymentMethod from the mounted Stripe Elements; shared by order and family checkout. */
  private async createStripeCardPaymentMethod(): Promise<{
    paymentMethod?: { id: string };
    error?: { message?: string };
  }> {
    if (!this.cardNumberElement) {
      throw new Error('Stripe card element not initialized');
    }

    const cardNumberContainer = document.getElementById(this.getStripeCardElementIds().number);
    if (cardNumberContainer && !cardNumberContainer.querySelector('.StripeElement')) {
      this.mountStripeElements();
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    return this.stripe.createPaymentMethod({
      type: 'card',
      card: this.cardNumberElement,
      billing_details: {
        name: this.paymentForm.cardholderName || '',
        address: { line1: this.paymentForm.billingAddress || '' },
      },
    });
  }

  async processStripePayment(): Promise<void> {
    this.logModalInteraction('payment.stripe.checkout.start');
    if (
      !this.isProcessingPayment &&
      this.claimCheckoutOrderThen('card', () => void this.processStripePayment())
    ) {
      return;
    }
    const orderId =
      this.notification?.data?.order_id ||
      this.notification?.data?.orderId ||
      this.formData.orderId;

    if (!orderId || this.isProcessingPayment) {
      if (!orderId) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Required Fields',
          detail: 'Order ID is required for payment',
        });
      }
      return;
    }

    if (!this.isPaymentFormValid()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Error',
        detail: 'Please fill in all card details correctly.',
      });
      return;
    }

    this.isProcessingPayment = true;

    try {
      // Use notification data if already present
      if (!this.paymentIntentClientSecret && this.notification?.data) {
        this.paymentIntentClientSecret =
          this.notification.data.payment_intent_client_secret ||
          this.notification.data.client_secret ||
          '';
      }

      // Step 1: Create PaymentMethod so we can pass payment_method_id to create-payment-intent
      const { paymentMethod, error: pmError } = await this.createStripeCardPaymentMethod();

      if (pmError) {
        this.isProcessingPayment = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Payment Error',
          detail:
            pmError.message || 'Could not create payment method. Please check your card details.',
          life: 5000,
        });
        return;
      }

      const paymentMethodId = paymentMethod!.id;
      console.log('✅ PaymentMethod created:', paymentMethodId);

      // Step 2: Create payment intent via API with payment_method_id in payload
      if (!this.paymentIntentClientSecret && orderId) {
        try {
          const res = await firstValueFrom(
            this.paymentService.createPaymentIntent(orderId, paymentMethodId)
          );
          const intentData = (res?.data ?? res ?? {}) as Record<string, any>;
          if (intentData['client_secret']) {
            this.paymentIntentClientSecret = String(intentData['client_secret']);
          }
          // Imported membership: $0 today + trial until expiry — card saved, no invoice PI.
          if (isDeferredMembershipCardSetup(res) || isDeferredMembershipCardSetup(intentData)) {
            this.completeImportedMembershipCardSetup(orderId, res ?? intentData);
            return;
          }
        } catch (err: any) {
          console.error('Create payment intent failed:', err);
          this.isProcessingPayment = false;
          // 402 means the card itself was refused for a membership (it authorizes a one-time
          // charge but cannot be saved for off-session renewals). Nothing was captured, so this
          // is a clean decline rather than a transport error.
          if (isPaymentRequiredError(err)) {
            this.failMembershipActivation(resolveMembershipFailureMessage(err));
            return;
          }
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Error',
            detail: sanitizeErrorMessage(
              err?.error?.message ||
                err?.message ||
                'Could not prepare card payment. Please try again.'
            ),
            life: 5000,
          });
          return;
        }
      }

      // The backend already fully confirms the PaymentIntent server-side
      // (createPaymentIntent attaches and confirms with payment_method_id).
      // Stripe.js confirm is not needed — go straight to notifying backend.
      console.log('✅ Payment confirmed by backend — notifying backend with payment method');

      const request = {
        order_id: orderId,
        payment_method_id: paymentMethodId ?? undefined,
      };

      this.paymentService
        .confirmCardPayment(request)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            console.log('Card payment confirmed successfully:', response);
            this.isProcessingPayment = false;
            if (response?.status === false) {
              this.messageService.add({
                severity: 'error',
                summary: 'Payment Error',
                detail: sanitizeErrorMessage(
                  response?.message || 'Payment could not be completed.'
                ),
                life: 5000,
              });
              return;
            }
            // Imported vendor membership: this is card-on-file only ($0 today). Never open
            // the wash "Payment Successful" overlay.
            if (orderId && this.shouldStayOnImportedVendorCardSetup()) {
              this.completeImportedMembershipCardSetup(orderId, response);
              return;
            }
            // Memberships are subscription-first: the charge is accepted here but Stripe's webhook
            // is what activates the membership. Showing success now would be a lie, and the order
            // still reads `incomplete`, so wait for the realtime event instead.
            if (orderId && isMembershipActivationPending(response)) {
              this.beginMembershipActivationWait(orderId);
              return;
            }
            if (orderId) {
              this.showOrderPaymentSuccessUi(orderId);
            }
          },
          error: error => {
            console.error('Error confirming card payment:', error);
            this.isProcessingPayment = false;
            this.messageService.add({
              severity: 'error',
              summary: 'Payment Error',
              detail: sanitizeErrorMessage(
                error?.error?.message ||
                  error?.message ||
                  'Payment could not be completed. Please try again.'
              ),
              life: 5000,
            });
          },
        });
    } catch (err: any) {
      this.isProcessingPayment = false;
      console.error('Error processing payment:', err);
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Error',
        detail: sanitizeErrorMessage(
          err?.error?.message || err?.message || 'Payment processing failed. Please try again.'
        ),
      });
    }
  }

  /**
   * Parse expiry date from string format (MM/YY or MM/YYYY)
   */
  private parseExpiry(expiry: string): { expMonth: number; expYear: number } | null {
    if (!expiry) {
      return null;
    }

    const digitsOnly = expiry.replace(/\D/g, '');
    if (digitsOnly.length < 4) {
      return null;
    }

    const monthPart = digitsOnly.substring(0, 2);
    const yearPart = digitsOnly.substring(2);

    const expMonth = parseInt(monthPart, 10);
    if (isNaN(expMonth) || expMonth < 1 || expMonth > 12) {
      return null;
    }

    let expYear = parseInt(yearPart, 10);
    if (isNaN(expYear)) {
      return null;
    }

    if (yearPart.length === 2) {
      expYear += 2000;
    } else if (yearPart.length !== 4) {
      return null;
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;

    if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
      return null;
    }

    return { expMonth, expYear };
  }

  createPaymentLink(): void {
    this.logModalInteraction('payment.link.create.start');

    // Check if phone number is missing (before the order is created for this attempt)
    if (!this.paymentForm.phoneNumber) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Required Field',
        detail: 'Phone number is required for payment link',
      });
      return;
    }

    // CWS-1945: sending the link is the payment attempt; create / claim the order first.
    if (this.claimCheckoutOrderThen('payment_link', () => this.createPaymentLink())) {
      return;
    }

    // Check if order_id is missing (either from notification.data.order_id OR formData.orderId)
    if (!this.notification?.data?.order_id && !this.formData.orderId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Required Field',
        detail: 'Order ID is required for payment link',
      });
      return;
    }

    this.isCreatingPaymentLink = true;

    const request: CreatePaymentLinkRequest = {
      order_id: this.notification?.data?.order_id || this.formData.orderId,
      phone_number: this.paymentForm.phoneNumber,
      email: this.formData.customerEmail || undefined,
      source: 'lpr',
    };

    this.paymentService
      .createPaymentLink(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isCreatingPaymentLink = false;
          if (response.status) {
            console.log('✅ Payment link created successfully');
            const sentTo = this.paymentForm.phoneNumber;

            this.messageService.add({
              severity: 'success',
              summary: 'Payment Link Sent',
              detail: 'Payment link sent to ' + sentTo,
            });
            // Set payment link sent flag to show message and confirm button
            this.paymentLinkSent = true;
            this.paymentExpired = false;

            // Start the payment timer
            this.startPaymentTimer();

            // Subscribe to Pusher channel for payment_done event
            const orderId =
              this.notification?.data?.order_id ||
              this.notification?.data?.orderId ||
              this.formData.orderId;
            if (orderId) {
              this.subscribeToOrderChannel(orderId);
            }
          } else {
            const detail = sanitizeErrorMessage(
              extractErrorMessage(response, 'Failed to create payment link. Please try again.')
            );
            this.messageService.add({
              severity: 'error',
              summary: 'Payment Link Failed',
              detail,
            });
          }
        },
        error: error => {
          console.error('❌ Error creating payment link:', error);
          this.isCreatingPaymentLink = false;
          const detail = sanitizeErrorMessage(
            extractErrorMessage(error, 'Failed to create payment link. Please try again.')
          );
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Link Error',
            detail,
          });
        },
      });
  }

  confirmCashPayment(): void {
    this.logModalInteraction('payment.confirm.cash');
    this.confirmPaymentMode(PaymentMethodCode.Cash as PaymentMethod);
  }

  /** Complete a $0 checkout without showing payment methods. */
  confirmZeroBalanceCheckout(): void {
    if (this.isCheckoutPaymentRequired() || this.isConfirmingCashPayment) {
      return;
    }
    this.logModalInteraction('payment.confirm.zeroBalance');
    this.paymentForm.cashReceived = '0';
    this.resetCheckoutCashPanelEntryState();
    this.confirmPaymentMode(PaymentMethodCode.Cash as PaymentMethod);
  }

  /**
   * Generic function to confirm payment mode using the new API endpoint
   * Supports: cash and API-driven custom/manual payment modes
   */
  confirmPaymentMode(paymentMode: PaymentMethod): void {
    this.logModalInteraction('payment.confirm.mode', { paymentMode });

    if (
      paymentMode === PaymentMethodCode.Cash &&
      this.notification?.type === 'create-payment-request' &&
      this.isCheckoutPaymentRequired() &&
      !this.isCheckoutCashTenderSufficient()
    ) {
      const detail = this.isCheckoutCashUnderTender()
        ? this.getCheckoutCashMinimumRequiredMessage()
        : 'Enter a valid dollar amount.';
      this.messageService.add({
        severity: 'warn',
        summary: 'Cash received',
        detail,
      });
      return;
    }

    if (this.isConfirmingCashPayment) {
      return;
    }

    // CWS-1945: create / claim the order for this attempt before recording the payment.
    if (
      this.claimCheckoutOrderThen(String(paymentMode), () => this.confirmPaymentMode(paymentMode))
    ) {
      return;
    }

    // Get order_id from notification data or formData
    const orderId =
      this.notification?.data?.order_id ||
      this.notification?.data?.orderId ||
      this.formData.orderId;

    if (!orderId) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Required Field',
        detail: 'Order ID is required for payment confirmation',
      });
      return;
    }

    this.isConfirmingCashPayment = true;
    this.paymentStartTime = Date.now();

    const confirmBody: Record<string, any> = { payment_mode: paymentMode };
    if (paymentMode === PaymentMethodCode.Cash) {
      const received = this.parseUsdNumber(String(this.paymentForm.cashReceived ?? '').trim());
      if (received != null && Number.isFinite(received)) {
        confirmBody['cash_received'] = received;
      }
    }

    this.logger.trackPayment('Payment confirmation started (notification modal)', {
      paymentMethod: paymentMode,
      orderId,
      notificationType: this.notification?.type,
      elapsedSinceOpenMs: this.modalOpenTime ? Date.now() - this.modalOpenTime : undefined,
      cashReceived:
        paymentMode === PaymentMethodCode.Cash ? confirmBody['cash_received'] : undefined,
    });

    this.apiService
      .post(`order/confirm-payment-mode/${orderId}`, confirmBody)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isConfirmingCashPayment = false;
          this.logger.trackPayment('Payment confirmed (notification modal)', {
            paymentMethod: paymentMode,
            orderId,
            elapsedMs: Date.now() - this.paymentStartTime,
          });
          this.handlePaymentDone(orderId, { status: 'succeeded', order_id: orderId });
        },
        error: error => {
          this.isConfirmingCashPayment = false;
          this.logger.trackError('NotificationModal.confirmPaymentMode', error, {
            paymentMethod: paymentMode,
            orderId,
            elapsedMs: Date.now() - this.paymentStartTime,
          });
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Error',
            detail: sanitizeErrorMessage(
              error?.error?.message ||
                error?.message ||
                `Failed to confirm ${paymentMode} payment. Please try again.`
            ),
          });
        },
      });
  }

  onContinue(): void {
    this.logModalInteraction('payment.continue', { method: this.paymentForm.method });
    if (!this.isCheckoutPaymentRequired()) {
      this.confirmZeroBalanceCheckout();
      return;
    }
    // A Stripe method can arrive here without ever passing through its tile — Card is the default
    // for a desktop membership and Tap to Pay for a mobile checkout. The notice above the tiles
    // already says why; this just refuses to start a payment that cannot be created.
    if (this.isSelectedStripeMethodUnavailable()) {
      return;
    }
    if (this.paymentForm.method === PaymentMethodCode.TapToPay) {
      this.openTapToPayApp();
    } else if (this.paymentForm.method === PaymentMethodCode.Terminal) {
      this.processTerminalCheckout();
    } else if (this.paymentForm.method === PaymentMethodCode.Card) {
      this.processStripePayment();
    } else if (
      this.paymentForm.method === PaymentMethodCode.Cash ||
      this.isDynamicPaymentMethod(this.paymentForm.method)
    ) {
      // These payment methods are handled by confirmPaymentMode via their respective buttons.
      return;
    } else {
      this.createPaymentLink();
    }
  }

  processTerminalCheckout(): void {
    this.logModalInteraction('payment.terminal.checkout.start');
    if (
      !this.isProcessingPayment &&
      this.claimCheckoutOrderThen('terminal', () => this.processTerminalCheckout())
    ) {
      return;
    }
    const orderId =
      this.notification?.data?.order_id ||
      this.notification?.data?.orderId ||
      this.formData.orderId;
    const locationId = this.getCheckoutLocationId();

    if (!orderId || !locationId || this.isProcessingPayment) {
      if (!orderId) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Required Fields',
          detail: 'Order ID is required for payment',
        });
      }
      return;
    }

    if (!this.isPaymentFormValid()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Error',
        detail: 'Select an online reader and card type.',
      });
      return;
    }

    this.isProcessingPayment = true;
    this.paymentStartTime = Date.now();

    const baseAmount = this.getPaymentCheckoutBreakdown().total;
    const convenienceFee = this.getTerminalConvenienceFeeAmount();

    this.apiService
      .get(`order/${orderId}`)
      .pipe(take(1))
      .subscribe({
        next: orderResponse => {
          const apiOrder =
            (unwrapOrderFromApiResponse(orderResponse) as Record<string, any> | null) ??
            orderResponse?.data?.order ??
            orderResponse?.data ??
            orderResponse;
          const internalOrderId = resolveInternalOrderId(apiOrder, orderId);
          if (internalOrderId) {
            this.formData.orderId = internalOrderId;
          }
          this.subscribeToOrderChannel(internalOrderId || orderId);
          const currentUser = this.authService.user();
          const metadata = buildTerminalReaderCheckoutMetadata({
            internalOrderId,
            publicOrderId: resolvePublicOrderId(apiOrder, orderId),
            source: (apiOrder?.['source'] as 'lpr' | 'saas') || 'lpr',
            customerId:
              String(
                apiOrder?.['customer_id'] ??
                  apiOrder?.['customerId'] ??
                  this.formData.customerId ??
                  ''
              ).trim() || undefined,
            locationId:
              String(
                apiOrder?.['location_id'] ?? apiOrder?.['locationId'] ?? locationId ?? ''
              ).trim() || undefined,
            adminUserId:
              String(
                apiOrder?.['user_id'] ?? apiOrder?.['userId'] ?? currentUser?.id ?? ''
              ).trim() || undefined,
            washType:
              String(apiOrder?.['wash_type'] ?? apiOrder?.['washType'] ?? '').trim() || undefined,
            packageId:
              String(apiOrder?.['package_id'] ?? apiOrder?.['packageId'] ?? '').trim() || undefined,
            vehicleId:
              String(
                apiOrder?.['vehicle_id'] ?? apiOrder?.['vehicleId'] ?? this.formData.vehicleId ?? ''
              ).trim() || undefined,
          });

          this.terminalSettingsService
            .createTerminalPaymentIntent(locationId, {
              amount: baseAmount,
              convenienceFee,
              metadata,
            })
            .pipe(take(1))
            .subscribe({
              next: intent => {
                if (!intent?.paymentIntentId) {
                  this.isProcessingPayment = false;
                  this.stopTerminalPaymentStatusPoll();
                  this.messageService.add({
                    severity: 'error',
                    summary: 'Payment Error',
                    detail: 'Could not start terminal payment.',
                  });
                  return;
                }

                this.terminalSettingsService
                  .proceedTerminalPayment(locationId, {
                    readerId: this.paymentForm.terminalReaderId,
                    paymentIntentId: intent.paymentIntentId,
                    totalAmount: intent.totalAmountCents,
                    subtotal: intent.baseAmountCents,
                    convenienceFee: intent.convenienceFeeCents,
                  })
                  .pipe(take(1))
                  .subscribe({
                    next: proceed => {
                      if (!proceed) {
                        this.isProcessingPayment = false;
                        this.stopTerminalPaymentStatusPoll();
                        this.messageService.add({
                          severity: 'error',
                          summary: 'Payment Error',
                          detail: 'Could not send payment to reader.',
                        });
                        return;
                      }
                      this.startTerminalPaymentStatusPoll(internalOrderId || orderId);
                    },
                    error: () => {
                      this.isProcessingPayment = false;
                      this.stopTerminalPaymentStatusPoll();
                      this.messageService.add({
                        severity: 'error',
                        summary: 'Payment Error',
                        detail: 'Could not send payment to reader.',
                      });
                    },
                  });
              },
              error: () => {
                this.isProcessingPayment = false;
                this.stopTerminalPaymentStatusPoll();
                this.messageService.add({
                  severity: 'error',
                  summary: 'Payment Error',
                  detail: 'Could not start terminal payment.',
                });
              },
            });
        },
        error: () => {
          this.isProcessingPayment = false;
          this.stopTerminalPaymentStatusPoll();
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Error',
            detail: 'Could not load order details for terminal payment.',
          });
        },
      });
  }

  private startTerminalPaymentStatusPoll(orderId: string): void {
    this.stopTerminalPaymentStatusPoll();
    const normalizedOrderId = String(orderId || '')
      .replace(/^#+/, '')
      .trim();
    if (!normalizedOrderId) {
      return;
    }
    this.terminalPaymentPollStartedAt = Date.now();
    this.terminalPaymentPollTimer = setInterval(() => {
      this.pollTerminalPaymentStatus(normalizedOrderId);
    }, NotificationModalComponent.TERMINAL_PAYMENT_POLL_MS);
    setTimeout(
      () => this.pollTerminalPaymentStatus(normalizedOrderId),
      NotificationModalComponent.TERMINAL_PAYMENT_POLL_MS
    );
  }

  private stopTerminalPaymentStatusPoll(): void {
    if (this.terminalPaymentPollTimer) {
      clearInterval(this.terminalPaymentPollTimer);
      this.terminalPaymentPollTimer = null;
    }
    this.terminalPaymentPollStartedAt = 0;
  }

  private pollTerminalPaymentStatus(orderId: string): void {
    if (!this.isProcessingPayment || this.paymentForm.method !== PaymentMethodCode.Terminal) {
      this.stopTerminalPaymentStatusPoll();
      return;
    }
    if (
      this.terminalPaymentPollStartedAt &&
      Date.now() - this.terminalPaymentPollStartedAt >
        NotificationModalComponent.TERMINAL_PAYMENT_POLL_TIMEOUT_MS
    ) {
      this.isProcessingPayment = false;
      this.stopTerminalPaymentStatusPoll();
      this.messageService.add({
        severity: 'warn',
        summary: 'Payment Timeout',
        detail:
          'Payment was sent to the reader but confirmation timed out. Check the order status.',
        life: 6000,
      });
      return;
    }

    this.apiService
      .get(`order/${orderId}`)
      .pipe(take(1))
      .subscribe({
        next: orderResponse => {
          const apiOrder =
            (unwrapOrderFromApiResponse(orderResponse) as Record<string, any> | null) ??
            orderResponse?.data?.order ??
            orderResponse?.data ??
            orderResponse;
          const paymentStatus = String(apiOrder?.['payment_status'] ?? '')
            .trim()
            .toLowerCase();
          const isPosTerminalMonthly =
            this.paymentForm.method === PaymentMethodCode.Terminal &&
            this.resolveWashTypeForLprUpdate(apiOrder) === 'monthly_membership';

          if (
            (paymentStatus === 'paid' ||
              (isPosTerminalMonthly && this.orderHasActiveMembershipSubscription(apiOrder))) &&
            !this.showPaymentSuccess
          ) {
            this.stopTerminalPaymentStatusPoll();
            this.handlePaymentDone(orderId, {
              status: 'succeeded',
              order_id: resolveInternalOrderId(apiOrder, orderId),
              public_order_id: resolvePublicOrderId(apiOrder, orderId),
              source: 'terminal_poll',
            });
            return;
          }
          if (
            isPosTerminalMonthly &&
            paymentStatus !== 'paid' &&
            !this.orderHasActiveMembershipSubscription(apiOrder) &&
            !this.membershipActivationAwaitingWebhook
          ) {
            this.beginMembershipActivationWait(orderId);
            return;
          }
          if (paymentStatus === 'failed') {
            this.stopTerminalPaymentStatusPoll();
            const failureMessage = String(
              apiOrder?.['payment_failure_message'] ?? apiOrder?.['failure_message'] ?? ''
            ).trim();
            this.handlePaymentFailed('terminal_poll', orderId, {
              status: 'failed',
              order_id: resolveInternalOrderId(apiOrder, orderId),
              public_order_id: resolvePublicOrderId(apiOrder, orderId),
              message:
                failureMessage ||
                'Card was declined or payment failed at the reader. Please try again.',
              source: 'terminal_poll',
            });
          }
        },
      });
  }

  /**
   * Open Tap to Pay app using deep link with order/form details
   *
   * Deep Link Format:
   * taptopay://payment?amount=20&orderId=82498271192&currency=USD&phoneNumber=2122333341&email=user@example.com&source=notification&customerId=123&locationId=456&id=789&admin_user_id=123&wash_type=single_wash&package_id=456&vehicle_id=789
   *
   * Parameters passed:
   * - amount: The total payment amount (just number, e.g., "20")
   * - orderId: The order ID without # symbol (e.g., "82498271192")
   * - currency: Currency code (default: "USD")
   * - phoneNumber: Customer phone number (from paymentForm or formData)
   * - email: Customer email (from formData, optional)
   * - source: Order source (default: "notification" for notification modal)
   * - customerId: Customer ID from API or form data
   * - locationId: Location ID from API or form data
   * - id: Internal order ID from API or form data
   * - admin_user_id: User ID from auth service (named as admin_user_id in deep link)
   * - wash_type: Wash type from API or form data (e.g., "single_wash", "monthly_membership")
   * - package_id: Package ID from API or form data
   * - vehicle_id: Vehicle ID from API or form data
   */
  openTapToPayApp(): void {
    this.logModalInteraction('payment.tapToPay.open');
    if (this.claimCheckoutOrderThen('tap_to_pay', () => this.openTapToPayApp())) {
      return;
    }
    this.beginTapToPayPaymentAttempt();
    // Get order ID and clean it: remove # symbol and any prefixes
    let orderId = this.formData.orderId || '';
    // Remove # symbol and any leading/trailing whitespace
    orderId = orderId.replace(/^#+/, '').trim();

    // If orderId exists, fetch full order details from API (like order-details-modal does)
    if (orderId && orderId.trim() !== '') {
      this.apiService
        .get(`order/${orderId}`)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (orderResponse: any) => {
            const apiOrder =
              (unwrapOrderFromApiResponse(orderResponse) as Record<string, any> | null) ??
              orderResponse?.data?.order ??
              orderResponse?.data ??
              orderResponse;
            const internalOrderId = resolveInternalOrderId(apiOrder, orderId);
            console.log('🔍 Full API Order Response:', apiOrder);

            if (apiOrder?.location_id) {
              this.formData.locationId = apiOrder.location_id as string;
            }
            if (internalOrderId) {
              this.formData.orderId = internalOrderId;
            }
            this.subscribeToOrderChannel(internalOrderId || orderId);

            this.apiService
              .put(`order/tap-to-pay-status/${internalOrderId || orderId}`, {
                tap_to_pay_status: 'app_opened',
              })
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (statusResponse: any) => {
                  console.log('✅ Tap to pay status updated to app_opened:', statusResponse);
                },
                error: (statusError: any) => {
                  console.error('Error updating tap to pay status:', statusError);
                },
              });

            setTimeout(() => {
              this.openTapToPayWithOrderData(apiOrder, internalOrderId || orderId);
            }, 500);
          },
          error: error => {
            console.error('Error fetching order details for Tap to Pay:', error);
            // Fallback: use form data if API fetch fails
            this.openTapToPayWithFormData();
          },
        });
    } else {
      // Use form data if order not created yet
      this.openTapToPayWithFormData();
    }
  }

  /**
   * Open Tap to Pay with order data from API (when order is already created)
   */
  private openTapToPayWithOrderData(apiOrder: any, orderId: string): void {
    const orderRecord = (apiOrder ?? {}) as Record<string, any>;
    const amount =
      cleanTapToPayAmount(orderRecord['final_price']) ||
      cleanTapToPayAmount(this.getOrderPrice() / 100);

    const authToken = getActiveUserAuthToken();

    const params = buildTapToPayNativeParams({
      apiOrder: orderRecord,
      fallbackOrderId: orderId,
      source: resolveTapToPayOrderSource(
        orderRecord,
        this.notification?.data as Record<string, any>
      ),
      amount,
      customerId: String(
        orderRecord['customer_id'] ?? orderRecord['customerId'] ?? this.formData.customerId ?? ''
      ),
      locationId: String(
        orderRecord['location_id'] ?? orderRecord['locationId'] ?? this.formData.locationId ?? ''
      ),
      adminUserId: String(orderRecord['user_id'] ?? this.authService.user()?.id ?? ''),
      washType: String(orderRecord['wash_type'] ?? this.selectedWashType ?? ''),
      packageId: String(
        orderRecord['package_id'] ?? this.formData.packageId ?? this.selectedPackage?.id ?? ''
      ),
      vehicleId: String(orderRecord['vehicle_id'] ?? this.formData.vehicleId ?? ''),
      stripeAccountId: String(orderRecord['stripe_account_id'] ?? ''),
      terminalLocationId: String(orderRecord['terminal_location_id'] ?? ''),
      phoneNumber:
        this.paymentForm.phoneNumber ||
        String(orderRecord['customer_contact'] ?? this.formData.customerContact ?? ''),
      email: String(orderRecord['customer_email'] ?? this.formData.customerEmail ?? ''),
      authToken,
      publicOrderId: String(
        this.notification?.data?.public_order_id ??
          (this.notification?.data?.order as Record<string, any> | undefined)?.['order_id'] ??
          orderRecord['order_id'] ??
          orderRecord['orderId'] ??
          ''
      ),
    });

    this.cachedOrderWashType = String(orderRecord['wash_type'] ?? this.cachedOrderWashType ?? '');
    this.sendTapToPayToNativeApp(params);
  }

  /**
   * Open Tap to Pay with form data (when order not created yet)
   */
  private openTapToPayWithFormData(): void {
    const amount = cleanTapToPayAmount(this.getOrderPrice() / 100);
    const orderId = (this.formData.orderId || '').replace(/^#+/, '').trim();
    const orderFromNotification = this.notification?.data?.order as Record<string, any> | undefined;
    const publicOrderId = String(
      this.notification?.data?.public_order_id ?? orderFromNotification?.['order_id'] ?? ''
    );

    const washType = this.getNormalizedWashType();

    const authToken = getActiveUserAuthToken();

    const apiOrder: Record<string, any> | null = orderId
      ? {
          id: orderId,
          ...(publicOrderId ? { order_id: publicOrderId } : {}),
          wash_type: washType,
        }
      : null;

    const params = buildTapToPayNativeParams({
      apiOrder,
      fallbackOrderId: orderId,
      source: resolveTapToPayOrderSource(apiOrder, this.notification?.data as Record<string, any>),
      amount,
      customerId: this.formData.customerId,
      locationId: this.formData.locationId,
      adminUserId: String(this.authService.user()?.id ?? ''),
      washType,
      packageId: this.formData.packageId || this.selectedPackage?.id || '',
      vehicleId: this.formData.vehicleId,
      stripeAccountId: String(
        (this.notification?.data?.order as Record<string, any> | undefined)?.[
          'stripe_account_id'
        ] ?? ''
      ),
      terminalLocationId: String(
        (this.notification?.data?.order as Record<string, any> | undefined)?.[
          'terminal_location_id'
        ] ?? ''
      ),
      phoneNumber: this.paymentForm.phoneNumber || this.formData.customerContact,
      email: this.formData.customerEmail,
      authToken,
      publicOrderId,
    });

    this.sendTapToPayToNativeApp(params);
  }

  private sendTapToPayToNativeApp(params: URLSearchParams): void {
    if (!this.tapToPayAndroidNative.isAvailable) {
      console.warn('[TapToPay] Native Android shell required (TapToPayAndroid bridge missing).');
      return;
    }
    const awaitingId = String(
      params.get('id') ?? params.get('order_id') ?? this.formData.orderId ?? ''
    )
      .replace(/^#+/, '')
      .trim();
    if (awaitingId) {
      this.markTapToPayAwaitingNativeResult(awaitingId);
    }
    this.tapToPayAndroidNative.startPaymentFromParams(params);
  }

  private getOrderPrice(): number {
    const listForApi = this.computeOrderRequestListPriceForApi();
    if (listForApi != null) {
      const n = parseFloat(listForApi);
      if (Number.isFinite(n) && n > 0) {
        return Math.round(n * 100);
      }
    }
    if (this.selectedPackage?.single_list_price) {
      const priceStr = this.selectedPackage.single_list_price.replace(/[$,]/g, '');
      const n = parseFloat(priceStr);
      if (!isNaN(n) && n > 0) {
        return Math.round(n * 100);
      }
    }
    if (this.formData.price) {
      const priceStr = this.formData.price.replace(/[$,]/g, '');
      const n = parseFloat(priceStr);
      if (!isNaN(n) && n > 0) {
        return Math.round(n * 100);
      }
    }
    return 0;
  }

  /** Compute the final_price for order creation: (wash + add-ons) - discount. */
  private computeOrderRequestFinalPrice(): string {
    const bd = this.getPaymentCheckoutBreakdown();
    const n = Math.max(0, bd.total);
    return n.toFixed(2);
  }

  /**
   * List line for POST/PUT `list_price`: wash package catalog/custom price, or add-on subtotal when addon-only.
   */
  private computeOrderRequestListPriceForApi(): string | undefined {
    if (this.isCheckoutPrepaidPurchase()) {
      const prepaidList = this.resolvePrepaidListPriceForCheckout();
      if (prepaidList != null) {
        return prepaidList.toFixed(2);
      }
    }
    const fromPackage = this.getCheckoutListPriceFromPackage();
    if (fromPackage != null && Number.isFinite(fromPackage) && fromPackage > 0) {
      return fromPackage.toFixed(2);
    }
    if (!this.hasOrderRequestWashPackageForCheckout() && this.hasOrderRequestCommittedAddons()) {
      const addonTotal = this.getCheckoutAddonAmount();
      if (Number.isFinite(addonTotal) && addonTotal > 0) {
        return addonTotal.toFixed(2);
      }
    }
    return undefined;
  }

  /** Build `add_on_variants` for POST /order from order-request UI (requires API package/variant ids). */
  private buildCreateOrderAddOnVariantsPayload(): CreateOrderAddOnVariantPayload[] {
    const out: CreateOrderAddOnVariantPayload[] = [];
    for (const addon of this.orderRequestAddons) {
      const pkgId = addon.apiPackageId?.trim();
      if (!pkgId) {
        continue;
      }

      if (addon.mode === 'choose_products') {
        const sel = this.orderRequestVendingSelections[addon.id];
        for (const line of sel?.lines ?? []) {
          const cat = addon.vendingCatalog?.find(x => x.id === line.id);
          const variantId = cat?.apiVariantId?.trim() ?? '';
          out.push({
            add_on_id: pkgId,
            add_on_variant_id: variantId,
            add_on_custom_price: Math.round(line.unitPrice * 100) / 100,
            add_on_count: Math.max(1, Math.floor(line.quantity) || 1),
          });
        }
        continue;
      }

      if (addon.mode === 'multi_variant_package' && addon.packageVariants) {
        for (const v of addon.packageVariants) {
          const q = this.getOrderRequestAddonVariantCommittedQty(v.id);
          if (q <= 0) {
            continue;
          }
          const variantApi = v.apiVariantId?.trim() ?? '';
          const unit =
            v.mode === 'custom_price'
              ? (this.parseUsdNumber(this.orderRequestAddonSavedCustomPrice[v.id]) ??
                v.unitPrice ??
                0)
              : (v.unitPrice ?? 0);
          if (unit <= 0) {
            continue;
          }
          out.push({
            add_on_id: pkgId,
            add_on_variant_id: variantApi,
            add_on_custom_price: Math.round(unit * 100) / 100,
            add_on_count: q,
          });
        }
        continue;
      }

      if (!this.isOrderRequestAddonSelected(addon.id)) {
        continue;
      }

      if (addon.mode === 'fixed' && addon.fixedPrice) {
        const unit = this.parseUsdNumber(addon.fixedPrice) ?? 0;
        if (unit <= 0) {
          continue;
        }
        const variantApi = addon.apiVariantId?.trim() ?? '';
        out.push({
          add_on_id: pkgId,
          add_on_variant_id: variantApi,
          add_on_custom_price: Math.round(unit * 100) / 100,
          add_on_count: 1,
        });
      } else if (addon.mode === 'custom_price') {
        const saved = this.orderRequestAddonSavedCustomPrice[addon.id];
        const amt = this.parseUsdNumber(saved) ?? 0;
        if (amt <= 0) {
          continue;
        }
        const variantApi = addon.apiVariantId?.trim() ?? '';
        out.push({
          add_on_id: pkgId,
          add_on_variant_id: variantApi,
          add_on_custom_price: Math.round(amt * 100) / 100,
          add_on_count: 1,
        });
      }
    }
    return out;
  }

  /** Prepaid wash book id when a prepaid tile is selected. */
  private buildCreateOrderPrepaidPayloadFields(): Pick<CreateOrderPayload, 'prepaid_wash_book_id'> {
    const prepaidId = this.getOrderRequestSelectedPrepaidBookIdForApi();
    if (!prepaidId) {
      return {};
    }
    return { prepaid_wash_book_id: prepaidId };
  }

  /**
   * Lane for POST /order.
   * UI image scan: prefer the form lane dropdown (required when shown).
   * Camera LPR / header "+": unchanged — notification lane or header fallback; "+" omits lane.
   */
  private buildCreateOrderLaneIdField(): Pick<CreateOrderPayload, 'lane_id'> {
    // Origin, not `is_lpr_upload`: after Back from payment the flag is gone but the picked lane stands.
    if (this.resolveCurrentLocationPickerOrigin() !== 'ui-upload') {
      return buildCreateOrderLaneIdPayload({
        omitLane: this.isDirectOrder(),
        notificationData: this.notification?.data as Record<string, any> | undefined,
        fallbackLaneId: getUserDataLaneId(),
      });
    }

    const selectedLaneId = String(this.formData.laneId || '').trim();
    return buildCreateOrderLaneIdPayload({
      selectedLaneId: selectedLaneId || null,
      notificationData: this.notification?.data as Record<string, any> | undefined,
      // Never inherit header/stamped lane for UI image scan — only the form dropdown.
      fallbackLaneId: null,
    });
  }

  /** Discount + add-on lines for POST /order (alongside existing fields). */
  private buildCreateOrderDiscountAndAddonPayload(): Pick<
    CreateOrderPayload,
    'discount_id' | 'discount' | 'add_on_variants'
  > {
    const extras: Pick<CreateOrderPayload, 'discount_id' | 'discount' | 'add_on_variants'> = {};
    const sel = this.orderRequestSelectedDiscountId;
    const bd = this.getPaymentCheckoutBreakdown();
    const discAmt = Math.round(Math.max(0, bd.discount) * 100) / 100;

    if (sel && sel !== 'disc_custom' && sel.startsWith('disc_')) {
      const uuid = sel.slice('disc_'.length).trim();
      if (uuid) {
        extras.discount_id = uuid;
        extras.discount = discAmt;
      }
    } else if (sel === 'disc_custom' && this.orderRequestCustomDiscountSaved && discAmt > 0) {
      const customId = String(this.orderRequestCustomDiscountSaved.id || '').trim();
      if (customId) {
        extras.discount_id = customId;
      }
      extras.discount = discAmt;
    }

    extras.add_on_variants = this.buildCreateOrderAddOnVariantsPayload();
    return extras;
  }

  /** PUT /order checkout fields: always send explicit discount + add-on state (including clears). */
  private appendOrderCheckoutDiscountAndAddonsToPayload(payload: Record<string, any>): void {
    const extras = this.buildCreateOrderDiscountAndAddonPayload();
    const sel = String(this.orderRequestSelectedDiscountId || '').trim();

    if (sel && sel !== 'disc_custom' && sel.startsWith('disc_')) {
      payload['discount_id'] = extras.discount_id ?? sel.slice('disc_'.length);
      payload['discount'] = extras.discount ?? 0;
    } else if (sel === 'disc_custom' && this.orderRequestCustomDiscountSaved) {
      const customId = String(this.orderRequestCustomDiscountSaved.id || '').trim();
      payload['discount_id'] = customId || null;
      payload['discount'] = extras.discount ?? 0;
    } else {
      payload['discount_id'] = null;
      payload['discount'] = 0;
    }

    payload['add_on_variants'] = extras.add_on_variants ?? [];
  }

  /**
   * Ensure a selected custom discount has a `discounts` row id before order create/update.
   * Shop discounts already have ids; no-op when custom is not selected.
   */
  private ensureOrderRequestCustomDiscountId$(): Observable<string | null> {
    const sel = String(this.orderRequestSelectedDiscountId || '').trim();
    const saved = this.orderRequestCustomDiscountSaved;
    if (sel !== 'disc_custom' || !saved) {
      return of(null);
    }
    const existingId = String(saved.id || '').trim();
    if (existingId) {
      return of(existingId);
    }

    const bd = this.getPaymentCheckoutBreakdown();
    const discAmt = Math.round(Math.max(0, bd.discount) * 100) / 100;
    if (discAmt <= 0) {
      return of(null);
    }

    const configuredValue = this.getOrderRequestCustomDiscountConfiguredValue(saved);
    if (configuredValue == null || !Number.isFinite(configuredValue) || configuredValue < 0) {
      return throwError(() => new Error('Custom discount value is invalid'));
    }

    return this.orderRequestCatalogService
      .createCustomDiscountForOrder({
        name: saved.name,
        discountType: saved.typeKey === 'percentage' ? 'percentage' : 'flat',
        discountValue: configuredValue,
        reason: saved.reason,
      })
      .pipe(
        map((id: string) => {
          this.orderRequestCustomDiscountSaved = { ...saved, id };
          return id;
        })
      );
  }

  private getOrderRequestCustomDiscountConfiguredValue(saved: {
    valueLabel: string;
    typeKey: 'fixed_amount' | 'percentage';
  }): number | null {
    if (saved.typeKey === 'percentage') {
      return this.parseUsdNumber(String(saved.valueLabel).replace(/%/g, '').trim());
    }
    return this.parseUsdNumber(this.stripUsdForAddonInput(saved.valueLabel));
  }

  /** Merge PUT /order add-on line detail only — totals stay UI-authoritative via sync. */
  private applyOrderCheckoutTotalsFromPutResponse(response: any): void {
    if (!response || typeof response !== 'object' || !this.notification?.data) {
      return;
    }
    const root = (response as { data?: any }).data ?? response;
    if (!root || typeof root !== 'object') {
      return;
    }
    const data = this.notification.data as Record<string, any>;
    const addonLines = this.extractAddOnLinesFromOrderApiRoot(root);
    if (addonLines.length > 0) {
      data['add_ons'] = addonLines;
      data['add_on_variants'] = addonLines;
    }
    // Same as POST /order: keep appointment_time + timezone on data.order for checkout Date/Time.
    this.mergeCheckoutOrderTimezoneFieldsFromApiResponse(response);
  }

  /** Copy appointment_time / timezone from POST|PUT /order onto notification.data.order (+ form). */
  private mergeCheckoutOrderTimezoneFieldsFromApiResponse(response: any): void {
    if (!response || typeof response !== 'object' || !this.notification?.data) {
      return;
    }
    const order = this.extractOrderRecordFromApiResponse(response);
    if (!order) {
      return;
    }
    const data = this.notification.data as Record<string, any>;
    const prevOrder =
      data['order'] && typeof data['order'] === 'object' && !Array.isArray(data['order'])
        ? ({ ...(data['order'] as Record<string, any>) } as Record<string, any>)
        : {};
    const appointmentTime = order['appointment_time'] ?? order['appointmentTime'];
    const timezone = order['timezone'];
    let changed = false;
    if (appointmentTime != null && String(appointmentTime).trim()) {
      const next = String(appointmentTime).trim();
      prevOrder['appointment_time'] = next;
      this.formData.appointmentTime = next;
      data['appointmentTime'] = next;
      changed = true;
    }
    if (typeof timezone === 'string' && timezone.trim()) {
      prevOrder['timezone'] = timezone.trim();
      changed = true;
    }
    if (changed) {
      data['order'] = prevOrder;
    }
  }

  /** Re-align checkout include toggles after Back → edit → save. */
  private syncCheckoutSummaryTogglesAfterEditSave(): void {
    if (this.notification?.type !== 'create-payment-request' || this.isEditMode) {
      return;
    }
    const hasDiscount = this.getCheckoutRawDiscountAmount() > 0.001;
    const hasAddon =
      this.buildCreateOrderAddOnVariantsPayload().length > 0 ||
      this.getCheckoutAddOnVariantsSnapshot().length > 0 ||
      this.getCheckoutAddonAmount() > 0.001;
    this.checkoutSummaryDiscountSelected = hasDiscount;
    this.checkoutSummaryAddonSelected = hasAddon;
  }

  /**
   * Get today's date in YYYY-MM-DD format
   */
  private getTodayDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Get current timezone
   */
  private getTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /**
   * Search customers by phone number
   */
  searchCustomersByPhone(phoneNumber: string): void {
    if (!phoneNumber || phoneNumber.trim().length < 3) {
      this.phoneSearchResults = [];
      this.showPhoneDropdown = false;
      return;
    }

    this.isSearchingCustomers = true;
    this.showPhoneDropdown = true;

    // Search without page/limit params
    this.customerService
      .getCustomers({ contact: phoneNumber })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isSearchingCustomers = false;
          if (response.status && response.data && response.data.customer) {
            this.phoneSearchResults = response.data.customer;
            // Show dropdown if there are results
            this.showPhoneDropdown = this.phoneSearchResults.length > 0;
          } else {
            this.phoneSearchResults = [];
            this.showPhoneDropdown = false;
          }
        },
        error: error => {
          console.error('Error searching customers:', error);
          this.isSearchingCustomers = false;
          this.phoneSearchResults = [];
          this.showPhoneDropdown = false;
        },
      });
  }

  /** Normalize customer id from search / license-plate lookup rows (API field names vary). */
  private resolveCustomerIdFromSearchResult(
    customer: Record<string, any> | null | undefined
  ): string {
    const id = customer?.['id'] ?? customer?.['customer_id'] ?? customer?.['customerId'];
    return id != null && String(id).trim() ? String(id).trim() : '';
  }

  /**
   * Select customer from search results.
   * Create-order phone/name/email pick: if this phone has 2+ plates with redeemable
   * memberships/prepaid, or one plate with 2+ packages → Active subscription (Choose Vehicle
   * / Choose Package). Otherwise stay on sales panel.
   * When the form already has a plate that is not linked to this phone/customer, skip Active
   * Subscription and keep that plate on the create-order form.
   * CWS-1914: a QR or plate scan of a prepaid book selects the purchaser through here too; that
   * pick never hands off, since the scan already named the book and Redeem Prepaid Purchase is
   * the screen that captures who came in.
   */
  selectCustomerFromSearch(customer: any): void {
    const phone = String(customer?.contact || customer?.phone || '').trim();
    if (
      shouldRouteCustomerPickThroughPhoneChooser({
        isCheckoutLayout: this.showOrderRequestCheckoutLayout(),
        phone,
        isLivePrepaidRedeemEntry: this.isOrderRequestPrepaidRedeemEntryLive(),
      })
    ) {
      this.maybeOpenActiveSubscriptionFromOrderRequestPhone(customer, phone);
      return;
    }
    this.applyCustomerSelectionFromSearch(customer);
  }

  /**
   * Phone in Create order → Active subscription when multiple plates have packages, or one plate
   * has multiple packages (CWS-889 / product).
   * No plate on form → run Active Subscription check as today.
   * Form has a plate not linked to this customer → stay on sales panel and preserve the plate.
   * No memberships → stay on sales panel (Aastha: no membership → create order).
   */
  private maybeOpenActiveSubscriptionFromOrderRequestPhone(customer: any, phone: string): void {
    this.isSearchingCustomers = true;
    this.showPhoneDropdown = false;
    const formPlate = (this.formData.vehicleLicensePlate || '').trim();
    const stateHint =
      (this.notification?.data?.state as string | undefined) ||
      (this.notification?.data?.['plate_state'] as string | undefined) ||
      null;

    const locationId = this.getOrderRequestWayLookupLocationId();
    const memberships$ = this.vehicleService.getVehiclesWithMembershipsByPhone(phone);
    const way$ =
      locationId && this.isWayPurchaseLookupEnabledForLocation(locationId)
        ? this.wayPurchaseApi
            .lookupWayPurchases({ location_id: locationId, phone })
            .pipe(catchError(() => of(null)))
        : of(null);

    forkJoin({ memberships: memberships$, way: way$ })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: ({ memberships, way }) => {
          const lookupPayload = memberships?.data ?? null;
          const wayPurchases = (way?.data?.purchases ?? []) as WayNormalizedPurchase[];
          const vehicleCards = buildVehicleCardsFromPhoneResponse(lookupPayload);
          const waySingleWashCount = countRedeemableWaySingleWashPurchases(wayPurchases);
          const customerId =
            String(lookupPayload?.customer?.id || '').trim() ||
            this.resolveCustomerIdFromSearchResult(customer);

          const openActiveSubscriptionIfMultiPlate = (): void => {
            this.isSearchingCustomers = false;
            if (shouldOpenPhoneActiveSubscriptionLookup(vehicleCards, waySingleWashCount)) {
              this.logModalInteraction('customer.select.phone.active_subscription', {
                phone,
                vehicleCount: vehicleCards.length,
                multiPackageOnSinglePlate: vehicleCards.some(card => card.memberships.length >= 2),
                waySingleWashCount,
                customerId: this.resolveCustomerIdFromSearchResult(customer),
              });
              this.multiEligibleModalState.openPhoneLookup({
                phone,
                behavior: 'replace',
                lookupPayload,
                wayPurchases,
              });
              return;
            }
            this.applyCustomerSelectionFromSearch(customer, {
              preserveFormPlate: !!formPlate,
            });
          };

          // No plate on create-order form → existing multi-plate Active Subscription path.
          if (!formPlate) {
            openActiveSubscriptionIfMultiPlate();
            return;
          }

          const plateInMemberships = isFormPlateInMembershipsPhonePayload(
            formPlate,
            lookupPayload,
            stateHint
          );
          if (plateInMemberships) {
            openActiveSubscriptionIfMultiPlate();
            return;
          }

          // Plate may be linked without an active membership — confirm via vehicle list.
          if (!customerId) {
            this.isSearchingCustomers = false;
            this.logModalInteraction('customer.select.phone.skip_active_subscription_new_plate', {
              phone,
              plate: formPlate,
              reason: 'no_customer_id',
            });
            this.applyCustomerSelectionFromSearch(customer, { preserveFormPlate: true });
            return;
          }

          this.vehicleService
            .getVehiclesByCustomerId(customerId)
            .pipe(
              take(1),
              finalize(() => {
                this.isSearchingCustomers = false;
              }),
              takeUntil(this.destroy$)
            )
            .subscribe({
              next: listResponse => {
                const rows = Array.isArray(listResponse?.data) ? listResponse.data : [];
                const plateLinked = isFormPlateInCustomerVehicleRows(formPlate, rows, stateHint);
                if (plateLinked) {
                  openActiveSubscriptionIfMultiPlate();
                  return;
                }
                this.logModalInteraction(
                  'customer.select.phone.skip_active_subscription_new_plate',
                  {
                    phone,
                    plate: formPlate,
                    customerId,
                    membershipVehicleCount: vehicleCards.length,
                  }
                );
                this.applyCustomerSelectionFromSearch(customer, { preserveFormPlate: true });
              },
              error: () => {
                this.logModalInteraction(
                  'customer.select.phone.skip_active_subscription_new_plate',
                  {
                    phone,
                    plate: formPlate,
                    reason: 'vehicle_list_error',
                  }
                );
                this.applyCustomerSelectionFromSearch(customer, { preserveFormPlate: true });
              },
            });
        },
        error: () => {
          this.isSearchingCustomers = false;
          this.applyCustomerSelectionFromSearch(customer, {
            preserveFormPlate: !!formPlate,
          });
        },
      });
  }

  /** Populate create-order form from a customer search row (sales panel path). */
  private applyCustomerSelectionFromSearch(
    customer: any,
    options?: { preserveFormPlate?: boolean }
  ): void {
    const customerId = this.resolveCustomerIdFromSearchResult(customer);
    this.logModalInteraction('customer.select.search', {
      customerId,
      prepaidPurchaseId: customer?.prepaid_purchase_id,
      preserveFormPlate: !!options?.preserveFormPlate,
    });
    this.applyPrepaidFromCustomerSelection(customer);

    if (this.hasOrderRequestRedemptionSelection() && !this.prepaidRedemptionRecipientTouched) {
      this.applyDefaultPrepaidRedemptionRecipientFromPurchaser();
    }

    // Populate form with customer data
    this.formData.customerFirstName = customer.first_name || '';
    this.formData.customerLastName = customer.last_name || '';
    this.formData.customerEmail = customer.email || '';
    this.formData.customerContact = customer.contact || '';
    this.formData.customerId = customerId;
    // CWS-1880: baseline the contact fields so edits made after picking this customer are
    // persisted on submit, exactly as they are when the customer came from a plate lookup.
    this.linkedCustomerSnapshot = customerId
      ? {
          customerId,
          firstName: (this.formData.customerFirstName || '').trim(),
          lastName: (this.formData.customerLastName || '').trim(),
          email: (this.formData.customerEmail || '').trim(),
          contact: (this.formData.customerContact || '').trim(),
        }
      : null;
    this.trackLookedUpCustomerStatus(customerId, customer.customer_status);
    // Plate drives audience rules; re-judge in case plate also changed with the customer.
    this.refreshOrderRequestCustomerHistory();

    // Unified order flows: keep plate lookup when user picks customer for the same LPR plate.
    if (this.showOrderRequestCheckoutLayout()) {
      this.clearOrderRequestHistoryForPlateChange();
      const currentPlate = (this.formData.vehicleLicensePlate || '').trim();
      const preserveFormPlate = !!options?.preserveFormPlate && !!currentPlate;
      const keepPlateLookup =
        preserveFormPlate || this.hasCachedVehicleLookupForPlate(currentPlate);

      if (!keepPlateLookup) {
        this.formData.vehicleId = '';
        this.vehicleDataFetched = false;
        this.fetchedVehicleId = '';
      }

      if (preserveFormPlate) {
        // User already entered a plate (often a new one) — keep it; do not swap to default linked plate.
        if (this.hasCachedVehicleLookupForPlate(currentPlate) && this.fetchedVehicleId) {
          this.formData.vehicleId = this.fetchedVehicleId;
          this.refreshOrderRequestHistoryAfterVehicleResolved(true);
        }
        this.userInteractedFields.add('vehicleLicensePlate');
      } else {
        const appliedInlineVehicle = this.applyInlineVehicleFromCustomerSearch(customer);
        if (!appliedInlineVehicle && this.formData.customerId) {
          if (keepPlateLookup && this.fetchedVehicleId) {
            this.formData.vehicleId = this.fetchedVehicleId;
            this.refreshOrderRequestHistoryAfterVehicleResolved(true);
          } else if (!keepPlateLookup) {
            // Match Add Order behavior: for existing customers, auto-load default vehicle into plate field.
            this.loadDefaultVehicleForSelectedCustomer(
              this.formData.customerId,
              String(customer?.purchase_vehicle_id || '').trim() || undefined
            );
          }
        } else if (
          appliedInlineVehicle &&
          (this.formData.customerId || '').trim() &&
          (this.formData.vehicleId || '').trim()
        ) {
          // Phone/name search embeds vehicle — skip default load but still load wash history/rewash (CWS-1245).
          this.refreshOrderRequestHistoryAfterVehicleResolved(true);
        }
      }
    }

    // Mark fields as user-interacted
    this.userInteractedFields.add('customerFirstName');
    this.userInteractedFields.add('customerLastName');
    this.userInteractedFields.add('customerEmail');
    this.userInteractedFields.add('customerContact');
    this.userInteractedFields.add('customerId');

    // Clear and re-validate phone number
    this.phoneValidationError = '';
    if (this.formData.customerContact) {
      this.onPhoneInputChange(this.formData.customerContact);
    }

    // Clear and re-validate email
    this.emailValidationError = '';
    if (this.formData.customerEmail) {
      this.onEmailInputChange(this.formData.customerEmail);
    }

    this.tryAutoSelectPackageWhenExistingCustomerLinked();

    // Hide dropdowns
    this.showPhoneDropdown = false;
    this.phoneSearchResults = [];
    this.showNameDropdown = false;
    this.nameSearchResults = [];
    this.showEmailDropdown = false;
    this.emailSearchResults = [];
    this.showLicensePlateDropdown = false;
    this.licensePlateSearchResult = null;

    if (this.isInPrepaidRedemptionCheckoutMode()) {
      this.capturePrepaidRedemptionPrefilledSnapshot();
    }
  }

  /**
   * For selected existing customer, load default vehicle (or first vehicle) into order request vehicle fields.
   * Mirrors Add Order modal behavior where first/default vehicle is auto-selected.
   */
  private loadDefaultVehicleForSelectedCustomer(
    customerId: string,
    preferredVehicleId?: string
  ): void {
    this.vehicleService
      .getVehiclesByCustomerId(customerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          // Guard against stale async response if user selected a different customer quickly.
          if ((this.formData.customerId || '').toString().trim() !== customerId.toString().trim()) {
            return;
          }

          const rows = Array.isArray(response?.data) ? response.data : [];
          if (!response?.status || rows.length === 0) {
            return;
          }

          const preferredId = (preferredVehicleId || '').trim();
          const defaultVehicle =
            (preferredId &&
              rows.find((v: any) => String(v?.id || v?.vehicle_id || '').trim() === preferredId)) ||
            rows.find((v: any) => {
              const byDefaultVehicle = String(v?.default_vehicle ?? '').toLowerCase();
              const byIsDefault = String(v?.is_default ?? '').toLowerCase();
              const byPrimary = String(v?.is_primary ?? '').toLowerCase();
              return (
                v?.default_vehicle === true ||
                v?.is_default === true ||
                v?.is_primary === true ||
                byDefaultVehicle === '1' ||
                byDefaultVehicle === 'true' ||
                byIsDefault === '1' ||
                byIsDefault === 'true' ||
                byPrimary === '1' ||
                byPrimary === 'true'
              );
            }) ||
            rows[0];

          if (!defaultVehicle) {
            return;
          }

          const plate = String(
            defaultVehicle.license_plate || defaultVehicle.licensePlate || ''
          ).trim();
          const make = String(defaultVehicle.make || '').trim();
          const model = String(defaultVehicle.model || '').trim();
          const yearRaw = defaultVehicle.year ?? '';
          const vehicleId = String(defaultVehicle.id || defaultVehicle.vehicle_id || '').trim();

          if (plate) {
            this.setCombinedLicensePlateOnForm(
              plate,
              typeof defaultVehicle.state === 'string' ? defaultVehicle.state : null
            );
            this.userInteractedFields.add('vehicleLicensePlate');
          }
          if (make) {
            this.formData.vehicleMake = make;
            this.userInteractedFields.add('vehicleMake');
          }
          if (model) {
            this.formData.vehicleModel = model;
            this.userInteractedFields.add('vehicleModel');
          }
          if (yearRaw !== null && yearRaw !== undefined && String(yearRaw).trim()) {
            this.formData.vehicleYear = String(yearRaw).trim();
            this.userInteractedFields.add('vehicleYear');
          }
          if (vehicleId) {
            this.formData.vehicleId = vehicleId;
            if (plate) {
              this.syncLinkedVehicleSelection(vehicleId, plate);
            }
          }

          // Same UX as license-plate lookup: show make/model/year row when plate is entered.
          const hasYear =
            yearRaw !== null && yearRaw !== undefined && String(yearRaw).trim() !== '';
          const hasAnyDetail = !!make || !!model || hasYear;
          this.showVehicleFieldsForLicensePlate = !!plate && !vehicleId;
          this.vehicleDataFetched = hasAnyDetail || !!vehicleId;
          if (vehicleId) {
            this.fetchedVehicleId = vehicleId;
          }
          if (plate && !hasAnyDetail && !vehicleId) {
            this.fetchVehicleDataByLicensePlate(plate);
          } else if ((this.formData.vehicleId || '').trim()) {
            this.refreshOrderRequestHistoryAfterVehicleResolved(true);
          }
          this.cdr.markForCheck();
        },
        error: error => {
          console.error('Error loading default vehicle for selected customer:', error);
        },
      });
  }

  /**
   * Hide phone dropdown when clicking outside
   */
  hidePhoneDropdown(): void {
    this.logModalInteraction('dropdown.phone.hide');
    setTimeout(() => {
      this.showPhoneDropdown = false;
    }, 200);
  }

  /**
   * Search customers by name
   */
  searchCustomersByName(name: string): void {
    if (!name || name.trim().length < 2) {
      this.nameSearchResults = [];
      this.showNameDropdown = false;
      return;
    }

    this.isSearchingCustomersByName = true;
    this.showNameDropdown = true;

    // Search without page/limit params
    this.customerService
      .getCustomers({ name: name })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isSearchingCustomersByName = false;
          if (response.status && response.data && response.data.customer) {
            this.nameSearchResults = response.data.customer;
            // Show dropdown if there are results
            this.showNameDropdown = this.nameSearchResults.length > 0;
          } else {
            this.nameSearchResults = [];
            this.showNameDropdown = false;
          }
        },
        error: error => {
          console.error('Error searching customers by name:', error);
          this.isSearchingCustomersByName = false;
          this.nameSearchResults = [];
          this.showNameDropdown = false;
        },
      });
  }

  /**
   * Hide name dropdown when clicking outside
   */
  hideNameDropdown(): void {
    this.logModalInteraction('dropdown.name.hide');
    setTimeout(() => {
      this.showNameDropdown = false;
    }, 200);
  }

  /**
   * Search customers by email
   */
  searchCustomersByEmail(email: string): void {
    if (!email || email.trim().length < 3) {
      this.emailSearchResults = [];
      this.showEmailDropdown = false;
      return;
    }

    this.isSearchingCustomersByEmail = true;
    this.showEmailDropdown = true;

    // Search without page/limit params
    this.customerService
      .getCustomers({ search: email })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isSearchingCustomersByEmail = false;
          if (response.status && response.data && response.data.customer) {
            this.emailSearchResults = response.data.customer;
            // Show dropdown if there are results
            this.showEmailDropdown = this.emailSearchResults.length > 0;
          } else {
            this.emailSearchResults = [];
            this.showEmailDropdown = false;
          }
        },
        error: error => {
          this.logger.trackError('NotificationModal.searchCustomersByEmail', error);
          this.isSearchingCustomersByEmail = false;
          this.emailSearchResults = [];
          this.showEmailDropdown = false;
        },
      });
  }

  /**
   * Hide email dropdown when clicking outside
   */
  hideEmailDropdown(): void {
    this.logModalInteraction('dropdown.email.hide');
    setTimeout(() => {
      this.showEmailDropdown = false;
    }, 200);
  }

  /**
   * Get authorization headers for HTTP requests
   */
  private getAuthHeaders(): HttpHeaders {
    return getAuthHeadersFromUserData({ includeJsonContentType: true });
  }

  /**
   * Search customer by license plate
   */
  searchCustomerByLicensePlate(licensePlate: string): void {
    if (!licensePlate || licensePlate.trim().length < 3) {
      this.licensePlateSearchResult = null;
      this.showLicensePlateDropdown = false;
      this.showVehicleFieldsForLicensePlate = false;
      // Don't clear a purchase already in flight/loaded (e.g. from QR or LPR scan bootstrap) —
      // this branch also fires from the synthetic '' reset on modal open.
      if (!(this.prepaidPurchaseId || '').trim()) {
        this.clearPrepaidRedemptionState();
      }
      return;
    }

    const trimmedPlate = licensePlate.trim();
    const plateKey = this.normalizePlateForVehicleLookupCache(trimmedPlate);
    if (this.licensePlateCustomerSearchInFlightKey === plateKey) {
      return;
    }
    if (
      this.licensePlateCustomerSearchCompletedKey === plateKey &&
      (this.wasVehiclePlateLookupAttempted(trimmedPlate) ||
        this.hasCachedVehicleLookupForPlate(trimmedPlate))
    ) {
      return;
    }

    const formPlateKey = this.normalizePlateForVehicleLookupCache(
      this.formData.vehicleLicensePlate
    );
    const samePlateAsForm = formPlateKey === plateKey;
    if (samePlateAsForm) {
      this.licensePlateSearchResult = null;
    } else {
      this.resetVehicleFieldsForPlateLookup();
    }

    this.licensePlateCustomerSearchInFlightKey = plateKey;
    this.isSearchingCustomerByLicensePlate = true;
    this.showLicensePlateDropdown = false;
    this.licensePlateSearchResult = null;
    this.showVehicleFieldsForLicensePlate = false;

    const url = `${environment.baseUrl}/${environment.apiVersion}/customer/by-license-plate?license_plate=${encodeURIComponent(trimmedPlate)}`;
    this.http
      .get<any>(url, { headers: this.getAuthHeaders() })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (this.licensePlateCustomerSearchInFlightKey === plateKey) {
            this.licensePlateCustomerSearchInFlightKey = null;
          }
          this.licensePlateCustomerSearchCompletedKey = plateKey;
        })
      )
      .subscribe({
        next: response => {
          this.isSearchingCustomerByLicensePlate = false;
          const currentPlate = this.formData.vehicleLicensePlate?.trim() ?? '';
          if (currentPlate !== trimmedPlate) {
            return;
          }
          if (response.status && response.status_code === 200 && response.data) {
            this.licensePlateSearchResult = response.data;
            this.showLicensePlateDropdown = false;
            this.applyCustomerFromLicensePlateSearch(response.data);
            this.clearLprPlateCorrectionAfterLicensePlateLookup();
          } else {
            if (!(this.prepaidPurchaseId || '').trim()) {
              this.clearPrepaidRedemptionState();
            }
            this.clearCustomerFieldsFromPlateLookupIfLinked();
            if (!this.licensePlateInputFocused) {
              return;
            }
            this.licensePlateSearchResult = null;
            this.showLicensePlateDropdown = false;
            this.showVehicleFieldsForLicensePlate = true;
            this.fetchVehicleDataByLicensePlate(trimmedPlate, { notifyOnMiss: true });
          }
        },
        error: error => {
          console.error('Error searching customer by license plate:', error);
          this.isSearchingCustomerByLicensePlate = false;
          const currentPlate = this.formData.vehicleLicensePlate?.trim() ?? '';
          if (currentPlate !== trimmedPlate) {
            return;
          }
          if (!(this.prepaidPurchaseId || '').trim()) {
            this.clearPrepaidRedemptionState();
          }
          if (!this.licensePlateInputFocused) {
            return;
          }
          this.clearCustomerFieldsFromPlateLookupIfLinked();
          this.licensePlateSearchResult = null;
          this.showLicensePlateDropdown = false;
          if (error.status === 404 || (error.error && error.error.status_code === 404)) {
            this.showVehicleFieldsForLicensePlate = true;
            this.fetchVehicleDataByLicensePlate(trimmedPlate, { notifyOnMiss: true });
          } else {
            this.showVehicleFieldsForLicensePlate = false;
            this.clearLprPlateCorrectionAfterLicensePlateLookup();
          }
        },
      });
  }

  /** Clears LPR plate-correction overlay after license search / vehicle lookup completes. */
  private clearLprPlateCorrectionAfterLicensePlateLookup(): void {
    if (!this.isApplyingLprPlateCorrection) {
      return;
    }
    this.isApplyingLprPlateCorrection = false;
    this.cdr.detectChanges();
  }

  private normalizePrepaidPurchaseId(value: any): string | null {
    const id = value != null ? String(value).trim() : '';
    return id || null;
  }

  /** Order-request or fleet checkout where prepaid purchase vs redeem applies. */
  isOrderRequestPrepaidCheckoutContext(): boolean {
    return (
      this.showOrderRequestCheckoutLayout() || this.notification?.type === 'fleet-vehicle-found'
    );
  }

  customerHasPrepaidPurchase(customer: { prepaid_purchase_id?: any } | null | undefined): boolean {
    return !!this.normalizePrepaidPurchaseId(customer?.prepaid_purchase_id);
  }

  /**
   * Plate for a customer-search row. A vendor-imported member (DRB / EverWash) becomes one
   * Way customer per membership, so a phone search returns several rows with identical name
   * and number — the plate is the only field that separates them (CWS-920).
   *
   * Falls back through customers.license_plate then the joined default vehicle, matching the
   * COALESCE the search query already does, so this needs no API change.
   */
  customerSearchPlate(
    customer: { license_plate?: any; vehicle?: { license_plate?: any } | null } | null
  ): string {
    const plate =
      String(customer?.license_plate ?? '').trim() ||
      String(customer?.vehicle?.license_plate ?? '').trim();
    return plate.toUpperCase();
  }

  /**
   * "2012 Honda Odyssey" for a customer-search row, from whatever the import managed to parse.
   * Secondary to the plate: some vendor rows carry a vehicle description but no plate at all.
   */
  customerSearchVehicle(
    customer: { vehicle?: { make?: any; model?: any; year?: any } | null } | null
  ): string {
    const v = customer?.vehicle;
    if (!v) {
      return '';
    }
    return [v.year, v.make, v.model]
      .map(part => String(part ?? '').trim())
      .filter(Boolean)
      .join(' ');
  }

  private applyPrepaidFromCustomerSelection(customer: any): void {
    if (!this.isOrderRequestPrepaidCheckoutContext()) {
      return;
    }
    const prepaidId = this.normalizePrepaidPurchaseId(customer?.prepaid_purchase_id);
    if (prepaidId) {
      this.resolvePrepaidPurchaseFromCustomer(customer, prepaidId);
    } else if (!(this.prepaidPurchaseId || '').trim()) {
      // Only clear if no purchase is already in flight (e.g. from QR or LPR scan bootstrap)
      this.clearPrepaidRedemptionState();
    }
  }

  /**
   * Applies vehicle fields from customer list row when API embeds vehicle or license_plate.
   * @returns true when inline vehicle data was applied (skip default-vehicle fetch).
   */
  private applyInlineVehicleFromCustomerSearch(customer: any): boolean {
    const vehicle = customer?.vehicle;
    const plateFromCustomer = String(customer?.license_plate || '').trim();
    const plateFromVehicle = String(vehicle?.license_plate || vehicle?.licensePlate || '').trim();
    const plate = plateFromVehicle || plateFromCustomer;

    if (!vehicle && !plate) {
      return false;
    }

    if (vehicle) {
      this.formData.vehicleMake = vehicle.make || '';
      this.formData.vehicleModel = vehicle.model || '';
      this.formData.vehicleYear = vehicle.year != null ? String(vehicle.year) : '';
      const vid = String(vehicle.id || vehicle.vehicle_id || '').trim();
      this.formData.vehicleId = vid;
      this.userInteractedFields.add('vehicleMake');
      this.userInteractedFields.add('vehicleModel');
      this.userInteractedFields.add('vehicleYear');
    } else {
      this.formData.vehicleId = '';
    }

    if (plate && this.canApplyExternalLicensePlateToForm()) {
      this.formData.vehicleLicensePlate = plate;
      this.userInteractedFields.add('vehicleLicensePlate');
    }

    const hasYear =
      vehicle?.year != null && vehicle?.year !== undefined && String(vehicle.year).trim() !== '';
    const hasAnyDetail = !!(vehicle?.make || vehicle?.model || hasYear);
    const vehicleId = String(this.formData.vehicleId || '').trim();
    this.showVehicleFieldsForLicensePlate = !!(plate || hasAnyDetail || vehicleId);
    this.vehicleDataFetched = hasAnyDetail || !!vehicleId;
    if (vehicleId) {
      this.fetchedVehicleId = vehicleId;
    }
    if (plate && !hasAnyDetail && !vehicleId) {
      this.fetchVehicleDataByLicensePlate(plate);
    }
    return !!(vehicle || plate);
  }

  getPurchaserSnapshotForRedemption(): PrepaidRedemptionPurchaserSnapshot {
    return getPrepaidRedemptionPurchaserBaseline(
      this.prepaidRedemptionPrefilledSnapshot,
      this.formData,
      this.prepaidPurchaseStatus
    );
  }

  private getPrepaidRedemptionPrefilledBaseline(): PrepaidRedemptionPurchaserSnapshot {
    return this.prepaidRedemptionPrefilledSnapshot ?? this.getPurchaserSnapshotForRedemption();
  }

  private capturePrepaidRedemptionPrefilledSnapshot(): void {
    if (this.prepaidRedemptionPrefilledSnapshot) {
      return;
    }
    this.prepaidRedemptionPrefilledSnapshot = buildPurchaserSnapshotFromFormData(
      this.formData,
      this.prepaidPurchaseStatus
    );
  }

  private resetPrepaidRedemptionRecipient(): void {
    this.prepaidRedemptionRecipient = emptyPrepaidRedemptionRecipient();
    this.prepaidRedemptionRecipientTouched = false;
    this.prepaidRedemptionPrefilledSnapshot = null;
    this.clearPrepaidRedemptionRecipientFieldErrors();
    this.clearRecipientPhoneSearch();
  }

  private applyDefaultPrepaidRedemptionRecipientFromPurchaser(): void {
    this.capturePrepaidRedemptionPrefilledSnapshot();
    this.prepaidRedemptionRecipient = buildPrepaidRedemptionRecipientFromPurchaser(
      this.getPurchaserSnapshotForRedemption()
    );
  }

  onPrepaidRedemptionRecipientChange(recipient: PrepaidRedemptionRecipientForm): void {
    const previous = this.prepaidRedemptionRecipient;
    // CWS-1881: the pane was prefilled from the purchaser, ids and all. Once the plate or the
    // phone has been typed over, those ids are no longer this redeemer's.
    this.prepaidRedemptionRecipient = dropStalePurchaserIdsFromRecipient(
      { ...recipient },
      this.prepaidRedemptionPrefilledSnapshot
    );
    if (recipient.phone !== previous.phone) {
      this.prepaidRedemptionPhoneError = '';
    }
    if (recipient.email !== previous.email) {
      this.prepaidRedemptionEmailError = '';
    }
    if (recipient.firstName !== previous.firstName) {
      this.prepaidRedemptionFirstNameError = '';
    }
    if (recipient.lastName !== previous.lastName) {
      this.prepaidRedemptionLastNameError = '';
    }
    this.cdr.detectChanges();
  }

  onPrepaidRedemptionRecipientFieldTouched(): void {
    this.prepaidRedemptionRecipientTouched = true;
  }

  onRecipientPlateSearchInput(plate: string): void {
    // CWS-1881: the refusal was about the plate that was there; editing it clears the verdict.
    this.prepaidRedemptionPlateError = '';
    const trimmed = (plate ?? '').trim();
    if (trimmed.length >= 3) {
      this.searchRecipientCustomerByLicensePlate(trimmed);
    }
  }

  onRecipientPhoneSearchInput(phone: string): void {
    const trimmed = (phone ?? '').trim();
    if (trimmed.length >= 3) {
      this.recipientPhoneSearchSubject.next(trimmed);
    } else {
      this.clearRecipientPhoneSearch();
    }
  }

  hideRecipientPhoneDropdown(): void {
    setTimeout(() => {
      this.showRecipientPhoneDropdown = false;
    }, 200);
  }

  selectRecipientFromPhoneSearch(customer: Record<string, any>): void {
    this.onPrepaidRedemptionRecipientFieldTouched();
    this.applyRecipientFromCustomerLookup(customer);
    this.clearRecipientPhoneSearch();
  }

  private clearRecipientPhoneSearch(): void {
    this.recipientPhoneSearchResults = [];
    this.showRecipientPhoneDropdown = false;
    this.isSearchingRecipientByPhone = false;
  }

  /** Phone lookup for redeemer pane only (does not change booking purchaser fields). */
  private searchRecipientCustomerByPhone(phoneNumber: string): void {
    const phone = phoneNumber?.trim();
    if (!phone || phone.length < 3) {
      this.clearRecipientPhoneSearch();
      return;
    }

    this.isSearchingRecipientByPhone = true;
    this.showRecipientPhoneDropdown = true;

    this.customerService
      .getCustomers({ contact: phone })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isSearchingRecipientByPhone = false;
          const currentPhone = this.prepaidRedemptionRecipient.phone?.trim() ?? '';
          if (
            normalizePhoneForRecipientCompare(currentPhone) !==
            normalizePhoneForRecipientCompare(phone)
          ) {
            return;
          }

          const rows = this.extractCustomerListRowsFromApiData(response?.data);
          if (!response?.status || rows.length === 0) {
            this.recipientPhoneSearchResults = [];
            this.showRecipientPhoneDropdown = false;
            return;
          }

          if (rows.length === 1) {
            this.onPrepaidRedemptionRecipientFieldTouched();
            this.applyRecipientFromCustomerLookup(rows[0]);
            this.recipientPhoneSearchResults = [];
            this.showRecipientPhoneDropdown = false;
            return;
          }

          this.recipientPhoneSearchResults = rows;
          this.showRecipientPhoneDropdown = true;
          this.cdr.detectChanges();
        },
        error: () => {
          this.isSearchingRecipientByPhone = false;
          this.recipientPhoneSearchResults = [];
          this.showRecipientPhoneDropdown = false;
        },
      });
  }

  /** License-plate lookup for redeemer pane only (does not change booking purchaser fields). */
  private searchRecipientCustomerByLicensePlate(licensePlate: string): void {
    const plate = licensePlate?.trim();
    if (!plate || plate.length < 3) {
      return;
    }
    const url = `${environment.baseUrl}/${environment.apiVersion}/customer/by-license-plate?license_plate=${encodeURIComponent(plate)}`;
    this.http
      .get<any>(url, { headers: this.getAuthHeaders() })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const currentPlate = this.prepaidRedemptionRecipient.licensePlate?.trim() ?? '';
          if (currentPlate !== plate) {
            return;
          }
          if (response.status && response.status_code === 200 && response.data) {
            this.applyRecipientFromCustomerLookup(response.data);
          }
        },
        error: () => {
          /* No customer for plate — attendant may enter details manually. */
        },
      });
  }

  private applyRecipientFromCustomerLookup(customerData: Record<string, any>): void {
    const vehicle = customerData['vehicle'] as Record<string, any> | undefined;
    const plateFromVehicle = String(
      vehicle?.['license_plate'] || vehicle?.['licensePlate'] || ''
    ).trim();
    const plateFromCustomer = String(customerData['license_plate'] || '').trim();
    const customerId = String(customerData['customer_id'] || customerData['id'] || '').trim();
    const vehicleId = String(vehicle?.['id'] || vehicle?.['vehicle_id'] || '').trim();

    this.prepaidRedemptionRecipient = {
      ...this.prepaidRedemptionRecipient,
      licensePlate:
        plateFromVehicle || plateFromCustomer || this.prepaidRedemptionRecipient.licensePlate,
      phone: String(customerData['contact'] || '') || this.prepaidRedemptionRecipient.phone,
      firstName:
        String(customerData['first_name'] || '') || this.prepaidRedemptionRecipient.firstName,
      lastName: String(customerData['last_name'] || '') || this.prepaidRedemptionRecipient.lastName,
      email: String(customerData['email'] || '') || this.prepaidRedemptionRecipient.email,
      customerId: customerId || this.prepaidRedemptionRecipient.customerId,
      vehicleId: vehicleId || this.prepaidRedemptionRecipient.vehicleId,
    };
    this.cdr.detectChanges();
  }

  private fetchCustomerByLicensePlateObservable(licensePlate: string): Observable<any | null> {
    const plate = licensePlate?.trim();
    if (!plate || plate.length < 3) {
      return of(null);
    }
    const url = `${environment.baseUrl}/${environment.apiVersion}/customer/by-license-plate?license_plate=${encodeURIComponent(plate)}`;
    return this.http.get<any>(url, { headers: this.getAuthHeaders() }).pipe(
      map(response =>
        response.status && response.status_code === 200 && response.data ? response.data : null
      ),
      catchError(() => of(null))
    );
  }

  private resolvePrepaidRedemptionRecipientIds(): Observable<{
    customerId: string;
    vehicleId: string;
  }> {
    const purchaser = this.getPurchaserSnapshotForRedemption();
    const recipient = this.prepaidRedemptionRecipient;
    const action = resolvePrepaidRedemptionAction(
      recipient,
      purchaser,
      this.prepaidRedemptionPrefilledSnapshot
    );

    switch (action) {
      case 'reuse_purchaser':
        return this.reusePurchaserRedemptionIds(purchaser, recipient);
      case 'update_customer':
        return this.updatePurchaserRedemptionCustomer(purchaser, recipient);
      case 'create_customer':
        return this.createRedemptionRecipientCustomerAndVehicle(recipient);
      case 'add_vehicle':
        return this.addVehicleForPurchaserRedemption(purchaser, recipient);
      case 'resolve_different_customer':
        return this.resolveDifferentRedemptionRecipient(recipient);
      default:
        return this.reusePurchaserRedemptionIds(purchaser, recipient);
    }
  }

  private reusePurchaserRedemptionIds(
    purchaser: PrepaidRedemptionPurchaserSnapshot,
    recipient: PrepaidRedemptionRecipientForm
  ): Observable<{ customerId: string; vehicleId: string }> {
    const customerId = (purchaser.customerId || recipient.customerId || '').trim();
    const vehicleId = (purchaser.vehicleId || recipient.vehicleId || '').trim();
    if (!customerId) {
      return throwError(
        () => new Error('Customer is required. Select the customer from the license plate match.')
      );
    }
    if (!vehicleId) {
      return this.findOrCreateVehicleForRecipientCustomer(customerId, recipient.licensePlate).pipe(
        map(resolvedVehicleId => ({ customerId, vehicleId: resolvedVehicleId }))
      );
    }
    return of({ customerId, vehicleId });
  }

  private updatePurchaserRedemptionCustomer(
    purchaser: PrepaidRedemptionPurchaserSnapshot,
    recipient: PrepaidRedemptionRecipientForm
  ): Observable<{ customerId: string; vehicleId: string }> {
    const purchaserCustomerId = (purchaser.customerId || recipient.customerId || '').trim();
    if (!purchaserCustomerId) {
      return throwError(
        () => new Error('Customer is required. Select the customer from the license plate match.')
      );
    }

    const baseline = this.getPrepaidRedemptionPrefilledBaseline();
    const phoneOverwritten = prepaidRedemptionContactFieldOverwritten(recipient, baseline, 'phone');

    if (!phoneOverwritten) {
      return this.patchPurchaserRedemptionCustomer(purchaserCustomerId, recipient, purchaser);
    }

    return this.lookupRedemptionCustomerByPhone(recipient).pipe(
      switchMap(existingCustomerId => {
        if (existingCustomerId && existingCustomerId !== purchaserCustomerId) {
          return this.reuseExistingRedemptionCustomer(existingCustomerId, recipient);
        }
        return this.patchPurchaserRedemptionCustomer(purchaserCustomerId, recipient, purchaser);
      })
    );
  }

  /**
   * CWS-1881: the redeemer is the purchaser, with something typed into a field their record
   * had empty. Fill only those blanks.
   *
   * This used to PUT the whole lane form onto the purchaser, which is how a walk-in typed
   * into a prefilled pane renamed and re-emailed the person who bought the book. The same
   * blank-only rule the matched-on-phone path already follows applies here: a record can
   * gain a field it never had and can never lose one it has, and everything typed is kept
   * verbatim on the redemption row either way.
   */
  private patchPurchaserRedemptionCustomer(
    customerId: string,
    recipient: PrepaidRedemptionRecipientForm,
    purchaser: PrepaidRedemptionPurchaserSnapshot
  ): Observable<{ customerId: string; vehicleId: string }> {
    return this.customerService.getCustomer(customerId).pipe(
      catchError(() => of(null)),
      switchMap(response => {
        const existing = (response as { data?: Record<string, unknown> } | null)?.data ?? null;
        const patch = buildBlankOnlyCustomerPatch(existing, recipient);
        return patch ? this.customerService.updateCustomer(customerId, patch) : of(null);
      }),
      switchMap(() => this.resolveVehicleIdForRedemptionCustomer(customerId, recipient, purchaser)),
      catchError(error =>
        throwError(
          () =>
            new Error(
              error?.error?.message ||
                error?.message ||
                'Failed to update customer before redemption.'
            )
        )
      )
    );
  }

  private addVehicleForPurchaserRedemption(
    purchaser: PrepaidRedemptionPurchaserSnapshot,
    recipient: PrepaidRedemptionRecipientForm
  ): Observable<{ customerId: string; vehicleId: string }> {
    const customerId = (purchaser.customerId || recipient.customerId || '').trim();
    if (!customerId) {
      return throwError(
        () => new Error('Customer is required. Select the customer from the license plate match.')
      );
    }
    return this.findOrCreateVehicleForRecipientCustomer(customerId, recipient.licensePlate).pipe(
      map(vehicleId => ({ customerId, vehicleId }))
    );
  }

  private resolveVehicleIdForRedemptionCustomer(
    customerId: string,
    recipient: PrepaidRedemptionRecipientForm,
    purchaser: PrepaidRedemptionPurchaserSnapshot
  ): Observable<{ customerId: string; vehicleId: string }> {
    const plateUnchanged = !prepaidRedemptionVehiclePlateChanged(recipient, purchaser);
    const existingVehicleId = (
      recipient.vehicleId ||
      (plateUnchanged ? purchaser.vehicleId : '') ||
      ''
    ).trim();
    if (existingVehicleId) {
      return of({ customerId, vehicleId: existingVehicleId });
    }
    return this.findOrCreateVehicleForRecipientCustomer(customerId, recipient.licensePlate).pipe(
      map(vehicleId => ({ customerId, vehicleId }))
    );
  }

  private resolveDifferentRedemptionRecipient(
    recipient: PrepaidRedemptionRecipientForm
  ): Observable<{ customerId: string; vehicleId: string }> {
    const plate = recipient.licensePlate?.trim();
    if (!plate) {
      return throwError(() => new Error('License plate is required for redemption.'));
    }

    // CWS-1881: the phone is the identity, so it is matched before the plate. A car parked
    // under someone else's name must not decide who is standing at the lane, and a phone we
    // already hold opens the "customer already exists" panel rather than reusing them quietly.
    if (recipient.phone?.trim()) {
      return this.createRedemptionRecipientCustomerAndVehicle(recipient);
    }

    return this.fetchCustomerByLicensePlateObservable(plate).pipe(
      switchMap(customerData => {
        if (customerData) {
          return this.resolveRedemptionRecipientFromExistingCustomer(customerData, recipient);
        }
        return this.createRedemptionRecipientCustomerAndVehicle(recipient);
      })
    );
  }

  private resolveRedemptionRecipientFromExistingCustomer(
    customerData: any,
    recipient: PrepaidRedemptionRecipientForm
  ): Observable<{ customerId: string; vehicleId: string }> {
    const customerId = String(customerData?.customer_id || customerData?.id || '').trim();
    if (!customerId) {
      return throwError(() => new Error('Customer found but no customer ID returned.'));
    }

    const vehicleIdFromLookup = String(
      customerData?.vehicle?.id || customerData?.vehicle?.vehicle_id || ''
    ).trim();

    // CWS-1881: a customer we already have, matched on the plate the attendant typed. Fill
    // only what their record is missing — the full PUT that stood here renamed a third
    // customer whenever a plate was entered wrongly.
    const patch = buildBlankOnlyCustomerPatch(
      customerData as Record<string, unknown> | null,
      recipient
    );
    const afterCustomerUpdate$ = patch
      ? this.customerService.updateCustomer(customerId, patch).pipe(catchError(() => of(null)))
      : of(null);

    if (vehicleIdFromLookup) {
      return afterCustomerUpdate$.pipe(map(() => ({ customerId, vehicleId: vehicleIdFromLookup })));
    }

    return afterCustomerUpdate$.pipe(
      switchMap(() =>
        this.findOrCreateVehicleForRecipientCustomer(customerId, recipient.licensePlate)
      ),
      map(resolvedVehicleId => ({ customerId, vehicleId: resolvedVehicleId }))
    );
  }

  private lookupRedemptionCustomerByPhone(
    recipient: PrepaidRedemptionRecipientForm
  ): Observable<string | null> {
    const phone = recipient.phone?.trim();
    if (!phone) {
      return of(null);
    }

    return this.customerService.getCustomers({ contact: phone }).pipe(
      map(response => this.extractFirstCustomerIdFromListResponse(response)),
      catchError(() => of(null))
    );
  }

  private lookupRedemptionCustomerByPhoneOrEmail(
    recipient: PrepaidRedemptionRecipientForm
  ): Observable<string | null> {
    const phone = recipient.phone?.trim();
    if (phone) {
      return this.lookupRedemptionCustomerByPhone(recipient);
    }

    const email = recipient.email?.trim();
    if (email) {
      return this.customerService.getCustomers({ search: email }).pipe(
        map(response => this.extractFirstCustomerIdFromListResponse(response)),
        catchError(() => of(null))
      );
    }

    return of(null);
  }

  private extractFirstCustomerIdFromListResponse(response: {
    status?: boolean;
    data?: any;
  }): string | null {
    if (!response?.status || !response?.data) {
      return null;
    }
    const rows = this.extractCustomerListRowsFromApiData(response.data);
    const first = rows[0];
    if (!first) {
      return null;
    }
    const customerId = String(first.customer_id || first.id || '').trim();
    return customerId || null;
  }

  private extractCustomerListRowsFromApiData(data: any): {
    customer_id?: string;
    id?: string;
  }[] {
    if (!data || typeof data !== 'object') {
      return [];
    }
    const record = data as Record<string, any>;
    const candidates = [
      record['customer'],
      record['customers'],
      record['items'],
      record['results'],
      record['list'],
      Array.isArray(record['data']) ? record['data'] : null,
    ];
    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate as { customer_id?: string; id?: string }[];
      }
      if (candidate && typeof candidate === 'object') {
        return [candidate as { customer_id?: string; id?: string }];
      }
    }
    return [];
  }

  /**
   * CWS-1881: the redeemer's phone matched a customer we already have. Reuse them — never
   * duplicate — and fill only the fields that record is actually missing.
   *
   * This used to PUT the whole lane form onto the matched customer, so a mistyped digit that
   * landed on a real customer silently renamed them and replaced their email. Everything the
   * attendant typed is still kept verbatim on the redemption row, and the design's route for
   * correcting a customer is the row's link to their record.
   */
  private reuseExistingRedemptionCustomer(
    customerId: string,
    recipient: PrepaidRedemptionRecipientForm
  ): Observable<{ customerId: string; vehicleId: string }> {
    return this.customerService.getCustomer(customerId).pipe(
      catchError(() => of(null)),
      switchMap(response => {
        const existing = (response as { data?: Record<string, unknown> } | null)?.data ?? null;
        const patch = buildBlankOnlyCustomerPatch(existing, recipient);
        if (!patch) {
          return of(null);
        }
        return this.customerService
          .updateCustomer(customerId, patch)
          .pipe(catchError(() => of(null)));
      }),
      switchMap(() =>
        this.findOrCreateVehicleForRecipientCustomer(customerId, recipient.licensePlate)
      ),
      map(vehicleId => ({ customerId, vehicleId }))
    );
  }

  /**
   * CWS-1881: resolve the redeemer to a customer, creating one only when that is warranted.
   *
   * Two rules, in order:
   *
   *  1. Never duplicate. An existing customer is matched on the phone first, then the email,
   *     before anything is created — a relative who already shops here must not gain a second
   *     record because they redeemed a wash.
   *  2. A phone is what creates a customer. Someone who gives only a name, or a name and an
   *     email, is recorded on the redemption row itself and gets no customer record; the
   *     design shows exactly this row ("Dana K.", no phone, no link to a customer).
   *
   * Returns a null `customerId` when no customer was matched or created. The caller keeps the
   * purchaser on the order — an order cannot exist without a customer — and sends the
   * redeemer's details alongside, so the redemption is still logged against the right person.
   */
  private createRedemptionRecipientCustomerAndVehicle(
    recipient: PrepaidRedemptionRecipientForm
  ): Observable<{ customerId: string | null; vehicleId: string }> {
    return this.lookupRedemptionCustomerByPhoneOrEmail(recipient).pipe(
      switchMap(existingCustomerId => {
        if (existingCustomerId) {
          return this.reuseExistingRedemptionCustomer(existingCustomerId, recipient);
        }
        if (!recipient.phone?.trim()) {
          return this.resolveRedemptionWithoutCustomerRecord(recipient);
        }
        return this.postRedemptionRecipientCustomerAndVehicle(recipient);
      })
    );
  }

  /**
   * No phone, so no customer record. The wash still has to be attributed to a vehicle, which
   * is resolved against the purchaser so the plate is on file for the next visit.
   */
  private resolveRedemptionWithoutCustomerRecord(
    recipient: PrepaidRedemptionRecipientForm
  ): Observable<{ customerId: string | null; vehicleId: string }> {
    // CWS-1914: whose record the car is parked against. The redeemer has none — they gave a
    // name or an email but no phone — so the book's own customer carries it. Read off the
    // purchase rather than the redeemer snapshot: the snapshot describes the lane, which is
    // exactly the thing we have just established has nobody behind it.
    const purchaserCustomerId = (
      this.getPurchaserSnapshotForRedemption().customerId ||
      this.prepaidPurchaseStatus?.customer_id ||
      ''
    ).trim();
    this.logModalInteraction('prepaid.redeem.noCustomerRecord', {
      hasName: !!recipient.firstName?.trim(),
      hasEmail: !!recipient.email?.trim(),
      hasPlate: !!recipient.licensePlate?.trim(),
    });
    if (!purchaserCustomerId) {
      return of({ customerId: null, vehicleId: (recipient.vehicleId || '').trim() });
    }
    return this.findOrCreateVehicleForRecipientCustomer(
      purchaserCustomerId,
      recipient.licensePlate
    ).pipe(map(vehicleId => ({ customerId: null, vehicleId })));
  }

  private postRedemptionRecipientCustomerAndVehicle(
    recipient: PrepaidRedemptionRecipientForm
  ): Observable<{ customerId: string; vehicleId: string }> {
    const payload: Record<string, any> = {
      first_name: recipient.firstName,
      last_name: recipient.lastName,
      email: recipient.email,
      contact: recipient.phone,
      type: 'regular',
      license_plate: null,
      customer_status: 'active',
      force_create: false,
    };

    return this.apiService
      .post<{ customer_id?: string; id?: string; message?: string }>('customer', payload)
      .pipe(
        switchMap(response => {
          const customerId = this.extractCustomerIdFromCreateResponse(response);
          if (!customerId) {
            return throwError(() => new Error('Customer created but no customer ID returned.'));
          }
          return this.findOrCreateVehicleForRecipientCustomer(
            customerId,
            recipient.licensePlate
          ).pipe(map(vehicleId => ({ customerId, vehicleId })));
        }),
        catchError(error => this.handleRedemptionRecipientCreateCustomerError(error, recipient))
      );
  }

  private extractCustomerIdFromCreateResponse(response: {
    data?: { customer_id?: string; id?: string; message?: string };
    message?: string;
  }): string {
    return String(response.data?.customer_id || response.data?.id || '').trim();
  }

  private handleRedemptionRecipientCreateCustomerError(
    error: any,
    recipient: PrepaidRedemptionRecipientForm
  ): Observable<{ customerId: string; vehicleId: string }> {
    const err = error as {
      status?: number;
      error?: { message?: string; data?: { customer_id?: string; id?: string } };
      message?: string;
    };
    const message = String(err?.error?.message || err?.message || '');
    const isDuplicate = err?.status === 409 || message.toLowerCase().includes('already exists');

    if (isDuplicate) {
      const customerId = String(err?.error?.data?.customer_id || err?.error?.data?.id || '').trim();
      if (customerId) {
        return this.reuseExistingRedemptionCustomer(customerId, recipient);
      }
    }

    return throwError(() => new Error(message || 'Failed to create customer for redemption.'));
  }

  /**
   * When redeem contact resolves to a different customer but the plate is unchanged from the
   * prefilled purchaser, POST vehicle must include existing_customer_id so the backend can
   * transfer ownership (a plate may only belong to one customer at a time).
   */
  private getPrepaidRedemptionVehicleTransferCustomerId(
    targetCustomerId: string,
    fullLicensePlate: string
  ): string | undefined {
    const baseline = this.getPrepaidRedemptionPrefilledBaseline();
    const baselineCustomerId = (baseline.customerId || '').trim();
    const targetId = (targetCustomerId || '').trim();
    if (!baselineCustomerId || baselineCustomerId === targetId) {
      return undefined;
    }
    const baselinePlate = normalizePlateForRecipientCompare(baseline.licensePlate);
    const targetPlate = normalizePlateForRecipientCompare(fullLicensePlate);
    if (!baselinePlate || !targetPlate || baselinePlate !== targetPlate) {
      return undefined;
    }
    return baselineCustomerId;
  }

  /**
   * CWS-1881: the backend refuses a plate that belongs to a third customer with a typed
   * code (VEHICLE_PLATE_TAKEN, 409) rather than quietly moving the car. Name it on the
   * plate box, the way the Add vehicle card and the family flow already do, so the
   * attendant can see which field is wrong instead of reading a sentence in the footer.
   */
  private flagRedemptionPlateTaken(error: unknown): void {
    const err = error as { status?: number; error?: { data?: { code?: string } } };
    if (err?.status === 409 && err?.error?.data?.code === 'VEHICLE_PLATE_TAKEN') {
      this.prepaidRedemptionPlateError = PREPAID_REDEMPTION_PLATE_TAKEN_MESSAGE;
    }
  }

  private findOrCreateVehicleForRecipientCustomer(
    customerId: string,
    fullLicensePlate: string
  ): Observable<string> {
    const parsed = splitLicensePlateForApi(fullLicensePlate, this.notification?.data?.state);
    const plateNoState = parsed.license_plate?.trim().toUpperCase();
    if (!plateNoState) {
      return throwError(() => new Error('Vehicle ID is required for redemption.'));
    }

    return this.vehicleService.getVehiclesByCustomerId(customerId).pipe(
      switchMap(response => {
        if (response.status && Array.isArray(response.data)) {
          const match = response.data.find(
            (vehicle: {
              license_plate?: string;
              licensePlate?: string;
              vehicle_id?: string;
              id?: string;
            }) => {
              const vehiclePlate = (vehicle.license_plate || vehicle.licensePlate || '')
                .trim()
                .toUpperCase();
              return vehiclePlate === plateNoState;
            }
          );
          const existingId = String(match?.vehicle_id || match?.id || '').trim();
          if (existingId) {
            return of(existingId);
          }
        }
        return this.resolveRedemptionVehicleTransferCustomerId(
          customerId,
          fullLicensePlate,
          plateNoState
        ).pipe(
          switchMap(existingCustomerIdForTransfer => {
            const vehicleData: CreateVehiclePayload = {
              license_plate: parsed.license_plate,
              state: parsed.state,
              vin: '',
              make: '',
              model: '',
              year: new Date().getFullYear(),
              customer_id: customerId,
              is_family_plan: false,
              ...(existingCustomerIdForTransfer
                ? { existing_customer_id: existingCustomerIdForTransfer }
                : {}),
            };
            return this.createRedemptionRecipientVehicle(vehicleData);
          })
        );
      })
    );
  }

  /**
   * CWS-1914: who, if anyone, this plate should be taken from.
   *
   * The baseline rule comes first: the purchaser's own car, being moved to the person who
   * actually drove it in. Failing that, the plate may be parked under the placeholder a lane
   * read creates before anyone is identified — an "Unknown Customer" with no name, phone or
   * email — and that is not a customer anybody would miss. Anything else is a real customer
   * and is left alone, so the backend's VEHICLE_PLATE_TAKEN still refuses the reassignment.
   */
  private resolveRedemptionVehicleTransferCustomerId(
    targetCustomerId: string,
    fullLicensePlate: string,
    plateNoState: string
  ): Observable<string | undefined> {
    const fromBaseline = this.getPrepaidRedemptionVehicleTransferCustomerId(
      targetCustomerId,
      fullLicensePlate
    );
    if (fromBaseline) {
      return of(fromBaseline);
    }
    // The lookup takes the plate the way it is stored — without the state prefix the form
    // carries. Asking for "GACVVFFVFD" instead of "CVVFFVFD" matches nobody, so the car
    // looked unowned and the create was refused by the backend instead.
    return this.fetchCustomerByLicensePlateObservable(plateNoState).pipe(
      map(customerData => {
        if (!customerData) {
          return undefined;
        }
        const ownerId = String(customerData.customer_id || customerData.id || '').trim();
        if (!ownerId || ownerId === (targetCustomerId || '').trim()) {
          return undefined;
        }
        return isPlaceholderCustomerRecord(customerData as Record<string, unknown>)
          ? ownerId
          : undefined;
      }),
      catchError(() => of(undefined))
    );
  }

  private createRedemptionRecipientVehicle(vehicleData: CreateVehiclePayload): Observable<string> {
    return this.vehicleService.createVehicle(vehicleData).pipe(
      map(createResponse => {
        const vehicleId = String(
          createResponse.data?.vehicle_id || createResponse.data?.id || ''
        ).trim();
        if (!vehicleId) {
          throw new Error('Vehicle created but no vehicle ID returned.');
        }
        return vehicleId;
      }),
      catchError(error => {
        this.flagRedemptionPlateTaken(error);
        return throwError(() => error);
      })
    );
  }

  private clearPrepaidRedemptionState(): void {
    this.prepaidPurchaseId = null;
    this.prepaidPurchaseStatus = null;
    this.isLoadingPrepaidPurchaseStatus = false;
    this.prepaidPurchaseStatusError = null;
    this.clearPrepaidRedemptionsListState();
    this.orderRequestRedemptionSlotKeys = [];
    this.resetPrepaidRedemptionRecipient();
  }

  private clearPrepaidRedemptionsListState(): void {
    this.prepaidRedemptions = [];
    this.isLoadingPrepaidRedemptions = false;
    this.prepaidRedemptionsError = null;
    this.prepaidRedemptionsLoaded = false;
  }

  get prepaidRedemptionSlots(): PrepaidRedemptionSlot[] {
    return mapPrepaidPurchaseRedemptionsToSlots(this.prepaidRedemptions);
  }

  private loadPrepaidPurchaseStatus(prepaidPurchaseId: string): void {
    const id = prepaidPurchaseId?.trim();
    if (!id) {
      this.clearPrepaidRedemptionState();
      return;
    }
    this.isLoadingPrepaidPurchaseStatus = true;
    this.prepaidPurchaseStatusError = null;
    this.prepaidPurchaseStatus = null;
    this.clearPrepaidRedemptionsListState();
    this.orderRequestRedemptionSlotKeys = [];
    this.orderRequestSelectedPrepaidBookId = null;

    this.prepaidWashBookService
      .getPrepaidPurchaseStatus(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isLoadingPrepaidPurchaseStatus = false;
          if (response.status && response.data) {
            this.prepaidPurchaseStatus = response.data;
            this.prepaidPurchaseId = response.data.prepaid_purchase_id || id;
            this.prepaidPurchaseStatusError = null;
            this.ensureOrderRequestPrepaidBooksLoadedForRedemption();
            this.syncOrderRequestDiscountStateForFleetAndPrepaid();
            this.clearOrderRequestWashPackageSelection();
            if (!this.isPrepaidPurchaseConsumedForUi()) {
              const bookId = this.prepaidPurchaseStatus?.prepaid_wash_book_id;
              if (bookId && this.orderRequestPrepaidBooks.length > 0) {
                const matched = this.orderRequestPrepaidBooks.filter(book => book.id === bookId);
                this.orderRequestPrepaidBooks =
                  matched.length > 0
                    ? matched
                    : this.buildRedeemContextPrepaidBooksFallback(this.orderRequestPrepaidBooks);
              } else if (bookId && this.orderRequestPrepaidBooks.length === 0) {
                this.orderRequestPrepaidBooks = this.buildRedeemContextPrepaidBooksFallback([]);
              }
            }
          } else {
            this.prepaidPurchaseStatus = null;
            this.prepaidPurchaseStatusError =
              response.message || 'Could not load prepaid purchase status.';
          }
          this.refreshOrderRequestAsyncUi();
        },
        error: err => {
          this.isLoadingPrepaidPurchaseStatus = false;
          this.prepaidPurchaseStatus = null;
          this.prepaidPurchaseStatusError =
            err?.error?.message || err?.message || 'Could not load prepaid purchase status.';
          this.refreshOrderRequestAsyncUi();
        },
      });
  }

  private resolvePrepaidPurchaseFromCustomer(customerData: any, prepaidId: string | null): void {
    if (!prepaidId) {
      this.clearPrepaidRedemptionState();
      return;
    }
    if (
      this.prepaidPurchaseId === prepaidId &&
      (this.prepaidPurchaseStatus || this.isLoadingPrepaidPurchaseStatus)
    ) {
      this.ensureOrderRequestPrepaidBooksLoadedForRedemption();
      return;
    }
    this.prepaidPurchaseId = prepaidId;
    this.loadPrepaidPurchaseStatus(prepaidId);
  }

  /**
   * Identity key for a prepaid QR/LPR redeem notification, used to decide whether bootstrap
   * enrichment needs to (re-)run. Keyed on content rather than the modal's isOpen transition so
   * a QR notification force-shown while isOpen is already true (queue replay, rescan) still
   * bootstraps correctly instead of being silently skipped.
   */
  private getPrepaidRedeemBootstrapKey(
    notification: NotificationData | null | undefined
  ): string | null {
    const data = notification?.data as Record<string, any> | undefined;
    const purchaseId = String(data?.['prepaid_purchase_id'] || '').trim();
    if (!purchaseId) {
      return null;
    }
    const mode =
      data?.['qr_scan'] === true ? 'qr' : data?.['lpr_prepaid_redeem'] === true ? 'lpr' : null;
    if (!mode) {
      return null;
    }
    return `${mode}:${purchaseId}:${String(data?.['customer_id'] || '').trim()}`;
  }

  private isPrepaidQrScanBootstrapNotification(
    notification: NotificationData | null | undefined
  ): boolean {
    const data = notification?.data as Record<string, any> | undefined;
    return (
      notification?.type === 'create-order-request' &&
      data?.['qr_scan'] === true &&
      !!String(data?.['prepaid_purchase_id'] || '').trim()
    );
  }

  /** Header scan QR → open redeem UI with purchaser loaded from purchase status. */
  private bootstrapPrepaidRedeemFromQrScan(notification: NotificationData): void {
    const data = notification.data as Record<string, any>;
    const purchaseId = String(data['prepaid_purchase_id'] || '').trim();
    if (!purchaseId) {
      return;
    }
    const bootstrapKey = this.prepaidRedeemBootstrapKey;
    const purchaseVehicleId = String(data['purchase_vehicle_id'] || '').trim();

    this.resolvePrepaidPurchaseFromCustomer({ prepaid_purchase_id: purchaseId }, purchaseId);

    const customerId = String(
      data['customer_id'] || this.prepaidPurchaseStatus?.customer_id || ''
    ).trim();
    if (!customerId) {
      return;
    }

    this.customerService
      .getCustomer(customerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (this.prepaidRedeemBootstrapKey !== bootstrapKey) {
            return; // a newer scan/notification has superseded this one
          }
          const customer = response?.data ?? response;
          const resolvedCustomerId = String(customer?.id || customer?.customer_id || '').trim();
          if (!resolvedCustomerId) {
            return;
          }
          this.selectCustomerFromSearch({
            ...customer,
            id: resolvedCustomerId,
            prepaid_purchase_id: purchaseId,
            purchase_vehicle_id: purchaseVehicleId || undefined,
          });
          this.cdr.detectChanges();
        },
        error: () => {
          // Redeem UI still works from purchase status when customer fetch fails.
        },
      });
  }

  private isPrepaidLprRedeemBootstrapNotification(
    notification: NotificationData | null | undefined
  ): boolean {
    const data = notification?.data as Record<string, any> | undefined;
    return (
      notification?.type === 'create-order-request' &&
      data?.['lpr_prepaid_redeem'] === true &&
      !!String(data?.['prepaid_purchase_id'] || '').trim()
    );
  }

  /** LPR get-lp-image redeem-prepaid-purchase → redeem UI; booking fields already in form. */
  private bootstrapPrepaidRedeemFromLprScan(notification: NotificationData): void {
    const data = notification.data as Record<string, any>;
    const purchaseId = String(data['prepaid_purchase_id'] || '').trim();
    if (!purchaseId) {
      return;
    }

    this.resolvePrepaidPurchaseFromCustomer({ prepaid_purchase_id: purchaseId }, purchaseId);
    // Load wash-book tiles in parallel with purchase status so live LPR is not stuck on
    // catalog shimmer after status returns (notification-list clicks already force CD).
    this.ensureOrderRequestPrepaidBooksLoadedForRedemption();
    this.applyDefaultPrepaidRedemptionRecipientFromPurchaser();
    this.refreshOrderRequestAsyncUi();
  }

  private applyLicensePlateCustomerMatchForOrderRequest(customerData: any): void {
    this.licensePlateSearchResult = customerData;
    const prepaidId = this.normalizePrepaidPurchaseId(customerData?.prepaid_purchase_id);
    this.resolvePrepaidPurchaseFromCustomer(customerData, prepaidId);

    if (prepaidId && !this.licensePlateInputFocused) {
      this.showLicensePlateDropdown = false;
      this.showVehicleFieldsForLicensePlate = false;
      this.selectCustomerFromLicensePlateSearch(customerData);
      return;
    }

    if (!this.licensePlateInputFocused) {
      return;
    }

    this.showLicensePlateDropdown = true;
    this.showVehicleFieldsForLicensePlate = false;
  }

  isPrepaidPurchaseConsumedForUi(): boolean {
    if (!this.prepaidPurchaseStatus) {
      return false;
    }
    if (getPrepaidPurchaseStatusBadge(this.prepaidPurchaseStatus) === 'expired') {
      return true;
    }
    return isPrepaidPurchaseFullyRedeemed(this.prepaidPurchaseStatus);
  }

  get orderRequestPrepaidFlow(): 'purchase' | 'redeem' {
    return this.prepaidPurchaseId && this.prepaidPurchaseStatus ? 'redeem' : 'purchase';
  }

  isOrderRequestPrepaidRedeemFlow(): boolean {
    return this.orderRequestPrepaidFlow === 'redeem';
  }

  /** True when redeem UI is active (purchase status loaded or purchase id known). */
  /**
   * The order is actually a redemption: a purchase is in play and nothing has taken its place.
   * `isInPrepaidRedemptionCheckoutMode()` only says a purchase exists, which is true from the
   * moment a plate lookup finds one, whatever the attendant goes on to choose.
   */
  private isOrderRequestRedeemingPrepaid(): boolean {
    return (
      this.isInPrepaidRedemptionCheckoutMode() &&
      !this.isPrepaidPurchaseConsumedForUi() &&
      !this.selectedPackage &&
      !this.orderRequestSelectedPrepaidBookId
    );
  }

  isInPrepaidRedemptionCheckoutMode(): boolean {
    return (
      this.isOrderRequestPrepaidRedeemFlow() ||
      (!!(this.prepaidPurchaseId || '').trim() && !!this.prepaidPurchaseStatus)
    );
  }

  hasOrderRequestRedemptionSelection(): boolean {
    return this.orderRequestRedemptionSlotKeys.length > 0;
  }

  onOrderRequestRedemptionSelectionChange(slotKeys: string[]): void {
    const hadSelection = this.orderRequestRedemptionSlotKeys.length > 0;
    const hasSelection = slotKeys.length > 0;
    this.orderRequestRedemptionSlotKeys = [...slotKeys];
    this.orderRequestSelectedPrepaidBookId = null;
    this.clearOrderRequestWashPackageSelection();
    this.syncOrderRequestDiscountStateForFleetAndPrepaid();
    this.rebuildMostUsedServiceItems();

    if (!hasSelection) {
      this.resetPrepaidRedemptionRecipient();
    } else if (!hadSelection && !this.prepaidRedemptionRecipientTouched) {
      this.applyDefaultPrepaidRedemptionRecipientFromPurchaser();
    }

    this.cdr.detectChanges();
  }

  private buildPrepaidRedemptionOrderPayload(
    redeemerCustomerId: string,
    redeemerVehicleId: string
  ): CreateOrderPayload | null {
    const status = this.prepaidPurchaseStatus;
    const purchaseId = (this.prepaidPurchaseId || status?.prepaid_purchase_id || '').trim();
    const items = buildPrepaidOrderRedemptionItemsFromSlotKeys(
      status,
      this.orderRequestRedemptionSlotKeys
    );
    if (!purchaseId || items.length === 0) {
      return null;
    }
    const resolvedLocationId = this.resolveLocationIdForSubmission();
    const lprEntryId = (
      this.formData.notificationId ||
      this.resolveLprEntryNotificationId() ||
      ''
    ).trim();
    if (this.isDirectOrder() && !resolvedLocationId) {
      return null;
    }
    return {
      // CWS-1914: an order cannot exist without a customer. A redeemer who gave no phone has
      // no record of their own (CWS-1881 keeps that deliberate), so the sale's customer backs
      // the order and who actually came in is recorded on the redemption row.
      customer_id: redeemerCustomerId || status?.customer_id || '',
      location_id: resolvedLocationId,
      ...buildCreateOrderVehicleIdField(redeemerVehicleId),
      wash_type: 'prepaid_wash_redemption',
      prepaid_purchase_id: purchaseId,
      redemption_items: items,
      appointment_time: this.getCurrentAppointmentTime(),
      source: 'web',
      voucher_code: '',
      order_source: this.getOrderSourceForCreatePayload(),
      final_price: '0',
      ...(lprEntryId ? { lpr_entry_id: lprEntryId } : {}),
      ...this.buildCreateOrderLaneIdField(),
      // CWS-1881: who came in, for the purchase's redemption log.
      ...buildPrepaidRedeemerPayloadFields(this.prepaidRedemptionRecipient, redeemerCustomerId),
    };
  }

  private submitPrepaidRedemptionOrder(): void {
    // CWS-1914: no pre-emptive LPR stamp on a redemption. It hardcoded
    // `event: 'create-order-request'` onto an entry that is a redemption, and wrote the form's
    // customer and vehicle — the ones from before the redeemer is resolved, so a walk-in's
    // visit was recorded against whoever the form happened to be holding. The entry is
    // stamped properly once the redemption succeeds, by updateLprEntryAfterPrepaidRedemption.
    if (!this.hasOrderRequestCheckoutLineItems()) {
      this.isUpdatingOrder = false;
      return;
    }
    const checkoutErr = this.getOrderRequestCheckoutValidationError();
    if (checkoutErr) {
      this.rejectOrderRequestCheckout(checkoutErr);
      return;
    }

    this.resolvePrepaidRedemptionRecipientIds()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ customerId, vehicleId }) => {
          const orderPayload = this.buildPrepaidRedemptionOrderPayload(customerId, vehicleId);
          if (!orderPayload) {
            this.updateOrderError = sanitizeErrorMessage('Select at least one wash to redeem.');
            this.isUpdatingOrder = false;
            return;
          }

          this.formData.locationId = orderPayload.location_id;
          this.orderCreateStartTime = Date.now();

          const validation = this.orderCreationService.validateOrderPayload(orderPayload, {
            requireVehicle: true,
          });
          if (!validation.isValid) {
            this.updateOrderError = sanitizeErrorMessage(validation.errors.join(', '));
            this.isUpdatingOrder = false;
            return;
          }

          this.orderCreationService
            .createOrder(orderPayload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: response => this.handlePrepaidRedemptionOrderSuccess(response, orderPayload),
              error: err => this.handlePrepaidRedemptionOrderError(err),
            });
        },
        error: (err: any) => {
          this.isUpdatingOrder = false;
          const message =
            err instanceof Error
              ? err.message
              : (err as { error?: { message?: string }; message?: string })?.error?.message ||
                (err as { message?: string })?.message ||
                'Failed to resolve redeemer customer or vehicle.';
          this.updateOrderError = sanitizeErrorMessage(message);
          this.cdr.detectChanges();
        },
      });
  }

  /** Prepaid redemption is already paid — sync scan LPR row to order-success (not create-payment-request). */
  private updateLprEntryAfterPrepaidRedemption(
    orderId: string,
    response: CreateOrderResponse
  ): void {
    if (this.isDirectOrder()) {
      return;
    }
    const notificationId = this.resolveLprEntryNotificationId();
    if (!notificationId) {
      return;
    }

    const order = (response.data?.order ?? null) as Record<string, any> | null;
    const updatePayload = this.buildLprEntryUpdatePayloadAfterPayment(
      orderId,
      'single_wash',
      order
    );
    updatePayload['message'] = 'Prepaid wash redeemed. Please proceed to the wash bay.';

    this.apiService
      .put(`order/lpr-entries/${notificationId}`, updatePayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: putResponse => {
          this.notificationService.syncLprEntryFromPutResponse(putResponse);
        },
        error: error => {
          console.error('Error updating LPR entry after prepaid redemption:', error);
        },
      });
  }

  private handlePrepaidRedemptionOrderSuccess(
    response: CreateOrderResponse,
    orderPayload: CreateOrderPayload
  ): void {
    this.isUpdatingOrder = false;
    this.updateOrderError = '';

    // CWS-1882: a redemption no longer creates an order. `data.order` is the purchase order
    // and `order.prepaid_redemption` describes the visit that was recorded against it.
    const prepaidRedemption = (response.data?.order?.prepaid_redemption ??
      null) as PrepaidRedemptionVisitLike | null;
    const requestedCount =
      orderPayload.redemption_items?.reduce((sum, line) => sum + (line.count ?? 0), 0) ?? 0;
    const orderId = resolvePrepaidRedemptionOrderId(
      prepaidRedemption,
      response.data?.order?.id ?? ''
    );
    const redemptionResult = describePrepaidRedemptionResult(prepaidRedemption, requestedCount);
    const redemptionCount = redemptionResult.redeemedCount || requestedCount;
    this.logger.trackOrder('Prepaid redemption recorded', {
      orderId,
      redemptionCount,
      reusedVisit: redemptionResult.reused,
      visitId: prepaidRedemption?.['visit_id'] ?? null,
      elapsedMs: Date.now() - this.orderCreateStartTime,
    });

    const redeemedSlotKeysSnapshot = [...this.orderRequestRedemptionSlotKeys];
    this.prepaidRedeemedSuccessData = buildPrepaidRedeemedSuccessDisplay(
      this.prepaidPurchaseStatus,
      redeemedSlotKeysSnapshot
    );
    this.prepaidRedeemedResult = redemptionResult;

    this.orderRequestRedemptionSlotKeys = [];
    this.prepaidRedemptionsLoaded = false;
    const purchaseId = (this.prepaidPurchaseId || orderPayload.prepaid_purchase_id || '').trim();
    if (purchaseId) {
      this.loadPrepaidPurchaseStatus(purchaseId);
    }

    // The purchase order id, not a redemption order — redemptions have no order of their own.
    if (this.notification?.data && orderId) {
      this.notification.data.order_id = orderId;
    }

    if (prepaidRedemption || orderId) {
      // A reused visit drew no wash and the backend already stamped the LPR entry on the
      // original visit, so re-stamping it here would overwrite that entry for nothing.
      if (orderId && shouldUpdateLprEntryAfterPrepaidRedemption(prepaidRedemption)) {
        this.updateLprEntryAfterPrepaidRedemption(orderId, response);
      }
      this.showPrepaidRedeemedSuccess = true;
      this.onClose();
      this.logger.trackNotification('Showing prepaid redeemed success modal', {
        orderId,
        redemptionCount,
        reusedVisit: redemptionResult.reused,
      });
      this.cdr.detectChanges();
      return;
    }

    this.messageService.add({
      severity: 'success',
      summary: redemptionResult.title,
      detail: redemptionResult.message,
    });
    this.onClose();
  }

  onPrepaidRedeemedSuccessModalClose(): void {
    this.logModalInteraction('modal.prepaidRedeemedSuccess.close');
    this.showPrepaidRedeemedSuccess = false;
    const currentRoute = this.router.url.split('?')[0];
    if (currentRoute.includes('/orders')) {
      this.ordersService.refreshOrders();
    }
  }

  onPrepaidRedeemedSuccessModalAction(): void {
    this.logModalInteraction('modal.prepaidRedeemedSuccess.action');
    this.onPrepaidRedeemedSuccessModalClose();
  }

  private handlePrepaidRedemptionOrderError(error: any): void {
    this.isUpdatingOrder = false;
    const err = error as { error?: { message?: string }; message?: string };
    this.updateOrderError = sanitizeErrorMessage(
      err?.error?.message || err?.message || 'Failed to redeem prepaid washes.'
    );
    this.logger.trackError('NotificationModal.prepaidRedemption', error, {
      redemptionCount: this.orderRequestRedemptionSlotKeys.length,
    });
  }

  retryLoadPrepaidPurchaseStatus(): void {
    const id = this.prepaidPurchaseId?.trim();
    if (id) {
      this.loadPrepaidPurchaseStatus(id);
    }
  }

  onPrepaidRedemptionTabOpened(): void {
    if (this.prepaidRedemptionsLoaded || this.isLoadingPrepaidRedemptions) {
      return;
    }
    const id = this.prepaidPurchaseId?.trim();
    if (id) {
      this.loadPrepaidPurchaseRedemptions(id);
    }
  }

  retryLoadPrepaidRedemptions(): void {
    const id = this.prepaidPurchaseId?.trim();
    if (!id) {
      return;
    }
    this.prepaidRedemptionsLoaded = false;
    this.loadPrepaidPurchaseRedemptions(id);
  }

  private loadPrepaidPurchaseRedemptions(prepaidPurchaseId: string): void {
    const id = prepaidPurchaseId?.trim();
    if (!id) {
      this.clearPrepaidRedemptionsListState();
      return;
    }
    this.isLoadingPrepaidRedemptions = true;
    this.prepaidRedemptionsError = null;

    this.prepaidWashBookService
      .getPrepaidPurchaseRedemptions(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.isLoadingPrepaidRedemptions = false;
          this.prepaidRedemptions = result.redemptions;
          this.prepaidRedemptionsLoaded = true;
          this.prepaidRedemptionsError = null;
          this.cdr.detectChanges();
        },
        error: err => {
          this.isLoadingPrepaidRedemptions = false;
          this.prepaidRedemptions = [];
          this.prepaidRedemptionsLoaded = false;
          this.prepaidRedemptionsError =
            err?.error?.message || err?.message || 'Could not load redeemed washes.';
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Fetch vehicle data by license plate when no customer is found.
   * Uses {@link splitLicensePlateForApi}: explicit US state prefix (e.g. CA8AUG441) or default state + full plate.
   */
  /**
   * `notifyOnMiss` is only set by the plate box the operator types into. The automatic
   * lookups that follow an LPR read share this method, and a lane can produce a dozen
   * unreadable plates an hour — toasting each one would be noise, not feedback (CWS-762).
   */
  private fetchVehicleDataByLicensePlate(
    licensePlate: string,
    options?: { notifyOnMiss?: boolean; onLookupFinished?: () => void }
  ): void {
    const onLookupFinished = options?.onLookupFinished;
    const notifyOnMiss = options?.notifyOnMiss === true;
    if (!licensePlate || licensePlate.trim().length < 2) {
      this.clearLprPlateCorrectionAfterLicensePlateLookup();
      onLookupFinished?.();
      return;
    }

    const parsed = splitLicensePlateForApi(licensePlate, this.notification?.data?.state);
    if (!parsed.license_plate) {
      this.clearLprPlateCorrectionAfterLicensePlateLookup();
      onLookupFinished?.();
      return;
    }

    const lookupPlate = licensePlate.trim();
    const plateKey = this.normalizePlateForVehicleLookupCache(lookupPlate);

    if (this.hasCachedVehicleLookupForPlate(lookupPlate)) {
      this.clearLprPlateCorrectionAfterLicensePlateLookup();
      this.refreshOrderRequestHistoryAfterVehicleResolved(true);
      onLookupFinished?.();
      return;
    }
    if (this.wasVehiclePlateLookupAttempted(lookupPlate)) {
      this.clearLprPlateCorrectionAfterLicensePlateLookup();
      this.refreshOrderRequestHistoryAfterVehicleResolved(true);
      // Already tried and missed. Without this the operator retries the same plate and gets
      // silence, which is what made the behaviour look arbitrary in the first place.
      this.notifyVehicleLookupMiss(notifyOnMiss);
      onLookupFinished?.();
      return;
    }
    if (this.plateLookupInFlightPlate === plateKey) {
      onLookupFinished?.();
      return;
    }

    const payload = {
      license_plate: parsed.license_plate,
      state: parsed.state,
    };

    this.plateLookupInFlightPlate = plateKey;
    this.markVehiclePlateLookupAttempted(lookupPlate);

    this.vehicleService
      .getVehicleData(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (this.plateLookupInFlightPlate === plateKey) {
            this.plateLookupInFlightPlate = null;
          }
          this.clearLprPlateCorrectionAfterLicensePlateLookup();
          onLookupFinished?.();
        })
      )
      .subscribe({
        next: response => {
          const currentPlate = this.formData.vehicleLicensePlate?.trim() ?? '';
          if (currentPlate !== lookupPlate) {
            return;
          }

          if (response.status && response.data) {
            const vehicleData = response.data;
            this.vehicleDataFetched = true;
            const vehicleId = String(vehicleData.id || vehicleData.vehicle_id || '').trim();
            this.fetchedVehicleId = vehicleId;
            this.lastVehicleLookupPlate = plateKey;
            if (vehicleId) {
              this.formData.vehicleId = vehicleId;
              this.userInteractedFields.add('vehicleId');
              this.syncLinkedVehicleSelection(vehicleId, lookupPlate);
            }
            if (vehicleData.vin) {
              this.formData.vehicleVin = vehicleData.vin;
              this.userInteractedFields.add('vehicleVin');
            }
            if (vehicleData.make) {
              this.formData.vehicleMake = vehicleData.make;
              this.userInteractedFields.add('vehicleMake');
            }
            if (vehicleData.model) {
              this.formData.vehicleModel = vehicleData.model;
              this.userInteractedFields.add('vehicleModel');
            }
            if (vehicleData.year) {
              this.formData.vehicleYear = vehicleData.year.toString();
              this.userInteractedFields.add('vehicleYear');
            }
            this.refreshOrderRequestHistoryAfterVehicleResolved(true);
          } else {
            this.clearVehicleLookupSuccessCache();
            this.notifyVehicleLookupMiss(notifyOnMiss, response?.message);
          }
        },
        error: error => {
          console.error('Error fetching vehicle data by license plate:', error);
          const currentPlate = this.formData.vehicleLicensePlate?.trim() ?? '';
          if (currentPlate === lookupPlate) {
            this.clearVehicleLookupSuccessCache();
            if (notifyOnMiss) {
              this.messageService.add({
                severity: 'error',
                summary: 'Vehicle lookup failed',
                detail: sanitizeErrorMessage(
                  error?.error?.message ||
                    error?.message ||
                    'Could not fetch vehicle details. Please try again.'
                ),
              });
            }
          }
        },
      });
  }

  /** Tell the operator why the plate produced nothing, preferring the reason the API gave. */
  private notifyVehicleLookupMiss(notifyOnMiss: boolean, apiMessage?: string): void {
    if (!notifyOnMiss) {
      return;
    }
    this.messageService.add({
      severity: 'warn',
      summary: 'Vehicle not found',
      detail: String(apiMessage ?? '').trim() || NO_VEHICLE_DETAILS_MESSAGE,
    });
  }

  /** Clears stale vehicle data when the user starts a new plate search. */
  private resetVehicleFieldsForPlateLookup(): void {
    this.formData.vehicleId = '';
    this.linkedVehicleAtSelection = null;
    this.formData.vehicleMake = '';
    this.formData.vehicleModel = '';
    this.formData.vehicleYear = '';
    this.formData.vehicleVin = '';
    this.clearVehicleLookupSuccessCache();
    this.lastVehiclePlateLookupAttempted = '';
    this.plateLookupInFlightPlate = null;
    this.licensePlateCustomerSearchCompletedKey = '';
    this.linkedCustomerSnapshot = null;
    this.clearOrderRequestHistoryForPlateChange();
  }

  /** Clears successful lookup cache only (keeps {@link lastVehiclePlateLookupAttempted}). */
  private clearVehicleLookupSuccessCache(): void {
    this.vehicleDataFetched = false;
    this.fetchedVehicleId = '';
    this.lastVehicleLookupPlate = '';
  }

  private normalizePlateForVehicleLookupCache(licensePlate: string): string {
    return (licensePlate ?? '').trim().toUpperCase();
  }

  private markVehiclePlateLookupAttempted(licensePlate: string): void {
    const plate = this.normalizePlateForVehicleLookupCache(licensePlate);
    if (plate) {
      this.lastVehiclePlateLookupAttempted = plate;
    }
  }

  private wasVehiclePlateLookupAttempted(licensePlate: string): boolean {
    const plate = this.normalizePlateForVehicleLookupCache(licensePlate);
    return !!plate && this.lastVehiclePlateLookupAttempted === plate;
  }

  /** Keep plate lookup cache when populateFormData reloads the same plate; clear only when plate changes. */
  private syncVehiclePlateLookupCacheForFormPlate(licensePlate: string): void {
    const plateKey = this.normalizePlateForVehicleLookupCache(licensePlate);
    if (!plateKey) {
      this.clearVehicleLookupSuccessCache();
      this.lastVehiclePlateLookupAttempted = '';
      this.plateLookupInFlightPlate = null;
      return;
    }
    if (this.lastVehiclePlateLookupAttempted && this.lastVehiclePlateLookupAttempted !== plateKey) {
      this.clearVehicleLookupSuccessCache();
      this.lastVehiclePlateLookupAttempted = '';
      this.plateLookupInFlightPlate = null;
    }
  }

  /** True when license-plate search already called getVehicleData for this plate in this modal session. */
  private hasCachedVehicleLookupForPlate(licensePlate: string): boolean {
    const plate = this.normalizePlateForVehicleLookupCache(licensePlate);
    return !!(
      plate &&
      this.vehicleDataFetched &&
      (this.fetchedVehicleId || '').trim() &&
      this.lastVehicleLookupPlate === plate
    );
  }

  /**
   * Reuse plate lookup from search — link vehicle to customer via PUT, then create order (no second getVehicleData).
   */
  private linkCachedVehicleLookupToCustomerThenCreateOrder(
    licensePlate: string,
    customerId: string
  ): boolean {
    if (!this.hasCachedVehicleLookupForPlate(licensePlate)) {
      return false;
    }

    const fetchedId = (this.fetchedVehicleId || '').trim();
    const parsed = splitLicensePlateForApi(licensePlate, this.notification?.data?.state);
    const licensePlateWithoutState = parsed.license_plate;
    if (!licensePlateWithoutState || !parsed.state) {
      return false;
    }

    let yearNum = parseInt(String(this.formData.vehicleYear || ''), 10);
    if (!Number.isFinite(yearNum)) {
      yearNum = 0;
    }

    const updatePayload = {
      license_plate: licensePlateWithoutState,
      state: parsed.state,
      vin: this.formData.vehicleVin || '',
      make: this.formData.vehicleMake || '',
      model: this.formData.vehicleModel || '',
      year: yearNum,
      customer_id: customerId,
      is_family_plan: false,
    };

    console.log('🔗 Reusing cached vehicle lookup (skip getVehicleData):', fetchedId);
    this.vehicleService
      .updateVehicle(fetchedId, updatePayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: upd => {
          if (upd.status) {
            this.formData.vehicleId = fetchedId;
            this.createOrderAfterVehicleCreation();
          } else {
            this.continueVehicleLinkAfterPlateLookupMiss(licensePlate);
          }
        },
        error: () => this.continueVehicleLinkAfterPlateLookupMiss(licensePlate),
      });
    return true;
  }

  private continueVehicleLinkAfterPlateLookupMiss(licensePlate: string): void {
    if (this.wasVehiclePlateLookupAttempted(licensePlate)) {
      this.fetchVehiclesByCustomerIdForNewCustomerFlow(licensePlate);
      return;
    }
    this.tryLinkVehicleViaPlateLookupAfterCustomerCreationFetch(licensePlate);
  }

  /** Network fetch path when no cached plate lookup is available. */
  private tryLinkVehicleViaPlateLookupAfterCustomerCreationFetch(licensePlate: string): void {
    const customerId = this.formData.customerId;
    if (!customerId) {
      return;
    }

    if (this.wasVehiclePlateLookupAttempted(licensePlate)) {
      this.fetchVehiclesByCustomerIdForNewCustomerFlow(licensePlate);
      return;
    }

    const parsed = splitLicensePlateForApi(licensePlate, this.notification?.data?.state);
    const state = parsed.state;
    const licensePlateWithoutState = parsed.license_plate;

    if (!licensePlateWithoutState) {
      this.fetchVehiclesByCustomerIdForNewCustomerFlow(licensePlate);
      return;
    }

    console.log('🔄 Linking vehicle to new customer via get-vehicle-data + updateVehicle:', {
      state,
      licensePlateWithoutState,
    });

    this.markVehiclePlateLookupAttempted(licensePlate);
    this.vehicleService
      .getVehicleData({ license_plate: licensePlateWithoutState, state })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (!response.status || !response.data) {
            this.fetchVehiclesByCustomerIdForNewCustomerFlow(licensePlate);
            return;
          }
          const vid = (response.data.id || '').toString().trim();
          if (!vid) {
            this.fetchVehiclesByCustomerIdForNewCustomerFlow(licensePlate);
            return;
          }
          this.vehicleDataFetched = true;
          this.fetchedVehicleId = vid;
          this.lastVehicleLookupPlate = this.normalizePlateForVehicleLookupCache(licensePlate);
          const vd = response.data;
          let yearNum = typeof vd.year === 'string' ? parseInt(vd.year, 10) : vd.year || 0;
          if (!Number.isFinite(yearNum)) {
            yearNum = 0;
          }
          const updatePayload = {
            license_plate: licensePlateWithoutState,
            state,
            vin: vd.vin || this.formData.vehicleVin || '',
            make: vd.make || this.formData.vehicleMake || '',
            model: vd.model || this.formData.vehicleModel || '',
            year: yearNum,
            customer_id: customerId,
            is_family_plan: false,
          };
          this.vehicleService
            .updateVehicle(vid, updatePayload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: upd => {
                if (upd.status) {
                  this.formData.vehicleId = vid;
                  console.log(
                    '✅ Vehicle linked to new customer via updateVehicle after plate lookup:',
                    vid
                  );
                  this.createOrderAfterVehicleCreation();
                } else {
                  this.fetchVehiclesByCustomerIdForNewCustomerFlow(licensePlate);
                }
              },
              error: err => {
                console.warn(
                  '⚠️ updateVehicle after customer creation failed, falling back to vehicle list:',
                  err
                );
                this.fetchVehiclesByCustomerIdForNewCustomerFlow(licensePlate);
              },
            });
        },
        error: err => {
          console.warn(
            '⚠️ getVehicleData after customer creation failed, falling back to vehicle list:',
            err
          );
          this.fetchVehiclesByCustomerIdForNewCustomerFlow(licensePlate);
        },
      });
  }

  /**
   * Applies customer + vehicle from by-license-plate API (no suggestion dropdown).
   */
  private applyCustomerFromLicensePlateSearch(customerData: any): void {
    const customerId = this.resolveCustomerIdFromSearchResult(customerData);
    this.logModalInteraction('customer.apply.licensePlate', {
      customerId,
      vehicleId: customerData?.vehicle?.id,
      prepaidPurchaseId: customerData?.prepaid_purchase_id,
    });
    this.applyPrepaidFromCustomerSelection(customerData);

    if (this.hasOrderRequestRedemptionSelection() && !this.prepaidRedemptionRecipientTouched) {
      this.applyDefaultPrepaidRedemptionRecipientFromPurchaser();
    }

    // Populate form with customer data
    this.formData.customerFirstName = customerData.first_name || '';
    this.formData.customerLastName = customerData.last_name || '';
    this.formData.customerEmail = customerData.email || '';
    this.formData.customerContact = customerData.contact || '';
    this.formData.customerId = customerId;
    this.trackLookedUpCustomerStatus(customerId, customerData.customer_status);
    // Plate lookup can resolve a different plate context, so re-judge audience rules.
    this.refreshOrderRequestCustomerHistory();

    // Populate vehicle data only when API returns a vehicle row with id (otherwise resolve/create by plate on submit).
    if (customerData.vehicle) {
      this.formData.vehicleMake = customerData.vehicle.make || '';
      this.formData.vehicleModel = customerData.vehicle.model || '';
      this.formData.vehicleYear = customerData.vehicle.year
        ? customerData.vehicle.year.toString()
        : '';
      const vid = (customerData.vehicle.id || '').toString().trim();
      this.formData.vehicleId = vid;
      const linkedPlate = String(
        customerData.vehicle.license_plate ||
          customerData.vehicle.licensePlate ||
          this.formData.vehicleLicensePlate ||
          ''
      ).trim();
      if (vid && linkedPlate) {
        if (this.canApplyExternalLicensePlateToForm()) {
          this.setCombinedLicensePlateOnForm(
            linkedPlate,
            typeof customerData.vehicle.state === 'string' ? customerData.vehicle.state : null
          );
        }
        this.syncLinkedVehicleSelection(vid, this.formData.vehicleLicensePlate);
      } else if (vid && (this.formData.vehicleLicensePlate || '').trim()) {
        this.syncLinkedVehicleSelection(vid, this.formData.vehicleLicensePlate);
      }
    } else {
      this.formData.vehicleId = '';
      this.linkedVehicleAtSelection = null;
    }

    // Mark fields as user-interacted
    this.userInteractedFields.add('customerFirstName');
    this.userInteractedFields.add('customerLastName');
    this.userInteractedFields.add('customerEmail');
    this.userInteractedFields.add('customerContact');
    this.userInteractedFields.add('customerId');
    if (customerData.vehicle) {
      this.userInteractedFields.add('vehicleMake');
      this.userInteractedFields.add('vehicleModel');
      this.userInteractedFields.add('vehicleYear');
    }

    // Clear and re-validate phone number
    this.phoneValidationError = '';
    if (this.formData.customerContact) {
      this.onPhoneInputChange(this.formData.customerContact);
    }

    // Clear and re-validate email
    this.emailValidationError = '';
    if (this.formData.customerEmail) {
      this.onEmailInputChange(this.formData.customerEmail);
    }

    this.tryAutoSelectPackageWhenExistingCustomerLinked();

    this.linkedCustomerSnapshot = {
      customerId,
      firstName: (this.formData.customerFirstName || '').trim(),
      lastName: (this.formData.customerLastName || '').trim(),
      email: (this.formData.customerEmail || '').trim(),
      contact: (this.formData.customerContact || '').trim(),
    };

    this.showLicensePlateDropdown = false;
    this.licensePlateSearchResult = null;
    const hasVehicleId = !!(this.formData.vehicleId || '').trim();
    this.showVehicleFieldsForLicensePlate =
      !hasVehicleId && !!(this.orderRequestPlateNumber || '').trim();
    const plate = this.formData.vehicleLicensePlate?.trim();
    if (
      !hasVehicleId &&
      plate &&
      !this.hasCachedVehicleLookupForPlate(plate) &&
      !this.wasVehiclePlateLookupAttempted(plate)
    ) {
      this.fetchVehicleDataByLicensePlate(plate);
    } else {
      this.refreshOrderRequestHistoryAfterVehicleResolved(true);
    }
    this.scheduleLprEntryManualPlateEdit();

    if (this.isInPrepaidRedemptionCheckoutMode()) {
      this.capturePrepaidRedemptionPrefilledSnapshot();
    }
  }

  /** @deprecated Use auto-apply via plate search; kept for any legacy template bindings. */
  selectCustomerFromLicensePlateSearch(customerData: any): void {
    this.applyCustomerFromLicensePlateSearch(customerData);
  }

  /**
   * When plate lookup prefilled customer fields, PUT customer only on Save Changes (not per keystroke).
   */
  private persistLinkedCustomerDetailsIfChangedThen(next: () => void): void {
    if (!this.isOrderRequestStyleNotification() || this.isUpdatingLinkedCustomer) {
      next();
      return;
    }
    const customerId = (this.formData.customerId || '').trim();
    const snap = this.linkedCustomerSnapshot;
    if (!customerId || !snap || snap.customerId !== customerId) {
      next();
      return;
    }

    const first = (this.formData.customerFirstName || '').trim();
    const last = (this.formData.customerLastName || '').trim();
    const email = (this.formData.customerEmail || '').trim();
    const contact = (this.formData.customerContact || '').trim();

    if (
      first === snap.firstName &&
      last === snap.lastName &&
      email === snap.email &&
      contact === snap.contact
    ) {
      next();
      return;
    }

    const payload = {
      first_name: this.formData.customerFirstName,
      last_name: this.formData.customerLastName || null,
      email: this.formData.customerEmail,
      contact: this.formData.customerContact,
      type: 'regular',
      license_plate: this.formData.vehicleLicensePlate || '',
    };

    this.isUpdatingLinkedCustomer = true;
    this.customerService
      .updateCustomer(customerId, payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isUpdatingLinkedCustomer = false;
        })
      )
      .subscribe({
        next: () => {
          this.linkedCustomerSnapshot = {
            customerId,
            firstName: first,
            lastName: last,
            email,
            contact,
          };
          next();
        },
        error: error => {
          console.error('Error updating customer on Save Changes:', error);
          this.isUpdatingOrder = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: sanitizeErrorMessage(
              error?.error?.message || 'Failed to update customer details. Please try again.'
            ),
          });
        },
      });
  }

  /** Clears customer fields that were auto-filled from a prior plate lookup when plate has no customer. */
  private clearCustomerFieldsFromPlateLookupIfLinked(): void {
    if (!this.linkedCustomerSnapshot) {
      return;
    }
    this.linkedCustomerSnapshot = null;
    this.clearOrderRequestHistoryForPlateChange();
    this.formData.customerId = '';
    this.formData.customerFirstName = '';
    this.formData.customerLastName = '';
    this.formData.customerEmail = '';
    this.formData.customerContact = '';
    this.userInteractedFields.delete('customerId');
    this.userInteractedFields.delete('customerFirstName');
    this.userInteractedFields.delete('customerLastName');
    this.userInteractedFields.delete('customerEmail');
    this.userInteractedFields.delete('customerContact');
  }

  /**
   * Hide license plate dropdown when clicking outside
   */
  hideLicensePlateDropdown(): void {
    this.logModalInteraction('dropdown.licensePlate.hide');
    setTimeout(() => {
      this.licensePlateInputFocused = false;
      this.showLicensePlateDropdown = false;
    }, 200);
  }

  /**
   * Location-scoped channels emit payment events for every order at that location.
   * Only react when the payload order id matches the order this modal is handling.
   */
  private shouldProcessPusherPaymentEvent(data: any, subscribedOrderId: string): boolean {
    const eventOrderId = String(data?.order_id ?? data?.orderId ?? '').trim();
    if (!eventOrderId) {
      return true;
    }
    return tapToPayOrderIdsMatch(eventOrderId, [
      subscribedOrderId,
      this.formData.orderId,
      this.notification?.data?.order_id,
      this.notification?.data?.orderId,
      this.notification?.data?.['public_order_id'],
    ]);
  }

  /** Shared dedup for Tap to Pay failure toasts within one payment attempt (Android + Pusher). */
  private beginTapToPayPaymentAttempt(): void {
    this.tapToPayFailureDedupAttempt += 1;
    this.tapToPayFailureToastKeys.clear();
  }

  private markTapToPayAwaitingNativeResult(orderId: string): void {
    const normalized = String(orderId ?? '')
      .replace(/^#+/, '')
      .trim();
    this.tapToPayAwaitingNativeResult = !!normalized;
    this.tapToPayAwaitingOrderId = normalized || null;
  }

  private clearTapToPayAwaitingNativeResult(): void {
    this.tapToPayAwaitingNativeResult = false;
    this.tapToPayAwaitingOrderId = null;
  }

  /** Only handle Android Tap to Pay callbacks launched from this modal (not order-details). */
  private isNotificationModalTapToPayResult(orderId: string): boolean {
    if (!this.tapToPayAwaitingNativeResult || !this.isOpen) {
      return false;
    }
    const normalized = String(orderId ?? '')
      .replace(/^#+/, '')
      .trim();
    if (!normalized) {
      return false;
    }
    return tapToPayOrderIdsMatch(normalized, [
      this.tapToPayAwaitingOrderId,
      this.formData.orderId,
      this.notification?.data?.order_id,
      this.notification?.data?.orderId,
    ]);
  }

  private claimPaymentFailureNotification(data: any, orderId: string): boolean {
    const paymentId =
      data?.payment_intent_id || data?.payment_intent || data?.order_id || data?.orderId || orderId;
    const baseKey = paymentId ? `${paymentId}-failed` : `${orderId || 'any'}-failed`;
    const paymentKey = `${this.tapToPayFailureDedupAttempt}:${baseKey}`;
    if (this.tapToPayFailureToastKeys.has(paymentKey)) {
      return false;
    }
    this.tapToPayFailureToastKeys.add(paymentKey);
    return true;
  }

  /**
   * Subscribe for payment realtime for this order via multiplexed SSE
   * (`GET /events/stream`); filter by `order_id` in handlers.
   */
  private subscribeToOrderChannel(orderId: string): void {
    this.logModalInteraction('payment.realtime.subscribe.start', { orderId });
    this.unsubscribeFromOrderChannel();

    this.paymentSseSub = bindPaymentSseEvents(this.realtimeEvents, this.ngZone, {
      onSuccess: (eventName, data) => {
        try {
          if (eventName === 'payment_done') {
            if (!this.shouldProcessPusherPaymentEvent(data, orderId)) {
              console.log('⚠️ payment_done ignored: order_id does not match current order');
              return;
            }
            this.handlePaymentDone(orderId, data);
            return;
          }
          this.handlePaymentSuccess(eventName, orderId, data);
        } catch (err) {
          console.error('[SSE] payment handler error:', err);
        }
      },
      onFailed: (eventName, data) => {
        try {
          this.handlePaymentFailed(eventName, orderId, data);
        } catch (err) {
          console.error('[SSE] payment_failed handler error:', err);
        }
      },
      skipNamedInCatchAll: true,
    });
    console.log('📡 SSE payment events subscribed for order:', orderId);
  }

  /** Unsubscribe from payment SSE for this modal session. */
  private unsubscribeFromOrderChannel(): void {
    this.paymentSseSub?.unsubscribe();
    this.paymentSseSub = null;
  }

  /**
   * Handle payment success event with deduplication
   */
  private handlePaymentSuccess(eventName: string, orderId: string, data: any): void {
    const status = data?.status || 'any';

    if (status === 'succeeded') {
      if (!this.shouldProcessPusherPaymentEvent(data, orderId)) {
        console.log('⚠️ Payment success ignored: order_id does not match current order');
        return;
      }
      const paymentId =
        data?.payment_intent_id ||
        data?.payment_intent ||
        data?.order_id ||
        data?.orderId ||
        orderId;
      const paymentKey = paymentId ? `${paymentId}-${status}` : `${eventName}-${Date.now()}`;

      if (this.processedPayments.has(paymentKey)) {
        console.log('⚠️ Duplicate payment event detected, skipping:', paymentKey);
        return;
      }

      this.processedPayments.add(paymentKey);
      setTimeout(() => this.processedPayments.delete(paymentKey), 5 * 60 * 1000);

      // Call the existing handlePaymentDone for backward compatibility
      this.handlePaymentDone(orderId, data);
    }
  }

  /**
   * Handle payment failure event with deduplication
   */
  private handlePaymentFailed(eventName: string, orderId: string, data: any): void {
    if (!this.shouldProcessPusherPaymentEvent(data, orderId)) {
      return;
    }
    const status = data?.status || 'any';

    if (status === 'failed' && !this.claimPaymentFailureNotification(data, orderId)) {
      console.log('⚠️ Duplicate payment failure event detected, skipping toast');
      return;
    }

    if (status === 'failed') {
      if (this.paymentForm.method === PaymentMethodCode.Terminal) {
        this.isProcessingPayment = false;
        this.stopTerminalPaymentStatusPoll();
        this.cdr.markForCheck();
      }
      const errorMessage = resolveMembershipFailureMessage(
        data,
        data?.message || 'Payment could not be processed. Please try again.'
      );
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Failed',
        detail: errorMessage,
        life: 5000,
      });
      // This is the authoritative answer for a membership we were waiting on, so stop waiting.
      const wasAwaitingActivation = this.membershipActivationAwaitingWebhook;
      this.clearMembershipActivationWait();
      if (wasAwaitingActivation) {
        this.failMembershipActivation(errorMessage);
        return;
      }
      // A membership charge can fail/refund AFTER payment_done (subscription setup failed).
      // If an active/success membership UI was optimistically shown, roll it back now.
      this.revertOptimisticMembershipActivation(data);
    }
  }

  /** Current checkout context is a monthly membership (form/cache/notification wash type). */
  private isMonthlyMembershipContext(): boolean {
    if (this.notification?.type === 'active-subscription') {
      return true;
    }
    return this.getNormalizedWashType() === 'monthly_membership';
  }

  /**
   * Roll back an optimistically-shown active/success membership when a post-payment
   * failure/refund arrives. No-op for non-membership flows and when nothing was shown,
   * so single_wash / add_on / prepaid behavior is unchanged.
   */
  private revertOptimisticMembershipActivation(data?: any): void {
    const membershipContext =
      this.notification?.type === 'active-subscription' ||
      this.getNormalizedWashType() === 'monthly_membership';
    if (!membershipContext) {
      return;
    }
    if (!this.showPaymentSuccess && this.notification?.type !== 'active-subscription') {
      return;
    }
    const message =
      String((data as Record<string, any> | null)?.['message'] ?? '').trim() ||
      "Your card can't be used to purchase a membership.";
    this.showPaymentSuccess = false;
    if (this.notification) {
      this.notification.type = 'create-payment-request';
      this.notification.message = message;
    }
    this.cdr.detectChanges();
  }

  /**
   * Android app calls `window.onTapToPayAndroidPaymentResult(detail)` when the user returns from
   * Tap to Pay. Do not also listen for `taptopay_android` here — the native shell fires both and
   * TapToPayAndroidBridgeService already handles the custom event for shared services.
   */
  private installTapToPayAndroidBridge(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const w = window as Window & { onTapToPayAndroidPaymentResult?: (detail: any) => void };
    this.tapToPayAndroidJsCallback = (detail: any) => {
      this.ngZone.run(() => this.applyTapToPayAndroidPaymentResult(detail));
    };
    w.onTapToPayAndroidPaymentResult = this.tapToPayAndroidJsCallback;
  }

  private removeTapToPayAndroidBridge(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const w = window as Window & { onTapToPayAndroidPaymentResult?: (detail: any) => void };
    if (
      this.tapToPayAndroidJsCallback &&
      w.onTapToPayAndroidPaymentResult === this.tapToPayAndroidJsCallback
    ) {
      delete w.onTapToPayAndroidPaymentResult;
    }
    this.tapToPayAndroidJsCallback = null;
  }

  private applyTapToPayAndroidPaymentResult(raw: any): void {
    const d = raw && typeof raw === 'object' ? (raw as Record<string, any>) : {};
    const status = String(d['status'] ?? '');
    if (status === 'failed') {
      const orderId = String(
        d['internal_id'] ?? d['order_id'] ?? d['orderId'] ?? this.formData.orderId ?? ''
      ).trim();
      if (!this.isNotificationModalTapToPayResult(orderId)) {
        return;
      }
      if (!this.claimPaymentFailureNotification(d, orderId)) {
        return;
      }
      this.clearTapToPayAwaitingNativeResult();
      const errorMessage =
        String(d['message'] ?? '').trim() || 'Payment could not be processed. Please try again.';
      this.clearPaymentTimer();
      this.messageService.add({
        severity: 'error',
        summary: 'Payment Failed',
        detail: errorMessage,
        life: 5000,
      });
      return;
    }
    if (status !== 'success') {
      return;
    }

    const orderId = String(
      d['internal_id'] ?? d['order_id'] ?? d['orderId'] ?? this.formData.orderId ?? ''
    ).trim();
    if (!orderId) {
      if (!this.tapToPayAwaitingNativeResult || !this.isOpen) {
        return;
      }
      this.clearTapToPayAwaitingNativeResult();
      this.populateSuccessModalWithBasicData();
      return;
    }

    if (!this.isNotificationModalTapToPayResult(orderId)) {
      return;
    }

    if (this.tapToPayNativeSuccessOrderIds.has(orderId)) {
      return;
    }
    this.tapToPayNativeSuccessOrderIds.add(orderId);
    setTimeout(() => this.tapToPayNativeSuccessOrderIds.delete(orderId), 5 * 60 * 1000);

    this.clearTapToPayAwaitingNativeResult();
    this.clearPaymentTimer();
    this.formData.orderId = orderId;
    if (this.notification?.data) {
      this.notification.data.order_id = orderId;
      this.notification.data.orderId = orderId;
    }
    this.paymentDoneOrderId = orderId;

    this.unsubscribeFromOrderChannel();

    this.finalizeTapToPayAfterSuccess(orderId);
  }

  /**
   * Tap to Pay collects via Stripe Terminal but, like cash, must call confirm-payment-mode so the
   * order moves to `scheduled` (especially for saas / header "+" direct orders).
   */
  private finalizeTapToPayAfterSuccess(orderId: string): void {
    const internalId = String(orderId ?? '')
      .replace(/^#+/, '')
      .trim();
    if (!internalId) {
      return;
    }

    this.apiService
      .post(`order/confirm-payment-mode/${internalId}`, {
        payment_mode: PaymentMethodCode.CardPresent,
      })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.logger.trackPayment('Tap to Pay finalized (notification modal)', {
            orderId: internalId,
            paymentMode: PaymentMethodCode.CardPresent,
          });
          this.verifyOrderPaidThenShowTapToPaySuccess(internalId);
        },
        error: error => {
          this.logger.trackError('NotificationModal.finalizeTapToPayAfterSuccess', error, {
            orderId: internalId,
          });
          this.verifyOrderPaidThenShowTapToPaySuccess(internalId);
        },
      });
  }

  /** Only open success UI when the order is actually paid (avoids false success on decline/incomplete). */
  private verifyOrderPaidThenShowTapToPaySuccess(orderId: string): void {
    this.apiService
      .get(`order/${orderId}`)
      .pipe(take(1))
      .subscribe({
        next: (orderResponse: any) => {
          const apiOrder = this.extractOrderRecordFromApiResponse(orderResponse);
          const paymentStatus = String(apiOrder?.['payment_status'] ?? '').toLowerCase();
          if (paymentStatus === 'paid') {
            this.completeTapToPayPaymentUi(orderId);
            return;
          }
          this.messageService.add({
            severity: 'warn',
            summary: 'Payment Not Completed',
            detail:
              apiOrder?.['failure_message']?.toString() ||
              'Payment was not completed. You can try again.',
            life: 6000,
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Status Unknown',
            detail: 'Could not verify payment status. Please check the order before retrying.',
            life: 6000,
          });
        },
      });
  }

  private completeTapToPayPaymentUi(orderId: string): void {
    this.clearPaymentTimer();
    this.formData.orderId = orderId;
    if (this.notification?.data) {
      this.notification.data.order_id = orderId;
      this.notification.data.orderId = orderId;
    }
    this.paymentDoneOrderId = orderId;
    this.showOrderPaymentSuccessUi(orderId);
  }

  /**
   * Pusher success modal: payment link only. Card is shown from checkout; tap-to-pay uses Android bridge.
   */
  private shouldShowPaymentSuccessModalOnPaymentDone(data: any): boolean {
    if (
      this.paymentForm.method === PaymentMethodCode.TapToPay ||
      this.paymentForm.method === PaymentMethodCode.Card ||
      this.paymentForm.method === PaymentMethodCode.Terminal
    ) {
      return false;
    }
    const source = String((data as Record<string, any> | null)?.['source'] ?? '');
    if (source === 'tap_to_pay_android_webview' || source.includes('tap_to_pay')) {
      return false;
    }
    return this.paymentForm.method === PaymentMethodCode.Link || this.paymentLinkSent;
  }

  /**
   * Show payment success modal with package / vehicle details (cash, card, membership, payment link, Android tap-to-pay).
   */
  private showOrderPaymentSuccessUi(orderId: string): void {
    if (this.shouldStayOnImportedVendorCardSetup()) {
      this.completeImportedMembershipCardSetup(orderId, null);
      return;
    }
    if (!orderId) {
      this.populateSuccessModalWithBasicData();
      return;
    }
    this.deferLprEntrySyncUntilOrderLoaded = true;
    this.pendingPostPaymentLprSyncOrderId = String(orderId).trim() || null;
    this.applySuccessModalSnapshot(this.buildSuccessModalSnapshot(null), false, {
      deferLprSync: true,
    });
    this.fetchOrderDetailsForSuccessModal(orderId);
  }

  /**
   * Handle payment_done event from SSE
   */
  private handlePaymentDone(orderId: string, data: any): void {
    this.logger.trackPayment('Payment done (notification modal)', {
      orderId,
      notificationType: this.notification?.type,
      licensePlate:
        this.notification?.data?.plate || this.notification?.data?.vehicle_license_plate,
      elapsedSinceOpenMs: this.modalOpenTime ? Date.now() - this.modalOpenTime : undefined,
      elapsedSincePaymentStartMs: this.paymentStartTime
        ? Date.now() - this.paymentStartTime
        : undefined,
    });
    this.logModalInteraction('payment.done.handle', {
      orderId,
      ssePayload:
        data && typeof data === 'object' ? { status: data.status, keys: Object.keys(data) } : data,
    });

    // Stop the payment timer since payment is done
    this.clearPaymentTimer();

    if (orderId) {
      this.formData.orderId = orderId;
    }

    // Update notification data if available
    if (this.notification?.data) {
      this.notification.data.order_id = orderId;
      this.notification.data.orderId = orderId;
    }

    this.paymentDoneOrderId = orderId;

    // For monthly memberships, payment_done (the one-time charge) does NOT confirm the
    // subscription. The backend can refund the charge and emit a later payment_failed a
    // few seconds after payment_done — keep the order channel subscribed so that late
    // failure can revert the UI. Non-membership flows keep tearing down as before.
    if (!this.isMonthlyMembershipContext()) {
      this.unsubscribeFromOrderChannel();
    }

    if (this.paymentForm.method === PaymentMethodCode.TapToPay) {
      if (!this.showPaymentSuccess) {
        const normalizedOrderId = String(orderId ?? '')
          .replace(/^#+/, '')
          .trim();
        if (normalizedOrderId && !this.tapToPayNativeSuccessOrderIds.has(normalizedOrderId)) {
          this.finalizeTapToPayAfterSuccess(orderId);
        }
      }
      return;
    }

    if (this.paymentForm.method === PaymentMethodCode.Terminal) {
      this.isProcessingPayment = false;
      this.stopTerminalPaymentStatusPoll();
      this.cdr.markForCheck();
    }

    const normalizedOrderId = String(orderId ?? '')
      .replace(/^#+/, '')
      .trim();
    if (normalizedOrderId && !this.showPaymentSuccess) {
      this.showOrderPaymentSuccessUi(normalizedOrderId);
    }
  }

  /**
   * Fetch full order details from API to populate success modal (merges — never wipes good values with empty).
   */
  private fetchOrderDetailsForSuccessModal(orderId: string): void {
    if (!orderId) {
      return;
    }

    this.apiService
      .get(`order/${orderId}`)
      .pipe(take(1))
      .subscribe({
        next: (orderResponse: any) => {
          const apiOrder = this.extractOrderRecordFromApiResponse(orderResponse);
          if (apiOrder?.['wash_type']) {
            this.syncWashTypeContextToNotification(String(apiOrder['wash_type']));
          }
          // Membership success is confirmed by the loaded order, not by payment_done alone.
          if (
            this.resolveWashTypeForLprUpdate(apiOrder) === 'monthly_membership' &&
            !this.orderHasActiveMembershipSubscription(apiOrder)
          ) {
            // While we are waiting on the activating webhook, a non-active order is the expected
            // intermediate state — not a failure. Keep the confirming state and let the realtime
            // event (or the polling fallback) decide the outcome.
            if (this.membershipActivationAwaitingWebhook) {
              return;
            }
            this.handleMembershipActivationFailure(orderId, apiOrder);
            return;
          }
          this.clearMembershipActivationWait();
          this.isProcessingPayment = false;
          this.applySuccessModalSnapshot(this.buildSuccessModalSnapshot(apiOrder), true);
          this.resolveOrderLaneGateForSuccessModal(apiOrder);
          this.completeDeferredLprEntrySyncAfterSuccessModal(apiOrder, orderId);
        },
        error: error => {
          console.error('Error fetching order details for success modal:', error);
          if (!this.showPaymentSuccess) {
            this.applySuccessModalSnapshot(this.buildSuccessModalSnapshot(null), false);
          }
          this.completeDeferredLprEntrySyncAfterSuccessModal(null, orderId);
        },
      });
  }

  /**
   * Imported membership card-on-file: Stripe already created a trialing subscription and charged $0.
   * Stay on Active subscription — hide payment methods and show that the card was saved.
   */
  private shouldStayOnImportedVendorCardSetup(): boolean {
    if (this.importedVendorCardOnFileCheckout) {
      return true;
    }
    return Boolean(
      this.shouldShowVendorActiveSubscriptionModal() &&
      this.getVendorActiveSubscriptionDisplay()?.cardDetailsCollected
    );
  }

  private completeImportedMembershipCardSetup(orderId: string, _setupPayload: any): void {
    const alreadyCollected = Boolean(
      this.getVendorActiveSubscriptionDisplay()?.cardDetailsCollected
    );
    this.isProcessingPayment = false;
    this.showPaymentSuccess = false;
    this.showVendorUpgradePlanPicker = false;
    this.clearMembershipActivationWait();
    this.markVendorMembershipCardCollected(orderId);
    if (!alreadyCollected) {
      this.messageService.add({
        severity: 'success',
        summary: 'Card saved',
        detail: 'Card details collected.',
        life: 4000,
      });
    }
    this.cdr.markForCheck();
  }

  private markVendorMembershipCardCollected(orderId: string): void {
    const current =
      this.vendorActiveSubscriptionLookupDisplay || this.getVendorActiveSubscriptionDisplay();
    this.vendorActiveSubscriptionLookupDisplay = {
      ...current,
      orderId: orderId || current?.orderId || null,
      paymentRequired: false,
      cardDetailsCollected: true,
    };
    this.showVendorActiveSubscriptionLookupModal = true;
    if (this.notification?.data) {
      this.notification = {
        ...this.notification,
        data: {
          ...this.notification.data,
          order_id: orderId || this.notification.data.order_id,
          payment_required: false,
          has_payment: true,
          card_details_collected: true,
        },
      };
    }
  }

  /**
   * The charge went through but the membership is not active yet. Show a confirming state and let
   * the order's realtime channel deliver `payment_done` (activated) or `payment_failed`, with a
   * polling fallback in case the webhook is slow or the SSE connection dropped.
   */
  private beginMembershipActivationWait(orderId: string): void {
    this.clearMembershipActivationWait();
    this.membershipActivationAwaitingWebhook = true;
    // Keep the spinner up: the purchase is genuinely still in progress, and this also stops a
    // second submit while Stripe is confirming.
    this.isProcessingPayment = true;
    if (this.notification) {
      this.notification.message = MEMBERSHIP_ACTIVATION_PENDING_MESSAGE;
    }
    this.messageService.add({
      severity: 'info',
      summary: 'Payment received',
      detail: MEMBERSHIP_ACTIVATION_PENDING_MESSAGE,
      life: 4000,
    });
    // The activation event arrives on the order channel; without a subscription it would be missed.
    if (!this.paymentSseSub) {
      this.subscribeToOrderChannel(orderId);
    }
    this.membershipActivationWaitTimer = setTimeout(
      () => this.pollMembershipActivationStatus(orderId),
      MEMBERSHIP_ACTIVATION_WAIT_MS
    );
    this.cdr.detectChanges();
  }

  /** Stop waiting on webhook activation (activated, failed, or given up). */
  private clearMembershipActivationWait(): void {
    if (this.membershipActivationWaitTimer) {
      clearTimeout(this.membershipActivationWaitTimer);
      this.membershipActivationWaitTimer = null;
    }
    this.membershipActivationAwaitingWebhook = false;
    this.membershipActivationPollAttempts = 0;
  }

  /**
   * Webhook never arrived in the wait window. Re-read the order a few times; the membership may
   * well be active and only the realtime event was lost.
   */
  private pollMembershipActivationStatus(orderId: string): void {
    this.membershipActivationWaitTimer = null;
    if (!this.membershipActivationAwaitingWebhook) {
      return;
    }
    this.membershipActivationPollAttempts += 1;

    this.apiService
      .get(`order/${orderId}`)
      .pipe(take(1))
      .subscribe({
        next: (orderResponse: any) => {
          const apiOrder = this.extractOrderRecordFromApiResponse(orderResponse);
          if (this.orderHasActiveMembershipSubscription(apiOrder)) {
            this.clearMembershipActivationWait();
            this.showOrderPaymentSuccessUi(orderId);
            return;
          }
          this.continueOrEndMembershipActivationPolling(orderId);
        },
        error: () => this.continueOrEndMembershipActivationPolling(orderId),
      });
  }

  private continueOrEndMembershipActivationPolling(orderId: string): void {
    if (this.membershipActivationPollAttempts < MEMBERSHIP_ACTIVATION_POLL_ATTEMPTS) {
      this.membershipActivationWaitTimer = setTimeout(
        () => this.pollMembershipActivationStatus(orderId),
        MEMBERSHIP_ACTIVATION_POLL_INTERVAL_MS
      );
      return;
    }
    this.showMembershipActivationDelayed();
  }

  /**
   * Give up waiting without calling it a failure: the money was taken and Stripe may still
   * activate the membership, so a hard error would be wrong. The backend owns the LPR row from
   * here, so nothing is written locally.
   */
  private showMembershipActivationDelayed(): void {
    this.clearMembershipActivationWait();
    this.isProcessingPayment = false;
    if (this.notification) {
      this.notification.message = MEMBERSHIP_ACTIVATION_DELAYED_MESSAGE;
    }
    this.messageService.add({
      severity: 'warn',
      summary: 'Still confirming',
      detail: MEMBERSHIP_ACTIVATION_DELAYED_MESSAGE,
      life: 8000,
    });
    this.cdr.detectChanges();
  }

  /**
   * Membership was refused outright (402 on setup, or a `payment_failed` event). Nothing was
   * captured, or the backend already refunded it, so show the reason and leave the request pending
   * for a retry.
   */
  private failMembershipActivation(message: string): void {
    this.clearMembershipActivationWait();
    this.isProcessingPayment = false;
    this.showPaymentSuccess = false;
    if (this.notification) {
      this.notification.type = 'create-payment-request';
      this.notification.message = message;
    }
    this.messageService.add({
      severity: 'error',
      summary: 'Membership not activated',
      detail: message,
      life: 6000,
    });
    this.cdr.detectChanges();
  }

  private handleMembershipActivationFailure(
    orderId: string,
    order: Record<string, any> | null
  ): void {
    const message = this.resolveMembershipActivationFailureMessage(order);
    this.showPaymentSuccess = false;
    if (this.notification) {
      this.notification.type = 'create-payment-request';
      this.notification.message = message;
    }
    this.messageService.add({
      severity: 'error',
      summary: 'Membership not activated',
      detail: message,
      life: 6000,
    });
    this.completeDeferredLprEntrySyncAfterSuccessModal(order, orderId);
    this.cdr.detectChanges();
  }

  /** Normalize GET order/:id payloads (`data`, `data.order`, nested `vehicle`). */
  private extractOrderRecordFromApiResponse(response: any): Record<string, any> | null {
    if (!response || typeof response !== 'object') {
      return null;
    }
    const r = response as Record<string, any>;
    const data = r['data'];
    if (data && typeof data === 'object') {
      const d = data as Record<string, any>;
      const nested = d['order'];
      if (nested && typeof nested === 'object') {
        return nested as Record<string, any>;
      }
      return d;
    }
    return r;
  }

  private joinNameParts(...parts: any[]): string {
    return parts
      .map(p => String(p ?? '').trim())
      .filter(p => p.length > 0)
      .join(' ')
      .trim();
  }

  private pickFirstNonEmpty(...values: any[]): string {
    for (const v of values) {
      if (v == null) {
        continue;
      }
      const s = String(v).trim();
      if (s && s.toLowerCase() !== 'any' && s !== 'N/A') {
        return s;
      }
    }
    return '';
  }

  private resolveSuccessModalPackageName(order?: Record<string, any> | null): string {
    const o = order ?? {};
    const packageId = this.pickFirstNonEmpty(
      o['package_id'],
      o['packageId'],
      this.formData.packageId
    );

    let name = this.pickFirstNonEmpty(
      o['package_name'],
      o['packageName'],
      o['way_package_name'],
      o['wayPackageName'],
      this.formData.package,
      this.selectedPackage?.package_name,
      this.notification?.data?.package_name,
      this.notification?.data?.packageName,
      this.notification?.data?.vehicle_data?.package_name,
      this.orderCompletedPackageName
    );

    if (!name && packageId && this.expandedPackages.length > 0) {
      const ep = this.expandedPackages.find(x => x.package.id === packageId);
      if (ep) {
        name = ep.displayName || ep.package.package_name || '';
      }
    }

    if (!name && packageId && this.packages.length > 0) {
      const pkg = this.packages.find(p => p.id === packageId);
      if (pkg) {
        name = pkg.package_name || '';
      }
    }

    return name.replace(/^Car\s+Wash\s+Package\s+/i, '').trim();
  }

  private resolveSuccessModalCustomerName(order?: Record<string, any> | null): string {
    const o = order ?? {};
    return resolveRawCustomerName({
      firstName: this.pickFirstNonEmpty(
        o['customer_first_name'],
        o['customerFirstName'],
        this.formData.customerFirstName,
        this.notification?.data?.customer_first_name
      ),
      lastName: this.pickFirstNonEmpty(
        o['customer_last_name'],
        o['customerLastName'],
        this.formData.customerLastName,
        this.notification?.data?.customer_last_name
      ),
      customerName: this.pickFirstNonEmpty(o['customer_name'], o['customerName']),
      fallback: this.pickFirstNonEmpty(
        `${this.formData.customerFirstName} ${this.formData.customerLastName}`.trim(),
        [this.notification?.data?.customer_first_name, this.notification?.data?.customer_last_name]
          .filter(Boolean)
          .join(' ')
          .trim(),
        this.orderCompletedCustomerName
      ),
    });
  }

  private resolveSuccessModalVehicleFields(order?: Record<string, any> | null): {
    vehicleYear: string;
    vehicleMake: string;
    vehicleModel: string;
  } {
    const o = order ?? {};
    const vehicle =
      o['vehicle'] && typeof o['vehicle'] === 'object'
        ? (o['vehicle'] as Record<string, any>)
        : null;

    const licensePlate = this.pickFirstNonEmpty(
      o['license_plate'],
      o['vehicle_license_plate'],
      o['plate'],
      vehicle?.['license_plate'],
      this.formData.vehicleLicensePlate,
      this.notification?.data?.vehicle_license_plate,
      this.notification?.data?.license_plate,
      this.notification?.data?.plate,
      this.orderCompletedVehicleNumber
    );

    const rawModel = this.pickFirstNonEmpty(
      o['model'],
      o['vehicle_model'],
      vehicle?.['model'],
      this.formData.vehicleModel,
      this.notification?.data?.vehicle_model,
      this.notification?.data?.vehicleModel,
      this.orderCompletedVehicleModel
    );

    return {
      vehicleYear: this.pickFirstNonEmpty(
        o['year'],
        o['vehicle_year'],
        vehicle?.['year'],
        vehicle?.['vehicle_year'],
        this.formData.vehicleYear,
        this.notification?.data?.vehicle_year,
        this.orderCompletedVehicleYear
      ),
      vehicleMake: this.pickFirstNonEmpty(
        o['make'],
        o['vehicle_make'],
        vehicle?.['make'],
        this.formData.vehicleMake,
        this.notification?.data?.vehicle_make,
        this.notification?.data?.vehicleMake,
        this.orderCompletedVehicleMake
      ),
      vehicleModel: this.formatSuccessModalVehicleModel(rawModel, licensePlate),
    };
  }

  private buildSuccessModalSnapshot(order?: Record<string, any> | null): SuccessModalData {
    const vehicle = this.resolveSuccessModalVehicleFields(order);
    return buildPaymentSuccessModalData({
      packageName: this.resolveSuccessModalPackageName(order),
      customerName: this.resolveSuccessModalCustomerName(order),
      vehicleYear: vehicle.vehicleYear,
      vehicleMake: vehicle.vehicleMake,
      vehicleModel: vehicle.vehicleModel,
    });
  }

  /** Apply success modal fields; when `merge` is true, keep existing non-empty values. */
  private applySuccessModalSnapshot(
    snapshot: SuccessModalData,
    merge: boolean,
    options?: { deferLprSync?: boolean }
  ): void {
    const wasShowing = this.showPaymentSuccess;
    const prev = this.successModalData;
    const keep = (next: string, old: string) => {
      const n = (next || '').trim();
      const o = (old || '').trim();
      if (merge && !n && o) {
        return o;
      }
      return n || o;
    };

    if (this.shouldStayOnImportedVendorCardSetup()) {
      this.showPaymentSuccess = false;
      this.cdr.detectChanges();
      return;
    }

    this.successModalData = {
      title: snapshot.title || prev.title,
      description: snapshot.description || prev.description,
      buttonText: snapshot.buttonText || prev.buttonText,
      packageName: keep(snapshot.packageName ?? '', prev.packageName ?? ''),
      customerName: keep(snapshot.customerName ?? '', prev.customerName ?? ''),
      vehicleYear: keep(snapshot.vehicleYear ?? '', prev.vehicleYear ?? ''),
      vehicleMake: keep(snapshot.vehicleMake ?? '', prev.vehicleMake ?? ''),
      vehicleModel: keep(snapshot.vehicleModel ?? '', prev.vehicleModel ?? ''),
    };
    this.showPaymentSuccess = true;
    const deferLpr = options?.deferLprSync === true || this.deferLprEntrySyncUntilOrderLoaded;
    if (!wasShowing && !deferLpr) {
      this.syncLprEntryAfterPaymentWhenSuccessModalOpens();
    }
    this.cdr.detectChanges();
  }

  /** Runs deferred LPR PUT once GET order/:id resolves (success modal wash type). */
  private completeDeferredLprEntrySyncAfterSuccessModal(
    order: Record<string, any> | null,
    paymentOrderId?: string
  ): void {
    const orderId = this.resolvePostPaymentLprSyncOrderId(paymentOrderId);
    const pendingSync =
      this.deferLprEntrySyncUntilOrderLoaded ||
      (!!orderId && !this.lprEntryUpdatedAfterPaymentOrderIds.has(orderId));
    if (!pendingSync) {
      return;
    }
    this.deferLprEntrySyncUntilOrderLoaded = false;
    this.syncLprEntryAfterPaymentWhenSuccessModalOpens(order, orderId);
  }

  private resolvePostPaymentLprSyncOrderId(explicitOrderId?: string): string {
    return String(
      explicitOrderId ||
        this.pendingPostPaymentLprSyncOrderId ||
        this.paymentDoneOrderId ||
        this.formData.orderId ||
        this.notification?.data?.order_id ||
        this.notification?.data?.orderId ||
        ''
    ).trim();
  }

  /** After payment completes, PUT order-success (single wash) or active-subscription (monthly). */
  private syncLprEntryAfterPaymentWhenSuccessModalOpens(
    order?: Record<string, any> | null,
    orderIdOverride?: string
  ): void {
    const orderId = this.resolvePostPaymentLprSyncOrderId(orderIdOverride);
    if (!orderId) {
      return;
    }
    if (this.lprEntryUpdatedAfterPaymentOrderIds.has(orderId)) {
      return;
    }
    if (this.lprEntryPaymentSyncInFlightOrderIds.has(orderId)) {
      return;
    }
    try {
      if (order) {
        const notificationId = this.resolveLprEntryNotificationId();
        if (notificationId) {
          const washType = this.resolveWashTypeForLprUpdate(order);
          this.putLprEntryAfterPayment(notificationId, orderId, washType, order);
          return;
        }
      }
      this.updateNotificationAfterPayment(orderId);
    } catch (error) {
      console.error('❌ Error updating LPR entry on payment success modal:', error);
    }
  }

  /** Append license plate to model line when present (success modal VEHICLE row). */
  private formatSuccessModalVehicleModel(model: string, licensePlate: string): string {
    const m = String(model ?? '').trim();
    const plate = String(licensePlate ?? this.formData.vehicleLicensePlate ?? '').trim();
    if (!plate) {
      return m;
    }
    return m ? `${m} · ${plate}` : plate;
  }

  /** Populate success modal from form / notification (then optionally enriched by GET order/:id). */
  private populateSuccessModalWithBasicData(): void {
    this.applySuccessModalSnapshot(this.buildSuccessModalSnapshot(null), false);
  }

  /**
   * Handle payment success modal close
   */
  onPaymentSuccessModalClose(): void {
    this.logModalInteraction('modal.paymentSuccess.close');
    this.onClose();
    this.showPaymentSuccess = false;
    const currentRoute = this.router.url.split('?')[0]; // Remove query params
    if (currentRoute.includes('/orders')) {
      console.log('🔄 Refreshing orders table and stats via API...');
      this.ordersService.refreshOrders();
    }
  }

  /**
   * Handle payment success modal action
   */
  onPaymentSuccessAction(): void {
    this.logModalInteraction('modal.paymentSuccess.action');
    this.onClose();
    this.showPaymentSuccess = false;
    const currentRoute = this.router.url.split('?')[0];
    if (currentRoute.includes('/orders')) {
      this.ordersService.refreshOrders();
    }
  }

  /**
   * Decide whether the payment success modal should offer "Open Gate": only when the
   * ORDER's lane has a MANUAL gate configured. Automatic gates open on their own via the
   * LPR/auto path (no button), and lanes without a gate cannot be opened. Gate metadata is
   * not on the order, so resolve it from the location's lanes list and match by lane_id.
   */
  private resolveOrderLaneGateForSuccessModal(order: Record<string, any> | null): void {
    this.successModalData = { ...this.successModalData, showOpenGate: false };
    this.openGateLocationId = '';
    this.openGateLaneId = '';

    const laneId = this.pickFirstNonEmpty(order?.['lane_id'], order?.['laneId']);
    if (!laneId) {
      return;
    }
    const locationId =
      this.pickFirstNonEmpty(order?.['location_id'], order?.['locationId']) ||
      String(getUserDataLocationId() ?? '').trim();
    if (!locationId) {
      return;
    }

    this.apiService
      .get<LaneGateRecord[]>(`location/view-location/${encodeURIComponent(locationId)}/lanes`)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const lanes =
            response.status && Array.isArray(response.data)
              ? (response.data as LaneGateRecord[])
              : [];
          const manualLane = findManualOperableLane(lanes, laneId);
          if (manualLane && this.showPaymentSuccess) {
            this.openGateLocationId = locationId;
            this.openGateLaneId = laneId;
            this.successModalData = { ...this.successModalData, showOpenGate: true };
            this.cdr.detectChanges();
          }
        },
        error: error => {
          console.error('Error resolving order lane gate for success modal:', error);
        },
      });
  }

  /** Open the gate for the ORDER's lane from the payment success modal (manual gates only). */
  onOpenGate(): void {
    const locationId = this.openGateLocationId;
    const laneId = this.openGateLaneId;
    if (!locationId || !laneId || this.isOpeningGate) {
      return;
    }
    this.isOpeningGate = true;
    this.gateService
      .openLaneGate(locationId, laneId)
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.isOpeningGate = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: response => {
          this.messageService.add({
            severity: 'success',
            summary: 'Gate opened',
            detail: response?.message || 'The gate is opening now.',
          });
        },
        error: (error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Open gate failed',
            detail: extractErrorMessage(error, 'Unable to open gate. Please try again.'),
          });
        },
      });
  }

  onPrintInvoice(): void {
    this.orderReceiptService.printReceiptForOrderId(
      String(this.paymentDoneOrderId || this.formData?.orderId || '')
    );
  }

  /**
   * Payment Timer Methods
   */
  private startPaymentTimer(): void {
    this.clearPaymentTimer();
    this.resetPaymentTimer();
    this.paymentExpired = false;

    this.paymentTimerInterval = setInterval(() => {
      if (this.paymentSeconds > 0) {
        this.paymentSeconds--;
      } else if (this.paymentMinutes > 0) {
        this.paymentMinutes--;
        this.paymentSeconds = 59;
      } else {
        // Timer finished - payment expired
        this.clearPaymentTimer();
        this.onPaymentTimerExpired();
      }
    }, 1000);
  }

  private clearPaymentTimer(): void {
    if (this.paymentTimerInterval) {
      clearInterval(this.paymentTimerInterval);
      this.paymentTimerInterval = null;
    }
  }

  private resetPaymentTimer(): void {
    this.paymentMinutes = 5;
    this.paymentSeconds = 0;
  }

  private onPaymentTimerExpired(): void {
    this.paymentExpired = true;
    this.messageService.add({
      severity: 'warn',
      summary: 'Payment Link Expired',
      detail: 'The payment link has expired. Please create a new payment link.',
    });
  }

  get formattedPaymentMinutes(): string {
    return this.paymentMinutes.toString().padStart(2, '0');
  }

  get formattedPaymentSeconds(): string {
    return this.paymentSeconds.toString().padStart(2, '0');
  }

  get formattedPaymentPhoneNumber(): string {
    if (!this.paymentForm.phoneNumber) return '';
    // Format phone number for display
    const cleaned = this.paymentForm.phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2 $3');
    } else if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return cleaned.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3 $4');
    }
    return this.paymentForm.phoneNumber;
  }

  /**
   * Resend payment link
   */
  onResendPaymentLink(): void {
    if (this.isCreatingPaymentLink) {
      this.logModalInteraction('payment.link.resend.skipped', { reason: 'already_creating' });
      return;
    }

    this.logModalInteraction('payment.link.resend');

    // Reset timer and resend
    this.resetPaymentTimer();
    this.startPaymentTimer();
    this.createPaymentLink();
  }

  /**
   * Cancel payment link
   */
  onCancelPaymentLink(): void {
    this.logModalInteraction('payment.link.cancel');
    this.clearPaymentTimer();
    this.paymentLinkSent = false;
    this.paymentExpired = false;
    this.resetPaymentTimer();
    // Reset payment method to allow user to select again
    this.paymentForm.method = PaymentMethodCode.Card;
  }

  /**
   * Create new payment link after expiration
   */
  onCreateNewPaymentLink(): void {
    if (this.isCreatingPaymentLink) {
      this.logModalInteraction('payment.link.createNew.skipped', { reason: 'already_creating' });
      return;
    }

    this.logModalInteraction('payment.link.createNew');

    // Reset expired state and create new link
    this.paymentExpired = false;
    this.resetPaymentTimer();
    this.startPaymentTimer();
    this.createPaymentLink();
  }

  /**
   * Get package name for order - checks loaded packages first, then fetches from API if needed
   */
  private async getPackageNameForOrder(packageId: string, fallbackName: string): Promise<string> {
    // If we have a fallback name, use it immediately
    if (fallbackName) {
      return fallbackName;
    }

    // If no package ID, return empty
    if (!packageId) {
      return '';
    }

    // First check loaded packages
    if (this.packages.length > 0) {
      const foundPackage = this.packages.find(pkg => pkg.id === packageId);
      if (foundPackage) {
        return foundPackage.package_name;
      }
    }

    // If not found in loaded packages, try to fetch from API
    try {
      const response = await this.apiService
        .get(`package/view/${packageId}`)
        .pipe(takeUntil(this.destroy$))
        .toPromise();
      if (response && (response as any).status && (response as any).data) {
        const packageData = (response as any).data;
        return packageData.package_name || '';
      }
    } catch (error) {
      console.error('Error fetching package name:', error);
    }

    return '';
  }

  /**
   * Get formatted description for order completed modal
   */
  getOrderCompletedDescription(): string {
    let description =
      'Payment has been successfully completed for ' + this.orderCompletedCustomerName;

    // Build vehicle info string
    const vehicleParts: string[] = [];

    if (this.orderCompletedVehicleMake || this.orderCompletedVehicleModel) {
      const makeModel = [this.orderCompletedVehicleMake, this.orderCompletedVehicleModel]
        .filter(Boolean)
        .join(' ');
      if (makeModel) {
        vehicleParts.push(makeModel);
      }
    }

    if (this.orderCompletedVehicleNumber) {
      vehicleParts.push(this.orderCompletedVehicleNumber);
    }

    if (vehicleParts.length > 0) {
      description += ' (Vehicle - ' + vehicleParts.join(' - ') + ')';
    }

    // Add package name if available
    if (this.orderCompletedPackageName) {
      description += ' - Package - ' + this.orderCompletedPackageName;
    }

    return description;
  }

  /**
   * Handle okay button click for order-success, active-subscription, and other dismiss-only notifications
   */
  onOkayClick(): void {
    this.logModalInteraction('primary.okay', { notificationType: this.notification?.type });
    if (this.notification?.type === 'order-success') {
      // For order-success, first fetch order by ID to check status
      this.handleOrderSuccessClick();
    } else if (this.notification?.type === 'active-subscription') {
      if (canOpenGateForActiveSubscription(this.notification)) {
        this.openGateForActiveSubscription();
        return;
      }
      this.onClose();
      this.ordersService.refreshOrders();
    } else {
      // For cancel button or other cases, just close the modal without page refresh
      this.onClose();
    }
  }

  getViewOnlyDismissButtonLabel(): string {
    if (canOpenGateForActiveSubscription(this.notification)) {
      return 'Open Gate';
    }
    return 'Got it';
  }

  private openGateForActiveSubscription(): void {
    const data = this.notification?.data as Record<string, any> | undefined;
    const locationId = resolveNotificationLocationId(data);
    const laneId = resolveNotificationLaneId(data);

    if (!canOpenGateForActiveSubscription(this.notification)) {
      this.updateOrderError = 'Gate is not configured for this lane.';
      return;
    }

    this.updateOrderError = '';
    this.isOpeningGate = true;
    this.gateService
      .openLaneGate(locationId, laneId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isOpeningGate = false;
        })
      )
      .subscribe({
        next: () => {
          this.ordersService.refreshOrders();
          this.onClose();
        },
        error: (error: any) => {
          this.updateOrderError = extractErrorMessage(
            error,
            'Failed to open gate. Please try again.'
          );
        },
      });
  }

  /**
   * Handle order-success primary action: optional LPR follow-up when scheduled, then close.
   */
  private handleOrderSuccessClick(): void {
    const data = this.notification?.data as any;
    const orderId = data?.order_id || data?.orderId || this.formData.orderId;

    if (!orderId) {
      this.onClose();
      return;
    }

    const orderStatus = data?.order_status || data?.status || '';

    if (orderStatus === 'scheduled') {
      this.updateNotificationAfterPayment(orderId);
    }

    this.ordersService.refreshOrders();
    this.onClose();
  }

  /**
   * Handle cancel button click - simply close the modal without any actions
   * For create-payment-request, toggle edit mode instead
   */
  onCancelClick(): void {
    this.logModalInteraction('primary.cancel', {
      notificationType: this.notification?.type,
      isEditMode: this.isEditMode,
    });
    if (this.notification?.type === 'create-payment-request') {
      if (this.isEditMode) {
        this.cancelEditMode();
      } else if (this.hasCheckoutOrderId()) {
        this.enterEditMode();
      } else if (!this.isUpdatingOrder) {
        this.persistLprEntryOnOrderRequestDismiss('cancel');
        this.onClose();
      } else {
        this.onClose();
      }
    } else if (!this.isUpdatingOrder) {
      this.persistLprEntryOnOrderRequestDismiss('cancel');
      this.onClose();
    } else {
      this.onClose();
    }
  }

  /** Keep `notification.data` in sync with the order-request UI for checkout + Back → edit prefill. */
  private syncCreatePaymentRequestNotificationDataLayerFromOrderRequestUi(): void {
    if (this.notification?.type !== 'create-payment-request' || !this.notification.data) {
      return;
    }
    const data = this.notification.data as Record<string, any>;
    const bd = this.getPaymentCheckoutBreakdown();
    const prevOrder =
      data['order'] && typeof data['order'] === 'object' && !Array.isArray(data['order'])
        ? ({ ...(data['order'] as Record<string, any>) } as Record<string, any>)
        : {};
    const listPriceSnap = this.computeOrderRequestListPriceForApi();
    prevOrder['list_price'] =
      listPriceSnap != null
        ? this.formatCheckoutCurrency(parseFloat(listPriceSnap))
        : this.formatCheckoutCurrency(bd.subtotal);
    prevOrder['discount'] = this.formatCheckoutCurrency(bd.discount);
    prevOrder['final_price'] = this.formatCheckoutCurrency(bd.total);
    const prepaidId = this.getOrderRequestSelectedPrepaidBookIdForApi();
    if (prepaidId) {
      prevOrder['prepaid_wash_book_id'] = prepaidId;
      const book = this.getOrderRequestSelectedPrepaidBook();
      if (book?.name) {
        prevOrder['prepaid_wash_book_name'] = book.name;
      }
      delete prevOrder['package_id'];
      delete prevOrder['package_name'];
      prevOrder['wash_type'] = 'prepaid_wash_book';
      data['wash_type'] = 'prepaid_wash_book';
      data['prepaid_wash_book_id'] = prepaidId;
      if (book?.name) {
        data['prepaid_wash_book_name'] = book.name;
      }
    } else if (this.hasOrderRequestWashPackageSelected()) {
      const pkgId = (this.formData.packageId || this.selectedPackage?.id || '').trim();
      if (pkgId) {
        prevOrder['package_id'] = pkgId;
      }
      const pkgName = (this.formData.package || this.selectedPackage?.package_name || '').trim();
      if (pkgName) {
        prevOrder['package_name'] = pkgName;
      }
      delete prevOrder['prepaid_wash_book_id'];
      delete prevOrder['prepaid_wash_book_name'];
      prevOrder['wash_type'] = this.getNormalizedWashType();
      data['wash_type'] = this.getNormalizedWashType();
      delete data['prepaid_wash_book_id'];
      delete data['prepaid_wash_book_name'];
    } else {
      delete prevOrder['package_id'];
      delete prevOrder['package_name'];
      delete prevOrder['prepaid_wash_book_id'];
      delete prevOrder['prepaid_wash_book_name'];
      prevOrder['wash_type'] = this.getNormalizedWashType();
      data['wash_type'] = this.getNormalizedWashType();
      delete data['prepaid_wash_book_id'];
      delete data['prepaid_wash_book_name'];
    }
    data['order'] = prevOrder;

    const uiAddOnLines = this.buildCreatePaymentRequestAddOnSnapshotRows();
    if (uiAddOnLines.length > 0) {
      data['add_ons'] = uiAddOnLines;
      data['add_on_variants'] = uiAddOnLines;
    } else if (this.isEditMode || this.getCheckoutAddOnVariantsSnapshot().length === 0) {
      // Checkout view: preserve synced add-on rows after populateFormData reset the UI.
      data['add_ons'] = [];
      data['add_on_variants'] = [];
    }

    const discRow = this.buildCreatePaymentRequestDiscountSnapshotRow();
    if (discRow) {
      data['discount'] = discRow;
    } else {
      delete data['discount'];
    }
    this.syncCreatePaymentRequestBookingDetailsFromOrderRequestUi();
  }

  /** Plate, state, and lane image for Back → edit restore (API order rows often omit these). */
  private syncCreatePaymentRequestBookingDetailsFromOrderRequestUi(): void {
    if (this.notification?.type !== 'create-payment-request' || !this.notification.data) {
      return;
    }
    const data = this.notification.data as Record<string, any>;
    const parsed = splitLicensePlateForApi(
      this.formData.vehicleLicensePlate,
      this.formData.vehicleLicensePlateState
    );
    const combined = parsed.license_plate ? `${parsed.state}${parsed.license_plate}` : '';

    data['state'] = parsed.state;
    if (combined) {
      data['plate'] = combined;
      data['vehicle_license_plate'] = combined;
    }

    const imageUrl = this.orderRequestLprAxisPlateImageUrl;
    if (imageUrl) {
      data['plate_image_s3_url'] = imageUrl;
      this.orderRequestLprAxisPlateImageUrlOverride = imageUrl;
    }

    const order = data['order'];
    if (order && typeof order === 'object' && !Array.isArray(order)) {
      const o = order as Record<string, any>;
      o['state'] = parsed.state;
      if (parsed.license_plate) {
        o['license_plate'] = parsed.license_plate;
      }
    }
  }

  /** Re-apply booking fields saved on notification.data when re-entering edit from checkout. */
  private restoreCreatePaymentRequestBookingDetailsFromNotificationLayer(): void {
    if (this.notification?.type !== 'create-payment-request' || !this.notification.data) {
      return;
    }
    const data = this.notification.data as Record<string, any>;
    const plate = String(data['plate'] ?? data['vehicle_license_plate'] ?? '').trim();
    if (plate) {
      const stateHint = data['state'] ?? data['state_code'] ?? data['vehicle_state'];
      this.setCombinedLicensePlateOnForm(plate, typeof stateHint === 'string' ? stateHint : null);
    }
    this.syncOrderRequestPlateImageFromNotificationData();
    this.refreshOrderRequestLprAxisPlateImagePreload();
    this.cdr.detectChanges();
  }

  private hasOrderRequestVehicleBookingChanges(): boolean {
    const current = splitLicensePlateForApi(
      this.formData.vehicleLicensePlate,
      this.formData.vehicleLicensePlateState
    );
    const original = splitLicensePlateForApi(
      this.originalValues.vehicleLicensePlate,
      this.originalValues.vehicleLicensePlateState
    );
    return current.state !== original.state || current.license_plate !== original.license_plate;
  }

  private buildCreatePaymentRequestAddOnSnapshotRows(): Record<string, any>[] {
    const payload = this.buildCreateOrderAddOnVariantsPayload();
    const rows: Record<string, any>[] = [];
    for (const row of payload) {
      const names = this.resolveAddOnNamesForPersistedPayloadRow(row);
      rows.push({
        add_on_id: row.add_on_id,
        add_on_variant_id: row.add_on_variant_id,
        add_on_custom_price: row.add_on_custom_price,
        add_on_count: row.add_on_count,
        add_on_name: names.add_on_name,
        add_on_variant_name: names.add_on_variant_name,
      });
    }
    return rows;
  }

  private resolveAddOnNamesForPersistedPayloadRow(row: CreateOrderAddOnVariantPayload): {
    add_on_name: string;
    add_on_variant_name: string;
  } {
    const pkgId = (row.add_on_id || '').trim();
    const varId = (row.add_on_variant_id || '').trim();
    const addon = this.orderRequestAddons.find(a => (a.apiPackageId || '').trim() === pkgId);
    if (!addon) {
      const fallbackKind = this.getOrderRequestCommittedAddonPackageKind();
      return {
        add_on_name: fallbackKind === 'detailing' ? 'Detailing' : 'Add-on',
        add_on_variant_name: '',
      };
    }
    if (addon.mode === 'choose_products' && addon.vendingCatalog) {
      const cat = addon.vendingCatalog.find(c => (c.apiVariantId || '').trim() === varId);
      return {
        add_on_name: addon.label,
        add_on_variant_name: cat?.name ?? '',
      };
    }
    if (addon.mode === 'multi_variant_package' && addon.packageVariants) {
      const v = addon.packageVariants.find(x => (x.apiVariantId || '').trim() === varId);
      return { add_on_name: addon.label, add_on_variant_name: v?.label ?? '' };
    }
    return { add_on_name: addon.label, add_on_variant_name: '' };
  }

  private buildCreatePaymentRequestDiscountSnapshotRow(): Record<string, any> | null {
    const sel = this.orderRequestSelectedDiscountId;
    if (!sel) {
      return null;
    }
    // `discount_value` is the CONFIGURED value (80 for "80% off") and `discount` is the
    // resolved money. They are separate because the labels read the former while
    // `getCheckoutRawDiscountAmount` falls back to reading an amount off this snapshot — putting
    // the amount in `discount_value` made the type render as "160% off" and would make the
    // grand total wrong if the label fix took the value at face value.
    if (sel === 'disc_custom' && this.orderRequestCustomDiscountSaved) {
      const s = this.orderRequestCustomDiscountSaved;
      const pre = this.getOrderRequestPreDiscountTotal();
      const amt = this.computeOrderRequestCustomDiscountAmount(pre);
      const isPct = s.typeKey === 'percentage';
      return {
        ...(s.id ? { id: s.id, discount_id: s.id } : {}),
        name: s.name,
        code: '',
        discount_type: isPct ? 'percentage' : 'fixed_amount',
        discount_value: isPct ? String(s.valueLabel).replace(/%/g, '').trim() : amt,
        discount: amt,
      };
    }
    const api = this.orderRequestDiscounts.find(d => d.id === sel);
    if (api?.mode === 'fixed') {
      const rawAmt = this.computeOrderRequestApiDiscountAmount(
        this.getOrderRequestPreDiscountTotal(),
        api
      );
      return {
        name: api.label,
        code: api.discountCode ?? '',
        discount_type: api.discountType ?? 'fixed_amount',
        discount_value: api.discountValue ?? rawAmt,
        discount: rawAmt,
      };
    }
    return null;
  }

  private extractAddOnLinesFromOrderApiRoot(root: any): Record<string, any>[] {
    if (!root || typeof root !== 'object') {
      return [];
    }
    const nested = root['order'];
    const raw =
      root['add_ons'] ??
      root['add_on_variants'] ??
      root['addOnVariants'] ??
      (nested && typeof nested === 'object' && !Array.isArray(nested)
        ? (nested['add_ons'] ?? nested['add_on_variants'])
        : undefined);
    return Array.isArray(raw) ? (raw as Record<string, any>[]) : [];
  }

  private getPersistedAddOnLinesForOrderRequestRestore(fetchedRoot: any): Record<string, any>[] {
    const fromNotification = this.getCheckoutAddOnVariantsSnapshot();
    if (fromNotification.length > 0) {
      return fromNotification;
    }
    return this.extractAddOnLinesFromOrderApiRoot(fetchedRoot);
  }

  private mergeOrderLikeForRestore(root: any): Record<string, any> {
    if (!root || typeof root !== 'object') {
      return {};
    }
    const nested = root['order'];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return { ...(nested as Record<string, any>) };
    }
    return { ...(root as Record<string, any>) };
  }

  /**
   * Repopulate single-wash custom price UI from persisted `list_price` after
   * `resetOrderRequestAddonUi()` cleared in-memory maps.
   */
  private restoreOrderRequestSingleWashCustomPriceFromListPriceSource(
    orderLike: Record<string, any> | null | undefined
  ): void {
    if (!orderLike || typeof orderLike !== 'object') {
      return;
    }
    if (this.isAddonOnlyWashContext(orderLike) || !this.hasOrderRequestWashPackageSelected()) {
      return;
    }
    const n =
      this.parseUsdNumber(orderLike['list_price']) ?? this.parseUsdNumber(orderLike['listPrice']);
    if (n == null || !Number.isFinite(n) || n <= 0) {
      return;
    }
    const ep = this.getSelectedSingleWashCustomPriceExpandedPackage();
    if (!ep?.allowsCustomPrice || this.normalizeWashType(ep.washType) !== 'single_wash') {
      return;
    }
    const orderPkgId = String(orderLike['package_id'] ?? orderLike['packageId'] ?? '').trim();
    const selectedPkgId = String(ep.package.id ?? '').trim();
    if (orderPkgId && selectedPkgId && orderPkgId !== selectedPkgId) {
      return;
    }
    this.orderRequestSingleWashSavedCustomPrice[ep.uniqueId] = `$${n.toFixed(2)}`;
    this.formData.price = n.toFixed(2);
    this.rebuildMostUsedServiceItems();
  }

  /** Prefer freshly fetched order root; else `data.order` on create-payment-request. */
  private restoreOrderRequestSingleWashCustomPriceFromPersistedSources(): void {
    if (this.notification?.type !== 'create-payment-request') {
      return;
    }
    const root = this.lastOrderFetchPayloadForOrderRequestRestore;
    const orderLike = root ? this.mergeOrderLikeForRestore(root) : this.getCheckoutOrderSnapshot();
    this.restoreOrderRequestSingleWashCustomPriceFromListPriceSource(orderLike);
  }

  private applyPersistedAddonLines(lines: Record<string, any>[]): void {
    if (!lines.length) {
      return;
    }
    const byPkg = new Map<string, Record<string, any>[]>();
    for (const line of lines) {
      const id = String(line['add_on_id'] ?? line['addon_id'] ?? '').trim();
      if (!id) {
        continue;
      }
      const list = byPkg.get(id) ?? [];
      list.push(line);
      byPkg.set(id, list);
    }

    for (const [pkgId, pkgLines] of byPkg) {
      const addon = this.orderRequestAddons.find(a => (a.apiPackageId || '').trim() === pkgId);
      if (!addon) {
        continue;
      }

      if (addon.mode === 'choose_products') {
        const merged: OrderRequestVendingSelectionLine[] = [];
        let sum = 0;
        for (const line of pkgLines) {
          const variantId = String(line['add_on_variant_id'] ?? line['variant_id'] ?? '').trim();
          const cat = addon.vendingCatalog?.find(c => (c.apiVariantId || '').trim() === variantId);
          const count = Math.max(1, Number(line['add_on_count']) || 1);
          let unit = this.parseUsdNumber(line['add_on_custom_price'] ?? line['custom_price']);
          if ((unit == null || !Number.isFinite(unit)) && cat) {
            unit = cat.unitPrice;
          }
          if (unit == null || !Number.isFinite(unit)) {
            unit = 0;
          }
          const fallbackName = String(line['add_on_variant_name'] ?? line['name'] ?? '').trim();
          const fallbackLineId =
            variantId ||
            `restored_${addon.id}_${String(line['id'] ?? '').trim() || merged.length.toString()}`;
          const lineId = cat?.id ?? fallbackLineId;
          const lineName = fallbackName || cat?.name || addon.label;
          const lineAmount = Math.round(unit * count * 100) / 100;
          sum += lineAmount;
          merged.push({
            id: lineId,
            name: lineName,
            quantity: count,
            unitPrice: unit,
            lineTotalFormatted: `$${lineAmount.toFixed(2)}`,
          });
          if (
            cat?.allowsCustomPrice ||
            this.parseUsdNumber(line['add_on_custom_price'] ?? line['custom_price']) != null
          ) {
            this.orderRequestAddonSavedCustomPrice[lineId] = this.formatCheckoutCurrency(unit);
          }
        }
        if (merged.length) {
          this.orderRequestVendingSelections[addon.id] = {
            lines: merged,
            totalFormatted: `$${sum.toFixed(2)}`,
          };
          const next = new Set(this.orderRequestSelectedAddonIds);
          next.add(addon.id);
          this.orderRequestSelectedAddonIds = next;
        }
        continue;
      }

      if (addon.mode === 'multi_variant_package' && addon.packageVariants) {
        const nextSel = new Set(this.orderRequestSelectedAddonIds);
        const usedLineIndexes = new Set<number>();
        for (const pv of addon.packageVariants) {
          const pvApiId = (pv.apiVariantId || '').trim();
          let hitIndex = pkgLines.findIndex((l, idx) => {
            if (usedLineIndexes.has(idx)) {
              return false;
            }
            const lineVarId = String(l['add_on_variant_id'] ?? l['variant_id'] ?? '').trim();
            return !!pvApiId && !!lineVarId && lineVarId === pvApiId;
          });
          if (hitIndex < 0) {
            const pvName = String(pv.label || '')
              .trim()
              .toLowerCase();
            hitIndex = pkgLines.findIndex((l, idx) => {
              if (usedLineIndexes.has(idx)) {
                return false;
              }
              const lineName = String(l['add_on_variant_name'] ?? l['name'] ?? '')
                .trim()
                .toLowerCase();
              return !!pvName && !!lineName && lineName === pvName;
            });
          }
          if (hitIndex < 0) {
            continue;
          }
          usedLineIndexes.add(hitIndex);
          const hit = pkgLines[hitIndex];
          if (!hit) {
            continue;
          }
          const cnt = Math.max(1, Number(hit['add_on_count']) || 1);
          this.orderRequestAddonVariantQuantities[pv.id] = cnt;
          if (pv.mode === 'custom_price') {
            const u = this.parseUsdNumber(hit['add_on_custom_price'] ?? hit['custom_price']);
            if (u != null && Number.isFinite(u)) {
              this.orderRequestAddonSavedCustomPrice[pv.id] = this.formatCheckoutCurrency(u);
            }
          }
          nextSel.add(pv.id);
        }
        this.orderRequestSelectedAddonIds = nextSel;
        continue;
      }

      if (pkgLines.length >= 1) {
        const line = pkgLines[0];
        const next = new Set(this.orderRequestSelectedAddonIds);
        next.add(addon.id);
        this.orderRequestSelectedAddonIds = next;
        if (addon.mode === 'custom_price') {
          const u = this.parseUsdNumber(line['add_on_custom_price'] ?? line['custom_price']);
          if (u != null && Number.isFinite(u)) {
            this.orderRequestAddonSavedCustomPrice[addon.id] = this.formatCheckoutCurrency(u);
          }
        }
      }
    }
  }

  /** `discount` sibling on GET order / POST order body (not always merged into `order`). */
  private extractDiscountRowFromFetchedOrder(fetchedRoot: any): Record<string, any> | null {
    if (!fetchedRoot || typeof fetchedRoot !== 'object') {
      return null;
    }
    const root = fetchedRoot as Record<string, any>;
    const wrap =
      root['data'] !== undefined && root['data'] !== null
        ? (root['data'] as Record<string, any>)
        : root;
    const d = wrap['discount'];
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      return d as Record<string, any>;
    }
    return null;
  }

  private clearOrderRequestCustomDiscountEditorState(): void {
    this.orderRequestCustomDiscountSaved = null;
    this.orderRequestCustomDiscountName = '';
    this.orderRequestCustomDiscountValueDraft = '';
    this.orderRequestCustomDiscountType = 'fixed_amount';
    this.orderRequestCustomDiscountReason = '';
    this.orderRequestCustomDiscountReasonError = null;
    this.orderRequestCustomDiscountExpanded = false;
  }

  private applyOrderRequestCustomDiscountSavedFromRow(row: Record<string, any>): void {
    const dt = String(row['discount_type'] ?? row['type'] ?? '').toLowerCase();
    const isPct = dt === 'percentage' || dt === 'percent';
    const rawVal = row['discount_value'];
    const pctNum = isPct ? this.parseUsdNumber(String(rawVal).replace(/%/g, '')) : null;
    const amtNum = !isPct ? this.parseUsdNumber(rawVal) : null;
    const persistedId = String(row['id'] ?? row['discount_id'] ?? '').trim();
    const reason = String(row['reason'] ?? '').trim();
    this.orderRequestCustomDiscountSaved = {
      name: (String(row['name'] ?? 'Discount').trim() || 'Discount') as string,
      typeKey: isPct ? 'percentage' : 'fixed_amount',
      valueLabel: isPct
        ? pctNum != null && Number.isFinite(pctNum)
          ? `${pctNum}%`
          : String(rawVal ?? '')
        : amtNum != null && Number.isFinite(amtNum)
          ? this.formatCheckoutCurrency(amtNum)
          : String(rawVal ?? ''),
      ...(persistedId ? { id: persistedId } : {}),
      ...(reason ? { reason } : {}),
    };
    this.orderRequestSelectedDiscountId = 'disc_custom';
    this.orderRequestCustomDiscountExpanded = false;
    this.orderRequestCustomDiscountName = this.orderRequestCustomDiscountSaved.name;
    this.orderRequestCustomDiscountReason = reason;
  }

  private restoreOrderRequestDiscountFromNotificationAndOrder(
    orderLike: Record<string, any>,
    fetchedRoot?: any
  ): void {
    if (this.isOrderRequestDiscountDisabled()) {
      return;
    }
    const id = String(orderLike['discount_id'] ?? orderLike['discountId'] ?? '').trim();
    const catalogById = id
      ? this.orderRequestDiscounts.find(d => d.id === `disc_${id}` && d.mode === 'fixed')
      : undefined;
    if (id && catalogById) {
      this.orderRequestSelectedDiscountId = `disc_${id}`;
      this.clearOrderRequestCustomDiscountEditorState();
      return;
    }

    const snap = this.getCheckoutDiscountSnapshot();
    if (snap && typeof snap === 'object') {
      const sid = String(snap['id'] ?? snap['discount_id'] ?? '').trim();
      const snapCatalog = sid
        ? this.orderRequestDiscounts.find(d => d.id === `disc_${sid}` && d.mode === 'fixed')
        : undefined;
      if (sid && snapCatalog) {
        this.orderRequestSelectedDiscountId = `disc_${sid}`;
        this.clearOrderRequestCustomDiscountEditorState();
        return;
      }
      if (Object.keys(snap).length > 0) {
        this.applyOrderRequestCustomDiscountSavedFromRow({
          ...snap,
          id: id || sid || snap['id'] || snap['discount_id'],
        });
        return;
      }
    }

    const discRow = this.extractDiscountRowFromFetchedOrder(fetchedRoot);
    if (discRow && Object.keys(discRow).length > 0) {
      const did = String(discRow['id'] ?? discRow['discount_id'] ?? '').trim();
      const rowCatalog = did
        ? this.orderRequestDiscounts.find(d => d.id === `disc_${did}` && d.mode === 'fixed')
        : undefined;
      if (did && rowCatalog) {
        this.orderRequestSelectedDiscountId = `disc_${did}`;
        this.clearOrderRequestCustomDiscountEditorState();
        return;
      }
      this.applyOrderRequestCustomDiscountSavedFromRow({
        ...discRow,
        id: id || did || discRow['id'] || discRow['discount_id'],
      });
      return;
    }

    // Order has a discount_id that is not a shop tile — treat as custom reference.
    if (id) {
      this.applyOrderRequestCustomDiscountSavedFromRow({
        id,
        discount_id: id,
        name: 'Custom discount',
        discount_type: 'flat',
        discount_value: orderLike['discount'] ?? 0,
      });
    }
  }

  private restorePaymentFormMethodFromOrder(orderLike: Record<string, any>): void {
    const raw = String(orderLike['payment_method'] ?? orderLike['paymentMethod'] ?? '')
      .trim()
      .toLowerCase();
    if (!raw) {
      return;
    }
    const map: Record<string, PaymentForm['method']> = {
      [PaymentMethodCode.Cash]: PaymentMethodCode.Cash,
      [PaymentMethodCode.Card]: PaymentMethodCode.Card,
      [PaymentMethodCode.Link]: PaymentMethodCode.Link,
      [PaymentMethodCode.Terminal]: PaymentMethodCode.Terminal,
      [PaymentMethodCode.TapToPay]: PaymentMethodCode.TapToPay,
      taptopay: PaymentMethodCode.TapToPay,
    };
    const m =
      map[raw] ??
      resolveBuiltInPaymentMethodCode(raw) ??
      this.dynamicPaymentMethodOptions.find(
        option =>
          this.normalizePaymentMethodName(option.name) === this.normalizePaymentMethodName(raw)
      )?.name;
    if (m) {
      this.selectPaymentMethod(m);
    }
  }

  private applyCreatePaymentOrderRequestUiFromPersistedData(fetchedRoot: any): void {
    if (this.notification?.type !== 'create-payment-request') {
      return;
    }

    this.resetOrderRequestAddonUi();
    this.resetOrderRequestDiscountUi();

    const orderLike = this.mergeOrderLikeForRestore(fetchedRoot);
    const fleetId = String(orderLike['fleet_id'] ?? orderLike['fleetId'] ?? '').trim();
    if (fleetId) {
      this.orderRequestSelectedFleetId = fleetId;
    }

    const lines = this.getPersistedAddOnLinesForOrderRequestRestore(fetchedRoot);
    this.applyPersistedAddonLines(lines);

    this.restoreOrderRequestDiscountFromNotificationAndOrder(orderLike, fetchedRoot);
    this.restorePaymentFormMethodFromOrder(orderLike);
    const prepaidRestored = this.restoreOrderRequestPrepaidSelectionFromPersistedSources(orderLike);
    if (prepaidRestored) {
      // Prepaid selection restored — skip package custom-price replay.
    } else if (!this.isAddonOnlyWashContext(orderLike)) {
      this.restoreOrderRequestSingleWashCustomPriceFromListPriceSource(orderLike);
    } else {
      this.clearOrderRequestWashPackageSelection();
    }
  }

  private tryApplyPendingCreatePaymentOrderRequestUiRestore(): void {
    if (!this.pendingCreatePaymentOrderRequestUiRestore) {
      return;
    }
    if (!this.isEditMode || this.notification?.type !== 'create-payment-request') {
      this.pendingCreatePaymentOrderRequestUiRestore = false;
      this.lastOrderFetchPayloadForOrderRequestRestore = null;
      return;
    }
    if (
      this.isLoadingOrderRequestAddons ||
      this.isLoadingOrderRequestFleets ||
      this.isLoadingOrderRequestDiscounts
    ) {
      return;
    }
    const payload = this.lastOrderFetchPayloadForOrderRequestRestore;
    if (!payload) {
      this.pendingCreatePaymentOrderRequestUiRestore = false;
      return;
    }
    this.applyCreatePaymentOrderRequestUiFromPersistedData(payload);
    this.pendingCreatePaymentOrderRequestUiRestore = false;
    this.lastOrderFetchPayloadForOrderRequestRestore = null;
    this.selectPackageFromFormData();
    this.syncOrderRequestAddonSelectionToPairedWith();
  }

  /** Persist package, totals, discount, and add-on lines for an existing checkout order. */
  private putOrderCheckoutFields(): Promise<any> {
    return firstValueFrom(this.ensureOrderRequestCustomDiscountId$()).then(
      () =>
        new Promise((resolve, reject) => {
          if (!this.formData.orderId) {
            reject(new Error('Order ID is required'));
            return;
          }
          const resolvedLocationId = this.resolveLocationIdForSubmission();
          const listPricePut = this.computeOrderRequestListPriceForApi();
          const washTypePut = this.getNormalizedWashType();
          const payload: Record<string, any> = {
            // CWS-1181: `package_id` is Joi.forbidden() on PUT /order/:id for a prepaid
            // wash type, and forbidden rejects the key's presence — not just a real id.
            // Sending '' therefore 400s the whole save rather than reading as "no
            // package", which is what going back from payment and picking prepaid did.
            // Same helper the POST path uses, so create and update now agree.
            ...buildCreateOrderPackageIdField(
              washTypePut,
              this.hasOrderRequestWashPackageForCheckout() ? this.formData.packageId || '' : ''
            ),
            wash_type: washTypePut,
            final_price: this.computeOrderRequestFinalPrice(),
            ...(listPricePut != null ? { list_price: listPricePut } : {}),
            ...this.buildCreateOrderPrepaidPayloadFields(),
          };
          if (resolvedLocationId) {
            payload['location_id'] = resolvedLocationId;
          }
          this.appendOrderCheckoutDiscountAndAddonsToPayload(payload);

          console.log('🔄 PUT order checkout snapshot:', this.formData.orderId, payload);

          this.apiService
            .put(`order/${this.formData.orderId}`, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: response => {
                this.applyOrderCheckoutTotalsFromPutResponse(response);
                resolve(response);
              },
              error: error => reject(error),
            });
        })
    );
  }

  /**
   * Enter edit mode - fetch order data from API and show form fields
   */
  enterEditMode(): void {
    this.logModalInteraction('edit.enter');
    // CWS-1945: Back on a draft returns to the form as the attendant left it; nothing to fetch.
    if (this.checkoutDraftPayload) {
      this.isEditMode = true;
      this.isFetchingOrderData = false;
      this.updateOrderError = '';
      this.cdr.detectChanges();
      return;
    }
    // Get order ID from formData or notification data
    const orderId =
      this.formData.orderId ||
      this.notification?.data?.order_id ||
      this.notification?.data?.orderId;

    if (!orderId) {
      console.error('❌ Order ID not found, cannot enter edit mode');
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Order ID not found. Cannot load order details.',
      });
      return;
    }

    // Set edit mode flag and show loading state
    this.isEditMode = true;
    this.isFetchingOrderData = true;
    this.updateOrderError = '';

    console.log('📝 Entering edit mode. Fetching order data for order ID:', orderId);

    // Fetch order data from API
    this.apiService
      .get(`order/${orderId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (orderResponse: any) => {
          const root = orderResponse?.data ?? orderResponse;
          const order = root?.order ?? root;

          console.log('✅ Order data fetched successfully:', order);

          this.lastOrderFetchPayloadForOrderRequestRestore = root;
          this.pendingCreatePaymentOrderRequestUiRestore = true;

          this.populateFormDataFromOrder(order);
          this.restoreCreatePaymentRequestBookingDetailsFromNotificationLayer();
          if (this.isAddonOnlyWashContext(order)) {
            this.clearOrderRequestWashPackageSelection();
          }

          const orderLike = this.mergeOrderLikeForRestore(root);
          const prepaidRestored =
            this.restoreOrderRequestPrepaidSelectionFromPersistedSources(orderLike);

          // Store original values from fetched data
          this.originalValues = {
            packageId: this.formData.packageId || '',
            customerFirstName: this.formData.customerFirstName || '',
            customerLastName: this.formData.customerLastName || '',
            customerEmail: this.formData.customerEmail || '',
            customerContact: this.formData.customerContact || '',
            vehicleLicensePlate: this.formData.vehicleLicensePlate || '',
            vehicleLicensePlateState:
              this.formData.vehicleLicensePlateState || DEFAULT_LICENSE_PLATE_STATE,
            vehicleVin: this.formData.vehicleVin || '',
            vehicleId: this.formData.vehicleId || '',
          };

          const locationId =
            this.formData.locationId ||
            order.location_id ||
            order.locationId ||
            this.notification?.data?.location_id ||
            this.notification?.data?.locationId;

          this.loadOrderRequestPrepaidBooks(locationId);

          if (this.packages.length === 0 && locationId) {
            console.log('📦 Loading packages for locationId:', locationId);
            if (!this.formData.locationId) {
              this.formData.locationId = locationId;
            }
            this.loadCarWashPackages();
          } else if (this.packages.length > 0) {
            if (locationId) {
              this.ensureOrderRequestAddonsAndFleetsLoaded(locationId);
              this.loadOrderRequestDiscounts(locationId);
              this.loadMostUsedDefaultsForOrderRequest(locationId);
            }
            if (!prepaidRestored) {
              this.selectPackageFromFormData();
            }
          } else {
            console.warn('⚠️ No locationId available, cannot load packages');
          }

          this.tryApplyPendingCreatePaymentOrderRequestUiRestore();
          this.isFetchingOrderData = false;

          console.log('📝 Edit mode ready. Original values:', this.originalValues);
          console.log('📝 Current formData:', this.formData);
        },
        error: error => {
          this.pendingCreatePaymentOrderRequestUiRestore = false;
          this.lastOrderFetchPayloadForOrderRequestRestore = null;
          this.isFetchingOrderData = false;
          console.error('❌ Error fetching order data:', error);
          this.isEditMode = false;
          this.updateOrderError = sanitizeErrorMessage(
            error.error?.message || 'Failed to fetch order details. Please try again.'
          );
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.updateOrderError,
          });
        },
      });
  }

  /**
   * Populate formData from fetched order object
   */
  private populateFormDataFromOrder(order: any): void {
    console.log('📝 Populating formData from order:', order);

    // Update customer information
    // Handle customer_name (single field) or customer_first_name/customer_last_name (separate fields)

    // If customer_name is provided, use it as firstName (or split if it contains space)
    // const customerName = order.customer_name.trim();
    // const nameParts = customerName.split(' ');
    if (order.customer_first_name) {
      this.formData.customerFirstName = order.customer_first_name || '';
    }
    if (order.customer_last_name) {
      this.formData.customerLastName = order.customer_last_name || '';
    }

    if (order.customer_email || order.customerEmail) {
      this.formData.customerEmail = order.customer_email || order.customerEmail || '';
    }
    if (order.customer_contact || order.customerContact) {
      this.formData.customerContact = order.customer_contact || order.customerContact || '';
    }

    // Update vehicle information
    if (order.license_plate || order.vehicle_license_plate || order.plate) {
      const plateValue = order.license_plate || order.vehicle_license_plate || order.plate || '';
      if (plateValue === 'ACTIONTEST') {
        this.formData.vehicleLicensePlate = '';
      } else {
        this.setCombinedLicensePlateOnForm(String(plateValue), order.state || null);
      }
    }

    // Update IDs
    if (order.customer_id || order.customerId) {
      this.formData.customerId = (order.customer_id || order.customerId).toString();
    }
    if (order.vehicle_id || order.vehicleId) {
      this.formData.vehicleId = (order.vehicle_id || order.vehicleId).toString();
    }
    if (order.package_id || order.packageId) {
      this.formData.packageId = (order.package_id || order.packageId).toString();
    }
    const orderWashRaw = order.wash_type || order.washType;
    if (orderWashRaw != null && String(orderWashRaw).trim() !== '') {
      const wt = String(orderWashRaw).trim();
      this.cachedOrderWashType = wt;
      if (this.notification?.data && typeof this.notification.data === 'object') {
        const d = this.notification.data as Record<string, any>;
        d['wash_type'] = this.normalizeWashType(wt);
        const nested = d['order'];
        if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
          (nested as Record<string, any>)['wash_type'] = this.normalizeWashType(wt);
        }
      }
    }
    if (order.id || order.order_id || order.orderId) {
      this.formData.orderId = (order.id || order.order_id || order.orderId).toString();
    }

    // Update locationId if available (needed for loading packages)
    if (order.location_id || order.locationId) {
      this.formData.locationId = (order.location_id || order.locationId).toString();
    }

    // Update package name if available
    if (order.package_name || order.packageName) {
      this.formData.package = order.package_name || order.packageName || '';
    }

    console.log('📝 FormData populated:', {
      customerFirstName: this.formData.customerFirstName,
      customerLastName: this.formData.customerLastName,
      customerEmail: this.formData.customerEmail,
      customerContact: this.formData.customerContact,
      vehicleLicensePlate: this.formData.vehicleLicensePlate,
      packageId: this.formData.packageId,
      locationId: this.formData.locationId,
    });
  }

  /**
   * Select package from formData packageId (and wash_type when the same API package has single + monthly rows).
   */
  private selectPackageFromFormData(): void {
    if (this.isAddonOnlyWashContext()) {
      this.clearOrderRequestWashPackageSelection();
      return;
    }
    const d = this.notification?.data as
      { wash_type?: string; order?: { wash_type?: string } } | undefined;
    const snapshotWashType = this.getCheckoutOrderSnapshot()?.['wash_type'];
    if (
      this.isPrepaidWashBookWashType(this.cachedOrderWashType) ||
      this.isPrepaidWashBookWashType(d?.wash_type) ||
      this.isPrepaidWashBookWashType(d?.order?.wash_type) ||
      this.isPrepaidWashBookWashType(
        snapshotWashType != null ? String(snapshotWashType) : undefined
      )
    ) {
      return;
    }
    const pkgId = (this.formData.packageId || '').trim();
    if (!pkgId) {
      return;
    }
    if (this.expandedPackages.length === 0) {
      console.log('⏳ Packages still loading, will select package when available');
      return;
    }
    const sameId = this.expandedPackages.filter(ep => ep.package.id === pkgId);
    if (!sameId.length) {
      console.warn('⚠️ Package not found in expanded packages for packageId:', pkgId);
      return;
    }
    const hintSources = [
      this.cachedOrderWashType,
      d?.wash_type,
      d?.order?.wash_type,
      this.selectedWashType,
    ];
    let chosen: ExpandedPackage | undefined;
    for (const h of hintSources) {
      if (h == null || String(h).trim() === '') {
        continue;
      }
      const norm = this.normalizeWashType(String(h));
      chosen = sameId.find(ep => this.normalizeWashType(ep.washType) === norm);
      if (chosen) {
        break;
      }
    }
    if (!chosen && this.selectedWashType) {
      const prevWash = this.selectedWashType;
      chosen = sameId.find(
        ep => this.normalizeWashType(ep.washType) === this.normalizeWashType(prevWash)
      );
    }
    if (!chosen && sameId.length === 1) {
      chosen = sameId[0];
    }
    if (!chosen) {
      chosen =
        sameId.find(ep => this.normalizeWashType(ep.washType) === 'single_wash') ?? sameId[0];
    }
    this.selectedPackage = chosen.package;
    this.selectedWashType = chosen.washType;
    this.formData.packageId = chosen.package.id || '';
    this.formData.package = chosen.package.package_name || '';
    if (this.isEditMode) {
      this.userInteractedFields.add('package');
      this.userInteractedFields.add('packageId');
    }
    this.syncWashTypeContextToNotification(this.selectedWashType);
    console.log('✅ Selected package:', chosen.package.package_name, 'Wash type:', chosen.washType);
    this.restoreOrderRequestSingleWashCustomPriceFromPersistedSources();
  }

  /**
   * Cancel edit mode - restore original values and return to payment view
   */
  cancelEditMode(): void {
    this.logModalInteraction('edit.cancel');
    // CWS-1945: leaving the form of a draft goes back to its checkout.
    if (this.checkoutDraftPayload) {
      this.isEditMode = false;
      this.cdr.detectChanges();
      return;
    }
    this.onClose();
    // this.isEditMode = false;

    // // Restore original values
    // this.formData.packageId = this.originalValues.packageId;
    // this.formData.customerFirstName = this.originalValues.customerFirstName;
    // this.formData.customerLastName = this.originalValues.customerLastName;
    // this.formData.customerEmail = this.originalValues.customerEmail;
    // this.formData.customerContact = this.originalValues.customerContact;
    // this.formData.vehicleLicensePlate = this.originalValues.vehicleLicensePlate;
    // this.formData.vehicleVin = this.originalValues.vehicleVin;
    // this.formData.vehicleId = this.originalValues.vehicleId;

    // // Restore selected package if packageId exists
    // if (this.formData.packageId && this.expandedPackages.length > 0) {
    //   const matchingPackage = this.expandedPackages.find(
    //     ep => ep.package.id === this.formData.packageId
    //   );
    //   if (matchingPackage) {
    //     this.selectedPackage = matchingPackage.package;
    //     this.selectedWashType = matchingPackage.washType;
    //   } else {
    //     this.selectedPackage = null;
    //     this.selectedWashType = null;
    //   }
    // } else {
    //   this.selectedPackage = null;
    //   this.selectedWashType = null;
    // }

    // // Clear validation errors
    // this.clearValidationErrors();

    // // Initialize Stripe elements if card is default payment method (payment section becomes visible)
    // if (this.paymentForm.method === PaymentMethodCode.Card) {
    //   this.initializeStripeForCardDefault();
    // }

    console.log('❌ Cancelled edit mode. Restored original values.');
  }

  /**
   * Save changes made in edit mode
   */
  onSaveEditChanges(): void {
    if (this.isUpdatingOrder) {
      this.logModalInteraction('edit.save.skipped', { reason: 'already_updating' });
      return;
    }

    this.logModalInteraction('edit.save.start');

    // Clear previous validation errors
    this.clearValidationErrors();

    // Validate phone and email before submission
    if (!this.validatePhoneAndEmail()) {
      this.scrollAndFocusOrderRequestTarget(
        this.phoneValidationError ? 'phone' : this.emailValidationError ? 'email' : 'phone'
      );
      return;
    }

    this.isUpdatingOrder = true;
    this.updateOrderError = '';

    // Track what needs to be updated
    const updates = {
      package: false,
      customer: false,
      vehicle: false,
    };

    // Check if package changed
    if (this.formData.packageId !== this.originalValues.packageId) {
      updates.package = true;
    }

    // Check if customer fields changed
    if (
      this.formData.customerFirstName !== this.originalValues.customerFirstName ||
      this.formData.customerLastName !== this.originalValues.customerLastName ||
      this.formData.customerEmail !== this.originalValues.customerEmail ||
      this.formData.customerContact !== this.originalValues.customerContact
    ) {
      updates.customer = true;
    }

    // Check if vehicle license plate or state changed
    if (this.hasOrderRequestVehicleBookingChanges()) {
      updates.vehicle = true;
    }

    // If nothing changed in customer/vehicle/package, still persist checkout lines for payment-request
    if (!updates.package && !updates.customer && !updates.vehicle) {
      if (this.notification?.type === 'create-payment-request' && this.formData.orderId) {
        this.performUpdates(updates);
        return;
      }
      this.syncCreatePaymentRequestNotificationDataLayerFromOrderRequestUi();
      this.isEditMode = false;
      this.isUpdatingOrder = false;
      this.syncCashReceivedAfterOrderCheckoutUpdated();
      if (this.paymentForm.method === PaymentMethodCode.Card) {
        this.initializeStripeForCardDefault();
      }
      return;
    }

    this.performUpdates(updates);
  }

  /**
   * Perform updates in sequence: customer -> vehicle -> order -> LPR entry
   */
  private performUpdates(updates: { package: boolean; customer: boolean; vehicle: boolean }): void {
    const updatePromises: Promise<any>[] = [];

    // Update customer if needed
    if (updates.customer && this.formData.customerId) {
      updatePromises.push(this.updateCustomerData());
    }

    // Update vehicle if needed
    if (updates.vehicle && this.formData.vehicleId) {
      updatePromises.push(this.updateVehicleData());
    }

    if (this.notification?.type === 'create-payment-request' && this.formData.orderId) {
      updatePromises.push(this.putOrderCheckoutFields());
    } else if (updates.package && this.formData.orderId) {
      updatePromises.push(this.updateOrderPackage());
    }

    Promise.all(updatePromises)
      .then(() => {
        console.log('✅ All updates completed successfully');

        return this.updateLprEntry();
      })
      .then(() => {
        this.syncCreatePaymentRequestNotificationDataLayerFromOrderRequestUi();
        this.syncCheckoutSummaryTogglesAfterEditSave();
        this.isEditMode = false;
        this.isUpdatingOrder = false;
        this.syncCashReceivedAfterOrderCheckoutUpdated();
        this.cdr.detectChanges();

        // Update original values to reflect new state
        this.originalValues = {
          packageId: this.formData.packageId || '',
          customerFirstName: this.formData.customerFirstName || '',
          customerLastName: this.formData.customerLastName || '',
          customerEmail: this.formData.customerEmail || '',
          customerContact: this.formData.customerContact || '',
          vehicleLicensePlate: this.formData.vehicleLicensePlate || '',
          vehicleLicensePlateState:
            this.formData.vehicleLicensePlateState || DEFAULT_LICENSE_PLATE_STATE,
          vehicleVin: this.formData.vehicleVin || '',
          vehicleId: this.formData.vehicleId || '',
        };

        // Initialize Stripe elements if card is default payment method (payment section becomes visible)
        if (this.paymentForm.method === PaymentMethodCode.Card) {
          this.initializeStripeForCardDefault();
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Changes saved successfully!',
        });
      })
      .catch(error => {
        console.error('❌ Error performing updates:', error);
        this.isUpdatingOrder = false;
        this.updateOrderError = sanitizeErrorMessage(
          error.error?.message || 'Failed to save changes. Please try again.'
        );
      });
  }

  /**
   * Update customer data
   */
  private updateCustomerData(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.formData.customerId) {
        reject(new Error('Customer ID is required'));
        return;
      }

      const payload: any = {};

      if (this.formData.customerFirstName !== this.originalValues.customerFirstName) {
        payload.first_name = this.formData.customerFirstName;
      }
      if (this.formData.customerLastName !== this.originalValues.customerLastName) {
        payload.last_name = this.formData.customerLastName;
      }
      if (this.formData.customerEmail !== this.originalValues.customerEmail) {
        payload.email = this.formData.customerEmail;
      }
      if (this.formData.customerContact !== this.originalValues.customerContact) {
        payload.contact = this.formData.customerContact;
      }

      if (Object.keys(payload).length === 0) {
        resolve({});
        return;
      }

      console.log('🔄 Updating customer:', this.formData.customerId, payload);

      this.customerService
        .updateCustomer(this.formData.customerId, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            console.log('✅ Customer updated successfully:', response);
            resolve(response);
          },
          error: error => {
            console.error('❌ Error updating customer:', error);
            reject(error);
          },
        });
    });
  }

  /**
   * Update vehicle data
   */
  private updateVehicleData(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.formData.vehicleId) {
        reject(new Error('Vehicle ID is required'));
        return;
      }

      const licensePlate = this.formData.vehicleLicensePlate?.trim();
      if (!licensePlate) {
        if (this.isOrderRequestLicensePlateRequired()) {
          reject(new Error('License plate is required'));
          return;
        }
        resolve({});
        return;
      }

      const parsed = splitLicensePlateForApi(licensePlate, this.formData.vehicleLicensePlateState);
      const payload: any = {
        license_plate: parsed.license_plate,
        state: parsed.state,
      };

      console.log('🔄 Updating vehicle:', this.formData.vehicleId, payload);

      this.vehicleService
        .updateVehicle(this.formData.vehicleId, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            console.log('✅ Vehicle updated successfully:', response);
            resolve(response);
          },
          error: error => {
            console.error('❌ Error updating vehicle:', error);
            reject(error);
          },
        });
    });
  }

  /**
   * Update order package
   */
  private updateOrderPackage(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.formData.orderId) {
        reject(new Error('Order ID is required'));
        return;
      }

      if (!this.formData.packageId) {
        reject(new Error('Package ID is required'));
        return;
      }

      const resolvedLocationId = this.resolveLocationIdForSubmission();
      const listPricePkg = this.computeOrderRequestListPriceForApi();
      const payload: any = {
        package_id: this.formData.packageId,
        wash_type: this.getNormalizedWashType(),
        ...(listPricePkg != null ? { list_price: listPricePkg } : {}),
      };
      if (resolvedLocationId) {
        payload.location_id = resolvedLocationId;
      }

      console.log('🔄 Updating order package:', this.formData.orderId, payload);

      this.apiService
        .put(`order/${this.formData.orderId}`, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            console.log('✅ Order package updated successfully:', response);
            this.applyOrderCheckoutTotalsFromPutResponse(response);
            resolve(response);
          },
          error: error => {
            console.error('❌ Error updating order package:', error);
            reject(error);
          },
        });
    });
  }

  /**
   * Update LPR entry with corresponding data
   */
  private updateLprEntry(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (this.isDirectOrder()) {
        resolve({});
        return;
      }
      // Get notification ID
      const notificationId =
        this.formData.notificationId ||
        this.notification?.data?.id ||
        this.notification?.data?.licence_plate_id ||
        this.notification?.data?.license_plate_id;

      if (!notificationId) {
        console.warn('⚠️ Notification ID not found, skipping LPR entry update');
        resolve({}); // Resolve without error if notification ID is missing
        return;
      }

      // Build update payload with current formData values
      const updatePayload: any = {};

      // Add customer_id if available
      if (this.formData.customerId) {
        updatePayload.customer_id = this.formData.customerId;
      }

      // Add vehicle_id if available
      if (this.formData.vehicleId) {
        updatePayload.vehicle_id = this.formData.vehicleId;
      }

      // Add package_id if available
      if (this.formData.packageId) {
        updatePayload.package_id = this.formData.packageId;
      }

      // Add order_id if available
      if (this.formData.orderId) {
        updatePayload.order_id = this.formData.orderId;
      }

      // Keep wash_type in sync with the current package selection
      // This ensures LPR entries reflect the updated wash type whenever the package is changed
      const washType = this.getNormalizedWashType();
      if (washType) {
        updatePayload.wash_type = washType;
      }

      Object.assign(
        updatePayload,
        buildLprEntryLocationLanePutFields({
          notificationData: this.notification?.data as Record<string, any> | undefined,
          locationId: this.formData.locationId,
          laneId: this.formData.laneId,
        })
      );

      // Add license plate and state if available
      if (this.formData.vehicleLicensePlate && this.formData.vehicleLicensePlate.trim()) {
        const parsed = splitLicensePlateForApi(
          this.formData.vehicleLicensePlate,
          this.formData.vehicleLicensePlateState
        );
        updatePayload.plate = parsed.license_plate
          ? `${parsed.state}${parsed.license_plate}`
          : this.formData.vehicleLicensePlate.trim();
        updatePayload.state = parsed.state;
      }

      // Only update if there's at least one field to update
      if (Object.keys(updatePayload).length === 0) {
        console.log('⚠️ No fields to update in LPR entry');
        resolve({});
        return;
      }

      console.log('🔄 Updating LPR entry:', notificationId, updatePayload);

      this.apiService
        .put(`order/lpr-entries/${notificationId}`, updatePayload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            console.log('✅ LPR entry updated successfully:', response);
            this.notificationService.syncLprEntryFromPutResponse(response);
            resolve(response);
          },
          error: error => {
            console.error('❌ Error updating LPR entry:', error);
            // Don't reject - LPR update failure shouldn't block the flow
            // Just log the error and resolve
            resolve({});
          },
        });
    });
  }

  /**
   * Helper method to format value, showing 'N/A' if value is empty, null, undefined, or 'any'
   */
  formatValue(value: string | null | undefined): string {
    if (!value || value.trim() === '' || value.toLowerCase().trim() === 'any') {
      return 'N/A';
    }
    return value;
  }
}
